import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { 
  HardDrive, Monitor, RefreshCw, Users, FileText, Wrench, Settings, 
  CheckCircle, AlertTriangle, TrendingUp, Zap, Clock, Search, Plus,
  CheckCircle2, AlertCircle, Loader2, 
  Trash2, ChevronRight, ChevronDown, Filter, Download, Upload, Play,
  Activity, Shield, Layers, Database, Edit, Pause, PlayCircle, X,
  Rocket, ArrowRight, Info, BarChart3, Target, ExternalLink,
  ChevronLeft, Eye, Cpu, Mail, HardDriveDownload, HelpCircle, Sliders,
  ListChecks, ShieldAlert, EyeOff
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Sheet, SheetPortal, SheetClose, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { toast } from "sonner";
import { usePermissions } from '@/contexts/PermissionsContext';
import OSPatchReadOnlyNotice from '@/components/OSPatchReadOnlyNotice';
import axios from 'axios';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip, Legend, ReferenceLine, ReferenceArea } from 'recharts';
import FirmwareReportTab from './os-patch/FirmwareReportTab';
import FirmwareDeploymentsTab from './os-patch/FirmwareDeploymentsTab';
import FirmwareDiscoverTab from './os-patch/FirmwareDiscoverTab';

// Light/Dark themes (October 2026): big percentage figures reuse their progress bar's colour,
// which is too dark to read on the Light gradient. Text uses the matching light ink instead
// (the Status Pill palette); the bars keep the saturated colour.
const FIGURE_INK = {
  "#22c55e": "#C6F8DA",
  "#f59e0b": "#FEE9B8",
  "#06b6d4": "#C8F8FD",
  "#64748b": "#D3D8E3",
  "#ef4444": "#FFD9D3",
  "#3b82f6": "#DDEFFE",
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api/os-patch`;

// Free-disk gate for feature update eligibility, in GB. One number for the
// planner, the wizard defaults, the stuck-device views and the low-disk
// remediation picker. Mirrors FU_MIN_DISK_GB in os_patch_routes.py and the
// detection threshold in the "Free disk space for updates" catalog script —
// change all three together. 03/09: 40 → 30.
const FU_MIN_DISK_GB = 30;

// ============ Background-sync polling helper ============
// /sync/all now returns immediately with a job_id and the work happens
// in a backend asyncio task. This helper hides the polling loop so the
// page handlers can stay async/await-shaped: it resolves with the final
// `results` object once the job completes, or rejects on error / lost
// job (HTTP 404 — backend restarted mid-sync).
//
// Usage:
//   const results = await runSyncWithPolling({
//     onProgress: (step) => setProgress(step),
//   });
async function runSyncWithPolling({ onProgress } = {}) {
  // 1. Kick off the job
  const startRes = await axios.post(`${API}/sync/all`);
  const jobId = startRes?.data?.job_id;
  if (!jobId) {
    // Defensive: very old backend that still returns inline results
    if (startRes?.data?.results) return startRes.data.results;
    throw new Error('Sync did not return a job_id');
  }

  // 2. Poll until terminal state
  const POLL_INTERVAL_MS = 2000;
  const MAX_POLL_MS = 30 * 60 * 1000; // 30 min safety ceiling
  const startedAt = Date.now();
  let lastStep = '';

  /* eslint-disable no-await-in-loop */
  while (true) {
    if (Date.now() - startedAt > MAX_POLL_MS) {
      throw new Error('Sync polling timed out after 30 minutes');
    }
    await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));

    let pollRes;
    try {
      pollRes = await axios.get(`${API}/sync/job/${jobId}`);
    } catch (e) {
      if (e?.response?.status === 404) {
        // Backend restarted (or job aged out). Treat as "we don't know,
        // please refresh." Caller should show a friendly toast.
        const err = new Error('Sync status was lost — backend may have restarted. Please refresh.');
        err.isJobLost = true;
        throw err;
      }
      throw e;
    }

    const data = pollRes?.data || {};
    const status = data.status;
    const step = data.current_step || '';
    if (step && step !== lastStep) {
      lastStep = step;
      if (typeof onProgress === 'function') onProgress(step);
    }

    if (status === 'complete') {
      return data.results || {};
    }
    if (status === 'error') {
      throw new Error(data.error || 'Sync failed');
    }
    // queued / running → keep polling
  }
  /* eslint-enable no-await-in-loop */
}

// ============ Build-name cache (populated from backend, falls back to local map) ============
// Single module-scope cache shared across OS Patch pages. Backend is source of truth.
// Exposes prefetchBuildNames(builds) and getCachedBuildName(build). If the backend call
// fails or a build isn't cached yet, callers fall back to their local estimator.
const __buildNameCache = new Map(); // build -> { name, release_date, version, os, status, servicing, edition_note, confidence, is_current }
let __buildNamePending = null;       // Promise de-dup for in-flight prefetches
const __buildNameInFlight = new Set();

export async function prefetchBuildNames(builds) {
  try {
    if (!Array.isArray(builds) || builds.length === 0) return;
    // Only request builds we haven't cached and aren't already fetching
    const needed = Array.from(new Set(builds.filter(Boolean)))
      .filter(b => !__buildNameCache.has(b) && !__buildNameInFlight.has(b));
    if (needed.length === 0) return;
    needed.forEach(b => __buildNameInFlight.add(b));
    const res = await axios.post(`${API}/catalog/lookup-bulk`, { builds: needed });
    const resolved = res?.data?.builds || {};
    Object.entries(resolved).forEach(([b, info]) => {
      if (info && info.name) __buildNameCache.set(b, info);
    });
  } catch (e) {
    // Silent fallback — frontend local estimator still works
    // eslint-disable-next-line no-console
    console.debug('prefetchBuildNames fallback:', e?.message);
  } finally {
    (builds || []).forEach(b => __buildNameInFlight.delete(b));
  }
}

export function getCachedBuildName(build) {
  const info = __buildNameCache.get(build);
  return info ? info.name : null;
}

export function getCachedBuildInfo(build) {
  return __buildNameCache.get(build) || null;
}

// API client for OS Patch Management
const osPatchApi = {
  getDashboardMetrics: () => axios.get(`${API}/dashboard/metrics`),
  getDevices: () => axios.get(`${API}/devices`),
  syncDevices: () => axios.post(`${API}/sync/devices`),
  searchDevices: (q) => axios.get(`${API}/devices/search?q=${encodeURIComponent(q)}`),
  getAppCategories: () => axios.get(`${API}/app-categories`),
  syncDiscoveredApps: () => axios.post(`${API}/sync/discovered-apps`),
  getDeploymentGroups: () => axios.get(`${API}/deployment-groups`),
  createDeploymentGroup: (data) => axios.post(`${API}/deployment-groups`, data),
  deleteDeploymentGroup: (id, deleteIntuneObjects = false) =>
    axios.delete(`${API}/deployment-groups/${id}?delete_intune_objects=${deleteIntuneObjects}`),
  getDevicesInGroup: (id) => axios.get(`${API}/deployment-groups/${id}/devices`),
  getUpdateRings: () => axios.get(`${API}/update-rings`),
  createUpdateRing: (data) => axios.post(`${API}/update-rings`, data),
  updateUpdateRing: (id, data) => axios.put(`${API}/update-rings/${id}`, data),
  deleteUpdateRing: (id) => axios.delete(`${API}/update-rings/${id}`),
  pauseUpdateRing: (id, pauseType = 'both') => axios.post(`${API}/update-rings/${id}/pause`, { pause_type: pauseType }),
  resumeUpdateRing: (id) => axios.post(`${API}/update-rings/${id}/resume`),
  syncUpdateRings: () => axios.post(`${API}/sync/update-rings`),
  // Feature Updates
  getFeatureUpdateProfiles: () => axios.get(`${API}/feature-updates/profiles`),
  createFeatureUpdateProfile: (data) => axios.post(`${API}/feature-updates/profiles`, data),
  updateFeatureUpdateProfile: (id, data) => axios.put(`${API}/feature-updates/profiles/${id}`, data),
  deleteFeatureUpdateProfile: (id) => axios.delete(`${API}/feature-updates/profiles/${id}`),
  pauseFeatureUpdateProfile: (id) => axios.post(`${API}/feature-updates/profiles/${id}/pause`),
  resumeFeatureUpdateProfile: (id) => axios.post(`${API}/feature-updates/profiles/${id}/resume`),
  syncFeatureUpdates: () => axios.post(`${API}/feature-updates/sync`),
  getFeatureUpdateCatalog: () => axios.get(`${API}/feature-updates/catalog`),
  // Feature Update Staged Deployments
  getFeatureUpdateDeployments: () => axios.get(`${API}/feature-updates/deployments`),
  getFeatureUpdateDeployment: (id) => axios.get(`${API}/feature-updates/deployments/${id}`),
  checkDeviceEligibility: (data) => axios.post(`${API}/feature-updates/deployments/check-eligibility`, data),
  previewFeatureDeployment: (data) => axios.post(`${API}/feature-updates/deployments/preview`, data),
  createFeatureDeployment: (data) => axios.post(`${API}/feature-updates/deployments`, data),
  getDeploymentProgress: (id) => axios.get(`${API}/feature-updates/deployments/${id}/progress`),
  // 02/09 — activating a sprint and scheduling its offer are one decision.
  advanceDeploymentWave: (id, body = {}) =>
    axios.post(`${API}/feature-updates/deployments/${id}/advance-wave`, body),
  reconcileDeploymentMembership: (id, dryRun = false, unownedAction = 'report') =>
    axios.post(`${API}/feature-updates/deployments/${id}/reconcile-membership`, {
      dry_run: dryRun,
      unowned_action: unownedAction,
    }),
  deleteFeatureDeployment: (id) => axios.delete(`${API}/feature-updates/deployments/${id}`),
  getDeploymentPhaseDevices: (id, phase) => axios.get(
    `${API}/feature-updates/deployments/${id}/phase-devices`,
    { params: phase ? { phase } : {} },
  ),
  // New Feature 2 (01/09) — manual deployment selections.
  moveDeploymentDevices: (id, body) =>
    axios.post(`${API}/feature-updates/deployments/${id}/move-devices`, body),
  // `phase` ('test' | 'sprint-N') scopes the change to one sprint; omit it to
  // set every sprint plus the deployment default (01/09 round 2).
  setDeploymentInstallationType: (id, installationType, phase = null) =>
    axios.patch(`${API}/feature-updates/deployments/${id}/installation-type`, {
      installation_type: installationType,
      ...(phase ? { phase } : {}),
    }),
  // 01/09 round 3 — hand-edit one sprint's offer / comms / enforcement dates.
  approveTestGroup: (id, body = {}) =>
    axios.post(`${API}/feature-updates/deployments/${id}/approve-test-group`, body),
  setDeploymentPhaseSchedule: (id, body) =>
    axios.patch(`${API}/feature-updates/deployments/${id}/phase-schedule`, body),
  // Windows feature update readiness + compatibility risks.
  syncReadinessReports: (body = {}) => axios.post(`${API}/feature-updates/readiness/sync`, body),
  getNeedsActionDevices: (risk = null) => axios.get(
    `${API}/feature-updates/readiness/needs-action`, { params: risk ? { risk } : {} },
  ),
  // Firmware Deployments (Phase 4)
  previewFirmwareDeployment: (data) => axios.post(`${API}/os-patch/firmware/deployments/preview`, data),
  createFirmwareDeployment: (data) => axios.post(`${API}/os-patch/firmware/deployments`, data),
  listFirmwareDeployments: (params = {}) => axios.get(`${API}/os-patch/firmware/deployments`, { params }),
  getFirmwareDeployment: (id) => axios.get(`${API}/os-patch/firmware/deployments/${id}`),
  retryFirmwareDeployment: (id) => axios.post(`${API}/os-patch/firmware/deployments/${id}/retry`),
  deleteFirmwareDeployment: (id) => axios.delete(`${API}/os-patch/firmware/deployments/${id}`),
  getFirmwareFilterOptions: () => axios.get(`${API}/os-patch/firmware/devices/filter-options`),
  // Expedited Updates
  getExpeditedUpdates: () => axios.get(`${API}/expedited-updates`),
  createExpeditedUpdate: (data) => axios.post(`${API}/expedited-updates`, data),
  deleteExpeditedUpdate: (id) => axios.delete(`${API}/expedited-updates/${id}`),
  syncExpeditedUpdates: () => axios.post(`${API}/expedited-updates/sync`),
  getAvailableQualityReleases: () => axios.get(`${API}/expedited-updates/available-releases`),
  // Reports
  getComplianceReport: () => axios.get(`${API}/reports/compliance`),
  getUpdateStatusReport: () => axios.get(`${API}/reports/update-status`),
  getRemediationScripts: () => axios.get(`${API}/remediation-scripts`),
  // Remediation Scripts for Stuck Devices (v2)
  getRemediationCatalog: () => axios.get(`${API}/remediation/catalog`),
  getRemediationCatalogEntry: (key) => axios.get(`${API}/remediation/catalog/${key}`),
  // `{ reconcile: false }` skips the Intune comparison (the run modal only needs the list).
  getRemediationScriptsV2: (params = {}) => axios.get(`${API}/remediation/scripts`, { params }),
  createRemediationFromCatalog: (catalog_key) => axios.post(`${API}/remediation/scripts/from-catalog`, { catalog_key }),
  createCustomRemediation: (data) => axios.post(`${API}/remediation/scripts/custom`, data),
  updateRemediation: (id, data) => axios.put(`${API}/remediation/scripts/${id}`, data),
  deleteRemediation: (id) => axios.delete(`${API}/remediation/scripts/${id}`),
  runRemediation: (id, payload) => axios.post(`${API}/remediation/scripts/${id}/run`, payload),
  // `params.device_id` narrows the history to one device (Devices tab panel).
  getRemediationRuns: (params = {}) => axios.get(`${API}/remediation/runs`, { params }),
  // Preview by default; pass { dry_run: false } to apply.
  cleanupRemediationGroups: (body = {}) => axios.post(`${API}/remediation/cleanup`, { dry_run: true, ...body }),
  getRemediationRun: (id, refresh = false) => axios.get(`${API}/remediation/runs/${id}`, { params: { refresh } }),
  // Sync Status & Device Assignments
  getSyncStatus: () => axios.get(`${API}/sync-status`),
  getDeviceAssignments: () => axios.get(`${API}/device-assignments`),
  syncDeviceAssignments: () => axios.post(`${API}/sync/device-assignments`),
  checkRingAssignments: () => axios.get(`${API}/check/ring-assignments`),
  // Device Feature Update Phase
  updateDeviceFeaturePhase: (id, phase) => axios.put(`${API}/devices/${id}/feature-update-phase`, { phase }),
  // 09/09 — staged, bulk phase commit. `dry_run` returns the impact on live
  // deployments (which device leaves which sprint, which returns) without
  // writing anything; the same call without it performs exactly that plan.
  // 09/09 — sprint activation announcement.
  // Rendered server-side through the same branded shell the send uses, so the
  // preview cannot drift from the email.
  previewFeatureAnnouncement: (body) =>
    axios.post(`${API}/feature-updates/announcement-preview`, body),
  setFeatureAnnouncementSettings: (id, body) =>
    axios.patch(`${API}/feature-updates/deployments/${id}/announcement-settings`, body),
  announceFeaturePhase: (id, phase) =>
    axios.post(`${API}/feature-updates/deployments/${id}/announce`, { phase }),
  applyDeviceFeaturePhases: (changes, { dryRun = false } = {}) =>
    axios.post(`${API}/devices/feature-update-phase/apply`, {
      changes, dry_run: dryRun, apply_to_deployments: true,
    }),
  bulkUpdateDeviceFeaturePhase: (updates) => axios.put(`${API}/devices/bulk-feature-update-phase`, { updates }),
  // WUfB Integration
  syncClientStates: () => axios.post(`${API}/wufb-integration/sync-client-states`),
  getClientStatesSummary: () => axios.get(`${API}/wufb-integration/client-states-summary`),
  // Windows 11 Readiness
  getWin11Readiness: () => axios.get(`${API}/reports/win11-readiness`),
  getWin11ReadinessDevices: (filter, search, page) => axios.get(`${API}/reports/win11-readiness/devices`, { 
    params: { filter, search, page } 
  }),
  exportWin11Readiness: (format, filter) => axios.get(`${API}/reports/win11-readiness/export`, {
    params: { format, filter },
    responseType: format === 'csv' ? 'blob' : 'json'
  }),
  checkWin11ReadinessAvailability: () => axios.get(`${API}/reports/win11-readiness/check-availability`),
  // Firmware Analysis & Updates — Phase 1 (Reporting)
  getFirmwareSummary: () => axios.get(`${API}/firmware/summary`),
  getFirmwareDevices: (params = {}) => axios.get(`${API}/firmware/devices`, { params }),
  // `scott/feature-updates-3` — per-tenant configurable Patch Adoption Curve target.
  // Note: the `API` const above already terminates in `/api/os-patch`, so the
  // path here is RELATIVE to that — do NOT prefix with /os-patch again.
  getComplianceTargetCurve: () => axios.get(`${API}/reports/compliance-target-curve`),
  saveComplianceTargetCurve: (body) => axios.put(`${API}/reports/compliance-target-curve`, body),
};

// Shared styles
const CHART_COLORS = {
  cyan: '#00D4E4',
  lime: '#BBFF71',
  coral: '#FC5442',
  blue: '#6376F0',
  purple: '#8B5CF6',
};

// Feature Update Phase colours and labels
// 09/09 — `poc` is new. `blue` used to mean "Test / Sprint 1" — both at once —
// so a customer running a real POC could not keep their nominated pilot
// machines separate from the far larger Sprint 1 population. They are now
// distinct phases. `short` is what the segmented control renders; `label` is
// the full name used in legends, tooltips and the commit summary.
//
// 7 Oct 2026 — colours chosen to stay distinct as 12-14px circles on the dark
// glass background (the Devices SPRINT column): POC and Sprint 1 were violet
// and purple, which read as one colour at that size. All five are light, so
// anything drawn ON the colour uses `onColor` (dark text) rather than white.
// Hex values as Tailwind arbitrary classes — they must stay full literals here
// so Tailwind's scanner generates them.
const PHASE_CONFIG = {
  poc: { color: 'bg-[#00D4FF]', ring: 'ring-[#00D4FF]', label: 'Test (POC)', short: 'POC', textColor: 'text-[#00D4FF]',
    onColor: 'text-slate-900',
    hint: 'Nominated pilot machines. Fills the test group; any that do not fit roll into Sprint 1.' },
  blue: { color: 'bg-[#FF4FD8]', ring: 'ring-[#FF4FD8]', label: 'Sprint 1', short: 'S1', textColor: 'text-[#FF4FD8]',
    onColor: 'text-slate-900',
    hint: 'First sprint of the main rollout.' },
  green: { color: 'bg-[#22C55E]', ring: 'ring-[#22C55E]', label: 'Main Deployment', short: 'Main', textColor: 'text-[#22C55E]',
    onColor: 'text-slate-900',
    hint: 'The default. Allocated across the middle sprints.' },
  amber: { color: 'bg-[#FFB020]', ring: 'ring-[#FFB020]', label: 'Last Sprint', short: 'Last', textColor: 'text-[#FFB020]',
    onColor: 'text-slate-900',
    hint: 'Held back to the final sprint.' },
  red: { color: 'bg-[#FF5252]', ring: 'ring-[#FF5252]', label: 'Exclude', short: 'Excl', textColor: 'text-[#FF5252]',
    onColor: 'text-slate-900',
    hint: 'Out of feature updates entirely. On commit, also removed from any sprint it is currently in.' },
};
const PHASE_KEYS = Object.keys(PHASE_CONFIG);

// Feature Update Phase Selector — compact labelled segmented control.
//
// 09/09: this was a row of four coloured dots that PUT on every click. Two
// things changed and both forced a rethink of the control:
//
//   * A fifth phase. Five colours is past what anyone reliably tells apart,
//     especially at 16px with no text, so the segments carry short labels.
//   * The change now has consequences. Setting a device to Exclude removes it
//     from the sprints it is in, and Intune stops offering it the update. That
//     must not fire on a click while an admin is working down a list of 40
//     machines — so a click now records a PENDING change (dashed outline) and
//     nothing is written until the commit bar is used.
//
// `pendingPhase` is the staged value; `currentPhase` is what is saved. When
// they differ the control shows the pending one and marks itself dirty.
const FeatureUpdatePhaseSelector = ({
  deviceId, currentPhase = 'green', pendingPhase = null, onStageChange, disabled = false,
}) => {
  const effective = pendingPhase || currentPhase;
  const dirty = Boolean(pendingPhase && pendingPhase !== currentPhase);

  return (
    <div
      className={`inline-flex items-stretch rounded-md overflow-hidden border ${
        dirty ? 'border-dashed border-cyan-400/70' : 'border-white/15'
      }`}
      title={dirty ? `Pending: ${PHASE_CONFIG[effective]?.label} (not yet committed)` : undefined}
      data-testid={`phase-selector-${deviceId}`}
    >
      {PHASE_KEYS.map((phase) => {
        const cfg = PHASE_CONFIG[phase];
        const active = effective === phase;
        return (
          <button
            key={phase}
            type="button"
            onClick={() => !disabled && onStageChange(deviceId, phase)}
            disabled={disabled}
            title={`${cfg.label} — ${cfg.hint}`}
            className={`px-2 py-1 text-[10px] font-semibold uppercase tracking-wide transition-colors border-r last:border-r-0 border-white/10 ${
              active
                ? `${cfg.color} ${cfg.onColor || 'text-white'}`
                : 'bg-transparent text-white/40 hover:text-white/80 hover:bg-white/5'
            } ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
            data-testid={`phase-dot-${phase}-${deviceId}`}
          >
            {cfg.short}
          </button>
        );
      })}
    </div>
  );
};

// Feature Update Phase as coloured circles — the Discover > Devices SPRINT
// column (6 Oct 2026: back to circles in the list, hover for the name; the
// labelled segmented control above stays in the device panel). Same staging
// rules: a click records a pending change, shown dashed, and nothing is saved
// until the commit bar. Read-only users get the one circle for the device's
// sprint.
const FeatureUpdatePhaseDots = ({
  deviceId, currentPhase = 'green', pendingPhase = null, onStageChange, readOnly = false,
}) => {
  const effective = pendingPhase || currentPhase;
  const dirty = Boolean(pendingPhase && pendingPhase !== currentPhase);
  if (readOnly) {
    const cfg = PHASE_CONFIG[effective] || PHASE_CONFIG.green;
    return (
      <span
        className={`inline-block w-3.5 h-3.5 rounded-full ${cfg.color}`}
        title={cfg.label}
        aria-label={`Sprint: ${cfg.label}`}
        data-testid={`phase-readonly-${deviceId}`}
      />
    );
  }
  return (
    <div
      className="inline-flex items-center gap-2"
      title={dirty ? `Pending: ${PHASE_CONFIG[effective]?.label} (not yet committed)` : undefined}
      data-testid={`phase-selector-${deviceId}`}
    >
      {PHASE_KEYS.map((phase) => {
        const cfg = PHASE_CONFIG[phase];
        const active = effective === phase;
        return (
          <button
            key={phase}
            type="button"
            onClick={() => onStageChange(deviceId, phase)}
            title={`${cfg.label} — ${cfg.hint}`}
            aria-label={cfg.label}
            aria-pressed={active}
            className={`w-3.5 h-3.5 rounded-full transition-all ${cfg.color} ${
              active
                ? (dirty
                  ? 'outline outline-2 outline-dashed outline-cyan-300 outline-offset-2'
                  : `ring-2 ring-offset-2 ring-offset-slate-900 ${cfg.ring}`)
                : 'opacity-25 hover:opacity-70'
            }`}
            data-testid={`phase-dot-${phase}-${deviceId}`}
          />
        );
      })}
    </div>
  );
};

// KPI Card Component
export const KPICard =({ label, value, subtitle, icon: Icon, accentColor = CHART_COLORS.cyan }) => (
  <Card className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border-[rgba(229,239,250,0.1)]">
    <CardContent className="p-6">
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm font-medium uppercase tracking-wider text-white/90">{label}</span>
        {Icon && (
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${accentColor}20` }}>
            <Icon className="w-5 h-5" style={{ color: accentColor }} />
          </div>
        )}
      </div>
      <div className="text-4xl font-bold text-white mb-1">{value}</div>
      {subtitle && <div className="text-sm text-white/90">{subtitle}</div>}
    </CardContent>
  </Card>
);

// Shown in place of a policy page (update rings, feature/expedited updates,
// remediation) for tenants on the reporting-only OS Patch plan. Those tenants
// have no permission to read Intune update policies, so the backend refuses
// the endpoints these pages depend on; the page renders this and calls nothing.
const OSPatchPolicyUnavailable = ({ title, message, testId }) => (
  <div className="p-8" data-testid={testId}>
    <h1 className="text-3xl font-bold text-white mb-4">{title}</h1>
    <Card className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border-[rgba(229,239,250,0.1)] max-w-2xl">
      <CardContent className="p-6 flex items-start gap-3">
        <Info className="w-5 h-5 text-cyan-400 mt-0.5 shrink-0" />
        <div>
          <p className="text-white">{message}</p>
          <p className="text-sm text-white/70 mt-2">
            Device, compliance and firmware reporting remain available. Contact your ALICE administrator to discuss upgrading your plan.
          </p>
        </div>
      </CardContent>
    </Card>
  </div>
);

// Firmware status chip — used in the Devices grid (BIOS column) and the
// Reports → Firmware drill-in table. Catalog-aware: lights up green/amber/
// red when the central OEM catalog matches, "Virtual" for Hyper-V / Cloud
// PC rows, and "Unknown" with a tooltip explaining why for the long tail.
export const FirmwareStatusChip = ({ status, reason }) => {
  const s = (status || 'unknown').toLowerCase();
  const config = {
    on_latest:          { label: 'On latest',          cls: 'bg-green-500/20 text-green-400' },
    ahead_of_catalogue: { label: 'Ahead of catalogue', cls: 'bg-amber-500/20 text-amber-300' },
    missing_one:        { label: 'Missing 1',          cls: 'bg-amber-500/20 text-amber-400' },
    missing_multiple:   { label: 'Missing multiple',   cls: 'bg-red-500/20 text-red-400' },
    virtual:            { label: 'Virtual',            cls: 'bg-purple-500/20 text-purple-300' },
    unknown:            { label: 'Unknown',            cls: 'bg-white/10 text-white/70' },
  }[s] || { label: 'Unknown', cls: 'bg-white/10 text-white/70' };
  // Show a tooltip explaining the "ahead of catalogue" state so admins
  // know the device isn't actually behind — the OEM feed is.
  const tooltipText = reason || (
    s === 'ahead_of_catalogue'
      ? 'Device BIOS version is higher than the newest catalogue entry — your OEM feed may be stale. Re-sync the catalogue or import the latest version via CSV.'
      : undefined
  );
  return (
    <span
      className={`px-2 py-1 rounded-full text-xs font-medium ${config.cls}`}
      data-testid={`firmware-status-chip-${s}`}
      title={tooltipText}
    >
      {config.label}
    </span>
  );
};

// Loading Spinner
const LoadingSpinner = ({ message = "Loading..." }) => (
  <div className="flex flex-col items-center justify-center p-12">
    <div className="w-12 h-12 rounded-full border-4 border-[rgba(229,239,250,0.1)] border-t-cyan-400 animate-spin mb-4" />
    <span className="text-white/90">{message}</span>
  </div>
);

// Progress Overlay - shown during long-running creation operations
const CreationProgressOverlay = ({ message = "Creating...", detail = "Please wait while resources are provisioned in Azure AD" }) => (
  <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60]" data-testid="creation-progress-overlay">
    <Card className="alice-popup bg-slate-900 border-[rgba(229,239,250,0.1)] w-full max-w-md mx-4">
      <CardContent className="p-8 text-center">
        <div className="w-14 h-14 rounded-full border-4 border-[rgba(229,239,250,0.1)] border-t-purple-400 border-r-cyan-400 animate-spin mx-auto mb-5" />
        <h3 className="text-white font-medium text-lg mb-2">{message}</h3>
        <p className="text-white/60 text-sm mb-4">{detail}</p>
        <div className="w-full bg-white/10 rounded-full h-1.5 overflow-hidden">
          <div className="h-full bg-gradient-to-r from-purple-500 via-cyan-400 to-purple-500 rounded-full animate-pulse" style={{ width: '100%', animation: 'pulse 1.5s ease-in-out infinite' }} />
        </div>
        <p className="text-white/40 text-xs mt-3">Do not navigate away from this page</p>
      </CardContent>
    </Card>
  </div>
);

// Sync Status Indicator Component
export const SyncStatusIndicator = ({ lastSync, label = "Last synced from Intune" }) => {
  if (!lastSync) return null;
  
  const syncDate = new Date(lastSync);
  const now = new Date();
  const diffMs = now - syncDate;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  let timeAgo;
  if (diffMins < 1) {
    timeAgo = 'just now';
  } else if (diffMins < 60) {
    timeAgo = `${diffMins}m ago`;
  } else if (diffHours < 24) {
    timeAgo = `${diffHours}h ago`;
  } else {
    timeAgo = `${diffDays}d ago`;
  }
  
  const isStale = diffHours > 24; // Mark as stale if more than 24 hours old
  
  return (
    <div className={`flex items-center gap-2 text-sm ${isStale ? 'text-amber-400' : 'text-white/90'}`}>
      <Clock className="w-4 h-4" />
      <span>{label}: <span className="font-medium">{timeAgo}</span></span>
      <span className="text-white/70">({syncDate.toLocaleString()})</span>
    </div>
  );
};

// Pagination Component with Page Size Selection
export const PaginationControls = ({ 
  currentPage, 
  totalItems, 
  pageSize, 
  onPageChange, 
  onPageSizeChange,
  pageSizeOptions = [20, 100, 500]
}) => {
  const totalPages = Math.ceil(totalItems / pageSize);
  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems);
  
  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 border-t border-[rgba(229,239,250,0.1)]">
      <div className="flex items-center gap-4">
        <span className="text-sm text-white/70">
          Showing {startItem}-{endItem} of {totalItems}
        </span>
        <div className="flex items-center gap-2">
          <span className="text-sm text-white/70">Per page:</span>
          <select
            value={pageSize}
            onChange={(e) => {
              onPageSizeChange(Number(e.target.value));
              onPageChange(1); // Reset to first page when changing page size
            }}
            className="bg-[rgba(229,239,250,0.1)] border border-[rgba(229,239,250,0.1)] text-white rounded-md px-2 py-1 text-sm"
            data-testid="page-size-select"
          >
            {pageSizeOptions.map(size => (
              <option key={size} value={size} className="bg-slate-800">{size}</option>
            ))}
          </select>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage <= 1}
          className="border-[rgba(229,239,250,0.2)] text-white/90 hover:bg-white/10 disabled:opacity-50"
        >
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <span className="text-sm text-white/90 px-3">
          Page {currentPage} of {totalPages || 1}
        </span>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage >= totalPages}
          className="border-[rgba(229,239,250,0.2)] text-white/90 hover:bg-white/10 disabled:opacity-50"
        >
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

// CSV Export Helper Function
const exportToCSV = (data, columns, filename) => {
  if (!data || data.length === 0) {
    toast.error('No data to export');
    return;
  }
  
  // Build CSV header
  const header = columns.map(col => col.header).join(',');
  
  // Build CSV rows
  const rows = data.map(row => 
    columns.map(col => {
      let value = col.accessor(row);
      // Handle null/undefined
      if (value === null || value === undefined) value = '';
      // Escape quotes and wrap in quotes if contains comma, quote, or newline
      value = String(value);
      if (value.includes(',') || value.includes('"') || value.includes('\n')) {
        value = `"${value.replace(/"/g, '""')}"`;
      }
      return value;
    }).join(',')
  );
  
  const csvContent = [header, ...rows].join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
  link.click();
  URL.revokeObjectURL(url);
  toast.success('Export completed');
};

// ==================== OS PATCH DASHBOARD ====================
// ============ Next Patch Tuesday Countdown Badge ============
// Computes the upcoming Patch Tuesday and renders a small at-a-glance pill.
// - Default tone: cyan (informational)
// - Within 7 days: amber (heads-up window)
// - Within 24h or today: red (imminent / day-of)
// - Re-evaluates every minute so the day count stays current without a refresh.
const NextPatchTuesdayBadge = () => {
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 60_000);
    return () => clearInterval(id);
  }, []);

  const now = new Date();
  // Use UTC math to keep behaviour stable across timezones.
  const todayUTC = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));

  // PT helper (kept self-contained so this component can move anywhere).
  const ptOf = (y, m) => {
    const firstWeekday = new Date(Date.UTC(y, m, 1)).getUTCDay();
    const daysToFirstTue = (2 - firstWeekday + 7) % 7;
    return new Date(Date.UTC(y, m, 1 + daysToFirstTue + 7));
  };

  // This month's Patch Tuesday.
  let target = ptOf(todayUTC.getUTCFullYear(), todayUTC.getUTCMonth());
  // If we're past it, roll to next month's.
  if (todayUTC > target) {
    const nm = todayUTC.getUTCMonth() + 1;
    target = ptOf(
      todayUTC.getUTCFullYear() + (nm > 11 ? 1 : 0),
      nm > 11 ? 0 : nm
    );
  }

  const msPerDay = 86_400_000;
  const daysLeft = Math.round((target.getTime() - todayUTC.getTime()) / msPerDay);
  const isToday = daysLeft === 0;
  const isImminent = daysLeft <= 1; // today or tomorrow
  const isSoon = daysLeft <= 7;

  // Tone selection
  const tone = isImminent
    ? { bg: 'bg-rose-500/15', border: 'border-rose-400/40', dot: 'bg-rose-300', text: 'text-rose-200', pulse: true }
    : isSoon
      ? { bg: 'bg-amber-500/15', border: 'border-amber-400/40', dot: 'bg-amber-400', text: 'text-amber-100', pulse: false }
      : { bg: 'bg-cyan-500/12', border: 'border-cyan-400/30', dot: 'bg-cyan-300', text: 'text-cyan-100', pulse: false };

  const dateLabel = target.toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC',
  });
  const headline = isToday
    ? 'Patch Tuesday TODAY'
    : daysLeft === 1
      ? 'Patch Tuesday tomorrow'
      : `Next Patch Tuesday in ${daysLeft} day${daysLeft === 1 ? '' : 's'}`;

  return (
    <div
      className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border ${tone.bg} ${tone.border} ${tone.text} text-xs`}
      data-testid="next-patch-tuesday-badge"
      title={`Microsoft releases Windows security updates on the 2nd Tuesday of each month. Next: ${dateLabel}.`}
    >
      <span className={`relative inline-flex h-2 w-2 rounded-full ${tone.dot}`}>
        {tone.pulse && (
          <span className={`absolute inline-flex h-full w-full rounded-full ${tone.dot} opacity-75 animate-ping`} />
        )}
      </span>
      <span className="font-medium">{headline}</span>
      <span className="opacity-60">·</span>
      <span className="font-mono">{dateLabel}</span>
    </div>
  );
};

// ============ Inline Info Popover (click-to-toggle, accessible) ============
const InfoPopover = ({ label, children, align = 'left' }) => {
  const [open, setOpen] = useState(false);
  return (
    <span className="relative inline-flex items-center">
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(v => !v); }}
        className="inline-flex items-center justify-center w-4 h-4 rounded-full text-cyan-300/70 hover:text-cyan-200 hover:bg-cyan-400/10 transition focus:outline-none focus:ring-2 focus:ring-cyan-400/40"
        aria-label={`Info: ${label || 'help'}`}
        aria-expanded={open}
        data-testid={`info-icon-${(label || 'item').toLowerCase().replace(/\s+/g, '-')}`}
      >
        <Info className="w-3.5 h-3.5" />
      </button>
      {open && (
        <>
          {/* Dismiss layer */}
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div
            className={`absolute top-full mt-2 ${align === 'right' ? 'right-0' : 'left-0'} z-50 w-[320px] p-3 rounded-lg border border-cyan-400/30 bg-slate-900/95 backdrop-blur shadow-xl text-xs text-white/85 leading-relaxed`}
            role="tooltip"
            data-testid={`info-panel-${(label || 'item').toLowerCase().replace(/\s+/g, '-')}`}
          >
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-cyan-300 font-semibold uppercase tracking-wider text-[10px]">{label || 'Info'}</span>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setOpen(false); }}
                className="text-white/40 hover:text-white/80"
                aria-label="Close"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
            <div className="space-y-1.5">{children}</div>
          </div>
        </>
      )}
    </span>
  );
};

// ============ Compliance Trending — Sections A / B / C ============

// Compute the 2nd Tuesday of a given YYYY-MM string (Microsoft Patch Tuesday).
const patchTuesdayOf = (monthId) => {
  if (!monthId || typeof monthId !== 'string' || !monthId.includes('-')) return null;
  const [y, m] = monthId.split('-').map(Number);
  if (!y || !m) return null;
  // First-of-month weekday (0=Sun..6=Sat in JS Date)
  const firstWeekday = new Date(Date.UTC(y, m - 1, 1)).getUTCDay();
  // Days until first Tuesday (Tue = 2 in JS)
  const daysToFirstTue = (2 - firstWeekday + 7) % 7;
  const firstTuesdayDate = 1 + daysToFirstTue;
  return new Date(Date.UTC(y, m - 1, firstTuesdayDate + 7));
};

// Format a Date as "Apr 21 2026" (short, neutral, locale-stable for screenshots).
const fmtShortDate = (d) => {
  if (!d) return '—';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
};

// `scott/kpi-cycle-update` — how a Patch Cycle Completion Rate was arrived at.
// Operators need to know when a figure is the real per-device supersedence
// measurement versus a fallback, because the fallbacks are conservative and
// will read slightly LOW for tenants running long deferrals.
const CYCLE_COMPLETION_BASIS_LABELS = {
  supersedence: ' · measured at each ring’s supersedence date',
  supersedence_approx: ' · nearest snapshot before each ring’s supersedence date',
  wufb: ' · WUfB measured at each ring’s supersedence date',
  wufb_no_ring_map: ' · WUfB measured at the next Patch Tuesday (no ring map)',
  cycle_end_proxy: ' · measured at cycle close (pre-dates per-ring history)',
  projected: ' · projected',
};
const cycleCompletionBasisLabel = (basis) => CYCLE_COMPLETION_BASIS_LABELS[basis] || '';
// =============================================================================
// PatchAdoptionTargetCurveEditor — inline popover that lets a tenant admin
// pick the target curve preset (Default / Conservative / Aggressive) or build
// a custom anchor list (day → pct). Powers the Patch Adoption Curve dashed
// "Recommended Target" line. Per-tenant — saved via
// PUT /api/os-patch/reports/compliance-target-curve.
//
// `scott/feature-updates-3` — Monthly Compliance Trend reference lines are
// deliberately NOT touched (per user feedback they stay at 95% / 90% floor).
// =============================================================================
const PRESET_DESCRIPTIONS = {
  default: 'Microsoft WUfB guidance — 60% by Day 7, 80% by Day 14, 95% by Day 30',
  conservative: 'Slower roll-out — 50% by Day 7, 70% by Day 14, 90% by Day 30',
  aggressive: 'Fast roll-out — 50% by Day 3, 75% by Day 7, 90% by Day 14, 99% by Day 30',
};

const PatchAdoptionTargetCurveEditor = ({ curve, onSave }) => {
  const [open, setOpen] = useState(false);
  const [draftPreset, setDraftPreset] = useState(curve?.preset || 'default');
  const [draftAnchors, setDraftAnchors] = useState(curve?.anchors || []);
  const [saving, setSaving] = useState(false);

  // Re-seed local draft when the parent's curve prop refreshes (e.g. after save).
  useEffect(() => {
    setDraftPreset(curve?.preset || 'default');
    setDraftAnchors(curve?.anchors || []);
  }, [curve]);

  const presets = curve?.presets || {};
  const isCustom = draftPreset === 'custom';

  const handleSelectPreset = (preset) => {
    setDraftPreset(preset);
    if (preset !== 'custom' && presets[preset]) {
      setDraftAnchors(presets[preset]);
    } else if (preset === 'custom' && draftAnchors.length < 2) {
      // Seed Custom with the current curve so the user can edit, not start from blank.
      setDraftAnchors(curve?.anchors || presets.default || []);
    }
  };

  const updateAnchor = (idx, field, value) => {
    setDraftAnchors(prev => prev.map((a, i) => i === idx ? { ...a, [field]: value === '' ? '' : Number(value) } : a));
  };

  const addAnchor = () => {
    const used = new Set(draftAnchors.map(a => Number(a.day)));
    const next = [3, 7, 10, 14, 21, 30].find(d => !used.has(d)) ?? 30;
    setDraftAnchors(prev => [...prev, { day: next, pct: 80 }].sort((a, b) => a.day - b.day));
  };

  const removeAnchor = (idx) => {
    setDraftAnchors(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSave = async () => {
    if (isCustom) {
      const valid = draftAnchors.filter(a =>
        Number.isFinite(Number(a.day)) && Number.isFinite(Number(a.pct))
        && Number(a.day) >= 0 && Number(a.day) <= 30
        && Number(a.pct) >= 0 && Number(a.pct) <= 100
      );
      if (valid.length < 2) {
        toast.error('Add at least two anchors (e.g. Day 0 + Day 14) to save a custom curve');
        return;
      }
    }
    setSaving(true);
    try {
      await onSave(draftPreset, isCustom ? draftAnchors : null);
      setOpen(false);
    } catch (_e) {
      // toast already shown in the parent handler
    } finally {
      setSaving(false);
    }
  };

  const activePreset = curve?.preset || 'default';
  const activePresetLabel = activePreset === 'custom'
    ? 'Custom'
    : activePreset.charAt(0).toUpperCase() + activePreset.slice(1);

  return (
    <div className="relative inline-block" data-testid="target-curve-editor">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-amber-400/40 bg-amber-500/10 hover:bg-amber-500/20 text-amber-200 text-[11px] font-medium transition-colors"
        data-testid="target-curve-toggle"
      >
        <Sliders className="w-3 h-3" />
        Target curve: <span className="font-semibold">{activePresetLabel}</span>
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          className="alice-menu absolute z-50 right-0 mt-2 w-[420px] rounded-xl border border-[rgba(229,239,250,0.18)] bg-slate-900/95 backdrop-blur-md shadow-2xl p-4 space-y-3"
          data-testid="target-curve-editor-popover"
        >
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-white">Patch Adoption target curve</p>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-white/50 hover:text-white"
              data-testid="target-curve-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <p className="text-[11px] text-white/55">
            Customer-configurable target for the dashed amber line on the Patch Adoption Curve.
            Per-tenant — applies to every admin in this tenant. Patch Cycle Completion Trend lines
            (95% / 90%) are unchanged.
          </p>

          {/* Preset buttons */}
          <div className="grid grid-cols-2 gap-2">
            {['default', 'conservative', 'aggressive', 'custom'].map(p => (
              <button
                key={p}
                type="button"
                onClick={() => handleSelectPreset(p)}
                className={`text-left px-3 py-2 rounded-lg border text-xs transition-colors ${
                  draftPreset === p
                    ? 'border-amber-400/60 bg-amber-500/15 text-amber-100'
                    : 'border-white/10 bg-white/5 text-white/75 hover:bg-white/10'
                }`}
                data-testid={`target-curve-preset-${p}`}
              >
                <div className="font-semibold capitalize">{p}</div>
                <div className="text-[10px] text-white/55 mt-0.5">
                  {p === 'custom' ? 'Pick your own anchor days + %s' : PRESET_DESCRIPTIONS[p]}
                </div>
              </button>
            ))}
          </div>

          {/* Anchor editor — read-only for presets, editable when Custom */}
          <div className="rounded-lg border border-white/10 bg-white/[0.03] p-2">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[11px] uppercase tracking-wider text-white/60">Anchors (day → %)</p>
              {isCustom && (
                <button
                  type="button"
                  onClick={addAnchor}
                  className="text-[11px] text-cyan-300 hover:text-cyan-200"
                  data-testid="target-curve-add-anchor"
                >
                  + Add anchor
                </button>
              )}
            </div>
            <div className="space-y-1.5 max-h-48 overflow-y-auto">
              {draftAnchors.length === 0 && (
                <p className="text-[11px] text-white/40 text-center py-2">No anchors defined.</p>
              )}
              {draftAnchors.map((a, idx) => (
                <div key={`${idx}-${a.day}`} className="flex items-center gap-2 text-xs" data-testid={`target-curve-anchor-row-${idx}`}>
                  <span className="text-white/55 w-10">Day</span>
                  <input
                    type="number"
                    min={0}
                    max={30}
                    value={a.day}
                    disabled={!isCustom}
                    onChange={(e) => updateAnchor(idx, 'day', e.target.value)}
                    className="w-16 bg-white/10 border border-white/15 rounded px-2 py-1 text-white disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid={`target-curve-anchor-day-${idx}`}
                  />
                  <span className="text-white/55">→</span>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    step={0.5}
                    value={a.pct}
                    disabled={!isCustom}
                    onChange={(e) => updateAnchor(idx, 'pct', e.target.value)}
                    className="w-20 bg-white/10 border border-white/15 rounded px-2 py-1 text-white disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid={`target-curve-anchor-pct-${idx}`}
                  />
                  <span className="text-white/55">%</span>
                  {isCustom && draftAnchors.length > 2 && (
                    <button
                      type="button"
                      onClick={() => removeAnchor(idx)}
                      className="ml-auto text-red-400/70 hover:text-red-400"
                      data-testid={`target-curve-remove-anchor-${idx}`}
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            {isCustom && (
              <p className="text-[10px] text-white/40 mt-2">
                Tip: keep day values between 0–30 and percentages between 0–100. The chart linearly
                interpolates between adjacent anchors.
              </p>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between gap-2 pt-1">
            <p className="text-[10px] text-white/40">
              {curve?.updated_at ? `Last saved ${new Date(curve.updated_at).toLocaleString()}` : 'Not saved yet'}
            </p>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setOpen(false)}
                className="h-7 px-3 text-xs border-white/15 text-white hover:bg-white/10"
                data-testid="target-curve-cancel"
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleSave}
                disabled={saving}
                className="h-7 px-3 text-xs bg-amber-500/20 border border-amber-400/40 text-amber-100 hover:bg-amber-500/30"
                data-testid="target-curve-save"
              >
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};




// =============================================================================
// `scott/deployment-kpi-bugfix` — Snapshot health / stale-row maintenance.
//
// Rows written before the catalog-aware resolver shipped can carry an inflated
// compliance % (the static estimator could date a build into the future, which
// then satisfied every cycle's compliance test — e.g. 10.0.26100.9106 read as
// "2026-09"). They can't be recomputed: snapshots have no historical mode, they
// read CURRENT device state and stamp today's date. So the honest remedy is to
// delete them and let the curve start from the first correctly-resolved day.
//
// Two-step by design — preview what would go, then confirm. Rows already
// resolved against the synced Windows Update catalog are never offered up.
// =============================================================================
const SnapshotHealthPopover = ({ report, loading, onInspect, onPurge }) => {
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    setConfirming(false);
    if (next) await onInspect();
  };

  const staleCount = report?.stale_count ?? null;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={toggle}
        className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md border border-white/15 bg-white/[0.06] text-[11px] text-white/70 hover:text-white hover:bg-white/[0.12] transition"
        data-testid="snapshot-health-toggle"
        title="Check this cycle's snapshot rows for devices whose build could not be positively dated"
      >
        <Database className="w-3 h-3" />
        Snapshot health
        {staleCount > 0 && (
          <span
            className="ml-0.5 px-1.5 rounded-full bg-amber-500/25 text-amber-200 font-semibold"
            data-testid="snapshot-health-badge"
          >
            {staleCount}
          </span>
        )}
      </button>

      {open && (
        <div
          className="alice-menu absolute right-0 top-8 z-50 w-[420px] max-h-[420px] overflow-y-auto rounded-xl border border-white/15 bg-slate-900/95 backdrop-blur-md shadow-2xl p-4 text-xs text-white/80 space-y-3"
          data-testid="snapshot-health-panel"
        >
          <div className="flex items-start justify-between gap-2">
            <p className="font-semibold text-white text-sm">Snapshot health</p>
            <button
              type="button"
              onClick={() => { setOpen(false); setConfirming(false); }}
              className="text-white/50 hover:text-white"
              data-testid="snapshot-health-close"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>

          {loading && !report && (
            <p className="flex items-center gap-2 text-white/60">
              <Loader2 className="w-3 h-3 animate-spin" /> Reading snapshots…
            </p>
          )}

          {report && (
            <>
              <div className="text-[11px] text-white/50 leading-relaxed">
                Cycle <span className="font-mono text-white/75">{report.month_id}</span>
                {report.patch_tuesday ? ` · Patch Tuesday ${report.patch_tuesday}` : ''}
                <br />
                Tenant DB <span className="font-mono text-white/75">{report.tenant_db || '—'}</span>
                {report.snapshots_in_db != null && (
                  <> · <span className="font-mono text-white/75">{report.snapshots_in_db}</span> rows total</>
                )}
              </div>

              {staleCount === 0 ? (
                <p
                  className="flex items-start gap-2 rounded-lg border border-emerald-400/30 bg-emerald-500/10 p-2.5 text-emerald-200"
                  data-testid="snapshot-health-clean"
                >
                  <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <span>
                    This cycle is clean — all {report.healthy_count} row
                    {report.healthy_count === 1 ? '' : 's'} were resolved against the
                    synced Windows Update catalog, with every device's build positively dated.
                  </span>
                </p>
              ) : (
                <>
                  <p className="flex items-start gap-2 rounded-lg border border-amber-400/30 bg-amber-500/10 p-2.5 text-amber-200">
                    <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    <span>
                      <strong>{staleCount}</strong> row{staleCount === 1 ? '' : 's'} contain devices
                      whose build couldn't be dated from the catalog or a real anchor. Those
                      builds were dated to the current Patch-Tuesday month, which scored them
                      compliant for the exact cycle being plotted — so these rows read high, and
                      the curve drops sharply on whichever day the catalog finally covered them.
                      They can't be recomputed (snapshots capture device state at write time), so
                      removing them lets the curve start from the first fully-resolved day.
                      Sync the Windows Update catalog first, or the next snapshot will be stale too.
                    </span>
                  </p>

                  <div className="rounded-lg border border-white/10 overflow-hidden">
                    <table className="w-full text-[11px]" data-testid="snapshot-health-table">
                      <thead className="bg-white/[0.06] text-white/60">
                        <tr>
                          <th className="text-left px-2 py-1 font-medium">Date</th>
                          <th className="text-left px-2 py-1 font-medium">Day</th>
                          <th className="text-left px-2 py-1 font-medium">Scope</th>
                          <th className="text-right px-2 py-1 font-medium">Unverified</th>
                          <th className="text-right px-2 py-1 font-medium">Reported</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(report.stale || []).map((r) => (
                          <tr key={r._id} className="border-t border-white/5">
                            <td className="px-2 py-1 font-mono text-white/80">{r.date}</td>
                            <td className="px-2 py-1 text-white/60">+{r.day_offset}</td>
                            <td className="px-2 py-1 text-white/60 truncate max-w-[120px]">{r.ring_name || 'Overall'}</td>
                            <td
                              className="px-2 py-1 text-right text-white/60"
                              title={r.stale_reason || ''}
                            >
                              {r.release_month_resolution
                                ? `${r.release_month_resolution.unverified} (${r.release_month_resolution.unverified_pct}%)`
                                : 'unknown'}
                            </td>
                            <td className="px-2 py-1 text-right text-amber-200 font-semibold">
                              {r.compliance_pct == null ? '—' : `${r.compliance_pct}%`}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {report.healthy_count > 0 && (
                    <p className="text-[11px] text-white/45">
                      {report.healthy_count} catalog-resolved row
                      {report.healthy_count === 1 ? '' : 's'} in this cycle will be kept.
                      Today's snapshot is rewritten immediately after.
                    </p>
                  )}

                  {!confirming ? (
                    <Button
                      onClick={() => setConfirming(true)}
                      disabled={loading}
                      className="w-full bg-amber-500/20 border border-amber-400/40 text-amber-100 hover:bg-amber-500/30 text-xs h-8"
                      data-testid="snapshot-health-purge-btn"
                    >
                      <Trash2 className="w-3 h-3 mr-1.5" />
                      Remove {staleCount} stale row{staleCount === 1 ? '' : 's'}
                    </Button>
                  ) : (
                    <div className="space-y-2" data-testid="snapshot-health-confirm">
                      <p className="text-[11px] text-white/70">
                        This permanently deletes {staleCount} snapshot row
                        {staleCount === 1 ? '' : 's'} from the {report.month_id} cycle.
                        It cannot be undone.
                      </p>
                      <div className="flex gap-2">
                        <Button
                          onClick={async () => { setConfirming(false); await onPurge(); }}
                          disabled={loading}
                          className="flex-1 bg-red-500/25 border border-red-400/50 text-red-100 hover:bg-red-500/35 text-xs h-8"
                          data-testid="snapshot-health-purge-confirm"
                        >
                          {loading
                            ? <><Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> Removing…</>
                            : <>Yes, remove {staleCount}</>}
                        </Button>
                        <Button
                          onClick={() => setConfirming(false)}
                          className="bg-white/10 border border-white/20 text-white/80 hover:bg-white/20 text-xs h-8 px-3"
                          data-testid="snapshot-health-purge-cancel"
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

const ComplianceTrendingSection = ({ data, onBackfill, backfilling, collapsed = false, onToggleCollapse, targetCurve, onSaveTargetCurve, ringsSummary, wufbConnected, trendRingFilter, onTrendRingChange, trendFilterLoading, staleReport, staleLoading, onInspectStale, onPurgeStale }) => {
  const monthly = data?.monthly_trend || [];
  const adoption = data?.adoption_curve || [];
  const previousCurves = data?.previous_adoption_curves || [];
  const kpis = data?.kpis || {};
  const continuousKpis = kpis.continuous || {};
  // `scott/kpi-cycle-update` — Day-14 (Cyber Essentials) + Patch Cycle
  // Completion Rate (service delivery) headline numbers for the most
  // recently superseded cycle, plus the in-flight cycle.
  const cycleKpis = data?.cycle_kpis || {};
  const lastCompletedCycle = cycleKpis.last_completed || null;
  const currentCycle = cycleKpis.current || null;
  const snapshotCount = data?.snapshot_count || 0;
  const currentMonthId = data?.current_month_id;
  const realMonthlyCount = data?.real_monthly_count ?? 0;

  // `scott/kpi-cycle-update2` — rings we hold per-ring snapshots for. The
  // Ring filter is meaningless (and was silently returning empty charts) for
  // any ring not in this list, so it only offers these.
  const ringsWithSnapshots = data?.rings_with_snapshots;
  const ringOptions = useMemo(() => {
    const all = ringsSummary?.rings || [];
    // Backend predates the field → fall back to the old "offer everything"
    // behaviour rather than hiding a control that might be working.
    if (!Array.isArray(ringsWithSnapshots)) return all;
    const allowed = new Set(ringsWithSnapshots.map(String));
    // Never strip the ring that is currently selected — the user needs a way
    // back to "All rings".
    return all.filter(r => allowed.has(String(r.id)) || String(r.id) === String(trendRingFilter));
  }, [ringsSummary, ringsWithSnapshots, trendRingFilter]);

  // `scott/kpi-cycle-update2` — resolver discontinuities for the in-flight
  // cycle: days where OUR VIEW of the estate changed, which would otherwise
  // look like a compliance cliff. The matching coverage detail
  // (`build_resolution`) is deliberately NOT rendered here — it lives on
  // Settings > Data Management with the catalog controls that act on it.
  const curveDiscontinuities = data?.adoption_curve_discontinuities || [];

  // `scott/qualityupdate-trending` — Monthly Compliance Trend collapsed
  // until at least 3 months of REAL (non-estimated) data exist.
  const [monthlyTrendExpanded, setMonthlyTrendExpanded] = useState(false);
  const monthlyTrendAutoCollapsed = realMonthlyCount < 3;

  // `scott/qualityupdate-trending` — Multi-month adoption-curve overlay.
  // Default behaviour: the current month is always rendered, the
  // immediately-previous month is rendered by default, and the user can
  // toggle on up to 4 further months via the "+ overlay" dropdown.
  const previousMonthIds = useMemo(
    () => previousCurves.map((c) => c.month_id),
    [previousCurves],
  );
  const defaultPrevMonth = previousMonthIds[0]; // most recent prior
  const [overlayMonths, setOverlayMonths] = useState(() =>
    defaultPrevMonth ? [defaultPrevMonth] : [],
  );
  const [overlayPickerOpen, setOverlayPickerOpen] = useState(false);
  // When backend data arrives for the first time, seed the default prior
  // month so users don't have to discover it manually.
  useEffect(() => {
    if (defaultPrevMonth && overlayMonths.length === 0) {
      setOverlayMonths([defaultPrevMonth]);
    }
  }, [defaultPrevMonth]);  // eslint-disable-line

  const fmtPct = (v) => (v == null ? '—' : `${Number(v).toFixed(1)}%`);
  const fmtDelta = (d) => {
    if (d == null) return null;
    const arrow = d > 0 ? '▲' : d < 0 ? '▼' : '◆';
    const cls = d > 0 ? 'text-green-400' : d < 0 ? 'text-red-400' : 'text-white/50';
    return <span className={`text-xs ${cls} ml-1`}>{arrow} {Math.abs(d).toFixed(1)}pt</span>;
  };

  const hasMonthly = monthly.length > 0;
  const hasAdoption = adoption.length > 0;
  const isEstimated = monthly.some(m => m.source === 'estimated_backfill') ||
                      adoption.some(a => a.source === 'estimated_backfill');

  // ---- Monthly trend benchmark — Hybrid Option A ----
  // The current month is in-progress (still collecting day-N snapshots),
  // so it would always look "low" until end-of-month. Hide it from the
  // trend chart and surface a small footer note explaining why.
  const monthlyForChart = useMemo(
    () => {
      // `scott/kpi-cycle-update` — the Patch Cycle Completion Trend plots ONE
      // number per monthly quality update: the Patch Cycle Completion Rate,
      // i.e. the % of devices that had installed that month's update at the
      // moment it was superseded FOR THEM —
      //     SupersedenceDate = next Patch Tuesday + their ring's deferral.
      //
      // This replaces Peak Cycle Compliance, which locked in the best moment
      // of the cycle and so hid every subsequent regression (new devices,
      // eligibility changes, stale reporting).
      //
      // Only cycles where EVERY ring has passed its own supersedence date are
      // plotted — otherwise the newest point would dip purely because the
      // slower rings have not been asked the question yet.
      //
      // `projected` marks a month extrapolated from the current trajectory
      // rather than measured; it drives that month's dot style and tooltip.
      // `end_of_cycle_*` is still carried through untouched for back-compat.
      const rows = monthly.filter(
        (m) => m.month_id !== currentMonthId && m.cycle_completion_is_final,
      );
      const mapped = rows.map((m) => {
        const src = m.cycle_completion_source;
        const projected = src === 'estimated_backfill' || (!src && m.source === 'estimated_backfill');
        const real = src === 'measured' || src === 'wufb';
        const completion = m.cycle_completion_pct;
        return {
          ...m,
          cycle_pct: real ? completion : null,
          cycle_pct_projected: projected ? completion : null,
          projected,
          cycle_basis: m.cycle_completion_basis,
          cycle_source: src,
          cycle_applicable: m.cycle_completion_applicable_devices,
          cycle_completed: m.cycle_completion_completed_devices,
          cycle_deferrals: m.cycle_completion_deferral_days,
          cycle_breakdown: m.cycle_completion_breakdown,
          cycle_first_supersedence: m.cycle_completion_first_supersedence_date,
          cycle_last_supersedence: m.cycle_completion_last_supersedence_date,
          day14_pct_strict: m.day14_compliance_pct,
          // Keep legacy fields so anything still reading day7/14/30 or the
          // end-of-cycle block (tests, tooltips) doesn't break — but they
          // are no longer plotted.
          eoc_pct: real ? m.end_of_cycle_pct : null,
          eoc_pct_projected: projected ? m.end_of_cycle_pct : null,
          eoc_day_offset: m.end_of_cycle_day_offset,
          eoc_canonical_day_offset: m.end_of_cycle_canonical_day_offset,
          eoc_is_canonical: m.end_of_cycle_is_canonical,
          eoc_source: m.end_of_cycle_source,
        };
      });

      // `scott/kpi-cycle-update2` — ONE metric drawn as ONE path that changes
      // style at the handover: dotted amber while it is projected, solid cyan
      // once it is measured.
      //
      // The two series must never carry a value for the same month except at
      // the single junction point, otherwise both lines run the full width of
      // the chart and overlay each other — which is what made this look like
      // two competing metrics.
      //
      // So: everything up to and including the LAST projected month belongs to
      // the amber series, everything from the first measured month after it
      // belongs to the cyan series, and that first measured month is written
      // to BOTH so the dotted line terminates on the solid line's own dot.
      // One shared vertex, no shared segment.
      const lastProjectedIdx = mapped.reduce(
        (acc, m, i) => (m.projected ? i : acc), -1,
      );
      const junctionIdx = lastProjectedIdx >= 0 ? lastProjectedIdx + 1 : -1;

      return mapped.map((m, i) => {
        const value = m.cycle_pct != null ? m.cycle_pct : m.cycle_pct_projected;
        const inBackfill = lastProjectedIdx >= 0 && i <= lastProjectedIdx;
        const isJunction = i === junctionIdx;
        return {
          ...m,
          // No projected months at all → the cyan line owns everything.
          cycle_pct: (lastProjectedIdx < 0 || i >= junctionIdx) ? value : null,
          cycle_pct_projected: (inBackfill || isJunction) ? value : null,
          is_projection_junction: isJunction,
        };
      });
    },
    [monthly, currentMonthId],
  );
  const hasProjectedMonths = useMemo(
    () => monthlyForChart.some((m) => m.projected),
    [monthlyForChart],
  );
  // Governance count: how many of the trailing months in the chart hit the
  // 95% target. `scott/kpi-cycle-update` — now anchored on each month's
  // Patch Cycle Completion Rate rather than its peak reading.
  const monthlyGovernance = useMemo(() => {
    const sample = monthlyForChart.filter(m =>
      (m.cycle_pct != null) || (m.cycle_pct_projected != null)
    );
    const total = sample.length;
    if (!total) return null;
    const onTarget = sample.filter(m => {
      const v = m.cycle_pct != null ? Number(m.cycle_pct) : Number(m.cycle_pct_projected);
      return v >= 95;
    }).length;
    const pct = (onTarget / total) * 100;
    return {
      total,
      on_target: onTarget,
      tier: pct >= 75 ? 'good' : pct >= 50 ? 'warn' : 'risk',
    };
  }, [monthlyForChart]);

  // Patch Tuesday for the current month — used to translate day-offsets into
  // calendar dates in tooltips and the chart subheading.
  const currentPT = patchTuesdayOf(currentMonthId);
  const dateForOffset = (offset) => {
    if (!currentPT) return null;
    const d = new Date(currentPT.getTime());
    d.setUTCDate(d.getUTCDate() + Number(offset || 0));
    return d;
  };

  // ---- Recommended Target benchmark (enterprise WUfB best practice) ----
  // `scott/feature-updates-3` — anchors are now per-tenant configurable via
  // the `/reports/compliance-target-curve` endpoint. Default preset matches
  // the previous hard-coded values (0d=0%, 7d=60%, 14d=80%, 30d=95%) so
  // existing tenants see no visible change until they pick a preset / curve.
  const DEFAULT_BENCHMARK_ANCHORS = useMemo(() => [
    { day: 0, pct: 0 },
    { day: 7, pct: 60 },
    { day: 14, pct: 80 },
    { day: 30, pct: 95 },
  ], []);

  const BENCHMARK_ANCHORS = useMemo(() => {
    const a = targetCurve?.anchors;
    if (Array.isArray(a) && a.length >= 2) {
      // Normalise + sort defensively in case the parent stashed an unsorted list.
      return [...a]
        .map(r => ({ day: Number(r.day), pct: Number(r.pct) }))
        .filter(r => Number.isFinite(r.day) && Number.isFinite(r.pct))
        .sort((x, y) => x.day - y.day);
    }
    return DEFAULT_BENCHMARK_ANCHORS;
  }, [targetCurve, DEFAULT_BENCHMARK_ANCHORS]);

  const benchmarkAt = useMemo(() => {
    return (day) => {
      const d = Math.max(0, Math.min(30, Number(day) || 0));
      for (let i = 0; i < BENCHMARK_ANCHORS.length - 1; i++) {
        const a = BENCHMARK_ANCHORS[i];
        const b = BENCHMARK_ANCHORS[i + 1];
        if (d >= a.day && d <= b.day) {
          const span = b.day - a.day;
          if (span === 0) return a.pct;
          const t = (d - a.day) / span;
          return Math.round((a.pct + t * (b.pct - a.pct)) * 10) / 10;
        }
      }
      return BENCHMARK_ANCHORS[BENCHMARK_ANCHORS.length - 1].pct;
    };
  }, [BENCHMARK_ANCHORS]);

  // Merge actual + benchmark on a single dataset, padding so the benchmark
  // line spans 0..30 even when there are gaps in the actual data.
  // Two transforms applied to the actual rows:
  //   1. If `total_devices === 0` on a measured day, treat the percentage
  //      as null (no eligible devices on that day, not a "real" 0%).
  //   2. Carry the `source` flag through so the chart can style
  //      `estimated_backfill` segments dimmer.
  const adoptionWithBenchmark = useMemo(() => {
    const byDay = new Map();
    // Seed with benchmark anchors so the dashed line is well-defined even
    // when the tenant has no actual datapoints yet.
    for (let d = 0; d <= 30; d++) {
      byDay.set(d, {
        day_offset: d,
        compliance_pct: null,
        compliance_pct_estimated: null,
        benchmark_pct: benchmarkAt(d),
      });
    }
    for (const row of adoption) {
      const d = Number(row.day_offset);
      const total = Number(row.total_devices || 0);
      const isEstimated = row.source === 'estimated_backfill';
      const pct = (total === 0) ? null : (row.compliance_pct == null ? null : Number(row.compliance_pct));
      const existing = byDay.get(d) || { day_offset: d, benchmark_pct: benchmarkAt(d) };
      byDay.set(d, {
        ...existing,
        day_offset: d,
        total_devices: total,
        source: row.source,
        // Measured rows feed the solid line, estimated rows feed a dimmer
        // companion line — never both on the same point.
        compliance_pct: isEstimated ? null : pct,
        compliance_pct_estimated: isEstimated ? pct : null,
        benchmark_pct: benchmarkAt(d),
      });
    }
    return Array.from(byDay.values()).sort((a, b) => a.day_offset - b.day_offset);
  }, [adoption, benchmarkAt]);

  // Helper: are any of the rendered points estimated? Drives legend chip
  // and the visibility of the dimmer "estimated" companion line.
  const hasEstimatedDays = useMemo(
    () => adoption.some(a => a.source === 'estimated_backfill'),
    [adoption],
  );

  // `scott/qualityupdate-trending` — Merge previous-month adoption curves
  // onto the same dataset by writing one column per selected month_id
  // (e.g. `overlay_2025_10`). Recharts then renders one Line per key.
  // We index by day_offset so overlay-by-day-since-Patch-Tuesday lines up
  // visually with the current month even though the calendar dates differ.
  const adoptionWithOverlays = useMemo(() => {
    if (!overlayMonths.length || !previousCurves.length) return adoptionWithBenchmark;
    const byDay = new Map(adoptionWithBenchmark.map((r) => [r.day_offset, { ...r }]));
    for (const mid of overlayMonths) {
      const entry = previousCurves.find((c) => c.month_id === mid);
      if (!entry) continue;
      const key = `overlay_${mid.replace('-', '_')}`;
      for (const row of entry.curve) {
        const d = Number(row.day_offset);
        const existing = byDay.get(d) || { day_offset: d, benchmark_pct: benchmarkAt(d) };
        const total = Number(row.total_devices || 0);
        existing[key] = total === 0 ? null : (row.compliance_pct == null ? null : Number(row.compliance_pct));
        byDay.set(d, existing);
      }
    }
    return Array.from(byDay.values()).sort((a, b) => a.day_offset - b.day_offset);
  }, [adoptionWithBenchmark, overlayMonths, previousCurves, benchmarkAt]);

  // Stable colour palette for the per-month overlay lines. Picked to be
  // distinguishable from the cyan "actual" line and the amber benchmark.
  const OVERLAY_PALETTE = [
    '#f472b6', // pink
    '#a855f7', // purple
    '#34d399', // emerald
    '#fb923c', // orange
    '#facc15', // amber
    '#fbbf24', // yellow-amber
  ];
  // Format a YYYY-MM into "Oct '25" for compact chart legends / pickers.
  const fmtMonthShort = (mid) => {
    if (!mid || typeof mid !== 'string' || !mid.includes('-')) return mid;
    const [y, m] = mid.split('-').map(Number);
    if (!y || !m) return mid;
    const d = new Date(Date.UTC(y, m - 1, 1));
    return d.toLocaleDateString('en-GB', { month: 'short', year: '2-digit', timeZone: 'UTC' });
  };
  // `scott/deployment-kpi-bugfix` — "July 2026" for the cycle KPI tiles,
  // which read as prose ("July Day-14 Compliance") rather than chart labels.
  const fmtMonthLong = (mid) => {
    if (!mid || typeof mid !== 'string' || !mid.includes('-')) return mid || '—';
    const [y, m] = mid.split('-').map(Number);
    if (!y || !m) return mid;
    const d = new Date(Date.UTC(y, m - 1, 1));
    return d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' });
  };

  // Where do we stand against the Day-14 SLO (80%)?
  const day14Actual = useMemo(() => {
    const exact = adoption.find(a => Number(a.day_offset) === 14);
    if (exact && exact.compliance_pct != null) return Number(exact.compliance_pct);
    // Fall back to the latest datapoint at day <= 14
    const eligible = adoption
      .filter(a => Number(a.day_offset) <= 14 && a.compliance_pct != null)
      .sort((a, b) => Number(b.day_offset) - Number(a.day_offset));
    return eligible.length ? Number(eligible[0].compliance_pct) : null;
  }, [adoption]);
  const day14Status = useMemo(() => {
    if (day14Actual == null) return null;
    return day14Actual >= benchmarkAt(14) ? 'on_track' : 'behind';
  }, [day14Actual, benchmarkAt]);

  // Latest data point — used for a side-by-side status pill.
  const latestActual = useMemo(() => {
    const withVals = adoption
      .filter(a => a.compliance_pct != null)
      .sort((a, b) => Number(b.day_offset) - Number(a.day_offset));
    if (!withVals.length) return null;
    const r = withVals[0];
    const day = Number(r.day_offset);
    return {
      day,
      pct: Number(r.compliance_pct),
      target: benchmarkAt(day),
    };
  }, [adoption, benchmarkAt]);
  const latestStatus = latestActual
    ? (latestActual.pct >= latestActual.target ? 'on_track' : 'behind')
    : null;

  const Kpi = ({ label, value, delta, accent, info }) => (
    <div
      className="rounded-xl p-4 border border-[rgba(229,239,250,0.1)] bg-[rgba(229,239,250,0.06)]"
      data-testid={`compliance-kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <p className="text-xs uppercase tracking-wider text-white/70 mb-2 flex items-center gap-1.5">
        {label}
        {info && <InfoPopover label={label}>{info}</InfoPopover>}
      </p>
      <div className="flex items-end gap-2">
        <p className={`text-3xl font-bold ${accent}`}>{fmtPct(value)}</p>
        {delta !== undefined && fmtDelta(delta)}
      </div>
      <p className="text-xs text-white/50 mt-1">vs previous month</p>
    </div>
  );

  // `scott/qualityupdate-trending` — Continuous KPI card. A small tile
  // built to surface a single live number + optional subtitle / status pill.
  // No "vs previous month" footer (we deliberately keep these point-in-time
  // metrics decoupled from the slower-moving monthly comparison).
  const ContinuousKpi = ({ label, info, value, subtitle, accent, pill, testId }) => (
    <div
      className="rounded-xl p-4 border border-[rgba(229,239,250,0.1)] bg-[rgba(229,239,250,0.06)] flex flex-col gap-2"
      data-testid={testId || `compliance-kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <p className="text-xs uppercase tracking-wider text-white/70 flex items-center gap-1.5">
        {label}
        {info && <InfoPopover label={label}>{info}</InfoPopover>}
      </p>
      <div className="flex items-end gap-2 flex-wrap">
        <p className={`text-3xl font-bold ${accent || 'text-white'}`}>{value}</p>
        {pill}
      </div>
      {subtitle && <p className="text-[11px] text-white/55 leading-snug">{subtitle}</p>}
    </div>
  );

  // ---- Continuous KPI maths (frontend) ----
  // Most numbers come from the backend `continuous` block (current_compliance_pct,
  // latest_day_offset, velocity_3d_pct_per_day, projection.{day14,day30}.{linear,decay}).
  // "Days behind target" is computed here because it depends on the user-selected
  // target curve — which the parent owns and might change without a backend round-trip.
  const currentCompliancePct = continuousKpis.current_compliance_pct;
  const latestDayOffset = continuousKpis.latest_day_offset;
  const velocity = continuousKpis.velocity_3d_pct_per_day;
  const velocityWindow = continuousKpis.velocity_window_days || 0;
  const day14Proj = continuousKpis.projection?.day14 || { linear: null, decay: null };
  const day30Proj = continuousKpis.projection?.day30 || { linear: null, decay: null };

  // Days behind / ahead: invert the benchmark curve. Find the calendar day
  // whose target % equals the current actual %. The gap (in days) between
  // today's day-offset and that "calendar day on the target curve" is the
  // number of days we are behind (positive) or ahead (negative).
  const daysBehindTarget = useMemo(() => {
    if (currentCompliancePct == null || latestDayOffset == null) return null;
    // Invert: walk 0..30 and find the first day whose target_pct >= current_pct.
    let targetReachedAtDay = null;
    for (let d = 0; d <= 30; d++) {
      if (benchmarkAt(d) >= currentCompliancePct) {
        targetReachedAtDay = d;
        break;
      }
    }
    if (targetReachedAtDay == null) {
      // We're above the highest target (95% by day 30) → comfortably ahead.
      return -(30 - latestDayOffset);
    }
    return latestDayOffset - targetReachedAtDay;
  }, [currentCompliancePct, latestDayOffset, benchmarkAt]);

  const onTrack = daysBehindTarget != null && daysBehindTarget <= 0;
  const targetForToday = latestDayOffset != null ? benchmarkAt(latestDayOffset) : null;

  // Format the projection range value for display (low–high). When we only
  // have one number, surface just that. When the projected day has already
  // passed, surface the actual value (already collapsed to a single number
  // by the backend) without a range.
  const fmtProjection = (proj) => {
    if (!proj) return '—';
    const { linear, decay } = proj;
    if (linear == null && decay == null) return '—';
    if (linear == null) return `${decay.toFixed(0)}%`;
    if (decay == null) return `${linear.toFixed(0)}%`;
    if (Math.abs(linear - decay) < 0.5) return `${linear.toFixed(0)}%`;
    const lo = Math.min(linear, decay);
    const hi = Math.max(linear, decay);
    return `${lo.toFixed(0)}–${hi.toFixed(0)}%`;
  };
  const projectionMeetsTarget = (proj, targetPct) => {
    if (!proj || (proj.linear == null && proj.decay == null)) return null;
    const lo = Math.min(...[proj.linear, proj.decay].filter((v) => v != null));
    return lo >= targetPct ? 'on_track' : 'behind';
  };

  return (
    <Card
      className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border-[rgba(229,239,250,0.1)] mt-6"
      data-testid="compliance-trending-section"
    >
      <CardHeader
        className={`border-b border-[rgba(229,239,250,0.1)] ${onToggleCollapse ? 'cursor-pointer select-none' : ''}`}
        onClick={onToggleCollapse}
        data-testid="compliance-trending-header"
      >
        <div className="flex items-center justify-between">
          <CardTitle className="text-white flex items-center gap-2">
            {onToggleCollapse && (
              <ChevronDown
                className={`w-4 h-4 text-white/60 transition-transform ${collapsed ? '-rotate-90' : ''}`}
              />
            )}
            <TrendingUp className="w-5 h-5 text-cyan-400" />
            Quality Update Compliance Trending
            <InfoPopover label="About this section">
              <p>Tracks Windows security-update adoption across your estate, <strong>without requiring WUfB or Autopatch enrolment</strong>. We map each device's current build to its Microsoft release month — if it's on month <em>M</em>'s build or newer, it counts as compliant for month <em>M</em>.</p>
              <p><strong>Backfill is an estimate</strong>: it assumes a device on today's build has always been on today's build. So older months tend to look <em>better than they really were</em>. Recent months and the current month are progressively more accurate.</p>
              <p><strong>The expected "drop on day +1, climb to 95% by day +14" pattern</strong> only appears once we've collected real measured snapshots through a Patch Tuesday — first one will be the next 2nd Tuesday after this feature was deployed.</p>
            </InfoPopover>
            {currentMonthId && (
              <span className="ml-2 text-xs text-white/60 font-normal">Current month: {currentMonthId}</span>
            )}
            {isEstimated && (
              <span className="ml-2 px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-500/20 text-amber-300" data-testid="estimated-projection-badge">
                Includes projected trajectory
              </span>
            )}
          </CardTitle>
          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
            {/* `scott/qualityupdate-trending` — Ring filter elevated to
                the section header so its scope (KPIs + adoption curve +
                monthly trend all rescope together) is always visible,
                including when the Monthly Compliance Trend section is
                collapsed. Was previously buried inside the monthly chart. */}
            {/* `scott/kpi-cycle-update2` — the picker used to list every ring
                in the tenant and return an empty chart for all of them:
                nothing ever wrote `os_patch_devices.ring_id`, so per-ring
                snapshot rows were never created. Now that ring membership is
                stamped during sync, we only offer rings we actually hold
                snapshots for (`rings_with_snapshots`), and hide the control
                entirely until the first per-ring snapshot lands. */}
            {onTrendRingChange && ringOptions.length > 0 && (
              <div className="flex items-center gap-1.5" data-testid="trend-section-ring-filter">
                <label className="text-[11px] text-white/55 uppercase tracking-wider">Ring</label>
                <select
                  value={trendRingFilter || ''}
                  onChange={(e) => onTrendRingChange(e.target.value)}
                  disabled={trendFilterLoading}
                  className="bg-slate-900/80 border border-white/15 rounded-md px-2 py-1 text-xs text-white disabled:opacity-50 min-w-[160px]"
                  data-testid="trend-section-ring-select"
                >
                  <option value="">All rings</option>
                  {ringOptions.map(r => (
                    <option key={r.id} value={r.id}>{r.name}</option>
                  ))}
                </select>
                {trendFilterLoading && <Loader2 className="w-3 h-3 animate-spin text-white/60" />}
              </div>
            )}
            <span className="text-xs text-white/50" data-testid="trend-snapshot-count">{snapshotCount} snapshots</span>
            {/* `scott/deployment-kpi-bugfix` — sits in the section header
                because stale rows distort the Adoption Curve (always visible)
                as well as the cycle trend (collapsed by default). */}
            {onInspectStale && (
              <SnapshotHealthPopover
                report={staleReport}
                loading={staleLoading}
                onInspect={onInspectStale}
                onPurge={onPurgeStale}
              />
            )}
          </div>
        </div>
      </CardHeader>

      {!collapsed && (
      <CardContent className="py-6 space-y-8">
        {/* C. Continuous KPI Tiles — point-in-time live indicators that
            are useful from a tenant's first day onboard (unlike the
            7/14/30-day snapshot KPIs they replaced, which only filled
            in after each Patch-Tuesday cycle had run). */}
        <div>
          <h3 className="text-sm font-semibold text-white/80 mb-3 flex items-center gap-2">
            Current Cycle KPIs ({kpis.month_id || currentMonthId || '—'})
            <InfoPopover label="Current Cycle KPIs">
              <p>Live indicators for the in-flight Patch-Tuesday cycle. Unlike the previous 7/14/30-day snapshot tiles, these update every time a daily snapshot is captured — useful from day 1 of being onboard.</p>
              <p><strong>Current compliance</strong>: % of Windows devices already on this month's security update (or newer). The headline number.</p>
              <p><strong>Days behind target</strong>: how far above/below the chosen target curve you are <em>today</em>. Negative = ahead. The pill shows "On track" when you're meeting or beating the curve.</p>
              <p><strong>Adoption velocity</strong>: average % gained per day over the last 3 measured datapoints. A simple "are we still climbing?" gauge.</p>
              <p><strong>Day 14 / Day 30 outcomes</strong>: projected compliance % at those checkpoints. Range shown is <em>linear extrapolation</em> (high) ↔ <em>velocity-decay model</em> (low — assumes the rate will slow as you approach 100%).</p>
            </InfoPopover>
          </h3>

          {currentCompliancePct == null ? (
            <div
              className="rounded-xl p-6 border border-dashed border-white/15 bg-white/[0.03] text-center text-white/60 text-sm"
              data-testid="continuous-kpis-empty"
            >
              No measured snapshots yet for {currentMonthId || 'this month'}. KPIs will populate once the first daily snapshot is written (or after the next Patch Tuesday).
            </div>
          ) : (
            <div className="alice-kpi-row grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              <ContinuousKpi
                label="Current compliance"
                testId="continuous-kpi-current-compliance"
                value={`${currentCompliancePct.toFixed(1)}%`}
                accent="text-cyan-300"
                subtitle={latestDayOffset != null
                  ? `Latest measurement: Day +${latestDayOffset}${targetForToday != null ? ` · Target: ${targetForToday}%` : ''}`
                  : null}
                info={
                  <>
                    <p>% of Windows devices on this month's security update (or newer) at the most recent daily snapshot.</p>
                    <p>Counts a device as compliant for month <em>M</em> if its current build was released in month <em>M</em> or later. Works without WUfB.</p>
                  </>
                }
              />

              <ContinuousKpi
                label="Days behind target"
                testId="continuous-kpi-days-behind-target"
                value={
                  onTrack
                    ? 'On track'
                    : `${Math.abs(daysBehindTarget)} day${Math.abs(daysBehindTarget) === 1 ? '' : 's'}`
                }
                accent={onTrack ? 'text-emerald-300' : 'text-red-300'}
                pill={
                  onTrack ? (
                    <span
                      className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border border-emerald-400/40 bg-emerald-500/10 text-emerald-200"
                      data-testid="days-behind-on-track-pill"
                    >
                      <CheckCircle2 className="w-3 h-3" /> ≥ Target
                    </span>
                  ) : (
                    <span
                      className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border border-red-400/40 bg-red-500/10 text-red-200"
                      data-testid="days-behind-behind-pill"
                    >
                      <AlertCircle className="w-3 h-3" /> Behind
                    </span>
                  )
                }
                subtitle={
                  daysBehindTarget == null
                    ? null
                    : onTrack
                      ? `Current ${currentCompliancePct.toFixed(0)}% ≥ ${targetForToday}% target for Day +${latestDayOffset}`
                      : `Current ${currentCompliancePct.toFixed(0)}% < ${targetForToday}% target for Day +${latestDayOffset}`
                }
                info={
                  <>
                    <p>How many days you are <em>behind</em> the chosen target curve at this moment.</p>
                    <p>Computed by walking the curve forward and finding the day whose target % first reaches your current actual %. The gap (in days) between today and that point is your lag.</p>
                    <p>Negative values (rendered as "On track") mean you're hitting today's target — devices are landing the patch faster than the curve predicts.</p>
                  </>
                }
              />

              <ContinuousKpi
                label="Adoption velocity"
                testId="continuous-kpi-adoption-velocity"
                value={
                  velocity == null
                    ? '—'
                    : `${velocity >= 0 ? '+' : ''}${velocity.toFixed(1)}%/day`
                }
                accent={
                  velocity == null
                    ? 'text-white/60'
                    : velocity > 0
                      ? 'text-emerald-300'
                      : velocity < 0
                        ? 'text-red-300'
                        : 'text-white/70'
                }
                subtitle={
                  velocity == null
                    ? 'Needs at least 2 measured snapshots'
                    : `Average %-point gain over the last ${velocityWindow} day${velocityWindow === 1 ? '' : 's'}`
                }
                info={
                  <>
                    <p>The simple daily slope: <code>(latest % − % 3 days ago) / days between</code>.</p>
                    <p>Positive = still climbing toward the next plateau. Near zero in the first 24 hours of a cycle (Day 0 → Day 1 is usually huge), tapers off as the curve plateaus.</p>
                  </>
                }
              />

              <ContinuousKpi
                label="Estimated Day 14 / Day 30"
                testId="continuous-kpi-projected-outcome"
                value={
                  <span className="flex flex-col items-start gap-0.5">
                    <span className="text-3xl font-bold">{fmtProjection(day14Proj)}</span>
                    <span className="text-xs text-white/55 font-normal">D14</span>
                    <span className="text-3xl font-bold mt-1">{fmtProjection(day30Proj)}</span>
                    <span className="text-xs text-white/55 font-normal">D30</span>
                  </span>
                }
                accent="text-blue-300"
                pill={(() => {
                  const status14 = projectionMeetsTarget(day14Proj, benchmarkAt(14));
                  const status30 = projectionMeetsTarget(day30Proj, benchmarkAt(30));
                  if (status14 === 'on_track' && status30 === 'on_track') {
                    return (
                      <span
                        className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border border-emerald-400/40 bg-emerald-500/10 text-emerald-200"
                        data-testid="projection-on-track-pill"
                      >
                        <CheckCircle2 className="w-3 h-3" /> On Track
                      </span>
                    );
                  }
                  if (status14 === 'behind' || status30 === 'behind') {
                    return (
                      <span
                        className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border border-amber-400/40 bg-amber-500/10 text-amber-200"
                        data-testid="projection-at-risk-pill"
                      >
                        <AlertCircle className="w-3 h-3" /> At Risk
                      </span>
                    );
                  }
                  return null;
                })()}
                subtitle="Range = velocity-decay (low) → linear (high)"
                info={
                  <>
                    <p>Projects today's compliance forward to the Day +14 and Day +30 checkpoints using the current velocity. Range shown as <em>low–high</em>:</p>
                    <ul className="list-disc ml-4 space-y-0.5">
                      <li><strong>High (linear)</strong> = velocity holds constant until the target day. Optimistic; treats the steady early-cycle slope as if it never tapers.</li>
                      <li><strong>Low (decay)</strong> = velocity tapers exponentially toward zero as you approach 100%. More realistic later in the cycle.</li>
                    </ul>
                    <p>The pill compares the low end of the range to the chosen curve's Day-14 / Day-30 targets.</p>
                  </>
                }
              />
            </div>
          )}
          {kpis.device_count > 0 && (
            <p className="text-xs text-white/50 mt-2">
              Device count: <span className="font-mono text-white/80">{kpis.device_count}</span>
            </p>
          )}
        </div>

        {/* `scott/kpi-cycle-update` — Cycle Reporting KPIs.
            Two numbers that answer two different questions and must never be
            conflated:
              • Day-14 Compliance   → Cyber Essentials / security reporting.
                Measured across the WHOLE estate 14 days after Patch Tuesday,
                with NO deferral adjustment — CE counts from release date and
                an internal ring deferral does not stop that clock.
              • Patch Cycle Completion Rate → service-delivery reporting. The
                % of devices that had installed the update at the moment it
                was superseded FOR THEM: next Patch Tuesday + that device's
                own ring deferral. Not a peak — a single point-in-time
                reading per ring, taken at that ring's supersedence date. */}
        <div data-testid="cycle-reporting-kpis">
          <h3 className="text-sm font-semibold text-white/80 mb-3 flex items-center gap-2">
            Cycle Reporting ({lastCompletedCycle?.month_id
              ? `last completed cycle · ${fmtMonthLong(lastCompletedCycle.month_id)}`
              : 'no completed cycle yet'})
            <InfoPopover label="Cycle Reporting KPIs">
              <p>Two deliberately different numbers for the most recently <strong>superseded</strong> quality update — the last cycle whose result can no longer change.</p>
              <p><strong>Day-14 Compliance</strong> is the Cyber Essentials measure: the % of <em>all</em> Windows devices on that month's update exactly 14 days after Patch Tuesday. Update Ring deferrals are deliberately <em>not</em> applied — Cyber Essentials counts from Microsoft's release date, so a 21-day deferral is a finding, not an excuse.</p>
              <p><strong>Patch Cycle Completion Rate</strong> is the service-delivery measure: the % of devices that had <em>finished installing</em> that month's update at the moment it was superseded for them. Each ring is read on its own <strong>supersedence date</strong> — <code>next Patch Tuesday + that ring's Quality Update Deferral</code> — and the results are pooled as <code>devices completed ÷ devices applicable</code>.</p>
              <p className="text-white/55 text-[11px]">A cycle only becomes final once <em>every</em> ring has passed its supersedence date, so a tenant whose slowest ring defers 14 days closes each month 14 days into the next one.</p>
              <p className="text-white/55 text-[11px]">Use Day-14 for security and certification evidence. Use Cycle Completion for "did we deliver the patch before it was replaced?" service reporting.</p>
            </InfoPopover>
          </h3>

          {!lastCompletedCycle ? (
            <div
              className="rounded-xl p-6 border border-dashed border-white/15 bg-white/[0.03] text-center text-white/60 text-sm"
              data-testid="cycle-reporting-kpis-empty"
            >
              No completed patch cycle yet. These figures populate once a monthly quality update has been superseded by the following month's release.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <ContinuousKpi
                label="Cyber Essentials — Day-14 Compliance"
                testId="cycle-kpi-day14-compliance"
                value={lastCompletedCycle.day14_compliance_pct == null
                  ? '—'
                  : `${Number(lastCompletedCycle.day14_compliance_pct).toFixed(1)}%`}
                accent={lastCompletedCycle.day14_compliance_pct == null
                  ? 'text-white/60'
                  : Number(lastCompletedCycle.day14_compliance_pct) >= 95
                    ? 'text-emerald-300'
                    : 'text-amber-300'}
                subtitle={lastCompletedCycle.day14_compliance_pct == null
                  ? `No Day +14 snapshot captured for ${fmtMonthLong(lastCompletedCycle.month_id)}`
                  : `${fmtMonthLong(lastCompletedCycle.month_id)} · all devices, 14 days after Patch Tuesday · no deferral adjustment`}
                info={
                  <>
                    <p>Compliance exactly 14 days after Patch Tuesday, across every Windows device — the Cyber Essentials clock.</p>
                    <p>Deliberately ignores Update Ring deferrals: CE measures from Microsoft's release date, so a device still waiting on a 21-day deferral counts as non-compliant here.</p>
                  </>
                }
              />

              <ContinuousKpi
                label="Patch Management Success — Patch Cycle Completion Rate"
                testId="cycle-kpi-cycle-completion-rate"
                value={lastCompletedCycle.cycle_completion_pct == null
                  ? '—'
                  : `${Number(lastCompletedCycle.cycle_completion_pct).toFixed(1)}%`}
                accent={lastCompletedCycle.cycle_completion_pct == null
                  ? 'text-white/60'
                  : Number(lastCompletedCycle.cycle_completion_pct) >= 95
                    ? 'text-emerald-300'
                    : 'text-amber-300'}
                pill={currentCycle ? (
                  <span
                    className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border border-cyan-400/40 bg-cyan-500/10 text-cyan-200"
                    title={currentCycle.cycle_completion_pct == null
                      ? `${fmtMonthLong(currentCycle.month_id)} is still in flight — no reading yet`
                      : `${fmtMonthLong(currentCycle.month_id)} so far: ${Number(currentCycle.cycle_completion_pct).toFixed(1)}%. Not final until every ring passes its supersedence date (next Patch Tuesday + that ring's deferral).`}
                    data-testid="cycle-kpi-current-in-progress-pill"
                  >
                    {fmtMonthShort(currentCycle.month_id)}: In Progress
                    {currentCycle.cycle_completion_pct != null
                      ? ` · ${Number(currentCycle.cycle_completion_pct).toFixed(0)}%`
                      : ''}
                  </span>
                ) : null}
                subtitle={lastCompletedCycle.cycle_completion_pct == null
                  ? `No measurement available for the ${fmtMonthLong(lastCompletedCycle.month_id)} cycle`
                  : `${fmtMonthLong(lastCompletedCycle.month_id)} · ${
                      lastCompletedCycle.cycle_completion_completed_devices ?? '?'
                    }/${
                      lastCompletedCycle.cycle_completion_applicable_devices ?? '?'
                    } devices completed by supersedence${
                      cycleCompletionBasisLabel(lastCompletedCycle.cycle_completion_basis)
                    }`}
                info={
                  <>
                    <p>The % of devices that had <strong>finished installing</strong> this month's quality update at the moment it was superseded for them.</p>
                    <p>Each device is measured on <em>its own</em> supersedence date — <code>next Patch Tuesday + its Update Ring's Quality Update Deferral</code>. Rings with different deferrals are read on different dates and pooled as <code>completed ÷ applicable</code>; no tenant-wide deferral is assumed.</p>
                    <p>This is <strong>not</strong> a peak. A tenant that briefly touched 98% and then fell back to 88% before supersedence scores 88%.</p>
                    <p>The in-progress pill shows the live cycle's reading so far. It is not final until every ring has passed its supersedence date.</p>
                    {lastCompletedCycle.cycle_completion_breakdown?.length > 1 && (
                      <div className="text-[11px] text-white/60">
                        <p className="text-white/75 font-medium">Per-deferral breakdown</p>
                        <ul className="list-disc ml-4 space-y-0.5">
                          {lastCompletedCycle.cycle_completion_breakdown.map((b, i) => (
                            <li key={i}>
                              +{b.deferral_days ?? 0}d ring{b.supersedence_date ? ` · ${b.supersedence_date}` : ''}:{' '}
                              {b.completion_pct == null
                                ? (b.status === 'pending' ? 'not yet superseded' : 'no data')
                                : `${b.completion_pct}% (${b.completed_devices}/${b.applicable_devices})`}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </>
                }
              />
            </div>
          )}
        </div>

        {/* B. Patch Adoption Curve (current month) — surfaced first because the in-flight cycle is the most-actionable view; the longer-term Monthly Trend follows below. */}
        <div>
          <div className="flex items-start justify-between gap-3 flex-wrap mb-1">
            <h3 className="text-sm font-semibold text-white/80 flex items-center gap-2">
              Patch Adoption Curve — {currentMonthId || 'current month'} (Day 0 → 30 since Patch Tuesday)
              <InfoPopover label="Patch Adoption Curve">
                <p>The <strong>day-by-day rollout for this month only</strong>. X-axis is days since Patch Tuesday (the 2nd Tuesday of the month, when Microsoft releases security updates).</p>
                <p><strong>Recommended Target (dashed amber line)</strong>: a per-tenant configurable curve — defaults to Microsoft WUfB guidance (60% by Day 7, <strong>80% by Day 14</strong>, 95% by Day 30). Use the "Target curve" selector at the top-right of this section to switch between Default / Conservative / Aggressive presets or define your own anchors.</p>
                <p><strong>Expected shape</strong>: starts near 0% on day 0, rises sharply to ~80% by day 7, climbs to 95%+ by day 14, plateaus thereafter.</p>
                <p><strong>Red flags</strong>: a slow ramp = patches queuing in update rings; a staircase = devices coming online one batch at a time; a plateau below 95% = trailing-edge devices never reboot.</p>
                <p><strong>No deferral adjustment here.</strong> This curve is deliberately the whole estate measured from Day 0 against your chosen target — Update Ring Quality Update Deferrals are <em>not</em> applied. Deferral-aware measurement belongs to the Patch Cycle Completion Trend below, which reads each device on its own supersedence date.</p>
                <p className="text-white/55 text-[11px]">A device counts as compliant only when we can positively identify the Windows build it is running. We never assume an unidentified build is patched.</p>
                <p className="text-white/55 text-[11px]">First proper "drop and climb" pattern appears the day after the next Patch Tuesday — until then, you may only see the tail end of the previous cycle.</p>
              </InfoPopover>
            </h3>
            {/* `scott/feature-updates-3` — inline target-curve selector + editor */}
            {onSaveTargetCurve && (
              <PatchAdoptionTargetCurveEditor
                curve={targetCurve}
                onSave={onSaveTargetCurve}
              />
            )}
          </div>
          {currentPT && (
            <p className="text-[11px] text-white/45 mb-3" data-testid="patch-tuesday-subheading">
              Patch Tuesday: <span className="font-mono text-white/65">{fmtShortDate(currentPT)}</span>
              <span className="mx-2 text-white/25">·</span>
              Day +7 = <span className="font-mono text-white/65">{fmtShortDate(dateForOffset(7))}</span>
              <span className="mx-2 text-white/25">·</span>
              Day +14 = <span className="font-mono text-white/65">{fmtShortDate(dateForOffset(14))}</span>
              <span className="mx-2 text-white/25">·</span>
              Day +30 = <span className="font-mono text-white/65">{fmtShortDate(dateForOffset(30))}</span>
            </p>
          )}

          {/* `scott/kpi-cycle-update2` — a step in the curve caused by the
              measurement, not by the estate.
              This is the ONLY resolver signal we surface here, and only when
              the chart visibly steps. The "N devices on a build we can't date"
              detail and the catalog sync control live on Settings > Data
              Management: customers should be able to trust that ALICE knows
              when each Windows build shipped, not have to reason about catalog
              coverage while reading a compliance report. */}
          {curveDiscontinuities.length > 0 && (
            <div
              className="mb-3 text-[11px] text-cyan-100/90 leading-snug px-2.5 py-2 rounded-lg bg-cyan-500/[0.07] border border-cyan-400/30"
              data-testid="adoption-resolver-discontinuity-banner"
            >
              <Info className="w-3 h-3 inline mr-1.5 -mt-0.5" />
              {curveDiscontinuities.map((d, i) => (
                <span key={i} className="block">
                  Our view of the estate improved between{' '}
                  <span className="font-mono text-white/85">Day +{d.from_day_offset}</span> and{' '}
                  <span className="font-mono text-white/85">Day +{d.to_day_offset}</span>
                  {d.compliance_delta_pct != null && (
                    <> — the {d.compliance_delta_pct > 0 ? '+' : ''}{d.compliance_delta_pct}pt step there
                    is that correction, not devices gaining or losing a patch.</>
                  )}
                </span>
              ))}
            </div>
          )}

          {/* Adoption vs Target status pills */}
          {hasAdoption && (latestActual || day14Actual != null) && (
            <div className="flex flex-wrap gap-2 mb-3" data-testid="adoption-status-pills">
              {latestActual && (
                <span
                  className={`inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full border ${
                    latestStatus === 'on_track'
                      ? 'border-emerald-400/40 bg-emerald-500/10 text-emerald-200'
                      : 'border-red-400/40 bg-red-500/10 text-red-200'
                  }`}
                  data-testid="adoption-latest-status-pill"
                  title={`Day +${latestActual.day}: actual ${latestActual.pct.toFixed(1)}% vs target ${latestActual.target}%`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${latestStatus === 'on_track' ? 'bg-emerald-400' : 'bg-red-400'}`}></span>
                  Day +{latestActual.day}: {latestActual.pct.toFixed(0)}% {latestStatus === 'on_track' ? '≥' : '<'} {latestActual.target}% target
                </span>
              )}
              {day14Actual != null && (
                <span
                  className={`inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full border ${
                    day14Status === 'on_track'
                      ? 'border-emerald-400/40 bg-emerald-500/10 text-emerald-200'
                      : 'border-red-400/40 bg-red-500/10 text-red-200'
                  }`}
                  data-testid="adoption-day14-status-pill"
                  title={`Industry benchmark: ${benchmarkAt(14)}% of devices compliant by Day +14`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${day14Status === 'on_track' ? 'bg-emerald-400' : 'bg-red-400'}`}></span>
                  Day +14 SLO: {day14Actual.toFixed(0)}% vs {benchmarkAt(14)}% target
                </span>
              )}
            </div>
          )}

          {/* `scott/qualityupdate-trending` — Multi-month overlay picker */}
          {previousMonthIds.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap mb-3" data-testid="adoption-overlay-picker">
              <span className="text-[11px] uppercase tracking-wider text-white/55">Overlay months</span>
              {overlayMonths.map((mid, idx) => (
                <span
                  key={mid}
                  className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border bg-white/[0.04]"
                  style={{ borderColor: `${OVERLAY_PALETTE[idx % OVERLAY_PALETTE.length]}55`, color: OVERLAY_PALETTE[idx % OVERLAY_PALETTE.length] }}
                  data-testid={`overlay-active-${mid}`}
                >
                  <span
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: OVERLAY_PALETTE[idx % OVERLAY_PALETTE.length] }}
                  />
                  {fmtMonthShort(mid)}
                  <button
                    type="button"
                    onClick={() => setOverlayMonths((arr) => arr.filter((m) => m !== mid))}
                    className="ml-1 opacity-70 hover:opacity-100"
                    data-testid={`overlay-remove-${mid}`}
                    aria-label={`Remove ${mid} overlay`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
              {(() => {
                const remaining = previousMonthIds.filter((mid) => !overlayMonths.includes(mid));
                if (!remaining.length || overlayMonths.length >= 6) return null;
                return (
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setOverlayPickerOpen((o) => !o)}
                      className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border border-cyan-400/40 bg-cyan-500/10 text-cyan-200 hover:bg-cyan-500/20"
                      data-testid="overlay-add-btn"
                    >
                      <Plus className="w-3 h-3" /> overlay
                    </button>
                    {overlayPickerOpen && (
                      <div
                        className="alice-menu absolute z-20 mt-1 left-0 min-w-[160px] rounded-lg border border-white/15 bg-slate-900/95 backdrop-blur shadow-xl p-1"
                        data-testid="overlay-picker-menu"
                      >
                        {remaining.map((mid) => (
                          <button
                            key={mid}
                            type="button"
                            onClick={() => {
                              setOverlayMonths((arr) => (arr.includes(mid) ? arr : [...arr, mid]));
                              setOverlayPickerOpen(false);
                            }}
                            className="w-full text-left text-[11px] px-2 py-1 rounded text-white/80 hover:bg-white/10"
                            data-testid={`overlay-pick-${mid}`}
                          >
                            {fmtMonthShort(mid)}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}
              {overlayMonths.length === 0 && (
                <span className="text-[11px] text-white/40">No previous months overlaid — pick one to compare</span>
              )}
            </div>
          )}

          {hasAdoption ? (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={adoptionWithOverlays} margin={{ top: 10, right: 16, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                <XAxis
                  dataKey="day_offset"
                  type="number"
                  domain={[0, 30]}
                  ticks={[0, 5, 7, 10, 14, 20, 25, 30]}
                  tick={{ fill: '#fff', fontSize: 11 }}
                  label={{ value: 'Days since Patch Tuesday', position: 'insideBottom', offset: -2, fill: '#9ca3af', fontSize: 11 }}
                />
                <YAxis tick={{ fill: '#fff', fontSize: 11 }} domain={[0, 100]} unit="%" />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px', color: '#fff' }}
                  formatter={(v, name) => (v == null ? ['—', name] : [`${Number(v).toFixed(1)}%`, name])}
                  labelFormatter={(v) => {
                    const d = dateForOffset(v);
                    return d ? `Day +${v} — ${fmtShortDate(d)}` : `Day +${v}`;
                  }}
                />
                <Legend
                  wrapperStyle={{ fontSize: 11, paddingTop: 6 }}
                  iconType="line"
                />
                {/* Day-14 SLO reference line — pulls the target from the
                    active curve so a Conservative/Aggressive/custom preset
                    surfaces the right "Target: NN% by Day 14" label. */}
                <ReferenceLine
                  x={14}
                  stroke="#f59e0b"
                  strokeDasharray="4 4"
                  strokeOpacity={0.6}
                  label={{
                    value: `Target: ${benchmarkAt(14)}% by Day 14`,
                    position: 'insideTopRight',
                    fill: '#fbbf24',
                    fontSize: 10,
                  }}
                />
                <ReferenceLine y={benchmarkAt(14)} stroke="#f59e0b" strokeDasharray="2 6" strokeOpacity={0.25} />
                {/* Benchmark / recommended target — dashed amber */}
                <Line
                  type="monotone"
                  dataKey="benchmark_pct"
                  name="Recommended Target"
                  stroke="#f59e0b"
                  strokeWidth={2}
                  strokeDasharray="6 4"
                  dot={false}
                  activeDot={false}
                  isAnimationActive={false}
                />
                {/* Estimated current-month days — dimmer cyan, smaller dots */}
                {hasEstimatedDays && (
                  <Line
                    type="monotone"
                    dataKey="compliance_pct_estimated"
                    name="Estimated (current-month gap fill)"
                    stroke="#22d3ee"
                    strokeWidth={1.5}
                    strokeOpacity={0.5}
                    strokeDasharray="2 4"
                    dot={{ r: 2.5, strokeOpacity: 0.5, fillOpacity: 0.5 }}
                    connectNulls
                    isAnimationActive={false}
                  />
                )}
                {/* Actual compliance — solid cyan */}
                <Line
                  type="monotone"
                  dataKey="compliance_pct"
                  name="Actual Compliance"
                  stroke="#22d3ee"
                  strokeWidth={2.5}
                  dot={{ r: 4 }}
                  connectNulls
                />
                {/* `scott/qualityupdate-trending` — Previous-month overlay
                    lines, one per active overlayMonths entry. Rendered
                    with a stable per-month colour so the legend stays
                    consistent as the user toggles months on/off. */}
                {overlayMonths.map((mid, idx) => {
                  const key = `overlay_${mid.replace('-', '_')}`;
                  const colour = OVERLAY_PALETTE[idx % OVERLAY_PALETTE.length];
                  return (
                    <Line
                      key={key}
                      type="monotone"
                      dataKey={key}
                      name={fmtMonthShort(mid)}
                      stroke={colour}
                      strokeWidth={1.5}
                      strokeOpacity={0.85}
                      dot={{ r: 2.5, strokeOpacity: 0.85, fillOpacity: 0.85 }}
                      connectNulls
                      isAnimationActive={false}
                    />
                  );
                })}
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-white/60 text-center py-8 text-sm">
              No adoption data yet for {currentMonthId || 'this month'}. Data accumulates daily — first datapoint
              appears on the day of Patch Tuesday and grows for 30 days.
            </p>
          )}
        </div>

        {/* A. Monthly Compliance Trend — moved below the Patch Adoption Curve so the in-flight cycle leads.
            `scott/qualityupdate-trending` — Collapsed by default until at
            least 3 months of REAL (non-estimated) data are available, since
            the previous behaviour rendered a heavily-estimated chart that
            was misleading the first time someone opened the page. */}
        <div data-testid="monthly-compliance-trend-section">
          <button
            type="button"
            onClick={() => setMonthlyTrendExpanded((v) => !v)}
            className="w-full flex items-center justify-between text-left mb-2 group"
            data-testid="monthly-compliance-trend-toggle"
          >
            <span className="text-sm font-semibold text-white/80 flex items-center gap-2">
              <ChevronDown
                className={`w-4 h-4 text-white/60 transition-transform ${monthlyTrendExpanded ? '' : '-rotate-90'}`}
              />
              Patch Cycle Completion Trend
              {monthlyTrendAutoCollapsed && (
                <span
                  className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border border-amber-400/40 bg-amber-500/10 text-amber-200 font-medium"
                  data-testid="monthly-trend-low-data-pill"
                >
                  {realMonthlyCount}/3 months collected
                </span>
              )}
            </span>
            <span className="text-[11px] text-white/45 group-hover:text-white/65 transition">
              {monthlyTrendExpanded ? 'Collapse' : (monthlyTrendAutoCollapsed ? 'Show projected view' : 'Expand')}
            </span>
          </button>
          {!monthlyTrendExpanded && monthlyTrendAutoCollapsed && (
            <p
              className="text-[11px] text-white/55 leading-snug pl-6 pr-2 py-2 rounded-lg bg-white/[0.03] border border-white/10"
              data-testid="monthly-trend-low-data-explainer"
            >
              <Info className="w-3 h-3 inline mr-1.5 -mt-0.5 text-amber-300" />
              We need 3 completed Patch-Tuesday cycles to draw a meaningful month-over-month trend.
              Currently collected: <span className="font-mono text-white/80">{realMonthlyCount}</span>. Expand to preview
              a <em>"projected from current trajectory"</em> view for the missing months.
            </p>
          )}
        {monthlyTrendExpanded && (
        <div>
          <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
            <h3 className="text-sm font-semibold text-white/80 flex items-center gap-2">
              Patch Cycle Completion Rate · last {monthlyForChart.length} months
              <InfoPopover label="Patch Cycle Completion Trend">
                {/* `scott/kpi-cycle-update` — required tooltip copy. */}
                <p>Each point answers one question: <strong>did the estate finish installing that month's quality update before the next month's update became available to it?</strong></p>
                <p><strong>Results are measured at device-specific supersedence dates</strong>, derived from each device's assigned Update Ring deferral:</p>
                <p className="font-mono text-[11px] text-white/70">Supersedence date = next Patch Tuesday + that ring's Quality Update Deferral</p>
                <p><strong>Worked example</strong> — Production ring, 14-day deferral: the 14 July update reaches those devices on 28 July; the 11 August update reaches them on 25 August. So the July cycle is scored by compliance <em>on 25 August</em>, immediately before August's update superseded it.</p>
                <p><strong>Per-device deferrals</strong>: rings are read on <em>different dates</em> and pooled as <code>devices completed ÷ devices applicable</code> — never a tenant-wide deferral, never an average of ring percentages.</p>
                <p><strong>Not a peak.</strong> A month that briefly touched 98% and slipped back to 88% before supersedence scores 88%. Temporary highs caused by new devices, eligibility changes or stale reporting no longer stick.</p>
                <p><strong>Recommended Target (dashed amber line at 95%)</strong>: the enterprise SLO for quality-update compliance. A faint 90% line marks the warning floor — sustained dips below mean rings need re-balancing.</p>
                <p><strong>Healthy</strong>: a roughly-flat line at 95%+ month-after-month. <strong>Deteriorating</strong>: a downward slope — each patch is landing on fewer devices before supersedence.</p>
                {wufbConnected && (
                  <p className="text-cyan-200/80 text-[11px]">
                    <strong>WUfB source</strong>: when Windows Update for Business reporting is connected, <code>UCClientUpdateStatus</code> is queried directly for each device's install state on its own supersedence date. Because that table keeps per-device history, it can recalculate months that pre-date our local per-ring snapshots.
                  </p>
                )}
                <p className="text-amber-200/80 text-[11px]">
                  <strong>The dotted, paler section</strong> is <em>projected from the current trajectory</em> — cycles that closed before we were collecting snapshots, extrapolated from the latest snapshot we do have. It runs into the solid line at the first month we actually measured. Same line, same metric; only the confidence changes. The amber dashes are the target and floor, not data. Requires at least Day +3 of data — earlier than that the slope is too steep to extrapolate honestly.
                </p>
                <p className="text-white/55 text-[11px]">Cycles are plotted only once <em>every</em> ring has passed its supersedence date. The current cycle ({currentMonthId || '—'}) — and any earlier cycle still waiting on its slowest ring — is reported as <em>In Progress</em> in the KPI tiles above instead.</p>
              </InfoPopover>
            </h3>
            <div className="flex items-center gap-2 flex-wrap">
              {/* `scott/kpi-cycle-update` — WUfB overlay source indicator.
                  `basis` distinguishes a true per-ring supersedence
                  measurement from the degraded no-ring-map fallback. */}
              {data?.wufb_overlay?.enabled && (
                <span
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider border border-cyan-400/40 bg-cyan-500/10 text-cyan-200"
                  title={`Cycle completion rates measured in WUfB Log Analytics (${data.wufb_overlay.row_count} cycle${data.wufb_overlay.row_count === 1 ? '' : 's'})${
                    data.wufb_overlay.basis === 'wufb_no_ring_map'
                      ? ' — read at the next Patch Tuesday: no device→ring map was available, so per-ring deferrals could not be applied'
                      : ' — each device read on its own supersedence date (next Patch Tuesday + its ring deferral)'
                  }`}
                  data-testid="monthly-trend-wufb-pill"
                >
                  <CheckCircle2 className="w-3 h-3" />
                  Source: WUfB
                </span>
              )}
              {data?.wufb_overlay?.error && (
                <span
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border border-amber-400/40 bg-amber-500/10 text-amber-200"
                  title={data.wufb_overlay.error}
                  data-testid="monthly-trend-wufb-error-pill"
                >
                  <AlertCircle className="w-3 h-3" />
                  WUfB overlay skipped
                </span>
              )}
              {/* `scott/qualityupdate-trending` — Per-ring filter was
                  moved to the section header (CardHeader above) because
                  the same ring scope is now applied across KPIs, the
                  adoption curve AND the monthly trend. Keeping the
                  filter local to one chart would have been misleading. */}
              {/* `scott/qualityupdate-trending` — Project / backfill button
                  moved from the section header to this chart's action row.
                  Per operator request: keep the action close to the data
                  it affects, not at the top of the whole section. */}
              <Button
                onClick={onBackfill}
                disabled={backfilling}
                className="bg-cyan-500/20 border border-cyan-400/40 text-cyan-100 hover:bg-cyan-500/30 text-xs h-7 px-3"
                data-testid="trend-backfill-btn"
              >
                {backfilling
                  ? <><RefreshCw className="w-3 h-3 mr-1.5 animate-spin" /> Projecting…</>
                  : <><RefreshCw className="w-3 h-3 mr-1.5" /> Project 6 months</>}
              </Button>
            </div>
          </div>

          {hasMonthly && monthlyGovernance && (
            <div className="flex flex-wrap gap-2 mb-3" data-testid="monthly-trend-governance-pills">
              <span
                className={`inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full border ${
                  monthlyGovernance.tier === 'good'
                    ? 'border-emerald-400/40 bg-emerald-500/10 text-emerald-200'
                    : monthlyGovernance.tier === 'warn'
                    ? 'border-amber-400/40 bg-amber-500/10 text-amber-200'
                    : 'border-red-400/40 bg-red-500/10 text-red-200'
                }`}
                data-testid="monthly-governance-pill"
                title="Monthly quality updates whose Patch Cycle Completion Rate met the 95% enterprise SLO at each ring's supersedence date"
              >
                <span
                  className={`w-1.5 h-1.5 rounded-full ${
                    monthlyGovernance.tier === 'good'
                      ? 'bg-emerald-400'
                      : monthlyGovernance.tier === 'warn'
                      ? 'bg-amber-400'
                      : 'bg-red-400'
                  }`}
                ></span>
                {monthlyGovernance.on_target} of last {monthlyGovernance.total} months ≥ 95% target
              </span>
            </div>
          )}

          {hasMonthly && monthlyForChart.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={monthlyForChart} margin={{ top: 10, right: 16, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                <XAxis dataKey="month_id" tick={{ fill: '#fff', fontSize: 11 }} />
                <YAxis tick={{ fill: '#fff', fontSize: 11 }} domain={[0, 100]} unit="%" />
                {/* Faint "good zone" tint above 95% — subtle, dark-mode friendly */}
                <ReferenceArea y1={95} y2={100} fill="#22c55e" fillOpacity={0.05} />
                {/* 95% target line (industry SLO) — dashed amber.
                    `scott/kpi-cycle-update2` — dimmed so it reads as chart
                    furniture rather than a data series. A tenant sitting in
                    the low 90s has its data lines running right alongside
                    this, and a full-width amber dash at the same weight was
                    being read as a second metric. */}
                <ReferenceLine
                  y={95}
                  stroke="#f59e0b"
                  strokeDasharray="6 4"
                  strokeWidth={1}
                  strokeOpacity={0.45}
                  label={{
                    value: 'Target: ≥ 95%',
                    position: 'insideTopRight',
                    fill: '#fbbf24',
                    fillOpacity: 0.7,
                    fontSize: 10,
                  }}
                />
                {/* 90% warning floor — dotted, low opacity */}
                <ReferenceLine
                  y={90}
                  stroke="#f59e0b"
                  strokeDasharray="2 6"
                  strokeOpacity={0.25}
                  label={{
                    value: '90% floor',
                    position: 'insideBottomRight',
                    fill: '#fbbf24',
                    fillOpacity: 0.5,
                    fontSize: 10,
                  }}
                />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px', color: '#fff' }}
                  content={({ active, payload, label }) => {
                    if (!active || !payload || !payload.length) return null;
                    const d = payload[0]?.payload || {};
                    const pt = patchTuesdayOf(label);
                    const pct = d.cycle_pct ?? d.cycle_pct_projected;
                    const src = d.cycle_source;
                    const isProjected = d.projected;
                    const basis = d.cycle_basis;
                    const breakdown = d.cycle_breakdown || [];
                    const d14 = d.day14_pct_strict;
                    return (
                      <div style={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: 8, padding: 10, color: '#fff', fontSize: 12 }} data-testid="monthly-trend-tooltip">
                        <div style={{ fontWeight: 600 }}>{label}{pt ? ` · PT ${fmtShortDate(pt)}` : ''}</div>
                        <div style={{ marginTop: 4 }}>
                          Cycle completion rate: <span style={{ fontWeight: 600, color: isProjected ? '#a5f3fc' : '#22d3ee' }}>{pct == null ? '—' : `${pct}%`}</span>
                        </div>
                        {d.cycle_applicable > 0 && (
                          <div style={{ color: '#cbd5e1', fontSize: 11 }}>
                            {d.cycle_completed}/{d.cycle_applicable} devices completed before supersedence
                          </div>
                        )}
                        {/* `scott/kpi-cycle-update` — show WHERE each reading
                            was taken. Rings with different deferrals are
                            measured on different dates, and operators need to
                            see those dates to trust the pooled number. */}
                        {(basis === 'supersedence' || basis === 'supersedence_approx' || basis === 'wufb') &&
                          breakdown.filter((b) => b.applicable_devices > 0).map((b, i) => (
                            <div key={i} style={{ color: '#cbd5e1', fontSize: 11 }}>
                              +{b.deferral_days ?? 0}d ring · superseded {b.supersedence_date}
                              {b.completion_pct != null ? ` · ${b.completion_pct}%` : ''}
                              {b.status === 'approximated' ? ` (read ${b.measured_date})` : ''}
                            </div>
                          ))}
                        {basis === 'cycle_end_proxy' && (
                          <div style={{ color: '#fcd34d', fontSize: 11 }}>
                            Measured at cycle close — this cycle pre-dates per-ring
                            supersedence history, so deferred rings may read low
                          </div>
                        )}
                        {basis === 'wufb_no_ring_map' && (
                          <div style={{ color: '#fcd34d', fontSize: 11 }}>
                            Measured at the next Patch Tuesday — no device→ring map available
                          </div>
                        )}
                        {d14 != null && (
                          <div style={{ color: '#cbd5e1', fontSize: 11, marginTop: 2 }}>
                            Day-14 (Cyber Essentials): <span style={{ color: '#fff' }}>{d14}%</span>
                          </div>
                        )}
                        {src && (
                          <div style={{ color: src === 'wufb' ? '#67e8f9' : (src === 'measured' ? '#a7f3d0' : '#fcd34d'), fontSize: 11, marginTop: 2 }}>
                            Source: {src === 'wufb' ? 'WUfB ✓' : src === 'measured' ? 'Measured snapshot' : 'Projected from current trajectory'}
                          </div>
                        )}
                      </div>
                    );
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {/* `scott/kpi-cycle-update` — single cycle-completion line.
                    Solid cyan for measured / WUfB, dashed amber for projected
                    backfill rows. The 7/14/30-day breakdown was retired here
                    because it duplicated information already shown on the
                    Adoption Curve and made the monthly chart hard to read. */}
                {/* `scott/kpi-cycle-update2` — one metric, one path, two
                    styles. The projected series runs dotted from the oldest
                    month up to the FIRST measured month and stops there; the
                    solid series starts at that same month. They share one
                    vertex and no segment, so neither line spans the other's
                    range. Projected renders first so the solid dot sits on
                    top at the junction.

                    Colour: a PALER CYAN, not amber. Amber in this chart means
                    "target / floor" — the 95% and 90% reference lines are
                    amber dashes running the full width. An amber dotted data
                    series merges into them (a tenant in the mid-90s plots
                    right on top of the target line) and reads as a second,
                    longer metric. Same hue as the measured line, dotted and
                    dimmed, says what it actually is: the same number, lower
                    confidence. */}
                {hasProjectedMonths && (
                  <Line
                    type="monotone"
                    dataKey="cycle_pct_projected"
                    name="Projected from current trajectory"
                    stroke="#a5f3fc"
                    strokeWidth={2}
                    strokeOpacity={0.75}
                    strokeDasharray="2 4"
                    connectNulls={false}
                    isAnimationActive={false}
                    activeDot={false}
                    dot={(props) => {
                      // The junction month belongs to the solid line — it draws
                      // the marker there, so we draw nothing.
                      const p = props?.payload || {};
                      if (p.is_projection_junction) {
                        return <circle key={`p-${props.index}`} cx={props.cx} cy={props.cy} r={0} />;
                      }
                      // A month we DID measure can still sit inside the dotted
                      // stretch (a lone snapshot among backfilled cycles).
                      // Mark it solid — the line style says "this era is
                      // backfill", the dot says "this point is real".
                      return (
                        <circle
                          key={`p-${props.index}`}
                          cx={props.cx} cy={props.cy} r={4}
                          fill={p.projected ? '#1e293b' : '#22d3ee'}
                          stroke={p.projected ? '#a5f3fc' : '#22d3ee'}
                          strokeWidth={2}
                        />
                      );
                    }}
                  />
                )}
                <Line
                  type="monotone"
                  dataKey="cycle_pct"
                  name="Cycle completion rate"
                  stroke="#22d3ee"
                  strokeWidth={2.5}
                  dot={{ r: 4, fill: '#22d3ee', stroke: '#22d3ee' }}
                  connectNulls={false}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <EmptyTrendState onBackfill={onBackfill} backfilling={backfilling} />
          )}

          {hasMonthly && currentMonthId && (
            <p className="text-[11px] text-white/45 mt-2" data-testid="monthly-trend-current-month-note">
              <Info className="w-3 h-3 inline mr-1 -mt-0.5" />
              Current cycle <span className="font-mono text-white/65">{currentMonthId}</span> is excluded — it joins the chart once <em>every</em> ring has passed its supersedence date (next Patch Tuesday + that ring's Quality Update Deferral), which for a 14-day ring is two weeks into the following month. The new cycle becomes "current" once Microsoft publishes at <strong>10:00 Pacific</strong> on Patch Tuesday (17:00 UTC in PDT, 18:00 UTC in PST) — not at midnight.
            </p>
          )}
        </div>
        )}
        </div>

        <p className="text-[11px] text-white/45">
          Compliance is computed without WUfB by mapping each device's current build to its Microsoft release
          month. A device counts as compliant for month <em>M</em> if its build's release month is <em>M</em> or
          newer. Snapshots are written daily at 01:00 UTC and after every "Sync from Intune".
          Selecting an Update Ring above rescopes the KPIs, the adoption curve and the cycle trend
          to devices in that ring; historical per-ring data accrues from the day per-ring snapshots
          start being written.
          {' '}The Patch Cycle Completion Rate reads each device on its own supersedence date
          (<span className="font-mono text-white/60">next Patch Tuesday + its ring's Quality Update Deferral</span>)
          and pools the rings as <span className="font-mono text-white/60">completed ÷ applicable</span>;
          Day-14 Compliance counts the whole estate from Patch Tuesday with no deferral adjustment.
          Cycles that closed before per-ring supersedence history was recorded fall back to the
          cycle-close reading (flagged in the tooltip) unless WUfB reporting can recalculate them.
        </p>
      </CardContent>
      )}
    </Card>
  );
};

const EmptyTrendState = ({ onBackfill, backfilling }) => (
  <div className="text-center py-10 px-4 rounded-lg border border-dashed border-white/15 bg-white/[0.03]" data-testid="monthly-trend-empty-state">
    <TrendingUp className="w-8 h-8 text-cyan-400/60 mx-auto mb-3" />
    <p className="text-white/80 text-sm font-medium">No trend data yet</p>
    <p className="text-white/55 text-xs mt-1 max-w-md mx-auto">
      Real snapshots accumulate daily going forward. Click below to project the previous 6 months
      from each device's current build + last-sync date — months without a real datapoint
      will appear as a dashed <em>"projected from current trajectory"</em> line.
    </p>
    <Button
      onClick={onBackfill}
      disabled={backfilling}
      className="mt-4 bg-cyan-500/20 border border-cyan-400/40 text-cyan-100 hover:bg-cyan-500/30 text-xs"
      data-testid="monthly-trend-project-btn"
    >
      {backfilling ? 'Projecting…' : 'Project 6 months from device history'}
    </Button>
  </div>
);

export const OSPatchDashboard = () => {
  // Reporting-only tenants can't read Intune update policies; the backend
  // refuses /update-rings for them, so ring data and ring KPIs are skipped.
  const { canViewOSPatchPolicies } = usePermissions();
  const [metrics, setMetrics] = useState(null);
  const [rings, setRings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncStep, setSyncStep] = useState('');
  const [lastSync, setLastSync] = useState(null);
  // Catalog freshness badge moved to Reports page

  const loadData = async () => {
    setLoading(true);
    try {
      const [metricsRes, ringsRes, syncStatusRes, autopatchRes] = await Promise.all([
        osPatchApi.getDashboardMetrics(),
        canViewOSPatchPolicies ? osPatchApi.getUpdateRings() : Promise.resolve({ data: [] }),
        axios.get(`${API}/sync/status`).catch(() => ({ data: null })),
        axios.get(`${API}/reports/autopatch-quality-status`).catch(() => ({ data: { rings: [] } }))
      ]);
      setMetrics(metricsRes.data);
      
      // Enrich rings with success rate from autopatch status
      const autopatchRings = autopatchRes.data?.rings || [];
      const autopatchByName = {};
      autopatchRings.forEach(r => {
        if (r.name) autopatchByName[r.name.toLowerCase()] = r;
      });
      
      const enrichedRings = (Array.isArray(ringsRes.data) ? ringsRes.data : []).map(ring => {
        const ringName = (ring.name || ring.display_name || '').toLowerCase();
        const autopatch = autopatchByName[ringName];
        return {
          ...ring,
          success_rate: autopatch?.latest_quality_update_pct || 0,
          device_count: autopatch?.device_count || ring.device_count || 0
        };
      });
      
      setRings(enrichedRings);
      if (syncStatusRes.data) {
        setLastSync(syncStatusRes.data);
      }
    } catch (error) {
      console.error('Error loading dashboard:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleSyncAll = async () => {
    setSyncing(true);
    setSyncStep('Starting sync…');
    try {
      // /sync/all now returns immediately with a job_id; runSyncWithPolling
      // hides the polling loop and resolves with the final results.
      const r = await runSyncWithPolling({
        onProgress: (step) => setSyncStep(step),
      });

      let msg = canViewOSPatchPolicies
        ? `Sync completed: ${r.devices?.count || 0} devices, ${r.update_rings?.count || 0} rings`
        : `Sync completed: ${r.devices?.count || 0} devices`;
      if (r.devices?.stale_removed > 0) {
        msg += ` (${r.devices.stale_removed} stale device${r.devices.stale_removed > 1 ? 's' : ''} removed)`;
      }
      if (r.cleanup?.duplicates_removed > 0) {
        msg += ` (${r.cleanup.duplicates_removed} duplicates cleaned)`;
      }
      if (r.catalog?.status === 'success') {
        const n = (r.catalog.entries_synced || 0) + (r.catalog.entries_updated || 0);
        if (n > 0) msg += ` · catalog: ${r.catalog.entries_synced || 0} new / ${r.catalog.entries_updated || 0} updated`;
      } else if (r.catalog?.status === 'skipped') {
        msg += ` · catalog cached (${r.catalog.age_hours ?? '?'}h old)`;
      } else if (r.catalog?.status === 'error') {
        msg += ` · catalog sync failed (${r.catalog.error || 'unknown'})`;
      }
      toast.success(msg);
      setLastSync(r);
      // Reload dashboard data
      await loadData();
    } catch (error) {
      console.error('Error syncing:', error);
      if (error?.isJobLost) {
        toast.warning(error.message);
      } else {
        toast.error('Sync failed: ' + (error.response?.data?.detail || error.message));
      }
    } finally {
      setSyncing(false);
      setSyncStep('');
    }
  };

  useEffect(() => { loadData(); }, []);

  if (loading || !metrics) {
    return (
      <div className="p-8" data-testid="os-patch-dashboard">
        <LoadingSpinner message="Loading OS Patch Dashboard..." />
      </div>
    );
  }

  // Prepare chart data
  const complianceData = [
    { name: 'Compliant', value: metrics.compliant_devices || 0, color: '#22C55E' },
    { name: 'Non-Compliant', value: metrics.non_compliant_devices || (metrics.managed_devices - metrics.compliant_devices) || 0, color: CHART_COLORS.coral },
  ];

  const deploymentData = [
    { name: 'Up to Date', value: metrics.up_to_date_count || Math.round((metrics.windows_managed_devices || 0) * (metrics.up_to_date_pct || 0) / 100), color: '#22C55E' },
    { name: 'In Progress', value: metrics.in_progress_count || 0, color: CHART_COLORS.cyan },
    { name: 'Not Up to Date', value: metrics.not_up_to_date_count || metrics.pending_updates || 0, color: CHART_COLORS.coral },
  ].filter(d => d.value > 0);

  return (
    <div className="p-8" data-testid="os-patch-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Dashboard</h1>
          <p className="text-white/90">Get an overview of your update deployments</p>
          {lastSync?.completed_at && (
            <p className="text-xs text-white/60 mt-1">
              Last synced: {new Date(lastSync.completed_at).toLocaleString()}
              {lastSync.trigger === 'nightly_scheduler' && ' (automatic)'}
            </p>
          )}
        </div>
        <div className="flex gap-3">
          <Button 
            onClick={handleSyncAll} 
            disabled={syncing}
            className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white hover:from-cyan-600 hover:to-blue-600 font-semibold disabled:opacity-50" 
            data-testid="sync-all-btn"
            title={syncing && syncStep ? syncStep : 'Sync data from Intune'}
          >
            {syncing ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                {syncStep ? (syncStep.length > 32 ? syncStep.slice(0, 32) + '…' : syncStep) : 'Syncing…'}
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                Sync from Intune
              </>
            )}
          </Button>
          <Button onClick={loadData} variant="outline" className="border-white/20 text-white hover:bg-white/10" data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      <OSPatchReadOnlyNotice className="mb-4" />

      {/* Main KPIs - Bento Grid */}
      <div className={`alice-kpi-row grid grid-cols-1 md:grid-cols-2 ${canViewOSPatchPolicies ? 'lg:grid-cols-4' : ''} gap-6 mb-8`}>
        {/* Total Update Rings Card - Matching style with other KPI cards */}
        {canViewOSPatchPolicies && (
          <KPICard
            label="Total Update Rings"
            value={metrics.total_rings}
            subtitle="Active deployment configurations"
            icon={Settings}
            accentColor={CHART_COLORS.purple}
          />
        )}
        <KPICard label="Managed Devices" value={metrics.managed_devices} subtitle={`${metrics.windows_managed_devices ?? 0} Windows · ${metrics.online_devices} online`} icon={Monitor} accentColor={CHART_COLORS.blue} />
        <KPICard label="Compliance Rate" value={`${metrics.compliance_rate}%`} subtitle={`${metrics.compliant_devices} compliant`} icon={CheckCircle} accentColor="#22C55E" />
        {canViewOSPatchPolicies && (
          <KPICard label="Feature Updates" value={metrics.feature_update_profiles || 0} subtitle="Profiles configured" icon={Rocket} accentColor={CHART_COLORS.cyan} />
        )}
      </div>

      {/* Update Status KPIs */}
      <div className={`alice-kpi-row grid grid-cols-1 ${canViewOSPatchPolicies ? 'md:grid-cols-4' : 'md:grid-cols-3'} gap-6 mb-8`}>
        <KPICard label="Up to Date" value={`${metrics.up_to_date_pct || metrics.deployment_success || 0}%`} subtitle={`${metrics.up_to_date_count || 0} of ${metrics.windows_managed_devices ?? 0} Windows on latest`} icon={CheckCircle} accentColor="#22C55E" />
        <KPICard label="In Progress" value={`${metrics.in_progress_pct || 0}%`} subtitle={`${metrics.in_progress_count || 0} devices updating`} icon={Download} accentColor={CHART_COLORS.cyan} />
        <KPICard label="Not Up to Date" value={`${metrics.not_up_to_date_pct || (100 - (metrics.up_to_date_pct || 0)).toFixed(0)}%`} subtitle={`${metrics.not_up_to_date_count ?? metrics.pending_updates ?? 0} of ${metrics.windows_managed_devices ?? 0} Windows need updates`} icon={AlertTriangle} accentColor={CHART_COLORS.coral} />
        {canViewOSPatchPolicies && (
          <KPICard label="Deployments" value={metrics.deployment_groups_count || 0} subtitle={`${metrics.deployment_plans_count ?? 0} feature update plans · ${metrics.azure_ad_deployment_groups_count ?? 0} Azure AD groups`} icon={Users} accentColor={CHART_COLORS.purple} />
        )}
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Compliance Chart */}
        <Card className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border-[rgba(229,239,250,0.1)]" data-testid="compliance-chart">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Shield className="w-5 h-5 text-green-400" />
              Device Compliance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-center">
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie
                    data={complianceData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                    stroke="transparent"
                  >
                    {complianceData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#1e293b', 
                      border: '1px solid #334155',
                      borderRadius: '8px',
                      color: '#fff'
                    }}
                    formatter={(value, name) => [`${value} devices`, name]}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex justify-center gap-6 mt-4">
              {complianceData.map((item, index) => (
                <div key={index} className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                  <span className="text-sm text-white/90">{item.name}: <span className="text-white font-semibold">{item.value}</span></span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Update Progress Chart */}
        <Card className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border-[rgba(229,239,250,0.1)]" data-testid="deployment-chart">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Activity className="w-5 h-5 text-cyan-400" />
              Update Progress
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={deploymentData}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={3}
                  dataKey="value"
                  stroke="transparent"
                >
                  {deploymentData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#1e293b', 
                    border: '1px solid #334155',
                    borderRadius: '8px',
                    color: '#fff'
                  }}
                  formatter={(value, name) => [`${value} devices`, name]}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex justify-center gap-4 mt-2 flex-wrap">
              {deploymentData.map((item, index) => (
                <div key={index} className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                  <span className="text-xs text-white/90">{item.name}: <span className="text-white font-semibold">{item.value}</span></span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Update Rings Table */}
      {canViewOSPatchPolicies && (
      <Card className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border-[rgba(229,239,250,0.1)]" data-testid="update-rings-table">
        <CardHeader className="border-b border-[rgba(229,239,250,0.1)]">
          <CardTitle className="text-white flex items-center gap-2">
            <Layers className="w-5 h-5 text-purple-400" />
            Recent Update Rings
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px]">
                  <th className="text-left p-4 text-white/90 font-medium">Ring Name</th>
                  <th className="text-left p-4 text-white/90 font-medium">Quality Deferral</th>
                  <th className="text-left p-4 text-white/90 font-medium">Feature Deferral</th>
                  <th className="text-left p-4 text-white/90 font-medium">Success Rate</th>
                  <th className="text-left p-4 text-white/90 font-medium">Devices</th>
                </tr>
              </thead>
              <tbody>
                {rings.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="text-center py-12 text-white/70">
                      No update rings found. Create your first ring to get started!
                    </td>
                  </tr>
                ) : (
                  rings.slice(0, 5).map((ring, i) => (
                    <tr key={ring.id || i} className="border-t border-white/5 hover:bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] transition-colors">
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(0, 212, 228, 0.15)' }}>
                            <Settings className="w-5 h-5" style={{ color: '#00D4E4' }} />
                          </div>
                          <div>
                            <div className="text-white font-medium">{ring.name || ring.display_name}</div>
                            {ring.description && <div className="text-xs text-white/70">{ring.description}</div>}
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        <span className="px-3 py-1 rounded-full text-sm font-medium bg-cyan-500/20 text-cyan-400">
                          {ring.quality_deferral || ring.qualityUpdatesDeferralPeriodInDays || 0} days
                        </span>
                      </td>
                      <td className="p-4">
                        <span className="px-3 py-1 rounded-full text-sm font-medium bg-purple-500/20 text-purple-400">
                          {ring.feature_deferral || ring.featureUpdatesDeferralPeriodInDays || 0} days
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <div className="w-20 h-2 bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] rounded-full overflow-hidden">
                            <div 
                              className="h-full rounded-full transition-all duration-500"
                              style={{ 
                                width: `${ring.success_rate || 0}%`,
                                background: (ring.success_rate || 0) >= 90 
                                  ? '#22C55E' 
                                  : (ring.success_rate || 0) >= 70 
                                    ? CHART_COLORS.cyan 
                                    : CHART_COLORS.coral
                              }}
                            />
                          </div>
                          <span className="text-white font-mono text-sm">{ring.success_rate || 0}%</span>
                        </div>
                      </td>
                      <td className="p-4">
                        <span className="text-white/90">{ring.device_count || 0}</span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
      )}
    </div>
  );
};

// 09/09 — Review & commit for staged FU-phase changes.
//
// Two round trips on purpose. The first is a dry run: it returns exactly what
// the commit will do to each live deployment, and the admin approves that.
// Removing a device from a sprint stops Intune offering it the update, and
// re-admitting one puts it back in an Entra group — neither should happen off
// the back of a checkbox click nobody read twice.
const PhaseChangeCommitModal = ({ changes, onClose, onCommitted }) => {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [committing, setCommitting] = useState(false);
  const [error, setError] = useState(null);

  const payload = useMemo(
    () => changes.map(c => ({ device_id: c.device_id, phase: c.phase })),
    [changes],
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await osPatchApi.applyDeviceFeaturePhases(payload, { dryRun: true });
        if (!cancelled) setPlan(res.data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || 'Could not work out the impact of these changes');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, []);

  const handleCommit = async () => {
    setCommitting(true);
    try {
      const res = await osPatchApi.applyDeviceFeaturePhases(payload, { dryRun: false });
      const committed = {};
      changes.forEach(c => { committed[c.device_id] = c.phase; });
      const entra = res.data?.entra || {};
      const moved = (entra.removed || 0) + (entra.added || 0);
      toast.success(
        `Committed ${res.data?.phase_changes ?? changes.length} phase change(s)`
        + (moved ? ` · ${entra.added || 0} added to / ${entra.removed || 0} removed from Entra groups` : '')
      );
      if (entra.failed) {
        toast.warning(`${entra.failed} Entra membership change(s) failed — see the deployment card`);
      }
      onCommitted(committed);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to commit phase changes');
      setCommitting(false);
    }
  };

  const deployments = plan?.deployments || [];
  const totalRemovals = deployments.reduce((n, d) => n + (d.removals?.length || 0), 0);
  const totalAdditions = deployments.reduce((n, d) => n + (d.additions?.length || 0), 0);
  const totalBlocked = deployments.reduce((n, d) => n + (d.blocked?.length || 0), 0);

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="phase-commit-modal">
      <div className="alice-popup bg-slate-900 border border-white/15 rounded-xl w-full max-w-3xl max-h-[85vh] flex flex-col">
        <div className="p-5 border-b border-white/10">
          <h3 className="text-lg font-semibold text-white">Commit Deployment Sprint changes</h3>
          <p className="text-sm text-white/60 mt-1">
            {changes.length} device{changes.length === 1 ? '' : 's'}. Review what this does to live
            deployments before saving — nothing has been written yet.
          </p>
        </div>

        <div className="p-5 overflow-y-auto space-y-4">
          {/* The phase changes themselves */}
          <div className="rounded-lg border border-white/10 overflow-hidden">
            <table className="w-full text-sm" data-testid="phase-commit-changes">
              <thead className="bg-white/5 text-white/60 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">Device</th>
                  <th className="text-left px-3 py-2 font-medium">From</th>
                  <th className="text-left px-3 py-2 font-medium">To</th>
                </tr>
              </thead>
              <tbody>
                {changes.map(c => (
                  <tr key={c.device_id} className="border-t border-white/5">
                    <td className="px-3 py-1.5 text-white truncate max-w-[240px]">{c.device_name}</td>
                    <td className={`px-3 py-1.5 ${PHASE_CONFIG[c.from]?.textColor || 'text-white/60'}`}>
                      {PHASE_CONFIG[c.from]?.label || c.from}
                    </td>
                    <td className={`px-3 py-1.5 font-medium ${PHASE_CONFIG[c.phase]?.textColor || 'text-white'}`}>
                      {PHASE_CONFIG[c.phase]?.label || c.phase}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {loading && <LoadingSpinner message="Working out the impact on live deployments..." />}

          {error && (
            <div className="rounded-lg border border-amber-400/40 bg-amber-500/10 p-3 text-sm text-amber-100"
              data-testid="phase-commit-error">
              {error}
            </div>
          )}

          {!loading && !error && plan && (
            <>
              {plan.graph_configured === false && (
                <div className="rounded-lg border border-amber-400/40 bg-amber-500/10 p-3 text-xs text-amber-100">
                  Graph is not configured for this tenant. The allocation will be rewritten, but Entra group
                  membership will not change — Intune will keep offering the update until it is.
                </div>
              )}

              {deployments.length === 0 ? (
                <p className="text-sm text-white/60" data-testid="phase-commit-no-impact">
                  No live deployment is affected. These devices are not in any active sprint, so this only
                  changes how they will be allocated the next time a deployment is planned.
                </p>
              ) : (
                <>
                  <div className="text-sm text-white/80">
                    <span className="font-medium">Impact:</span>{' '}
                    <span className="text-red-300">{totalRemovals} removal{totalRemovals === 1 ? '' : 's'}</span>
                    {' · '}
                    <span className="text-emerald-300">{totalAdditions} re-admission{totalAdditions === 1 ? '' : 's'}</span>
                    {totalBlocked > 0 && (
                      <>{' · '}<span className="text-amber-300">{totalBlocked} blocked</span></>
                    )}
                  </div>
                  {deployments.map(dep => (
                    <div key={dep.deployment_id} className="rounded-lg border border-white/10 p-3"
                      data-testid={`phase-commit-deployment-${dep.deployment_id}`}>
                      <div className="text-sm font-medium text-white mb-2">
                        {dep.deployment_name}
                        <span className="text-white/40 font-normal"> · {dep.target_version}</span>
                      </div>
                      <div className="space-y-1 text-xs">
                        {(dep.removals || []).map(r => (
                          <div key={`r-${r.device_id}`} className="text-red-300">
                            − {r.device_name} leaves {r.phase_label}
                          </div>
                        ))}
                        {(dep.additions || []).map(a => (
                          <div key={`a-${a.device_id}`} className="text-emerald-300">
                            + {a.device_name} returns to {a.phase_label}
                            {a.is_pending === false && (
                              <span className="text-amber-300"> (that sprint is already running)</span>
                            )}
                          </div>
                        ))}
                        {(dep.blocked || []).map(b => (
                          <div key={`b-${b.device_id}`} className="text-amber-300">
                            ! {b.device_name} cannot return — {b.reason}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </>
              )}

              {/* Why a device the admin just set to Main might not come back. */}
              {plan.phase_changes > 0 && totalAdditions === 0 && totalRemovals === 0 && deployments.length === 0 && (
                <p className="text-xs text-white/40">
                  A device only returns to a deployment it was previously removed from. Devices that were
                  never on a deployment are picked up by the nightly reconciliation under that deployment&apos;s
                  own rules.
                </p>
              )}
            </>
          )}
        </div>

        <div className="p-5 border-t border-white/10 flex justify-end gap-2">
          <Button variant="outline" className="border-white/20 text-white hover:bg-white/10"
            onClick={onClose} disabled={committing} data-testid="phase-commit-cancel">
            Cancel
          </Button>
          <Button
            className="bg-cyan-500 hover:bg-cyan-600 text-white font-semibold"
            onClick={handleCommit}
            disabled={loading || committing || Boolean(error)}
            data-testid="phase-commit-confirm"
          >
            {committing ? 'Committing…' : `Commit ${changes.length} change${changes.length === 1 ? '' : 's'}`}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ==================== UPDATE RING EDIT MODAL ====================
const UpdateRingEditModal = ({ ring, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    name: ring.name || '',
    description: ring.description || '',
    quality_deferral: ring.quality_deferral || 0,
    feature_deferral: ring.feature_deferral || 0,
    microsoft_product_updates: ring.microsoft_product_updates !== false,
    windows_drivers: ring.windows_drivers !== false,
    automatic_update_mode: ring.automatic_update_mode || 'autoInstallAtMaintenanceTime',
    business_ready_updates_only: ring.business_ready_updates_only || 'all',
    feature_update_uninstall_period: ring.feature_update_uninstall_period || 10,
    active_hours_start: ring.active_hours_start || 8,
    active_hours_end: ring.active_hours_end || 17,
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(formData);
    } finally {
      setSaving(false);
    }
  };

  const updateModes = [
    { value: 'userDefined', label: 'User Defined' },
    { value: 'windowsDefault', label: 'Windows Default' },
    { value: 'notifyDownload', label: 'Notify Download' },
    { value: 'autoInstallAtMaintenanceTime', label: 'Auto Install (Maintenance Time)' },
    { value: 'autoInstallAndRebootAtMaintenanceTime', label: 'Auto Install & Reboot (Maintenance)' },
    { value: 'autoInstallAndRebootAtScheduledTime', label: 'Auto Install & Reboot (Scheduled)' },
    { value: 'autoInstallAndRebootWithoutEndUserControl', label: 'Auto (No User Control)' },
  ];

  const updateChannels = [
    { value: 'all', label: 'All Updates' },
    { value: 'businessReadyOnly', label: 'Business Ready Only' },
    { value: 'windowsInsiderBuildFast', label: 'Windows Insider (Fast)' },
    { value: 'windowsInsiderBuildSlow', label: 'Windows Insider (Slow)' },
    { value: 'windowsInsiderBuildRelease', label: 'Windows Insider (Release)' },
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto py-8">
      <Card className="alice-popup bg-slate-900 border-[rgba(229,239,250,0.1)] w-full max-w-2xl mx-4">
        <CardHeader className="border-b border-[rgba(229,239,250,0.1)]">
          <div className="flex items-center justify-between">
            <CardTitle className="text-white flex items-center gap-2">
              <Edit className="w-5 h-5 text-cyan-400" />
              Edit Update Ring
            </CardTitle>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="w-4 h-4 text-white/90" />
            </Button>
          </div>
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
            {/* Basic Info */}
            <div className="space-y-4">
              <h4 className="text-white font-medium border-b border-[rgba(229,239,250,0.1)] pb-2">Basic Information</h4>
              <div>
                <label className="block text-sm text-white/90 mb-1">Ring Name</label>
                <Input 
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] border-[rgba(229,239,250,0.1)] text-white"
                  required
                />
              </div>
              <div>
                <label className="block text-sm text-white/90 mb-1">Description</label>
                <Input 
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] border-[rgba(229,239,250,0.1)] text-white"
                  placeholder="Optional description"
                />
              </div>
            </div>

            {/* Update Behavior */}
            <div className="space-y-4">
              <h4 className="text-white font-medium border-b border-[rgba(229,239,250,0.1)] pb-2">Update Behavior</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-white/90 mb-1">Automatic Update Mode</label>
                  <select
                    value={formData.automatic_update_mode}
                    onChange={(e) => setFormData({...formData, automatic_update_mode: e.target.value})}
                    className="w-full bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] border border-[rgba(229,239,250,0.1)] text-white rounded-md px-3 py-2"
                  >
                    {updateModes.map(mode => (
                      <option key={mode.value} value={mode.value} className="bg-slate-900">{mode.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-white/90 mb-1">Update Channel</label>
                  <select
                    value={formData.business_ready_updates_only}
                    onChange={(e) => setFormData({...formData, business_ready_updates_only: e.target.value})}
                    className="w-full bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] border border-[rgba(229,239,250,0.1)] text-white rounded-md px-3 py-2"
                  >
                    {updateChannels.map(ch => (
                      <option key={ch.value} value={ch.value} className="bg-slate-900">{ch.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Deferral Settings */}
            <div className="space-y-4">
              <h4 className="text-white font-medium border-b border-[rgba(229,239,250,0.1)] pb-2">Deferral Settings</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-white/90 mb-1">Quality Update Deferral (days)</label>
                  <Input 
                    type="number"
                    min="0"
                    max="30"
                    value={formData.quality_deferral}
                    onChange={(e) => setFormData({...formData, quality_deferral: parseInt(e.target.value) || 0})}
                    className="bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] border-[rgba(229,239,250,0.1)] text-white"
                  />
                  <p className="text-xs text-white/70 mt-1">Max 30 days</p>
                </div>
                <div>
                  <label className="block text-sm text-white/90 mb-1">Feature Update Deferral (days)</label>
                  <Input 
                    type="number"
                    min="0"
                    max="365"
                    value={formData.feature_deferral}
                    onChange={(e) => setFormData({...formData, feature_deferral: parseInt(e.target.value) || 0})}
                    className="bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] border-[rgba(229,239,250,0.1)] text-white"
                  />
                  <p className="text-xs text-white/70 mt-1">Max 365 days</p>
                </div>
              </div>
              <div>
                <label className="block text-sm text-white/90 mb-1">Feature Update Uninstall Period (days)</label>
                <Input 
                  type="number"
                  min="2"
                  max="60"
                  value={formData.feature_update_uninstall_period}
                  onChange={(e) => setFormData({...formData, feature_update_uninstall_period: parseInt(e.target.value) || 10})}
                  className="bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] border-[rgba(229,239,250,0.1)] text-white w-32"
                />
                <p className="text-xs text-white/70 mt-1">Days users can uninstall feature updates (2-60)</p>
              </div>
            </div>

            {/* Active Hours */}
            <div className="space-y-4">
              <h4 className="text-white font-medium border-b border-[rgba(229,239,250,0.1)] pb-2">Active Hours</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-white/90 mb-1">Start Time</label>
                  <select
                    value={formData.active_hours_start}
                    onChange={(e) => setFormData({...formData, active_hours_start: parseInt(e.target.value)})}
                    className="w-full bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] border border-[rgba(229,239,250,0.1)] text-white rounded-md px-3 py-2"
                  >
                    {Array.from({length: 24}, (_, i) => (
                      <option key={i} value={i} className="bg-slate-900">{i.toString().padStart(2, '0')}:00</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-white/90 mb-1">End Time</label>
                  <select
                    value={formData.active_hours_end}
                    onChange={(e) => setFormData({...formData, active_hours_end: parseInt(e.target.value)})}
                    className="w-full bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] border border-[rgba(229,239,250,0.1)] text-white rounded-md px-3 py-2"
                  >
                    {Array.from({length: 24}, (_, i) => (
                      <option key={i} value={i} className="bg-slate-900">{i.toString().padStart(2, '0')}:00</option>
                    ))}
                  </select>
                </div>
              </div>
              <p className="text-xs text-white/70">Updates won't restart devices during active hours</p>
            </div>

            {/* Additional Options */}
            <div className="space-y-4">
              <h4 className="text-white font-medium border-b border-[rgba(229,239,250,0.1)] pb-2">Additional Options</h4>
              <div className="space-y-3">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input 
                    type="checkbox"
                    checked={formData.microsoft_product_updates}
                    onChange={(e) => setFormData({...formData, microsoft_product_updates: e.target.checked})}
                    className="w-4 h-4 rounded border-[rgba(229,239,250,0.1)] bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] text-cyan-500"
                  />
                  <span className="text-white">Include Microsoft product updates (Office, etc.)</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input 
                    type="checkbox"
                    checked={formData.windows_drivers}
                    onChange={(e) => setFormData({...formData, windows_drivers: e.target.checked})}
                    className="w-4 h-4 rounded border-[rgba(229,239,250,0.1)] bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] text-cyan-500"
                  />
                  <span className="text-white">Include Windows driver updates</span>
                </label>
              </div>
            </div>
          </CardContent>
          
          <div className="border-t border-[rgba(229,239,250,0.1)] p-4 flex justify-end gap-3">
            <Button type="button" variant="outline" className="border-[rgba(229,239,250,0.1)] text-white" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" className="bg-[#E5EFFA] text-[#3C02CC] hover:bg-[#BBFF71] hover:text-[#3C02CC] active:bg-[#BBFF71] active:text-[#3C02CC] font-semibold" disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

// Helper function to format automatic update mode
const formatUpdateMode = (mode) => {
  const modes = {
    'userDefined': 'User Defined',
    'notifyDownload': 'Notify Download',
    'autoInstallAtMaintenanceTime': 'Auto Install (Maintenance)',
    'autoInstallAndRebootAtMaintenanceTime': 'Auto Install & Reboot (Maintenance)',
    'autoInstallAndRebootAtScheduledTime': 'Auto Install & Reboot (Scheduled)',
    'autoInstallAndRebootWithoutEndUserControl': 'Auto (No User Control)',
    'windowsDefault': 'Windows Default'
  };
  return modes[mode] || mode || 'Not Set';
};

// Helper function to format update channel
const formatUpdateChannel = (channel) => {
  const channels = {
    'userDefined': 'User Defined',
    'all': 'All Updates',
    'businessReadyOnly': 'Business Ready Only',
    'windowsInsiderBuildFast': 'Windows Insider (Fast)',
    'windowsInsiderBuildSlow': 'Windows Insider (Slow)',
    'windowsInsiderBuildRelease': 'Windows Insider (Release)'
  };
  return channels[channel] || channel || 'All Updates';
};

// Tab Button Component
const TabButton = ({ active, onClick, icon: Icon, label, accentColor }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-all border-b-2 ${
      active 
        ? 'text-white border-current' 
        : 'text-white/90 border-transparent hover:text-white hover:border-[rgba(229,239,250,0.1)]'
    }`}
    style={active ? { color: accentColor, borderColor: accentColor } : {}}
  >
    {Icon && <Icon className="w-4 h-4" />}
    {label}
  </button>
);

// ==================== OS UPDATES PAGE (WITH TABS) ====================
export const OSUpdatesPage = () => {
  const { canWriteOSPatch, canViewOSPatchPolicies } = usePermissions();
  const [activeTab, setActiveTab] = useState('rings');
  
  // Update Rings state
  const [rings, setRings] = useState([]);
  const [ringsLoading, setRingsLoading] = useState(true);
  const [ringsSyncing, setRingsSyncing] = useState(false);
  const [showCreateRingModal, setShowCreateRingModal] = useState(false);
  const [editingRing, setEditingRing] = useState(null);
  const [pausingRing, setPausingRing] = useState(null);
  
  // Feature Updates state
  const [featureProfiles, setFeatureProfiles] = useState([]);
  const [featureCatalog, setFeatureCatalog] = useState([]);
  const [featureLoading, setFeatureLoading] = useState(true);
  const [featureSyncing, setFeatureSyncing] = useState(false);
  const [showCreateFeatureModal, setShowCreateFeatureModal] = useState(false);
  const [editingFeatureProfile, setEditingFeatureProfile] = useState(null);
  
  // Feature Update Deployments state
  const [featureDeployments, setFeatureDeployments] = useState([]);
  const [showDeploymentWizard, setShowDeploymentWizard] = useState(false);
  
  // Expedited Updates state
  const [expeditedPolicies, setExpeditedPolicies] = useState([]);
  const [availableReleases, setAvailableReleases] = useState([]);
  const [expeditedLoading, setExpeditedLoading] = useState(true);
  const [expeditedSyncing, setExpeditedSyncing] = useState(false);
  const [showCreateExpeditedModal, setShowCreateExpeditedModal] = useState(false);

  // Sync status state
  const [syncStatus, setSyncStatus] = useState(null);

  // Check Assignments state
  const [checkingAssignments, setCheckingAssignments] = useState(false);
  const [assignmentCheckResult, setAssignmentCheckResult] = useState(null);
  const [showAssignmentCheckModal, setShowAssignmentCheckModal] = useState(false);

  // Load sync status
  const loadSyncStatus = async () => {
    try {
      const res = await osPatchApi.getSyncStatus();
      setSyncStatus(res.data);
    } catch (error) {
      console.error('Error loading sync status:', error);
    }
  };

  // Load Update Rings
  const loadRings = async () => {
    setRingsLoading(true);
    try {
      const [ringsRes, statusRes] = await Promise.all([
        osPatchApi.getUpdateRings(),
        osPatchApi.getSyncStatus()
      ]);
      setRings(Array.isArray(ringsRes.data) ? ringsRes.data : []);
      setSyncStatus(statusRes.data);
    } catch (error) {
      console.error('Error loading update rings:', error);
      toast.error('Failed to load update rings');
    } finally {
      setRingsLoading(false);
    }
  };

  // Load Feature Updates
  const loadFeatureUpdates = async () => {
    setFeatureLoading(true);
    try {
      const [profilesRes, catalogRes, statusRes, deploymentsRes] = await Promise.all([
        osPatchApi.getFeatureUpdateProfiles(),
        osPatchApi.getFeatureUpdateCatalog(),
        osPatchApi.getSyncStatus(),
        osPatchApi.getFeatureUpdateDeployments()
      ]);
      setFeatureProfiles(Array.isArray(profilesRes.data) ? profilesRes.data : []);
      setFeatureCatalog(Array.isArray(catalogRes.data) ? catalogRes.data : []);
      setSyncStatus(statusRes.data);
      setFeatureDeployments(Array.isArray(deploymentsRes.data) ? deploymentsRes.data : []);
    } catch (error) {
      console.error('Error loading feature updates:', error);
      toast.error('Failed to load feature updates');
    } finally {
      setFeatureLoading(false);
    }
  };

  // Load Expedited Updates
  const loadExpeditedUpdates = async () => {
    setExpeditedLoading(true);
    try {
      const [policiesRes, releasesRes] = await Promise.all([
        osPatchApi.getExpeditedUpdates(),
        osPatchApi.getAvailableQualityReleases()
      ]);
      setExpeditedPolicies(Array.isArray(policiesRes.data) ? policiesRes.data : []);
      setAvailableReleases(Array.isArray(releasesRes.data) ? releasesRes.data : []);
    } catch (error) {
      console.error('Error loading expedited updates:', error);
      toast.error('Failed to load expedited updates');
    } finally {
      setExpeditedLoading(false);
    }
  };

  // Load data based on active tab
  useEffect(() => {
    if (!canViewOSPatchPolicies) return;
    if (activeTab === 'rings') loadRings();
    else if (activeTab === 'feature') loadFeatureUpdates();
    else if (activeTab === 'expedited') loadExpeditedUpdates();
  }, [activeTab, canViewOSPatchPolicies]);

  // Sync handlers
  const handleSyncRings = async () => {
    setRingsSyncing(true);
    try {
      const res = await osPatchApi.syncUpdateRings();
      toast.success(res.data.message || 'Update rings synced');
      loadRings();
    } catch (error) {
      toast.error('Failed to sync update rings');
    } finally {
      setRingsSyncing(false);
    }
  };

  const handleSyncFeature = async () => {
    setFeatureSyncing(true);
    try {
      const res = await osPatchApi.syncFeatureUpdates();
      toast.success(res.data.message || 'Feature updates synced');
      loadFeatureUpdates();
    } catch (error) {
      toast.error('Failed to sync feature updates');
    } finally {
      setFeatureSyncing(false);
    }
  };

  const handleSyncExpedited = async () => {
    setExpeditedSyncing(true);
    try {
      const res = await osPatchApi.syncExpeditedUpdates();
      toast.success(res.data.message || 'Expedited updates synced');
      loadExpeditedUpdates();
    } catch (error) {
      toast.error('Failed to sync expedited updates');
    } finally {
      setExpeditedSyncing(false);
    }
  };

  // Check assignments handler
  const handleCheckAssignments = async () => {
    setCheckingAssignments(true);
    try {
      // First sync device assignments to get latest data
      await osPatchApi.syncDeviceAssignments();
      // Then run the check
      const res = await osPatchApi.checkRingAssignments();
      setAssignmentCheckResult(res.data);
      setShowAssignmentCheckModal(true);
    } catch (error) {
      toast.error('Failed to check assignments');
      console.error('Error checking assignments:', error);
    } finally {
      setCheckingAssignments(false);
    }
  };

  // Ring actions
  const handleDeleteRing = async (ringId) => {
    if (!window.confirm('Are you sure you want to delete this update ring?')) return;
    try {
      await osPatchApi.deleteUpdateRing(ringId);
      toast.success('Update ring deleted');
      loadRings();
    } catch (error) {
      toast.error('Failed to delete update ring');
    }
  };

  const handlePause = async (ringId, pauseType = 'both') => {
    try {
      const res = await osPatchApi.pauseUpdateRing(ringId, pauseType);
      toast.success(res.data.message || 'Updates paused');
      setPausingRing(null);
      loadRings();
    } catch (error) {
      toast.error('Failed to pause updates');
    }
  };

  const handleResume = async (ringId) => {
    try {
      const res = await osPatchApi.resumeUpdateRing(ringId);
      toast.success(res.data.message || 'Updates resumed');
      loadRings();
    } catch (error) {
      toast.error('Failed to resume updates');
    }
  };

  const handleSaveRingEdit = async (ringData) => {
    try {
      await osPatchApi.updateUpdateRing(editingRing.id, ringData);
      toast.success('Update ring saved');
      setEditingRing(null);
      loadRings();
    } catch (error) {
      toast.error('Failed to save update ring');
    }
  };

  // Feature actions
  const handleCreateFeatureProfile = async (data) => {
    try {
      await osPatchApi.createFeatureUpdateProfile(data);
      toast.success('Feature update profile created');
      setShowCreateFeatureModal(false);
      loadFeatureUpdates();
    } catch (error) {
      toast.error('Failed to create feature update profile');
    }
  };

  const handleDeleteFeatureProfile = async (profileId) => {
    if (!window.confirm('Are you sure you want to delete this feature update profile?')) return;
    try {
      await osPatchApi.deleteFeatureUpdateProfile(profileId);
      toast.success('Feature update profile deleted');
      loadFeatureUpdates();
    } catch (error) {
      toast.error('Failed to delete feature update profile');
    }
  };

  const handlePauseFeatureProfile = async (profileId) => {
    try {
      const res = await osPatchApi.pauseFeatureUpdateProfile(profileId);
      toast.success(res.data.message || 'Feature update profile paused');
      loadFeatureUpdates();
    } catch (error) {
      toast.error('Failed to pause feature update profile');
    }
  };

  const handleResumeFeatureProfile = async (profileId) => {
    try {
      const res = await osPatchApi.resumeFeatureUpdateProfile(profileId);
      toast.success(res.data.message || 'Feature update profile resumed');
      loadFeatureUpdates();
    } catch (error) {
      toast.error('Failed to resume feature update profile');
    }
  };

  const handleSaveFeatureProfileEdit = async (profileData) => {
    try {
      await osPatchApi.updateFeatureUpdateProfile(editingFeatureProfile.id, profileData);
      toast.success('Feature update profile saved');
      setEditingFeatureProfile(null);
      loadFeatureUpdates();
    } catch (error) {
      toast.error('Failed to save feature update profile');
    }
  };

  // Expedited actions
  const handleCreateExpedited = async (data) => {
    try {
      await osPatchApi.createExpeditedUpdate(data);
      toast.success('Expedited update policy created');
      setShowCreateExpeditedModal(false);
      loadExpeditedUpdates();
    } catch (error) {
      toast.error('Failed to create expedited update policy');
    }
  };

  const handleDeleteExpedited = async (policyId) => {
    if (!window.confirm('Are you sure you want to delete this expedited update policy?')) return;
    try {
      await osPatchApi.deleteExpeditedUpdate(policyId);
      toast.success('Expedited update policy deleted');
      loadExpeditedUpdates();
    } catch (error) {
      toast.error('Failed to delete expedited update policy');
    }
  };

  const isPaused = (ring) => ring.quality_updates_paused || ring.feature_updates_paused;
  const formatDate = (dateStr) => dateStr ? new Date(dateStr).toLocaleDateString() : 'N/A';

  // After every hook, so hook order is identical whichever plan the tenant is on.
  if (!canViewOSPatchPolicies) {
    return (
      <OSPatchPolicyUnavailable
        title="Updates"
        testId="os-patch-updates-unavailable"
        message="Update rings, feature updates and expedited updates aren't included in your organisation's ALICE plan."
      />
    );
  }

  return (
    <div className="p-8" data-testid="os-patch-updates">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Updates</h1>
          <p className="text-white/90">Manage Windows Update policies, feature updates, and expedited patches</p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="flex gap-3">
            {activeTab === 'rings' && (
              <>
                <Button 
                  onClick={handleCheckAssignments} 
                  disabled={checkingAssignments}
                  variant="outline" 
                  className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
                  data-testid="check-assignments-btn"
                >
                  <Shield className={`w-4 h-4 mr-2 ${checkingAssignments ? 'animate-spin' : ''}`} />
                  {checkingAssignments ? 'Checking...' : 'Check Assignments'}
                </Button>
                {canWriteOSPatch && (
                <Button onClick={() => setShowCreateRingModal(true)} className="bg-[#E5EFFA] text-[#3C02CC] hover:bg-[#BBFF71] hover:text-[#3C02CC] active:bg-[#BBFF71] active:text-[#3C02CC] font-semibold">
                  <Plus className="w-4 h-4 mr-2" />
                  Custom Ring
                </Button>
                )}
              </>
            )}
            {activeTab === 'feature' && (
              <>
                <Button 
                  onClick={handleCheckAssignments} 
                  disabled={checkingAssignments}
                  variant="outline" 
                  className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
                  data-testid="check-feature-assignments-btn"
                >
                  <Shield className={`w-4 h-4 mr-2 ${checkingAssignments ? 'animate-spin' : ''}`} />
                  {checkingAssignments ? 'Checking...' : 'Check Assignments'}
                </Button>
                {canWriteOSPatch && (
                <Button onClick={() => setShowCreateFeatureModal(true)} className="bg-purple-500 hover:bg-purple-600">
                  <Plus className="w-4 h-4 mr-2" />
                  Custom Profile
                </Button>
                )}
              </>
            )}
            {activeTab === 'expedited' && (
              <>
                {canWriteOSPatch && (
                <Button onClick={() => setShowCreateExpeditedModal(true)} className="bg-[#FC5442] hover:bg-[#e84a3a]">
                  <Zap className="w-4 h-4 mr-2" />
                  Deploy Update
                </Button>
                )}
              </>
            )}
          </div>
          {/* Sync Status Indicator */}
          {activeTab === 'rings' && <SyncStatusIndicator lastSync={syncStatus?.update_rings} label="Update rings sync" />}
          {activeTab === 'feature' && <SyncStatusIndicator lastSync={syncStatus?.feature_updates} label="Feature updates sync" />}
          {activeTab === 'expedited' && <SyncStatusIndicator lastSync={syncStatus?.expedited_updates} label="Expedited updates sync" />}
        </div>
      </div>

      <OSPatchReadOnlyNotice className="mb-4" />

      {/* Tabs */}
      <div className="flex border-b border-[rgba(229,239,250,0.1)] mb-6">
        <TabButton 
          active={activeTab === 'rings'} 
          onClick={() => setActiveTab('rings')} 
          icon={RefreshCw} 
          label="Update Rings" 
          accentColor={CHART_COLORS.cyan}
        />
        <TabButton 
          active={activeTab === 'feature'} 
          onClick={() => setActiveTab('feature')} 
          icon={Layers} 
          label="Feature Updates" 
          accentColor={CHART_COLORS.purple}
        />
        <TabButton 
          active={activeTab === 'expedited'} 
          onClick={() => setActiveTab('expedited')} 
          icon={Zap} 
          label="Expedited / OOB" 
          accentColor={CHART_COLORS.coral}
        />
      </div>

      {/* Update Rings Tab Content */}
      {activeTab === 'rings' && (
        <>
          {ringsLoading ? (
            <LoadingSpinner message="Loading update rings..." />
          ) : rings.length === 0 ? (
            <Card className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border-[rgba(229,239,250,0.1)]">
              <CardContent className="p-12 text-center">
                <Settings className="w-12 h-12 text-[#E5EFFA]/30 mx-auto mb-4" />
                <h3 className="text-xl font-medium text-white mb-2">No Update Rings</h3>
                <p className="text-white/90 mb-4">{canWriteOSPatch ? 'Create your first Windows Update ring to manage device updates' : 'No update rings yet.'}</p>
                {canWriteOSPatch && (
                <Button onClick={() => setShowCreateRingModal(true)} className="bg-[#E5EFFA] text-[#3C02CC] hover:bg-[#BBFF71] hover:text-[#3C02CC] active:bg-[#BBFF71] active:text-[#3C02CC] font-semibold">
                  <Plus className="w-4 h-4 mr-2" />
                  Custom Ring
                </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {rings.map((ring, i) => (
                <Card key={ring.id || i} className={`bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border-[rgba(229,239,250,0.1)] ${isPaused(ring) ? 'border-l-4 border-l-amber-500' : ''}`}>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-medium text-white">{ring.name}</h3>
                          {isPaused(ring) && (
                            <span className="px-2 py-1 bg-amber-500/20 text-amber-400 text-xs rounded-full flex items-center gap-1">
                              <Pause className="w-3 h-3" />
                              Paused
                            </span>
                          )}
                          {ring.synced_from_intune && (
                            <span className="px-2 py-1 bg-blue-500/20 text-blue-400 text-xs rounded-full">Synced from Intune</span>
                          )}
                        </div>
                        <p className="text-white/90 text-sm mb-3">{ring.description || 'No description'}</p>
                        <div className="flex flex-wrap gap-4 mb-3 text-sm">
                          <span className="text-white/90">Mode: <span className="text-cyan-400 font-medium">{formatUpdateMode(ring.automatic_update_mode)}</span></span>
                          <span className="text-white/90">Channel: <span className="text-purple-400 font-medium">{formatUpdateChannel(ring.business_ready_updates_only)}</span></span>
                        </div>
                        <div className="flex flex-wrap gap-4 text-sm">
                          <span className="text-white/90">Quality Deferral: <span className="text-white">{ring.quality_deferral || 0} days</span></span>
                          <span className="text-white/90">Feature Deferral: <span className="text-white">{ring.feature_deferral || 0} days</span></span>
                          <span className="text-white/90" data-testid={`ring-devices-count-${ring.id || i}`}>
                            Devices: <span className="text-white">{ring.device_count_in_intune ?? ring.device_count ?? 0}</span>
                            {(() => {
                              const inGroup = ring.device_count_in_group;
                              const inIntune = ring.device_count_in_intune ?? ring.device_count ?? 0;
                              if (typeof inGroup === 'number' && inGroup !== inIntune) {
                                return (
                                  <span
                                    className="text-white/60 ml-1"
                                    title="Devices currently present in this Entra ID group. The difference between this and 'Devices' is devices that have aged out of Intune but remain in the Entra ID group (they'll reappear automatically when they next check in)."
                                    data-testid={`ring-devices-in-group-${ring.id || i}`}
                                  >
                                    ({inGroup} {inGroup === 1 ? 'device' : 'devices'} in group)
                                  </span>
                                );
                              }
                              return null;
                            })()}
                          </span>
                        </div>
                      </div>
                      {canWriteOSPatch && (
                      <div className="flex items-center gap-2 ml-4">
                        {isPaused(ring) ? (
                          <Button variant="outline" size="sm" className="border-green-500/50 text-green-400 hover:bg-green-500/20" onClick={() => handleResume(ring.id)}>
                            <PlayCircle className="w-4 h-4 mr-1" />Resume
                          </Button>
                        ) : (
                          <Button variant="outline" size="sm" className="border-amber-500/50 text-amber-400 hover:bg-amber-500/20" onClick={() => setPausingRing(ring)}>
                            <Pause className="w-4 h-4 mr-1" />Pause
                          </Button>
                        )}
                        <Button variant="outline" size="sm" className="border-white/30 text-white hover:bg-white/[0.04]" onClick={() => setEditingRing(ring)}>
                          <Edit className="w-4 h-4 mr-1" />Edit
                        </Button>
                        <Button variant="ghost" size="icon" className="text-white/90 hover:text-red-400" onClick={() => handleDeleteRing(ring.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </>
      )}

      {/* Feature Updates Tab Content */}
      {activeTab === 'feature' && (
        <>
          {featureLoading ? (
            <LoadingSpinner message="Loading feature updates..." />
          ) : (
            <>
              {/* Available Feature Updates Catalog */}
              <div className="mb-8">
                <h2 className="text-xl font-semibold text-white mb-4">Available Feature Updates</h2>
                <Card className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border-[rgba(229,239,250,0.1)]">
                  <CardContent className="p-0">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-[rgba(229,239,250,0.1)]">
                          <th className="text-left p-4 text-white/90 font-medium">Version</th>
                          <th className="text-left p-4 text-white/90 font-medium">Display Name</th>
                          <th className="text-left p-4 text-white/90 font-medium">Release Date</th>
                          <th className="text-left p-4 text-white/90 font-medium">End of Service</th>
                          <th className="text-left p-4 text-white/90 font-medium">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {featureCatalog.map((item, i) => (
                          <tr key={item.id || i} className="border-b border-white/5 hover:bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px]">
                            <td className="p-4 text-purple-400 font-medium">{item.version || 'N/A'}</td>
                            <td className="p-4 text-white">{item.displayName}</td>
                            <td className="p-4 text-white">{formatDate(item.releaseDate)}</td>
                            <td className="p-4 text-white">{formatDate(item.endOfServiceDate)}</td>
                            <td className="p-4">
                              {item.isInService ? (
                                <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded-full">In Service</span>
                              ) : (
                                <span className="px-2 py-1 bg-red-500/20 text-red-400 text-xs rounded-full">End of Service</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </CardContent>
                </Card>
              </div>

              {/* Deployment Profiles */}
              <div>
                <h2 className="text-xl font-semibold text-white mb-4">Deployment Profiles</h2>
                {featureProfiles.length === 0 ? (
                  <Card className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border-[rgba(229,239,250,0.1)]">
                    <CardContent className="p-8 text-center">
                      <Layers className="w-12 h-12 text-[#E5EFFA]/30 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-white mb-2">No Feature Update Profiles</h3>
                      <p className="text-white/90 mb-4">{canWriteOSPatch ? 'Create a profile to deploy Windows feature updates to your devices' : 'No feature update profiles yet.'}</p>
                      {canWriteOSPatch && (
                      <Button onClick={() => setShowCreateFeatureModal(true)} className="bg-purple-500 hover:bg-purple-600">
                        <Plus className="w-4 h-4 mr-2" />
                        Custom Profile
                      </Button>
                      )}
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid gap-4">
                    {featureProfiles.map((profile, i) => (
                      <Card key={profile.id || i} className={`bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border-[rgba(229,239,250,0.1)] ${profile.is_paused ? 'border-l-4 border-l-amber-500' : ''}`}>
                        <CardContent className="p-6">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <h3 className="text-lg font-medium text-white">{profile.name}</h3>
                                {profile.is_paused && (
                                  <span className="px-2 py-1 bg-amber-500/20 text-amber-400 text-xs rounded-full flex items-center gap-1">
                                    <Pause className="w-3 h-3" />
                                    Paused
                                  </span>
                                )}
                                {profile.synced_from_intune && (
                                  <span className="px-2 py-1 bg-blue-500/20 text-blue-400 text-xs rounded-full">Synced from Intune</span>
                                )}
                              </div>
                              <p className="text-white/90 text-sm mb-3">{profile.description || 'No description'}</p>
                              
                              {/* First Row: Target Version & Created Date */}
                              <div className="flex flex-wrap gap-4 mb-2 text-sm">
                                <span className="text-white/90">
                                  Target Version: <span className="text-purple-400 font-medium">{profile.target_version || 'Not specified'}</span>
                                </span>
                                {(profile.intune_created_at || profile.created_at) && (
                                  <span className="text-white/90">
                                    Created: <span className="text-white">{formatDate(profile.intune_created_at || profile.created_at)}</span>
                                  </span>
                                )}
                              </div>
                              
                              {/* Second Row: Rollout Dates */}
                              {(profile.rollout_start || profile.rollout_end) && (
                                <div className="flex flex-wrap gap-4 mb-2 text-sm">
                                  <span className="text-white/90">
                                    Rollout Start: <span className="text-cyan-400">{formatDate(profile.rollout_start)}</span>
                                  </span>
                                  <span className="text-white/90">
                                    Rollout End: <span className="text-cyan-400">{formatDate(profile.rollout_end)}</span>
                                  </span>
                                </div>
                              )}
                              
                              {/* Third Row: Assigned & Excluded Groups */}
                              <div className="flex flex-wrap gap-4 text-sm">
                                <span className="text-white/90">
                                  Devices: <span className="text-white">{profile.device_count || 0}</span>
                                </span>
                                <span className="text-white/90">
                                  Assigned: {profile.assigned_groups?.length > 0 ? (
                                    <span className="text-green-400">{profile.assigned_groups.map(g => g.name || g).join(', ')}</span>
                                  ) : (
                                    <span className="text-white/70">None</span>
                                  )}
                                </span>
                                {profile.excluded_groups?.length > 0 && (
                                  <span className="text-white/90">
                                    Excluded: <span className="text-red-400">{profile.excluded_groups.map(g => g.name || g).join(', ')}</span>
                                  </span>
                                )}
                              </div>
                            </div>
                            
                            {/* Action Buttons */}
                            {canWriteOSPatch && (
                            <div className="flex items-center gap-2 ml-4">
                              {profile.is_paused ? (
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  className="border-green-500/50 text-green-400 hover:bg-green-500/20"
                                  onClick={() => handleResumeFeatureProfile(profile.id)}
                                >
                                  <PlayCircle className="w-4 h-4 mr-1" />Resume
                                </Button>
                              ) : (
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  className="border-amber-500/50 text-amber-400 hover:bg-amber-500/20"
                                  onClick={() => handlePauseFeatureProfile(profile.id)}
                                >
                                  <Pause className="w-4 h-4 mr-1" />Pause
                                </Button>
                              )}
                              <Button 
                                variant="outline" 
                                size="sm" 
                                className="border-white/30 text-white hover:bg-white/[0.04]"
                                onClick={() => setEditingFeatureProfile(profile)}
                              >
                                <Edit className="w-4 h-4 mr-1" />Edit
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                className="text-white/90 hover:text-red-400" 
                                onClick={() => handleDeleteFeatureProfile(profile.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
          
        </>
      )}

      {/* Expedited Updates Tab Content */}
      {activeTab === 'expedited' && (
        <>
          {expeditedLoading ? (
            <LoadingSpinner message="Loading expedited updates..." />
          ) : (
            <>
              {/* Deployed Quality Updates */}
              <div className="mb-8">
                <h2 className="text-xl font-semibold text-white mb-4">Deployed Quality Updates</h2>
                {expeditedPolicies.length === 0 ? (
                  <Card className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border-[rgba(229,239,250,0.1)]">
                    <CardContent className="p-8 text-center">
                      <Zap className="w-12 h-12 text-[#E5EFFA]/30 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-white mb-2">No Expedited Updates Deployed</h3>
                      <p className="text-white/90 mb-4">{canWriteOSPatch ? 'Deploy an expedited update to quickly patch critical vulnerabilities' : 'No expedited updates yet.'}</p>
                      {canWriteOSPatch && (
                      <Button onClick={() => setShowCreateExpeditedModal(true)} className="bg-[#FC5442] hover:bg-[#e84a3a]">
                        <Zap className="w-4 h-4 mr-2" />
                        Deploy Expedited Update
                      </Button>
                      )}
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid gap-4">
                    {expeditedPolicies.map((policy, i) => (
                      <Card key={policy.id || i} className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border-[rgba(229,239,250,0.1)] border-l-4 border-l-[#FC5442]">
                        <CardContent className="p-6">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <Zap className="w-5 h-5 text-[#FC5442]" />
                                <h3 className="text-lg font-medium text-white">{policy.name}</h3>
                                {policy.synced_from_intune && (
                                  <span className="px-2 py-1 bg-blue-500/20 text-blue-400 text-xs rounded-full">Synced from Intune</span>
                                )}
                              </div>
                              <p className="text-white/90 text-sm mb-3">{policy.description || 'No description'}</p>
                              <div className="flex flex-wrap gap-4 text-sm">
                                {policy.quality_update_release && (
                                  <span className="text-white/90">Update: <span className="text-[#FC5442] font-medium">{policy.quality_update_release}</span></span>
                                )}
                                {policy.content_display_name && (
                                  <span className="text-white/90">Content: <span className="text-white">{policy.content_display_name}</span></span>
                                )}
                                {policy.days_until_forced_reboot && (
                                  <span className="text-white/90">Forced Reboot: <span className="text-amber-400">{policy.days_until_forced_reboot} days</span></span>
                                )}
                              </div>
                            </div>
                            {canWriteOSPatch && (
                            <Button variant="ghost" size="icon" className="text-white/90 hover:text-red-400" onClick={() => handleDeleteExpedited(policy.id)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>

              {/* Available Quality Releases */}
              <div>
                <h2 className="text-xl font-semibold text-white mb-4">Available Quality Updates</h2>
                <Card className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border-[rgba(229,239,250,0.1)]">
                  <CardContent className="p-0">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-[rgba(229,239,250,0.1)]">
                          <th className="text-left p-4 text-white/90 font-medium">Update</th>
                          <th className="text-left p-4 text-white/90 font-medium">KB Number</th>
                          <th className="text-left p-4 text-white/90 font-medium">Release Date</th>
                          <th className="text-left p-4 text-white/90 font-medium">Classification</th>
                          {canWriteOSPatch && (
                          <th className="text-left p-4 text-white/90 font-medium">Action</th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {availableReleases.map((release, i) => (
                          <tr key={release.id || i} className="border-b border-white/5 hover:bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px]">
                            <td className="p-4 text-white">{release.displayName}</td>
                            <td className="p-4 text-cyan-400 font-mono">{release.kbNumber || 'N/A'}</td>
                            <td className="p-4 text-white">{formatDate(release.releaseDate)}</td>
                            <td className="p-4">
                              <span className={`px-2 py-1 text-xs rounded-full ${
                                release.classification === 'Critical' ? 'bg-red-500/20 text-red-400' : 'bg-amber-500/20 text-amber-400'
                              }`}>
                                {release.classification}
                              </span>
                            </td>
                            {canWriteOSPatch && (
                            <td className="p-4">
                              <Button size="sm" className="bg-[#FC5442] hover:bg-[#e84a3a]" onClick={() => setShowCreateExpeditedModal(true)}>
                                <Zap className="w-3 h-3 mr-1" />Expedite
                              </Button>
                            </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </>
      )}

      {/* Pause Modal */}
      {pausingRing && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="alice-popup bg-slate-900 border-[rgba(229,239,250,0.1)] w-full max-w-md mx-4">
            <CardHeader className="border-b border-[rgba(229,239,250,0.1)]">
              <div className="flex items-center justify-between">
                <CardTitle className="text-white">Pause Updates</CardTitle>
                <Button variant="ghost" size="icon" onClick={() => setPausingRing(null)}>
                  <X className="w-4 h-4 text-white/90" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              <p className="text-white mb-6">Choose which updates to pause for <span className="text-cyan-400 font-medium">{pausingRing.name}</span>:</p>
              <div className="space-y-3">
                <Button className="w-full bg-amber-600 hover:bg-amber-700 justify-start" onClick={() => handlePause(pausingRing.id, 'both')}>
                  <Pause className="w-4 h-4 mr-2" />Pause All Updates (Quality & Feature)
                </Button>
                <Button variant="outline" className="w-full bg-[#E5EFFA] text-[#006AF2] border-none hover:bg-[#BBFF71] hover:text-[#3C02CC] active:bg-[#BBFF71] active:text-[#3C02CC] font-semibold justify-start" onClick={() => handlePause(pausingRing.id, 'quality')}>
                  <Pause className="w-4 h-4 mr-2" />Pause Quality Updates Only
                </Button>
                <Button variant="outline" className="w-full bg-[#E5EFFA] text-[#006AF2] border-none hover:bg-[#BBFF71] hover:text-[#3C02CC] active:bg-[#BBFF71] active:text-[#3C02CC] font-semibold justify-start" onClick={() => handlePause(pausingRing.id, 'feature')}>
                  <Pause className="w-4 h-4 mr-2" />Pause Feature Updates Only
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Edit Ring Modal */}
      {editingRing && (
        <UpdateRingEditModal ring={editingRing} onSave={handleSaveRingEdit} onClose={() => setEditingRing(null)} />
      )}

      {/* Create Ring Modal */}
      {showCreateRingModal && (
        <CreateRingModal onClose={() => setShowCreateRingModal(false)} onCreated={() => { setShowCreateRingModal(false); loadRings(); }} />
      )}

      {/* Create Feature Update Modal */}
      {showCreateFeatureModal && (
        <FeatureUpdateCreateModal catalog={featureCatalog} onSave={handleCreateFeatureProfile} onClose={() => setShowCreateFeatureModal(false)} />
      )}

      {/* Edit Feature Update Modal */}
      {editingFeatureProfile && (
        <FeatureUpdateEditModal profile={editingFeatureProfile} catalog={featureCatalog} onSave={handleSaveFeatureProfileEdit} onClose={() => setEditingFeatureProfile(null)} />
      )}

      {/* Create Expedited Update Modal */}
      {showCreateExpeditedModal && (
        <ExpeditedUpdateCreateModal availableReleases={availableReleases} onSave={handleCreateExpedited} onClose={() => setShowCreateExpeditedModal(false)} />
      )}

      {/* Assignment Check Modal */}
      {showAssignmentCheckModal && assignmentCheckResult && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto py-8">
          <Card className="alice-popup bg-slate-900 border-[rgba(229,239,250,0.1)] w-full max-w-3xl mx-4">
            <CardHeader className="border-b border-[rgba(229,239,250,0.1)]">
              <div className="flex items-center justify-between">
                <CardTitle className="text-white flex items-center gap-2">
                  <Shield className={`w-5 h-5 ${assignmentCheckResult.is_healthy ? 'text-green-400' : 'text-amber-400'}`} />
                  Assignment Check Results
                </CardTitle>
                <Button variant="ghost" size="icon" onClick={() => setShowAssignmentCheckModal(false)}>
                  <X className="w-4 h-4 text-white/90" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-6 max-h-[70vh] overflow-y-auto">
              {/* Summary */}
              <div className={`p-4 rounded-lg mb-6 ${assignmentCheckResult.is_healthy ? 'bg-green-500/10 border border-green-500/30' : 'bg-amber-500/10 border border-amber-500/30'}`}>
                <div className="flex items-center gap-2 mb-2">
                  {assignmentCheckResult.is_healthy ? (
                    <>
                      <CheckCircle className="w-5 h-5 text-green-400" />
                      <span className="text-green-400 font-medium">All devices are correctly assigned</span>
                    </>
                  ) : (
                    <>
                      <AlertTriangle className="w-5 h-5 text-amber-400" />
                      <span className="text-amber-400 font-medium">Assignment conflicts detected</span>
                    </>
                  )}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-white">{assignmentCheckResult.summary?.total_devices || 0}</div>
                    <div className="text-sm text-white/90">Total Devices</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-400">{assignmentCheckResult.summary?.compliant_devices || 0}</div>
                    <div className="text-sm text-white/90">Correctly Assigned</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-amber-400">{assignmentCheckResult.summary?.devices_with_conflicts || 0}</div>
                    <div className="text-sm text-white/90">With Conflicts</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-red-400">{assignmentCheckResult.summary?.no_update_ring || 0}</div>
                    <div className="text-sm text-white/90">No Update Ring</div>
                  </div>
                </div>
              </div>

              {/* Conflict Details */}
              {!assignmentCheckResult.is_healthy && (
                <div className="space-y-4">
                  {/* Multiple Update Rings */}
                  {assignmentCheckResult.conflicts?.multiple_update_rings?.length > 0 && (
                    <div>
                      <h4 className="text-white font-medium mb-2 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-red-400" />
                        Devices in Multiple Update Rings ({assignmentCheckResult.conflicts.multiple_update_rings.length})
                      </h4>
                      <div className="bg-red-500/10 border border-red-500/30 rounded-lg overflow-hidden">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="bg-red-500/10">
                              <th className="text-left p-3 text-white/90">Device</th>
                              <th className="text-left p-3 text-white/90">Assigned Rings</th>
                            </tr>
                          </thead>
                          <tbody>
                            {assignmentCheckResult.conflicts.multiple_update_rings.slice(0, 10).map((item, i) => (
                              <tr key={i} className="border-t border-red-500/20">
                                <td className="p-3 text-white">{item.device_name}</td>
                                <td className="p-3 text-red-400">
                                  {item.assigned_rings?.map(r => r.ring_name).join(', ')}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {assignmentCheckResult.conflicts.multiple_update_rings.length > 10 && (
                          <div className="p-2 text-center text-white/70 text-sm">
                            ...and {assignmentCheckResult.conflicts.multiple_update_rings.length - 10} more
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Multiple Feature Updates */}
                  {assignmentCheckResult.conflicts?.multiple_feature_updates?.length > 0 && (
                    <div>
                      <h4 className="text-white font-medium mb-2 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-amber-400" />
                        Devices in Multiple Feature Update Profiles ({assignmentCheckResult.conflicts.multiple_feature_updates.length})
                      </h4>
                      <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg overflow-hidden">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="bg-amber-500/10">
                              <th className="text-left p-3 text-white/90">Device</th>
                              <th className="text-left p-3 text-white/90">Assigned Profiles</th>
                            </tr>
                          </thead>
                          <tbody>
                            {assignmentCheckResult.conflicts.multiple_feature_updates.slice(0, 10).map((item, i) => (
                              <tr key={i} className="border-t border-amber-500/20">
                                <td className="p-3 text-white">{item.device_name}</td>
                                <td className="p-3 text-amber-400">
                                  {item.assigned_profiles?.map(p => p.profile_name).join(', ')}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {assignmentCheckResult.conflicts.multiple_feature_updates.length > 10 && (
                          <div className="p-2 text-center text-white/70 text-sm">
                            ...and {assignmentCheckResult.conflicts.multiple_feature_updates.length - 10} more
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* No Update Ring */}
                  {assignmentCheckResult.conflicts?.no_update_ring?.length > 0 && (
                    <div>
                      <h4 className="text-white font-medium mb-2 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-cyan-400" />
                        Devices Without Update Ring ({assignmentCheckResult.conflicts.no_update_ring.length})
                      </h4>
                      <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-3">
                        <div className="flex flex-wrap gap-2">
                          {assignmentCheckResult.conflicts.no_update_ring.slice(0, 20).map((item, i) => (
                            <span key={i} className="px-2 py-1 bg-cyan-500/20 text-cyan-400 rounded text-sm">
                              {item.device_name}
                            </span>
                          ))}
                          {assignmentCheckResult.conflicts.no_update_ring.length > 20 && (
                            <span className="px-2 py-1 text-white/70 text-sm">
                              ...and {assignmentCheckResult.conflicts.no_update_ring.length - 20} more
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Healthy Message */}
              {assignmentCheckResult.is_healthy && (
                <div className="text-center py-8">
                  <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
                  <h3 className="text-xl font-medium text-white mb-2">All Clear!</h3>
                  <p className="text-white/90">All devices are correctly assigned to exactly one update ring.</p>
                  <p className="text-white/70 text-sm mt-2">Devices with feature update assignments are also assigned to only one profile.</p>
                </div>
              )}
            </CardContent>
            <div className="border-t border-[rgba(229,239,250,0.1)] p-4 flex justify-end">
              <Button onClick={() => setShowAssignmentCheckModal(false)} className="bg-[#E5EFFA] text-[#3C02CC] hover:bg-[#BBFF71] hover:text-[#3C02CC] active:bg-[#BBFF71] active:text-[#3C02CC] font-semibold">
                Close
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

// ==================== UPDATE RING OPTIONS MODAL ====================
const UpdateRingOptionsModal = ({ onClose, onConfirm, submitting }) => {
  const [enableDrivers, setEnableDrivers] = useState(false);
  const [enableOffice, setEnableOffice] = useState(false);
  const [enableEdge, setEnableEdge] = useState(false);

  const handleConfirm = () => {
    onConfirm({
      enable_driver_updates: enableDrivers,
      enable_office_updates: enableOffice,
      enable_edge_updates: enableEdge,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="update-ring-options-modal">
      <Card className="alice-popup bg-[rgba(15,17,35,0.95)] backdrop-blur-[10px] border border-[rgba(229,239,250,0.15)] w-full max-w-2xl shadow-[0_8px_32px_rgba(0,0,0,0.4)]">
        <CardContent className="p-0">
          <div className="p-6 border-b border-[rgba(229,239,250,0.1)]">
            <h2 className="text-2xl font-semibold text-white">Create Update Ring Deployments</h2>
            <p className="text-white/70 text-sm mt-1">
              Auto-creates 3 deployment groups and update rings (Low / Medium / High criticality).
              Enable additional profile types below to also deploy Driver, Office and Edge update policies.
            </p>
            {/* 7 Oct: moved here from the banner that sat on the Deployments page. */}
            <p className="text-white/70 text-sm mt-2" data-testid="update-ring-options-criticality-explainer">
              The groups follow the app business criticality levels (High / Medium / Low) set on the Discover Apps
              page. Each device joins exactly <span className="text-white font-medium">one</span> group, by its
              highest-criticality app. <span className="text-green-400">Low</span> criticality devices get updates
              first (the pilot group) and <span className="text-red-400">High</span> criticality devices last, with
              the longest deferral.
            </p>
          </div>

          <div className="p-6 space-y-4">
            <label className="flex items-start gap-3 p-4 rounded-lg border border-[rgba(229,239,250,0.15)] hover:bg-[rgba(229,239,250,0.05)] cursor-pointer transition-colors" data-testid="option-driver-updates">
              <input
                type="checkbox"
                checked={enableDrivers}
                onChange={(e) => setEnableDrivers(e.target.checked)}
                className="mt-1 w-4 h-4 accent-purple-500"
                data-testid="checkbox-enable-drivers"
              />
              <div className="flex-1">
                <p className="text-white font-medium">Driver Updates</p>
                <p className="text-white/70 text-sm mt-1">
                  Creates 3 Windows Driver Update profiles with{' '}
                  <span className="text-purple-300">Automatic approval of recommended driver updates</span>.
                  Make updates available after: <span className="text-green-400">Low = 0d</span>,{' '}
                  <span className="text-amber-400">Medium = 3d</span>,{' '}
                  <span className="text-red-400">High = 7d</span>.
                </p>
              </div>
            </label>

            <label className="flex items-start gap-3 p-4 rounded-lg border border-[rgba(229,239,250,0.15)] hover:bg-[rgba(229,239,250,0.05)] cursor-pointer transition-colors" data-testid="option-office-updates">
              <input
                type="checkbox"
                checked={enableOffice}
                onChange={(e) => setEnableOffice(e.target.checked)}
                className="mt-1 w-4 h-4 accent-purple-500"
                data-testid="checkbox-enable-office"
              />
              <div className="flex-1">
                <p className="text-white font-medium">Office Updates</p>
                <p className="text-white/70 text-sm mt-1">
                  Creates 3 Office Update Configuration Profiles (Settings Catalog) — Monthly Enterprise Channel,
                  deadline 7 days, delay{' '}
                  <span className="text-green-400">0d (Low)</span> /{' '}
                  <span className="text-amber-400">3d (Medium)</span> /{' '}
                  <span className="text-red-400">7d (High)</span>, MDM Wins Over GP, Office CDN path.
                </p>
              </div>
            </label>

            <label className="flex items-start gap-3 p-4 rounded-lg border border-[rgba(229,239,250,0.15)] hover:bg-[rgba(229,239,250,0.05)] cursor-pointer transition-colors" data-testid="option-edge-updates">
              <input
                type="checkbox"
                checked={enableEdge}
                onChange={(e) => setEnableEdge(e.target.checked)}
                className="mt-1 w-4 h-4 accent-purple-500"
                data-testid="checkbox-enable-edge"
              />
              <div className="flex-1">
                <p className="text-white font-medium">Edge Updates</p>
                <p className="text-white/70 text-sm mt-1">
                  Creates a single Edge Configuration Profile (assigned to all 3 criticality groups) —
                  Target Channel: <span className="text-cyan-400">Stable</span>, MDM Wins Over GP.
                </p>
              </div>
            </label>
          </div>

          <div className="p-4 border-t border-[rgba(229,239,250,0.1)] flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={submitting}
              className="border-[rgba(229,239,250,0.2)] text-white/90 hover:bg-[rgba(229,239,250,0.1)]"
              data-testid="cancel-update-ring-options"
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={submitting}
              className="bg-purple-500 hover:bg-purple-600 text-white font-semibold"
              data-testid="confirm-update-ring-options"
            >
              <Zap className={`w-4 h-4 mr-2 ${submitting ? 'animate-pulse' : ''}`} />
              {submitting ? 'Creating...' : 'Create Deployments'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ==================== DEPLOYMENT GROUPS PAGE ====================
// =============================================================================
// QualityUpdatesReport — Deployments > Quality Updates (October 2026)
// -----------------------------------------------------------------------------
// Moved from Discover > Quality Updates, keeping its progress-row reporting,
// and split by owner:
//   * ALICE deployments — rings ALICE created (`auto_created`, exposed by
//     /reports/autopatch-quality-status as `alice_managed`), joined to their
//     deployment group for criticality and delete; custom groups with no ring
//     are listed too, so every group the old tab showed is still here.
//   * Tenant update rings — rings created in the tenant's own Intune, plus
//     "Unassigned" (devices in no ring).
// The summary cards filter both groups. Owns its own data; the page passes
// the deployment groups it already loads.
// =============================================================================
const RISK_PILL = { red: 'bg-red-500/20 text-red-400', amber: 'bg-amber-500/20 text-amber-400', green: 'bg-green-500/20 text-green-400' };
const RISK_LABEL = { red: 'High Criticality', amber: 'Medium Criticality', green: 'Low Criticality' };
const RISK_ORDER = { red: 0, amber: 1, green: 2 };

const QualityUpdatesReport = ({ groups = [], canWrite = false, onDeleteGroup = null, aliceActions = null }) => {
  const [autopatchStatus, setAutopatchStatus] = useState(null);
  const [riskAnalysis, setRiskAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ringFilter, setRingFilter] = useState('all'); // 'all', 'up_to_date', 'in_progress', 'not_up_to_date'
  const [showEmptyRings, setShowEmptyRings] = useState(false);
  const [expandedRing, setExpandedRing] = useState(null);
  const [ringDevices, setRingDevices] = useState({});
  const [loadingRingDevices, setLoadingRingDevices] = useState(null);
  const [ringDevicePage, setRingDevicePage] = useState({});
  const [ringDevicePageSize, setRingDevicePageSize] = useState(20);
  const [collapsed, setCollapsed] = useState({}); // group key -> collapsed

  const load = async () => {
    setLoading(true);
    const [statusRes, riskRes] = await Promise.allSettled([
      axios.get(`${API}/reports/autopatch-quality-status`),
      axios.get(`${API}/reports/risk-analysis`),
    ]);
    setAutopatchStatus(statusRes.status === 'fulfilled' ? statusRes.value.data : { rings: [], summary: {} });
    setRiskAnalysis(riskRes.status === 'fulfilled' ? riskRes.value.data : null);
    if (statusRes.status !== 'fulfilled') toast.error('Failed to load ring compliance');
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  if (loading) return <LoadingSpinner message="Loading quality update compliance..." />;

  const allRings = autopatchStatus?.rings || [];
  const groupsById = new Map((groups || []).map(g => [String(g.id), g]));
  // ALICE rings carry their group; groups with no ring become rows of their own.
  const aliceRings = allRings
    .filter(r => r.alice_managed)
    .map(r => ({ ...r, group: r.deployment_group_id ? groupsById.get(String(r.deployment_group_id)) : null }));
  const linkedGroupIds = new Set(aliceRings.map(r => r.group && String(r.group.id)).filter(Boolean));
  const ringlessGroups = (groups || [])
    .filter(g => !linkedGroupIds.has(String(g.id)))
    .map(g => ({
      ring_id: null, deployment_id: `group-${g.id}`, name: g.name, group: g, no_ring: true,
      device_count: g.device_count || 0, up_to_date: 0, not_up_to_date: 0, in_progress: 0, not_ready: 0,
      latest_quality_update_pct: 0,
    }));
  const byCriticality = (a, b) => (RISK_ORDER[a.group?.risk_level] ?? 9) - (RISK_ORDER[b.group?.risk_level] ?? 9)
    || String(a.name || '').localeCompare(String(b.name || ''));
  const aliceRows = [...aliceRings, ...ringlessGroups].sort(byCriticality);
  const tenantRows = [
    ...allRings.filter(r => !r.alice_managed && !r.is_unassigned),
    ...allRings.filter(r => r.is_unassigned),
  ];

  // Summary cards and filters work across every real ring (as on Discover).
  const rings = allRings;
  const totalRings = rings.length;
  const activeRings = rings.filter(r => (r.device_count || 0) > 0);
  const activeRingCount = activeRings.length;
  const emptyRingCount = totalRings - activeRingCount;
  const ringsUpToDate = activeRings.filter(r => (r.latest_quality_update_pct || 0) === 100).length;
  const ringsInProgress = activeRings.filter(r => (r.in_progress || 0) > 0 && (r.latest_quality_update_pct || 0) < 100).length;
  const ringsNotUpToDate = activeRings.filter(r => (r.latest_quality_update_pct || 0) < 100 && (r.in_progress || 0) === 0).length;
  const pctDenom = activeRingCount || 1;
  const passesFilter = (ring) => {
    const hasDevices = (ring.device_count || 0) > 0;
    if (ring.no_ring) return ringFilter === 'all';
    if (ringFilter === 'all') return hasDevices || showEmptyRings;
    if (!hasDevices) return false;
    if (ringFilter === 'up_to_date') return (ring.latest_quality_update_pct || 0) === 100;
    if (ringFilter === 'in_progress') return (ring.in_progress || 0) > 0 && (ring.latest_quality_update_pct || 0) < 100;
    if (ringFilter === 'not_up_to_date') return (ring.latest_quality_update_pct || 0) < 100 && (ring.in_progress || 0) === 0;
    return true;
  };

  const renderRing = (ring, i) => {
      const ringKey = ring.ring_id || ring.deployment_id || `ring-${i}`;
      const isExpanded = expandedRing === ringKey;
      const ringDeviceList = ringDevices[ringKey] || [];
      const isLoadingDevices = loadingRingDevices === ringKey;
      const pct = Number(ring.latest_quality_update_pct) || 0;
      let barColor = '#ef4444'; // Red < 50%
      let statusIcon = '🔴';
      if (pct >= 80) {
        barColor = '#22c55e'; // Green >= 80%
        statusIcon = '🟢';
      } else if (pct >= 50) {
        barColor = '#f59e0b'; // Amber 50-79%
        statusIcon = '🟡';
      }

      return (
        <React.Fragment key={ringKey}>
          <div
            className={`border-t border-white/5 first:border-t-0 hover:bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] transition-colors cursor-pointer ${isExpanded ? 'bg-[rgba(229,239,250,0.08)]' : ''}`}
            onClick={async () => {
              if (isExpanded) {
                setExpandedRing(null);
              } else {
                setExpandedRing(ringKey);
                // Load devices for this ring if not already loaded
                if (!ringDevices[ringKey] && ring.ring_id) {
                  setLoadingRingDevices(ringKey);
                  try {
                    const res = await axios.get(`${API}/reports/ring-devices/${ring.ring_id}`);
                    setRingDevices(prev => ({...prev, [ringKey]: res.data.devices || []}));
                  } catch (err) {
                    console.error('Failed to load ring devices:', err);
                    // Use devices from ring data as fallback
                    setRingDevices(prev => ({...prev, [ringKey]: ring.devices || []}));
                  } finally {
                    setLoadingRingDevices(null);
                  }
                }
              }
            }}
            data-testid={`ring-row-${ringKey}`}
          >
            <div className="flex items-start gap-3 p-4">
              <div className="w-6 h-6 flex items-center justify-center text-cyan-400 shrink-0">
                {isLoadingDevices ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : isExpanded ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between mb-1.5">
                  <p className="text-sm text-white font-medium truncate pr-4" title={ring.name}>
                    {ring.name}
                  </p>
                  {/* ALICE rings: criticality from the deployment group, and the group's
                      delete, which used to live on the Update Ring Deployments cards. */}
                  {(ring.group || ring.alice_managed) && (
                    <span className="flex items-center gap-2 shrink-0 mr-3" onClick={(e) => e.stopPropagation()}>
                      {ring.group?.risk_level && (
                        <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${RISK_PILL[ring.group.risk_level] || 'bg-white/10 text-white/70'}`}>
                          {RISK_LABEL[ring.group.risk_level] || 'Criticality'}
                        </span>
                      )}
                      {ring.no_ring && (
                        <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-white/10 text-white/60">No update ring</span>
                      )}
                      {canWrite && ring.group && onDeleteGroup && (
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-white/70 hover:text-red-400"
                                title="Delete this deployment group" onClick={() => onDeleteGroup(ring.group.id)}
                                data-testid={`ring-delete-group-${ring.group.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </span>
                  )}
                  <span className="text-lg font-bold text-white whitespace-nowrap">
                    {pct.toFixed(1)}%
                  </span>
                </div>
                <div className="h-5 bg-slate-700/50 rounded-full overflow-hidden mb-1">
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{
                      width: `${Math.max(pct, 2)}%`,
                      backgroundColor: barColor
                    }}
                  />
                </div>
                {/* The counts the old table carried as columns. */}
                <p className="text-xs text-white/90">
                  {statusIcon} {ring.up_to_date || 0} / {ring.device_count || 0} devices fully up to date
                  <span className="text-white/60">
                    {' · '}<span className="text-red-400">{ring.not_up_to_date || 0}</span> not up to date
                    {' · '}<span className="text-amber-400">{ring.in_progress || 0}</span> in progress
                    {(ring.not_ready || 0) > 0 && (
                      <>{' · '}<span className="text-purple-400">{ring.not_ready}</span> not ready</>
                    )}
                  </span>
                </p>
              </div>
            </div>
          </div>
          {/* Expanded device list */}
          {isExpanded && (
                <div className="bg-[rgba(0,0,0,0.2)] border-t border-b border-white/10">
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-medium text-white/90 flex items-center gap-2">
                        <Monitor className="w-4 h-4 text-cyan-400" />
                        Devices in {ring.name} ({ring.device_count || 0})
                      </h4>
                      {!isLoadingDevices && ringDeviceList.length > 0 && (
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid={`ring-export-csv-${ringKey}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            try {
                              const cols = [
                                { header: 'Device Name', accessor: d => d.device_name || d.deviceName || '' },
                                { header: 'OS Version', accessor: d => d.os_version || d.osVersion || '' },
                                { header: 'Status', accessor: d => d.security_status || d.status || '' },
                                { header: 'Client State', accessor: d => d.client_state || d.clientState || '' },
                                { header: 'Last Sync', accessor: d => (d.last_sync || d.lastSync) ? new Date(d.last_sync || d.lastSync).toLocaleString() : '' }
                              ];
                              const fname = `ring_devices_${(ring.name || 'ring').replace(/[^A-Za-z0-9_-]+/g, '_')}`;
                              exportToCSV(ringDeviceList, cols, fname);
                            } catch (err) {
                              console.error('Failed to export ring devices:', err);
                            }
                          }}
                          className="bg-[#E5EFFA] text-[#3C02CC] hover:bg-[#BBFF71] hover:text-[#3C02CC] border-none font-semibold h-7 px-3"
                        >
                          <Download className="w-3.5 h-3.5 mr-1.5" />
                          Export CSV
                        </Button>
                      )}
                    </div>
                    {isLoadingDevices ? (
                      <div className="flex items-center justify-center py-8">
                        <RefreshCw className="w-5 h-5 animate-spin text-cyan-400 mr-2" />
                        <span className="text-white/70">Loading devices...</span>
                      </div>
                    ) : ringDeviceList.length === 0 ? (
                      <p className="text-white/60 text-sm py-4 text-center">
                        No device details available. Device data is synced from WUfB reporting.
                      </p>
                    ) : (
                      <>
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead>
                              <tr className="bg-[rgba(229,239,250,0.05)]">
                                <th className="text-left p-2 text-white/70 text-xs font-medium">Device Name</th>
                                <th className="text-left p-2 text-white/70 text-xs font-medium">OS Version</th>
                                <th className="text-center p-2 text-white/70 text-xs font-medium">Status</th>
                                <th className="text-center p-2 text-white/70 text-xs font-medium">Client State</th>
                                <th className="text-left p-2 text-white/70 text-xs font-medium">Last Sync</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(() => {
                                const currentRingPage = ringDevicePage[ringKey] || 1;
                                const start = (currentRingPage - 1) * ringDevicePageSize;
                                const paginatedRingDevices = ringDeviceList.slice(start, start + ringDevicePageSize);
                                
                                return paginatedRingDevices.map((device, di) => (
                                  <tr key={device.id || di} className="border-t border-white/5">
                                    <td className="p-2">
                                      <span className="text-white text-sm">{device.device_name || device.deviceName || 'Unknown'}</span>
                                    </td>
                                    <td className="p-2">
                                      <span className="text-white/80 text-xs font-mono">{device.os_version || device.osVersion || 'N/A'}</span>
                                    </td>
                                    <td className="p-2 text-center">
                                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                        device.security_status === 'current' || device.status === 'UpToDate' 
                                          ? 'bg-green-500/20 text-green-400' 
                                          : device.security_status === 'failed' || device.status === 'Failed'
                                          ? 'bg-red-500/20 text-red-400'
                                          : 'bg-amber-500/20 text-amber-400'
                                      }`}>
                                        {device.security_status || device.status || 'Unknown'}
                                      </span>
                                    </td>
                                    <td className="p-2 text-center">
                                      <span className={`px-2 py-0.5 rounded-full text-xs ${
                                        device.client_state === 'completed' ? 'bg-green-500/20 text-green-400' :
                                        device.client_state === 'downloading' || device.client_state === 'installing' ? 'bg-cyan-500/20 text-cyan-400' :
                                        device.client_state === 'pending_reboot' ? 'bg-amber-500/20 text-amber-400' :
                                        'bg-[rgba(229,239,250,0.1)] text-white/70'
                                      }`}>
                                        {device.client_state || device.clientState || '-'}
                                      </span>
                                    </td>
                                    <td className="p-2 text-white/60 text-xs">
                                      {device.last_sync || device.lastSync 
                                        ? new Date(device.last_sync || device.lastSync).toLocaleString() 
                                        : 'N/A'}
                                    </td>
                                  </tr>
                                ));
                              })()}
                            </tbody>
                          </table>
                        </div>
                        {/* Pagination for ring devices */}
                        {ringDeviceList.length > ringDevicePageSize && (
                          <div className="flex items-center justify-between px-4 py-2 border-t border-white/10">
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-white/70">
                                Showing {Math.min(((ringDevicePage[ringKey] || 1) - 1) * ringDevicePageSize + 1, ringDeviceList.length)}-{Math.min((ringDevicePage[ringKey] || 1) * ringDevicePageSize, ringDeviceList.length)} of {ringDeviceList.length}
                              </span>
                              <select
                                value={ringDevicePageSize}
                                onChange={(e) => {
                                  setRingDevicePageSize(Number(e.target.value));
                                  setRingDevicePage(prev => ({...prev, [ringKey]: 1}));
                                }}
                                className="bg-[rgba(229,239,250,0.1)] border border-[rgba(229,239,250,0.1)] text-white rounded px-2 py-0.5 text-xs"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <option value={20} className="bg-slate-800">20</option>
                                <option value={50} className="bg-slate-800">50</option>
                                <option value={100} className="bg-slate-800">100</option>
                              </select>
                            </div>
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setRingDevicePage(prev => ({...prev, [ringKey]: Math.max(1, (prev[ringKey] || 1) - 1)}));
                                }}
                                disabled={(ringDevicePage[ringKey] || 1) <= 1}
                                className="text-white/70 hover:text-white h-6 px-2"
                              >
                                <ChevronLeft className="w-3 h-3" />
                              </Button>
                              <span className="text-xs text-white/70 px-2">
                                Page {ringDevicePage[ringKey] || 1} of {Math.ceil(ringDeviceList.length / ringDevicePageSize)}
                              </span>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const maxPage = Math.ceil(ringDeviceList.length / ringDevicePageSize);
                                  setRingDevicePage(prev => ({...prev, [ringKey]: Math.min(maxPage, (prev[ringKey] || 1) + 1)}));
                                }}
                                disabled={(ringDevicePage[ringKey] || 1) >= Math.ceil(ringDeviceList.length / ringDevicePageSize)}
                                className="text-white/70 hover:text-white h-6 px-2"
                              >
                                <ChevronRight className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
          )}
        </React.Fragment>
      );
  };

  const renderGroup = (key, title, subtitle, icon, rows, actions = null) => {
    const visible = rows.filter(passesFilter);
    const isCollapsed = !!collapsed[key];
    const Icon = icon;
    return (
      <Card className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border-[rgba(229,239,250,0.1)] mb-6" data-testid={`rings-group-${key}`}>
        <CardHeader
          className="border-b border-[rgba(229,239,250,0.1)] cursor-pointer select-none"
          onClick={() => setCollapsed(prev => ({ ...prev, [key]: !prev[key] }))}
        >
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <CardTitle className="text-white flex items-center gap-2">
                <ChevronDown className={`w-4 h-4 text-white/60 transition-transform ${isCollapsed ? '-rotate-90' : ''}`} />
                <Icon className="w-5 h-5 text-purple-400" />
                {title}
                <span className="text-xs text-white/60 font-normal ml-1">({visible.length} of {rows.length})</span>
              </CardTitle>
              <p className="text-xs text-white/60 mt-1 ml-6">{subtitle}</p>
            </div>
            {actions && <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>{actions}</div>}
          </div>
        </CardHeader>
        {!isCollapsed && (
          <CardContent className="p-0">
            {visible.length === 0 ? (
              <div className="text-center py-10 text-white/70">
                {rows.length === 0 ? 'None' : `No rings matching "${ringFilter.replace(/_/g, ' ')}"`}
              </div>
            ) : visible.map(renderRing)}
          </CardContent>
        )}
      </Card>
    );
  };

  return (
    <div data-testid="quality-updates-report">
    {/* Summary Cards - Ring counts with drill-down filters */}
    <div className="alice-kpi-row grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
      <div
        onClick={() => setRingFilter('all')}
        className={`bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border rounded-xl p-6 cursor-pointer transition-all hover:scale-[1.02] ${
          ringFilter === 'all'
            ? 'border-cyan-400 ring-2 ring-cyan-400/30'
            : 'border-[rgba(229,239,250,0.1)] hover:border-cyan-400/40'
        }`}
        data-testid="rings-total-card"
      >
        <div className="flex items-center justify-between mb-4">
          <span className="text-sm font-medium uppercase tracking-wider text-white/90">Total Rings</span>
          <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-cyan-400/30 ring-1 ring-cyan-400/50">
            <Settings className="w-5 h-5 text-cyan-400" />
          </div>
        </div>
        <div className="text-4xl font-bold text-white mb-1">{totalRings}</div>
        <div className="text-sm text-white/90">
          {emptyRingCount > 0
            ? `${activeRingCount} active · ${emptyRingCount} empty`
            : 'Click to show all'}
        </div>
      </div>
      <div
        onClick={() => setRingFilter('up_to_date')}
        className={`bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border rounded-xl p-6 cursor-pointer transition-all hover:scale-[1.02] ${
          ringFilter === 'up_to_date'
            ? 'border-green-400 ring-2 ring-green-400/30'
            : 'border-[rgba(229,239,250,0.1)] hover:border-green-400/40'
        }`}
        data-testid="rings-up-to-date-card"
      >
        <div className="flex items-center justify-between mb-4">
          <span className="text-sm font-medium uppercase tracking-wider text-white/90">Up to Date</span>
          <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-green-400/30 ring-1 ring-green-400/50">
            <CheckCircle className="w-5 h-5 text-green-400" />
          </div>
        </div>
        <div className="text-4xl font-bold text-white mb-1">{ringsUpToDate}</div>
        <div className="text-sm text-white/90">
          {((ringsUpToDate / pctDenom) * 100).toFixed(0)}% of active rings · 100% devices current
        </div>
      </div>
      <div
        onClick={() => setRingFilter('in_progress')}
        className={`bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border rounded-xl p-6 cursor-pointer transition-all hover:scale-[1.02] ${
          ringFilter === 'in_progress'
            ? 'border-amber-400 ring-2 ring-amber-400/30'
            : 'border-[rgba(229,239,250,0.1)] hover:border-amber-400/40'
        }`}
        data-testid="rings-in-progress-card"
      >
        <div className="flex items-center justify-between mb-4">
          <span className="text-sm font-medium uppercase tracking-wider text-white/90">In Progress</span>
          <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-amber-400/30 ring-1 ring-amber-400/50">
            <Clock className="w-5 h-5 text-amber-400" />
          </div>
        </div>
        <div className="text-4xl font-bold text-white mb-1">{ringsInProgress}</div>
        <div className="text-sm text-white/90">
          {((ringsInProgress / pctDenom) * 100).toFixed(0)}% of active rings · Devices updating
        </div>
      </div>
      <div
        onClick={() => setRingFilter('not_up_to_date')}
        className={`bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border rounded-xl p-6 cursor-pointer transition-all hover:scale-[1.02] ${
          ringFilter === 'not_up_to_date'
            ? 'border-red-400 ring-2 ring-red-400/30'
            : 'border-[rgba(229,239,250,0.1)] hover:border-red-400/40'
        }`}
        data-testid="rings-not-up-to-date-card"
      >
        <div className="flex items-center justify-between mb-4">
          <span className="text-sm font-medium uppercase tracking-wider text-white/90">Not Up to Date</span>
          <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-red-400/30 ring-1 ring-red-400/50">
            <AlertTriangle className="w-5 h-5 text-red-400" />
          </div>
        </div>
        <div className="text-4xl font-bold text-white mb-1">{ringsNotUpToDate}</div>
        <div className="text-sm text-white/90">
          {((ringsNotUpToDate / pctDenom) * 100).toFixed(0)}% of active rings · Devices need updates
        </div>
      </div>
    </div>

      {/* Filters shared by both groups */}
      <div className="flex items-center justify-end gap-3 mb-3 flex-wrap">
        <span className="text-xs text-white/60 mr-auto">
          Source: {autopatchStatus?.source || 'local_data'}
          {ringFilter !== 'all' && <> · Filtered: {ringFilter.replace(/_/g, ' ')}</>}
        </span>
        <label className="inline-flex items-center gap-2 cursor-pointer select-none text-xs text-white/80"
               title="Show update rings that have no devices assigned." data-testid="show-empty-rings-toggle">
          <input type="checkbox" checked={showEmptyRings} onChange={(e) => setShowEmptyRings(e.target.checked)}
                 className="accent-cyan-400 w-3.5 h-3.5" />
          Show empty rings{emptyRingCount > 0 ? ` (${emptyRingCount})` : ''}
        </label>
        {ringFilter !== 'all' && (
          <Button variant="ghost" size="sm" onClick={() => setRingFilter('all')} className="text-white/70 hover:text-white hover:bg-white/10">
            Clear Filter
          </Button>
        )}
      </div>

      {renderGroup('alice', 'ALICE deployments', 'Update rings ALICE created for each application-criticality group (High, Medium, Low) and any custom groups.', Rocket, aliceRows, aliceActions)}
      {renderGroup('tenant', 'Tenant update rings (synced from Intune)', 'Rings created directly in Intune, and devices in no ring.', Settings, tenantRows)}

      <div className="px-1 pb-2">
        {/* Legend and explanation, carried over from the
            Overview card this section replaced. */}
        <div className="px-4 py-4 border-t border-[rgba(229,239,250,0.1)]">
          <div className="flex flex-wrap gap-3 mb-3">
            <div className="flex items-center gap-1.5 text-xs text-white/90">
              <span>🟢</span> 80-100%
            </div>
            <div className="flex items-center gap-1.5 text-xs text-white/90">
              <span>🟡</span> 50-79%
            </div>
            <div className="flex items-center gap-1.5 text-xs text-white/90">
              <span>🔴</span> &lt;50%
            </div>
          </div>
          <p className="text-xs text-white/80 leading-relaxed">
            Percentage indicates the proportion of devices in the update ring currently running the latest released Windows security update (most recent Patch Tuesday cumulative update). Click a ring to list its devices.
          </p>
        </div>
      </div>

        {/* Risk Analysis content — moved here 2026-06-15 from the
            former Risk Analysis tab. Sits below the per-ring quality
            update status table inside the renamed Quality Updates tab. */}
        <div className="mt-10 mb-4">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Shield className="w-5 h-5 text-cyan-400" />
            Risk Analysis
          </h2>
          <p className="text-sm text-white/70 mt-1">
            Device update status grouped by business-criticality risk category.
          </p>
        </div>

        {/* Risk Category Cards — restyled 2026-06-15 to match Apps
            Discover glass-blue cards (no more coloured gradient on
            the tile body; status colour lives in the icon chip + a
            subtle border highlight only). */}
        <div className="alice-kpi-row grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border border-[rgba(229,239,250,0.1)] rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-medium uppercase tracking-wider text-white/90">High Risk</span>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-red-400/30 ring-1 ring-red-400/50">
                <AlertTriangle className="w-5 h-5 text-red-400" />
              </div>
            </div>
            <div className="text-4xl font-bold text-white mb-1">{riskAnalysis?.summary?.high_risk?.percentage || 0}%</div>
            <div className="text-sm text-white/90">{riskAnalysis?.summary?.high_risk?.count || 0} devices</div>
          </div>
          <div className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border border-[rgba(229,239,250,0.1)] rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-medium uppercase tracking-wider text-white/90">Medium Risk</span>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-amber-400/30 ring-1 ring-amber-400/50">
                <Shield className="w-5 h-5 text-amber-400" />
              </div>
            </div>
            <div className="text-4xl font-bold text-white mb-1">{riskAnalysis?.summary?.medium_risk?.percentage || 0}%</div>
            <div className="text-sm text-white/90">{riskAnalysis?.summary?.medium_risk?.count || 0} devices</div>
          </div>
          <div className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border border-[rgba(229,239,250,0.1)] rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-medium uppercase tracking-wider text-white/90">Low Risk</span>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-green-400/30 ring-1 ring-green-400/50">
                <CheckCircle className="w-5 h-5 text-green-400" />
              </div>
            </div>
            <div className="text-4xl font-bold text-white mb-1">{riskAnalysis?.summary?.low_risk?.percentage || 0}%</div>
            <div className="text-sm text-white/90">{riskAnalysis?.summary?.low_risk?.count || 0} devices</div>
          </div>
          <div className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border border-[rgba(229,239,250,0.1)] rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-medium uppercase tracking-wider text-white/90">Uncategorised</span>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-slate-400/30 ring-1 ring-slate-400/50">
                <HardDrive className="w-5 h-5 text-slate-300" />
              </div>
            </div>
            <div className="text-4xl font-bold text-white mb-1">{riskAnalysis?.summary?.uncategorised?.percentage || 0}%</div>
            <div className="text-sm text-white/90">{riskAnalysis?.summary?.uncategorised?.count || 0} devices</div>
          </div>
        </div>

        {/* Risk Analysis Bar Chart */}
        <Card className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border-[rgba(229,239,250,0.1)]">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Activity className="w-5 h-5 text-cyan-400" />
              Device Update Status by Risk Category
            </CardTitle>
          </CardHeader>
          <CardContent>
            {riskAnalysis?.chart_data?.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={riskAnalysis.chart_data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <XAxis dataKey="name" tick={{ fill: '#fff', fontSize: 12 }} />
                  <YAxis tick={{ fill: '#9ca3af', fontSize: 12 }} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px', color: '#fff' }}
                  />
                  <Legend 
                    formatter={(value) => <span className="text-white/90 text-sm">{value === 'current' ? 'Current' : 'Outdated'}</span>}
                  />
                  <Bar dataKey="current" name="current" fill="#22C55E" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="outdated" name="outdated" fill={CHART_COLORS.coral} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-white/90 text-center py-12">No risk analysis data available. Set business criticality levels on apps to see risk distribution.</p>
            )}
          </CardContent>
        </Card>
    </div>
  );
};

export const DeploymentGroupsPage = () => {
  // Reporting-only tenants can't read update policies: the Update Ring and
  // Feature Update tabs (and their data) are hidden, leaving Firmware.
  const { canWriteOSPatch, canViewOSPatchPolicies } = usePermissions();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  // Bumped whenever the groups reload, so the Quality Updates report (which
  // owns its own ring data) reloads with them after a create, delete or reset.
  const [groupsVersion, setGroupsVersion] = useState(0);
  const [autoCreating, setAutoCreating] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [runningDiagnostic, setRunningDiagnostic] = useState(false);
  const [diagnosticData, setDiagnosticData] = useState(null);
  const [showFeatureDeployWizard, setShowFeatureDeployWizard] = useState(false);
  const [featureCatalog, setFeatureCatalog] = useState([]);
  const [showCreateGroupModal, setShowCreateGroupModal] = useState(false);
  const [deployTab, setDeployTab] = useState(canViewOSPatchPolicies ? 'update-rings' : 'firmware-deployments'); // 'update-rings' | 'feature-updates' | 'firmware-deployments'
  // Bug fix 3 (01/09) — the Eligibility Planner moved out of Discover >
  // Feature updates. It lives inside the Feature Update Deployments tab, on
  // the action row beside the two deployment wizards, because planning who is
  // eligible is a step in creating a deployment rather than a peer of it.
  const [featureUpdateView, setFeatureUpdateView] = useState('deployments'); // 'deployments' | 'planner'
  const [firmwareDeploySelection, setFirmwareDeploySelection] = useState(null);
  const [featureDeployments, setFeatureDeployments] = useState([]);
  const [featureDeploymentsLoading, setFeatureDeploymentsLoading] = useState(false);
  const [showStagedDeployWizard, setShowStagedDeployWizard] = useState(false);
  // 03/09 — target version carried in from the Eligibility Planner, so the
  // wizard can skip the question the planner already answered. Empty when the
  // wizard is opened from the action row in the normal way.
  const [stagedWizardSeedVersion, setStagedWizardSeedVersion] = useState("");
  // 09/09 — device-type scoping chosen on the Eligibility Planner, carried
  // into the wizard so the plan and the deployment agree.
  // null = opened directly, so the wizard applies its own default. An ARRAY
  // (including an empty one) = seeded by the planner, and is honoured as-is.
  const [stagedWizardSeedDeviceTypes, setStagedWizardSeedDeviceTypes] = useState(null);
  const [showUpdateRingOptionsModal, setShowUpdateRingOptionsModal] = useState(false);

  // Cross-page hand-off from FirmwareDiscoverTab → this wizard. When
  // the user clicks "Package / Deploy…" on a family row we stash the
  // selection in sessionStorage + append ``?deployTab=firmware-
  // deployments`` to the URL. Consume it here on mount.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const requestedTab = params.get('deployTab');
      // Bug fix 3 (01/09) — Discover > Feature updates links straight to the
      // relocated planner, so honour every known tab, not just firmware.
      // `eligibility-planner` is not a tab of its own — it is a view inside
      // the Feature Update Deployments tab — but it stays a valid deep link.
      if (!canViewOSPatchPolicies) {
        // Every other tab is hidden on the reporting-only plan.
        setDeployTab('firmware-deployments');
      } else if (requestedTab === 'eligibility-planner') {
        setDeployTab('feature-updates');
        setFeatureUpdateView('planner');
      } else if (['firmware-deployments', 'feature-updates', 'update-rings'].includes(requestedTab)) {
        setDeployTab(requestedTab);
      }
      const raw = sessionStorage.getItem('alice_firmware_deploy_selection');
      if (raw) {
        try {
          setFirmwareDeploySelection(JSON.parse(raw));
        } catch (_e) { /* malformed — ignore */ }
        sessionStorage.removeItem('alice_firmware_deploy_selection');
      }
    } catch (_e) { /* storage unavailable in some environments */ }
  }, []);

  const loadGroups = async () => {
    setLoading(true);
    try {
      const res = await osPatchApi.getDeploymentGroups();
      setGroups(Array.isArray(res.data) ? res.data : []);
      setGroupsVersion(v => v + 1);
    } catch (error) {
      console.error('Error loading groups:', error);
      toast.error('Failed to load deployment groups');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (groupId) => {
    if (!window.confirm('Are you sure you want to delete this deployment group?')) return;
    // Opt-in: deleting the Intune objects is a separate, explicit choice. Cancelling here still
    // deletes the deployment group, leaving the Intune profiles untouched.
    const deleteIntuneObjects = window.confirm(
      'Also delete the Intune objects ALICE created for this group?\n\n' +
      'OK = also delete the update ring, driver update profile and Office update profile ALICE ' +
      'created for this group (plus the shared Edge profile if this is the last group).\n\n' +
      'Cancel = delete the deployment group only and leave the Intune profiles in place.\n\n' +
      'Only profiles ALICE created are affected.'
    );
    try {
      const res = await osPatchApi.deleteDeploymentGroup(groupId, deleteIntuneObjects);
      const cleanup = res?.data?.intune_cleanup;
      if (cleanup) {
        toast.success(
          `Deployment group deleted (Intune: ${cleanup.update_rings_deleted} ring(s), ` +
          `${cleanup.driver_profiles_deleted} driver, ${cleanup.office_profiles_deleted} Office, ` +
          `${cleanup.edge_profiles_deleted} Edge)`
        );
      } else {
        toast.success('Deployment group deleted');
      }
      loadGroups();
    } catch (error) {
      toast.error('Failed to delete deployment group');
    }
  };

  const handleRunDiagnostic = async () => {
    setRunningDiagnostic(true);
    try {
      const res = await axios.get(`${API}/deployment-groups/diagnostic`);
      setDiagnosticData(res.data);
      
      if (res.data.summary?.data_quality?.issue) {
        toast.warning(res.data.summary.data_quality.issue, { duration: 6000 });
      } else {
        toast.success('Diagnostic complete - data looks good!');
      }
    } catch (error) {
      console.error('Error running diagnostic:', error);
      toast.error('Failed to run diagnostic');
    } finally {
      setRunningDiagnostic(false);
    }
  };

  const handleResetAll = async () => {
    if (!window.confirm('This will delete ALL deployment groups and auto-created update rings. Are you sure?')) return;
    // Opt-in: also remove the matching objects from Intune, not just ALICE's records.
    const deleteIntuneObjects = window.confirm(
      'Also delete the Intune objects ALICE created?\n\n' +
      'OK = also delete the ALICE-created update rings and the driver, Office and Edge update ' +
      'profiles from Intune.\n\n' +
      'Cancel = clear ALICE records only and leave the Intune objects in place.\n\n' +
      'Only profiles ALICE created are affected.'
    );
    setResetting(true);
    try {
      const res = await axios.delete(
        `${API}/deployment-groups/reset-all?delete_intune_objects=${deleteIntuneObjects}`
      );
      toast.success(res.data.message || 'All deployment groups reset');
      setLastResult(null);
      loadGroups();
    } catch (error) {
      console.error('Error resetting groups:', error);
      toast.error('Failed to reset deployment groups');
    } finally {
      setResetting(false);
    }
  };

  const handleAutoCreateGroups = async (options = {}) => {
    setAutoCreating(true);
    try {
      const payload = {
        enable_driver_updates: !!options.enable_driver_updates,
        enable_office_updates: !!options.enable_office_updates,
        enable_edge_updates: !!options.enable_edge_updates,
      };
      const res = await axios.post(`${API}/deployment-groups/auto-create-risk-groups`, payload);
      const data = res.data;
      
      setLastResult(data);
      
      if (data.summary?.total_devices_assigned > 0) {
        const driverCount = (data.created_driver_profiles || []).length;
        const officeCount = (data.created_office_profiles || []).length;
        const edgeCount = data.created_edge_profile ? 1 : 0;
        toast.success(
          <div>
            <p className="font-medium">Deployment groups & rings created!</p>
            <p className="text-sm mt-1">
              High: {data.summary?.high_criticality_devices || 0} | 
              Medium: {data.summary?.medium_criticality_devices || 0} | 
              Low: {data.summary?.low_criticality_devices || 0}
            </p>
            <p className="text-xs mt-1 text-white/90">
              {data.created_rings?.length || 0} update rings created
              {driverCount > 0 && `, ${driverCount} driver profiles`}
              {officeCount > 0 && `, ${officeCount} Office profiles`}
              {edgeCount > 0 && `, ${edgeCount} Edge profile`}
            </p>
          </div>,
          { duration: 6000 }
        );
      } else {
        toast.warning(
          <div>
            <p className="font-medium">Groups created but no devices assigned</p>
            <p className="text-sm mt-1">
              Apps with criticality: {data.summary?.apps_with_criticality || 0}<br/>
              Apps with device data: {data.summary?.apps_with_device_data || 0}
            </p>
            <p className="text-xs mt-1 text-amber-300">
              Ensure apps have business criticality set and Intune sync includes device data.
            </p>
          </div>,
          { duration: 8000 }
        );
      }
      
      loadGroups();
    } catch (error) {
      console.error('Error auto-creating groups:', error);
      toast.error('Failed to auto-create risk groups');
    } finally {
      setAutoCreating(false);
    }
  };

  useEffect(() => {
    if (!canViewOSPatchPolicies) return;
    loadGroups(); loadFeatureDeployments();
  }, [canViewOSPatchPolicies]);

  // Permissions can resolve after mount; if the tenant turns out to be
  // reporting-only, move off a tab that is no longer shown.
  useEffect(() => {
    if (!canViewOSPatchPolicies) setDeployTab('firmware-deployments');
  }, [canViewOSPatchPolicies]);

  const loadFeatureDeployments = async () => {
    setFeatureDeploymentsLoading(true);
    try {
      const res = await osPatchApi.getFeatureUpdateDeployments();
      setFeatureDeployments(Array.isArray(res.data) ? res.data : []);
    } catch { /* empty */ } finally {
      setFeatureDeploymentsLoading(false);
    }
  };

  const handleOpenFeatureDeployWizard = async () => {
    try {
      const res = await osPatchApi.getFeatureUpdateCatalog();
      setFeatureCatalog(Array.isArray(res.data) ? res.data : res.data?.catalog || []);
      setShowFeatureDeployWizard(true);
    } catch (err) {
      toast.error('Failed to load feature update catalog');
    }
  };

  const handleOpenStagedDeployWizard = async (seedVersion = '', seedDeviceTypes = null) => {
    try {
      const res = await osPatchApi.getFeatureUpdateCatalog();
      setFeatureCatalog(Array.isArray(res.data) ? res.data : res.data?.catalog || []);
      setStagedWizardSeedVersion(typeof seedVersion === 'string' ? seedVersion : '');
      setStagedWizardSeedDeviceTypes(Array.isArray(seedDeviceTypes) ? seedDeviceTypes : null);
      setShowStagedDeployWizard(true);
    } catch (err) {
      toast.error('Failed to load feature update catalog');
    }
  };

  // 03/09 — "Create deployment" on the Eligibility Planner. Switches back to
  // the deployments view so closing the wizard leaves the user on the list
  // holding their new deployment, not back on the planner.
  // 09/09 — carries the planner's device-type scoping across too. Deciding to
  // leave AVD hosts out is part of the plan; making the admin make that call
  // twice is how the two end up disagreeing.
  const handleCreateDeploymentFromPlanner = async (targetVersion, excludedDeviceTypes = null) => {
    setFeatureUpdateView('deployments');
    await handleOpenStagedDeployWizard(targetVersion, excludedDeviceTypes);
  };

  return (
    <div className="p-8" data-testid="os-patch-groups">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Deployments</h1>
          <p className="text-white/90">Manage Application based risk for deployment groups with Windows Quality and Feature updates</p>
        </div>
        {canViewOSPatchPolicies && (
        <div className="flex gap-3">
          <Button
            onClick={handleRunDiagnostic}
            disabled={runningDiagnostic}
            variant="outline" 
            className="border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/20" 
            data-testid="diagnostic-btn"
          >
            <Activity className={`w-4 h-4 mr-2 ${runningDiagnostic ? 'animate-pulse' : ''}`} />
            {runningDiagnostic ? 'Running...' : 'Diagnostic'}
          </Button>
          {canWriteOSPatch && (
          <Button 
            onClick={handleResetAll} 
            disabled={resetting || groups.length === 0}
            variant="outline" 
            className="border-red-500/50 text-red-400 hover:bg-red-500/20" 
            data-testid="reset-groups-btn"
          >
            <Trash2 className={`w-4 h-4 mr-2 ${resetting ? 'animate-spin' : ''}`} />
            {resetting ? 'Resetting...' : 'Reset All'}
          </Button>
          )}
          {canWriteOSPatch && (
          <Button onClick={() => setShowCreateGroupModal(true)} className="bg-[#E5EFFA] text-[#3C02CC] hover:bg-[#BBFF71] hover:text-[#3C02CC] active:bg-[#BBFF71] active:text-[#3C02CC] font-semibold" data-testid="create-group-btn">
            <Plus className="w-4 h-4 mr-2" />
            Custom Group
          </Button>
          )}
        </div>
        )}
      </div>

      <OSPatchReadOnlyNotice className="mb-4" />

      {/* Diagnostic Results */}
      {canViewOSPatchPolicies && diagnosticData && (
        <Card className="bg-slate-800/50 border-cyan-500/30 mb-6">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-cyan-400 text-lg flex items-center gap-2">
                <Activity className="w-5 h-5" />
                Diagnostic Results
              </CardTitle>
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-white/70 hover:text-white/90"
                onClick={() => setDiagnosticData(null)}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Data Quality Issue Alert */}
            {diagnosticData.summary?.data_quality?.issue && (
              <div className="bg-red-500/20 border border-red-500/40 rounded-lg p-3">
                <p className="text-red-400 font-medium flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  Issue Detected
                </p>
                <p className="text-white text-sm mt-1">{diagnosticData.summary.data_quality.issue}</p>
              </div>
            )}
            
            {/* Summary Grid */}
            <div className="alice-kpi-row grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] rounded-lg p-3">
                <p className="text-white/90 text-xs uppercase">Apps with Criticality</p>
                <p className="text-2xl font-bold text-white">{diagnosticData.summary?.total_apps_with_criticality || 0}</p>
              </div>
              <div className="bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] rounded-lg p-3">
                <p className="text-white/90 text-xs uppercase">Apps with Device Data</p>
                <p className="text-2xl font-bold text-white">{diagnosticData.summary?.total_apps_with_device_data || 0}</p>
              </div>
              <div className="bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] rounded-lg p-3">
                <p className="text-white/90 text-xs uppercase">Apps Ready for Grouping</p>
                <p className="text-2xl font-bold text-green-400">{diagnosticData.summary?.apps_ready_for_grouping || 0}</p>
              </div>
              <div className="bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] rounded-lg p-3">
                <p className="text-white/90 text-xs uppercase">Devices with Azure ID</p>
                <p className="text-2xl font-bold text-white">
                  {diagnosticData.summary?.devices_with_azure_ad_id || 0}
                  <span className="text-sm text-white/70">/{diagnosticData.summary?.total_devices_in_system || 0}</span>
                </p>
              </div>
            </div>

            {/* Apps Ready for Grouping */}
            {diagnosticData.apps_ready_for_grouping?.length > 0 && (
              <div>
                <p className="text-white/90 text-sm mb-2">Apps Ready for Grouping (have both criticality & devices):</p>
                <div className="flex flex-wrap gap-2">
                  {diagnosticData.apps_ready_for_grouping.slice(0, 10).map((app, i) => (
                    <span key={i} className="px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded">
                      {app.family_name} ({app.device_count} devices)
                    </span>
                  ))}
                  {diagnosticData.apps_ready_for_grouping.length > 10 && (
                    <span className="px-2 py-1 bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] text-white/90 text-xs rounded">
                      +{diagnosticData.apps_ready_for_grouping.length - 10} more
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Sample Devices */}
            {diagnosticData.devices_sample?.length > 0 && (
              <div>
                <p className="text-white/90 text-sm mb-2">Sample Devices:</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-white/70 text-left">
                        <th className="pb-2">Device Name</th>
                        <th className="pb-2">Has Azure AD ID</th>
                        <th className="pb-2">Intune ID</th>
                      </tr>
                    </thead>
                    <tbody>
                      {diagnosticData.devices_sample.slice(0, 5).map((device, i) => (
                        <tr key={i} className="text-white border-t border-[rgba(229,239,250,0.1)]">
                          <td className="py-2">{device.device_name}</td>
                          <td className="py-2">
                            {device.has_azure_ad_id ? 
                              <span className="text-green-400">Yes</span> : 
                              <span className="text-red-400">No</span>
                            }
                          </td>
                          <td className="py-2 text-white/70 text-xs">{device.intune_id?.slice(0, 8)}...</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Recommendations */}
            <div className="bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] rounded-lg p-3">
              <p className="text-white/90 text-sm mb-2">Recommendations:</p>
              <ul className="text-white text-sm space-y-1">
                {diagnosticData.recommendations?.map((rec, i) => (
                  <li key={i}>{rec}</li>
                ))}
              </ul>
            </div>
          </CardContent>
        </Card>
      )}

      {/* The Update Ring and Feature Update explanation banners that sat here
          moved (7 Oct) to where the choice is made: the Create Update Ring
          Deployments dialog, and page 1 of the feature update wizard, worded
          for the Staged or App Risk mode it was opened in. */}

      {/* Last Result Summary */}
      {canViewOSPatchPolicies && lastResult && (
        <Card className="bg-cyan-500/10 border-cyan-500/30 mb-6">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-cyan-400 font-medium text-sm">Last Auto-Create Result</p>
                <div className="flex gap-4 mt-2 text-sm">
                  <span className="text-white">
                    <span className="text-red-400 font-medium">{lastResult.summary?.high_criticality_devices || 0}</span> High
                  </span>
                  <span className="text-white">
                    <span className="text-amber-400 font-medium">{lastResult.summary?.medium_criticality_devices || 0}</span> Medium
                  </span>
                  <span className="text-white">
                    <span className="text-green-400 font-medium">{lastResult.summary?.low_criticality_devices || 0}</span> Low
                  </span>
                  <span className="text-white/90">|</span>
                  <span className="text-white/90">
                    Apps with criticality: {lastResult.summary?.apps_with_criticality || 0}
                  </span>
                  <span className="text-white/90">
                    Apps with devices: {lastResult.summary?.apps_with_device_data || 0}
                  </span>
                </div>
                {((lastResult.created_driver_profiles || []).length > 0 ||
                  (lastResult.created_office_profiles || []).length > 0 ||
                  lastResult.created_edge_profile) && (
                  <div className="flex gap-4 mt-2 text-sm">
                    {(lastResult.created_driver_profiles || []).length > 0 && (
                      <span className="text-white" data-testid="result-driver-count">
                        <span className="text-purple-300 font-medium">{(lastResult.created_driver_profiles || []).length}</span> Driver profiles
                      </span>
                    )}
                    {(lastResult.created_office_profiles || []).length > 0 && (
                      <span className="text-white" data-testid="result-office-count">
                        <span className="text-purple-300 font-medium">{(lastResult.created_office_profiles || []).length}</span> Office profiles
                      </span>
                    )}
                    {lastResult.created_edge_profile && (
                      <span className="text-white" data-testid="result-edge-count">
                        <span className="text-purple-300 font-medium">1</span> Edge profile
                      </span>
                    )}
                  </div>
                )}
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-white/70 hover:text-white/90"
                onClick={() => setLastResult(null)}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Deployment Tabs */}
      <div className="flex gap-1 mb-6 bg-[rgba(229,239,250,0.1)] rounded-lg p-1 w-fit" data-testid="deploy-tabs">
        {canViewOSPatchPolicies && (
        <>
        <button
          onClick={() => setDeployTab('update-rings')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-2 ${
            deployTab === 'update-rings' ? 'bg-purple-500 text-white' : 'text-white/70 hover:text-white hover:bg-white/10'
          }`}
          data-testid="tab-update-rings"
        >
          <RefreshCw className="w-4 h-4" />
          Quality Updates
        </button>
        <button
          onClick={() => setDeployTab('feature-updates')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-2 ${
            deployTab === 'feature-updates' ? 'bg-purple-500 text-white' : 'text-white/70 hover:text-white hover:bg-white/10'
          }`}
          data-testid="tab-feature-updates"
        >
          <Rocket className="w-4 h-4" />
          Feature Update Deployments
        </button>
        </>
        )}
        <button
          onClick={() => setDeployTab('firmware-deployments')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-2 ${
            deployTab === 'firmware-deployments' ? 'bg-purple-500 text-white' : 'text-white/70 hover:text-white hover:bg-white/10'
          }`}
          data-testid="tab-firmware-deployments"
        >
          <Cpu className="w-4 h-4" />
          Firmware Deployments
        </button>
      </div>

      {/* ===== QUALITY UPDATES TAB (was Update Ring Deployments) =====
          October 2026: Discover > Quality Updates moved here. Ring progress
          for ALICE's criticality rings and the tenant's own Intune rings,
          each expandable to its devices; the deployment groups' create and
          delete live on the ALICE group. */}
      {canViewOSPatchPolicies && deployTab === 'update-rings' && (
        loading ? (
          <LoadingSpinner message="Loading deployment groups..." />
        ) : (
          <QualityUpdatesReport
            // Remount after a create / delete / reset so the ring report reloads with the groups.
            key={`qu-${groupsVersion}`}
            groups={groups}
            canWrite={canWriteOSPatch}
            onDeleteGroup={handleDelete}
            aliceActions={canWriteOSPatch ? (
              <Button
                size="sm"
                onClick={() => setShowUpdateRingOptionsModal(true)}
                disabled={autoCreating}
                variant="outline"
                className="border-purple-500 text-purple-300 hover:bg-purple-500/20"
                data-testid="auto-create-groups-btn"
              >
                <Zap className={`w-4 h-4 mr-2 ${autoCreating ? 'animate-pulse' : ''}`} />
                {autoCreating ? 'Creating...' : '+ Update Ring Deployments'}
              </Button>
            ) : null}
          />
        )
      )}

      {/* ===== FEATURE UPDATE DEPLOYMENTS TAB ===== */}
      {canViewOSPatchPolicies && deployTab === 'feature-updates' && (
        <>
          <div className="flex items-center gap-3 mb-4">
            {canWriteOSPatch && (
            <Button 
              onClick={handleOpenStagedDeployWizard}
              className="bg-purple-500 hover:bg-purple-600 text-white font-semibold" 
              data-testid="new-staged-deployment-btn"
            >
              <Rocket className="w-4 h-4 mr-2" />
              + Staged Deployment
            </Button>
            )}
            {canWriteOSPatch && (
            <Button
              onClick={handleOpenFeatureDeployWizard}
              variant="outline"
              className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
              data-testid="create-feature-deploy-btn"
            >
              <Target className="w-4 h-4 mr-2" />
              + App Risk Deployment
            </Button>
            )}

            {/* Bug fix 3 (01/09) — the Eligibility Planner sits on this action
                row rather than being a peer tab: it is the step you run BEFORE
                either wizard, to see who is eligible and how they would split
                across sprints. */}
            <Button
              onClick={() => setFeatureUpdateView(
                featureUpdateView === 'planner' ? 'deployments' : 'planner',
              )}
              variant="outline"
              className={featureUpdateView === 'planner'
                ? 'border-cyan-400 bg-cyan-500/20 text-cyan-200 hover:bg-cyan-500/30'
                : 'border-cyan-500/50 text-cyan-300 hover:bg-cyan-500/10'}
              data-testid="tab-eligibility-planner"
            >
              <Layers className="w-4 h-4 mr-2" />
              Eligibility Planner
            </Button>

            {featureUpdateView === 'planner' && (
              <button
                type="button"
                onClick={() => setFeatureUpdateView('deployments')}
                className="text-xs text-white/60 hover:text-white underline underline-offset-2"
                data-testid="eligibility-planner-back-btn"
              >
                Back to deployments
              </button>
            )}
          </div>

          {featureUpdateView === 'planner' ? (
            <div data-testid="eligibility-planner-tab">
              <FeatureUpdateEligibilityPlanner
                onCreateDeployment={handleCreateDeploymentFromPlanner}
              />
            </div>
          ) : featureDeploymentsLoading && featureDeployments.length === 0 ? (
            <LoadingSpinner message="Loading feature update deployments..." />
          ) : (
            // October 2026: Discover > Feature Updates moved here. Progress
            // for every profile, grouped into ALICE deployments (each with a
            // Manage toggle showing its existing card) and tenant profiles.
            <FeatureUpdateProgressReport
              deployments={featureDeployments}
              renderManage={(deployment) => (
                <FeatureUpdateDeploymentCard
                  deployment={deployment}
                  // New Feature 2 (01/09) — the sibling list lets the move
                  // modal offer other deployments as a destination.
                  deployments={featureDeployments}
                  onRefresh={loadFeatureDeployments}
                  onDelete={loadFeatureDeployments}
                />
              )}
            />
          )}
        </>
      )}

      {/* ===== FIRMWARE DEPLOYMENTS TAB (Phase 4) ===== */}
      {deployTab === 'firmware-deployments' && (
        <FirmwareDeploymentsTab
          initialSelection={firmwareDeploySelection}
          onWizardConsumed={() => setFirmwareDeploySelection(null)}
        />
      )}

      {/* Feature Update Deployment Wizard (App Risk Mode) */}
      {showFeatureDeployWizard && (
        <FeatureUpdateDeploymentWizard
          catalog={featureCatalog}
          appRiskMode={true}
          onClose={() => setShowFeatureDeployWizard(false)}
          onDeploymentCreated={() => {
            setShowFeatureDeployWizard(false);
            loadFeatureDeployments();
            toast.success('Feature update deployment created');
          }}
        />
      )}

      {/* Staged Deployment Wizard (Device Phase Mode) */}
      {showStagedDeployWizard && (
        <FeatureUpdateDeploymentWizard
          catalog={featureCatalog}
          initialTargetVersion={stagedWizardSeedVersion}
          initialExcludedDeviceTypes={stagedWizardSeedDeviceTypes}
          onClose={() => {
            setShowStagedDeployWizard(false);
            setStagedWizardSeedVersion("");
            setStagedWizardSeedDeviceTypes(null);
          }}
          onDeploymentCreated={() => {
            setShowStagedDeployWizard(false);
            setStagedWizardSeedVersion("");
            setStagedWizardSeedDeviceTypes(null);
            loadFeatureDeployments();
            toast.success('Staged deployment created');
          }}
        />
      )}

      {/* Update Ring Options Modal */}
      {showUpdateRingOptionsModal && (
        <UpdateRingOptionsModal
          submitting={autoCreating}
          onClose={() => setShowUpdateRingOptionsModal(false)}
          onConfirm={async (opts) => {
            setShowUpdateRingOptionsModal(false);
            await handleAutoCreateGroups(opts);
          }}
        />
      )}

      {/* Create Group Modal */}
      {showCreateGroupModal && (
        <CreateGroupModal
          onClose={() => setShowCreateGroupModal(false)}
          onCreated={() => {
            setShowCreateGroupModal(false);
            loadGroups();
          }}
        />
      )}

      {/* Progress overlay for long operations */}
      {autoCreating && (
        <CreationProgressOverlay 
          message="Creating Update Ring Deployments" 
          detail="Creating Azure AD groups, assigning devices, and configuring update rings. This may take a minute..." 
        />
      )}
    </div>
  );
};

// ==================== REPORTS PAGE ====================

// ==================== FEATURE UPDATE REPORT COMPONENT ====================

// 09/09 — Device-type scoping.
//
// Intune reports Windows 365 Cloud PCs, Azure VMs and AVD session hosts all as
// virtual machines, but they want opposite treatment in a feature update
// rollout: a Cloud PC is somebody's desktop and needs the update; an AVD
// session host is rebuilt from a golden image on a schedule the desktop team
// does not own, and upgrading it mid-session is actively harmful. So this is a
// per-category choice, not one "skip VMs" switch.
//
// Rendered in two places — the planner (before Check Eligibility, because it
// changes the answer) and the wizard's step 2 (where every other scoping
// decision lives). One component so the two can never describe the categories
// differently.
//
// The category list comes from the API when it is there; the local fallback is
// only for a frontend built ahead of its backend.
const DEVICE_TYPE_FALLBACK_CATALOGUE = [
  { key: 'physical', label: 'Physical device', selectable: false,
    description: 'Laptops, desktops and tablets with real hardware.' },
  { key: 'cloud_pc', label: 'Cloud PC (Windows 365)', selectable: true,
    description: 'Usually kept in the rollout — they are user desktops and need the update like any other endpoint.' },
  { key: 'azure_vm', label: 'Azure VM / AVD / Hyper-V', selectable: true,
    description: 'Intune reports these as manufacturer Microsoft Corporation, model Virtual Machine. Commonly excluded — rebuilt from an image rather than upgraded in place.' },
  { key: 'other_vm', label: 'Other virtual machine', selectable: true,
    description: 'VMware, VirtualBox, Parallels, QEMU/KVM/Xen, and VMs on other cloud providers.' },
];

// 10/09 round 2 — the house default for device-type scope, shared by the
// wizard and the Eligibility Planner so the two cannot drift.
//
// Both screens have always ADVISED this in prose — "most rollouts keep Cloud
// PCs and leave Azure VMs and AVD session hosts out, because those are rebuilt
// from an image rather than upgraded in place" — while defaulting to including
// everything. An admin who took the advice had to act on it twice, on two
// screens, every time. The advice is now the default; unticking is still one
// click, and the counts beside each row say what including them would add.
const FU_DEFAULT_EXCLUDED_DEVICE_TYPES = ['azure_vm', 'other_vm'];

// Order-insensitive comparison of two short string lists. Used to tell whether
// the results on screen were produced with the scoping currently selected.
const sameStringSet = (a = [], b = []) => {
  const x = [...(a || [])].sort();
  const y = [...(b || [])].sort();
  return x.length === y.length && x.every((v, i) => v === y[i]);
};

const DeviceTypeScopeSelector = ({
  catalogue,
  counts,
  value = [],
  onChange,
  testIdPrefix = 'device-type',
  disabled = false,
  note = null,
}) => {
  const rows = (Array.isArray(catalogue) && catalogue.length ? catalogue : DEVICE_TYPE_FALLBACK_CATALOGUE);
  const excluded = new Set(value || []);
  const known = counts && typeof counts === 'object';

  const toggle = (key) => {
    if (disabled) return;
    const next = excluded.has(key)
      ? (value || []).filter(k => k !== key)
      : [...(value || []), key];
    onChange(next);
  };

  return (
    <div className="space-y-2" data-testid={`${testIdPrefix}-scope`}>
      {rows.map((row) => {
        const count = known ? (counts[row.key] ?? 0) : null;
        // Physical devices are shown for context — "these are your 300 real
        // laptops" — but cannot be excluded. Excluding every physical device
        // is not a feature anyone wants; it is a mis-click.
        if (!row.selectable) {
          return (
            <div key={row.key} className="flex items-start gap-3 opacity-60" data-testid={`${testIdPrefix}-${row.key}`}>
              <div className="w-4 h-4 mt-0.5 shrink-0" />
              <span className="text-white/80 text-sm">
                {row.label}
                {count != null && <span className="text-white/50"> — {count} device{count === 1 ? '' : 's'}</span>}
                <span className="block text-xs text-white/40 mt-0.5">Always included.</span>
              </span>
            </div>
          );
        }
        const isExcluded = excluded.has(row.key);
        return (
          <label
            key={row.key}
            className={`flex items-start gap-3 ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
            data-testid={`${testIdPrefix}-${row.key}`}
          >
            <input
              type="checkbox"
              checked={!isExcluded}
              disabled={disabled}
              onChange={() => toggle(row.key)}
              className="w-4 h-4 mt-0.5 rounded border-[rgba(229,239,250,0.1)] bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] text-purple-500"
            />
            <span className="text-white text-sm">
              Include {row.label}
              {count != null && (
                <span className={count === 0 ? 'text-white/40' : 'text-white/60'}>
                  {' '}— {count} device{count === 1 ? '' : 's'}
                </span>
              )}
              <span className="block text-xs text-white/50 mt-0.5">{row.description}</span>
              {isExcluded && count > 0 && (
                <span className="block text-xs text-amber-300 mt-0.5" data-testid={`${testIdPrefix}-${row.key}-excluded`}>
                  {count} device{count === 1 ? '' : 's'} will be left out.
                </span>
              )}
            </span>
          </label>
        );
      })}
      {note}
    </div>
  );
};

const FeatureUpdateEligibilityPlanner = ({ onCreateDeployment }) => {
  const { canWriteOSPatch } = usePermissions();
  const [selectedVersion, setSelectedVersion] = useState('');
  const [catalog, setCatalog] = useState([]);
  const [eligibilityData, setEligibilityData] = useState(null);
  // 03/09 — the version the results on screen were actually produced for.
  // `selectedVersion` is the dropdown, which the user can move after a run;
  // creating a deployment has to follow what was planned, not what is selected.
  const [resultsVersion, setResultsVersion] = useState('');
  const [loading, setLoading] = useState(false);
  const [catalogLoading, setCatalogLoading] = useState(true);
  // 02/09 — a readiness sync failure has to be visible on the results, not
  // only in a toast that disappears.
  const [syncError, setSyncError] = useState(null);
  // 02/09 — the two risk views are separate measurements and each collapses
  // independently. Readiness opens by default because it is what gates a
  // Staged deployment; application criticality only matters to an App Risk one.
  const [showAppRisk, setShowAppRisk] = useState(false);
  // 09/09 — device-type scoping. Which virtual machine categories to leave out
  // of the plan, e.g. exclude Azure VMs / AVD session hosts but keep Cloud PCs.
  // Empty by default: every plan before today included VMs, and a default that
  // quietly shrank the eligible pool would read as a regression.
  // 10/09 round 2 — defaults to leaving Azure VMs and AVD session hosts out.
  //  See FU_DEFAULT_EXCLUDED_DEVICE_TYPES. Copied, never shared by reference.
  const [excludedDeviceTypes, setExcludedDeviceTypes] = useState([...FU_DEFAULT_EXCLUDED_DEVICE_TYPES]);
  // The scoping the results on screen were actually produced with — same
  // reasoning as `resultsVersion`. Without it the "re-run to apply" note fires
  // on a perfectly current result, and an admin learns to ignore it.
  const [resultsDeviceTypes, setResultsDeviceTypes] = useState([]);

  // Load catalog on mount
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/feature-updates/catalog`);
        setCatalog(res.data || []);
      } catch (e) { console.error(e); }
      finally { setCatalogLoading(false); }
    })();
  }, []);

  const handleCheckEligibility = async () => {
    if (!selectedVersion) return;
    setLoading(true);
    setSyncError(null);
    try {
      const res = await osPatchApi.checkDeviceEligibility({
        target_version: selectedVersion,
        // 02/09 — the planner is a planner. It shows the whole picture and
        // never hides a risk band behind a filter; the deployment wizard is
        // where devices are actually included or excluded.
        include_medium_risk: true,
        // No `min_disk_space_gb` here, deliberately. The planner has no
        // per-run threshold of its own — it should always apply the product
        // gate, and the backend owns that number (FU_MIN_DISK_GB). 03/09:
        // sending it from here meant a frontend bundle built before the gate
        // moved kept posting the old value, and the planner reported
        // "< 40GB required" against a backend that said 30. The wizard still
        // sends its per-deployment value, which is a real choice.
        // Readiness is an input to this answer, so refresh it as part of the
        // check rather than relying on someone having pressed a button first.
        sync_readiness: true,
        // 09/09 — device-type scoping is a real planning choice (an estate
        // with AVD in it plans very differently), so unlike the risk filters
        // above it IS applied here, and carried into the wizard.
        excluded_virtualisation_types: excludedDeviceTypes,
      });
      setEligibilityData(res.data);
      setResultsVersion(selectedVersion);
      // Take it from the response, not from local state — the backend drops
      // categories it does not recognise, and the note should reflect what was
      // applied rather than what was asked for.
      setResultsDeviceTypes(res.data?.excluded_virtualisation_types || excludedDeviceTypes);

      const sync = res.data?.readiness_sync;
      if (sync && sync.status !== 'success') {
        // Say it plainly. Planning against stale readiness data is worse than
        // knowing the data did not refresh.
        setSyncError(
          sync.error || sync.message
          || 'ALICE could not refresh the readiness data from Intune. The results below may be out of date.'
        );
        toast.warning('Readiness data could not be refreshed — see the note above the results');
      } else if (sync?.count != null) {
        toast.success(`Eligibility checked · readiness refreshed for ${sync.count} device(s)`);
      }
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || 'Failed to check eligibility');
    } finally { setLoading(false); }
  };

  // 02/09 — the sprint distribution preview has been removed from the planner.
  // This screen answers "which devices can be deployed to", and it was showing
  // a sprint split using its own simplified percentage model that did not match
  // what the deployment wizard would actually produce. Sprint analysis belongs
  // in the deployment preview, against the real allocator.

  // Readiness bands across the whole Windows estate, computed by the backend so
  // this view and the sprint allocator cannot disagree.
  // 09/09 — the bands are strictly Intune's assessment. `Unknown` means "not
  // assessed", and is broken down by why, so the planner can explain the number
  // instead of parking it under the High Risk card where it read as high risk.
  const readinessBands = useMemo(() => {
    const b = eligibilityData?.summary?.readiness_bands || {};
    const unknown = b.Unknown || 0;
    return {
      LowRisk: b.LowRisk || 0,
      MediumRisk: b.MediumRisk || 0,
      HighRisk: b.HighRisk || 0,
      Unknown: unknown,
      assessed: b.assessed ?? Math.max(0, (b.LowRisk || 0) + (b.MediumRisk || 0) + (b.HighRisk || 0)),
      unknownOnTarget: b.unknown_on_target || 0,
      unknownNotUpgradable: b.unknown_not_upgradable || 0,
      // Older API responses carry Unknown but no breakdown — attribute the
      // whole lot to "no data" rather than showing three zeroes that don't add up.
      unknownNoData: b.unknown_no_data ?? unknown,
    };
  }, [eligibilityData]);

  // Risk breakdown
  const riskBreakdown = useMemo(() => {
    if (!eligibilityData) return { low: 0, medium: 0, high: 0 };
    const eligible = eligibilityData.eligible_devices || [];
    const low = eligible.filter(d => ['low', 'green'].includes((d.app_risk_category || 'low').toLowerCase())).length;
    const medium = eligible.filter(d => ['medium', 'amber'].includes((d.app_risk_category || '').toLowerCase())).length;
    const high = eligible.filter(d => ['high', 'red'].includes((d.app_risk_category || '').toLowerCase())).length;
    return { low, medium, high };
  }, [eligibilityData]);

  // Bug fix 3 (01/09) — the "Total Devices" card was reading `total_devices`,
  // which is every enrolled device in the tenant (iOS, macOS, Android
  // included). The planner only ever considers Windows devices, so the
  // denominator must be the Windows count. `excluded` is everything Windows
  // that did not come back eligible — already in a sprint, high/medium
  // readiness risk, disk, upgrade path, phase=Excluded, or a manual exclusion.
  const deviceTotals = useMemo(() => {
    if (!eligibilityData) return { windows: 0, eligible: 0, excluded: 0, allDevices: 0 };
    const summary = eligibilityData.summary || {};
    const windows = summary.windows_devices ?? eligibilityData.windows_devices ?? 0;
    const eligible = eligibilityData.eligible_count ?? summary.eligible ?? 0;
    return {
      windows,
      eligible,
      excluded: summary.excluded ?? Math.max(0, windows - eligible),
      // 02/09 — what the Excluded card shows: everything that needs looking
      // at, with devices already on the target taken out. They get their own
      // card because they are not work.
      excludedActionable: summary.excluded_actionable
        ?? Math.max(0, windows - eligible - (summary.already_on_target_excluded || 0)),
      allDevices: eligibilityData.total_devices ?? summary.total ?? 0,
    };
  }, [eligibilityData]);

  // Eligibility export.
  //
  // 02/09 — no longer invents a sprint assignment. It used to map each device
  // to a sprint using the planner's own simplified percentage model, which is
  // not the allocator the deployment wizard runs, so the "Assigned Sprint"
  // column was a guess presented as a plan. Sprint assignment now comes from
  // the deployment preview export, against the real allocation. This export
  // answers the eligibility question and nothing else.
  const handleExportCSV = () => {
    const eligible = eligibilityData?.eligible_devices || [];
    const excluded = [
      ...(eligibilityData?.excluded_high_risk || []),
      ...(eligibilityData?.excluded_medium_risk || []),
      ...(eligibilityData?.excluded_other || []),
      ...(eligibilityData?.excluded_already_on_target || []),
    ];
    if (!eligible.length && !excluded.length) {
      toast.info('Run the eligibility check first');
      return;
    }

    const seen = new Set();
    const row = (d, status) => {
      const key = d.device_id || d.device_name;
      if (seen.has(key)) return null;
      seen.add(key);
      return [
        d.device_name || '',
        d.user_principal_name || d.user_name || '',
        // 09/09 — a rollout plan that silently contains AVD session hosts is
        // the thing these two columns exist to prevent.
        d.virtualisation_label || 'Physical device',
        d.model || '',
        d.current_os_version || '',
        // 09/09 — Intune's band, or "Not assessed". Never a band inferred
        // from compliance; compliance has its own column below.
        d.readiness_assessed === false ? 'Not assessed' : (d.readiness_status === 'Unknown' ? 'Not assessed' : (d.readiness_status || '')),
        (d.readiness_reason || '').replace(/\s+/g, ' '),
        d.compliance_state || (d.is_compliant === true ? 'compliant' : d.is_compliant === false ? 'noncompliant' : ''),
        d.app_risk_category || '',
        d.free_disk_space_gb == null ? '' : Number(d.free_disk_space_gb).toFixed(1),
        d.days_since_checkin == null ? '' : d.days_since_checkin,
        status,
        (d.eligibility_reasons || []).join('; '),
        (d.notes || []).join('; '),
      ];
    };

    const headers = [
      'Device Name', 'User', 'Device Type', 'Model', 'Current OS',
      'Readiness Risk (Intune)', 'Readiness Reason',
      'Intune Compliance', 'App Criticality', 'Free Disk (GB)', 'Days Since Check-in',
      'Status', 'Reason', 'Notes',
    ];
    const rows = [
      ...eligible.map(d => row(d, 'Eligible')),
      ...excluded.map(d => row(d, 'Excluded')),
    ].filter(Boolean);

    const esc = (c) => {
      const s = String(c ?? '').replace(/"/g, '""');
      return /[",\n]/.test(s) ? `"${s}"` : s;
    };
    const csv = '﻿' + [headers, ...rows].map(r => r.map(esc).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `feature-update-eligibility-${selectedVersion.replace(/[^a-zA-Z0-9]/g, '_')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} devices`);
  };

  return (
    <div className="space-y-6" data-testid="feature-update-report">
      {/* 02/09 — the readiness panel is gone. Its "Check data access" and
          "Sync readiness now" buttons were a pre-step you had to remember, and
          forgetting produced an eligibility check against stale or absent
          readiness data that looked perfectly healthy. The sync now runs as
          part of Check Eligibility, and a failure is reported there. */}

      {/* Version Selector */}
      <Card className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border-[rgba(229,239,250,0.1)]">
        <CardContent className="p-6">
          <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
            <Layers className="w-5 h-5 text-purple-400" />
            Feature Update Eligibility Report
          </h3>
          <p className="text-white/70 text-sm mb-4">
            Select a target version to see which devices can be deployed to, and why the rest cannot.
            Sprint sizing and allocation are decided later, in the deployment wizard.
          </p>
          
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm text-white/80 mb-1">Target Version</label>
              <select
                value={selectedVersion}
                onChange={e => setSelectedVersion(e.target.value)}
                className="w-full bg-[rgba(229,239,250,0.1)] border border-[rgba(229,239,250,0.1)] text-white rounded-md px-3 py-2"
                data-testid="report-version-select"
              >
                <option value="" className="bg-slate-900">Select a version...</option>
                {catalog.filter(c => c.isInService !== false).map((item, i) => (
                  <option key={item.id || i} value={item.id} className="bg-slate-900">{item.displayName || item.version}</option>
                ))}
              </select>
            </div>
            {/* 02/09 — Sprints, Test Group and Include Medium Risk removed.
                This screen is an eligibility check that shows the whole
                picture; what to include and how to shape the sprints are
                decisions made in the deployment wizard. */}
            {eligibilityData && (
              <Button onClick={handleExportCSV} variant="outline" size="default"
                className="border-[rgba(229,239,250,0.2)] text-white hover:bg-[rgba(229,239,250,0.1)]"
                data-testid="report-export-csv-btn">
                <Download className="w-4 h-4 mr-2" />
                Export eligibility
              </Button>
            )}
            {/* 03/09 — the planner answers "who could take this update"; the
                next thing anyone does with that answer is build the rollout.
                Carry the chosen version straight into the wizard rather than
                making them close this, reopen the wizard and pick it again.

                Only offered while the dropdown still matches the results on
                screen. Moving the selection after a run makes those results
                stale, and creating a deployment is not the place to inherit a
                stale number — re-run the check and the button returns. */}
            {canWriteOSPatch && eligibilityData && onCreateDeployment && selectedVersion === resultsVersion && (
              <Button
                onClick={() => onCreateDeployment(resultsVersion, excludedDeviceTypes)}
                className="bg-emerald-500 hover:bg-emerald-600 text-white font-semibold"
                title="Open the staged deployment wizard with this target version already selected"
                data-testid="planner-create-deployment-btn">
                <Rocket className="w-4 h-4 mr-2" />
                Create deployment
              </Button>
            )}
            <Button onClick={handleCheckEligibility} disabled={!selectedVersion || loading}
              className="bg-purple-500 hover:bg-purple-600" data-testid="report-check-eligibility-btn">
              {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
              {/* It pulls fresh readiness from Intune first, so it takes as
                  long as a report export. Say so rather than looking hung. */}
              {loading ? 'Refreshing readiness…' : 'Check Eligibility'}
            </Button>
          </div>
          {/* 09/09 — device-type scoping. Sits here, not with the risk filters,
              because it changes the answer this screen gives rather than
              filtering the table afterwards: excluding AVD session hosts is a
              planning decision and the eligible count has to reflect it. */}
          <div className="mt-5 pt-4 border-t border-white/10">
            <label className="block text-sm text-white/90 mb-1">Device types in scope</label>
            <p className="text-xs text-white/50 mb-3">
              Intune reports Cloud PCs, Azure VMs and AVD session hosts all as virtual machines, but they
              rarely want the same treatment. Untick a type to leave it out of the plan.
            </p>
            <DeviceTypeScopeSelector
              catalogue={eligibilityData?.virtualisation_catalogue}
              counts={eligibilityData?.summary?.virtualisation_counts}
              value={excludedDeviceTypes}
              onChange={setExcludedDeviceTypes}
              testIdPrefix="planner-device-type"
              note={
                eligibilityData && !sameStringSet(excludedDeviceTypes, resultsDeviceTypes) ? (
                  <p className="text-xs text-amber-300 mt-2" data-testid="planner-device-type-restale">
                    Press Check Eligibility again to apply this change — the results below were produced
                    with the scoping that was set when they ran.
                  </p>
                ) : null
              }
            />
          </div>

          <p className="text-xs text-white/45 mt-3">
            Checking pulls the latest Windows feature update readiness data from Intune before evaluating,
            so it can take up to a minute.
          </p>
        </CardContent>
      </Card>

      {/* A readiness refresh that failed has to be visible against the results
          it affects, not just in a toast that has already gone. */}
      {syncError && (
        <Card className="bg-amber-500/10 border border-amber-500/40" data-testid="planner-sync-error">
          <CardContent className="p-4">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
              <div className="text-sm text-amber-100">
                <span className="font-medium">Readiness data could not be refreshed.</span>{' '}
                The results below use whatever readiness data ALICE already held, which may be out of date.
                <pre className="mt-2 max-h-32 overflow-auto rounded bg-black/25 p-2 text-[10px] leading-relaxed text-amber-200/80 whitespace-pre-wrap break-words m-0">
{syncError}
                </pre>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Results */}
      {eligibilityData && (
        <>
          {/* Summary cards — 02/09.
              These now carry DEVICE READINESS risk, which is what gates a
              deployment, so the separate readiness section below is gone. The
              Low/Medium/High figures used to be application criticality sitting
              next to a readiness-derived "High Risk (Excluded)" card, which
              read as one measurement and was two.
              Devices already on the target release get their own card and are
              out of Excluded — they are not a problem to look at. */}
          <div className="alice-kpi-row grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <Card className="bg-[rgba(229,239,250,0.1)] border border-[rgba(229,239,250,0.1)]">
              <CardContent className="p-4 text-center">
                <div className="text-3xl font-bold text-white" data-testid="report-total-windows-count">{deviceTotals.windows}</div>
                <div className="text-sm text-white/80">Total Windows Devices</div>
                {deviceTotals.allDevices > deviceTotals.windows && (
                  <div className="text-[10px] text-white/45 mt-1" data-testid="report-non-windows-note">
                    {deviceTotals.allDevices - deviceTotals.windows} non-Windows excluded
                  </div>
                )}
              </CardContent>
            </Card>
            <Card className="bg-green-500/10 border border-green-500/20">
              <CardContent className="p-4 text-center">
                <div className="text-3xl font-bold text-green-400" data-testid="report-eligible-count">{deviceTotals.eligible}</div>
                <div className="text-sm text-white/80">Eligible Devices</div>
              </CardContent>
            </Card>
            <Card className="bg-emerald-500/10 border border-emerald-500/20">
              <CardContent className="p-4 text-center">
                <div className="text-3xl font-bold text-emerald-400" data-testid="report-low-risk-count">{readinessBands.LowRisk}</div>
                <div className="text-sm text-white/80">Low Readiness Risk</div>
              </CardContent>
            </Card>
            <Card className="bg-amber-500/10 border border-amber-500/20">
              <CardContent className="p-4 text-center">
                <div className="text-3xl font-bold text-amber-400" data-testid="report-medium-risk-count">{readinessBands.MediumRisk}</div>
                <div className="text-sm text-white/80">Medium Readiness Risk</div>
              </CardContent>
            </Card>
            <Card className="bg-red-500/10 border border-red-500/20">
              <CardContent className="p-4 text-center">
                <div className="text-3xl font-bold text-red-400" data-testid="report-high-risk-count">{readinessBands.HighRisk}</div>
                <div className="text-sm text-white/80">High Readiness Risk</div>
                {/* 09/09 — the "N not assessed" sub-line used to sit here, and
                    read as though those devices were part of the High Risk
                    figure. Not assessed is not a risk level; it has its own
                    panel below. This card is now Intune's High Risk count and
                    nothing else. */}
                <div className="text-[10px] text-white/45 mt-1 leading-snug">
                  Intune readiness assessment only
                </div>
              </CardContent>
            </Card>
            <Card className="bg-sky-500/10 border border-sky-500/25" data-testid="report-on-target-card">
              <CardContent className="p-4 text-center">
                <div className="text-3xl font-bold text-sky-300" data-testid="report-on-target-count">
                  {eligibilityData.summary?.already_on_target_excluded || 0}
                </div>
                <div className="text-sm text-white/80">Already on Target</div>
                <div className="text-[10px] text-white/45 mt-1 leading-snug">
                  Nothing to do — not counted as excluded
                </div>
              </CardContent>
            </Card>
          </div>

          {/* 09/09 — readiness coverage.
              "N not assessed" used to be a sub-line on the High Readiness Risk
              card, which made it read like a component of high risk. It is the
              opposite: Intune has no opinion on these devices. Given how large
              that number is on a real tenant, it needs its own panel saying
              what it means and why each device is in it — otherwise the only
              honest reading of the planner is "292 devices might be a
              problem", which is not what the data says. */}
          {readinessBands.Unknown > 0 && (
            <Card className="bg-[rgba(229,239,250,0.08)] border border-[rgba(229,239,250,0.12)]"
              data-testid="report-readiness-coverage">
              <CardContent className="p-4">
                <div className="flex items-baseline gap-3 flex-wrap mb-2">
                  <span className="text-3xl font-bold text-white/85" data-testid="report-unassessed-count">
                    {readinessBands.Unknown}
                  </span>
                  <span className="text-sm text-white/80">
                    Windows devices have no readiness assessment from Intune
                  </span>
                  <span className="text-xs text-white/50">
                    ({readinessBands.assessed} of {deviceTotals.windows} assessed)
                  </span>
                </div>
                <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-white/70">
                  <span>
                    Already on {eligibilityData.target_version || 'the target'}:{' '}
                    <span className="text-white">{readinessBands.unknownOnTarget}</span>
                  </span>
                  <span>
                    No valid upgrade path (e.g. Windows Server):{' '}
                    <span className="text-white">{readinessBands.unknownNotUpgradable}</span>
                  </span>
                  <span>
                    No readiness data reported:{' '}
                    <span className="text-white">{readinessBands.unknownNoData}</span>
                  </span>
                </div>
                <p className="text-[10px] text-white/40 mt-2 leading-relaxed">
                  Microsoft only assesses a device for the release it could move to, so devices already on the
                  target and devices with no valid upgrade path never appear in the readiness report. The
                  remainder have not sent Windows Update readiness diagnostic data — check that the device is
                  Intune-managed, has diagnostic data enabled and has checked in recently. Not assessed is
                  <span className="text-white/70"> not</span> a risk level: these devices are neither eligible
                  nor excluded on readiness grounds, and Intune compliance is never used as a substitute for it.
                </p>
              </CardContent>
            </Card>
          )}

          {/* Excluded, and why. Split out from the cards above because this is
              the number that generates work. */}
          <Card className="bg-[rgba(229,239,250,0.08)] border border-[rgba(229,239,250,0.12)]" data-testid="report-excluded-breakdown">
            <CardContent className="p-4">
              <div className="flex items-baseline gap-3 flex-wrap mb-2">
                <span className="text-3xl font-bold text-white/85" data-testid="report-excluded-count">
                  {deviceTotals.excludedActionable}
                </span>
                <span className="text-sm text-white/80">
                  devices excluded and needing attention
                </span>
              </div>
              <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-white/70">
                <span>High readiness risk: <span className="text-white">{eligibilityData.summary?.high_risk_excluded || 0}</span></span>
                <span>Already in a deployment: <span className="text-white">{eligibilityData.summary?.already_in_deployment_excluded || 0}</span></span>
                <span>Disk, upgrade path, phase or firmware: <span className="text-white">{eligibilityData.summary?.other_excluded || 0}</span></span>
              </div>
              <p className="text-[10px] text-white/40 mt-2">
                Devices already on {' '}
                {eligibilityData.target_version || 'the target release'} are counted on their own card, not
                here. &quot;Already in a deployment&quot; overlaps the other buckets, so these can sum to more
                than the figure above.
              </p>
            </CardContent>
          </Card>

          {/* Why is everything excluded? — 27/08: devices already inside a live
              ALICE deployment are intentionally ineligible for a new one. */}
          {(eligibilityData.summary?.already_in_deployment_excluded || 0) > 0 && (
            <Card className="bg-blue-500/10 border border-blue-500/30" data-testid="planner-already-in-deployment-note">
              <CardContent className="p-4 text-sm text-blue-100">
                <span className="font-medium text-blue-300">
                  {eligibilityData.summary.already_in_deployment_excluded} device
                  {eligibilityData.summary.already_in_deployment_excluded === 1 ? '' : 's'} excluded because they are
                  already in a live deployment
                </span>
                {(eligibilityData.existing_deployment_names || []).length > 0 && (
                  <span> ({eligibilityData.existing_deployment_names.join(', ')})</span>
                )}
                . A device can only be targeted by one feature update profile at a time, so ALICE keeps devices in an
                active sprint deployment out of new plans. They become eligible again when that deployment completes or
                is deleted.
              </CardContent>
            </Card>
          )}

          {/* Business application risk — collapsed by default.
              02/09 — this was headed "Risk Analysis", which read as device
              readiness. It is not: it is application business criticality,
              derived from detected apps and app governance. Two different
              measurements, now two separate sections with honest names. */}
          <Card className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border-[rgba(229,239,250,0.1)]">
            <CardHeader className="pb-2">
              <button
                type="button"
                onClick={() => setShowAppRisk(v => !v)}
                className="w-full flex items-center justify-between gap-3 text-left"
                data-testid="planner-app-risk-toggle"
              >
                <CardTitle className="text-white flex items-center gap-2">
                  {showAppRisk ? <ChevronDown className="w-4 h-4 text-white/60" /> : <ChevronRight className="w-4 h-4 text-white/60" />}
                  <Shield className="w-5 h-5 text-amber-400" />
                  Business Application Risk Analysis
                </CardTitle>
                <span className="text-xs text-white/50 whitespace-nowrap">
                  {riskBreakdown.low} low · {riskBreakdown.medium} medium · {riskBreakdown.high} high
                </span>
              </button>
            </CardHeader>
            {showAppRisk && (
              <CardContent>
                <p className="text-xs text-white/50 mb-3">
                  Application blast radius, from the business criticality set against detected applications.
                  Used by an App Risk Deployment to order sprints. A Staged Deployment ignores it.
                </p>
                <div className="space-y-3">
                  {[
                    { label: 'Low app criticality', count: riskBreakdown.low, color: '#22c55e' },
                    { label: 'Medium app criticality', count: riskBreakdown.medium, color: '#f59e0b' },
                    { label: 'High app criticality', count: riskBreakdown.high, color: '#ef4444' },
                  ].map((item, i) => {
                    const total = riskBreakdown.low + riskBreakdown.medium + riskBreakdown.high;
                    const pct = total > 0 ? (item.count / total * 100) : 0;
                    return (
                      <div key={i}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-white/80">{item.label}</span>
                          <span className="text-white">{item.count} ({pct.toFixed(0)}%)</span>
                        </div>
                        <div className="h-4 bg-slate-700/50 rounded overflow-hidden">
                          <div className="h-full rounded" style={{ width: `${Math.max(pct, 1)}%`, backgroundColor: item.color }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
                <p className="text-[11px] text-white/40 mt-3">
                  Percentages are of the {riskBreakdown.low + riskBreakdown.medium + riskBreakdown.high} eligible
                  devices, not the whole estate.
                </p>
              </CardContent>
            )}
          </Card>


          {/* -------- Per-device readiness reasons -------- */}
          {/*
            Adds the missing "why is this device flagged?" context the
            planner used to be missing. Combines:
              · readiness_status  (LowRisk / MediumRisk / HighRisk)
              · app_risk_category (low / medium / high — from
                detected_apps × app_governance)
              · eligibility_reasons[] (free-text reasons populated by
                check_device_eligibility — disk, upgrade path,
                already-in-deployment, app risk, WUfB blocker)
            Lets admins triage which devices need attention before
            kicking off a deployment. Renders eligible + excluded
            devices side-by-side with a risk filter so the table is
            actually usable on large tenants.
          */}
          {/* 09/09 — ONE switch drives both the collapsible Business
              Application Risk Analysis section above and the app-criticality
              columns in this table. They are the same question ("am I planning
              an App Risk deployment?") and having two controls for it is how
              the planner ended up showing app risk to Staged users in the
              first place. */}
          <FeatureEligibilityReadinessTable
            eligibilityData={eligibilityData}
            showAppRisk={showAppRisk}
            onToggleAppRisk={setShowAppRisk}
          />
        </>
      )}
    </div>
  );
};

// =============================================================================
// FeatureEligibilityReadinessTable
// -----------------------------------------------------------------------------
// Per-device "why is this risk level?" table for the eligibility planner.
// Pulls eligibility_reasons / risk_reasons / readiness_reason populated by
// the backend (POST /feature-updates/deployments/check-eligibility) and
// surfaces them alongside the existing risk colour chip.
// =============================================================================
// =============================================================================
// LowDiskRemediationModal — 02/09
// -----------------------------------------------------------------------------
// Low disk space is the most common eligibility blocker and the most fixable
// one, but until now the planner could only tell you about it. This turns that
// list into an action: pick the devices, push the catalog's
// "Free disk space for updates" remediation into Intune, and target them.
//
// The script frees space by removing Windows.old, clearing %TEMP% and the
// Delivery Optimisation cache. It is installed once per tenant and reused — the
// backend returns the existing script rather than creating a duplicate.
// =============================================================================
const LOW_DISK_CATALOG_KEY = 'free_disk_space';

const LowDiskRemediationModal = ({ devices, thresholdGb = FU_MIN_DISK_GB, onClose }) => {
  // Only devices with an Entra device id can be put in a group, so the ones
  // that cannot be targeted are shown as such rather than silently dropped.
  const targetable = useMemo(
    () => (devices || []).filter(d => d.azure_ad_device_id),
    [devices],
  );
  const untargetable = useMemo(
    () => (devices || []).filter(d => !d.azure_ad_device_id),
    [devices],
  );

  const [selected, setSelected] = useState(() => targetable.map(d => d.device_id));
  const [running, setRunning] = useState(false);
  const [stage, setStage] = useState(null);
  const [typedCount, setTypedCount] = useState('');

  const chosen = targetable.filter(d => selected.includes(d.device_id));
  const allSelected = chosen.length === targetable.length && targetable.length > 0;

  const handleRun = async () => {
    if (!chosen.length || !largeRunConfirmed(chosen.length, typedCount)) return;
    // Large runs are confirmed by typing the count (LargeRunConfirm); a small
    // one still gets a plain confirm, as before.
    if (chosen.length <= LARGE_REMEDIATION_RUN && !window.confirm(
      `Push the disk clean-up remediation to ${chosen.length} device`
      + `${chosen.length === 1 ? '' : 's'}?\n\n`
      + 'The script runs at each device\'s next check-in. '
      + 'It removes Windows.old, clears %TEMP% and the Delivery Optimisation cache.'
    )) return;

    setRunning(true);
    try {
      // Install once per tenant; the backend hands back the existing script if
      // it is already there rather than creating a second copy.
      setStage('Preparing the remediation script…');
      const created = await osPatchApi.createRemediationFromCatalog(LOW_DISK_CATALOG_KEY);
      const script = created.data?.script;
      if (!script?.id) throw new Error('Could not resolve the remediation script');

      setStage(`Targeting ${chosen.length} device(s)…`);
      const res = await osPatchApi.runRemediation(script.id, {
        devices: chosen.map(d => ({
          device_id: d.device_id,
          azure_ad_device_id: d.azure_ad_device_id,
          device_name: d.device_name,
        })),
        notes: `Low disk space (< ${thresholdGb} GB) — raised from the feature update eligibility planner`,
        source: 'planner',
      });
      const run = res.data?.run || {};
      const added = run.added_count ?? chosen.length;
      const total = run.target_count ?? chosen.length;
      if (added < total) {
        toast.warning(`Remediation assigned to ${added} of ${total} devices — see Remediation for the rest`);
      } else {
        toast.success(`Disk clean-up assigned to ${added} device(s) — runs at next check-in`);
      }
      onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === 'string' ? detail : (detail?.message || 'Failed to assign the remediation'),
      );
    } finally {
      setRunning(false);
      setStage(null);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="low-disk-remediation-modal"
    >
      <div className="alice-popup bg-slate-900 border border-white/10 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="flex items-start justify-between gap-4 p-5 border-b border-white/10">
          <div>
            <h2 className="text-lg font-semibold text-white">Remediate - Disk Space</h2>
            <p className="text-xs text-white/60 mt-1">
              Removes Windows.old, clears %TEMP% and the Delivery Optimisation cache. Runs at each
              device&apos;s next Intune check-in.
            </p>
            {/* The catalog entry keeps its own name because that string is the
                exact key `create_from_catalog` matches on to adopt the script
                already in Intune rather than create a second one. Naming it
                here stops the two reading as different things. */}
            <p className="text-[11px] text-white/40 mt-1">
              Deploys the Intune remediation <span className="text-white/60">&quot;Free disk space for updates&quot;</span>.
            </p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} disabled={running} data-testid="low-disk-close">
            <X className="w-4 h-4 text-white/80" />
          </Button>
        </div>

        <div className="p-5 space-y-4 overflow-auto">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <span className="text-sm text-white/80" data-testid="low-disk-count">
              {targetable.length} device{targetable.length === 1 ? '' : 's'} below {thresholdGb} GB
              {chosen.length !== targetable.length && ` · ${chosen.length} selected`}
            </span>
            <Button
              size="sm" variant="outline"
              className="border-white/20 text-white"
              disabled={running}
              onClick={() => setSelected(allSelected ? [] : targetable.map(d => d.device_id))}
              data-testid="low-disk-select-all"
            >
              {allSelected ? 'Clear all' : 'Select all'}
            </Button>
          </div>

          <div className="rounded-lg border border-white/10 overflow-hidden">
            <div className="max-h-[40vh] overflow-auto">
              <table className="w-full text-sm">
                <thead className="bg-white/5 text-white/60 text-[11px] uppercase tracking-wider sticky top-0">
                  <tr>
                    <th className="w-9 px-2 py-2" />
                    <th className="text-left px-3 py-2 font-medium">Device</th>
                    <th className="text-left px-3 py-2 font-medium">User</th>
                    <th className="text-right px-3 py-2 font-medium">Free disk</th>
                  </tr>
                </thead>
                <tbody className="text-white/90">
                  {targetable.map((d, idx) => (
                    <tr key={d.device_id || idx} className="border-t border-white/5 hover:bg-white/[0.03]">
                      <td className="px-2 py-2 text-center">
                        <input
                          type="checkbox"
                          disabled={running}
                          checked={selected.includes(d.device_id)}
                          onChange={(e) => setSelected(prev => e.target.checked
                            ? [...new Set([...prev, d.device_id])]
                            : prev.filter(x => x !== d.device_id))}
                          data-testid={`low-disk-select-${idx}`}
                        />
                      </td>
                      <td className="px-3 py-2 font-medium text-white">{d.device_name || 'Unknown device'}</td>
                      <td className="px-3 py-2 text-white/70 max-w-[200px] truncate" title={d.user_principal_name || ''}>
                        {d.user_principal_name || '—'}
                      </td>
                      <td className="px-3 py-2 text-right text-xs text-red-300 font-semibold">
                        {d.free_disk_space_gb == null ? '—' : `${Number(d.free_disk_space_gb).toFixed(1)} GB`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {!targetable.length && (
              <div className="p-4 text-center text-white/50 text-sm" data-testid="low-disk-empty">
                No devices below the disk threshold can be targeted.
              </div>
            )}
          </div>

          {/* Said out loud rather than dropped: a device with no Entra id
              cannot be put in a group, so it cannot be remediated this way. */}
          {untargetable.length > 0 && (
            <p className="text-xs text-amber-300/90" data-testid="low-disk-untargetable">
              {untargetable.length} device{untargetable.length === 1 ? '' : 's'} below the threshold cannot be
              targeted — no Entra device ID, so {untargetable.length === 1 ? 'it' : 'they'} cannot be added to
              a group. A re-sync from Intune usually resolves this.
            </p>
          )}

          {chosen.length > LARGE_REMEDIATION_RUN && (
            <LargeRunConfirm count={chosen.length} value={typedCount} onChange={setTypedCount} testId="low-disk-large-confirm" />
          )}
        </div>

        <div className="flex items-center justify-between gap-3 p-5 border-t border-white/10">
          <span className="text-xs text-white/60">
            {stage || "Devices go into the script's own Entra group, reused for every run."}
          </span>
          <div className="flex gap-2">
            <Button variant="outline" className="border-white/20 text-white" onClick={onClose} disabled={running}>
              Cancel
            </Button>
            <Button
              className="bg-cyan-500 hover:bg-cyan-600 text-white"
              disabled={!chosen.length || running || !largeRunConfirmed(chosen.length, typedCount)}
              onClick={handleRun}
              data-testid="low-disk-run"
            >
              {running
                ? <><RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" />Assigning…</>
                : `Remediate ${chosen.length || ''}`.trim()}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// 09/09 — `showAppRisk` gates the application-criticality column, its filter
// and its reason column.
//
// App business criticality is used by ONE thing: an App Risk deployment. A
// Staged deployment ignores it end to end (see `_resolve_fu_deployment_mode`),
// so showing it as a column next to Readiness Risk invited the reading that it
// gates the rollout — and two of the bugs recorded in this PRD were exactly
// that confusion. Off by default; the planner's own "Create deployment" button
// opens the Staged wizard, so Staged is the context this screen is in unless
// the admin says otherwise.
const FeatureEligibilityReadinessTable = ({ eligibilityData, showAppRisk = false, onToggleAppRisk = null }) => {
  const { canWriteOSPatch, canManageOSPatches } = usePermissions();
  const [filter, setFilter] = useState('all'); // app risk: all | high | medium | low | unknown
  const [readinessFilter, setReadinessFilter] = useState('all'); // readiness risk: all | high | medium | low | unknown
  const [statusFilter, setStatusFilter] = useState('all'); // all | eligible | excluded
  // 09/09 — all | physical | cloud_pc | azure_vm | other_vm | virtual (any VM)
  const [deviceTypeFilter, setDeviceTypeFilter] = useState('all');
  const [diskFilter, setDiskFilter] = useState('all'); // all | low (< FU_MIN_DISK_GB)
  const [showDiskRemediation, setShowDiskRemediation] = useState(false);
  const [search, setSearch] = useState('');
  const [showCount, setShowCount] = useState(100);

  const allDevices = useMemo(() => {
    if (!eligibilityData) return [];
    const eligible = (eligibilityData.eligible_devices || []).map(d => ({ ...d, bucket: 'eligible' }));
    const high = (eligibilityData.excluded_high_risk || []).map(d => ({ ...d, bucket: 'excluded' }));
    const medium = (eligibilityData.excluded_medium_risk || []).map(d => ({ ...d, bucket: 'excluded' }));
    const other = (eligibilityData.excluded_other || []).map(d => ({ ...d, bucket: 'excluded' }));
    const already = (eligibilityData.excluded_already_in_deployment || []).map(d => ({ ...d, bucket: 'excluded' }));
    // De-dupe: a device can appear in `excluded_already_in_deployment` as well
    // as one of the risk buckets.
    const merged = [...eligible, ...high, ...medium, ...other, ...already];
    const seen = new Set();
    return merged.filter(d => {
      const key = d.device_id || d.intune_id || d.device_name;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [eligibilityData]);

  // `scott/feature-updates-3` — column heading was renamed to "App Risk",
  // so the chip in this column now reflects the device's app criticality
  // only (low / medium / high). The Readiness column carries the WUfB
  // readiness risk separately.
  const riskOf = (d) => {
    let app = (d.app_risk_category || '').toLowerCase();
    if (app === 'green') return 'low';
    if (app === 'amber') return 'medium';
    if (app === 'red') return 'high';
    if (app === 'low' || app === 'medium' || app === 'high') return app;
    return 'unknown';
  };

  // 27/08 (Bug fix 2) — the Reason column previously showed only the WUfB
  // readiness reason + a disk flag, so a device excluded for any OTHER reason
  // (invalid upgrade path, red FU sprint, firmware gate, already deployed)
  // appeared as "excluded" with no explanation at all. The backend already
  // returns the full list in `eligibility_reasons`; show it.
  // 02/09 — the reasons are split into two columns, because they answer two
  // different questions and mixing them made both harder to read. Readiness
  // (and everything else that gates eligibility) sits in one; application
  // criticality — which never gates a Staged deployment — sits in the other.
  const APP_REASON_RE = /app risk|application|app criticality/i;

  const reasonOf = (d) => {
    const parts = [];
    (d.eligibility_reasons || []).forEach((r) => {
      const s = String(r || '');
      if (s && !APP_REASON_RE.test(s) && !parts.includes(s)) parts.push(s);
    });
    if (d.readiness_reason && !parts.some((p) => p.includes(String(d.readiness_reason)))) {
      parts.push(String(d.readiness_reason));
    }
    const disk = d.free_disk_space_gb;
    if (disk != null && Number(disk) < FU_MIN_DISK_GB && !parts.some((p) => p.toLowerCase().includes('disk'))) {
      parts.push(`Less than ${FU_MIN_DISK_GB} GB free disk (${Number(disk).toFixed(1)} GB)`);
    }
    if (d.already_in_deployment && !parts.some((p) => p.startsWith('Already in deployment'))) {
      parts.push(`Already in deployment: ${d.existing_deployment_name || 'existing deployment'}`);
    }
    if (!parts.length && d.bucket === 'excluded') {
      parts.push('Excluded — no reason recorded by the eligibility check');
    }
    return parts;
  };

  // Why this device carries the app criticality it does — the flagged
  // applications behind the band, not a gate on eligibility.
  const appReasonOf = (d) => {
    const parts = [];
    (d.eligibility_reasons || []).forEach((r) => {
      const s = String(r || '');
      if (s && APP_REASON_RE.test(s) && !parts.includes(s)) parts.push(s);
    });
    (d.risk_reasons || []).forEach((r) => {
      const label = r?.app ? `${r.app}${r.risk ? ` (${r.risk})` : ''}` : null;
      if (label && !parts.includes(label)) parts.push(label);
    });
    return parts;
  };

  // 09/09 — device type. Physical is the silent majority, so it renders as
  // nothing; a VM row is what an admin is scanning for.
  const DEVICE_TYPE_CHIP = {
    cloud_pc: { label: 'Cloud PC', style: 'bg-sky-500/15 text-sky-300 border-sky-400/40' },
    azure_vm: { label: 'Azure VM / AVD', style: 'bg-violet-500/15 text-violet-300 border-violet-400/40' },
    other_vm: { label: 'Virtual machine', style: 'bg-white/10 text-white/60 border-white/20' },
  };
  const deviceTypeOf = (d) => String(d.virtualisation || 'physical').toLowerCase();

  const readinessOf = (d) => {
    const s = String(d.readiness_status || '').toLowerCase();
    if (s.includes('high')) return 'high';
    if (s.includes('medium')) return 'medium';
    if (s.includes('low')) return 'low';
    return 'unknown';
  };

  // 09/09 — things that are true of the device but did not block it: Intune
  // compliance state, and "Intune has not assessed this device". Scott's ask
  // was to keep non-compliance visible in the Reason column while getting it
  // out of the Readiness Risk band, so it renders here, in its own muted
  // style, next to (not mixed into) the reasons that actually gate a rollout.
  const notesOf = (d) => {
    const parts = [];
    (d.notes || []).forEach((n) => {
      const s = String(n || '');
      if (s && !parts.includes(s)) parts.push(s);
    });
    // Older API responses have no `notes`; derive the compliance line so the
    // column does not silently lose it against a backend that hasn't shipped.
    if (!(d.notes || []).length && d.is_compliant === false) {
      parts.push('Not compliant in Intune');
    }
    return parts;
  };

  // Every device under the disk threshold, regardless of the table's current
  // filters — the remediation targets the problem, not the current view.
  const lowDiskDevices = useMemo(
    () => allDevices.filter(d => d.free_disk_space_gb != null && Number(d.free_disk_space_gb) < FU_MIN_DISK_GB),
    [allDevices],
  );

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return allDevices.filter(d => {
      const r = riskOf(d);
      if (filter !== 'all' && r !== filter) return false;
      if (readinessFilter !== 'all' && readinessOf(d) !== readinessFilter) return false;
      if (deviceTypeFilter !== 'all') {
        const kind = deviceTypeOf(d);
        if (deviceTypeFilter === 'virtual' ? kind === 'physical' : kind !== deviceTypeFilter) return false;
      }
      if (statusFilter === 'eligible' && d.bucket !== 'eligible') return false;
      if (statusFilter === 'excluded' && d.bucket !== 'excluded') return false;
      if (diskFilter === 'low') {
        const disk = d.free_disk_space_gb;
        if (disk == null || Number(disk) >= FU_MIN_DISK_GB) return false;
      }
      if (!term) return true;
      const reasonText = `${reasonOf(d).join(' ')} ${notesOf(d).join(' ')}`;
      const hay = `${d.device_name || ''} ${d.user_principal_name || ''} ${reasonText}`.toLowerCase();
      return hay.includes(term);
    });
  }, [allDevices, filter, readinessFilter, deviceTypeFilter, statusFilter, diskFilter, search]);

  const handleExportCsv = () => {
    if (!filtered.length) {
      toast.error('Nothing to export');
      return;
    }
    // 09/09 — "Readiness Risk" now carries Intune's band or "Not assessed",
    // and the compliance signal that used to be smuggled into it gets its own
    // columns. A reader of the export can no longer mistake one for the other.
    // 09/09 — the export follows the table: app criticality only appears when
    // the admin has asked to see it, so an exported Staged plan does not carry
    // a column that has no bearing on it.
    const headers = [
      'Device', 'User', 'Device Type', 'Model',
      ...(showAppRisk ? ['App Criticality'] : []),
      'Readiness Risk (Intune)', 'Readiness Assessed',
      'Intune Compliance', 'Free Disk (GB)', 'Exclusion Reason', 'Notes',
      ...(showAppRisk ? ['App Criticality Reason'] : []),
      'Status',
    ];
    const rows = filtered.map(d => {
      const r = riskOf(d);
      const reasons = reasonOf(d);
      const disk = d.free_disk_space_gb == null ? '' : Number(d.free_disk_space_gb).toFixed(1);
      const band = READINESS_CHIP[readinessOf(d)] || READINESS_CHIP.unknown;
      return [
        d.device_name || '',
        d.user_principal_name || '',
        d.virtualisation_label || (DEVICE_TYPE_CHIP[deviceTypeOf(d)]?.label ?? 'Physical device'),
        d.model || '',
        ...(showAppRisk ? [r] : []),
        band.label,
        d.readiness_assessed ? 'Yes' : 'No',
        d.compliance_state || (d.is_compliant === true ? 'compliant' : d.is_compliant === false ? 'noncompliant' : ''),
        disk,
        reasons.join(' | '),
        notesOf(d).join(' | '),
        ...(showAppRisk ? [appReasonOf(d).join(' | ')] : []),
        d.bucket === 'excluded' ? 'Excluded' : 'Eligible',
      ];
    });
    const csv = [headers, ...rows]
      .map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `device-readiness-detail-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filtered.length} device${filtered.length === 1 ? '' : 's'}`);
  };

  if (!eligibilityData) return null;

  const riskChipStyle = (r) => {
    if (r === 'high') return 'bg-red-500/15 text-red-300 border-red-400/40';
    if (r === 'medium') return 'bg-amber-500/15 text-amber-200 border-amber-400/40';
    if (r === 'unknown') return 'bg-white/5 text-white/50 border-white/15';
    return 'bg-emerald-500/15 text-emerald-300 border-emerald-400/40';
  };

  // 09/09 — the Readiness Risk column used to print `readiness_status` raw,
  // and that value could be a band ALICE had invented from Intune compliance.
  // The backend no longer invents one, so this column is now Microsoft's
  // assessment or an explicit "Not assessed" — never a guess wearing a band's
  // colour.
  const READINESS_CHIP = {
    high: { label: 'High', style: 'bg-red-500/15 text-red-300 border-red-400/40' },
    medium: { label: 'Medium', style: 'bg-amber-500/15 text-amber-200 border-amber-400/40' },
    low: { label: 'Low', style: 'bg-emerald-500/15 text-emerald-300 border-emerald-400/40' },
    unknown: { label: 'Not assessed', style: 'bg-white/5 text-white/50 border-white/15' },
  };

  return (
    <Card
      className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border-[rgba(229,239,250,0.1)]"
      data-testid="eligibility-readiness-table-card"
    >
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="text-white flex items-center gap-2">
              <Shield className="w-5 h-5 text-purple-400" />
              Device Readiness Detail
            </CardTitle>
            <p className="text-sm text-white/70 mt-1">
              Every device with the specific readiness reason and disk-space flag — filter by app risk, readiness risk,
              free disk, status or search reason text.
            </p>
            <p className="text-xs text-white/50 mt-1.5" data-testid="eligibility-criteria-note">
              A device is excluded when any of these apply: invalid upgrade path to the target version; readiness High
              Risk (or Medium Risk when not included); free disk below the required minimum; already in another feature
              update deployment; Deployment Sprint set to Exclude; or the model's latest BIOS is older than the
              firmware gate. Having no assigned user is <span className="text-white/70">never</span> a reason for
              exclusion.
              {' '}Readiness Risk is Intune's assessment only — a device Intune has not assessed shows
              <span className="text-white/70"> Not assessed</span>, and Intune compliance is
              <span className="text-white/70"> never</span> used to infer a readiness band. Non-compliance is
              still reported, as a note in the Reason column.
              {!showAppRisk && (
                <>
                  {' '}Application business criticality is hidden because it gates an
                  <span className="text-white/70"> App Risk</span> deployment only — a Staged deployment
                  ignores it end to end.
                </>
              )}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {/* App Risk + Readiness Risk are separate dimensions, so each gets
                its own dropdown (27/08 — the old chips filtered app risk only
                and read as if they covered readiness too). */}
            {/* 09/09 — the opt-in. The planner's own Create deployment button
                opens the Staged wizard, so Staged is the default context and
                app criticality is off unless the admin is planning an App Risk
                rollout and says so. */}
            {onToggleAppRisk && (
              <label
                className="flex items-center gap-1.5 text-xs text-white/60 cursor-pointer"
                data-testid="eligibility-show-app-risk-toggle"
              >
                <input
                  type="checkbox"
                  checked={showAppRisk}
                  onChange={(e) => onToggleAppRisk(e.target.checked)}
                  className="w-3.5 h-3.5 rounded border-white/20 bg-white/10 text-purple-500"
                />
                Show application risk
              </label>
            )}
            {showAppRisk && (
              <label className="flex items-center gap-1.5 text-xs text-white/70">
                App risk
                <select
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  data-testid="eligibility-readiness-app-risk-filter"
                  className="bg-slate-900/80 border border-white/15 rounded-md px-2 py-1 text-xs text-white focus:outline-none focus:border-purple-400/60"
                >
                  <option value="all">All</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                  <option value="unknown">Unknown</option>
                </select>
              </label>
            )}
            <label className="flex items-center gap-1.5 text-xs text-white/70">
              Readiness risk
              <select
                value={readinessFilter}
                onChange={(e) => setReadinessFilter(e.target.value)}
                data-testid="eligibility-readiness-risk-filter"
                className="bg-slate-900/80 border border-white/15 rounded-md px-2 py-1 text-xs text-white focus:outline-none focus:border-purple-400/60"
              >
                <option value="all">All</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
                {/* 09/09 — was "Unknown", which invited the reading that ALICE
                    had lost the value. Intune simply has not assessed it. */}
                <option value="unknown">Not assessed</option>
              </select>
            </label>
            {/* 09/09 — "show me just the AVD hosts" is the question this table
                could not answer before device types existed. */}
            <label className="flex items-center gap-1.5 text-xs text-white/70">
              Device type
              <select
                value={deviceTypeFilter}
                onChange={(e) => setDeviceTypeFilter(e.target.value)}
                data-testid="eligibility-device-type-filter"
                className="bg-slate-900/80 border border-white/15 rounded-md px-2 py-1 text-xs text-white focus:outline-none focus:border-purple-400/60"
              >
                <option value="all">All</option>
                <option value="physical">Physical</option>
                <option value="virtual">Any virtual</option>
                <option value="cloud_pc">Cloud PC</option>
                <option value="azure_vm">Azure VM / AVD</option>
                <option value="other_vm">Other VM</option>
              </select>
            </label>
            {/* Free Disk filter */}
            <button
              type="button"
              onClick={() => setDiskFilter(diskFilter === 'low' ? 'all' : 'low')}
              data-testid="eligibility-readiness-disk-filter"
              className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                diskFilter === 'low'
                  ? 'bg-red-500/25 text-red-200 border-red-400/50'
                  : 'bg-white/5 text-white/70 border-white/15 hover:bg-white/10'
              }`}
            >
              {diskFilter === 'low' ? `Disk < ${FU_MIN_DISK_GB} GB` : 'Free disk'}
            </button>
            {/* Status filter */}
            {[
              { key: 'all', label: 'All status' },
              { key: 'eligible', label: 'Eligible' },
              { key: 'excluded', label: 'Excluded' },
            ].map(chip => (
              <button
                key={`status-${chip.key}`}
                type="button"
                onClick={() => setStatusFilter(chip.key)}
                data-testid={`eligibility-readiness-status-filter-${chip.key}`}
                className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                  statusFilter === chip.key
                    ? 'bg-cyan-500/25 text-cyan-200 border-cyan-400/50'
                    : 'bg-white/5 text-white/70 border-white/15 hover:bg-white/10'
                }`}
              >
                {chip.label}
              </button>
            ))}
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-white/40" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search device, user, reason…"
                className="pl-8 h-8 text-sm bg-white/5 border-white/10 text-white placeholder:text-white/40 w-56"
                data-testid="eligibility-readiness-search"
              />
            </div>
            {/* 02/09 — low disk is the most common blocker and the most
                fixable one. The planner could only report it; now it can act
                on it. Only offered when there is something to fix. */}
            {canWriteOSPatch && canManageOSPatches && lowDiskDevices.length > 0 && (
              <Button
                size="sm"
                onClick={() => setShowDiskRemediation(true)}
                className="bg-cyan-500/20 border border-cyan-400/40 text-cyan-100 hover:bg-cyan-500/30 h-8"
                data-testid="eligibility-remediate-disk-btn"
              >
                <Wrench className="w-3.5 h-3.5 mr-1.5" />
                Remediate - Disk Space ({lowDiskDevices.length})
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              onClick={handleExportCsv}
              className="border-[rgba(229,239,250,0.2)] text-white hover:bg-[rgba(229,239,250,0.1)] h-8"
              data-testid="eligibility-readiness-export-csv"
            >
              <Download className="w-3.5 h-3.5 mr-1.5" />
              Export
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {filtered.length === 0 ? (
          <p className="text-white/60 text-sm py-6 text-center" data-testid="eligibility-readiness-empty">
            No devices match this filter.
          </p>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-white/10">
            <table className="w-full text-sm" data-testid="eligibility-readiness-table">
              <thead className="bg-white/5 text-white/70 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">Device</th>
                  <th className="text-left px-3 py-2 font-medium">User</th>
                  {showAppRisk && <th className="text-left px-3 py-2 font-medium">App Criticality</th>}
                  <th className="text-left px-3 py-2 font-medium">Readiness Risk</th>
                  <th className="text-right px-3 py-2 font-medium">Free disk</th>
                  <th className="text-left px-3 py-2 font-medium">Readiness Reason</th>
                  {showAppRisk && <th className="text-left px-3 py-2 font-medium">App Criticality Reason</th>}
                  <th className="text-left px-3 py-2 font-medium">Status</th>
                </tr>
              </thead>
              <tbody className="text-white/90">
                {filtered.slice(0, showCount).map((d, idx) => {
                  const risk = riskOf(d);
                  const reasons = reasonOf(d);
                  const notes = notesOf(d);
                  const appReasons = appReasonOf(d);
                  return (
                    <tr
                      key={`${d.device_id || d.intune_id || idx}-${idx}`}
                      className="border-t border-white/5 hover:bg-white/[0.03]"
                      data-testid={`eligibility-readiness-row-${idx}`}
                    >
                      <td className="px-3 py-2 font-medium text-white max-w-[180px]">
                        <div className="truncate" title={d.device_name}>{d.device_name || '—'}</div>
                        {/* Physical devices get no chip — they are the norm,
                            and a chip on every row would carry no signal. */}
                        {DEVICE_TYPE_CHIP[deviceTypeOf(d)] && (
                          <span
                            className={`inline-block mt-1 px-1.5 py-0.5 rounded text-[10px] font-medium border ${DEVICE_TYPE_CHIP[deviceTypeOf(d)].style}`}
                            title={d.model || d.virtualisation_label || ''}
                            data-testid={`eligibility-device-type-${idx}`}
                          >
                            {DEVICE_TYPE_CHIP[deviceTypeOf(d)].label}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-white/70 truncate max-w-[180px]" title={d.user_principal_name}>
                        {d.user_principal_name || '—'}
                      </td>
                      {showAppRisk && (
                        <td className="px-3 py-2">
                          <span
                            className={`px-2 py-0.5 rounded-full text-[11px] font-semibold uppercase tracking-wider border ${riskChipStyle(risk)}`}
                            data-testid={`eligibility-readiness-risk-${idx}`}
                          >
                            {risk}
                          </span>
                        </td>
                      )}
                      <td className="px-3 py-2">
                        {(() => {
                          const band = READINESS_CHIP[readinessOf(d)] || READINESS_CHIP.unknown;
                          return (
                            <span
                              className={`px-2 py-0.5 rounded-full text-[11px] font-semibold uppercase tracking-wider border ${band.style}`}
                              data-testid={`eligibility-readiness-band-${idx}`}
                              title={
                                band.label === 'Not assessed'
                                  ? 'Intune has not assessed this device for the target release'
                                  : `Intune readiness assessment: ${band.label} Risk`
                              }
                            >
                              {band.label}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="px-3 py-2 text-right text-xs">
                        {d.free_disk_space_gb == null ? (
                          <span className="text-white/40">—</span>
                        ) : (
                          <span className={d.free_disk_space_gb < FU_MIN_DISK_GB ? 'text-red-300 font-semibold' : 'text-white/80'}>
                            {Number(d.free_disk_space_gb).toFixed(1)} GB
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs text-white/80">
                        {reasons.length === 0 && notes.length === 0 ? (
                          <span className="text-white/40">No blockers — eligible</span>
                        ) : (
                          <div className="space-y-1" data-testid={`eligibility-readiness-reason-${idx}`}>
                            {reasons.length === 0 && (
                              <div className="text-white/40">No blockers — eligible</div>
                            )}
                            {reasons.map((r, i) => (
                              <div
                                key={i}
                                className={r.startsWith(`Less than ${FU_MIN_DISK_GB} GB`) ? 'text-red-300' : 'text-amber-200'}
                              >
                                {r}
                              </div>
                            ))}
                            {/* Notes are context, not blockers — muted and
                                visually separate so nobody reads "not compliant
                                in Intune" as the reason a device was excluded. */}
                            {notes.map((n, i) => (
                              <div key={`note-${i}`} className="text-white/45 italic" data-testid={`eligibility-readiness-note-${idx}`}>
                                {n}
                              </div>
                            ))}
                          </div>
                        )}
                      </td>
                      {/* App criticality reasons kept apart from the readiness
                          ones — they explain the band, they do not gate a
                          Staged deployment, and 09/09 they are hidden entirely
                          unless the admin is planning an App Risk one. */}
                      {showAppRisk && (
                        <td className="px-3 py-2 text-xs">
                          {appReasons.length === 0 ? (
                            <span className="text-white/40">—</span>
                          ) : (
                            <div className="space-y-1" data-testid={`eligibility-app-reason-${idx}`}>
                              {appReasons.map((r, i) => (
                                <div key={i} className="text-white/60">{r}</div>
                              ))}
                            </div>
                          )}
                        </td>
                      )}
                      <td className="px-3 py-2">
                        {d.bucket === 'excluded' ? (
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider border bg-red-500/10 text-red-300 border-red-400/30">
                            Excluded
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider border bg-emerald-500/10 text-emerald-300 border-emerald-400/30">
                            Eligible
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {filtered.length > showCount && (
          <div className="text-center mt-3">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowCount(c => c + 200)}
              className="border-[rgba(229,239,250,0.2)] text-white hover:bg-[rgba(229,239,250,0.1)]"
              data-testid="eligibility-readiness-show-more"
            >
              Show {Math.min(200, filtered.length - showCount)} more ({filtered.length - showCount} hidden)
            </Button>
          </div>
        )}

        {/* 02/09 — turn the low-disk list into an action. */}
        {showDiskRemediation && (
          <LowDiskRemediationModal
            devices={lowDiskDevices}
            thresholdGb={FU_MIN_DISK_GB}
            onClose={() => setShowDiskRemediation(false)}
          />
        )}
      </CardContent>
    </Card>
  );
};

// =============================================================================
// FeatureUpdateProgressReport
// -----------------------------------------------------------------------------
// Reports > Feature Updates: deployment progress for each Windows feature
// update profile, plus a per-Windows-version (24H2/25H2/...) summary. Visuals
// reuse the Reports > Overview page styling (gradient KPI cards, top progress
// banner) so the entire Reports tab feels consistent.
//
// Data source: GET /api/os-patch/feature-updates/report
//   -> profiles[]  one entry per Intune feature update profile
//   -> versions[]  rolled-up by Windows version target
//   -> summary     overall device count + status_counts + percent_complete
// =============================================================================
// Keep in step with `_empty_feature_buckets` in backend/routes/os_patch_routes.py.
// `Unknown` = allocated to the sprint but Intune has reported no status for it
// and its local OS build is not yet on target. Distinct from `Pending`, which
// is a real Intune state — a bucket missing from this list is silently dropped
// from the segmented bar and the chips stop summing to the device count.
const FEATURE_STATUS_BUCKETS = [
  { key: 'Upgraded',     label: 'Upgraded',       color: '#22c55e' },
  { key: 'Offering',     label: 'Offering',       color: '#3b82f6' },
  { key: 'InProgress',   label: 'In Progress',    color: '#06b6d4' },
  { key: 'Pending',      label: 'Pending',        color: '#f59e0b' },
  { key: 'Error',        label: 'Error',          color: '#ef4444' },
  { key: 'Cancelled',    label: 'Cancelled',      color: '#a855f7' },
  { key: 'NotApplicable',label: 'Not Applicable', color: '#64748b' },
  { key: 'Unknown',      label: 'Unknown',        color: '#94a3b8' },
];

// "2 offering · 1 in progress · 1 unknown" — the non-Upgraded buckets, for
// places that already show "N / M updated" and want the rest of the picture.
// Deployments and Discover read the same backend buckets, so these agree.
const summariseFeatureBuckets = (counts) => {
  if (!counts) return '';
  return FEATURE_STATUS_BUCKETS
    .filter(b => b.key !== 'Upgraded' && (counts[b.key] || 0) > 0)
    .map(b => `${counts[b.key]} ${b.label.toLowerCase()}`)
    .join(' · ');
};

const FeatureStatusLegend = () => (
  <div className="flex flex-wrap gap-x-4 gap-y-2 mt-3 pt-3 border-t border-[rgba(229,239,250,0.1)]" data-testid="feature-status-legend">
    {FEATURE_STATUS_BUCKETS.map(b => (
      <div key={b.key} className="flex items-center gap-1.5 text-xs text-white/80">
        <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: b.color }} />
        {b.label}
      </div>
    ))}
  </div>
);

// Stacked / segmented horizontal progress bar for one profile or version row.
const FeatureSegmentedBar = ({ counts, total }) => {
  const safeTotal = Math.max(0, total || 0);
  return (
    <div className="w-full">
      <div className="h-3 bg-slate-700/60 rounded-full overflow-hidden flex">
        {safeTotal === 0 ? (
          <div className="h-full w-full bg-slate-600/60" />
        ) : (
          FEATURE_STATUS_BUCKETS.map(b => {
            const v = counts?.[b.key] || 0;
            const pct = (v / safeTotal) * 100;
            if (pct <= 0) return null;
            return (
              <div
                key={b.key}
                className="h-full"
                style={{ width: `${pct}%`, backgroundColor: b.color }}
                title={`${b.label}: ${v} (${pct.toFixed(1)}%)`}
              />
            );
          })
        )}
      </div>
    </div>
  );
};

// -----------------------------------------------------------------------------
// Device Health > Client State chips.
//
// Canonical values come from backend/services/wufb_client_state.py — keep the
// two in step. The legacy Intune-derived values (pending_update / registered /
// pending_action) are included because devices without WUfB coverage still
// fall back to them.
// -----------------------------------------------------------------------------
const CLIENT_STATE_CHIP = {
  completed: 'bg-green-500/20 text-green-400',
  failed: 'bg-red-500/20 text-red-400',
  downloading: 'bg-cyan-500/20 text-cyan-400',
  installing: 'bg-cyan-500/20 text-cyan-400',
  pending_reboot: 'bg-amber-500/20 text-amber-400',
  pending_update: 'bg-purple-500/20 text-purple-400',
  paused: 'bg-slate-500/30 text-slate-200',
  cancelled: 'bg-slate-500/30 text-slate-200',
  not_applicable: 'bg-slate-500/20 text-slate-300',
  registered: 'bg-purple-500/20 text-purple-400',
  pending_action: 'bg-amber-500/20 text-amber-400',
};

const CLIENT_STATE_LABELS = {
  completed: 'Installed',
  failed: 'Failed',
  downloading: 'Downloading',
  installing: 'Installing',
  pending_reboot: 'Pending reboot',
  pending_update: 'Update pending',
  paused: 'Paused / deferred',
  cancelled: 'Cancelled',
  not_applicable: 'Not applicable',
};

// =============================================================================
// FeatureStuckDevicesSection
// -----------------------------------------------------------------------------
// Lists devices currently stuck in Error / In Progress / Pending across every
// feature update profile, with:
//   • status filter chips (All / Error / In Progress / Pending)
//   • days-stuck threshold (3 / 7 / 14 / 30) — derived from `last_event`
//   • CSV export of the filtered set
//   • paginated table (25 rows / page) sorted by oldest last_event first so
//     the most-stuck devices float to the top.
// =============================================================================
const STUCK_STATUS_COLORS = {
  Error:      { bg: 'bg-red-500/15',    fg: 'text-red-300',    border: 'border-red-400/30',    dot: '#ef4444' },
  InProgress: { bg: 'bg-cyan-500/15',   fg: 'text-cyan-200',   border: 'border-cyan-400/30',   dot: '#06b6d4' },
  Pending:    { bg: 'bg-amber-500/15',  fg: 'text-amber-200',  border: 'border-amber-400/30',  dot: '#f59e0b' },
};

const STUCK_STATUS_LABELS = {
  Error: 'Error',
  InProgress: 'In Progress',
  Pending: 'Pending',
};

const _stuckDaysSince = (iso) => {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.floor((Date.now() - t) / (1000 * 60 * 60 * 24)));
};

const FeatureStuckDevicesSection = ({ devices, summary, isFallback }) => {
  const { canWriteOSPatch, canManageOSPatches } = usePermissions();
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'Error' | 'InProgress' | 'Pending' | 'in_phase'
  const [thresholdDays, setThresholdDays] = useState(7);
  const [page, setPage] = useState(1);
  const [remediateTargets, setRemediateTargets] = useState(null); // null | array of devices
  const [reminderTargets, setReminderTargets] = useState(null); // null | array of devices
  const PAGE_SIZE = 25;

  const enriched = useMemo(() => (devices || []).map(d => ({
    ...d,
    daysStuck: _stuckDaysSince(d.last_event),
  })), [devices]);

  const filtered = useMemo(() => {
    return enriched
      .filter(d => {
        if (statusFilter === 'all') return true;
        if (statusFilter === 'in_phase') return !!d.is_stuck_in_phase;
        return d.status === statusFilter;
      })
      .filter(d => {
        if (thresholdDays === 0) return true;
        // If we don't know the age (e.g. fallback path with no last_sync),
        // include the device only when the threshold is "show all".
        return d.daysStuck != null && d.daysStuck >= thresholdDays;
      })
      .sort((a, b) => {
        // Oldest first; unknown ages last
        const av = a.daysStuck == null ? -1 : a.daysStuck;
        const bv = b.daysStuck == null ? -1 : b.daysStuck;
        return bv - av;
      });
  }, [enriched, statusFilter, thresholdDays]);

  // Reset page when filters change
  useEffect(() => { setPage(1); }, [statusFilter, thresholdDays]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageDevices = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleExportCsv = () => {
    if (filtered.length === 0) {
      toast.error('Nothing to export');
      return;
    }
    const headers = [
      'Device Name', 'User', 'Status', 'Days Stuck', 'Profile',
      // 09/09 — release name and build as separate columns, so a spreadsheet
      // can group by release without parsing a string.
      'Target', 'Optional', 'Current Version', 'Current Build', 'Last Event', 'Alert Message',
    ];
    const rows = filtered.map(d => [
      d.device_name || '',
      d.user || '',
      STUCK_STATUS_LABELS[d.status] || d.status || '',
      d.daysStuck == null ? 'unknown' : String(d.daysStuck),
      d.profile_name || '',
      d.target_label || '',
      d.is_optional ? 'Optional' : 'Mandatory',
      d.current_version_friendly || d.current_version || '',
      d.current_os_build || '',
      d.last_event || '',
      (d.alert_message || '').replace(/\r?\n/g, ' '),
    ]);
    const csv = [headers, ...rows]
      .map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `feature-update-stuck-devices-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filtered.length} device${filtered.length === 1 ? '' : 's'}`);
  };

  const totalStuck = summary?.total || 0;
  const byStatus = summary?.by_status || { Error: 0, InProgress: 0, Pending: 0 };

  return (
    <Card
      className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border-[rgba(229,239,250,0.1)]"
      data-testid="feature-stuck-devices-section"
    >
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
              Stuck Devices
              <span
                className="ml-2 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-500/20 text-amber-300 border border-amber-400/30"
                data-testid="feature-stuck-total-badge"
              >
                {totalStuck}
              </span>
            </CardTitle>
            <p className="text-sm text-white/70 mt-1">
              Devices that haven&apos;t completed their feature update yet — filter by status and days stuck to triage the long tail.
            </p>
          </div>
          <Button
            onClick={handleExportCsv}
            size="sm"
            variant="outline"
            className="border-[rgba(229,239,250,0.2)] text-white hover:bg-[rgba(229,239,250,0.1)]"
            data-testid="feature-stuck-export-btn"
            disabled={filtered.length === 0}
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
          {canWriteOSPatch && canManageOSPatches && (
          <Button
            onClick={() => setReminderTargets(filtered)}
            size="sm"
            data-testid="feature-stuck-send-reminder-btn"
            disabled={filtered.length === 0 || !filtered.some(d => d.deployment_id && (d.user || '').includes('@'))}
            className="bg-cyan-500/20 border border-cyan-400/40 text-cyan-100 hover:bg-cyan-500/30 font-semibold"
          >
            <Mail className="w-4 h-4 mr-2" />
            Send reminders ({filtered.filter(d => (d.user || '').includes('@')).length})
          </Button>
          )}
          {canWriteOSPatch && canManageOSPatches && (
          <Button
            onClick={() => setRemediateTargets(filtered)}
            size="sm"
            data-testid="feature-stuck-remediate-btn"
            disabled={filtered.length === 0 || !filtered.some(d => d.azure_ad_device_id)}
            className="bg-[#BBFF71] text-[#3C02CC] hover:bg-[#E5EFFA] hover:text-[#3C02CC] font-semibold"
          >
            <Wrench className="w-4 h-4 mr-2" />
            Remediate ({filtered.length})
          </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {/* Filter row */}
        <div className="flex items-center gap-2 flex-wrap mb-4">
          {[
            { key: 'all',        label: 'All',          count: totalStuck },
            { key: 'in_phase',   label: 'In-sprint stuck', count: summary?.in_phase_over_threshold || 0 },
            { key: 'Error',      label: 'Error',        count: byStatus.Error || 0 },
            { key: 'InProgress', label: 'In Progress',  count: byStatus.InProgress || 0 },
            { key: 'Pending',    label: 'Pending',      count: byStatus.Pending || 0 },
          ].map(chip => {
            const active = statusFilter === chip.key;
            const colors = (chip.key === 'all' || chip.key === 'in_phase')
              ? { bg: 'bg-white/10', fg: 'text-white', border: 'border-white/20', dot: chip.key === 'in_phase' ? '#ef4444' : '#94a3b8' }
              : STUCK_STATUS_COLORS[chip.key];
            return (
              <button
                key={chip.key}
                type="button"
                onClick={() => setStatusFilter(chip.key)}
                data-testid={`feature-stuck-chip-${chip.key.toLowerCase()}`}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors flex items-center gap-2 ${colors.bg} ${colors.fg} ${colors.border} ${active ? 'ring-2 ring-offset-2 ring-offset-slate-900 ring-cyan-400/40' : 'hover:bg-white/10'}`}
              >
                <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: colors.dot }} />
                {chip.label}
                <span className="opacity-80">{chip.count}</span>
              </button>
            );
          })}

          <div className="ml-auto flex items-center gap-2">
            <label className="text-xs text-white/70 uppercase tracking-wider">Stuck for at least</label>
            <select
              value={thresholdDays}
              onChange={e => setThresholdDays(parseInt(e.target.value, 10))}
              className="bg-[rgba(229,239,250,0.1)] border border-[rgba(229,239,250,0.1)] text-white rounded-md px-2 py-1 text-xs"
              data-testid="feature-stuck-threshold-select"
            >
              <option value={0}   className="bg-slate-900">Any age</option>
              <option value={3}   className="bg-slate-900">3 days</option>
              <option value={7}   className="bg-slate-900">7 days</option>
              <option value={14}  className="bg-slate-900">14 days</option>
              <option value={30}  className="bg-slate-900">30 days</option>
            </select>
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="rounded-lg border border-[rgba(229,239,250,0.1)] bg-slate-900/40 p-8 text-center" data-testid="feature-stuck-empty">
            {totalStuck === 0 ? (
              <>
                <CheckCircle className="w-8 h-8 text-green-400 mx-auto mb-2" />
                <p className="text-white font-medium">No stuck devices.</p>
                <p className="text-white/60 text-sm mt-1">Every assigned device is either upgraded or progressing normally.</p>
              </>
            ) : (
              <>
                <p className="text-white">No devices match the current filter.</p>
                <p className="text-white/60 text-sm mt-1">
                  {isFallback ? 'Local fallback mode does not include device-level age — choose "Any age" to see Pending devices.' : `Try lowering the threshold below ${thresholdDays} days.`}
                </p>
              </>
            )}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto rounded-lg border border-[rgba(229,239,250,0.1)]" data-testid="feature-stuck-table-wrapper">
              <table className="w-full text-sm" data-testid="feature-stuck-table">
                <thead>
                  <tr className="bg-[rgba(229,239,250,0.08)] text-white/80">
                    <th className="text-left p-3 font-medium">Device</th>
                    <th className="text-left p-3 font-medium">User</th>
                    <th className="text-left p-3 font-medium">Status</th>
                    <th className="text-right p-3 font-medium">Days stuck</th>
                    <th className="text-left p-3 font-medium">Sprint</th>
                    <th className="text-right p-3 font-medium">Free disk</th>
                    <th className="text-left p-3 font-medium">Last check-in</th>
                    <th className="text-left p-3 font-medium">Profile</th>
                    <th className="text-left p-3 font-medium">Alert</th>
                  </tr>
                </thead>
                <tbody>
                  {pageDevices.map((d, i) => {
                    const colors = STUCK_STATUS_COLORS[d.status] || STUCK_STATUS_COLORS.Pending;
                    const freeGb = d.free_storage_gb;
                    const diskLow = freeGb != null && freeGb < FU_MIN_DISK_GB;
                    const lastSyncIso = d.last_sync || d.last_event;
                    let dsc = null;
                    if (lastSyncIso) {
                      const t = Date.parse(lastSyncIso);
                      if (!Number.isNaN(t)) dsc = Math.max(0, Math.floor((Date.now() - t) / 86400000));
                    }
                    return (
                      <tr
                        key={`${d.profile_id || ''}-${d.device_id || d.azure_ad_device_id || i}`}
                        className="border-t border-white/5 hover:bg-white/[0.03]"
                        data-testid={`feature-stuck-row-${i}`}
                      >
                        <td className="p-3">
                          <div className="text-white font-medium">{d.device_name || 'Unknown'}</div>
                          {/* 09/09 — lead with the release name ("Windows 11
                              24H2"), not the build. That is what an admin
                              triages by; the build is what you need once you
                              are diagnosing one device, so it moves to the
                              tooltip. */}
                          {(d.current_version_friendly || d.current_version) && (
                            <div
                              className="text-xs text-white/50"
                              title={d.current_os_build || d.current_version || undefined}
                            >
                              {d.current_version_friendly || d.current_version}
                            </div>
                          )}
                        </td>
                        <td className="p-3 text-white/80">{d.user || <span className="text-white/40">—</span>}</td>
                        <td className="p-3">
                          <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium border ${colors.bg} ${colors.fg} ${colors.border}`}>
                            <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ backgroundColor: colors.dot }} />
                            {STUCK_STATUS_LABELS[d.status] || d.status}
                          </span>
                          {d.is_stuck_in_phase && (
                            <div className="text-[10px] uppercase tracking-wider text-red-300 mt-1" title="Active sprint >= threshold, device not on target">
                              In-sprint stuck
                            </div>
                          )}
                        </td>
                        <td className="p-3 text-right">
                          {d.daysStuck == null ? (
                            <span className="text-white/40 text-xs uppercase tracking-wider">unknown</span>
                          ) : (
                            <span className={`font-semibold ${d.daysStuck >= 14 ? 'text-red-300' : d.daysStuck >= 7 ? 'text-amber-300' : 'text-white'}`}>
                              {d.daysStuck}
                            </span>
                          )}
                        </td>
                        <td className="p-3">
                          {d.phase_label ? (
                            <div>
                              <div className="text-white text-xs">{d.phase_label}</div>
                              <div className="text-[10px] uppercase tracking-wider text-white/50">
                                {d.phase_status || '—'}
                                {d.phase_days_active != null && ` · ${d.phase_days_active}d`}
                              </div>
                            </div>
                          ) : (
                            <span className="text-white/40 text-xs">—</span>
                          )}
                        </td>
                        <td className="p-3 text-right text-xs">
                          {freeGb == null ? (
                            <span className="text-white/40">—</span>
                          ) : (
                            <span className={diskLow ? 'text-red-300 font-semibold' : 'text-white/80'}>
                              {freeGb.toFixed(1)} GB
                            </span>
                          )}
                        </td>
                        <td className="p-3 text-xs">
                          {dsc == null ? (
                            <span className="text-white/40">—</span>
                          ) : (
                            <span className={dsc >= 30 ? 'text-amber-300' : 'text-white/70'} title={lastSyncIso || ''}>
                              {dsc === 0 ? 'today' : `${dsc}d ago`}
                            </span>
                          )}
                        </td>
                        <td className="p-3">
                          <div className="text-white truncate max-w-[180px]" title={d.profile_name}>{d.profile_name}</div>
                          <div className="text-[10px] uppercase tracking-wider text-white/50">
                            {d.is_optional ? 'Optional' : 'Mandatory'}
                          </div>
                        </td>
                        <td className="p-3 text-white/70 text-xs">
                          {d.alert_message ? (
                            <span className="line-clamp-2 max-w-[260px] inline-block" title={d.alert_message}>
                              {d.alert_message}
                            </span>
                          ) : (
                            <span className="text-white/40">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {filtered.length > PAGE_SIZE && (
              <div className="flex items-center justify-between mt-3 text-xs text-white/70" data-testid="feature-stuck-pagination">
                <span>
                  Showing {(page - 1) * PAGE_SIZE + 1}-{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length}
                </span>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    className="text-white/70 hover:text-white h-7 px-2"
                    data-testid="feature-stuck-prev-btn"
                  >
                    <ChevronLeft className="w-3.5 h-3.5" />
                  </Button>
                  <span className="px-2">
                    Page {page} of {totalPages}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={page >= totalPages}
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    className="text-white/70 hover:text-white h-7 px-2"
                    data-testid="feature-stuck-next-btn"
                  >
                    <ChevronRight className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
      {remediateTargets && (
        <RunScriptModal
          devices={remediateTargets.map(d => ({
            device_id: d.device_id,
            azure_ad_device_id: d.azure_ad_device_id,
            device_name: d.device_name,
          }))}
          // Stuck devices default to the scan + install nudge; any other
          // script can still be picked in the modal.
          recommendedCatalogKey="force_wu_scan"
          source="stuck_devices"
          onClose={() => setRemediateTargets(null)}
          // The modal confirms the run itself; this only closes the section's state.
          onRunCreated={() => setRemediateTargets(null)}
        />
      )}
      {reminderTargets && (() => {
        // Group by deployment_id and open a modal per deployment. For
        // V1 we open one modal for the deployment with the most
        // devices in the selection. Admins can refilter if they want
        // to nudge a different deployment.
        const byDep = new Map();
        reminderTargets.forEach(d => {
          const k = d.deployment_id || '__unknown__';
          if (!byDep.has(k)) byDep.set(k, []);
          byDep.get(k).push(d);
        });
        // Pick the largest known-deployment bucket
        let pick = null;
        for (const [k, v] of byDep.entries()) {
          if (k === '__unknown__') continue;
          if (!pick || v.length > pick.devices.length) pick = { dep: k, devices: v };
        }
        if (!pick) {
          // No devices in the filtered list belong to an ALICE deployment.
          return (
            <SendReminderModal
              devices={reminderTargets}
              deploymentId={null}
              phaseLabel=""
              onClose={() => setReminderTargets(null)}
              onSent={() => setReminderTargets(null)}
            />
          );
        }
        return (
          <SendReminderModal
            devices={pick.devices}
            deploymentId={pick.dep}
            phaseLabel={pick.devices[0]?.phase_label || ''}
            onClose={() => setReminderTargets(null)}
            onSent={() => setReminderTargets(null)}
          />
        );
      })()}
    </Card>
  );
};

// ============================================================================
// Drill-in modal: per-profile / per-version device list with filter, search,
// and CSV export. Mounted by FeatureUpdateProgressReport when a card is
// clicked. Re-uses the FEATURE_STATUS_BUCKETS palette for status chips.
// ============================================================================
const FeatureUpdateDeviceDrilldown = ({ drillIn, onClose }) => {
  const { canWriteOSPatch, canManageOSPatches } = usePermissions();
  const { kind, id, label, title } = drillIn;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [payload, setPayload] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [showReminderModal, setShowReminderModal] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    (async () => {
      setLoading(true);
      try {
        const url = kind === 'profile'
          ? `${API}/feature-updates/report/profile/${encodeURIComponent(id)}/devices`
          : `${API}/feature-updates/report/version/${encodeURIComponent(label)}/devices`;
        const res = await axios.get(url);
        if (!cancelled) {
          setPayload(res.data);
          setError(null);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e?.response?.data?.detail || e.message || 'Failed to load devices');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; document.removeEventListener('keydown', onKey); };
  }, [kind, id, label, onClose]);

  const devices = payload?.devices || [];

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return devices.filter(d => {
      if (statusFilter !== 'all' && d.status !== statusFilter) return false;
      if (!q) return true;
      return (
        (d.device_name || '').toLowerCase().includes(q) ||
        (d.user || '').toLowerCase().includes(q) ||
        (d.current_version || '').toLowerCase().includes(q) ||
        (d.alert_message || '').toLowerCase().includes(q) ||
        (d.profile_name || '').toLowerCase().includes(q) ||
        (d.device_id || '').toLowerCase().includes(q) ||
        (d.azure_ad_device_id || '').toLowerCase().includes(q)
      );
    });
  }, [devices, statusFilter, search]);

  const downloadCsv = (rows, suffix) => {
    if (!rows.length) {
      toast.info('Nothing to export');
      return;
    }
    const cols = [
      ['device_name', 'Device Name'],
      ['user', 'User (UPN)'],
      ['profile_name', 'Profile'],
      ['raw_status', 'Update Status'],
      ['raw_substatus', 'Substatus'],
      ['status', 'Bucket'],
      // Bug fix 1 (01/09) — this is the device's own OS build, resolved from
      // the local Intune device mirror. Intune's WindowsUpdateVersion column
      // (the policy's TARGET release) is exported alongside it so the two are
      // never confused again.
      ['current_version', 'Current Version (device build)'],
      ['target_version', 'Target Version'],
      ['reported_update_version', 'Intune WindowsUpdateVersion (target)'],
      ['alert_message', 'Latest Alert'],
      ['last_event', 'Last Event / WU Scan'],
      ['azure_ad_device_id', 'AAD Device ID'],
      ['device_id', 'Intune Device ID'],
    ];
    const esc = (v) => {
      if (v == null) return '';
      const s = String(v).replace(/"/g, '""');
      return /[",\n]/.test(s) ? `"${s}"` : s;
    };
    const header = cols.map(c => c[1]).join(',');
    const lines = rows.map(r => cols.map(c => esc(r[c[0]])).join(','));
    const csv = '\ufeff' + [header, ...lines].join('\n'); // BOM for Excel
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const safe = (title || 'feature-updates').replace(/[^a-z0-9-_]+/gi, '_').slice(0, 60);
    const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    a.download = `feature-update-devices_${safe}_${suffix}_${ts}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const intuneDeviceUrl = (d) =>
    d.azure_ad_device_id
      ? `https://intune.microsoft.com/#blade/Microsoft_Intune_Devices/DeviceSettingsMenuBlade/overview/mdmDeviceId/${d.azure_ad_device_id}`
      : null;

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      return d.toLocaleString();
    } catch { return iso; }
  };

  const statusCounts = useMemo(() => {
    const out = { all: devices.length };
    FEATURE_STATUS_BUCKETS.forEach(b => { out[b.key] = 0; });
    devices.forEach(d => { if (d.status in out) out[d.status]++; });
    return out;
  }, [devices]);

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="feature-update-drilldown-overlay"
    >
      <div
        className="alice-popup bg-slate-900 border border-white/10 rounded-2xl shadow-2xl w-full max-w-7xl max-h-[90vh] flex flex-col"
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 p-5 border-b border-white/10">
          <div className="min-w-0 flex-1">
            <p className="text-xs uppercase tracking-wider text-white/50">
              {kind === 'profile' ? 'Feature Update Profile' : 'Windows Version'}
            </p>
            <h2 className="text-xl font-semibold text-white truncate" title={title} data-testid="feature-update-drilldown-title">
              {title}
            </h2>
            <p className="text-xs text-white/60 mt-1">
              {payload ? (
                <>
                  {payload.device_count} {kind === 'version' ? 'unique device' : 'device'}{payload.device_count === 1 ? '' : 's'}
                  {kind === 'version' && payload.profile_count > 1 && (
                    <span className="text-white/40"> (across {payload.profile_count} profiles, de-duped)</span>
                  )}
                  {' · '}
                  Source: <span className="text-white/80">{payload.source}</span>
                  {payload.generated_at && (
                    <>{' · '}Refreshed: <span className="text-white/80">{formatDate(payload.generated_at)}</span></>
                  )}
                  {/* Bug fix 1 (01/09) — state the target once, in the header,
                      so the per-device "Current Version" column is
                      unambiguously the device's own build. */}
                  {(payload.target_label || payload.target_version) && (
                    <>{' · '}Target: <span className="text-white/80" data-testid="feature-update-drilldown-target">
                      {payload.target_label || payload.target_version}
                    </span></>
                  )}
                </>
              ) : loading ? 'Loading…' : '—'}
            </p>
            {payload?.pre_drilldown_cache && (
              <p className="text-[11px] text-amber-300/90 mt-1.5 flex items-center gap-1.5" data-testid="feature-update-drilldown-stale-cache-notice">
                <AlertTriangle className="w-3 h-3" />
                Showing partial data from an older cache. Click <span className="font-semibold">Refresh from Intune</span> on the report for the complete device list.
              </p>
            )}
            {payload?.hidden_historical_rows > 0 && (
              <p className="text-[11px] text-white/50 mt-1.5" data-testid="feature-update-drilldown-scoped-notice">
                Scoped to this sprint: {payload.hidden_historical_rows} historical Intune row
                {payload.hidden_historical_rows === 1 ? '' : 's'} hidden (devices Intune still reports on but that are
                no longer allocated to, or a member of, this sprint).
              </p>
            )}
            {payload?.stale_allocation_count > 0 && (
              <p className="text-[11px] text-amber-300/90 mt-1.5 flex items-center gap-1.5" data-testid="feature-update-drilldown-stale-allocation-notice">
                <AlertTriangle className="w-3 h-3" />
                {payload.stale_allocation_count} allocated device{payload.stale_allocation_count === 1 ? '' : 's'} no longer
                {payload.stale_allocation_count === 1 ? ' has' : ' have'} a record in ALICE (deleted or re-enrolled in Intune)
                and {payload.stale_allocation_count === 1 ? 'is' : 'are'} excluded from the counts. Re-pick this sprint's devices to clear it.
              </p>
            )}

          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="text-white/60 hover:text-white hover:bg-white/10"
            data-testid="feature-update-drilldown-close-btn"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Filter / search / export */}
        <div className="p-4 border-b border-white/10 flex flex-wrap gap-3 items-center">
          <div className="flex flex-wrap gap-1.5">
            <button
              onClick={() => setStatusFilter('all')}
              className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
                statusFilter === 'all'
                  ? 'bg-white/20 text-white border-white/30'
                  : 'bg-white/5 text-white/70 border-white/10 hover:bg-white/10'
              }`}
              data-testid="feature-update-drilldown-filter-all"
            >
              All ({statusCounts.all})
            </button>
            {FEATURE_STATUS_BUCKETS.map(b => (
              <button
                key={b.key}
                onClick={() => setStatusFilter(b.key)}
                className={`px-2.5 py-1 text-xs rounded-full border transition-colors flex items-center gap-1.5 ${
                  statusFilter === b.key
                    ? 'text-white border-white/30'
                    : 'text-white/70 border-white/10 hover:bg-white/5'
                }`}
                style={statusFilter === b.key ? { backgroundColor: `${b.color}33`, borderColor: `${b.color}80` } : {}}
                data-testid={`feature-update-drilldown-filter-${b.key}`}
              >
                <span className="inline-block w-1.5 h-1.5 rounded-sm" style={{ backgroundColor: b.color }} />
                {b.label} ({statusCounts[b.key] || 0})
              </button>
            ))}
          </div>
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-white/40" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search device, user, version, alert…"
                className="pl-8 h-8 text-sm bg-white/5 border-white/10 text-white placeholder:text-white/40"
                data-testid="feature-update-drilldown-search"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => downloadCsv(filtered, 'filtered')}
              disabled={!filtered.length}
              className="bg-white/5 border-white/10 text-white hover:bg-white/10"
              data-testid="feature-update-drilldown-export-filtered-btn"
            >
              <Download className="w-3.5 h-3.5 mr-1.5" />
              Export filtered ({filtered.length})
            </Button>
            <Button
              size="sm"
              onClick={() => downloadCsv(devices, 'all')}
              disabled={!devices.length}
              className="bg-cyan-500/20 border border-cyan-400/40 text-cyan-100 hover:bg-cyan-500/30"
              data-testid="feature-update-drilldown-export-all-btn"
            >
              <Download className="w-3.5 h-3.5 mr-1.5" />
              Export all ({devices.length})
            </Button>
            {canWriteOSPatch && canManageOSPatches && (
            <Button
              size="sm"
              onClick={() => setShowReminderModal(true)}
              disabled={!selectedIds.length}
              className="bg-[#BBFF71] text-[#3C02CC] hover:bg-[#E5EFFA] hover:text-[#3C02CC] font-semibold disabled:opacity-40"
              data-testid="feature-update-drilldown-send-reminder-btn"
            >
              <Mail className="w-3.5 h-3.5 mr-1.5" />
              Send reminder ({selectedIds.length})
            </Button>
            )}
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto p-4">
          {loading ? (
            <div className="text-center py-12 text-white/70" data-testid="feature-update-drilldown-loading">
              <RefreshCw className="w-6 h-6 mx-auto animate-spin mb-2" />
              Loading device list…
            </div>
          ) : error ? (
            <div className="rounded-xl border border-red-400/40 bg-red-500/10 p-5 text-center" data-testid="feature-update-drilldown-error">
              <AlertTriangle className="w-6 h-6 mx-auto text-red-300 mb-2" />
              <p className="text-red-200">{error}</p>
              {error.includes('No data cached') && (
                <p className="text-xs text-red-200/70 mt-2">
                  Close this modal and click <span className="font-semibold">Refresh from Intune</span> on the report.
                </p>
              )}
            </div>
          ) : !filtered.length ? (
            <div className="text-center py-12 text-white/60" data-testid="feature-update-drilldown-empty">
              No devices match the current filters.
            </div>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-white/10">
              <table className="w-full text-sm" data-testid="feature-update-drilldown-table">
                <thead className="bg-white/5 text-white/70 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="text-center px-2 py-2 font-medium w-9">
                      <input
                        type="checkbox"
                        aria-label="Select all"
                        checked={
                          filtered.filter(d => d.status !== 'Upgraded').length > 0
                          && filtered
                            .filter(d => d.status !== 'Upgraded')
                            .every(d => selectedIds.includes(d.device_id || d.azure_ad_device_id))
                        }
                        onChange={(e) => {
                          if (e.target.checked) {
                            // Never auto-select devices that are already on
                            // the target version (status == Upgraded) — sending
                            // reminders to already-updated users is noise.
                            setSelectedIds(
                              filtered
                                .filter(d => d.status !== 'Upgraded')
                                .map(d => d.device_id || d.azure_ad_device_id)
                                .filter(Boolean)
                            );
                          } else {
                            setSelectedIds([]);
                          }
                        }}
                        data-testid="feature-update-drilldown-select-all"
                      />
                    </th>
                    <th className="text-left px-3 py-2 font-medium">Device</th>
                    <th className="text-left px-3 py-2 font-medium">User</th>
                    {kind === 'version' && (
                      <th className="text-left px-3 py-2 font-medium">Profile</th>
                    )}
                    <th className="text-left px-3 py-2 font-medium">Status</th>
                    <th className="text-left px-3 py-2 font-medium">Current Version</th>
                    <th className="text-right px-3 py-2 font-medium">Free Disk</th>
                    <th className="text-left px-3 py-2 font-medium">Last Check-in</th>
                    <th className="text-left px-3 py-2 font-medium">Latest Alert</th>
                    <th className="text-left px-3 py-2 font-medium">Intune</th>
                  </tr>
                </thead>
                <tbody className="text-white/90">
                  {filtered.map((d, idx) => {
                    const bucket = FEATURE_STATUS_BUCKETS.find(b => b.key === d.status);
                    const iurl = intuneDeviceUrl(d);
                    const did = d.device_id || d.azure_ad_device_id;
                    const checked = !!did && selectedIds.includes(did);
                    const freeGb = d.free_storage_gb;
                    const diskFail = freeGb != null && freeGb < FU_MIN_DISK_GB;
                    const lastSyncIso = d.last_sync || d.last_event;
                    let daysSince = null;
                    if (lastSyncIso) {
                      const t = Date.parse(lastSyncIso);
                      if (!Number.isNaN(t)) {
                        daysSince = Math.max(0, Math.floor((Date.now() - t) / 86400000));
                      }
                    }
                    return (
                      <tr
                        key={`${d.device_id || d.azure_ad_device_id || d.device_name || idx}-${idx}`}
                        className="border-t border-white/5 hover:bg-white/[0.03]"
                        data-testid={`feature-update-drilldown-row-${idx}`}
                      >
                        <td className="px-2 py-2 text-center">
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={!did || d.status === 'Upgraded'}
                            title={d.status === 'Upgraded'
                              ? 'Already on the target version — no reminder needed'
                              : undefined}
                            onChange={(e) => {
                              if (!did || d.status === 'Upgraded') return;
                              setSelectedIds(prev => e.target.checked
                                ? [...new Set([...prev, did])]
                                : prev.filter(x => x !== did));
                            }}
                            data-testid={`feature-update-drilldown-select-${idx}`}
                          />
                        </td>
                        <td className="px-3 py-2 font-medium text-white">{d.device_name || 'Unknown'}</td>
                        <td className="px-3 py-2 text-white/70">{d.user || '—'}</td>
                        {kind === 'version' && (
                          <td className="px-3 py-2 text-white/70 max-w-[180px] truncate" title={d.profile_name}>
                            {d.profile_name || '—'}
                          </td>
                        )}
                        <td className="px-3 py-2">
                          <span
                            className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] border"
                            style={bucket ? {
                              backgroundColor: `${bucket.color}22`,
                              borderColor: `${bucket.color}80`,
                              color: bucket.color,
                            } : {}}
                          >
                            <span className="w-1.5 h-1.5 rounded-sm" style={{ backgroundColor: bucket?.color || '#888' }} />
                            {d.raw_status || d.status}
                          </span>
                          {d.in_deployment_group === false && (
                            <span
                              className="ml-1 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] border border-amber-500/50 bg-amber-500/10 text-amber-300"
                              title="Allocated to this sprint in ALICE but not a member of the sprint's Entra group — Intune will not offer the update"
                              data-testid={`feature-update-drilldown-not-in-group-${idx}`}
                            >
                              Not in group
                            </span>
                          )}
                        </td>
                        {/* Bug fix 1 (01/09) — the device's OWN build, not the
                            policy's target release. */}
                        <td
                          className="px-3 py-2 text-white/70 font-mono text-xs"
                          title={d.current_os_build
                            ? `Build ${d.current_os_build}${d.target_version ? ` · target ${d.target_version}` : ''}`
                            : undefined}
                          data-testid={`feature-update-drilldown-current-version-${idx}`}
                        >
                          {d.current_version || '—'}
                        </td>
                        <td className="px-3 py-2 text-right text-xs">
                          {freeGb == null ? (
                            <span className="text-white/40">—</span>
                          ) : (
                            <span className={diskFail ? 'text-red-300 font-semibold' : 'text-white/80'}>
                              {freeGb.toFixed(1)} GB
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-xs">
                          {daysSince == null ? (
                            <span className="text-white/40">—</span>
                          ) : (
                            <span
                              className={daysSince >= 30 ? 'text-amber-300' : 'text-white/70'}
                              title={lastSyncIso || ''}
                            >
                              {daysSince === 0 ? 'today' : `${daysSince}d ago`}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-white/70 max-w-[200px] truncate" title={d.alert_message}>
                          {d.alert_message || '—'}
                        </td>
                        <td className="px-3 py-2">
                          {iurl ? (
                            <a
                              href={iurl}
                              target="_blank"
                              rel="noreferrer"
                              className="text-cyan-300 hover:text-cyan-200 inline-flex items-center gap-1"
                              data-testid={`feature-update-drilldown-intune-link-${idx}`}
                            >
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          ) : (
                            <span className="text-white/30">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      {showReminderModal && (
        <SendReminderModal
          devices={filtered.filter(d => selectedIds.includes(d.device_id || d.azure_ad_device_id))}
          deploymentId={(filtered.find(d => d.deployment_id) || {}).deployment_id || null}
          // A tenant-synced profile has no ALICE deployment: send by profile.
          profileId={kind === 'profile' ? id : null}
          phaseLabel={(filtered.find(d => d.phase_label) || {}).phase_label || ''}
          onClose={() => setShowReminderModal(false)}
          onSent={() => {
            setShowReminderModal(false);
            setSelectedIds([]);
          }}
        />
      )}
    </div>
  );
};


// =============================================================================
// SendReminderModal
// -----------------------------------------------------------------------------
// Push-button reminder dialog: an admin selects one or more devices from the
// Feature Updates drill-in (or a deployment phase view) and fires off a
// templated reminder email to each device's primary user. Backed by
// POST /api/os-patch/feature-updates/deployments/{id}/send-reminder, which
// reuses the existing M365 sendMail integration (alice@beyondapplications.io,
// app-only Graph permissions). No automated nudges — admin-driven only.
// =============================================================================
const DEFAULT_REMINDER_SUBJECT = "Action needed: Windows feature update available";
// CONTENT ONLY — 09/09. Kept byte-for-byte in step with
// `_REMINDER_TEMPLATE_DEFAULT_BODY_HTML` in os_patch_routes.py: this modal
// always POSTs the textarea, so whatever is here IS the email, and the backend
// default only applies to API callers.
//
// It used to be a dark, hard-coded ALICE shell sent with wrap=False, so a
// customer's users received a Camwood-branded email about their own estate. The
// send now wraps it in the TENANT's shell — their banner, colours, company name
// and support links from Settings → Branding — which renders the body on white
// with dark text. Nothing here may set a background or a light-on-dark colour.
const DEFAULT_REMINDER_BODY = `<p style="margin:0 0 14px 0;">Hi {display_name},</p>
<p style="margin:0 0 14px 0;">Your device <strong>{device_name}</strong> has a pending Windows feature update
   — <strong>{target_version}</strong> — and it is waiting on you to complete the install.</p>
<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="margin:0 0 14px 0;">
  <tr>
    <td style="background-color:#f1f5f9;border-left:4px solid #0f766e;padding:12px 14px;font-size:14px;line-height:1.5;">
      Open <strong>Windows Update</strong> (from the Start menu or the notification icon), click
      <strong>Check for updates</strong>, and choose <strong>Download &amp; install</strong> for this
      latest version.
    </td>
  </tr>
</table>
<p style="margin:0;font-size:13px;color:#4b5563;">If you need help, please contact your IT support team.</p>`;

// Stale device reminder (October 2026). CONTENT ONLY and in step with
// `_DEVICE_UPDATE_REMINDER_DEFAULT_BODY_HTML` in os_patch_routes.py: the modal
// posts the textarea, so what is here IS the email. No CSS braces (str.format).
const DEFAULT_STALE_REMINDER_SUBJECT = "Action needed: please update your Windows device";
const DEFAULT_STALE_REMINDER_BODY = `<p style="margin:0 0 14px 0;">Hi {display_name},</p>
<p style="margin:0 0 14px 0;">Your device <strong>{device_name}</strong> has not checked in with our
   systems for a while, so it is missing important Windows updates.</p>
<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="margin:0 0 14px 0;">
  <tr>
    <td style="background-color:#f1f5f9;border-left:4px solid #0f766e;padding:12px 14px;font-size:14px;line-height:1.7;">
      <strong>Please do the following:</strong><br>
      1. Power on your device and connect it to the internet (or the company network).<br>
      2. Click <strong>Start</strong> &gt; <strong>Settings</strong> &gt; <strong>Windows Update</strong>.<br>
      3. Click <strong>Check for updates</strong>, then install everything it finds.<br>
      4. Restart your device when asked.
    </td>
  </tr>
</table>
<p style="margin:0;font-size:13px;color:#4b5563;">If you need help, please contact your IT support team.</p>`;

// `profileId` (October 2026): for a feature update profile synced from the
// tenant's own Intune — no ALICE deployment — the send goes to the profile
// reminder route instead. A deployment id, when there is one, still wins.
// `endpoint` (October 2026): a full URL for reminders that belong to no
// deployment, such as the Fix Queue's stale device email, with its own wording.
const SendReminderModal = ({
  devices, deploymentId, profileId = null, phaseLabel, onClose, onSent,
  endpoint = null, eyebrow = 'Feature Update Reminder',
  defaultSubject = DEFAULT_REMINDER_SUBJECT, defaultBody = DEFAULT_REMINDER_BODY,
}) => {
  const [subject, setSubject] = useState(defaultSubject);
  const [body, setBody] = useState(defaultBody);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  // Refuse to email users on devices that have already finished the
  // feature update (status == Upgraded). Defends against admins manually
  // passing a device list that happens to include completed devices, and
  // matches the backend's `already_upgraded` skip reason.
  const eligibleByStatus = devices.filter(d => (d.status || '') !== 'Upgraded');
  const skippedAsUpgraded = devices.length - eligibleByStatus.length;
  const devicesWithEmail = eligibleByStatus.filter(d => (d.user || d.user_email || d.user_principal_name || '').includes('@'));
  const devicesNoEmail = eligibleByStatus.filter(d => !(d.user || d.user_email || d.user_principal_name || '').includes('@'));

  const handleSend = async () => {
    if (!endpoint && !deploymentId && !profileId) {
      toast.error("These devices aren't linked to a single deployment or feature update profile — open a profile's devices to send reminders.");
      return;
    }
    if (devicesWithEmail.length === 0) {
      toast.error("None of the selected devices have a user email on file.");
      return;
    }
    setSending(true);
    try {
      const url = endpoint || (deploymentId
        ? `${API}/feature-updates/deployments/${encodeURIComponent(deploymentId)}/send-reminder`
        : `${API}/feature-updates/profiles/${encodeURIComponent(profileId)}/send-reminder`);
      const res = await axios.post(url, {
        device_ids: devicesWithEmail.map(d => d.device_id || d.intune_id || d.azure_ad_device_id).filter(Boolean),
        subject,
        body_html: body,
        phase_label: phaseLabel,
      });
      setResult(res.data);
      if (res.data.error_count > 0) {
        toast.warning(`Sent ${res.data.sent_count}, failed ${res.data.error_count}`);
      } else {
        toast.success(`Sent ${res.data.sent_count} reminder email${res.data.sent_count === 1 ? '' : 's'}`);
      }
    } catch (e) {
      toast.error(_errText(e, 'Failed to send reminders'));
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[60] p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="send-reminder-modal"
    >
      <div className="alice-popup bg-slate-900 border border-white/10 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="flex items-start justify-between gap-4 p-5 border-b border-white/10">
          <div>
            <p className="text-xs uppercase tracking-wider text-white/50">{eyebrow}</p>
            <h2 className="text-xl font-semibold text-white">Send reminder to {devicesWithEmail.length} user{devicesWithEmail.length === 1 ? '' : 's'}</h2>
            {phaseLabel && !endpoint && (
              <p className="text-xs text-white/60 mt-1">Sprint: {phaseLabel}</p>
            )}
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} className="text-white/60 hover:text-white" data-testid="send-reminder-close-btn">
            <X className="w-4 h-4" />
          </Button>
        </div>

        <div className="flex-1 overflow-auto p-5 space-y-4">
          {!result ? (
            <>
              {devicesNoEmail.length > 0 && (
                <div className="rounded-lg border border-amber-400/30 bg-amber-500/10 text-amber-200 text-xs p-3" data-testid="send-reminder-no-email-warning">
                  <strong>{devicesNoEmail.length}</strong> device{devicesNoEmail.length === 1 ? '' : 's'} will be skipped — no user email on the device record.
                </div>
              )}

              {skippedAsUpgraded > 0 && (
                <div className="rounded-lg border border-emerald-400/30 bg-emerald-500/10 text-emerald-200 text-xs p-3" data-testid="send-reminder-already-upgraded-warning">
                  <strong>{skippedAsUpgraded}</strong> device{skippedAsUpgraded === 1 ? '' : 's'} will be skipped — already on the target version (status: Upgraded).
                </div>
              )}

              <div>
                <label className="block text-xs uppercase tracking-wider text-white/60 mb-1">Subject</label>
                <Input
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="bg-white/5 border-white/10 text-white"
                  data-testid="send-reminder-subject-input"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs uppercase tracking-wider text-white/60">Email body (HTML)</label>
                  <span className="text-[10px] text-white/40">
                    Placeholders: {'{display_name}'} · {'{device_name}'} · {'{target_version}'} · {'{deployment_name}'} · {'{phase_label}'}
                  </span>
                </div>
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={10}
                  className="w-full bg-white/5 border border-white/10 text-white/90 rounded-md p-3 font-mono text-xs"
                  data-testid="send-reminder-body-textarea"
                />
              </div>

              {/* 09/09 — the reminder is now wrapped in the tenant's branded
                  shell, so the textarea above is no longer the whole email.
                  Without a preview an admin cannot see what their body looks
                  like inside that shell — the branding change is what creates
                  the need for this. */}
              <AnnouncementPreview
                kind="reminder"
                subject={subject}
                bodyHtml={body}
                phaseLabel={phaseLabel}
              />

              <div className="rounded-lg border border-white/10 bg-white/5 p-3">
                <p className="text-xs text-white/70 mb-2">
                  Recipients ({devicesWithEmail.length}):
                </p>
                <div className="text-[11px] text-white/80 space-y-0.5 max-h-32 overflow-auto">
                  {devicesWithEmail.slice(0, 50).map((d, i) => (
                    <div key={i} className="flex justify-between">
                      <span className="font-medium">{d.device_name || '—'}</span>
                      <span className="text-white/60">{d.user || d.user_email || d.user_principal_name}</span>
                    </div>
                  ))}
                  {devicesWithEmail.length > 50 && (
                    <div className="text-white/40 italic">…and {devicesWithEmail.length - 50} more</div>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="space-y-3" data-testid="send-reminder-result">
              <div className="rounded-lg border border-green-400/30 bg-green-500/10 p-3 text-green-200 text-sm">
                <p><strong>Sent:</strong> {result.sent_count}</p>
                <p><strong>Skipped:</strong> {result.skipped_count}</p>
                <p><strong>Errors:</strong> {result.error_count}</p>
              </div>
              {(result.errors || []).slice(0, 10).map((e, i) => (
                <div key={i} className="text-xs text-red-200 bg-red-500/10 border border-red-400/30 rounded p-2">
                  <strong>{e.device_name || e.device_id}</strong>: {e.error}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="border-t border-white/10 p-4 flex justify-end gap-2">
          {!result ? (
            <>
              <Button variant="ghost" onClick={onClose} className="text-white/70 hover:text-white" data-testid="send-reminder-cancel-btn">
                Cancel
              </Button>
              <Button
                onClick={handleSend}
                disabled={sending || devicesWithEmail.length === 0}
                className="bg-[#BBFF71] text-[#3C02CC] hover:bg-[#E5EFFA] hover:text-[#3C02CC] font-semibold disabled:opacity-50"
                data-testid="send-reminder-submit-btn"
              >
                {sending ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Sending…
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4 mr-2" />
                    Send {devicesWithEmail.length} reminder{devicesWithEmail.length === 1 ? '' : 's'}
                  </>
                )}
              </Button>
            </>
          ) : (
            <Button
              onClick={onSent}
              className="bg-[#BBFF71] text-[#3C02CC] hover:bg-[#E5EFFA] hover:text-[#3C02CC] font-semibold"
              data-testid="send-reminder-done-btn"
            >
              Done
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};



// October 2026: moved from Discover to Deployments > Feature Update
// Deployments. `deployments` (ALICE deployments from the page) lets every
// deployment have its block; `renderManage(deployment)` renders that
// deployment's management card inside it.
const FeatureUpdateProgressReport = ({ deployments = [], renderManage = null }) => {
  const [managing, setManaging] = useState({}); // deployment id -> showing its management card
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshStatus, setRefreshStatus] = useState(null);
  const [error, setError] = useState(null);
  const [drillIn, setDrillIn] = useState(null); // { kind: 'profile'|'version', id, label }
  const pollTimerRef = useRef(null);

  const load = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const res = await axios.get(`${API}/feature-updates/report`);
      setData(res.data);
      setRefreshStatus(res.data?.refresh || null);
      setError(null);
      // If the backend says a refresh is running, kick off the poller.
      if (res.data?.refresh?.running) {
        setRefreshing(true);
        startPolling();
      }
    } catch (e) {
      console.error('Feature update report load failed:', e);
      setError(e?.response?.data?.detail || e.message || 'Failed to load report');
      if (!silent) toast.error('Failed to load feature update progress');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const stopPolling = () => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };

  const startPolling = () => {
    stopPolling();
    pollTimerRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/feature-updates/report/refresh-status`);
        const status = res.data || {};
        setRefreshStatus(status);
        if (!status.running) {
          stopPolling();
          setRefreshing(false);
          // Re-fetch the assembled report so cards update with fresh
          // numbers + the new last_refreshed_at badge.
          await load({ silent: true });
          if ((status.errors || []).length === 0) {
            toast.success('Feature update report refreshed');
          } else {
            toast.warning(`Refresh completed with ${status.errors.length} profile error(s) — see console`);
            console.warn('Feature update refresh errors:', status.errors);
          }
        }
      } catch (e) {
        console.error('refresh-status poll failed:', e);
        stopPolling();
        setRefreshing(false);
      }
    }, 3000);
  };

  const triggerRefresh = async () => {
    setRefreshing(true);
    try {
      const res = await axios.post(`${API}/feature-updates/report/refresh`);
      setRefreshStatus(res.data || null);
      if (res.data?.started === false && res.data?.reason === 'already_running') {
        toast.info('Refresh already in progress — tracking existing run');
      } else {
        toast.info('Refreshing feature update report from Intune…');
      }
      startPolling();
    } catch (e) {
      console.error('Refresh trigger failed:', e);
      toast.error('Could not start refresh — see console');
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    return stopPolling; /* eslint-disable-line */
  }, []);

  if (loading) {
    return (
      <div className="space-y-6" data-testid="feature-update-progress-report">
        <LoadingSpinner message="Loading feature update progress..." />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="space-y-6" data-testid="feature-update-progress-report">
        <Card className="bg-red-500/10 border border-red-500/30">
          <CardContent className="p-6 text-white">
            <p className="font-medium">Could not load feature update progress.</p>
            <p className="text-sm text-white/70 mt-1">{error}</p>
            <Button onClick={() => load()} className="mt-3 bg-red-500/20 border border-red-400/40 text-white hover:bg-red-500/30" data-testid="feature-progress-retry-btn">
              <RefreshCw className="w-4 h-4 mr-2" /> Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const summary = data.summary || {};
  const summaryCounts = summary.status_counts || {};
  const totalDevices = summary.total_devices || 0;
  const overallPct = summary.percent_complete || 0;
  const versions = data.versions || [];
  // `scott/feature-updates-3` — sort profile cards by (deployment, sprint#)
  // so the user sees Test Group → Sprint 1 → Sprint 2 → … in numeric order
  // instead of whatever insertion order the backend happens to return.
  const _phaseRank = (label) => {
    if (!label) return [3, '', 0];           // no phase → bucket 3 (bottom)
    if (/^test\b/i.test(label)) return [0, '', 0];  // Test group always first
    const m = String(label).match(/(?:sprint|phase|wave)\s*(\d+)/i);
    if (m) return [1, '', parseInt(m[1], 10)];      // Sprint N → bucket 1, ordered by N
    return [2, String(label).toLowerCase(), 0];     // other named phase → bucket 2 alpha
  };
  const profiles = (() => {
    const arr = (data.profiles || []).slice();
    arr.sort((a, b) => {
      // Primary: deployment name (alphabetic, missing → end)
      const aDep = (a.deployment_name || '\uffff').toLowerCase();
      const bDep = (b.deployment_name || '\uffff').toLowerCase();
      if (aDep !== bDep) return aDep < bDep ? -1 : 1;
      // Secondary: phase_label bucket → sprint number → alpha → profile name
      const [aBucket, aAlpha, aNum] = _phaseRank(a.phase_label);
      const [bBucket, bAlpha, bNum] = _phaseRank(b.phase_label);
      if (aBucket !== bBucket) return aBucket - bBucket;
      if (aBucket === 1) return aNum - bNum;
      if (aBucket === 2 && aAlpha !== bAlpha) return aAlpha < bAlpha ? -1 : 1;
      return (a.name || '').localeCompare(b.name || '');
    });
    return arr;
  })();
  const isFallback = data.source === 'local_fallback';

  // Human-readable "X ago" for the freshness pill. Backend returns
  // cache_age_seconds (newest profile cache write) on every report load.
  const formatAge = (sec) => {
    if (sec == null) return 'never';
    if (sec < 60) return 'just now';
    if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
    if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
    return `${Math.floor(sec / 86400)}d ago`;
  };
  const lastRefreshedLabel = formatAge(data.cache_age_seconds);
  const isStale = data.cache_age_seconds != null && data.cache_age_seconds > 24 * 3600;

  // October 2026 — profiles split by owner. A profile with a deployment_id
  // belongs to an ALICE deployment (the report joins it in); the rest were
  // created in the tenant's own Intune. Deployments come in from the page so
  // a deployment with no Intune profile yet still gets its block.
  const tenantProfiles = profiles.filter(pr => !pr.deployment_id);
  const aliceBlocks = (() => {
    const byId = new Map();
    (deployments || []).forEach(dep => {
      const phases = [dep.test_group, ...(dep.sprint_waves || [])].filter(Boolean);
      byId.set(String(dep.id), {
        id: String(dep.id),
        name: dep.name || 'Feature update deployment',
        target: dep.target_version_label || dep.target_version || '',
        status: dep.status || '',
        deployment: dep,
        plannedWithoutProfile: phases.filter(ph => ph.enabled !== false && !ph.intune_profile_id).length,
        profiles: [], upgraded: 0, total: 0,
      });
    });
    profiles.filter(pr => pr.deployment_id).forEach(pr => {
      const key = String(pr.deployment_id);
      if (!byId.has(key)) {
        // A profile whose deployment the list did not return (deleted, or
        // the list failed): still shown, without Manage.
        byId.set(key, { id: key, name: pr.deployment_name || 'ALICE deployment', target: pr.target_label || '',
          status: '', deployment: null, plannedWithoutProfile: 0, profiles: [], upgraded: 0, total: 0 });
      }
      const block = byId.get(key);
      block.profiles.push(pr);
      block.upgraded += pr.status_counts?.Upgraded || 0;
      block.total += pr.device_count || 0;
    });
    return [...byId.values()];
  })();

  // One profile card, as Discover drew it. Used for both groups below.
  const renderProfileCard = (p) => {
                const pct = p.percent_complete || 0;
                const isOptional = !!p.is_optional;
                const phaseStatus = p.phase_status; // 'active' | 'pending' | 'completed' | null
                const phaseLabel = p.phase_label;
                const targetedCount = p.targeted_device_count;
                const phaseStatusStyle = phaseStatus === 'active'
                  ? 'bg-green-500/20 text-green-300 border-green-400/40'
                  : phaseStatus === 'completed'
                    ? 'bg-blue-500/20 text-blue-300 border-blue-400/40'
                    : phaseStatus === 'pending'
                      ? 'bg-amber-500/15 text-amber-200 border-amber-400/30'
                      : 'bg-white/10 text-white/70 border-white/20';
                return (
                  <div
                    key={p.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => setDrillIn({ kind: 'profile', id: p.id, title: p.name })}
                    onKeyDown={(e) => { if (e.key === 'Enter') setDrillIn({ kind: 'profile', id: p.id, title: p.name }); }}
                    className="rounded-xl p-5 border border-[rgba(229,239,250,0.1)] bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] cursor-pointer transition-all hover:border-purple-400/40 hover:bg-[rgba(229,239,250,0.28)] focus:outline-none focus:ring-2 focus:ring-purple-400/40"
                    data-testid={`feature-progress-profile-card-${p.id}`}
                  >
                    <div className="flex items-start justify-between gap-4 mb-2 flex-wrap">
                      <div className="min-w-0 flex-1">
                        <p className="text-lg font-semibold text-white truncate" title={p.name}>{p.name}</p>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <span
                            className={`px-2 py-0.5 rounded-full text-[11px] font-semibold uppercase tracking-wider border ${
                              isOptional
                                ? 'bg-blue-500/15 text-blue-300 border-blue-400/30'
                                : 'bg-purple-500/15 text-purple-200 border-purple-400/30'
                            }`}
                            data-testid={`feature-progress-profile-type-${p.id}`}
                          >
                            {isOptional ? 'Optional' : 'Mandatory'}
                          </span>
                          <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-cyan-500/15 text-cyan-200 border border-cyan-400/30">
                            Target: {p.target_label || p.target_version || 'Unknown'}
                          </span>
                          {phaseLabel && (
                            <span
                              className={`px-2 py-0.5 rounded-full text-[11px] font-medium border ${phaseStatusStyle}`}
                              data-testid={`feature-progress-profile-phase-${p.id}`}
                              title={p.deployment_name ? `Deployment: ${p.deployment_name}` : undefined}
                            >
                              {phaseLabel} · {phaseStatus || 'unknown'}
                            </span>
                          )}
                          {targetedCount != null && (
                            <span
                              className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-white/5 text-white/80 border border-white/20"
                              data-testid={`feature-progress-profile-targeted-${p.id}`}
                            >
                              {targetedCount} targeted
                            </span>
                          )}
                          {p.from_cache && (
                            <span className="text-[10px] uppercase tracking-wider text-white/50">cached</span>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-white">{pct.toFixed(0)}%</p>
                        <p className="text-[11px] text-white/60 uppercase tracking-wider">{p.device_count} device{p.device_count === 1 ? '' : 's'}</p>
                      </div>
                    </div>

                    <FeatureSegmentedBar counts={p.status_counts} total={p.device_count} />

                    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-x-3 gap-y-2 mt-3 text-xs">
                      {FEATURE_STATUS_BUCKETS.map(b => (
                        <div key={b.key} className="flex items-center justify-between sm:justify-start sm:flex-col sm:items-start gap-0.5">
                          <span className="flex items-center gap-1.5 text-white/70">
                            <span className="inline-block w-2 h-2 rounded-sm" style={{ backgroundColor: b.color }} />
                            {b.label}
                          </span>
                          <span className="text-white font-semibold">{p.status_counts?.[b.key] || 0}</span>
                        </div>
                      ))}
                    </div>
                    {/* Where these numbers came from. Without this, a card
                        that disagrees with the Intune console gives an admin
                        nothing to go on. */}
                    {(p.status_from_intune > 0 || p.status_from_local_build > 0
                      || p.status_unknown_count > 0 || p.stale_allocation_count > 0
                      || p.intune_report_lagging_count > 0) && (
                      <p
                        className="mt-2 text-[10px] text-white/45 leading-relaxed"
                        data-testid={`feature-progress-profile-provenance-${p.id}`}
                      >
                        {p.status_from_intune > 0 && <>{p.status_from_intune} from the Intune report</>}
                        {p.status_from_local_build > 0 && (
                          <>{p.status_from_intune > 0 ? ' · ' : ''}{p.status_from_local_build} from local OS build</>
                        )}
                        {p.status_unknown_count > 0 && <> · {p.status_unknown_count} no status reported</>}
                        {p.intune_report_lagging_count > 0 && (
                          <> · {p.intune_report_lagging_count} already on target build but not yet marked complete by Intune</>
                        )}
                        {p.stale_allocation_count > 0 && (
                          <> · {p.stale_allocation_count} stale allocation{p.stale_allocation_count === 1 ? '' : 's'} excluded</>
                        )}
                      </p>
                    )}
                    <div className="mt-3 pt-3 border-t border-white/10 text-[11px] text-purple-300 hover:text-purple-200 flex items-center gap-1">
                      <Eye className="w-3 h-3" />
                      View devices
                    </div>
                  </div>
                );
  };

  return (
    <div className="space-y-8" data-testid="feature-update-progress-report">
      {/* -------- Source / freshness banner -------- */}
      {isFallback && (
        <div
          className="rounded-xl border border-amber-400/30 bg-amber-500/10 p-3 text-xs text-amber-200 flex items-start gap-2"
          data-testid="feature-progress-fallback-banner"
        >
          <Info className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            <p className="font-medium">Showing estimated progress from local device inventory.</p>
            <p className="mt-0.5 text-amber-200/90">
              {summary.total_profiles === 0
                ? 'No Windows feature update profiles are configured in this tenant yet — once you create one in Intune, this view will switch to live per-device status from the Intune Reports API automatically.'
                : 'The Intune Reports per-device export didn\'t return — usually a transient timeout. Use Refresh from Intune below to retry. (Permission required is the same DeviceManagementConfiguration.Read.All used for profile sync — no extra scope is needed.)'}
            </p>
          </div>
        </div>
      )}

      {/* -------- Overall completion banner (matches Overview page style) -------- */}
      {/* Overall Feature Update Completion banner — restyled 2026-06-15
          to glass-blue (Apps Management > Discover look). */}
      <div className="p-6 rounded-xl border border-[rgba(229,239,250,0.1)] bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)]" data-testid="feature-progress-overall-banner">
        <div className="flex flex-col lg:flex-row lg:items-center gap-6">
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-white mb-2 flex items-center gap-2">
              <Layers className="w-5 h-5 text-cyan-400" />
              Overall Feature Update Completion
            </h3>
            <div className="flex items-center gap-4">
              <div className="flex-1 h-4 bg-slate-700 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{
                    width: `${overallPct}%`,
                    backgroundColor:
                      overallPct >= 80 ? '#22c55e' :
                      overallPct >= 50 ? '#f59e0b' : '#ef4444',
                  }}
                />
              </div>
              <span className="text-2xl font-bold text-white min-w-[70px]" data-testid="feature-progress-overall-pct">
                {overallPct.toFixed(0)}%
              </span>
            </div>
            <p className="text-sm text-white/90 mt-2">
              {summaryCounts.Upgraded || 0} of {totalDevices} device assignments are on a target feature update version
              <span className="text-white/60"> · counts every sprint, including sprints that haven&apos;t been activated yet</span>
            </p>
          </div>

          <div className="lg:border-l lg:border-[rgba(229,239,250,0.1)] lg:pl-6">
            <h4 className="text-sm font-medium text-white/90 uppercase tracking-wider mb-3">Status Breakdown</h4>
            <div className="grid grid-cols-2 gap-x-6 gap-y-1">
              {FEATURE_STATUS_BUCKETS.map(b => (
                <div key={b.key} className="flex items-center gap-2 text-sm text-white">
                  <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: b.color }} />
                  <span className="font-semibold">{summaryCounts[b.key] || 0}</span>
                  <span className="text-white/70">{b.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* -------- KPI summary row (Profiles, Total Devices, Upgraded, In Flight) --------
           Restyled 2026-06-15 to match Apps Management > Discover (glass-blue
           background, white values + subtitles, crisp Lucide icons). The
           earlier coloured-gradient look made green / amber subtitles
           low-contrast against the gradient. */}
      <div className="alice-kpi-row grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="feature-progress-kpi-row">
        <div
          className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border border-[rgba(229,239,250,0.1)] rounded-xl p-6"
          data-testid="feature-progress-profiles-card"
        >
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm font-medium uppercase tracking-wider text-white/90">Profiles</span>
            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-cyan-400/30 ring-1 ring-cyan-400/50">
              <Layers className="w-5 h-5 text-cyan-400" />
            </div>
          </div>
          <div className="text-4xl font-bold text-white mb-1">{summary.total_profiles || 0}</div>
          <div className="text-sm text-white/90">{versions.length} version{versions.length === 1 ? '' : 's'}</div>
        </div>
        <div
          className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border border-[rgba(229,239,250,0.1)] rounded-xl p-6"
          data-testid="feature-progress-total-devices-card"
        >
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm font-medium uppercase tracking-wider text-white/90">Device Assignments</span>
            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-purple-400/30 ring-1 ring-purple-400/50">
              <Monitor className="w-5 h-5 text-purple-400" />
            </div>
          </div>
          <div className="text-4xl font-bold text-white mb-1">{totalDevices}</div>
          <div className="text-sm text-white/90">Devices may appear in more than one profile</div>
        </div>
        <div
          className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border border-[rgba(229,239,250,0.1)] rounded-xl p-6"
          data-testid="feature-progress-upgraded-card"
        >
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm font-medium uppercase tracking-wider text-white/90">Upgraded</span>
            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-green-400/30 ring-1 ring-green-400/50">
              <CheckCircle className="w-5 h-5 text-green-400" />
            </div>
          </div>
          <div className="text-4xl font-bold text-white mb-1">{summaryCounts.Upgraded || 0}</div>
          <div className="text-sm text-white/90">{overallPct.toFixed(1)}% complete</div>
        </div>
        <div
          className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border border-[rgba(229,239,250,0.1)] rounded-xl p-6"
          data-testid="feature-progress-inflight-card"
        >
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm font-medium uppercase tracking-wider text-white/90">In Flight</span>
            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-amber-400/30 ring-1 ring-amber-400/50">
              <Clock className="w-5 h-5 text-amber-400" />
            </div>
          </div>
          <div className="text-4xl font-bold text-white mb-1">
            {(summaryCounts.Offering || 0) + (summaryCounts.InProgress || 0) + (summaryCounts.Pending || 0)}
          </div>
          <div className="text-sm text-white/90">
            {(summaryCounts.Error || 0) + (summaryCounts.Cancelled || 0)} errored / cancelled
            {/* Unknown is deliberately NOT counted as in-flight: we have no
                status for those devices, so calling them in-flight would be
                inventing progress. Shown separately instead. */}
            {(summaryCounts.Unknown || 0) > 0 && (
              <> · {summaryCounts.Unknown} unknown</>
            )}
          </div>
        </div>
      </div>

      {/* -------- Per-Windows-Version Cards --------
           Outer wrapper is transparent so the inner tiles read as
           standalone glass-blue cards matching the KPI row above. */}
      <Card className="bg-transparent border-0 shadow-none" data-testid="feature-progress-versions-section">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <CardTitle className="text-white flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-cyan-400" />
              Progress by Windows Version
            </CardTitle>
            <div className="flex items-center gap-3 flex-wrap">
              <span
                className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border ${
                  isStale
                    ? 'border-amber-400/40 bg-amber-500/10 text-amber-200'
                    : 'border-white/10 bg-white/5 text-white/70'
                }`}
                title={data.last_refreshed_at ? `Last refreshed ${data.last_refreshed_at}` : 'Not refreshed yet'}
                data-testid="feature-progress-freshness-pill"
              >
                <span className={`w-1.5 h-1.5 rounded-full ${isStale ? 'bg-amber-400' : 'bg-emerald-400'}`}></span>
                Last refreshed: {lastRefreshedLabel}
              </span>
              <Button
                onClick={triggerRefresh}
                disabled={refreshing}
                size="sm"
                className="bg-cyan-500/20 border border-cyan-400/40 text-cyan-100 hover:bg-cyan-500/30"
                data-testid="feature-progress-refresh-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                {refreshing
                  ? (refreshStatus && refreshStatus.total_profiles
                      ? `Refreshing ${refreshStatus.completed_profiles || 0}/${refreshStatus.total_profiles}…`
                      : 'Refreshing…')
                  : 'Refresh from Intune'}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {versions.length === 0 ? (
            <p className="text-white/70 text-center py-6" data-testid="feature-progress-no-versions">
              No feature update profiles found. Sync from Intune to populate this report.
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="feature-progress-version-grid">
              {versions.map(v => {
                const pct = v.percent_complete || 0;
                const barColor = pct >= 80 ? '#22c55e' : pct >= 50 ? '#f59e0b' : pct > 0 ? '#06b6d4' : '#64748b';
                return (
                  <div
                    key={v.label}
                    role="button"
                    tabIndex={0}
                    onClick={() => setDrillIn({ kind: 'version', label: v.label, title: `${v.os} ${v.version}` })}
                    onKeyDown={(e) => { if (e.key === 'Enter') setDrillIn({ kind: 'version', label: v.label, title: `${v.os} ${v.version}` }); }}
                    className="rounded-xl p-5 border border-[rgba(229,239,250,0.1)] bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] cursor-pointer transition-all hover:border-cyan-400/40 hover:bg-[rgba(229,239,250,0.28)] focus:outline-none focus:ring-2 focus:ring-cyan-400/40"
                    data-testid={`feature-progress-version-card-${v.version}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <p className="text-xs uppercase tracking-wider text-white/60">{v.os}</p>
                        <p className="text-2xl font-bold text-white leading-tight">{v.version}</p>
                        <p className="text-xs text-white/60 mt-0.5">
                          {v.profile_count} profile{v.profile_count === 1 ? '' : 's'} · {v.device_count} device{v.device_count === 1 ? '' : 's'}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-3xl font-bold" style={{ color: FIGURE_INK[barColor] || barColor }}>{pct.toFixed(0)}%</p>
                        <p className="text-[10px] uppercase tracking-wider text-white/50">complete</p>
                      </div>
                    </div>

                    <FeatureSegmentedBar counts={v.status_counts} total={v.device_count} />

                    <div className="grid grid-cols-2 gap-x-3 gap-y-1 mt-3 text-xs">
                      {FEATURE_STATUS_BUCKETS.map(b => (
                        <div key={b.key} className="flex items-center justify-between">
                          <span className="flex items-center gap-1.5 text-white/80">
                            <span className="inline-block w-2 h-2 rounded-sm" style={{ backgroundColor: b.color }} />
                            {b.label}
                          </span>
                          <span className="text-white font-medium">{v.status_counts?.[b.key] || 0}</span>
                        </div>
                      ))}
                    </div>
                    <div className="mt-3 pt-3 border-t border-white/10 text-[11px] text-cyan-300 hover:text-cyan-200 flex items-center gap-1">
                      <Eye className="w-3 h-3" />
                      View devices
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* -------- Profiles, grouped (October 2026) --------
          This report moved from Discover to Deployments > Feature Update
          Deployments. Profiles are split by owner: ALICE's own deployments
          (a profile with a deployment_id), each under its deployment with a
          Manage toggle for the deployment's actions, and profiles the tenant
          created directly in Intune. Same card, same drill-in, both groups. */}
      <Card className="bg-transparent border-0 shadow-none" data-testid="feature-progress-alice-section">
        <CardHeader className="pb-2">
          <CardTitle className="text-white flex items-center gap-2">
            <Rocket className="w-5 h-5 text-purple-400" />
            ALICE deployments
          </CardTitle>
          <p className="text-sm text-white/70 mt-1">
            Staged and App Risk deployments created in ALICE, one block per deployment with a card per sprint.
          </p>
        </CardHeader>
        <CardContent>
          {aliceBlocks.length === 0 ? (
            <p className="text-white/70 text-center py-6" data-testid="feature-progress-no-alice">
              No ALICE feature update deployments yet.
            </p>
          ) : (
            <div className="space-y-6" data-testid="feature-progress-alice-blocks">
              {aliceBlocks.map(block => {
                const isManaging = !!managing[block.id];
                const blockPct = block.total > 0 ? (block.upgraded / block.total) * 100 : 0;
                const blockColor = blockPct >= 80 ? '#22c55e' : blockPct >= 50 ? '#f59e0b' : blockPct > 0 ? '#06b6d4' : '#64748b';
                return (
                  <div
                    key={block.id}
                    className="rounded-2xl border border-purple-400/20 bg-[rgba(139,92,246,0.06)] p-4"
                    data-testid={`feature-progress-deployment-${block.id}`}
                  >
                    <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
                      <div className="min-w-0">
                        <p className="text-lg font-semibold text-white truncate" title={block.name}>{block.name}</p>
                        <div className="flex items-center gap-2 mt-1 flex-wrap text-[11px]">
                          {block.target && (
                            <span className="px-2 py-0.5 rounded-full font-medium bg-cyan-500/15 text-cyan-200 border border-cyan-400/30">
                              Target: {block.target}
                            </span>
                          )}
                          {block.status && (
                            <span className="px-2 py-0.5 rounded-full font-medium bg-white/10 text-white/80 border border-white/20">
                              {block.status}
                            </span>
                          )}
                          {block.plannedWithoutProfile > 0 && (
                            <span className="px-2 py-0.5 rounded-full font-medium bg-amber-500/15 text-amber-200 border border-amber-400/30"
                                  title="Sprints that have not been created in Intune yet, so have no profile to report on">
                              {block.plannedWithoutProfile} sprint{block.plannedWithoutProfile === 1 ? '' : 's'} not started
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-2xl font-bold" style={{ color: FIGURE_INK[blockColor] || blockColor }}>{blockPct.toFixed(0)}%</p>
                          <p className="text-[10px] uppercase tracking-wider text-white/50">
                            {block.upgraded} / {block.total} upgraded
                          </p>
                        </div>
                        {block.deployment && renderManage && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setManaging(prev => ({ ...prev, [block.id]: !prev[block.id] }))}
                            className={isManaging
                              ? 'border-purple-400 bg-purple-500/20 text-purple-100 hover:bg-purple-500/30'
                              : 'border-purple-400/40 text-purple-200 hover:bg-purple-500/15'}
                            data-testid={`feature-progress-manage-${block.id}`}
                          >
                            <Settings className="w-4 h-4 mr-1.5" />
                            {isManaging ? 'Hide management' : 'Manage'}
                          </Button>
                        )}
                      </div>
                    </div>
                    {/* The deployment's own card, unchanged: sprint timeline,
                        advance / reconcile / move / approve / schedule /
                        installation type / announcements / delete. */}
                    {isManaging && block.deployment && renderManage && (
                      <div className="mb-4" data-testid={`feature-progress-manage-panel-${block.id}`}>
                        {renderManage(block.deployment)}
                      </div>
                    )}
                    {block.profiles.length === 0 ? (
                      <p className="text-sm text-white/60 py-2">
                        No Intune profiles created for this deployment yet{block.deployment && renderManage ? ' — use Manage to see its sprints.' : '.'}
                      </p>
                    ) : (
                      <div className="space-y-4">
                        {block.profiles.map(renderProfileCard)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="bg-transparent border-0 shadow-none" data-testid="feature-progress-tenant-section">
        <CardHeader className="pb-2">
          <CardTitle className="text-white flex items-center gap-2">
            <Activity className="w-5 h-5 text-cyan-400" />
            Tenant feature update profiles (synced from Intune)
          </CardTitle>
          <p className="text-sm text-white/70 mt-1">
            Profiles created directly in Intune rather than by an ALICE deployment.
          </p>
        </CardHeader>
        <CardContent>
          {tenantProfiles.length === 0 ? (
            <p className="text-white/70 text-center py-6" data-testid="feature-progress-no-tenant-profiles">
              No feature update profiles outside ALICE deployments.
            </p>
          ) : (
            <div className="space-y-4" data-testid="feature-progress-profile-list">
              {tenantProfiles.map(renderProfileCard)}
            </div>
          )}
          <FeatureStatusLegend />
        </CardContent>
      </Card>

      {/* -------- Stuck Devices Section -------- */}
      <FeatureStuckDevicesSection
        devices={data.stuck_devices || []}
        summary={summary.stuck || { total: 0, by_status: { Error: 0, InProgress: 0, Pending: 0 } }}
        isFallback={isFallback}
      />

      {drillIn && (
        <FeatureUpdateDeviceDrilldown
          drillIn={drillIn}
          onClose={() => setDrillIn(null)}
        />
      )}
    </div>
  );
};




// Discover tab ids, as carried in `?tab=`. `overview` and `device-health`
// were merged into `devices` in October 2026 (Overview renamed Devices, Device
// Health folded into it); old links and bookmarks resolve to the new tab
// rather than falling through to nothing. Unknown values go to the default.
const DISCOVER_TAB_IDS = ['devices', 'compliance', 'firmware', 'win11-readiness'];
// Moved to Deployments in October 2026 (6 Oct): an old Discover link to
// either redirects to the matching Deployments tab.
const MOVED_DISCOVER_TABS = { 'update-rings': 'update-rings', 'feature-updates': 'feature-updates' };
const LEGACY_DISCOVER_TAB_IDS = { overview: 'devices', 'device-health': 'devices' };
const resolveDiscoverTab = (raw) => {
  const tab = LEGACY_DISCOVER_TAB_IDS[raw] || raw;
  return DISCOVER_TAB_IDS.includes(tab) ? tab : 'devices';
};

// =============================================================================
// Device issues → suggested remediation (October 2026)
// -----------------------------------------------------------------------------
// One place that decides what is wrong with a device and which catalog script
// fixes it. The Devices tab uses it for the Flags column, the Issues strip and
// the detail panel, so the three never disagree. Every rule reads fields the
// Devices tab already has (device-health rows joined to /devices); nothing here
// calls Intune.
//
// `catalogKey` is only the recommendation — the run modal defaults to it and
// lets the admin pick any other catalog or custom script. Thresholds are the
// existing ones, not new ones: low disk is FU_MIN_DISK_GB (the feature update
// eligibility gate, and the free_disk_space script's own detection threshold),
// "checking in" is the Online definition the old Devices page used (an Intune
// sync in the last 24 hours), and stuck is the backend's 48-hour rule.
// =============================================================================
const ONLINE_WINDOW_MS = 24 * 60 * 60 * 1000;
const STALE_WU_SCAN_MS = 7 * 24 * 60 * 60 * 1000;
const FIRMWARE_BEHIND_STATUSES = ['missing_one', 'missing_multiple'];
// Above this many devices, a run needs the count typed back to confirm it.
const LARGE_REMEDIATION_RUN = 10;

const _msSince = (iso) => {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isNaN(t) ? null : Date.now() - t;
};
const isRecentlySynced = (d) => {
  const ms = _msSince(d.last_sync);
  return ms !== null && ms <= ONLINE_WINDOW_MS;
};

const DEVICE_ISSUES = {
  failed: {
    label: 'Update failed', icon: AlertCircle, cls: 'text-red-300',
    catalogKey: 'reset_wu_components_restart',
    why: 'Windows Update reports a failed install. Resetting the update components and restarting lets it retry from a clean stack.',
  },
  stuck: {
    label: 'Stuck in an update state', icon: Clock, cls: 'text-purple-300',
    catalogKey: 'force_wu_scan',
    why: 'In the same Windows Update state for 48 hours or more. A fresh scan and install usually moves it on.',
  },
  low_disk: {
    label: 'Low disk space', icon: HardDrive, cls: 'text-amber-300',
    catalogKey: 'free_disk_space',
    why: `Less than ${FU_MIN_DISK_GB} GB free — not enough for feature updates, and often the reason quality updates stall.`,
  },
  reboot_pending: {
    label: 'Restart pending', icon: RefreshCw, cls: 'text-amber-300',
    catalogKey: 'complete_pending_reboot',
    why: 'Updates are installed but waiting on a restart.',
  },
  behind_checking_in: {
    label: 'Behind on updates, checking in', icon: AlertTriangle, cls: 'text-amber-300',
    catalogKey: 'reset_wu_components',
    why: 'Checked in to Intune in the last 24 hours but still not on the latest security update, so it is reachable and Windows Update itself is the likely problem.',
  },
  stale_scan: {
    label: 'No Windows Update scan in 7 days', icon: Search, cls: 'text-cyan-300',
    catalogKey: 'force_wu_scan',
    why: 'Checking in to Intune but not scanning for updates.',
  },
  firmware: {
    label: 'BIOS update available', icon: Cpu, cls: 'text-amber-300',
    catalogKey: null, // Firmware ships as a package, not a script — links to the Firmware tab.
    why: 'The OEM catalogue has a newer BIOS for this model.',
  },
};
const DEVICE_ISSUE_ORDER = Object.keys(DEVICE_ISSUES);

// Returns the issue codes for one joined device row, most specific first.
// Failed and stuck suppress "behind but checking in" and "stale scan": they
// are the more precise diagnosis of the same symptom, and recommending two
// different Windows Update scripts for one device helps nobody.
const detectDeviceIssues = (d) => {
  const issues = [];
  const failed = Boolean(d.is_failed ?? d.security_status === 'failed');
  if (failed) issues.push('failed');
  if (d.is_stuck) issues.push('stuck');
  if (typeof d.free_storage_gb === 'number' && d.free_storage_gb < FU_MIN_DISK_GB) issues.push('low_disk');
  if (d.client_state === 'pending_reboot') issues.push('reboot_pending');
  const checkingIn = isRecentlySynced(d);
  if (!failed && !d.is_stuck && checkingIn && d.security_status === 'outdated') issues.push('behind_checking_in');
  const scanAge = _msSince(d.last_wu_scan);
  if (!failed && !d.is_stuck && checkingIn && scanAge !== null && scanAge > STALE_WU_SCAN_MS) issues.push('stale_scan');
  if (FIRMWARE_BEHIND_STATUSES.includes(d.firmware_status)) issues.push('firmware');
  return issues;
};

// =============================================================================
// Devices banner: Overview / Fix Queue / Cyber Essentials Breach (October 2026)
// -----------------------------------------------------------------------------
// The Fix Queue is the same device list narrowed to devices that need
// something done, in six groups an admin can work through one at a time. Every
// group is a view over `detectDeviceIssues` and the health row's own fields, so
// the queue, the Actions column and the device panel can never disagree.
//
// `issueCodes` names the DEVICE_ISSUES whose remediation belongs to the group:
// the group's action panel offers one run per code, because a Failed update
// (reset and restart) and a Stuck one (force a scan) need different scripts and
// one default for a mixed group would be wrong for half of it.
// =============================================================================
const STALE_DEVICE_MS = 7 * 24 * 60 * 60 * 1000;
// No Intune check-in for a week. A device that never reported has no timestamp
// to measure, so it is not called stale: that is a missing record, not a
// quiet device.
const isStaleDevice = (d) => {
  const ms = _msSince(d.last_sync);
  return ms !== null && ms > STALE_DEVICE_MS;
};

const FIX_QUEUE_GROUPS = {
  not_current: {
    label: 'Not current', icon: Clock, cls: 'text-amber-300',
    why: 'Not on the latest monthly security update.',
    member: (d) => d.security_status !== 'current',
    issueCodes: ['behind_checking_in'],
  },
  feature_update: {
    label: 'Feature update required', icon: Rocket, cls: 'text-cyan-300',
    why: 'Targeted by an ALICE feature update deployment and not yet on its target release.',
    member: (d) => !!d.feature_update_pending,
    issueCodes: [],
  },
  firmware: {
    label: 'Firmware update available', icon: Cpu, cls: 'text-purple-300',
    why: 'The OEM catalogue has a newer BIOS for this model.',
    member: (d) => FIRMWARE_BEHIND_STATUSES.includes(d.firmware_status),
    issueCodes: ['firmware'],
  },
  stale: {
    label: 'Stale devices', icon: Clock, cls: 'text-white/70',
    why: 'No Intune check-in for 7 days. ALICE cannot run anything on a device that is not reporting in.',
    member: (d) => isStaleDevice(d),
    issueCodes: [],
  },
  wu_issue: {
    label: 'Windows Update issue', icon: AlertCircle, cls: 'text-red-300',
    why: 'Failed, stuck, waiting on a restart, or checking in without scanning for updates.',
    member: (d, codes) => ['failed', 'stuck', 'reboot_pending', 'stale_scan'].some(c => codes.includes(c)),
    issueCodes: ['failed', 'stuck', 'reboot_pending', 'stale_scan'],
  },
  low_disk: {
    label: 'Low disk space', icon: HardDrive, cls: 'text-amber-300',
    why: `Less than ${FU_MIN_DISK_GB} GB free: not enough for a feature update, and often why quality updates stall.`,
    member: (d, codes) => codes.includes('low_disk'),
    issueCodes: ['low_disk'],
  },
};
const FIX_QUEUE_ORDER = Object.keys(FIX_QUEUE_GROUPS);

// Which Fix Queue groups a device belongs to (empty when nothing needs doing).
const fixQueueGroupsFor = (d, issueCodes) => (
  FIX_QUEUE_ORDER.filter(key => FIX_QUEUE_GROUPS[key].member(d, issueCodes))
);

// Why a device is in the Cyber Essentials view. Reasons come from the backend
// (`ce_reasons`) so the rule has one home, windows_lifecycle_service.
const CE_REASONS = {
  os_unsupported: { label: 'OS out of support', cls: 'text-red-300', tone: 'breach',
    why: 'This Windows release is no longer serviced for the device\'s edition, so it receives no security updates.' },
  not_current: { label: 'Not on the latest update', cls: 'text-red-300', tone: 'breach',
    why: 'Not on the latest monthly security update, and that update has been out for more than the 14-day window.' },
  patch_window: { label: 'Inside the 14-day window', cls: 'text-amber-300', tone: 'risk',
    why: 'One update behind, but the latest update was released within the last 14 days.' },
  support_ending: { label: 'Support ending soon', cls: 'text-amber-300', tone: 'risk',
    why: 'Still supported, but support ends within 90 days. Plan the feature update now.' },
  esu: { label: 'Extended Security Updates', cls: 'text-cyan-300', tone: 'info',
    why: 'Past end of support, but on a monthly update only Extended Security Updates provide, so it is judged on the monthly update alone.' },
};
const CE_REASON_ORDER = Object.keys(CE_REASONS);

// An API error as display text. FastAPI's `detail` can be a string, an
// object (our 502s) or a list (422 validation) — rendering a non-string as a
// React child throws, so everything shown goes through this.
const _errText = (e, fallback) => {
  const detail = e?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail.message === 'string') return detail.message;
  return fallback;
};

// The Intune managed-device id, which is what run states are keyed on. Device
// documents use it as their id; `intune_id` overrides when present.
const remediationTargetOf = (d) => ({
  device_id: d.intune_id || d.id,
  azure_ad_device_id: d.azure_ad_device_id,
  device_name: d.device_name,
});

// Typed confirmation for large runs: the admin types the device count back.
// A click is too cheap a gesture for a script that resets Windows Update (or
// restarts machines) across a large slice of an estate.
const LargeRunConfirm = ({ count, value, onChange, testId = 'large-run-confirm' }) => (
  <div className="rounded-lg border border-amber-400/40 bg-amber-500/10 p-3" data-testid={testId}>
    <p className="text-sm text-amber-100">
      This targets <span className="font-semibold">{count} devices</span>. Type <span className="font-mono font-semibold">{count}</span> to confirm.
    </p>
    <Input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      inputMode="numeric"
      placeholder={String(count)}
      className="mt-2 w-32 bg-slate-950 border-[rgba(229,239,250,0.15)] text-white"
      data-testid={`${testId}-input`}
    />
  </div>
);
const largeRunConfirmed = (count, typed) => count <= LARGE_REMEDIATION_RUN || String(typed).trim() === String(count);

const _critOf = (cat) => (
  cat === 'red' || cat === 'high' ? { label: 'High', cls: 'bg-red-500/20 text-red-400', dot: 'bg-red-400' }
    : cat === 'amber' || cat === 'medium' ? { label: 'Medium', cls: 'bg-amber-500/20 text-amber-400', dot: 'bg-amber-400' }
      : cat === 'green' || cat === 'low' ? { label: 'Low', cls: 'bg-green-500/20 text-green-400', dot: 'bg-green-400' }
        : { label: 'Uncategorised', cls: 'bg-white/10 text-white/60', dot: 'bg-white/30' }
);

const _relTime = (iso) => {
  const ms = _msSince(iso);
  if (ms === null) return null;
  const mins = Math.round(ms / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 48) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
};

// =============================================================================
// Device exclusions (October 2026): shared pieces for the Excluded view, the
// device panel's Governance section and the exclude dialog. The policy and what
// each scope does to Intune are explained in backend/services/device_exclusion_service.py.
// =============================================================================
const EXCLUSION_SCOPES = [
  { key: 'reporting', label: 'Reporting', writes: false,
    hint: 'Left out of the Devices cards, Fix Queue and Cyber Essentials. Changes nothing in Intune.' },
  { key: 'quality_updates', label: 'Quality updates', writes: true,
    hint: "Removed from ALICE's update ring groups. The device gets no ALICE ring, so it follows whatever else Intune applies. Lifted exclusions return at the next nightly sync." },
  { key: 'feature_updates', label: 'Feature updates', writes: true,
    hint: 'Left out of ALICE feature update deployments and taken out of any live sprint.' },
];
const EXCLUSION_SCOPE_LABEL = Object.fromEntries(EXCLUSION_SCOPES.map(s => [s.key, s.label]));

const ExclusionScopePicker = ({ value, onChange, canWrite, idPrefix = 'excl-scope' }) => (
  <div className="space-y-2">
    {EXCLUSION_SCOPES.map(scope => {
      const disabled = scope.writes && !canWrite;
      const checked = value.includes(scope.key);
      return (
        <label key={scope.key}
               className={`flex items-start gap-2 text-sm ${disabled ? 'opacity-50' : 'cursor-pointer'}`}
               data-testid={`${idPrefix}-${scope.key}`}>
          <input type="checkbox" className="accent-cyan-400 mt-1" checked={checked} disabled={disabled}
                 onChange={(e) => onChange(e.target.checked ? [...value, scope.key] : value.filter(k => k !== scope.key))} />
          <span>
            <span className="text-white">{scope.label}</span>
            <span className="block text-xs text-white/60">
              {scope.hint}{disabled ? ' Not included in this plan.' : ''}
            </span>
          </span>
        </label>
      );
    })}
  </div>
);

const ExclusionScopeChips = ({ scopes }) => (
  <span className="inline-flex flex-wrap gap-1">
    {scopes.map(s => (
      <span key={s} className="px-2 py-0.5 rounded-full text-[11px] bg-white/10 text-white/90">{EXCLUSION_SCOPE_LABEL[s] || s}</span>
    ))}
  </span>
);

// Exclude (or stop excluding) a set of devices. `stop` clears every scope.
// A change that reaches Intune (rings, feature updates) on more than
// LARGE_REMEDIATION_RUN devices needs the count typed back, as remediation does.
const ExcludeDevicesModal = ({ devices, stop = false, canWrite, onClose, onSaved }) => {
  const only = devices.length === 1 ? devices[0] : null;
  const existing = (only?.excluded_sources || []).find(s => s.kind === 'device');
  const [scopes, setScopes] = useState(stop ? [] : (existing?.scopes || ['reporting']));
  const [reason, setReason] = useState(existing?.reason || '');
  const [typed, setTyped] = useState('');
  const [saving, setSaving] = useState(false);
  const reachesIntune = scopes.some(s => s !== 'reporting');
  const confirmed = stop || !reachesIntune || largeRunConfirmed(devices.length, typed);

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/exclusions/devices`, {
        device_ids: devices.map(d => d.intune_id || d.id),
        scopes: stop ? [] : scopes,
        reason,
      });
      const warnings = res.data?.effects?.warnings || [];
      if (warnings.length) toast.warning(warnings.join(' '));
      else toast.success(stop ? 'Exclusion removed' : `Excluded ${res.data.updated} device${res.data.updated === 1 ? '' : 's'}`);
      onSaved();
    } catch (e) {
      toast.error(_errText(e, 'Could not save the exclusion'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[60] p-4"
         onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
         data-testid="exclude-devices-modal">
      <div className="alice-popup bg-slate-900 border border-white/10 rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="flex items-start justify-between gap-4 p-5 border-b border-white/10">
          <div>
            <p className="text-xs uppercase tracking-wider text-white/50">Excluded devices</p>
            <h2 className="text-xl font-semibold text-white">
              {stop ? 'Stop excluding' : 'Exclude'} {only ? only.device_name : `${devices.length} devices`}
            </h2>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} className="text-white/60 hover:text-white"><X className="w-4 h-4" /></Button>
        </div>
        <div className="flex-1 overflow-auto p-5 space-y-4">
          {stop ? (
            <p className="text-sm text-white/80">
              These devices go back into reporting, update rings and feature update deployments. Ring
              membership returns at the next nightly sync; feature update devices that ALICE took out of a
              sprint are put back now. Exclusions that come from a device-type rule stay until the rule is changed.
            </p>
          ) : (
            <>
              <p className="text-sm text-white/80">Choose what ALICE should leave these devices out of.</p>
              <ExclusionScopePicker value={scopes} onChange={setScopes} canWrite={canWrite} />
              <div>
                <label className="text-xs uppercase tracking-wider text-white/50">Reason (optional)</label>
                <Input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={300}
                       placeholder="e.g. AVD session host, rebuilt from image"
                       className="mt-1 bg-slate-950 border-[rgba(229,239,250,0.15)] text-white"
                       data-testid="exclude-reason" />
              </div>
            </>
          )}
          {reachesIntune && !stop && (
            <p className="text-xs text-amber-200/90">
              Quality and feature update exclusions change Intune group membership straight away.
            </p>
          )}
          {reachesIntune && !stop && devices.length > LARGE_REMEDIATION_RUN && (
            <LargeRunConfirm count={devices.length} value={typed} onChange={setTyped} testId="exclude-large-confirm" />
          )}
        </div>
        <div className="flex justify-end gap-2 p-4 border-t border-white/10">
          <Button variant="ghost" onClick={onClose} className="text-white/70 hover:text-white">Cancel</Button>
          <Button onClick={save} disabled={saving || !confirmed || (!stop && scopes.length === 0)}
                  className="bg-[#E5EFFA] text-[#3C02CC] hover:bg-[#BBFF71] hover:text-[#3C02CC] font-semibold"
                  data-testid="exclude-save">
            {saving ? 'Saving…' : stop ? 'Stop excluding' : 'Save exclusion'}
          </Button>
        </div>
      </div>
    </div>
  );
};

// Device-type rules: every device of a virtual type. Physical devices cannot
// be excluded this way. Applied per row, with a confirmation that names the
// count, because a type can cover hundreds of devices.
const ExcludedTypeRules = ({ policy, canWrite, canEdit, onChanged }) => {
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(null);
  if (!policy) return <p className="text-sm text-white/60" data-testid="type-rules-loading">Loading…</p>;
  const save = async (row) => {
    const scopes = draft[row.category] ?? row.scopes;
    const writes = scopes.some(s => s !== 'reporting');
    if (writes && !window.confirm(
      `Exclude all ${row.device_count} ${row.label} devices from ${scopes.map(s => EXCLUSION_SCOPE_LABEL[s]).join(' and ')}? `
      + 'Quality and feature update exclusions change Intune group membership straight away.')) return;
    setBusy(row.category);
    try {
      const res = await axios.put(`${API}/exclusions/types/${row.category}`, { scopes, reason: row.reason || '' });
      const warnings = res.data?.effects?.warnings || [];
      if (warnings.length) toast.warning(warnings.join(' '));
      else toast.success(scopes.length ? `${row.label} excluded` : `${row.label} no longer excluded`);
      setDraft(d => { const n = { ...d }; delete n[row.category]; return n; });
      onChanged();
    } catch (e) {
      toast.error(_errText(e, 'Could not save the rule'));
    } finally {
      setBusy(null);
    }
  };
  return (
    <div className="space-y-3" data-testid="type-rules">
      {policy.types.map(row => {
        const value = draft[row.category] ?? row.scopes;
        const dirty = draft[row.category] !== undefined
          && JSON.stringify([...draft[row.category]].sort()) !== JSON.stringify([...row.scopes].sort());
        return (
          <div key={row.category} className="rounded-lg border border-white/10 bg-white/5 p-3" data-testid={`type-rule-${row.category}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-white font-medium">{row.label} <span className="text-white/60 font-normal">· {row.device_count} device{row.device_count === 1 ? '' : 's'}</span></p>
                <p className="text-xs text-white/60 max-w-xl">{row.description}</p>
              </div>
              {canEdit && (
                <Button size="sm" disabled={!dirty || busy === row.category} onClick={() => save(row)}
                        className="bg-[#E5EFFA] text-[#3C02CC] hover:bg-[#BBFF71] hover:text-[#3C02CC] font-semibold"
                        data-testid={`type-rule-save-${row.category}`}>
                  {busy === row.category ? 'Saving…' : 'Apply'}
                </Button>
              )}
            </div>
            {canEdit ? (
              <div className="mt-2">
                <ExclusionScopePicker value={value} canWrite={canWrite} idPrefix={`type-${row.category}`}
                                      onChange={(next) => setDraft(d => ({ ...d, [row.category]: next }))} />
              </div>
            ) : (
              <p className="mt-2 text-xs text-white/60">
                {row.scopes.length ? <>Excluded from <ExclusionScopeChips scopes={row.scopes} /></> : 'Not excluded.'}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
};

// =============================================================================
// DeviceDetailPanel — right-hand panel opened from a Devices row (?device=<id>)
// -----------------------------------------------------------------------------
// A panel rather than a page so the list, its filters and its scroll position
// survive working down a list of devices one at a time. Everything here is
// already on the joined row except remediation history, fetched on open.
// =============================================================================
const DetailRow = ({ label, children, testId }) => (
  <div className="flex items-start justify-between gap-4 py-1.5 text-sm" data-testid={testId}>
    <span className="text-white/60 shrink-0">{label}</span>
    <span className="text-white text-right min-w-0 break-words">{children}</span>
  </div>
);

const DetailSection = ({ title, children }) => (
  <div className="pt-4 mt-4 border-t border-white/10">
    <h4 className="text-xs uppercase tracking-wider text-white/50 mb-2">{title}</h4>
    {children}
  </div>
);

const DeviceDetailPanel = ({
  device, open, onOpenChange, canRemediate, canEditSprint, policiesAvailable,
  pendingPhase, onStageChange, onRemediate, onViewFirmware, onViewRun, wufbConnected,
  getFriendlyName, refreshKey, canExclude, onManageExclusions,
}) => {
  const [runs, setRuns] = useState(null);
  const [runsError, setRunsError] = useState(null);
  const targetId = device ? (device.intune_id || device.id) : null;

  useEffect(() => {
    if (!open || !targetId) return undefined;
    let cancelled = false;
    setRuns(null);
    setRunsError(null);
    // Remediation history needs the policy plan (the endpoint refuses others).
    if (!policiesAvailable) { setRuns([]); return undefined; }
    osPatchApi.getRemediationRuns({ device_id: targetId, limit: 20 })
      .then((res) => { if (!cancelled) setRuns(res.data?.runs || []); })
      .catch((e) => {
        if (!cancelled) {
          setRuns([]);
          setRunsError(_errText(e, 'Could not load remediation history'));
        }
      });
    return () => { cancelled = true; };
  }, [open, targetId, policiesAvailable, refreshKey]);

  if (!device) return null;
  const issues = detectDeviceIssues(device);
  const crit = _critOf(device.app_risk_category);
  const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : null);

  // Non-modal on purpose (review, 05/10). The stock SheetContent is a modal
  // dialog: its overlay swallowed a click on the next row (closing the panel
  // instead of switching device) and its page-wide pointer lock could make the
  // "View run" toast unclickable. Here there is no overlay; clicks on the
  // device table or a toast don't dismiss it; Escape, the X, or a click
  // anywhere else still close it.
  const keepOpenFor = (e) => {
    const t = e.target;
    if (t && t.closest && (t.closest('[data-testid="devices-table"]') || t.closest('[data-sonner-toaster]'))) {
      e.preventDefault();
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange} modal={false}>
      <SheetPortal>
      <DialogPrimitive.Content
        onInteractOutside={keepOpenFor}
        className="alice-popup fixed z-50 inset-y-0 right-0 h-full w-full sm:max-w-lg overflow-y-auto bg-[#0F172A] text-white border-l border-white/10 p-6 shadow-2xl transition ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:duration-300 data-[state=open]:duration-500 data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right"
        data-testid="device-detail-panel"
      >
        <SheetClose
          className="absolute right-4 top-4 rounded-sm text-white/70 hover:text-white focus:outline-none focus:ring-2 focus:ring-cyan-400"
          data-testid="device-detail-close"
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </SheetClose>
        <SheetHeader className="pb-3 border-b border-white/10 text-left">
          <SheetTitle className="text-white text-xl pr-6 break-words">{device.device_name}</SheetTitle>
          <SheetDescription className="text-white/70">
            {[device.user_name, [device.manufacturer, device.model].filter(Boolean).join(' ')].filter(Boolean).join(' · ') || '—'}
          </SheetDescription>
          <div className="flex flex-wrap items-center gap-2 pt-2">
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
              device.security_status === 'current' ? 'bg-green-500/20 text-green-400'
                : device.security_status === 'failed' ? 'bg-red-500/20 text-red-400'
                  : 'bg-amber-500/20 text-amber-400'
            }`}>
              {device.security_status === 'current' ? 'Current'
                : device.security_status === 'failed' ? 'Failed'
                  : device.updates_behind > 1 ? `${device.updates_behind} months behind` : '1 month behind'}
            </span>
            {device.client_state && device.client_state !== 'unknown' && (
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${CLIENT_STATE_CHIP[device.client_state] || 'bg-white/10 text-white/80'}`}>
                {CLIENT_STATE_LABELS[device.client_state] || device.client_state.replace(/_/g, ' ')}
                {device.is_stuck && ` · ${Math.round(device.hours_in_state)}h`}
              </span>
            )}
          </div>
        </SheetHeader>

        {/* Issues first: it is why the admin opened the panel. */}
        <DetailSection title={`Issues (${issues.length})`}>
          {issues.length === 0 ? (
            <p className="text-sm text-white/60" data-testid="device-detail-no-issues">No issues detected.</p>
          ) : (
            <div className="space-y-3">
              {issues.map((code) => {
                const def = DEVICE_ISSUES[code];
                const Icon = def.icon;
                return (
                  <div key={code} className="rounded-lg border border-white/10 bg-white/[0.03] p-3" data-testid={`device-issue-${code}`}>
                    <div className="flex items-start gap-2">
                      <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${def.cls}`} />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-white">
                          {def.label}
                          {code === 'low_disk' && ` (${device.free_storage_gb} GB free)`}
                        </p>
                        <p className="text-xs text-white/60 mt-0.5">{def.why}</p>
                        {code === 'firmware' ? (
                          <Button size="sm" variant="outline" onClick={() => onViewFirmware(device)}
                            className="mt-2 h-7 text-xs border-white/20 text-white hover:bg-white/10"
                            data-testid="device-detail-view-firmware">
                            <ExternalLink className="w-3 h-3 mr-1.5" /> View on Firmware tab
                          </Button>
                        ) : canRemediate && (
                          <Button size="sm" onClick={() => onRemediate([device], def.catalogKey)}
                            className="mt-2 h-7 text-xs bg-[#E5EFFA] text-[#3C02CC] hover:bg-[#BBFF71] hover:text-[#3C02CC] font-semibold"
                            data-testid={`device-detail-remediate-${code}`}>
                            <Play className="w-3 h-3 mr-1.5" /> Remediate…
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {canRemediate && (
            <Button size="sm" variant="ghost" onClick={() => onRemediate([device], null)}
              className="mt-3 h-7 text-xs text-cyan-300 hover:bg-cyan-500/10"
              data-testid="device-detail-run-other">
              <Wrench className="w-3 h-3 mr-1.5" /> Run another script…
            </Button>
          )}
        </DetailSection>

        <DetailSection title="Windows Update">
          <DetailRow label="OS version">
            {getFriendlyName(device.os_version)}
            <span className="block text-xs text-white/40 font-mono">{device.os_version}</span>
          </DetailRow>
          {device.feature_update_target && (
            <DetailRow label="Feature update" testId="device-detail-feature-update">
              {device.feature_update_target}
              <span className={`block text-xs ${device.feature_update_pending ? 'text-amber-300' : 'text-green-400'}`}>
                {device.feature_update_pending ? 'Targeted — not yet on target' : 'On target'}
              </span>
            </DetailRow>
          )}
          <DetailRow label="Last Intune sync" testId="device-detail-last-sync">
            {fmt(device.last_sync) || <span className="text-white/40">Never</span>}
          </DetailRow>
          <DetailRow label="Last WU scan" testId="device-detail-last-scan">
            {fmt(device.last_wu_scan) || (
              <span className="text-white/40">{wufbConnected ? 'No scan time' : 'Connect WUfB'}</span>
            )}
          </DetailRow>
          <DetailRow label="Last boot" testId="device-detail-last-boot">
            {fmt(device.last_boot) || <span className="text-white/40">No boot data</span>}
          </DetailRow>
          <DetailRow label="Client substate">
            {device.wufb_client_substate || device.client_substate || <span className="text-white/40">—</span>}
            {device.client_state_source === 'intune' && <span className="block text-xs text-white/40 italic">via Intune</span>}
          </DetailRow>
        </DetailSection>

        <DetailSection title="Firmware">
          <DetailRow label="BIOS">{device.bios_version || <span className="text-white/40">Not reported</span>}</DetailRow>
          <DetailRow label="Latest available">
            {device.latest_bios_version || <span className="text-white/40">{device.firmware_unknown_reason || '—'}</span>}
          </DetailRow>
          <DetailRow label="Ready to automate">
            {device.firmware_ready_to_automate ? 'Yes — model has a catalogue BIOS download' : <span className="text-white/40">No</span>}
          </DetailRow>
          <DetailRow label="TPM / Secure Boot">
            {device.tpm_version || '—'} / {device.secure_boot_enabled == null ? '—' : device.secure_boot_enabled ? 'On' : 'Off'}
          </DetailRow>
          {!issues.includes('firmware') && (
            <Button size="sm" variant="ghost" onClick={() => onViewFirmware(device)}
              className="mt-1 h-7 text-xs text-cyan-300 hover:bg-cyan-500/10">
              <ExternalLink className="w-3 h-3 mr-1.5" /> View on Firmware tab
            </Button>
          )}
        </DetailSection>

        <DetailSection title="Device">
          <DetailRow label="Intune compliance" testId="device-detail-compliance">
            <span className={device.is_compliant ? 'text-green-400' : 'text-red-400'}>
              {device.is_compliant ? 'Compliant' : 'Non-Compliant'}
            </span>
          </DetailRow>
          <DetailRow label="App criticality" testId="device-detail-app-criticality">
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${crit.cls}`}>{crit.label}</span>
          </DetailRow>
          <DetailRow label="Windows edition" testId="device-detail-edition">
            {device.edition_assumed ? (
              <span className="text-white/70" title="Intune has not reported this device's edition yet. It is assessed as Home/Pro, the shorter lifecycle.">
                Not reported yet <span className="text-amber-300 text-xs">(assumed Home/Pro)</span>
              </span>
            ) : (device.edition || 'Unknown')}
          </DetailRow>
          <DetailRow label="OS support" testId="device-detail-support">
            <span className="block">{device.os_version_label || '—'}</span>
            {device.os_support_end ? (
              <span className={`block text-xs ${device.os_supported === false ? 'text-red-300' : 'text-white/70'}`}>
                {device.os_supported === false ? 'Support ended ' : 'Supported until '}
                {new Date(device.os_support_end).toLocaleDateString()}
                {device.os_supported !== false && typeof device.os_support_days_left === 'number' && ` (${device.os_support_days_left} days)`}
                {device.os_support_estimated && ' · estimated'}
              </span>
            ) : device.os_supported === false ? (
              <span className="block text-xs text-red-300">Out of support</span>
            ) : null}
            {device.os_esu && (
              <span className="block text-xs text-cyan-300" data-testid="device-detail-esu"
                    title="Intune does not report ESU enrolment. This device is on a monthly update released after support ended, which only Extended Security Updates provide.">
                Extended Security Updates (detected)
              </span>
            )}
          </DetailRow>
          <DetailRow label="Cyber Essentials" testId="device-detail-ce">
            <span className={device.ce_status === 'breach' ? 'text-red-300' : device.ce_status === 'at_risk' ? 'text-amber-300' : 'text-green-400'}>
              {device.ce_status === 'breach' ? 'Breach' : device.ce_status === 'at_risk' ? 'At risk' : 'No breach'}
            </span>
            {(device.ce_reasons || []).filter(r => r !== 'esu' && CE_REASONS[r]).map(r => (
              <span key={r} className="block text-xs text-white/70" title={CE_REASONS[r].why}>{CE_REASONS[r].label}</span>
            ))}
          </DetailRow>
          <DetailRow label="Disk free">
            {typeof device.free_storage_gb === 'number'
              ? `${device.free_storage_gb} GB${typeof device.total_storage_gb === 'number' ? ` of ${device.total_storage_gb} GB` : ''}`
              : <span className="text-white/40">Not reported</span>}
          </DetailRow>
          <DetailRow label="Serial">{device.serial_number || '—'}</DetailRow>
          <DetailRow label="Entra device ID">
            <span className="font-mono text-xs">{device.azure_ad_device_id || <span className="text-white/40">None — cannot be remediated</span>}</span>
          </DetailRow>
          {policiesAvailable && (
            <div className="flex items-center justify-between gap-4 py-1.5 text-sm">
              <span className="text-white/60">Deployment sprint</span>
              {canEditSprint ? (
                <FeatureUpdatePhaseSelector
                  deviceId={device.id}
                  currentPhase={device.feature_update_phase || 'green'}
                  pendingPhase={pendingPhase}
                  onStageChange={onStageChange}
                />
              ) : (
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${PHASE_CONFIG[device.feature_update_phase || 'green']?.onColor || 'text-white'} ${PHASE_CONFIG[device.feature_update_phase || 'green']?.color || 'bg-white/10'}`}>
                  {PHASE_CONFIG[device.feature_update_phase || 'green']?.label || 'Main Deployment'}
                </span>
              )}
            </div>
          )}
        </DetailSection>

        {/* Governance (October 2026): what ALICE has been told to leave this
            device out of, and why. A device-type rule is shown but changed from
            the Excluded view, since it covers more than this device. */}
        <DetailSection title="Governance">
          {(device.excluded_sources || []).length === 0 ? (
            <p className="text-sm text-white/60" data-testid="device-detail-not-excluded">
              Not excluded. ALICE reports on this device and includes it in rings and feature updates.
            </p>
          ) : (
            <ul className="space-y-2" data-testid="device-detail-exclusions">
              {device.excluded_sources.map((src, i) => (
                <li key={i} className="text-sm">
                  <span className="text-white">{src.label}</span>
                  <span className="block mt-0.5"><ExclusionScopeChips scopes={src.scopes} /></span>
                  {src.reason && <span className="block text-xs text-white/60 mt-0.5">{src.reason}</span>}
                  {src.set_by && <span className="block text-xs text-white/40">{src.set_by}{src.set_at ? ` · ${_fmtTime(src.set_at)}` : ''}</span>}
                </li>
              ))}
            </ul>
          )}
          {canExclude && (
            <Button size="sm" variant="outline" onClick={() => onManageExclusions(device)}
                    className="mt-2 border-white/20 text-white hover:bg-white/10" data-testid="device-detail-manage-exclusions">
              {(device.excluded_sources || []).some(s => s.kind === 'device') ? 'Change exclusion…' : 'Exclude this device…'}
            </Button>
          )}
        </DetailSection>

        {policiesAvailable && (
          <DetailSection title="Remediation history">
            {runs === null ? (
              <p className="text-sm text-white/50">Loading…</p>
            ) : runsError ? (
              <p className="text-sm text-amber-300">{runsError}</p>
            ) : runs.length === 0 ? (
              <p className="text-sm text-white/50" data-testid="device-detail-no-runs">No remediation has been run on this device.</p>
            ) : (
              <ul className="space-y-1.5" data-testid="device-detail-runs">
                {runs.map((r) => (
                  <li key={r.id} className="flex items-center justify-between gap-3 text-sm">
                    <span className="min-w-0">
                      <span className="text-white">{r.script_name}</span>
                      <span className="block text-xs text-white/50">
                        {_fmtTime(r.created_at)}{r.requested_by ? ` · ${r.requested_by}` : ''}
                      </span>
                    </span>
                    <Button size="sm" variant="ghost" onClick={() => onViewRun(r.id)}
                      className="h-7 text-xs text-cyan-300 hover:bg-cyan-500/10 shrink-0">
                      <Eye className="w-3 h-3 mr-1" /> Result
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </DetailSection>
        )}
      </DialogPrimitive.Content>
      </SheetPortal>
    </Sheet>
  );
};

export const ReportsPage = () => {
  const navigate = useNavigate();
  // Reporting-only tenants can't read update policies, so the endpoints the
  // backend refuses for them are never called. (Quality Updates and Feature
  // Updates moved to Deployments in October 2026.)
  const { canViewOSPatchPolicies, canWriteOSPatch, canManageOSPatches } = usePermissions();
  // Running a remediation needs the policy plan (scripts permission), the
  // write plan, and the manage_os_patches role — Viewers never see the
  // controls. The backend enforces the same three independently.
  const canRemediate = canViewOSPatchPolicies && canWriteOSPatch && canManageOSPatches;
  // Sprint changes move devices in and out of live deployments, so they need
  // the manage_os_patches role as well as the plan (the old Devices page
  // checked the plan only; the backend now refuses Viewers too).
  const canEditSprint = canViewOSPatchPolicies && canWriteOSPatch && canManageOSPatches;
  const [complianceReport, setComplianceReport] = useState(null);
  const [updateReport, setUpdateReport] = useState(null);
  const [deviceHealthReport, setDeviceHealthReport] = useState(null);
  const [ringsSummary, setRingsSummary] = useState(null);
  const [wufbSettings, setWufbSettings] = useState(null);
  const [osVersionExpanded, setOsVersionExpanded] = useState(false);
  // OS Version Distribution sits above the device list on the Devices tab;
  // collapsed by default so the list isn't pushed below the fold.
  const [osVersionSectionCollapsed, setOsVersionSectionCollapsed] = useState(true);
  // 7 Oct (Dev bug): the open OS Version chart only closed by clicking its
  // card again. It now also closes on a click anywhere outside the chart and
  // that card — another card, the list, the page — or on Escape. Clicks
  // inside the chart (a version bar filters the list) leave it open.
  const osVersionCardRef = useRef(null);
  const osVersionChartRef = useRef(null);
  useEffect(() => {
    if (osVersionSectionCollapsed) return undefined;
    const onMouseDown = (e) => {
      if (osVersionChartRef.current?.contains(e.target) || osVersionCardRef.current?.contains(e.target)) return;
      setOsVersionSectionCollapsed(true);
    };
    const onKeyDown = (e) => { if (e.key === 'Escape') setOsVersionSectionCollapsed(true); };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [osVersionSectionCollapsed]);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  // The active tab lives in `?tab=` rather than local state, so a tab can be
  // bookmarked and linked to (the Devices consolidation and firmware links
  // depend on it). Before October 2026 it was `useState('overview')` and no
  // tab could be deep-linked. Deriving it from the URL, rather than copying
  // the URL into state once, also means clicking "Discover" in the sidebar
  // (bare `/os-patch/discover`) really does return to the default tab.
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = resolveDiscoverTab(searchParams.get('tab'));
  // `extra` sets (or, with null, removes) other params in the same update.
  // Switching tab drops the per-tab params (`device`, `device_search`) unless
  // the caller passes them, so they never leak onto a tab that ignores them.
  const setActiveTab = (tab, extra = {}) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', tab);
      ['device', 'device_search', 'view', 'group', 'reason', 'show'].forEach((k) => { if (!(k in extra)) next.delete(k); });
      Object.entries(extra).forEach(([k, v]) => (v == null ? next.delete(k) : next.set(k, v)));
      return next;
    }, { replace: true });
  };
  const setUrlParam = (key, value) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value == null) next.delete(key); else next.set(key, value);
      return next;
    }, { replace: true });
  };
  // Devices tab: the open detail panel is `?device=<id>`, so a device can be
  // linked to and the panel survives a refresh.
  const selectedDeviceId = activeTab === 'devices' ? searchParams.get('device') : null;
  const firmwareDeviceSearch = activeTab === 'firmware' ? searchParams.get('device_search') : null;
  const [searchTerm, setSearchTerm] = useState('');
  const [savingWufb, setSavingWufb] = useState(false);
  const [syncingClientStates, setSyncingClientStates] = useState(false);
  // Compliance trending (Section A/B/C)
  const [complianceTrending, setComplianceTrending] = useState(null);
  const [trendBackfilling, setTrendBackfilling] = useState(false);
  // `scott/feature-updates-3` — per-tenant configurable Patch Adoption Curve target
  const [targetCurve, setTargetCurve] = useState(null);
  // `scott/feature-updates-3` — Update Ring filter for the Monthly Compliance Trend.
  // Drives both the WUfB KQL overlay (when integration enabled) and the
  // displayed monthly_trend rows (only those rings' devices counted).
  const [complianceTrendRing, setComplianceTrendRing] = useState('');
  const [complianceTrendLoading, setComplianceTrendLoading] = useState(false);
  // Last Intune sync (top-right of Reports header). `scott/kpi-cycle-update2`
  // — the Windows Update catalog freshness badge that used to sit alongside it
  // now lives on Settings > Data Management.
  const [lastIntuneSync, setLastIntuneSync] = useState(null);
  const [syncingFromIntune, setSyncingFromIntune] = useState(false);
  
  // Device Health filters
  const [healthFilter, setHealthFilter] = useState('all');
  const [complianceFilter, setComplianceFilter] = useState('all');
  const [osVersionFilter, setOsVersionFilter] = useState('all');
  // Optional second filter: narrow down to a specific patched build
  // (e.g. "Apr 2026 Security Update (24H2)") using the friendly name
  // emitted by getVersionFriendlyName(). Independent of the OS-release
  // filter so users can combine them ("24H2" + "Apr 2026").
  const [osBuildFilter, setOsBuildFilter] = useState('all');
  const [securityStatusFilter, setSecurityStatusFilter] = useState('all');
  
  // Device Health pagination
  const [healthPage, setHealthPage] = useState(1);
  const [healthPageSize, setHealthPageSize] = useState(20);

  // Devices tab (October 2026 — the main-menu Devices page merged in).
  // Filters the old Devices page had, plus the issue and firmware filters.
  const [onlineFilter, setOnlineFilter] = useState(false);
  const [devicesLoadFailed, setDevicesLoadFailed] = useState(false);
  const [issueFilter, setIssueFilter] = useState(null); // a DEVICE_ISSUES code
  // Row selection for bulk remediation: managed-device ids.
  const [selectedDeviceIds, setSelectedDeviceIds] = useState([]);
  // { devices, catalogKey } while the run modal is open.
  const [remediationRequest, setRemediationRequest] = useState(null);
  const [runDetailId, setRunDetailId] = useState(null);
  // Bumped after a run so the open panel reloads its history.
  const [panelRefreshKey, setPanelRefreshKey] = useState(0);
  // Staged sprint changes, exactly as the old Devices page did them: nothing
  // is written until the commit bar is used (see PhaseChangeCommitModal).
  const [pendingPhases, setPendingPhases] = useState({});
  const [showPhaseCommit, setShowPhaseCommit] = useState(false);
  
  // Compliance tab: collapsible trending section.
  const [trendingSectionCollapsed, setTrendingSectionCollapsed] = useState(false);
  
  // WUfB form state
  const [wufbForm, setWufbForm] = useState({ subscription_id: '', workspace_id: '' });
  
  // Windows 11 Readiness state
  const [win11Readiness, setWin11Readiness] = useState(null);
  const [win11Filter, setWin11Filter] = useState('all'); // 'all', 'capable', 'not_capable', 'unknown'
  const [win11Search, setWin11Search] = useState('');
  const [win11ExportLoading, setWin11ExportLoading] = useState(false);
  const [selectedWin11Device, setSelectedWin11Device] = useState(null); // For showing failure details
  // Forces re-render once backend friendly-name prefetch completes
  const [, setBuildNameTick] = useState(0);
  
  // Windows 11 Readiness pagination
  const [win11Page, setWin11Page] = useState(1);
  const [win11PageSize, setWin11PageSize] = useState(20);

  // Firmware tab is fully owned by `<FirmwareReportTab />` (Phase 2 extract).
  // No firmware state lives on this page anymore.

  const loadReports = async () => {
    setLoading(true);
    try {
      // Load ALL reports in parallel for faster page load
      // October 2026: risk-analysis and autopatch-quality-status moved with
      // Quality Updates to Deployments; quality-update-distribution and
      // quality-update-trending were fetched here but never read, and went.
      const [compRes, updateRes, devicesRes, healthRes, ringsRes, wufbRes, complianceTrendingRes, syncStatusRes, win11Res, curveRes] = await Promise.allSettled([
        osPatchApi.getComplianceReport(),
        osPatchApi.getUpdateStatusReport(),
        osPatchApi.getDevices(),
        axios.get(`${API}/reports/device-health`),
        axios.get(`${API}/reports/update-rings-summary`),
        axios.get(`${API}/wufb-integration/settings`),
        axios.get(`${API}/reports/compliance-trending?months=12`),
        axios.get(`${API}/sync/status`).catch(() => ({ data: null })),
        osPatchApi.getWin11Readiness(),
        osPatchApi.getComplianceTargetCurve().catch(() => ({ data: null })),
      ]);
      
      // Handle each result - set data if fulfilled, default if rejected
      if (compRes.status === 'fulfilled') setComplianceReport(compRes.value.data);
      if (updateRes.status === 'fulfilled') setUpdateReport(updateRes.value.data);
      if (devicesRes.status === 'fulfilled') setDevices(Array.isArray(devicesRes.value.data) ? devicesRes.value.data : []);
      // The Devices tab joins these documents onto every row (sync time,
      // sprint, Entra id, disk). Without them the rows still render, so say
      // plainly that those fields are missing rather than show "Never".
      setDevicesLoadFailed(devicesRes.status !== 'fulfilled');
      if (healthRes.status === 'fulfilled') setDeviceHealthReport(healthRes.value.data);
      if (ringsRes.status === 'fulfilled') setRingsSummary(ringsRes.value.data);
      if (wufbRes.status === 'fulfilled') {
        setWufbSettings(wufbRes.value.data);
        setWufbForm({
          subscription_id: wufbRes.value.data?.subscription_id || '',
          workspace_id: wufbRes.value.data?.workspace_id || ''
        });
      }
      if (complianceTrendingRes.status === 'fulfilled') {
        setComplianceTrending(complianceTrendingRes.value.data);
        const errs = complianceTrendingRes.value.data?.errors;
        if (Array.isArray(errs) && errs.length) {
          // eslint-disable-next-line no-console
          console.error('[compliance-trending] backend reported errors:', errs);
          toast.error(`Compliance trending issue: ${errs[0]}`, { duration: 9000 });
        }
      } else {
        setComplianceTrending({ monthly_trend: [], adoption_curve: [], kpis: {}, snapshot_count: 0 });
      }
      if (curveRes.status === 'fulfilled' && curveRes.value?.data) {
        setTargetCurve(curveRes.value.data);
      }
      if (syncStatusRes.status === 'fulfilled' && syncStatusRes.value?.data) {
        setLastIntuneSync(syncStatusRes.value.data);
      }
      if (win11Res.status === 'fulfilled') {
        setWin11Readiness(win11Res.value.data);
      } else {
        setWin11Readiness({ available: false, summary: { total_devices: 0, capable: 0, not_capable: 0, unknown: 0 }, devices: [] });
      }

      // ---- Prefetch backend-resolved friendly names for every unique OS build in the
      //      loaded reports. `getVersionFriendlyName()` will use this cache as the primary
      //      source, falling back to the local estimator if the prefetch failed. ----
      try {
        const buildSet = new Set();
        const updData = updateRes.status === 'fulfilled' ? updateRes.value.data : null;
        if (updData?.os_version_distribution) {
          Object.keys(updData.os_version_distribution).forEach(v => v && buildSet.add(v));
        }
        const healthData = healthRes.status === 'fulfilled' ? healthRes.value.data : null;
        (healthData?.devices || []).forEach(d => d?.os_version && buildSet.add(d.os_version));
        const devList = devicesRes.status === 'fulfilled' ? devicesRes.value.data : [];
        (Array.isArray(devList) ? devList : []).forEach(d => d?.os_version && buildSet.add(d.os_version));
        if (buildSet.size > 0) {
          const arr = Array.from(buildSet);
          // Fire-and-forget; first render may use local fallback, then cache fills in.
          prefetchBuildNames(arr).then(() => {
            // Trigger a lightweight re-render so cached names show up.
            setBuildNameTick(t => t + 1);
          });
        }
      } catch (prefetchErr) {
        console.debug('Build name prefetch skipped:', prefetchErr?.message);
      }
    } catch (error) {
      console.error('Error loading reports:', error);
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  // Sync client states from Log Analytics
  const handleSyncClientStates = async () => {
    setSyncingClientStates(true);
    try {
      const res = await osPatchApi.syncClientStates();
      if (res.data.status === 'success') {
        // The backend appends an explanation when WUfB rows came back but
        // couldn't be joined onto a device — that's the case worth flagging
        // loudly rather than showing a green "synced 0 devices".
        if (res.data.unmatched > 0 && res.data.devices_updated === 0) {
          toast.warning(res.data.message);
        } else {
          toast.success(res.data.message || 'Client states synced');
        }
        loadReports(); // Reload to show updated data
      } else if (res.data.status === 'warning') {
        toast.warning(res.data.message || 'No WUfB data returned from Log Analytics');
      } else {
        toast.error(res.data.message || 'Failed to sync client states');
      }
    } catch (error) {
      toast.error('Failed to sync client states from Log Analytics');
    } finally {
      setSyncingClientStates(false);
    }
  };

  useEffect(() => { loadReports(); }, []);

  // Trigger /sync/all from the Reports page so users don't have to go back to Dashboard.
  const handleSyncFromIntune = async () => {
    // Retained for backwards-compatibility with any tab-level sync
    // wiring that still calls this handler. The page-level button was
    // removed (the OS Patch Management Dashboard's "Sync from Intune"
    // is the canonical entry point now), but tab callbacks that
    // reference handleSyncFromIntune are left intact.
    setSyncingFromIntune(true);
    try {
      const r = await runSyncWithPolling();
      let msg = canViewOSPatchPolicies
        ? `Sync completed: ${r.devices?.count || 0} devices, ${r.update_rings?.count || 0} rings`
        : `Sync completed: ${r.devices?.count || 0} devices`;
      if (r.devices?.stale_removed > 0) {
        msg += ` (${r.devices.stale_removed} stale removed)`;
      }
      if (r.catalog?.status === 'success') {
        const n = (r.catalog.entries_synced || 0) + (r.catalog.entries_updated || 0);
        if (n > 0) msg += ` · catalog: ${r.catalog.entries_synced || 0}/${r.catalog.entries_updated || 0}`;
      } else if (r.catalog?.status === 'skipped') {
        msg += ` · catalog cached (${r.catalog.age_hours ?? '?'}h old)`;
      }
      toast.success(msg);
      // Re-pull every report so the page reflects the fresh data.
      await loadReports();
    } catch (e) {
      if (e?.isJobLost) {
        toast.warning(e.message);
      } else {
        const detail = e?.response?.data?.detail || e.message;
        toast.error(`Sync failed: ${detail}`);
      }
    } finally {
      setSyncingFromIntune(false);
    }
  };

  // Trigger heuristic backfill of compliance trending history.
  const handleBackfillTrend = async () => {
    setTrendBackfilling(true);
    try {
      const res = await axios.post(`${API}/reports/compliance-trending/backfill?months=6`);
      const d = res.data || {};
      // Always log the diagnostic payload so it's inspectable from the console
      // (DevTools → Console) without needing backend log access.
      // eslint-disable-next-line no-console
      console.info('[compliance-trending] backfill diagnostic:', d);

      const written = d.rows_written ?? d.inserted_or_updated ?? 0;
      if (written > 0) {
        toast.success(`Backfilled ${written} estimated snapshots across ${d.months_back || 6} months`);
      } else {
        // Helpful explanation of the most common silent-zero scenarios
        const reasons = [];
        if (d.devices_in_db === 0) reasons.push('no devices in DB — run "Sync from Intune" first');
        if (d.devices_in_db > 0 && d.devices_considered === 0) reasons.push('all devices excluded as non-Windows (check os_name/os_version on devices)');
        if (d.devices_considered > 0 && d.rows_skipped_zero_total > 0) reasons.push(`every snapshot date had 0 active devices (devices_excluded_no_dates=${d.devices_excluded_no_dates || 0})`);
        if (d.rows_skipped_existing_measured > 0 && reasons.length === 0) reasons.push('all rows already had measured snapshots — try ?force=true');
        const why = reasons.length ? ` — ${reasons.join('; ')}` : ' — see console for full diagnostics';
        toast(`Backfill wrote 0 rows${why}`, { duration: 9000 });
      }
      // Re-fetch trending payload only
      const fresh = await axios.get(`${API}/reports/compliance-trending?months=12`).catch(() => null);
      if (fresh?.data) setComplianceTrending(fresh.data);
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      const status = e?.response?.status;
      // eslint-disable-next-line no-console
      console.error('[compliance-trending] backfill error:', e);
      if (status === 404) {
        toast.error('Backfill endpoint not found — backend may not be deployed yet (push backend/routes/os_patch_routes.py + backend/services/compliance_snapshot_service.py)');
      } else {
        toast.error(`Backfill failed: ${detail}`);
      }
    } finally {
      setTrendBackfilling(false);
    }
  };

  // `scott/deployment-kpi-bugfix` — stale-snapshot maintenance.
  // Snapshots written before the catalog-aware resolver shipped can carry an
  // inflated compliance % (the static estimator could date a build into the
  // future, satisfying every cycle's compliance test). They can't be
  // recomputed — snapshots have no historical mode — so the remedy is to
  // delete them and let the curve start from the first correctly-resolved
  // day. Two steps: preview, then an explicit confirm.
  const [staleSnapshots, setStaleSnapshots] = useState(null);
  const [staleLoading, setStaleLoading] = useState(false);

  const handleInspectStaleSnapshots = async () => {
    setStaleLoading(true);
    try {
      const res = await axios.get(`${API}/reports/compliance-trending/stale-snapshots`);
      setStaleSnapshots(res.data || null);
      // eslint-disable-next-line no-console
      console.info('[compliance-trending] stale-snapshot preview:', res.data);
      return res.data;
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      // eslint-disable-next-line no-console
      console.error('[compliance-trending] stale-snapshot preview error:', e);
      toast.error(`Could not read snapshot health: ${detail}`);
      return null;
    } finally {
      setStaleLoading(false);
    }
  };

  const handlePurgeStaleSnapshots = async (dates) => {
    setStaleLoading(true);
    try {
      const qs = new URLSearchParams({ confirm: 'true' });
      if (dates && dates.length) qs.set('dates', dates.join(','));
      const res = await axios.post(
        `${API}/reports/compliance-trending/stale-snapshots/purge?${qs.toString()}`,
      );
      const d = res.data || {};
      // eslint-disable-next-line no-console
      console.info('[compliance-trending] stale-snapshot purge:', d);
      if (d.deleted_count > 0) {
        toast.success(
          `Removed ${d.deleted_count} stale snapshot row${d.deleted_count === 1 ? '' : 's'} ` +
          `from the ${d.month_id} cycle`,
        );
      } else {
        toast(`Nothing to remove — the ${d.month_id} cycle is already clean`, { duration: 6000 });
      }
      // Refresh both the preview and the chart data.
      const [fresh] = await Promise.all([
        axios.get(`${API}/reports/compliance-trending?months=12`).catch(() => null),
        handleInspectStaleSnapshots(),
      ]);
      if (fresh?.data) setComplianceTrending(fresh.data);
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      // eslint-disable-next-line no-console
      console.error('[compliance-trending] stale-snapshot purge error:', e);
      toast.error(`Purge failed: ${detail}`);
    } finally {
      setStaleLoading(false);
    }
  };

  // `scott/feature-updates-3` — save the Patch Adoption Curve target. Used by
  // ComplianceTrendingSection's inline editor; we lift the handler so the
  // updated curve is shared across re-renders and the per-tenant fetch stays
  // here next to `loadReports`.
  const handleSaveTargetCurve = async (preset, anchors) => {
    try {
      const res = await osPatchApi.saveComplianceTargetCurve({ preset, anchors });
      if (res?.data) setTargetCurve(res.data);
      toast.success(
        preset === 'custom'
          ? 'Custom target curve saved'
          : `Target curve set to ${preset.charAt(0).toUpperCase()}${preset.slice(1)}`
      );
      return res?.data;
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      toast.error(`Couldn't save target curve: ${detail}`);
      throw e;
    }
  };

  // `scott/feature-updates-3` — re-fetch the Monthly Compliance Trend with
  // a new Update Ring filter. The endpoint folds WUfB KQL data into the
  // monthly_trend day30 values automatically when WUfB is connected.
  const handleComplianceTrendRingChange = async (nextRingId) => {
    setComplianceTrendRing(nextRingId || '');
    setComplianceTrendLoading(true);
    try {
      const params = new URLSearchParams({ months: '12' });
      if (nextRingId) params.set('update_ring', nextRingId);
      const res = await axios.get(`${API}/reports/compliance-trending?${params.toString()}`);
      setComplianceTrending(res.data);
    } catch (e) {
      toast.error(`Couldn't refresh trend: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setComplianceTrendLoading(false);
    }
  };
  // Calculate metrics
  const totalDevices = complianceReport?.total_devices || devices.length || 0;

  // Prepare OS version distribution with friendly names — keep the full
  // sorted list available so the visual can be expanded beyond the
  // default-10 view per user request (2026-06).
  const osVersionDistribution = updateReport?.os_version_distribution || {};
  const osVersionDataAll = Object.entries(osVersionDistribution)
    .map(([version, count], i) => {
      // Map version to friendly name
      const friendlyName = getVersionFriendlyName(version);
      return {
        name: friendlyName.length > 25 ? friendlyName.substring(0, 25) + '...' : friendlyName,
        fullVersion: version,
        friendlyName,
        count,
        fill: [CHART_COLORS.lime, CHART_COLORS.cyan, CHART_COLORS.blue, CHART_COLORS.purple, CHART_COLORS.coral][i % 5]
      };
    })
    .sort((a, b) => b.count - a.count);
  const osVersionDefaultLimit = 10;
  const osVersionData = osVersionDataAll.slice(0, osVersionDefaultLimit);

  // Legend months for the OS Version Distribution chart, from the backend's
  // latest Patch Tuesday month ("YYYY-MM") rather than hard-coded names.
  const patchMonthLabel = (ym, offset = 0) => {
    const match = /^(\d{4})-(\d{2})$/.exec(ym || '');
    if (!match) return null;
    const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1 + offset, 1));
    return date.toLocaleString('en-GB', { month: 'short', year: 'numeric', timeZone: 'UTC' });
  };
  const latestPatchMonthLabel = patchMonthLabel(updateReport?.latest_month);
  const previousPatchMonthLabel = patchMonthLabel(updateReport?.latest_month, -1);

  // Helper to get friendly version name
  function getVersionFriendlyName(version) {
    // 1) Prefer backend-resolved name (single source of truth + auto-increment + Graph catalog).
    const cached = getCachedBuildName(version);
    if (cached) return cached;

    // 2) Local fallback: hardcoded buildMap + anchor-based estimation (used if backend call
    //    fails, is slow, or a tenant has no Graph catalog consented). Kept intentionally so
    //    the refactor is trivially revert-safe and never blocks the UI.
    // Map Windows build numbers to friendly names with correct KB and dates
    // Reference: https://support.microsoft.com/en-us/topic/windows-11-version-24h2-update-history
    const buildMap = {
      // Windows 11 26H2 (26300.x) - GA 2026-09-29
      '10.0.26300.9550': 'Sep 2026 Preview Update (26H2)',
      '10.0.26300.9457': 'Sep 2026 Security Update (26H2)',
      // Windows 11 25H2 (26200.x) - 2026 Security Updates
      '10.0.26200.8246': 'Apr 2026 Security Update (25H2)',
      '10.0.26200.8037': 'Mar 2026 Security Update (25H2)',
      '10.0.26200.7840': 'Feb 2026 Security Update (25H2)',  // KB5077181
      '10.0.26200.7900': 'Feb 2026 Security Update (25H2)',
      '10.0.26200.7800': 'Jan 2026 Security Update (25H2)',
      '10.0.26200.7623': 'Jan 2026 Security Update (25H2)',
      // Windows 11 24H2 (26100.x) - 2026 Security Updates
      '10.0.26100.8246': 'Apr 2026 Security Update (24H2)',
      '10.0.26100.8037': 'Mar 2026 Security Update (24H2)',
      '10.0.26100.7840': 'Feb 2026 Security Update (24H2)',  // KB5077181
      '10.0.26100.7922': 'Feb 2026 Preview Update (24H2)',
      '10.0.26100.7723': 'Feb 2026 Security Update (24H2)',
      '10.0.26100.7623': 'Jan 2026 Security Update (24H2)',
      '10.0.26100.7500': 'Dec 2025 Security Update (24H2)',
      '10.0.26100.7462': 'Dec 2025 Security Update (24H2)',
      '10.0.26100.7400': 'Nov 2025 Security Update (24H2)',
      '10.0.26100.7300': 'Oct 2025 Security Update (24H2)',
      // Windows 11 23H2 (22631.x) - 2026 Security Updates
      '10.0.22631.6936': 'Apr 2026 Security Update (23H2)',
      '10.0.22631.6783': 'Mar 2026 Security Update (23H2)',
      '10.0.22631.6445': 'Feb 2026 Security Update (23H2)',
      '10.0.22631.6345': 'Feb 2026 Security Update (23H2)',
      '10.0.22631.6200': 'Jan 2026 Security Update (23H2)',
      '10.0.22631.6100': 'Dec 2025 Security Update (23H2)',
      '10.0.22631.6000': 'Oct 2025 Security Update (23H2)',
      // Windows 11 22H2 (22621.x) - Enterprise/Education/IoT Enterprise only
      '10.0.22621.6068': 'Apr 2026 Security Update (22H2)',
      '10.0.22621.5300': 'Mar 2026 Security Update (22H2)',
      '10.0.22621.5200': 'Feb 2026 Security Update (22H2)',
      '10.0.22621.5100': 'Jan 2026 Security Update (22H2)',
      '10.0.22621.5000': 'Dec 2025 Security Update (22H2)',
      // Windows 11 21H2 (22000.x) - End of Servicing
      '10.0.22000.3260': 'Oct 2024 Final Security Update (21H2) (EOS)',
      // Windows Server 2022 LTSC (20348.x)
      '10.0.20348.5024': 'Apr 2026 Security Update (Windows Server 2022 LTSC)',
      // Windows 10 1809 / Server 2019 LTSC (17763.x)
      '10.0.17763.8647': 'Apr 2026 OOB Update (Win10 1809 / Server 2019 LTSC)',
      // Windows 10 22H2 (19045.x)
      '10.0.19045.7058': 'Mar 2026 Security Update (W10 22H2)',
      '10.0.19045.6532': 'Feb 2026 Security Update (W10 22H2)',
      '10.0.19045.6432': 'Jan 2026 Security Update (W10 22H2)',
      '10.0.19045.6200': 'Dec 2025 Security Update (W10 22H2)',
      '10.0.19045.6100': 'Nov 2025 Security Update (W10 22H2)',
      '10.0.19045.6000': 'Oct 2025 Security Update (W10 22H2)',
      // Windows 10 21H2 (19044.x)
      '10.0.19044.5200': 'Mar 2026 Security Update (W10 21H2)',
      '10.0.19044.5000': 'Dec 2025 Security Update (W10 21H2)'
    };
    
    if (buildMap[version]) return buildMap[version];
    
    // Fallback: Intelligently estimate update month based on patch build
    const parts = version.split('.');
    if (parts.length >= 4) {
      const majorBuild = parts[2];
      const patchBuild = parseInt(parts[3], 10);
      
      // Known latest patch builds - Feb 2026 is 7840, Mar 2026 is 8037
      const latestPatches = {
        '26300': { version: '26H2', isPreview: true },
        '26200': { apr: 8246, mar: 8037, feb: 7840, jan: 7623, version: '25H2' },
        '26100': { apr: 8246, mar: 8037, feb: 7840, jan: 7623, version: '24H2' },
        '22631': { apr: 6936, mar: 6783, feb: 6445, jan: 6200, version: '23H2' },
        '22621': { apr: 6068, mar: 5300, feb: 5200, jan: 5100, version: '22H2' },
        '22000': { oct_2024: 3260, version: '21H2', isEOS: true },
        '20348': { apr: 5024, version: 'Windows Server 2022 LTSC' },
        '17763': { apr: 8647, version: 'Win10 1809 / Server 2019 LTSC', isOOB: true },
        '19045': { mar: 7058, feb: 6532, jan: 6432, dec: 6200, version: 'W10 22H2' },
        '19044': { apr: 5400, mar: 5200, feb: 5000, jan: 4800, version: 'W10 21H2' }
      };
      
      const info = latestPatches[majorBuild];
      if (info) {
        if (info.isPreview) {
          return `${info.version} - Build ${patchBuild}`;
        }

        // Windows 11 21H2 - end of servicing (Oct 2024 final)
        if (info.isEOS && info.oct_2024 && patchBuild >= info.oct_2024) {
          return `Oct 2024 Final Security Update (${info.version}) (EOS)`;
        }
        if (info.isEOS) {
          return `Older Update (${info.version}) (EOS) - Build ${patchBuild}`;
        }

        // Windows 10 1809 / Server 2019 OOB
        if (info.isOOB && info.apr && patchBuild >= info.apr) {
          return `Apr 2026 OOB Update (${info.version})`;
        }
        
        // Known months in order (newest first) with their anchor builds
        const knownMonths = [
          { key: 'apr', label: 'Apr 2026', year: 2026, month: 4 },
          { key: 'mar', label: 'Mar 2026', year: 2026, month: 3 },
          { key: 'feb', label: 'Feb 2026', year: 2026, month: 2 },
          { key: 'jan', label: 'Jan 2026', year: 2026, month: 1 },
        ];
        
        // Find the latest known anchor for dynamic forward detection
        const latestAnchor = knownMonths.find(m => info[m.key]);
        
        if (latestAnchor && patchBuild > info[latestAnchor.key]) {
          // Build is ABOVE the latest known — dynamically estimate the month
          // Calculate average monthly increment from known builds
          const builds = knownMonths.map(m => info[m.key]).filter(Boolean);
          let avgIncrement = 200;
          if (builds.length >= 2) {
            const diffs = [];
            for (let i = 0; i < builds.length - 1; i++) diffs.push(builds[i] - builds[i + 1]);
            avgIncrement = diffs.reduce((a, b) => a + b, 0) / diffs.length;
          }
          const monthsAhead = Math.max(1, Math.round((patchBuild - info[latestAnchor.key]) / avgIncrement));
          let newMonth = latestAnchor.month + monthsAhead;
          let newYear = latestAnchor.year;
          while (newMonth > 12) { newMonth -= 12; newYear++; }
          const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
          return `${monthNames[newMonth - 1]} ${newYear} Security Update (${info.version})`;
        }
        
        // Match against known anchors
        if (info.apr && patchBuild >= info.apr) {
          return `Apr 2026 Security Update (${info.version})`;
        } else if (info.mar && patchBuild >= info.mar) {
          return `Mar 2026 Security Update (${info.version})`;
        } else if (info.feb && patchBuild >= info.feb) {
          return `Feb 2026 Security Update (${info.version})`;
        } else if (info.jan && patchBuild >= info.jan) {
          return `Jan 2026 Security Update (${info.version})`;
        } else if (patchBuild >= (info.jan || 0) - 200) {
          return `Dec 2025 Security Update (${info.version})`;
        } else if (patchBuild >= (info.jan || 0) - 400) {
          return `Nov 2025 Security Update (${info.version})`;
        } else {
          return `Older Update (${info.version}) - Build ${patchBuild}`;
        }
      }
      
      // Unknown major build - show generic name with Insider detection
      if (parseInt(majorBuild, 10) > 26300) return `Win 11 Preview (Insider) - Build ${majorBuild}.${patchBuild}`;
      if (majorBuild.startsWith('26')) return `Win 11 - Build ${majorBuild}.${patchBuild}`;
      if (majorBuild.startsWith('22')) return `Win 11 - Build ${majorBuild}.${patchBuild}`;
      if (majorBuild.startsWith('19')) return `Win 10 - Build ${majorBuild}.${patchBuild}`;
    }
    
    return version || 'Unknown';
  }

  // Device Health metrics from API
  const healthMetrics = deviceHealthReport?.metrics || {};
  
  // Get unique OS versions for filter dropdown
  const uniqueOsVersions = [...new Set((deviceHealthReport?.devices || [])
    .map(d => {
      const version = d.os_version || '';
      const parts = version.split('.');
      if (parts.length >= 3) {
        const majorBuild = parts[2];
        if (parseInt(majorBuild, 10) > 26300) return 'Windows 11 Preview (Insider)';
        if (majorBuild === '26300') return 'Windows 11 26H2';
        if (majorBuild === '26200') return 'Windows 11 25H2';
        if (majorBuild === '26100') return 'Windows 11 24H2';
        if (majorBuild === '22631') return 'Windows 11 23H2';
        if (majorBuild === '22621') return 'Windows 11 22H2';
        if (majorBuild === '19045') return 'Windows 10 22H2';
        if (majorBuild === '19044') return 'Windows 10 21H2';
      }
      return 'Other';
    })
    .filter(v => v)
  )].sort();

  // Unique patched-build labels (e.g. "Apr 2026 Security Update (24H2)")
  // derived through the same friendly-name path used in the device
  // rows. When `osVersionFilter` is set we narrow the dropdown to just
  // the builds visible under that release so the two filters compose
  // cleanly. Listed with the latest build first (descending by
  // detected month/year) so the most-relevant rows are at the top.
  const uniqueOsBuilds = useMemo(() => {
    const rows = deviceHealthReport?.devices || [];
    const filteredBy = (d) => {
      if (osVersionFilter === 'all') return true;
      const parts = (d.os_version || '').split('.');
      if (parts.length < 3) return osVersionFilter === 'Other';
      const majorBuild = parts[2];
      const map = {
        'Windows 11 26H2': '26300',
        'Windows 11 25H2': '26200',
        'Windows 11 24H2': '26100',
        'Windows 11 23H2': '22631',
        'Windows 11 22H2': '22621',
        'Windows 10 22H2': '19045',
        'Windows 10 21H2': '19044',
      };
      return map[osVersionFilter] === majorBuild;
    };
    const seen = new Set();
    const out = [];
    for (const d of rows) {
      if (!filteredBy(d)) continue;
      const label = getVersionFriendlyName(d.os_version || '');
      if (!label || seen.has(label)) continue;
      seen.add(label);
      out.push(label);
    }
    // Sort by month/year embedded in the label (heuristic) — latest first.
    const monthOrder = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const score = (lbl) => {
      const m = lbl.match(/(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+(\d{4})/);
      if (!m) return 0;
      return parseInt(m[2], 10) * 12 + monthOrder.indexOf(m[1]);
    };
    return out.sort((a, b) => score(b) - score(a));
  }, [deviceHealthReport, osVersionFilter]);
  
  // Devices tab rows: each device-health row joined to its /devices document
  // by id (both are the os_patch_devices _id). Device health carries the
  // computed patch, client-state and firmware fields; /devices carries what the
  // old main-menu Devices page showed — sprint, app criticality, last sync —
  // plus disk, Entra device id and hardware. Health wins where both have a
  // field, because its values are computed rather than stored. Both payloads
  // were already loaded by this page, so the join costs no request.
  const deviceDocsById = useMemo(
    () => new Map((devices || []).map(d => [String(d.id), d])),
    [devices],
  );
  const joinedDevices = useMemo(
    () => (deviceHealthReport?.devices || []).map(h => ({ ...(deviceDocsById.get(String(h.id)) || {}), ...h })),
    [deviceHealthReport, deviceDocsById],
  );
  const issuesById = useMemo(() => {
    const m = new Map();
    joinedDevices.forEach(d => m.set(d.id, detectDeviceIssues(d)));
    return m;
  }, [joinedDevices]);

  // ---- Devices banner (October 2026): Overview / Fix Queue / Cyber Essentials
  // Breach. The view, the Fix Queue group and the Cyber Essentials reason live
  // in the URL (`?view=`, `?group=`, `?reason=`) so a filtered list can be
  // linked to and survives a refresh. An unknown value falls back to Overview.
  const devicesView = activeTab === 'devices' && ['fix_queue', 'ce_breach', 'excluded'].includes(searchParams.get('view'))
    ? searchParams.get('view') : 'overview';
  const fixGroup = devicesView === 'fix_queue' && FIX_QUEUE_GROUPS[searchParams.get('group')]
    ? searchParams.get('group') : null;
  const ceReason = devicesView === 'ce_breach' && CE_REASONS[searchParams.get('reason')]
    ? searchParams.get('reason') : null;
  const [ceIncludeAtRisk, setCeIncludeAtRisk] = useState(false);
  const [editionFilter, setEditionFilter] = useState('all');
  // Excluded view: the devices already excluded, or the virtual machines a
  // customer can choose from (`?show=vms`). Intune cannot tell AVD session
  // hosts from other Azure VMs, so the customer picks them.
  const excludedShow = devicesView === 'excluded' && searchParams.get('show') === 'vms' ? 'vms' : 'excluded';
  const [excludeRequest, setExcludeRequest] = useState(null); // { devices, stop }
  const [exclusionPolicy, setExclusionPolicy] = useState(null);
  const canExclude = canManageOSPatches;
  const canWriteExclusions = canViewOSPatchPolicies && canWriteOSPatch;
  const canSelectRows = canRemediate || canExclude;
  useEffect(() => {
    if (devicesView !== 'excluded' || exclusionPolicy) return undefined;
    let cancelled = false;
    axios.get(`${API}/exclusions`)
      .then(res => { if (!cancelled) setExclusionPolicy(res.data); })
      .catch(e => toast.error(_errText(e, 'Could not load the exclusion rules')));
    return () => { cancelled = true; };
  }, [devicesView, exclusionPolicy]);
  // Every filter on the list: the cards, the dropdowns, Online, the issue chip
  // and the search box. The view and its group or reason are not filters (they
  // are the banner's choice of list), so they stay.
  const filtersActive = healthFilter !== 'all' || complianceFilter !== 'all' || osVersionFilter !== 'all'
    || osBuildFilter !== 'all' || securityStatusFilter !== 'all' || editionFilter !== 'all'
    || onlineFilter || Boolean(issueFilter) || searchTerm.trim() !== '';
  const clearAllFilters = () => {
    setHealthFilter('all');
    setComplianceFilter('all');
    setOsVersionFilter('all');
    setOsBuildFilter('all');
    setSecurityStatusFilter('all');
    setEditionFilter('all');
    setOnlineFilter(false);
    setIssueFilter(null);
    setSearchTerm('');
  };
  const afterExclusionChange = () => {
    setExcludeRequest(null);
    setSelectedDeviceIds([]);
    setExclusionPolicy(null);
    loadReports();
  };
  // { deploymentId, label, devices } while the reminder modal is open.
  const [reminderRequest, setReminderRequest] = useState(null);
  const setDevicesView = (view) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      ['view', 'group', 'reason', 'show'].forEach(k => next.delete(k));
      if (view !== 'overview') next.set('view', view);
      return next;
    }, { replace: true });
    // Card, issue and selection state belong to the view they were made in.
    setHealthFilter('all');
    setIssueFilter(null);
    setSelectedDeviceIds([]);
  };
  // The cards sit above the banner in every view. Clicking one means "show me
  // these devices", which is the Overview list, so it leaves any other view.
  const selectCard = (filter) => {
    if (devicesView !== 'overview') setDevicesView('overview');
    setHealthFilter(filter);
  };
  // Devices excluded from reporting appear only in the Excluded view; every
  // other view, and the counts on the banner, work on the rest.
  const reportedDevices = useMemo(
    () => joinedDevices.filter(d => !d.excluded_reporting),
    [joinedDevices],
  );
  const excludedCount = useMemo(
    () => joinedDevices.filter(d => (d.excluded_scopes || []).length > 0).length,
    [joinedDevices],
  );
  const vmCount = useMemo(
    () => joinedDevices.filter(d => d.virtualisation && d.virtualisation !== 'physical').length,
    [joinedDevices],
  );
  const fixGroupsById = useMemo(() => {
    const m = new Map();
    reportedDevices.forEach(d => m.set(d.id, fixQueueGroupsFor(d, issuesById.get(d.id) || [])));
    return m;
  }, [reportedDevices, issuesById]);
  // Counted across every device, not the current filters, like the issue chips
  // before them: a fix targets the problem, not the view.
  const fixQueueCounts = useMemo(() => {
    const counts = { _all: 0 };
    fixGroupsById.forEach(keys => {
      if (keys.length) counts._all += 1;
      keys.forEach(k => { counts[k] = (counts[k] || 0) + 1; });
    });
    return counts;
  }, [fixGroupsById]);
  const ceBreachDevices = useMemo(
    () => reportedDevices.filter(d => d.ce_status === 'breach'),
    [reportedDevices],
  );
  const ceAtRiskCount = useMemo(
    () => reportedDevices.filter(d => d.ce_status === 'at_risk').length,
    [reportedDevices],
  );
  // The population the filters, cards and table below work on.
  const viewDevices = useMemo(() => {
    if (devicesView === 'excluded') {
      return excludedShow === 'vms'
        ? joinedDevices.filter(d => d.virtualisation && d.virtualisation !== 'physical')
        : joinedDevices.filter(d => (d.excluded_scopes || []).length > 0);
    }
    if (devicesView === 'fix_queue') {
      return reportedDevices.filter(d => {
        const keys = fixGroupsById.get(d.id) || [];
        return fixGroup ? keys.includes(fixGroup) : keys.length > 0;
      });
    }
    if (devicesView === 'ce_breach') {
      return reportedDevices.filter(d => (
        (d.ce_status === 'breach' || (ceIncludeAtRisk && d.ce_status === 'at_risk'))
        && (!ceReason || (d.ce_reasons || []).includes(ceReason))
      ));
    }
    return reportedDevices;
  }, [devicesView, joinedDevices, reportedDevices, excludedShow, fixGroupsById, fixGroup, ceIncludeAtRisk, ceReason]);
  const selectedDevice = selectedDeviceId ? joinedDevices.find(d => String(d.id) === String(selectedDeviceId)) : null;

  // Filter devices for the Devices table
  const healthDevices = viewDevices
    .filter(d => {
      if (healthFilter === 'all') return true;
      if (healthFilter === 'current') return d.security_status === 'current';
      if (healthFilter === 'outdated') return d.security_status === 'outdated';
      if (healthFilter === 'missing_one') return d.security_status === 'outdated' && d.updates_behind === 1;
      if (healthFilter === 'missing_multiple') return d.security_status === 'outdated' && d.updates_behind > 1;
      // `is_failed` is what the Failed Updates card counts — a device on the
      // latest build whose WUfB ClientState is Failed still belongs here even
      // though its security_status stays 'current'.
      if (healthFilter === 'failed') return d.is_failed ?? d.security_status === 'failed';
      if (healthFilter === 'stuck') return !!d.is_stuck;
      // 6 Oct cards: Stuck / Failed merged; firmware ready; feature update pending.
      if (healthFilter === 'stuck_or_failed') return !!d.is_stuck || !!(d.is_failed ?? d.security_status === 'failed');
      if (healthFilter === 'firmware_ready') return !!d.firmware_ready_to_automate;
      if (healthFilter === 'fu_pending') return !!d.feature_update_pending;
      return true;
    })
    .filter(d => {
      if (complianceFilter === 'all') return true;
      if (complianceFilter === 'compliant') return d.is_compliant;
      if (complianceFilter === 'non-compliant') return !d.is_compliant;
      return true;
    })
    .filter(d => {
      if (osVersionFilter === 'all') return true;
      const version = d.os_version || '';
      const parts = version.split('.');
      if (parts.length >= 3) {
        const majorBuild = parts[2];
        if (osVersionFilter === 'Windows 11 25H2' && majorBuild === '26200') return true;
        if (osVersionFilter === 'Windows 11 24H2' && majorBuild === '26100') return true;
        if (osVersionFilter === 'Windows 11 23H2' && majorBuild === '22631') return true;
        if (osVersionFilter === 'Windows 11 22H2' && majorBuild === '22621') return true;
        if (osVersionFilter === 'Windows 10 22H2' && majorBuild === '19045') return true;
        if (osVersionFilter === 'Windows 10 21H2' && majorBuild === '19044') return true;
      }
      return osVersionFilter === 'Other';
    })
    .filter(d => {
      // Patched-build filter (e.g. "Apr 2026 Security Update (24H2)").
      // Uses the same friendly-name resolver the rest of the report
      // shows so the dropdown labels and the device rows match.
      if (osBuildFilter === 'all') return true;
      return getVersionFriendlyName(d.os_version || '') === osBuildFilter;
    })
    .filter(d => {
      if (securityStatusFilter === 'all') return true;
      return d.security_status === securityStatusFilter;
    })
    .filter(d => !onlineFilter || isRecentlySynced(d))
    .filter(d => editionFilter === 'all' || d.edition_class === editionFilter)
    .filter(d => !issueFilter || (issuesById.get(d.id) || []).includes(issueFilter))
    // Name or user — the old Devices page searched both.
    .filter(d => {
      const q = searchTerm.toLowerCase();
      return !q || d.device_name?.toLowerCase().includes(q) || d.user_name?.toLowerCase().includes(q);
    });
  
  // Paginated health devices
  const paginatedHealthDevices = useMemo(() => {
    const start = (healthPage - 1) * healthPageSize;
    return healthDevices.slice(start, start + healthPageSize);
  }, [healthDevices, healthPage, healthPageSize]);
  
  // Reset health page when filters change
  useEffect(() => {
    setHealthPage(1);
  }, [healthFilter, complianceFilter, osVersionFilter, osBuildFilter, securityStatusFilter, searchTerm, onlineFilter, issueFilter, devicesView, fixGroup, ceReason, ceIncludeAtRisk, excludedShow, editionFilter]);

  // ---- Sprint staging (moved from the old Devices page, unchanged) ----
  const stagePhaseChange = (deviceId, phase) => {
    setPendingPhases((prev) => {
      const saved = deviceDocsById.get(String(deviceId))?.feature_update_phase || 'green';
      const next = { ...prev };
      // Clicking back to the saved value clears the pending change rather than
      // recording a no-op.
      if (phase === saved) delete next[deviceId];
      else next[deviceId] = phase;
      return next;
    });
  };
  const pendingPhaseCount = Object.keys(pendingPhases).length;
  const pendingPhaseList = useMemo(
    () => Object.entries(pendingPhases).map(([deviceId, phase]) => {
      const device = deviceDocsById.get(String(deviceId));
      return {
        device_id: deviceId,
        device_name: device?.device_name || deviceId,
        from: device?.feature_update_phase || 'green',
        phase,
      };
    }),
    [pendingPhases, deviceDocsById],
  );

  // ---- Remediation ----
  const openRemediation = (targetDevices, catalogKey) => {
    setRemediationRequest({ devices: targetDevices.map(remediationTargetOf), catalogKey: catalogKey || null });
  };
  // "Remediate selected": default to the script for the most common fixable
  // issue among the selection; the modal lets the admin choose another.
  const remediateSelected = () => {
    const chosen = joinedDevices.filter(d => selectedDeviceIds.includes(d.id));
    const tally = {};
    chosen.forEach(d => (issuesById.get(d.id) || []).forEach(c => {
      if (DEVICE_ISSUES[c].catalogKey) tally[c] = (tally[c] || 0) + 1;
    }));
    const top = Object.entries(tally).sort((a, b) => b[1] - a[1])[0];
    openRemediation(chosen, top ? DEVICE_ISSUES[top[0]].catalogKey : null);
  };
  const toggleDeviceSelected = (id) => setSelectedDeviceIds(prev => (
    prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
  ));
  // Device health names an unnamed device "Unknown"; searching for that finds
  // nothing, so fall back to the serial (the Firmware search covers serials).
  const openFirmwareFor = (device) => {
    const name = device.device_name && device.device_name !== 'Unknown' ? device.device_name : null;
    const key = name || device.serial_number || null;
    setActiveTab('firmware', key ? { device_search: key } : {});
  };

  // Export the Devices list to CSV — every field, including the ones that
  // live in the detail panel rather than the table.
  const handleExportHealthCSV = () => {
    const columns = [
      { header: 'Device Name', accessor: d => d.device_name },
      { header: 'User', accessor: d => d.user_name || '' },
      { header: 'Model', accessor: d => d.model || '' },
      { header: 'OS Version', accessor: d => d.os_version || '' },
      { header: 'Security Status', accessor: d => d.security_status || '' },
      { header: 'Updates Behind', accessor: d => (d.updates_behind ?? '') },
      { header: 'Last Sync', accessor: d => d.last_sync ? new Date(d.last_sync).toLocaleString() : '' },
      ...(canViewOSPatchPolicies
        ? [{ header: 'Deployment Sprint', accessor: d => PHASE_CONFIG[d.feature_update_phase || 'green']?.label || 'Main Deployment' }]
        : []),
      { header: 'Windows Edition', accessor: d => (d.edition_assumed ? 'Not reported (assumed Home/Pro)' : (d.edition || '')) },
      { header: 'OS Support End', accessor: d => d.os_support_end || '' },
      { header: 'ESU (detected)', accessor: d => (d.os_esu ? 'Yes' : 'No') },
      { header: 'Cyber Essentials', accessor: d => (d.ce_status === 'breach' ? 'Breach' : d.ce_status === 'at_risk' ? 'At risk' : 'No breach') },
      { header: 'App Business Criticality', accessor: d => _critOf(d.app_risk_category).label },
      { header: 'Free Disk (GB)', accessor: d => (d.free_storage_gb ?? '') },
      { header: 'BIOS Version', accessor: d => d.bios_version || '' },
      { header: 'Latest BIOS', accessor: d => d.latest_bios_version || '' },
      { header: 'Firmware Status', accessor: d => d.firmware_status || '' },
      { header: 'Firmware Ready to Automate', accessor: d => (d.firmware_ready_to_automate ? 'Yes' : 'No') },
      { header: 'Feature Update Target', accessor: d => d.feature_update_target || '' },
      { header: 'Feature Update Pending', accessor: d => (d.feature_update_pending ? 'Yes' : 'No') },
      { header: 'Issues', accessor: d => (issuesById.get(d.id) || []).map(c => DEVICE_ISSUES[c].label).join('; ') },
      { header: 'Client State', accessor: d => d.client_state || '' },
      { header: 'WUfB ClientState', accessor: d => d.wufb_client_state || '' },
      { header: 'WUfB ClientSubstate', accessor: d => d.wufb_client_substate || '' },
      { header: 'Client State Source', accessor: d => d.client_state_source || '' },
      { header: 'Hours In State', accessor: d => (d.hours_in_state ?? '') },
      { header: 'Stuck', accessor: d => (d.is_stuck ? 'Yes' : 'No') },
      { header: 'Last Boot', accessor: d => d.last_boot ? new Date(d.last_boot).toLocaleString() : '' },
      { header: 'Last Boot Source', accessor: d => d.last_boot_source || '' },
      { header: 'Last WU Scan', accessor: d => d.last_wu_scan ? new Date(d.last_wu_scan).toLocaleString() : '' },
      { header: 'Last WU Scan Source', accessor: d => d.last_wu_scan_source || '' },
      { header: 'Compliance', accessor: d => d.is_compliant ? 'Compliant' : 'Non-Compliant' }
    ];
    exportToCSV(healthDevices, columns, 'devices_export');
  };
  
  // Reset win11 page when filters change
  useEffect(() => {
    setWin11Page(1);
  }, [win11Filter, win11Search]);

  // WUfB save handler
  const handleSaveWufb = async () => {
    setSavingWufb(true);
    try {
      await axios.post(`${API}/wufb-integration/settings`, wufbForm);
      toast.success('WUfB integration settings saved');
      loadReports();
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSavingWufb(false);
    }
  };

  const handleDisconnectWufb = async () => {
    if (!window.confirm('Are you sure you want to disconnect WUfB reporting integration?')) return;
    try {
      await axios.delete(`${API}/wufb-integration/settings`);
      toast.success('WUfB integration disconnected');
      setWufbForm({ subscription_id: '', workspace_id: '' });
      loadReports();
    } catch (error) {
      toast.error('Failed to disconnect');
    }
  };

  const ReportTabButton = ({ id, label, icon: Icon }) => (
    <button
      onClick={() => setActiveTab(id)}
      data-testid={`tab-${id}`}
      className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
        activeTab === id 
          ? 'bg-cyan-500 text-white' 
          : 'bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] text-white/90 hover:bg-white/[0.04]'
      }`}
    >
      <Icon className="w-4 h-4" />
      {label}
    </button>
  );

  // Drill-down card for Device Health — restyled 2026-06-15 to match
  // Apps Management > Discover (glass-blue tile, white value, coloured
  // icon chip + ring on the active filter). The previous coloured
  // gradient background made some filter values low-contrast.
  // 6 Oct: `subtitle` replaces the fixed "devices" line where a card has more
  // to say, and `progressPct` draws a thin progress line (On Latest Update).
  const HealthDrillCard = ({
    label, count, icon: Icon, color, onClick, isActive,
    subtitle = 'devices', progressPct = null, progressColor = null, testId,
  }) => (
    <div
      onClick={onClick}
      role="button"
      tabIndex={0}
      aria-pressed={!!isActive}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
      className={`bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border rounded-xl p-5 cursor-pointer transition-all hover:scale-[1.02] ${
        isActive ? 'ring-2 ring-offset-2 ring-offset-slate-900' : 'border-[rgba(229,239,250,0.1)]'
      }`}
      style={{
        ...(isActive && { borderColor: color, '--tw-ring-color': `${color}66` }),
      }}
      data-testid={testId}
    >
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-medium uppercase tracking-wider text-white/90">{label}</span>
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${color}33` }}>
          <Icon className="w-5 h-5" style={{ color }} />
        </div>
      </div>
      <div className="text-4xl font-bold text-white mb-1">{count}</div>
      <div className="text-sm text-white/90">{subtitle}</div>
      {progressPct != null && (
        <div className="mt-3 h-1.5 bg-slate-700/70 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{ width: `${Math.min(100, Math.max(0, progressPct))}%`, backgroundColor: progressColor || color }}
          />
        </div>
      )}
    </div>
  );

  // An old `?tab=update-rings` / `?tab=feature-updates` link: those tabs
  // live on Deployments now. After every hook, so hook order never changes.
  const movedTo = MOVED_DISCOVER_TABS[searchParams.get('tab')];
  if (movedTo) {
    return <Navigate to={`/os-patch/groups?deployTab=${movedTo}`} replace />;
  }

  return (
    <div className="p-8" data-testid="os-patch-discover">
      {/* Header */}
      <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Discover</h1>
          <p className="text-white/90">Comprehensive Windows Update deployment reporting</p>
          {lastIntuneSync?.completed_at && (
            <p className="text-xs text-white/60 mt-1" data-testid="reports-last-intune-sync">
              Last synced from Intune: {new Date(lastIntuneSync.completed_at).toLocaleString()}
              {lastIntuneSync.trigger === 'nightly_scheduler' && ' (automatic)'}
            </p>
          )}
          <div className="mt-2">
            <NextPatchTuesdayBadge />
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="flex items-center gap-2">
            <Button
              onClick={loadReports}
              className="bg-[#E5EFFA] text-[#3C02CC] hover:bg-[#BBFF71] hover:text-[#3C02CC] active:bg-[#BBFF71] active:text-[#3C02CC] font-semibold"
              data-testid="refresh-reports-btn"
              title="Reload all report data already in your database. Does not call Intune — for that, use Sync from Intune on the OS Patch Management Dashboard, or the per-report 'Refresh from Intune' buttons."
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Reload reports
            </Button>
          </div>
          {/* `scott/kpi-cycle-update2` — the Windows Update catalog freshness
              badge + manual sync moved to Settings > Data Management. It is
              data plumbing: customers should be able to trust that ALICE knows
              when each Windows build shipped, not be asked to reason about
              catalog coverage while reading a compliance report. */}
        </div>
      </div>

      <OSPatchReadOnlyNotice className="mb-4" />

      {/* Tabs — re-ordered 2026-06-15 (scott/ospatch-ui Phase 1):
          Reports → Discover; new Compliance tab carries the Quality Update
          Compliance Trending section that used to live on Update Rings; the
          Update Rings tab is renamed Quality Updates and now also carries
          the Risk Analysis content; WUfB Integration moved to Settings >
          Integrations. */}
      <div className="flex flex-wrap gap-2 mb-8">
        {/* October 2026: Overview renamed Devices, and Device Health folded
            into it (see the DEVICES TAB block below). */}
        {/* October 2026 (6 Oct): Quality Updates and Feature Updates moved to
            Deployments, beside the rings and deployments they report on. */}
        <ReportTabButton id="devices" label="Devices" icon={Monitor} />
        <ReportTabButton id="compliance" label="Compliance" icon={Shield} />
        <ReportTabButton id="firmware" label="Firmware" icon={Cpu} />
        <ReportTabButton id="win11-readiness" label="Windows 11 Readiness" icon={Rocket} />
        {/* Phase 5 REDESIGN 2026-07-25: Firmware · Devices tab merged
            into the primary Firmware tab (OEM catalogue, KPI tiles,
            compliance chart now live inside FirmwareDiscoverTab.jsx).
            FirmwareReportTab.jsx is kept on disk for future device-list
            re-use but is no longer surfaced as a top-level tab. */}
      </div>

      {loading ? (
        <LoadingSpinner message="Loading reports..." />
      ) : (
        <>
          {/* ==================== DEVICES TAB ====================
              October 2026 (scott/ospatch-discover-redesign): the former
              Overview tab, renamed, with the Device Health tab folded in
              below the compliance banner. Old `?tab=overview` and
              `?tab=device-health` links resolve here (resolveDiscoverTab). */}
          {activeTab === 'devices' && (
            <>
              {/* One line above the cards (6 Oct): WUfB connection, coverage
                  and last client-state sync. These were two lines above the
                  cards and a coverage banner below them. An empty Stuck /
                  Failed card is ambiguous on its own — nothing stuck, or no
                  WUfB data — so the coverage stays, in amber when it is zero. */}
              {(() => {
                const sourced = healthMetrics.wufb_sourced_devices || 0;
                const total = healthMetrics.total_devices || 0;
                const connected = !!wufbSettings?.is_connected;
                const syncedAt = deviceHealthReport?.client_state_synced_at;
                const warn = connected && total > 0 && sourced === 0;
                return (
                  <div
                    className={`mb-4 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm ${warn ? 'text-amber-200' : 'text-white/80'}`}
                    data-testid="device-health-wufb-coverage"
                  >
                    {warn ? <AlertTriangle className="w-4 h-4 shrink-0" /> : <Info className="w-4 h-4 shrink-0 text-cyan-300" />}
                    {!connected ? (
                      <span>Client states come from Intune compliance. Connect WUfB Integration (Settings &gt; Integrations) for Windows Update client states.</span>
                    ) : warn ? (
                      <span>WUfB is connected but <strong>no client states have been ingested yet</strong> — Client State falls back to Intune compliance. Use <strong>Sync WUfB</strong> below.</span>
                    ) : (
                      <span>
                        Client states from Windows Update for Business (Log Analytics) for <strong>{sourced}</strong> of <strong>{total}</strong> devices
                        {sourced < total && <span className="text-white/60"> · the rest from Intune (<em>via Intune</em>)</span>}
                      </span>
                    )}
                    {connected && (
                      <span className="text-white/50">
                        · {syncedAt ? `synced ${new Date(syncedAt).toLocaleString()}` : 'not synced yet'}
                      </span>
                    )}
                  </div>
                );
              })()}

              {/* Cards (6 Oct). Overall patch compliance lives in On Latest
                  Update; Stuck and Failed are one card; Ready to Automate is
                  the Firmware tab's card (same family-level rule, computed by
                  the backend's shared predicate) and Feature Updates counts
                  devices an ALICE deployment targets that are not yet on its
                  target release. Each filters the list below. */}
              {(() => {
                const total = healthMetrics.total_devices || 0;
                const onLatest = healthMetrics.on_latest_security_update || 0;
                const latestPct = total > 0 ? (onLatest / total) * 100 : 0;
                const latestColor = latestPct >= 80 ? '#22c55e' : latestPct >= 50 ? '#f59e0b' : total > 0 ? '#ef4444' : '#64748b';
                const ready = healthMetrics.firmware_ready_to_automate || 0;
                const readyPct = total > 0 ? Math.round((ready / total) * 100) : 0;
                return (
                  <div className="alice-kpi-row grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="devices-cards">
                    <HealthDrillCard
                      label="Total Devices"
                      count={total}
                      icon={Monitor}
                      color="#00D4E4"
                      onClick={() => selectCard('all')}
                      isActive={healthFilter === 'all'}
                      testId="devices-card-total"
                    />
                    <HealthDrillCard
                      label="On Latest Update"
                      count={`${latestPct.toFixed(0)}%`}
                      subtitle={`${onLatest} ${onLatest === 1 ? 'device' : 'devices'} on latest`}
                      progressPct={latestPct}
                      progressColor={latestColor}
                      icon={CheckCircle}
                      color="#22C55E"
                      onClick={() => selectCard('current')}
                      isActive={healthFilter === 'current'}
                      testId="devices-card-latest"
                    />
                    <HealthDrillCard
                      label="Missing 1 Update"
                      count={healthMetrics.missing_one_update || 0}
                      icon={Clock}
                      color="#F59E0B"
                      onClick={() => selectCard('missing_one')}
                      isActive={healthFilter === 'missing_one'}
                      testId="devices-card-missing-one"
                    />
                    <HealthDrillCard
                      label="Missing 2+ Updates"
                      count={healthMetrics.missing_multiple_updates || 0}
                      icon={AlertTriangle}
                      color="#FC5442"
                      onClick={() => selectCard('missing_multiple')}
                      isActive={healthFilter === 'missing_multiple'}
                      testId="devices-card-missing-multiple"
                    />
                    <HealthDrillCard
                      label="Stuck / Failed"
                      count={healthMetrics.stuck_or_failed_devices ?? ((healthMetrics.stuck_devices || 0) + (healthMetrics.failed_updates || 0))}
                      subtitle={`${healthMetrics.stuck_devices || 0} stuck (${healthMetrics.stuck_threshold_hours || 48}h+) · ${healthMetrics.failed_updates || 0} failed`}
                      icon={Pause}
                      color="#EF4444"
                      onClick={() => selectCard('stuck_or_failed')}
                      isActive={healthFilter === 'stuck_or_failed'}
                      testId="devices-card-stuck-failed"
                    />
                    {canViewOSPatchPolicies && (
                      <HealthDrillCard
                        label="Feature Updates"
                        count={healthMetrics.feature_update_pending || 0}
                        subtitle="targeted, not yet on target"
                        icon={Rocket}
                        color="#06B6D4"
                        onClick={() => selectCard('fu_pending')}
                        isActive={healthFilter === 'fu_pending'}
                        testId="devices-card-feature-updates"
                      />
                    )}
                    {/* 7 Oct: renamed (it is the Firmware tab's card) and moved
                        after Feature Updates. */}
                    <HealthDrillCard
                      label="Firmware Ready to Automate"
                      count={ready}
                      subtitle={`${readyPct}% of devices · ${healthMetrics.firmware_ready_needing_update || 0} need a BIOS update`}
                      icon={Cpu}
                      color="#8B5CF6"
                      onClick={() => selectCard('firmware_ready')}
                      isActive={healthFilter === 'firmware_ready'}
                      testId="devices-card-firmware-ready"
                    />
                    {/* 7 Oct: OS Version Distribution became the last card. It
                        does not filter: clicking it opens the full chart below
                        the cards (whose bars do filter the list). */}
                    {(() => {
                      const top = osVersionDataAll[0];
                      const topPct = top && totalDevices > 0 ? Math.round((top.count / totalDevices) * 100) : 0;
                      return (
                        // Wrapped so the outside-click handler can tell a click
                        // on this card (which toggles) from one elsewhere.
                        <div ref={osVersionCardRef} className="grid">
                        <HealthDrillCard
                          label="OS Versions"
                          count={osVersionDataAll.length}
                          subtitle={top ? `Most common: ${top.friendlyName} (${topPct}%)` : 'No OS version data'}
                          icon={Layers}
                          color="#00D4E4"
                          onClick={() => setOsVersionSectionCollapsed(v => !v)}
                          isActive={!osVersionSectionCollapsed}
                          testId="devices-card-os-versions"
                        />
                        </div>
                      );
                    })()}
                  </div>
                );
              })()}

              {/* OS Version Distribution — moved here in October 2026 from the
                  Overview charts row. Overview became Devices; the KPI row it
                  sat beside was dropped because the drill cards below carry
                  the same counts, and Quality Update Compliance by Ring moved
                  to the Quality Updates tab. It describes the same population
                  the device list shows, so it lives with it, collapsed by
                  default to keep the list near the top. It stays off the
                  policy-gated tabs so reduced-plan tenants keep it. */}
              {/* Shown only when the OS Versions card is open (7 Oct). */}
              {!osVersionSectionCollapsed && (
              <Card ref={osVersionChartRef} className="mb-8 bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border-[rgba(229,239,250,0.1)]" data-testid="os-version-distribution-card">
                <CardHeader
                  className="pb-2 cursor-pointer select-none"
                  onClick={() => setOsVersionSectionCollapsed(v => !v)}
                  data-testid="os-version-distribution-header"
                >
                  <CardTitle className="text-white flex items-center gap-2">
                    <ChevronDown
                      className={`w-4 h-4 text-white/60 transition-transform ${osVersionSectionCollapsed ? '-rotate-90' : ''}`}
                    />
                    <Layers className="w-5 h-5 text-cyan-400" />
                    OS Version Distribution
                    {osVersionSectionCollapsed && osVersionDataAll.length > 0 && (
                      <span className="text-xs text-white/70 font-normal ml-2">
                        {osVersionDataAll.length} {osVersionDataAll.length === 1 ? 'version' : 'versions'}
                      </span>
                    )}
                  </CardTitle>
                  <p className="text-sm text-white/90 mt-1">Based on latest security update level · click a version to list its devices</p>
                </CardHeader>
                {!osVersionSectionCollapsed && (
                <CardContent>
                  {osVersionDataAll.length > 0 ? (
                    <div className="space-y-3">
                      {(osVersionExpanded ? osVersionDataAll : osVersionData).map((item, index) => {
                        const pct = totalDevices > 0 ? (item.count / totalDevices) * 100 : 0;
                        // Determine color based on update recency
                        // Colour from the backend's per-version patch level, dated
                        // exactly like the compliance figures above. (This used to
                        // match the month names "apr 2026" / "mar 2026" literally.)
                        const friendlyName = item.friendlyName.toLowerCase();
                        const versionStatus = updateReport?.os_version_status?.[item.fullVersion]?.status;
                        let barColor = '#ef4444'; // Default red for outdated
                        let statusIcon = '🔴';
                        if (friendlyName.includes('preview')) {
                          barColor = '#3b82f6'; // Blue - preview
                          statusIcon = '🔵';
                        } else if (versionStatus === 'current') {
                          barColor = '#22c55e'; // Green - latest
                          statusIcon = '🟢';
                        } else if (versionStatus === 'n-1') {
                          barColor = '#f59e0b'; // Amber - N-1
                          statusIcon = '🟡';
                        }
                        
                        // Clicking a bar filters the device list below to that
                        // build (the existing Build filter), and again clears it.
                        const isActiveBuild = osBuildFilter === item.friendlyName;
                        return (
                          <div
                            key={index}
                            className={`group cursor-pointer rounded px-1 -mx-1 transition-colors ${isActiveBuild ? 'bg-cyan-500/15 ring-1 ring-cyan-400/40' : 'hover:bg-white/5'}`}
                            role="button"
                            tabIndex={0}
                            aria-pressed={isActiveBuild}
                            onClick={() => {
                              setOsVersionFilter('all');
                              setOsBuildFilter(isActiveBuild ? 'all' : item.friendlyName);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setOsVersionFilter('all');
                                setOsBuildFilter(isActiveBuild ? 'all' : item.friendlyName);
                              }
                            }}
                            title={isActiveBuild ? 'Click to clear the build filter' : 'Click to list these devices below'}
                            data-testid={`os-version-bar-${index}`}
                          >
                            <div className="flex items-center justify-between mb-1">
                              <div className="flex items-center gap-2">
                                <span className="text-xs">{statusIcon}</span>
                                <span className="text-sm text-white font-medium">{item.friendlyName}</span>
                                <span className="text-xs text-white/40 font-mono">{item.fullVersion}</span>
                              </div>
                              <span className="text-sm text-white">
                                {item.count} {item.count === 1 ? 'device' : 'devices'} ({pct.toFixed(0)}%)
                              </span>
                            </div>
                            <div className="h-6 bg-slate-700/50 rounded overflow-hidden">
                              <div 
                                className="h-full rounded transition-all duration-300 flex items-center px-2"
                                style={{ 
                                  width: `${Math.max(pct, 5)}%`, 
                                  backgroundColor: barColor 
                                }}
                              >
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      {/* Legend */}
                      <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t border-[rgba(229,239,250,0.1)]">
                        <div className="flex items-center gap-1.5 text-xs text-white/90">
                          <span>🟢</span> Latest{latestPatchMonthLabel ? ` (${latestPatchMonthLabel})` : ''}
                        </div>
                        <div className="flex items-center gap-1.5 text-xs text-white/90">
                          <span>🟡</span> N-1{previousPatchMonthLabel ? ` (${previousPatchMonthLabel})` : ''}
                        </div>
                        <div className="flex items-center gap-1.5 text-xs text-white/90">
                          <span>🔴</span> Outdated (N-2+)
                        </div>
                        <div className="flex items-center gap-1.5 text-xs text-white/90">
                          <span>🔵</span> Preview
                        </div>
                      </div>
                      {/* Show-more toggle — keeps the default view at 10
                          so big tenants don't get a 30-row wall of bars,
                          but lets admins drill into every distinct build
                          on demand. */}
                      {osVersionDataAll.length > osVersionDefaultLimit && (
                        <div className="pt-2 flex justify-center">
                          <button
                            type="button"
                            onClick={() => setOsVersionExpanded(v => !v)}
                            className="text-xs text-cyan-300 hover:text-cyan-200 underline underline-offset-2 transition-colors"
                            data-testid="os-version-toggle-expand"
                          >
                            {osVersionExpanded
                              ? `Show fewer (top ${osVersionDefaultLimit} only)`
                              : `Show all ${osVersionDataAll.length} versions (${osVersionDataAll.length - osVersionDefaultLimit} more)`}
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-white/90 text-center py-8">No OS version data available</p>
                  )}
                </CardContent>
                )}
              </Card>
              )}

              {/* Devices banner (October 2026), the same pattern as the
                  Vulnerabilities page in Apps Management: Overview is the
                  default view, Fix Queue and Cyber Essentials Breach narrow the
                  list to devices that need something done. */}
              <div
                className="mb-4 inline-flex flex-wrap rounded-lg bg-white/[0.06] p-1 border border-white/[0.1]"
                role="tablist"
                data-testid="devices-view-tabs"
              >
                {[
                  { id: 'overview', label: 'Overview', icon: Activity },
                  { id: 'fix_queue', label: 'Fix Queue', icon: ListChecks, count: fixQueueCounts._all, tone: 'warning' },
                  { id: 'ce_breach', label: 'Cyber Essentials Breach', icon: ShieldAlert, count: ceBreachDevices.length, tone: 'danger' },
                  { id: 'excluded', label: 'Excluded', icon: EyeOff, count: excludedCount, tone: 'neutral' },
                ].map((tab) => {
                  const Icon = tab.icon;
                  const active = devicesView === tab.id;
                  return (
                    <button
                      key={tab.id}
                      type="button"
                      role="tab"
                      aria-selected={active}
                      onClick={() => setDevicesView(tab.id)}
                      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition ${
                        active ? 'bg-cyan-600/70 text-white shadow' : 'text-white/70 hover:text-white hover:bg-white/[0.06]'
                      }`}
                      data-testid={`devices-view-${tab.id}`}
                    >
                      <Icon className="w-4 h-4" />
                      {tab.label}
                      {tab.count > 0 && (
                        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                          tab.tone === 'danger' ? 'bg-red-600 text-white' : tab.tone === 'neutral' ? 'bg-white/25 text-white' : 'bg-amber-500 text-black'
                        }`}>
                          {tab.count}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Fix Queue: the second banner, one chip per group, then what
                  can be done about the chosen group. */}
              {devicesView === 'fix_queue' && (
                <div className="mb-4 space-y-3" data-testid="fix-queue-panel">
                  <div className="flex flex-wrap items-center gap-2" data-testid="fix-queue-groups">
                    <span className="text-xs uppercase tracking-wider text-white/60 mr-1">Issues</span>
                    <button
                      type="button"
                      onClick={() => { setUrlParam('group', null); setIssueFilter(null); }}
                      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs text-white ${!fixGroup ? 'border-cyan-400 bg-cyan-500/20' : 'border-white/15 bg-white/5 hover:bg-white/10'}`}
                      data-testid="fix-queue-group-all"
                    >
                      All <span className="font-semibold">{fixQueueCounts._all}</span>
                    </button>
                    {FIX_QUEUE_ORDER.filter(key => fixQueueCounts[key] > 0 && (key !== 'feature_update' || canViewOSPatchPolicies)).map(key => {
                      const def = FIX_QUEUE_GROUPS[key];
                      const Icon = def.icon;
                      const active = fixGroup === key;
                      return (
                        <button
                          key={key}
                          type="button"
                          title={def.why}
                          onClick={() => { setUrlParam('group', active ? null : key); setIssueFilter(null); }}
                          className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs text-white ${active ? 'border-cyan-400 bg-cyan-500/20' : 'border-white/15 bg-white/5 hover:bg-white/10'}`}
                          data-testid={`fix-queue-group-${key}`}
                        >
                          <Icon className={`w-3.5 h-3.5 ${def.cls}`} />
                          {def.label}
                          <span className="font-semibold">{fixQueueCounts[key]}</span>
                        </button>
                      );
                    })}
                  </div>
                  {fixQueueCounts._all === 0 && (
                    <div className="rounded-lg border border-green-500/30 bg-green-500/10 px-4 py-3 text-sm text-green-100" data-testid="fix-queue-empty">
                      Nothing needs fixing: every Windows device is current, checking in and free of detected issues.
                    </div>
                  )}
                  {fixGroup && (() => {
                    const def = FIX_QUEUE_GROUPS[fixGroup];
                    const codeRows = def.issueCodes
                      .map(code => ({ code, devs: viewDevices.filter(d => (issuesById.get(d.id) || []).includes(code)) }))
                      .filter(r => r.devs.length > 0);
                    // Feature update reminders go out per deployment: the route
                    // needs the deployment for its template and comms dates.
                    const byDeployment = new Map();
                    if (fixGroup === 'feature_update') {
                      viewDevices.forEach(d => {
                        if (!d.feature_update_deployment_id) return;
                        const entry = byDeployment.get(d.feature_update_deployment_id)
                          || { name: d.feature_update_deployment_name || 'Feature update', target: d.feature_update_target, devs: [] };
                        entry.devs.push(d);
                        byDeployment.set(d.feature_update_deployment_id, entry);
                      });
                    }
                    const notCheckingIn = fixGroup === 'not_current'
                      ? viewDevices.filter(d => !isRecentlySynced(d)).length : 0;
                    return (
                      <div className="rounded-lg border border-white/15 bg-white/5 p-4" data-testid={`fix-queue-actions-${fixGroup}`}>
                        <p className="text-sm text-white/90 mb-3">
                          <span className="font-semibold text-white">{def.label}.</span> {def.why}
                        </p>
                        <div className="space-y-2">
                          {codeRows.map(({ code, devs }) => {
                            const issue = DEVICE_ISSUES[code];
                            const Icon = issue.icon;
                            const active = issueFilter === code;
                            return (
                              <div key={code} className="flex flex-wrap items-center gap-3 text-sm" data-testid={`fix-action-${code}`}>
                                <Icon className={`w-4 h-4 shrink-0 ${issue.cls}`} />
                                <span className="text-white">{issue.label}</span>
                                <span className="font-semibold text-white">{devs.length}</span>
                                <span className="text-white/60 text-xs flex-1 min-w-[12rem]">{issue.why}</span>
                                {codeRows.length > 1 && (
                                  <button type="button" onClick={() => setIssueFilter(active ? null : code)}
                                          className="text-xs text-cyan-200 hover:text-white underline">
                                    {active ? 'Show all in group' : 'Show only these'}
                                  </button>
                                )}
                                {code === 'firmware' ? (
                                  <Button size="sm" variant="outline" className="border-white/20 text-white hover:bg-white/10"
                                          onClick={() => setActiveTab('firmware')} data-testid="fix-action-view-firmware">
                                    Open Firmware
                                  </Button>
                                ) : canRemediate && (
                                  <Button size="sm" onClick={() => openRemediation(devs, issue.catalogKey)}
                                          className="bg-[#E5EFFA] text-[#3C02CC] hover:bg-[#BBFF71] hover:text-[#3C02CC] font-semibold"
                                          data-testid={`fix-action-remediate-${code}`}>
                                    <Wrench className="w-3.5 h-3.5 mr-1.5" /> Remediate…
                                  </Button>
                                )}
                              </div>
                            );
                          })}
                          {fixGroup === 'feature_update' && (
                            <>
                              {[...byDeployment.entries()].map(([depId, entry]) => (
                                <div key={depId} className="flex flex-wrap items-center gap-3 text-sm" data-testid={`fix-action-email-${depId}`}>
                                  <Mail className="w-4 h-4 shrink-0 text-cyan-300" />
                                  <span className="text-white">{entry.name}</span>
                                  <span className="font-semibold text-white">{entry.devs.length}</span>
                                  <span className="text-white/60 text-xs flex-1 min-w-[12rem]">
                                    Pending{entry.target ? ` ${entry.target}` : ''}. Email the signed-in users to start the update.
                                  </span>
                                  {canManageOSPatches && (
                                    <Button size="sm" onClick={() => setReminderRequest({
                                      deploymentId: depId,
                                      label: entry.name,
                                      devices: entry.devs.map(d => ({
                                        device_id: d.intune_id || d.id,
                                        device_name: d.device_name,
                                        user: d.user_name || '',
                                        status: '',
                                      })),
                                    })}
                                      className="bg-[#E5EFFA] text-[#3C02CC] hover:bg-[#BBFF71] hover:text-[#3C02CC] font-semibold"
                                      data-testid={`fix-action-email-btn-${depId}`}>
                                      <Mail className="w-3.5 h-3.5 mr-1.5" /> Email users…
                                    </Button>
                                  )}
                                </div>
                              ))}
                              {viewDevices.some(d => !d.feature_update_deployment_id) && (
                                <p className="text-xs text-white/60">
                                  Some devices have no deployment to send a reminder from.
                                </p>
                              )}
                              <Button size="sm" variant="outline" className="border-white/20 text-white hover:bg-white/10"
                                      onClick={() => navigate('/os-patch/groups?deployTab=feature-updates')}
                                      data-testid="fix-action-open-deployments">
                                <Rocket className="w-3.5 h-3.5 mr-1.5" /> Open Feature Update Deployments
                              </Button>
                            </>
                          )}
                          {fixGroup === 'stale' && (
                            <>
                              <div className="flex flex-wrap items-center gap-3 text-sm" data-testid="fix-action-email-stale">
                                <Mail className="w-4 h-4 shrink-0 text-cyan-300" />
                                <span className="text-white">Ask the users to update</span>
                                <span className="font-semibold text-white">{viewDevices.length}</span>
                                <span className="text-white/60 text-xs flex-1 min-w-[12rem]">
                                  Email each user to power on, open Windows Update, check for updates, install and restart.
                                </span>
                                {canManageOSPatches && (
                                  <Button size="sm" onClick={() => setReminderRequest({
                                    endpoint: `${API}/devices/send-update-reminder`,
                                    eyebrow: 'Stale device reminder',
                                    label: 'Stale devices',
                                    defaultSubject: DEFAULT_STALE_REMINDER_SUBJECT,
                                    defaultBody: DEFAULT_STALE_REMINDER_BODY,
                                    devices: viewDevices.map(d => ({
                                      device_id: d.intune_id || d.id, device_name: d.device_name, user: d.user_name || '', status: '',
                                    })),
                                  })}
                                    className="bg-[#E5EFFA] text-[#3C02CC] hover:bg-[#BBFF71] hover:text-[#3C02CC] font-semibold"
                                    data-testid="fix-action-email-stale-btn">
                                    <Mail className="w-3.5 h-3.5 mr-1.5" /> Email users…
                                  </Button>
                                )}
                              </div>
                              <p className="text-xs text-white/60">
                                ALICE cannot run anything on a device that is not reporting to Intune, so the user is the way to
                                reach it. Devices that are retired or switched off for good should be removed from Intune instead.
                              </p>
                            </>
                          )}
                          {notCheckingIn > 0 && (
                            <p className="text-xs text-amber-200/90">
                              {notCheckingIn} of these {notCheckingIn === 1 ? "isn't" : "aren't"} checking in, so
                              remediation can only reach the rest. They also appear under Stale devices.
                            </p>
                          )}
                          {!canRemediate && codeRows.some(r => r.code !== 'firmware') && (
                            <p className="text-xs text-white/50">Running remediation needs Manager access on a plan with update policies.</p>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* Cyber Essentials Breach: out of support for the device's
                  edition, or not on the latest update past the 14-day window. */}
              {devicesView === 'ce_breach' && (() => {
                const reasonCounts = {};
                reportedDevices.forEach(d => {
                  if (d.ce_status === 'breach' || (ceIncludeAtRisk && d.ce_status === 'at_risk')) {
                    (d.ce_reasons || []).forEach(r => { reasonCounts[r] = (reasonCounts[r] || 0) + 1; });
                  }
                });
                const behind = viewDevices.filter(d => (d.ce_reasons || []).some(r => r === 'not_current' || r === 'patch_window') && isRecentlySynced(d));
                const unsupportedPending = viewDevices.filter(d => d.feature_update_pending && d.feature_update_deployment_id);
                const unsupported = viewDevices.filter(d => (d.ce_reasons || []).includes('os_unsupported'));
                const byDeployment = new Map();
                unsupportedPending.forEach(d => {
                  const entry = byDeployment.get(d.feature_update_deployment_id)
                    || { name: d.feature_update_deployment_name || 'Feature update', devs: [] };
                  entry.devs.push(d);
                  byDeployment.set(d.feature_update_deployment_id, entry);
                });
                return (
                  <div className="mb-4 space-y-3" data-testid="ce-panel">
                    <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-100" data-testid="ce-banner">
                      <p>
                        <strong>Cyber Essentials:</strong> a device breaches when its Windows release is out of support for its
                        edition (Enterprise and Education get 36 months, Home and Pro 24), or it is not on the latest security
                        update more than {healthMetrics.ce_patch_window_days ?? 14} days after release.
                      </p>
                      {(healthMetrics.esu_devices || 0) > 0 && (
                        <p className="mt-1 text-red-100/80" data-testid="ce-esu-note">
                          {healthMetrics.esu_devices} Windows 10 device{healthMetrics.esu_devices === 1 ? ' is' : 's are'} under
                          Extended Security Updates (detected from a monthly update released after support ended) and
                          {healthMetrics.esu_devices === 1 ? ' is' : ' are'} judged on the monthly update alone.
                        </p>
                      )}
                      {(healthMetrics.edition_unknown_devices || 0) > 0 && (
                        <p className="mt-1 text-red-100/80">
                          {healthMetrics.edition_unknown_devices} device{healthMetrics.edition_unknown_devices === 1 ? ' has' : 's have'} no
                          edition from Intune yet and {healthMetrics.edition_unknown_devices === 1 ? 'is' : 'are'} assessed as Home/Pro
                          (the shorter lifecycle). The next device sync fills editions in.
                        </p>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-2" data-testid="ce-reasons">
                      <span className="text-xs uppercase tracking-wider text-white/60 mr-1">Reason</span>
                      <button type="button" onClick={() => setUrlParam('reason', null)}
                              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs text-white ${!ceReason ? 'border-cyan-400 bg-cyan-500/20' : 'border-white/15 bg-white/5 hover:bg-white/10'}`}
                              data-testid="ce-reason-all">
                        All
                      </button>
                      {CE_REASON_ORDER.filter(r => reasonCounts[r] > 0).map(r => (
                        <button key={r} type="button" title={CE_REASONS[r].why}
                                onClick={() => setUrlParam('reason', ceReason === r ? null : r)}
                                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs text-white ${ceReason === r ? 'border-cyan-400 bg-cyan-500/20' : 'border-white/15 bg-white/5 hover:bg-white/10'}`}
                                data-testid={`ce-reason-${r}`}>
                          <ShieldAlert className={`w-3.5 h-3.5 ${CE_REASONS[r].cls}`} />
                          {CE_REASONS[r].label}
                          <span className="font-semibold">{reasonCounts[r]}</span>
                        </button>
                      ))}
                      <label className="ml-2 inline-flex items-center gap-2 text-xs text-white/80 cursor-pointer select-none"
                             data-testid="ce-include-at-risk">
                        <input type="checkbox" className="accent-cyan-400" checked={ceIncludeAtRisk}
                               onChange={(e) => setCeIncludeAtRisk(e.target.checked)} />
                        Also show at risk ({ceAtRiskCount})
                      </label>
                    </div>
                    {viewDevices.length > 0 && (
                      <div className="rounded-lg border border-white/15 bg-white/5 p-4 space-y-2" data-testid="ce-actions">
                        {behind.length > 0 && (
                          <div className="flex flex-wrap items-center gap-3 text-sm">
                            <AlertTriangle className="w-4 h-4 shrink-0 text-amber-300" />
                            <span className="text-white">Behind on updates, checking in</span>
                            <span className="font-semibold text-white">{behind.length}</span>
                            <span className="text-white/60 text-xs flex-1 min-w-[12rem]">{DEVICE_ISSUES.behind_checking_in.why}</span>
                            {canRemediate && (
                              <Button size="sm" onClick={() => openRemediation(behind, DEVICE_ISSUES.behind_checking_in.catalogKey)}
                                      className="bg-[#E5EFFA] text-[#3C02CC] hover:bg-[#BBFF71] hover:text-[#3C02CC] font-semibold"
                                      data-testid="ce-action-remediate">
                                <Wrench className="w-3.5 h-3.5 mr-1.5" /> Remediate…
                              </Button>
                            )}
                          </div>
                        )}
                        {unsupported.length > 0 && (
                          <div className="flex flex-wrap items-center gap-3 text-sm">
                            <Rocket className="w-4 h-4 shrink-0 text-cyan-300" />
                            <span className="text-white">Out of support</span>
                            <span className="font-semibold text-white">{unsupported.length}</span>
                            <span className="text-white/60 text-xs flex-1 min-w-[12rem]">
                              Only a feature update fixes these. Plan one in Feature Update Deployments.
                            </span>
                            <Button size="sm" variant="outline" className="border-white/20 text-white hover:bg-white/10"
                                    onClick={() => navigate('/os-patch/groups?deployTab=feature-updates')}
                                    data-testid="ce-action-open-deployments">
                              Open Feature Update Deployments
                            </Button>
                          </div>
                        )}
                        {[...byDeployment.entries()].map(([depId, entry]) => (
                          <div key={depId} className="flex flex-wrap items-center gap-3 text-sm" data-testid={`ce-action-email-${depId}`}>
                            <Mail className="w-4 h-4 shrink-0 text-cyan-300" />
                            <span className="text-white">{entry.name}</span>
                            <span className="font-semibold text-white">{entry.devs.length}</span>
                            <span className="text-white/60 text-xs flex-1 min-w-[12rem]">Already targeted by this deployment and waiting on the user.</span>
                            {canManageOSPatches && (
                              <Button size="sm" onClick={() => setReminderRequest({
                                deploymentId: depId,
                                label: entry.name,
                                devices: entry.devs.map(d => ({
                                  device_id: d.intune_id || d.id, device_name: d.device_name, user: d.user_name || '', status: '',
                                })),
                              })}
                                className="bg-[#E5EFFA] text-[#3C02CC] hover:bg-[#BBFF71] hover:text-[#3C02CC] font-semibold"
                                data-testid={`ce-action-email-btn-${depId}`}>
                                <Mail className="w-3.5 h-3.5 mr-1.5" /> Email users…
                              </Button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    {viewDevices.length === 0 && (
                      <div className="rounded-lg border border-green-500/30 bg-green-500/10 px-4 py-3 text-sm text-green-100" data-testid="ce-empty">
                        No devices breach Cyber Essentials{ceReason ? ' for this reason' : ''}.
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Excluded: the devices ALICE has been told to leave alone, and
                  the rules and choices that put them there. Customers review
                  and change their exclusions here. */}
              {devicesView === 'excluded' && (
                <div className="mb-4 space-y-3" data-testid="excluded-panel">
                  <div className="rounded-lg border border-white/15 bg-white/5 px-4 py-3 text-sm text-white/90" data-testid="excluded-intro">
                    <p>
                      Devices ALICE leaves out. <strong>Reporting</strong> hides a device from the cards, Fix Queue and
                      Cyber Essentials; <strong>Quality updates</strong> and <strong>Feature updates</strong> also keep it out
                      of ALICE&apos;s update rings and feature update deployments.
                    </p>
                    <p className="mt-1 text-white/70">
                      Intune reports Azure VMs, AVD session hosts and on-premises Hyper-V machines the same way, so ALICE
                      can show you the virtual machines but cannot tell which are AVD. Pick those yourself under
                      Virtual machines.
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2" data-testid="excluded-show">
                    <span className="text-xs uppercase tracking-wider text-white/60 mr-1">Show</span>
                    {[
                      { id: 'excluded', label: 'Excluded devices', count: excludedCount },
                      { id: 'vms', label: 'Virtual machines', count: vmCount },
                    ].map(opt => (
                      <button key={opt.id} type="button"
                              onClick={() => { setUrlParam('show', opt.id === 'excluded' ? null : opt.id); setSelectedDeviceIds([]); }}
                              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs text-white ${excludedShow === opt.id ? 'border-cyan-400 bg-cyan-500/20' : 'border-white/15 bg-white/5 hover:bg-white/10'}`}
                              data-testid={`excluded-show-${opt.id}`}>
                        {opt.label} <span className="font-semibold">{opt.count}</span>
                      </button>
                    ))}
                  </div>
                  <div className="rounded-lg border border-white/15 bg-white/5 p-4" data-testid="excluded-type-rules-card">
                    <p className="text-sm font-semibold text-white mb-1">Device type rules</p>
                    <p className="text-xs text-white/60 mb-3">
                      Exclude every device of a virtual type. Physical devices can only be excluded one at a time.
                    </p>
                    <ExcludedTypeRules
                      policy={exclusionPolicy}
                      canEdit={canExclude}
                      canWrite={canWriteExclusions}
                      onChanged={afterExclusionChange}
                    />
                  </div>
                  {excludedShow === 'excluded' && excludedCount === 0 && (
                    <div className="rounded-lg border border-white/15 bg-white/5 px-4 py-3 text-sm text-white/80" data-testid="excluded-empty">
                      No devices are excluded. Choose a type rule above, or select devices in any list and use Exclude.
                    </div>
                  )}
                </div>
              )}

              {/* Filters */}
              <div className="flex flex-wrap gap-4 mb-6">
                <div className="flex items-center gap-2">
                  <Filter className="w-4 h-4 text-white/70" />
                  <select 
                    value={complianceFilter}
                    onChange={(e) => setComplianceFilter(e.target.value)}
                    className="bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] border border-[rgba(229,239,250,0.1)] text-white rounded-lg px-3 py-2 text-sm"
                  >
                    <option value="all" className="bg-slate-900">All Compliance</option>
                    <option value="compliant" className="bg-slate-900">Compliant Only</option>
                    <option value="non-compliant" className="bg-slate-900">Non-Compliant Only</option>
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  <select 
                    value={osVersionFilter}
                    onChange={(e) => { setOsVersionFilter(e.target.value); setOsBuildFilter('all'); }}
                    className="bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] border border-[rgba(229,239,250,0.1)] text-white rounded-lg px-3 py-2 text-sm"
                    data-testid="device-health-os-version-filter"
                  >
                    <option value="all" className="bg-slate-900">All OS Versions</option>
                    {uniqueOsVersions.map(v => (
                      <option key={v} value={v} className="bg-slate-900">{v}</option>
                    ))}
                  </select>
                </div>
                {/* OS build (patched month) — composes with the OS version
                    filter so users can narrow to "24H2 Apr 2026" etc. */}
                <div className="flex items-center gap-2">
                  <select
                    value={osBuildFilter}
                    onChange={(e) => setOsBuildFilter(e.target.value)}
                    className="bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] border border-[rgba(229,239,250,0.1)] text-white rounded-lg px-3 py-2 text-sm"
                    data-testid="device-health-os-build-filter"
                    title="Filter by specific patched build (e.g. Apr 2026 Security Update)"
                  >
                    <option value="all" className="bg-slate-900">All Builds</option>
                    {uniqueOsBuilds.map(v => (
                      <option key={v} value={v} className="bg-slate-900">{v}</option>
                    ))}
                    {/* A version clicked on the distribution chart that no
                        listed device resolves to — keep it visible rather
                        than have the select silently show "All Builds". */}
                    {osBuildFilter !== 'all' && !uniqueOsBuilds.includes(osBuildFilter) && (
                      <option value={osBuildFilter} className="bg-slate-900">{osBuildFilter}</option>
                    )}
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  <select
                    value={securityStatusFilter}
                    onChange={(e) => setSecurityStatusFilter(e.target.value)}
                    className="bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] border border-[rgba(229,239,250,0.1)] text-white rounded-lg px-3 py-2 text-sm"
                    aria-label="Patch Status"
                    title="Patch Status"
                    data-testid="devices-patch-status-filter"
                  >
                    <option value="all" className="bg-slate-900">All Patch Status</option>
                    <option value="current" className="bg-slate-900">Current</option>
                    <option value="outdated" className="bg-slate-900">Outdated</option>
                    <option value="failed" className="bg-slate-900">Failed</option>
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  <select
                    value={editionFilter}
                    onChange={(e) => setEditionFilter(e.target.value)}
                    className="bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] border border-[rgba(229,239,250,0.1)] text-white rounded-lg px-3 py-2 text-sm"
                    aria-label="Windows edition"
                    title="Windows edition"
                    data-testid="devices-edition-filter"
                  >
                    <option value="all" className="bg-slate-900">All Editions</option>
                    <option value="enterprise" className="bg-slate-900">Enterprise / Education</option>
                    <option value="standard" className="bg-slate-900">Home / Pro</option>
                    <option value="ltsc" className="bg-slate-900">LTSC</option>
                    <option value="unknown" className="bg-slate-900">Not reported yet</option>
                  </select>
                </div>
                {/* "Online" from the old Devices page: an Intune sync in the last 24h. */}
                <label
                  className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[rgba(229,239,250,0.1)] border border-[rgba(229,239,250,0.1)] text-sm text-white cursor-pointer select-none"
                  title="Devices that checked in to Intune in the last 24 hours"
                  data-testid="devices-online-filter"
                >
                  <input type="checkbox" checked={onlineFilter} onChange={(e) => setOnlineFilter(e.target.checked)}
                         className="accent-cyan-400" />
                  Online (24h)
                </label>
                <div className="relative flex-1 max-w-xs">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/70" />
                  <Input
                    placeholder="Search device or user..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9 bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] border-[rgba(229,239,250,0.1)] text-white"
                    data-testid="devices-search"
                  />
                </div>
                {filtersActive && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={clearAllFilters}
                    className="border-white/20 text-white hover:bg-white/10"
                    data-testid="devices-clear-filters"
                  >
                    <X className="w-4 h-4 mr-1.5" /> Clear all filters
                  </Button>
                )}
              </div>

              {/* Actions on their own line under the filters (6 Oct). */}
              <div className="flex flex-wrap justify-end gap-3 -mt-2 mb-6" data-testid="devices-actions-row">
                {/* Sync WUfB client states on demand.
                    `handleSyncClientStates` existed but had no control wired
                    to it, so the only way client states ever landed was the
                    nightly sync — connecting WUfB appeared to do nothing. */}
                {wufbSettings?.is_connected && (
                  <Button
                    onClick={handleSyncClientStates}
                    disabled={syncingClientStates}
                    variant="outline"
                    className="border-cyan-400/40 text-cyan-200 hover:bg-cyan-500/10"
                    data-testid="health-sync-wufb"
                    title="Pull ClientState / ClientSubstate from the WUfB Log Analytics workspace now"
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${syncingClientStates ? 'animate-spin' : ''}`} />
                    {syncingClientStates ? 'Syncing…' : 'Sync WUfB'}
                  </Button>
                )}
                {/* Export Button */}
                <Button
                  onClick={handleExportHealthCSV}
                  disabled={healthDevices.length === 0}
                  className="bg-[#E5EFFA] text-[#3C02CC] hover:bg-[#BBFF71] hover:text-[#3C02CC] font-semibold"
                  data-testid="health-export-csv"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export CSV
                </Button>
              </div>

              {devicesLoadFailed && (
                <div className="mb-4 rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-100 flex items-center gap-2"
                     data-testid="devices-docs-failed">
                  <AlertTriangle className="w-4 h-4 shrink-0" />
                  <span>
                    Device details could not be loaded, so last sync, sprint, disk space and Entra ID are missing
                    below and remediation can&apos;t target these devices. Use <strong>Reload reports</strong> to try again.
                  </span>
                </div>
              )}

              {/* Deployment Sprint legend — moved from the old Devices page.
                  Sprints are a feature-update deployment concept, so they are
                  hidden on plans without update policies. */}
              {canViewOSPatchPolicies && (
                <div className="mb-4 flex items-center gap-4 text-xs text-white/70 flex-wrap" data-testid="phase-legend">
                  <span className="font-medium text-white/90">Deployment Sprint:</span>
                  {Object.entries(PHASE_CONFIG).map(([phase, cfg]) => (
                    <div key={phase} className="flex items-center gap-1.5" title={cfg.hint}>
                      <div className={`w-3 h-3 rounded-full ${cfg.color}`} />
                      <span>{cfg.short} — {cfg.label}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Pending sprint changes (09/09 behaviour, unchanged). Sticky so
                  it stays reachable while paging; nothing is saved yet. */}
              {canEditSprint && pendingPhaseCount > 0 && (
                <div
                  className="sticky top-2 z-20 mb-4 flex items-center justify-between gap-4 flex-wrap rounded-lg border border-cyan-400/40 bg-cyan-500/10 backdrop-blur-[6px] px-4 py-3"
                  data-testid="phase-commit-bar"
                >
                  <div className="text-sm text-cyan-100">
                    <span className="font-semibold">{pendingPhaseCount} pending phase change{pendingPhaseCount === 1 ? '' : 's'}</span>
                    <span className="text-cyan-100/70">
                      {' '}— nothing has been saved yet. Committing also moves devices in and out of live sprints.
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" className="border-white/20 text-white hover:bg-white/10"
                      onClick={() => setPendingPhases({})} data-testid="phase-discard-btn">
                      Discard
                    </Button>
                    <Button size="sm" className="bg-cyan-500 hover:bg-cyan-600 text-white font-semibold"
                      onClick={() => setShowPhaseCommit(true)} data-testid="phase-review-commit-btn">
                      Review &amp; commit
                    </Button>
                  </div>
                </div>
              )}

              {/* Bulk selection */}
              {canSelectRows && selectedDeviceIds.length > 0 && (
                <div
                  className="sticky top-2 z-20 mb-4 flex items-center justify-between gap-4 flex-wrap rounded-lg border border-[#BBFF71]/40 bg-[#BBFF71]/10 backdrop-blur-[6px] px-4 py-3"
                  data-testid="devices-selection-bar"
                >
                  <span className="text-sm text-white">
                    <span className="font-semibold">{selectedDeviceIds.length} device{selectedDeviceIds.length === 1 ? '' : 's'} selected</span>
                  </span>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" className="border-white/20 text-white hover:bg-white/10"
                      onClick={() => setSelectedDeviceIds([])} data-testid="devices-selection-clear">
                      Clear
                    </Button>
                    {canExclude && devicesView === 'excluded' && excludedShow === 'excluded' && (
                      <Button variant="outline" size="sm" className="border-white/20 text-white hover:bg-white/10"
                        onClick={() => setExcludeRequest({ devices: joinedDevices.filter(d => selectedDeviceIds.includes(d.id)), stop: true })}
                        data-testid="devices-stop-excluding-selected">
                        Stop excluding…
                      </Button>
                    )}
                    {canExclude && (
                      <Button variant="outline" size="sm" className="border-white/20 text-white hover:bg-white/10"
                        onClick={() => setExcludeRequest({ devices: joinedDevices.filter(d => selectedDeviceIds.includes(d.id)), stop: false })}
                        data-testid="devices-exclude-selected">
                        <EyeOff className="w-4 h-4 mr-1.5" /> Exclude…
                      </Button>
                    )}
                    {canRemediate && (
                      <Button size="sm" onClick={remediateSelected} data-testid="devices-remediate-selected"
                        className="bg-[#E5EFFA] text-[#3C02CC] hover:bg-[#BBFF71] hover:text-[#3C02CC] font-semibold">
                        <Wrench className="w-4 h-4 mr-1.5" /> Remediate selected…
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {/* Devices table. October 2026: the old main-menu Devices page
                  and Device Health in one list. Seven columns that help scan
                  and triage; everything else (last boot, last WU scan,
                  Intune compliance, app criticality, hardware, firmware,
                  remediation history) is in the detail panel a row opens,
                  and in the CSV export. */}
              <Card className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border-[rgba(229,239,250,0.1)]">
                <CardHeader className="border-b border-[rgba(229,239,250,0.1)]">
                  <CardTitle className="text-white flex items-center gap-2">
                    <Monitor className="w-5 h-5 text-cyan-400" />
                    Devices
                    <span className="text-xs text-white/60 font-normal ml-2" data-testid="devices-count">
                      {healthDevices.length === viewDevices.length
                        ? `${viewDevices.length} Windows devices`
                        : `${healthDevices.length} of ${viewDevices.length} Windows devices`}
                      {devicesView === 'fix_queue' && ' · Fix Queue'}
                      {devicesView === 'ce_breach' && ' · Cyber Essentials'}
                      {devicesView === 'excluded' && (excludedShow === 'vms' ? ' · Virtual machines' : ' · Excluded')}
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full" data-testid="devices-table">
                      <thead>
                        <tr className="bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px]">
                          {canSelectRows && (
                            <th className="w-10 p-4">
                              <input
                                type="checkbox"
                                className="accent-cyan-400"
                                title="Select every device on this page"
                                checked={paginatedHealthDevices.length > 0 && paginatedHealthDevices.every(d => selectedDeviceIds.includes(d.id))}
                                onChange={(e) => {
                                  const pageIds = paginatedHealthDevices.map(d => d.id);
                                  setSelectedDeviceIds(prev => (e.target.checked
                                    ? [...new Set([...prev, ...pageIds])]
                                    : prev.filter(id => !pageIds.includes(id))));
                                }}
                                data-testid="devices-select-page"
                              />
                            </th>
                          )}
                          <th className="text-left p-4 text-white/90 font-medium">Device</th>
                          {/* Cyber Essentials needs the release, edition and support
                              date on the row: they are the whole reason it is listed. */}
                          {devicesView === 'excluded' && (
                            <>
                              <th className="text-left p-4 text-white/90 font-medium" title="Virtual machine type, from the manufacturer and model Intune reports">Type</th>
                              <th className="text-left p-4 text-white/90 font-medium">Excluded from</th>
                            </>
                          )}
                          {devicesView === 'ce_breach' && (
                            <>
                              <th className="text-left p-4 text-white/90 font-medium" title="Windows release and edition">OS / Edition</th>
                              <th className="text-left p-4 text-white/90 font-medium" title="When Microsoft stops servicing this release for this edition">Support</th>
                            </>
                          )}
                          {/* OS Version moved to the device panel (6 Oct); its filters stay. */}
                          <th className="text-left p-4 text-white/90 font-medium">Patch Status</th>
                          <th className="text-left p-4 text-white/90 font-medium">Client State</th>
                          <th className="text-left p-4 text-white/90 font-medium" title="Last Intune check-in">Last Sync</th>
                          {canViewOSPatchPolicies && (
                            <th className="text-left p-4 text-white/90 font-medium" title="Deployment sprint: which sprint of a staged feature update deployment this device belongs in. Hover a circle for its name.">Sprint</th>
                          )}
                          <th className="text-left p-4 text-white/90 font-medium" title="Detected issues and what can be done about them — open the device for the actions">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedHealthDevices.length === 0 ? (
                          <tr>
                            <td colSpan={(canSelectRows ? 7 : 6) - (canViewOSPatchPolicies ? 0 : 1) + (devicesView === 'ce_breach' || devicesView === 'excluded' ? 2 : 0)} className="text-center py-12 text-white/70">
                              No devices match the current filters
                            </td>
                          </tr>
                        ) : (
                          paginatedHealthDevices.map((device, i) => {
                            const issues = issuesById.get(device.id) || [];
                            const phase = device.feature_update_phase || 'green';
                            return (
                            <tr
                              key={device.id || i}
                              onClick={() => setUrlParam('device', device.id)}
                              // Keyboard: Tab to a row, Enter or Space opens its panel.
                              tabIndex={0}
                              aria-label={`Open details for ${device.device_name}`}
                              onKeyDown={(e) => {
                                if (e.target !== e.currentTarget) return;
                                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setUrlParam('device', device.id); }
                              }}
                              className={`border-t border-white/5 hover:bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] transition-colors cursor-pointer ${String(selectedDeviceId) === String(device.id) ? 'bg-[rgba(229,239,250,0.08)]' : ''}`}
                              data-testid={`device-row-${device.id}`}
                            >
                              {canSelectRows && (
                                <td className="p-4" onClick={(e) => e.stopPropagation()}>
                                  <input
                                    type="checkbox"
                                    className="accent-cyan-400"
                                    checked={selectedDeviceIds.includes(device.id)}
                                    onChange={() => toggleDeviceSelected(device.id)}
                                    data-testid={`device-select-${device.id}`}
                                  />
                                </td>
                              )}
                              <td className="p-4">
                                <div>
                                  <span className="text-white font-medium">{device.device_name}</span>
                                  {(device.user_name || device.model) && (
                                    <p className="text-xs text-white/70 mt-1">{[device.user_name, device.model].filter(Boolean).join(' · ')}</p>
                                  )}
                                  {devicesView !== 'excluded' && (device.excluded_scopes || []).length > 0 && (
                                    <p className="text-[11px] text-amber-300 mt-1" title={`Excluded from: ${device.excluded_scopes.map(k => EXCLUSION_SCOPE_LABEL[k]).join(', ')}`}>
                                      Excluded: {device.excluded_scopes.map(k => EXCLUSION_SCOPE_LABEL[k]).join(', ')}
                                    </p>
                                  )}
                                </div>
                              </td>
                              {devicesView === 'excluded' && (
                                <>
                                  <td className="p-4 text-sm text-white/90" data-testid={`device-type-${device.id}`}>
                                    {device.virtualisation_label || '—'}
                                  </td>
                                  <td className="p-4 text-sm" data-testid={`device-excluded-from-${device.id}`}>
                                    {(device.excluded_sources || []).length === 0 ? (
                                      <span className="text-white/40 text-xs">Not excluded</span>
                                    ) : device.excluded_sources.map((src, i) => (
                                      <div key={i} className="mb-1 last:mb-0">
                                        <ExclusionScopeChips scopes={src.scopes} />
                                        <p className="text-[11px] text-white/60 mt-0.5">
                                          {src.label}{src.reason ? ` · ${src.reason}` : ''}
                                        </p>
                                      </div>
                                    ))}
                                  </td>
                                </>
                              )}
                              {devicesView === 'ce_breach' && (
                                <>
                                  <td className="p-4 text-sm" data-testid={`device-ce-os-${device.id}`}>
                                    <span className="text-white">{device.os_version_label || getVersionFriendlyName(device.os_version || '') || device.os_version || '—'}</span>
                                    <p className="text-xs text-white/70 mt-1">
                                      {device.edition || 'Unknown'}
                                      {device.edition_assumed && (
                                        <span className="text-amber-300" title="Intune has not reported this device's edition yet. It is assessed as Home/Pro, the shorter lifecycle."> · assumed</span>
                                      )}
                                    </p>
                                  </td>
                                  <td className="p-4 text-sm whitespace-nowrap" data-testid={`device-ce-support-${device.id}`}>
                                    {device.os_support_end ? (
                                      <>
                                        <span className={device.os_supported === false ? 'text-red-300 font-medium' : 'text-white'}>
                                          {device.os_supported === false ? 'Ended ' : 'Until '}
                                          {new Date(device.os_support_end).toLocaleDateString()}
                                        </span>
                                        {device.os_support_estimated && (
                                          <p className="text-[11px] text-white/50 italic" title="Microsoft has not published this date; it follows the usual 24/36-month pattern.">estimated</p>
                                        )}
                                      </>
                                    ) : device.os_supported === false ? (
                                      <span className="text-red-300 font-medium">Out of support</span>
                                    ) : (
                                      <span className="text-white/50 text-xs">Not tracked</span>
                                    )}
                                    {device.os_esu && (
                                      <p className="text-[11px] text-cyan-300" title="Under Extended Security Updates (detected)">ESU</p>
                                    )}
                                    {(device.ce_reasons || []).filter(r => r !== 'esu' && CE_REASONS[r]).map(r => (
                                      <p key={r} className={`text-[11px] ${CE_REASONS[r].cls}`}>{CE_REASONS[r].label}</p>
                                    ))}
                                  </td>
                                </>
                              )}
                              <td className="p-4">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                                  device.security_status === 'current' ? 'bg-green-500/20 text-green-400' :
                                  device.security_status === 'failed' ? 'bg-red-500/20 text-red-400' :
                                  'bg-amber-500/20 text-amber-400'
                                }`}>
                                  {device.security_status === 'current' ? 'Current' :
                                   device.security_status === 'failed' ? 'Failed' :
                                   device.updates_behind > 1 ? `${device.updates_behind} months behind` : '1 month behind'}
                                </span>
                              </td>
                              <td className="p-4" data-testid={`device-health-client-state-${device.id}`}>
                                {device.client_state && device.client_state !== 'unknown' ? (
                                  <div className="space-y-1">
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${CLIENT_STATE_CHIP[device.client_state] || 'bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] text-white/90'}`}>
                                      {CLIENT_STATE_LABELS[device.client_state] || device.client_state.replace(/_/g, ' ')}
                                    </span>
                                    {device.client_state_source === 'intune' && (
                                      <p className="text-[11px] text-white/40 italic" title="Derived from Intune management + compliance state, not from Windows Update for Business">
                                        via Intune
                                      </p>
                                    )}
                                    {device.is_stuck && (
                                      <p className="text-[11px] text-purple-300 font-medium">
                                        stuck {Math.round(device.hours_in_state)}h
                                      </p>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-white/50 text-xs italic">
                                    {wufbSettings?.is_connected ? 'Sync WUfB' : 'From Intune'}
                                  </span>
                                )}
                              </td>
                              <td className="p-4 text-white/90 text-sm whitespace-nowrap" data-testid={`device-last-sync-${device.id}`}>
                                {device.last_sync
                                  ? <span title={new Date(device.last_sync).toLocaleString()}>{_relTime(device.last_sync)}</span>
                                  : <span className="text-white/50 text-xs italic">Never</span>}
                              </td>
                              {canViewOSPatchPolicies && (
                                <td className="p-4" onClick={(e) => e.stopPropagation()}>
                                  {/* Coloured circles again (6 Oct), name on hover. App
                                      criticality is in the device panel only. */}
                                  <FeatureUpdatePhaseDots
                                    deviceId={device.id}
                                    currentPhase={phase}
                                    pendingPhase={pendingPhases[device.id] || null}
                                    onStageChange={stagePhaseChange}
                                    readOnly={!canEditSprint}
                                  />
                                </td>
                              )}
                              <td className="p-4" data-testid={`device-flags-${device.id}`}>
                                {issues.length === 0 ? (
                                  <span className="text-white/30 text-xs">—</span>
                                ) : (
                                  <div className="flex items-center gap-1.5">
                                    {issues.map(code => {
                                      const def = DEVICE_ISSUES[code];
                                      const Icon = def.icon;
                                      const tip = code === 'firmware' && device.latest_bios_version
                                        ? `BIOS update available: ${device.bios_version || '?'} → ${device.latest_bios_version}`
                                        : code === 'low_disk' ? `${def.label}: ${device.free_storage_gb} GB free` : def.label;
                                      return (
                                        <span key={code} title={tip} aria-label={tip} className="inline-flex"
                                              data-testid={`device-flag-${code}-${device.id}`}>
                                          <Icon className={`w-4 h-4 ${def.cls}`} />
                                        </span>
                                      );
                                    })}
                                  </div>
                                )}
                              </td>
                            </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                  {/* Pagination Controls */}
                  <PaginationControls
                    currentPage={healthPage}
                    totalItems={healthDevices.length}
                    pageSize={healthPageSize}
                    onPageChange={setHealthPage}
                    onPageSizeChange={setHealthPageSize}
                  />
                </CardContent>
              </Card>

              {showPhaseCommit && canEditSprint && (
                <PhaseChangeCommitModal
                  changes={pendingPhaseList}
                  onClose={() => setShowPhaseCommit(false)}
                  onCommitted={(committed) => {
                    // Fold the saved values into the device documents so the
                    // control stops showing them as pending.
                    setDevices(prev => prev.map(d => (
                      committed[d.id] ? { ...d, feature_update_phase: committed[d.id] } : d
                    )));
                    setPendingPhases({});
                    setShowPhaseCommit(false);
                  }}
                />
              )}
              {excludeRequest && (
                <ExcludeDevicesModal
                  devices={excludeRequest.devices}
                  stop={excludeRequest.stop}
                  canWrite={canWriteExclusions}
                  onClose={() => setExcludeRequest(null)}
                  onSaved={afterExclusionChange}
                />
              )}
              {reminderRequest && (
                <SendReminderModal
                  devices={reminderRequest.devices}
                  deploymentId={reminderRequest.deploymentId}
                  phaseLabel={reminderRequest.label}
                  endpoint={reminderRequest.endpoint}
                  eyebrow={reminderRequest.eyebrow}
                  defaultSubject={reminderRequest.defaultSubject}
                  defaultBody={reminderRequest.defaultBody}
                  onClose={() => setReminderRequest(null)}
                  onSent={() => { setReminderRequest(null); setSelectedDeviceIds([]); }}
                />
              )}
              {remediationRequest && (
                <RunScriptModal
                  devices={remediationRequest.devices}
                  recommendedCatalogKey={remediationRequest.catalogKey}
                  source="devices_tab"
                  onClose={() => setRemediationRequest(null)}
                  onViewRun={setRunDetailId}
                  onRunCreated={() => { setSelectedDeviceIds([]); setPanelRefreshKey(k => k + 1); }}
                />
              )}
              {/* The panel steps aside (closed, but ?device kept) while a
                  modal is up, so the two never stack, and comes back when it
                  closes. */}
              <DeviceDetailPanel
                device={selectedDevice}
                open={Boolean(selectedDevice) && !remediationRequest && !reminderRequest && !excludeRequest && !runDetailId && !showPhaseCommit}
                onOpenChange={(isOpen) => { if (!isOpen) setUrlParam('device', null); }}
                canRemediate={canRemediate}
                canEditSprint={canEditSprint}
                policiesAvailable={canViewOSPatchPolicies}
                pendingPhase={selectedDevice ? (pendingPhases[selectedDevice.id] || null) : null}
                onStageChange={stagePhaseChange}
                onRemediate={openRemediation}
                onViewFirmware={openFirmwareFor}
                onViewRun={setRunDetailId}
                wufbConnected={!!wufbSettings?.is_connected}
                getFriendlyName={getVersionFriendlyName}
                refreshKey={panelRefreshKey}
                canExclude={canExclude}
                onManageExclusions={(d) => setExcludeRequest({ devices: [d], stop: false })}
              />
            </>
          )}

          {/* ==================== COMPLIANCE TAB ====================
              Created 2026-06-15 (scott/ospatch-ui Phase 1). Hosts the
              Quality Update Compliance Trending block that used to live
              at the bottom of the Update Rings (now Quality Updates) tab. */}
          {activeTab === 'compliance' && (
            <>
              {/* The Ring filter only renders when given a handler; reporting-only
                  tenants have no rings, so it is left out for them. */}
              <ComplianceTrendingSection
                data={complianceTrending}
                onBackfill={handleBackfillTrend}
                backfilling={trendBackfilling}
                collapsed={trendingSectionCollapsed}
                onToggleCollapse={() => setTrendingSectionCollapsed(v => !v)}
                targetCurve={targetCurve}
                onSaveTargetCurve={handleSaveTargetCurve}
                ringsSummary={ringsSummary}
                wufbConnected={!!wufbSettings?.is_connected}
                trendRingFilter={complianceTrendRing}
                onTrendRingChange={canViewOSPatchPolicies ? handleComplianceTrendRingChange : undefined}
                trendFilterLoading={complianceTrendLoading}
                staleReport={staleSnapshots}
                staleLoading={staleLoading}
                onInspectStale={handleInspectStaleSnapshots}
                onPurgeStale={handlePurgeStaleSnapshots}
              />
            </>
          )}

          {/* Risk Analysis tab removed 2026-06-15 — content moved into the
              renamed Quality Updates tab (former Update Rings). */}

          {/* ==================== WINDOWS 11 READINESS TAB ==================== */}
          {activeTab === 'win11-readiness' && (
            <div className="space-y-6" data-testid="win11-readiness-tab">
              {/* Summary Cards — restyled 2026-06-15 to match Apps
                  Management > Discover (glass-blue background, white
                  values + subtitles, crisp Lucide icons in coloured
                  chips). Active filter is now indicated by a coloured
                  border + ring instead of a saturated tile background. */}
              <div className="alice-kpi-row grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
                {/* Total Devices Card */}
                <div
                  onClick={() => setWin11Filter('all')}
                  className={`bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border rounded-xl p-6 cursor-pointer transition-all hover:scale-[1.02] ${
                    win11Filter === 'all'
                      ? 'border-blue-400 ring-2 ring-blue-400/30'
                      : 'border-[rgba(229,239,250,0.1)] hover:border-blue-400/40'
                  }`}
                  data-testid="win11-total-card"
                >
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-sm font-medium uppercase tracking-wider text-white/90">Total Assessed</span>
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-blue-400/30 ring-1 ring-blue-400/50">
                      <Monitor className="w-5 h-5 text-blue-400" />
                    </div>
                  </div>
                  <div className="text-4xl font-bold text-white mb-1">{win11Readiness?.summary?.total_devices || 0}</div>
                  <div className="text-sm text-white/90">
                    {(win11Readiness?.summary?.no_data || 0) > 0
                      ? `${win11Readiness.summary.no_data} devices no data`
                      : 'devices'}
                  </div>
                </div>

                {/* Upgraded Card - Already on Win11 */}
                <div
                  onClick={() => setWin11Filter('upgraded')}
                  className={`bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border rounded-xl p-6 cursor-pointer transition-all hover:scale-[1.02] ${
                    win11Filter === 'upgraded'
                      ? 'border-cyan-400 ring-2 ring-cyan-400/30'
                      : 'border-[rgba(229,239,250,0.1)] hover:border-cyan-400/40'
                  }`}
                  data-testid="win11-upgraded-card"
                >
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-sm font-medium uppercase tracking-wider text-white/90">Upgraded</span>
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-cyan-400/30 ring-1 ring-cyan-400/50">
                      <Rocket className="w-5 h-5 text-cyan-400" />
                    </div>
                  </div>
                  <div className="text-4xl font-bold text-white mb-1">{win11Readiness?.summary?.upgraded || 0}</div>
                  <div className="text-sm text-white/90">{win11Readiness?.summary?.upgraded_pct || 0}% on Win11</div>
                </div>

                {/* Capable Card */}
                <div
                  onClick={() => setWin11Filter('capable')}
                  className={`bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border rounded-xl p-6 cursor-pointer transition-all hover:scale-[1.02] ${
                    win11Filter === 'capable'
                      ? 'border-green-400 ring-2 ring-green-400/30'
                      : 'border-[rgba(229,239,250,0.1)] hover:border-green-400/40'
                  }`}
                  data-testid="win11-capable-card"
                >
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-sm font-medium uppercase tracking-wider text-white/90">Capable</span>
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-green-400/30 ring-1 ring-green-400/50">
                      <CheckCircle className="w-5 h-5 text-green-400" />
                    </div>
                  </div>
                  <div className="text-4xl font-bold text-white mb-1">{win11Readiness?.summary?.capable || 0}</div>
                  <div className="text-sm text-white/90">{win11Readiness?.summary?.capable_pct || 0}% ready</div>
                </div>

                {/* Not Capable Card */}
                <div
                  onClick={() => setWin11Filter('not_capable')}
                  className={`bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border rounded-xl p-6 cursor-pointer transition-all hover:scale-[1.02] ${
                    win11Filter === 'not_capable'
                      ? 'border-red-400 ring-2 ring-red-400/30'
                      : 'border-[rgba(229,239,250,0.1)] hover:border-red-400/40'
                  }`}
                  data-testid="win11-not-capable-card"
                >
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-sm font-medium uppercase tracking-wider text-white/90">Not Capable</span>
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-red-400/30 ring-1 ring-red-400/50">
                      <AlertTriangle className="w-5 h-5 text-red-400" />
                    </div>
                  </div>
                  <div className="text-4xl font-bold text-white mb-1">{win11Readiness?.summary?.not_capable || 0}</div>
                  <div className="text-sm text-white/90">{win11Readiness?.summary?.not_capable_pct || 0}% blocked</div>
                </div>

                {/* Unknown Card */}
                <div
                  onClick={() => setWin11Filter('unknown')}
                  className={`bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border rounded-xl p-6 cursor-pointer transition-all hover:scale-[1.02] ${
                    win11Filter === 'unknown'
                      ? 'border-purple-400 ring-2 ring-purple-400/30'
                      : 'border-[rgba(229,239,250,0.1)] hover:border-purple-400/40'
                  }`}
                  data-testid="win11-unknown-card"
                >
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-sm font-medium uppercase tracking-wider text-white/90">Unknown</span>
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-purple-400/30 ring-1 ring-purple-400/50">
                      <Info className="w-5 h-5 text-purple-400" />
                    </div>
                  </div>
                  <div className="text-4xl font-bold text-white mb-1">{win11Readiness?.summary?.unknown || 0}</div>
                  <div className="text-sm text-white/90">{win11Readiness?.summary?.unknown_pct || 0}% pending</div>
                </div>

                {/* No Data Card - devices not present in Endpoint Analytics */}
                <div
                  onClick={() => setWin11Filter('no_data')}
                  className={`bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border rounded-xl p-6 cursor-pointer transition-all hover:scale-[1.02] ${
                    win11Filter === 'no_data'
                      ? 'border-slate-300 ring-2 ring-slate-300/30'
                      : 'border-[rgba(229,239,250,0.1)] hover:border-slate-300/40'
                  }`}
                  data-testid="win11-no-data-card"
                >
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-sm font-medium uppercase tracking-wider text-white/90">No Data</span>
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-slate-400/30 ring-1 ring-slate-400/50">
                      <HelpCircle className="w-5 h-5 text-slate-300" />
                    </div>
                  </div>
                  <div className="text-4xl font-bold text-white mb-1">{win11Readiness?.summary?.no_data || 0}</div>
                  <div className="text-sm text-white/90">not in Endpoint Analytics</div>
                </div>
              </div>
              
              {/* Top Failure Reasons Card */}
              {win11Readiness?.failure_analysis?.top_failures?.length > 0 && (
                <Card className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border-[rgba(229,239,250,0.1)]">
                  <CardHeader className="border-b border-[rgba(229,239,250,0.1)]">
                    <CardTitle className="text-white flex items-center gap-2">
                      <Wrench className="w-5 h-5 text-amber-400" />
                      Top Upgrade Blockers
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {win11Readiness.failure_analysis.top_failures.slice(0, 6).map((failure, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50 border border-[rgba(229,239,250,0.1)]">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full flex items-center justify-center bg-amber-500/20">
                              <AlertTriangle className="w-4 h-4 text-amber-400" />
                            </div>
                            <span className="text-white font-medium">{failure.check}</span>
                          </div>
                          <span className="text-white/90 font-semibold">{failure.count}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
              
              {/* Filter and Search Bar */}
              <Card className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border-[rgba(229,239,250,0.1)]">
                <CardContent className="p-4">
                  <div className="flex flex-col md:flex-row items-center gap-4">
                    <div className="flex items-center gap-2 flex-1">
                      <Search className="w-4 h-4 text-white/70" />
                      <Input 
                        placeholder="Search devices..." 
                        value={win11Search}
                        onChange={(e) => setWin11Search(e.target.value)}
                        className="bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] border-[rgba(229,239,250,0.1)] text-white placeholder:text-white/70"
                        data-testid="win11-search"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      {win11Filter !== 'all' && (
                        <span className="px-3 py-1 rounded-full text-sm font-medium bg-cyan-500/20 text-cyan-400">
                          {win11Filter === 'upgraded' ? 'Upgraded' : 
                           win11Filter === 'capable' ? 'Capable' : 
                           win11Filter === 'not_capable' ? 'Not Capable' :
                           win11Filter === 'no_data' ? 'No Data' : 'Unknown'}
                        </span>
                      )}
                      {win11Filter !== 'all' && (
                        <button 
                          onClick={() => setWin11Filter('all')}
                          className="text-white/70 hover:text-white/90 text-sm underline"
                        >
                          Clear filter
                        </button>
                      )}
                    </div>
                    <Button 
                      onClick={async () => {
                        setWin11ExportLoading(true);
                        try {
                          const res = await osPatchApi.exportWin11Readiness('csv', win11Filter);
                          const blob = new Blob([res.data], { type: 'text/csv' });
                          const url = window.URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `win11_readiness_report_${new Date().toISOString().split('T')[0]}.csv`;
                          a.click();
                          window.URL.revokeObjectURL(url);
                          toast.success('Report exported successfully');
                        } catch (error) {
                          toast.error('Failed to export report');
                        } finally {
                          setWin11ExportLoading(false);
                        }
                      }}
                      disabled={win11ExportLoading || !win11Readiness?.devices?.length}
                      className="bg-[#E5EFFA] text-[#3C02CC] hover:bg-[#BBFF71] hover:text-[#3C02CC] active:bg-[#BBFF71] active:text-[#3C02CC] font-semibold"
                      data-testid="win11-export-btn"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      {win11ExportLoading ? 'Exporting...' : 'Export CSV'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
              
              {/* No Data Message */}
              {!win11Readiness?.available && (
                <Card className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border-[rgba(229,239,250,0.1)]">
                  <CardContent className="p-12 text-center">
                    <Rocket className="w-12 h-12 text-[#E5EFFA]/30 mx-auto mb-4" />
                    <h3 className="text-xl font-medium text-white mb-2">No Windows 11 Readiness Data</h3>
                    <p className="text-white/90 mb-4">
                      Use "Sync from Intune" on the Dashboard to fetch Windows 11 readiness data from Endpoint Analytics.
                    </p>
                    <p className="text-white/70 text-sm">
                      Note: Endpoint Analytics must be enabled in Intune to collect device readiness information.
                    </p>
                  </CardContent>
                </Card>
              )}
              
              {/* Devices Table */}
              {win11Readiness?.available && (
                <Card className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border-[rgba(229,239,250,0.1)]">
                  <CardHeader className="border-b border-[rgba(229,239,250,0.1)]">
                    <CardTitle className="text-white flex items-center gap-2">
                      <Monitor className="w-5 h-5 text-cyan-400" />
                      Device Readiness Details
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-[rgba(229,239,250,0.1)]">
                            <th className="text-left p-4 text-white/90 font-medium">Device Name</th>
                            <th className="text-left p-4 text-white/90 font-medium">Manufacturer</th>
                            <th className="text-left p-4 text-white/90 font-medium">Model</th>
                            <th className="text-left p-4 text-white/90 font-medium">OS Version</th>
                            <th className="text-left p-4 text-white/90 font-medium">Readiness</th>
                            <th className="text-left p-4 text-white/90 font-medium">Issues</th>
                            <th className="text-left p-4 text-white/90 font-medium">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(() => {
                            // Filter devices based on current filter and search
                            const filteredDevices = (win11Readiness?.devices || [])
                              .filter(d => {
                                if (win11Filter === 'upgraded') return d.is_upgraded;
                                if (win11Filter === 'capable') return d.is_capable;
                                if (win11Filter === 'not_capable') return d.is_not_capable;
                                if (win11Filter === 'no_data') return d.readiness_status === 'no_data';
                                if (win11Filter === 'unknown') return d.readiness_status !== 'no_data' && !d.is_upgraded && !d.is_capable && !d.is_not_capable;
                                return true;
                              })
                              .filter(d => {
                                if (!win11Search) return true;
                                const search = win11Search.toLowerCase();
                                return (
                                  (d.device_name || '').toLowerCase().includes(search) ||
                                  (d.manufacturer || '').toLowerCase().includes(search) ||
                                  (d.model || '').toLowerCase().includes(search)
                                );
                              });
                            
                            // Calculate pagination
                            const totalFiltered = filteredDevices.length;
                            const start = (win11Page - 1) * win11PageSize;
                            const paginatedDevices = filteredDevices.slice(start, start + win11PageSize);
                            
                            if (paginatedDevices.length === 0) {
                              return (
                                <tr>
                                  <td colSpan={7} className="text-center py-12 text-white/70">
                                    No devices found matching the current filters.
                                  </td>
                                </tr>
                              );
                            }
                            
                            return paginatedDevices.map((device, idx) => (
                              <tr key={device.device_id || idx} className="border-b border-white/5 hover:bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px]">
                                <td className="p-4">
                                  <div className="text-white font-medium">{device.device_name || 'Unknown'}</div>
                                </td>
                                <td className="p-4 text-white/90">{device.manufacturer || '-'}</td>
                                <td className="p-4 text-white/90">{device.model || '-'}</td>
                                <td className="p-4 text-white/90 font-mono text-sm">{device.os_version || '-'}</td>
                                <td className="p-4">
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                    device.readiness_status === 'no_data' ? 'bg-slate-400/20 text-slate-300' :
                                    device.is_upgraded ? 'bg-cyan-500/20 text-cyan-400' :
                                    device.is_capable ? 'bg-green-500/20 text-green-400' :
                                    device.is_not_capable ? 'bg-red-500/20 text-red-400' :
                                    'bg-purple-500/20 text-purple-400'
                                  }`}>
                                    {device.readiness_status === 'no_data' ? 'No Data' :
                                      device.is_upgraded ? 'Upgraded' :
                                      device.is_capable ? 'Capable' :
                                      device.is_not_capable ? 'Not Capable' : 'Unknown'}
                                  </span>
                                </td>
                                <td className="p-4">
                                  {device.readiness_status === 'no_data' ? (
                                    <span className="text-white/50">—</span>
                                  ) : device.fail_reason_count > 0 ? (
                                    <span className="text-amber-400 font-medium">{device.fail_reason_count} issues</span>
                                  ) : (
                                    <span className="text-white/70">None</span>
                                  )}
                                </td>
                                <td className="p-4">
                                  {device.fail_reason_count > 0 && (
                                    <Button 
                                      size="sm" 
                                      variant="outline" 
                                      className="border-[rgba(229,239,250,0.2)] text-white/90 hover:bg-white/10"
                                      onClick={() => setSelectedWin11Device(device)}
                                      data-testid={`view-details-${idx}`}
                                    >
                                      View Details
                                    </Button>
                                  )}
                                </td>
                              </tr>
                            ));
                          })()}
                        </tbody>
                      </table>
                    </div>
                    {/* Pagination Controls */}
                    {(() => {
                      // Calculate total filtered for pagination
                      const filteredDevices = (win11Readiness?.devices || [])
                        .filter(d => {
                          if (win11Filter === 'upgraded') return d.is_upgraded;
                          if (win11Filter === 'capable') return d.is_capable;
                          if (win11Filter === 'not_capable') return d.is_not_capable;
                          if (win11Filter === 'no_data') return d.readiness_status === 'no_data';
                          if (win11Filter === 'unknown') return d.readiness_status !== 'no_data' && !d.is_upgraded && !d.is_capable && !d.is_not_capable;
                          return true;
                        })
                        .filter(d => {
                          if (!win11Search) return true;
                          const search = win11Search.toLowerCase();
                          return (
                            (d.device_name || '').toLowerCase().includes(search) ||
                            (d.manufacturer || '').toLowerCase().includes(search) ||
                            (d.model || '').toLowerCase().includes(search)
                          );
                        });
                      return (
                        <PaginationControls
                          currentPage={win11Page}
                          totalItems={filteredDevices.length}
                          pageSize={win11PageSize}
                          onPageChange={setWin11Page}
                          onPageSizeChange={setWin11PageSize}
                        />
                      );
                    })()}
                  </CardContent>
                </Card>
              )}
              
              {/* Device Details Modal */}
              {selectedWin11Device && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                  <Card className="alice-popup bg-slate-900 border-[rgba(229,239,250,0.1)] w-full max-w-2xl max-h-[80vh] overflow-auto">
                    <CardHeader className="border-b border-[rgba(229,239,250,0.1)]">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-white flex items-center gap-2">
                          <Wrench className="w-5 h-5 text-amber-400" />
                          Upgrade Readiness: {selectedWin11Device.device_name}
                        </CardTitle>
                        <Button variant="ghost" size="icon" onClick={() => setSelectedWin11Device(null)}>
                          <X className="w-4 h-4 text-white/90" />
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent className="p-6 space-y-6">
                      {/* Device Info */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-white/70 text-sm">Manufacturer</p>
                          <p className="text-white font-medium">{selectedWin11Device.manufacturer || 'Unknown'}</p>
                        </div>
                        <div>
                          <p className="text-white/70 text-sm">Model</p>
                          <p className="text-white font-medium">{selectedWin11Device.model || 'Unknown'}</p>
                        </div>
                        <div>
                          <p className="text-white/70 text-sm">Current OS</p>
                          <p className="text-white font-medium">{selectedWin11Device.os_version || 'Unknown'}</p>
                        </div>
                        <div>
                          <p className="text-white/70 text-sm">Readiness Status</p>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            selectedWin11Device.readiness_status === 'no_data' ? 'bg-slate-400/20 text-slate-300' :
                            selectedWin11Device.is_upgraded ? 'bg-cyan-500/20 text-cyan-400' :
                            selectedWin11Device.is_capable ? 'bg-green-500/20 text-green-400' :
                            selectedWin11Device.is_not_capable ? 'bg-red-500/20 text-red-400' :
                            'bg-purple-500/20 text-purple-400'
                          }`}>
                            {selectedWin11Device.readiness_status === 'no_data' ? 'No Data' :
                              selectedWin11Device.is_upgraded ? 'Upgraded' :
                              selectedWin11Device.is_capable ? 'Capable' :
                              selectedWin11Device.is_not_capable ? 'Not Capable' : 'Unknown'}
                          </span>
                        </div>
                      </div>
                      
                      {/* Failure Reasons */}
                      {selectedWin11Device.eligibility_fail_reasons?.length > 0 && (
                        <div>
                          <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4 text-amber-400" />
                            Issues Preventing Upgrade ({selectedWin11Device.eligibility_fail_reasons.length})
                          </h4>
                          <div className="space-y-3">
                            {selectedWin11Device.eligibility_fail_reasons.map((reason, idx) => (
                              <div key={idx} className="p-4 rounded-lg bg-slate-800/50 border border-[rgba(229,239,250,0.1)]">
                                <div className="flex items-start gap-3">
                                  <div className="w-8 h-8 rounded-full flex items-center justify-center bg-amber-500/20 flex-shrink-0 mt-1">
                                    <AlertTriangle className="w-4 h-4 text-amber-400" />
                                  </div>
                                  <div className="flex-1">
                                    <h5 className="text-white font-medium">{reason.check}</h5>
                                    <p className="text-white/70 text-sm mt-1">{reason.reason}</p>
                                    <div className="mt-2 p-2 rounded bg-cyan-500/10 border border-cyan-500/20">
                                      <p className="text-cyan-400 text-sm">
                                        <strong>Remediation:</strong> {reason.remediation}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Hardware Check Summary */}
                      <div>
                        <h4 className="text-white font-medium mb-3">Hardware Compatibility Checks</h4>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                          {[
                            { check: 'TPM 2.0', passed: !selectedWin11Device.tpm_check_failed },
                            { check: 'Secure Boot', passed: !selectedWin11Device.secure_boot_check_failed },
                            { check: 'Processor', passed: !selectedWin11Device.processor_family_check_failed && !selectedWin11Device.processor_64bit_check_failed },
                            { check: 'RAM (4GB+)', passed: !selectedWin11Device.ram_check_failed },
                            { check: 'Storage (64GB+)', passed: !selectedWin11Device.storage_check_failed },
                            { check: 'OS Compatible', passed: !selectedWin11Device.os_check_failed },
                          ].map((item, idx) => (
                            <div key={idx} className={`flex items-center gap-2 p-2 rounded-lg ${
                              item.passed ? 'bg-green-500/10 border border-green-500/20' : 'bg-red-500/10 border border-red-500/20'
                            }`}>
                              {item.passed ? (
                                <CheckCircle className="w-4 h-4 text-green-400" />
                              ) : (
                                <X className="w-4 h-4 text-red-400" />
                              )}
                              <span className={item.passed ? 'text-green-400' : 'text-red-400'}>{item.check}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                    <div className="border-t border-[rgba(229,239,250,0.1)] p-4 flex justify-end">
                      <Button 
                        onClick={() => setSelectedWin11Device(null)}
                        className="bg-[#E5EFFA] text-[#3C02CC] hover:bg-[#BBFF71] hover:text-[#3C02CC] active:bg-[#BBFF71] active:text-[#3C02CC] font-semibold"
                      >
                        Close
                      </Button>
                    </div>
                  </Card>
                </div>
              )}
            </div>
          )}

          {/* ==================== FIRMWARE — DISCOVER BY MODEL (Phase 5, Workstream A) ==================== */}
          {activeTab === 'firmware' && (
            // `device_search` arrives from a Devices-tab link for one device.
            <FirmwareDiscoverTab
              lastIntuneSync={lastIntuneSync}
              deviceSearch={firmwareDeviceSearch}
              onClearDeviceSearch={() => setUrlParam('device_search', null)}
              onDeploySelection={(sel) => {
                // Hand off to the Firmware Deployments wizard on the
                // Deployment Groups page. Cross-page state travels via
                // sessionStorage — `DeploymentGroupsPage` reads it on
                // mount and pre-fills the wizard.
                try {
                  sessionStorage.setItem(
                    'alice_firmware_deploy_selection',
                    JSON.stringify(sel),
                  );
                } catch (_e) { /* SSR / storage disabled */ }
                navigate('/os-patch/groups?deployTab=firmware-deployments');
              }}
            />
          )}

          {/* Firmware · Devices tab removed 2026-07-25 (Phase 5 REDESIGN).
              Content is now inside FirmwareDiscoverTab.jsx. */}

          {/* WUfB Integration tab removed 2026-06-15 — content moved to
              Settings > Integrations (see <WufBIntegrationCard />). The
              wufbSettings state is still read above to drive the
              "wufbConnected" flag inside the Compliance trending section. */}
        </>
      )}

      {/* Page level, not inside the Devices tab: a toast's "View run" can be
          clicked after switching tab, and must open here rather than wait to
          pop up on the next visit to Devices (review, 05/10). */}
      {runDetailId && (
        <RunDetailModal runId={runDetailId} onClose={() => setRunDetailId(null)} />
      )}
    </div>
  );
};

// ==================== REMEDIATION PAGE ====================
//
// Library + runner UI for Intune Proactive Remediations targeted at stuck
// Windows Update devices.
//
//   • Scripts tab  — what the tenant currently owns in Intune (local mirror).
//   • Catalog tab  — 10 vetted built-ins, one click to install into Intune.
//   • Runs tab     — history of remediation runs (script, group, devices).
//
// Bulk-remediate from the Stuck Devices section feeds straight into the
// shared RunScriptModal below.
// =============================================================================

// Severity badge palette
const SEVERITY_STYLE = {
  high:   'bg-red-500/20 text-red-200 border border-red-400/30',
  medium: 'bg-amber-500/20 text-amber-200 border border-amber-400/30',
  low:    'bg-cyan-500/15 text-cyan-200 border border-cyan-400/30',
};

const SOURCE_STYLE = {
  catalog: 'bg-purple-500/20 text-purple-200 border border-purple-400/30',
  custom:  'bg-emerald-500/20 text-emerald-200 border border-emerald-400/30',
};

const _fmtTime = (iso) => {
  if (!iso) return '—';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  return new Date(t).toLocaleString();
};

// -----------------------------------------------------------------------------
// Catalog detail / preview modal
// -----------------------------------------------------------------------------
const CatalogDetailModal = ({ entryKey, onClose, onInstalled }) => {
  const { canWriteOSPatch } = usePermissions();
  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [installing, setInstalling] = useState(false);

  useEffect(() => {
    let cancelled = false;
    osPatchApi.getRemediationCatalogEntry(entryKey).then(res => {
      if (!cancelled) { setEntry(res.data); setLoading(false); }
    }).catch(() => { if (!cancelled) { setLoading(false); toast.error('Failed to load script details'); } });
    return () => { cancelled = true; };
  }, [entryKey]);

  const handleInstall = async () => {
    setInstalling(true);
    try {
      const res = await osPatchApi.createRemediationFromCatalog(entryKey);
      if (res.data.status === 'already_installed') {
        toast.info('Script is already installed for this tenant.');
      } else {
        toast.success(`Installed "${entry.name}" into Intune.`);
      }
      onInstalled && onInstalled(res.data.script);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to install script');
    } finally {
      setInstalling(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
         data-testid="remediation-catalog-detail-modal" onClick={onClose}>
      <div className="alice-popup bg-slate-900 border border-[rgba(229,239,250,0.15)] rounded-xl shadow-2xl max-w-4xl w-full max-h-[85vh] flex flex-col"
           onClick={e => e.stopPropagation()}>
        <div className="p-6 border-b border-[rgba(229,239,250,0.1)] flex items-start justify-between">
          <div>
            <h2 className="text-xl font-semibold text-white" data-testid="catalog-detail-title">
              {entry ? entry.name : 'Loading...'}
            </h2>
            {entry && (
              <p className="text-sm text-white/70 mt-1 max-w-2xl">{entry.description}</p>
            )}
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} data-testid="catalog-detail-close" className="text-white/70 hover:text-white">
            <X className="w-5 h-5" />
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {loading || !entry ? (
            <LoadingSpinner message="Loading script..." />
          ) : (
            <>
              <div className="flex flex-wrap gap-2">
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${SEVERITY_STYLE[entry.severity] || SEVERITY_STYLE.low}`}>
                  {entry.severity}
                </span>
                {(entry.tags || []).map(t => (
                  <span key={t} className="px-2 py-0.5 rounded-full text-xs bg-slate-700/60 text-white/80 border border-white/10">{t}</span>
                ))}
                <span className="px-2 py-0.5 rounded-full text-xs bg-slate-700/60 text-white/80 border border-white/10">
                  Run as: {entry.run_as || 'system'}
                </span>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-white/80 uppercase tracking-wider mb-2">Detection script</h3>
                <pre className="bg-slate-950 border border-white/10 rounded-lg p-3 text-xs text-cyan-200 font-mono overflow-x-auto whitespace-pre-wrap max-h-64">
{entry.detection_script}
                </pre>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-white/80 uppercase tracking-wider mb-2">Remediation script</h3>
                <pre className="bg-slate-950 border border-white/10 rounded-lg p-3 text-xs text-emerald-200 font-mono overflow-x-auto whitespace-pre-wrap max-h-80">
{entry.remediation_script}
                </pre>
              </div>
            </>
          )}
        </div>

        <div className="p-4 border-t border-[rgba(229,239,250,0.1)] flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={onClose} data-testid="catalog-detail-cancel-btn" className="text-white/80 hover:text-white">
            Close
          </Button>
          {canWriteOSPatch && (
          <Button onClick={handleInstall} disabled={loading || installing}
                  data-testid="catalog-detail-install-btn"
                  className="bg-[#E5EFFA] text-[#3C02CC] hover:bg-[#BBFF71] hover:text-[#3C02CC] font-semibold">
            <Plus className="w-4 h-4 mr-2" />
            {installing ? 'Installing…' : 'Install into Intune'}
          </Button>
          )}
        </div>
      </div>
    </div>
  );
};

// -----------------------------------------------------------------------------
// Custom / Edit script modal
// -----------------------------------------------------------------------------
const CustomScriptModal = ({ script, onClose, onSaved }) => {
  const editing = !!script;
  const [form, setForm] = useState(() => ({
    name: script?.name || '',
    description: script?.description || '',
    detection_script: script?.detection_script || '',
    remediation_script: script?.remediation_script || '',
    run_as: script?.run_as || 'system',
  }));
  const [saving, setSaving] = useState(false);

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.detection_script.trim() || !form.remediation_script.trim()) {
      toast.error('Name, detection script and remediation script are required.');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        const res = await osPatchApi.updateRemediation(script.id, form);
        toast.success('Script updated.');
        onSaved && onSaved(res.data.script);
      } else {
        const res = await osPatchApi.createCustomRemediation(form);
        toast.success('Script created.');
        onSaved && onSaved(res.data.script);
      }
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save script');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
         data-testid="remediation-custom-modal" onClick={onClose}>
      <form onSubmit={handleSubmit}
            onClick={e => e.stopPropagation()}
            className="alice-popup bg-slate-900 border border-[rgba(229,239,250,0.15)] rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        <div className="p-6 border-b border-[rgba(229,239,250,0.1)] flex items-start justify-between">
          <h2 className="text-xl font-semibold text-white">
            {editing ? 'Edit remediation script' : 'New custom script'}
          </h2>
          <Button type="button" variant="ghost" size="icon" onClick={onClose} className="text-white/70 hover:text-white">
            <X className="w-5 h-5" />
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <label className="text-xs uppercase tracking-wider text-white/70">Name</label>
              <Input value={form.name} onChange={e => setField('name', e.target.value)}
                     data-testid="custom-script-name"
                     className="bg-slate-950 border-[rgba(229,239,250,0.1)] text-white mt-1" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-white/70">Run as</label>
              <select value={form.run_as} onChange={e => setField('run_as', e.target.value)}
                      data-testid="custom-script-run-as"
                      className="w-full bg-slate-950 border border-[rgba(229,239,250,0.1)] text-white rounded-md px-3 py-2 mt-1 text-sm">
                <option value="system">SYSTEM</option>
                <option value="user">Logged-on user</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-white/70">Description</label>
            <Input value={form.description} onChange={e => setField('description', e.target.value)}
                   data-testid="custom-script-description"
                   className="bg-slate-950 border-[rgba(229,239,250,0.1)] text-white mt-1" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-white/70">Detection script (exit 0 = healthy, exit 1 = remediate)</label>
            <textarea value={form.detection_script} onChange={e => setField('detection_script', e.target.value)}
                      data-testid="custom-script-detection"
                      rows={8}
                      className="w-full bg-slate-950 border border-[rgba(229,239,250,0.1)] text-cyan-200 rounded-md px-3 py-2 mt-1 font-mono text-xs"
                      placeholder="# PowerShell detection script..." />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-white/70">Remediation script (exit 0 = success)</label>
            <textarea value={form.remediation_script} onChange={e => setField('remediation_script', e.target.value)}
                      data-testid="custom-script-remediation"
                      rows={10}
                      className="w-full bg-slate-950 border border-[rgba(229,239,250,0.1)] text-emerald-200 rounded-md px-3 py-2 mt-1 font-mono text-xs"
                      placeholder="# PowerShell remediation script..." />
          </div>
        </div>

        <div className="p-4 border-t border-[rgba(229,239,250,0.1)] flex items-center justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose} className="text-white/80 hover:text-white">
            Cancel
          </Button>
          <Button type="submit" disabled={saving} data-testid="custom-script-save-btn"
                  className="bg-[#E5EFFA] text-[#3C02CC] hover:bg-[#BBFF71] hover:text-[#3C02CC] font-semibold">
            {saving ? 'Saving…' : editing ? 'Save changes' : 'Create script'}
          </Button>
        </div>
      </form>
    </div>
  );
};

// -----------------------------------------------------------------------------
// Run-script modal (used by Scripts tab + Stuck Devices remediate flow)
// -----------------------------------------------------------------------------
const RunScriptModal = ({
  // Scripts tab "Run" passes the script; device-driven callers (Devices tab,
  // Stuck Devices) pass a recommended catalog key instead and let the admin
  // pick any catalog or custom script.
  preselectedScript,
  devices,                 // array of { device_id, azure_ad_device_id, device_name }
  recommendedCatalogKey,   // default choice, e.g. 'free_disk_space'
  source,                  // where the run was started, for the audit trail
  onClose,
  onRunCreated,
  onViewRun,
}) => {
  const navigate = useNavigate();
  const [scripts, setScripts] = useState(preselectedScript ? [preselectedScript] : []);
  const [catalog, setCatalog] = useState([]);
  const [recentRuns, setRecentRuns] = useState([]);
  // `script:<id>` for an installed script, `catalog:<key>` for a catalog entry
  // that is installed into Intune first (the backend adopts rather than
  // duplicates if it is already there).
  const [choice, setChoice] = useState(preselectedScript ? `script:${preselectedScript.id}` : '');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [stage, setStage] = useState(null);
  const [typedCount, setTypedCount] = useState('');
  const [overrideRecent, setOverrideRecent] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [s, c, r] = await Promise.allSettled([
        preselectedScript ? Promise.resolve(null) : osPatchApi.getRemediationScriptsV2({ reconcile: false }),
        osPatchApi.getRemediationCatalog(),
        osPatchApi.getRemediationRuns({ limit: 200 }),
      ]);
      if (cancelled) return;
      const scriptList = preselectedScript
        ? [preselectedScript]
        : (s.status === 'fulfilled' ? (s.value?.data?.scripts || []) : []);
      const catalogList = c.status === 'fulfilled' ? (c.value?.data?.catalog || []) : [];
      setScripts(scriptList);
      setCatalog(catalogList);
      setRecentRuns(r.status === 'fulfilled' ? (r.value?.data?.runs || []) : []);
      if (s.status === 'rejected' || c.status === 'rejected') toast.error('Could not load every remediation script');
      if (!preselectedScript && recommendedCatalogKey) {
        const installed = scriptList.find((x) => x.catalog_key === recommendedCatalogKey);
        if (installed) setChoice(`script:${installed.id}`);
        else if (catalogList.some((e) => e.key === recommendedCatalogKey)) setChoice(`catalog:${recommendedCatalogKey}`);
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line -- the build loads no react-hooks plugin, so name no rule
  }, []);

  const eligible = (devices || []).filter((d) => d.azure_ad_device_id);
  const skipped = (devices || []).length - eligible.length;

  const installedKeys = new Set(scripts.map((x) => x.catalog_key).filter(Boolean));
  const [choiceKind, choiceId] = choice ? choice.split(/:(.*)/s) : [null, null];
  const selectedScript = choiceKind === 'script' ? scripts.find((x) => x.id === choiceId) : null;
  const selectedCatalog = choiceKind === 'catalog' ? catalog.find((e) => e.key === choiceId) : null;
  const selected = selectedScript || selectedCatalog;
  const selectedKey = selectedScript?.catalog_key || selectedCatalog?.key || null;
  const restarts = /restart|reboot/.test(selectedKey || '');
  const recommendedEntry = catalog.find((e) => e.key === recommendedCatalogKey);

  // Duplicate guard: devices this script already targeted in the last 24h.
  // Intune runs a script at the next check-in, so a second run inside a day
  // is usually impatience rather than a new problem — warn, allow override.
  const recentDupes = useMemo(() => {
    if (!selected) return [];
    const cutoff = Date.now() - 24 * 60 * 60 * 1000;
    const targeted = new Map();
    recentRuns.forEach((r) => {
      const same = (selectedScript && r.script_id === selectedScript.id) || (selectedKey && r.catalog_key === selectedKey);
      // `!(t >= cutoff)` also skips runs with no parseable time (NaN).
      const t = Date.parse(r.created_at || '');
      if (!same || !(t >= cutoff)) return;
      (r.added_devices || []).forEach((d) => { if (d.device_id) targeted.set(d.device_id, r.created_at); });
    });
    return eligible.filter((d) => targeted.has(d.device_id));
    // eslint-disable-next-line -- the build loads no react-hooks plugin, so name no rule
  }, [recentRuns, choice, devices]);

  const confirmed = largeRunConfirmed(eligible.length, typedCount);
  const blockedByRecent = recentDupes.length > 0 && !overrideRecent;
  const canRun = Boolean(selected) && eligible.length > 0 && confirmed && !blockedByRecent && !running;

  const handleRun = async () => {
    if (!canRun) return;
    setRunning(true);
    try {
      let scriptId = selectedScript?.id;
      if (!scriptId && selectedCatalog) {
        setStage(`Installing "${selectedCatalog.name}" in Intune…`);
        const created = await osPatchApi.createRemediationFromCatalog(selectedCatalog.key);
        scriptId = created.data?.script?.id;
        if (!scriptId) throw new Error('Could not resolve the remediation script');
      }
      setStage(`Targeting ${eligible.length} device${eligible.length === 1 ? '' : 's'}…`);
      const res = await osPatchApi.runRemediation(scriptId, {
        devices: eligible.map((d) => ({
          device_id: d.device_id,
          azure_ad_device_id: d.azure_ad_device_id,
          device_name: d.device_name,
        })),
        notes: notes.trim() || undefined,
        source: source || 'remediation_page',
      });
      const run = res.data.run;
      const msg = run.added_count < run.target_count
        ? `Remediation assigned to ${run.added_count} of ${run.target_count} devices — open the run to see which failed.`
        : `Remediation assigned to ${run.added_count} device${run.added_count === 1 ? '' : 's'} — runs at next check-in.`;
      const notify = run.added_count < run.target_count ? toast.warning : toast.success;
      notify(msg, onViewRun ? { action: { label: 'View run', onClick: () => onViewRun(run.id) } } : undefined);
      onRunCreated && onRunCreated(run);
      onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : (detail?.message || err?.message || 'Run failed'));
    } finally {
      setRunning(false);
      setStage(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
         data-testid="remediation-run-modal" onClick={() => !running && onClose()}>
      <div className="alice-popup bg-slate-900 border border-[rgba(229,239,250,0.15)] rounded-xl shadow-2xl max-w-2xl w-full max-h-[85vh] flex flex-col"
           onClick={e => e.stopPropagation()}>
        <div className="p-6 border-b border-[rgba(229,239,250,0.1)] flex items-start justify-between">
          <h2 className="text-xl font-semibold text-white">Run remediation</h2>
          <Button variant="ghost" size="icon" onClick={onClose} disabled={running} className="text-white/70 hover:text-white">
            <X className="w-5 h-5" />
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* Script picker */}
          <div>
            <label className="text-xs uppercase tracking-wider text-white/70">Script</label>
            {loading ? (
              <div className="text-white/60 text-sm mt-1">Loading scripts…</div>
            ) : preselectedScript ? (
              <div className="bg-slate-950 border border-white/10 rounded-md px-3 py-2 mt-1 text-white">
                {preselectedScript.name}
              </div>
            ) : (
              <select value={choice} onChange={e => { setChoice(e.target.value); setOverrideRecent(false); }}
                      disabled={running}
                      data-testid="run-modal-script-select"
                      className="w-full bg-slate-950 border border-[rgba(229,239,250,0.1)] text-white rounded-md px-3 py-2 mt-1 text-sm">
                <option value="">Choose a script…</option>
                {scripts.length > 0 && (
                  <optgroup label="Installed in Intune">
                    {scripts.map(x => (
                      <option key={x.id} value={`script:${x.id}`}>
                        {x.name}{x.catalog_key && x.catalog_key === recommendedCatalogKey ? ' (recommended)' : ''}{x.source === 'custom' ? ' — custom' : ''}
                      </option>
                    ))}
                  </optgroup>
                )}
                {catalog.some(e => !installedKeys.has(e.key)) && (
                  <optgroup label="Catalog — installed into Intune first">
                    {catalog.filter(e => !installedKeys.has(e.key)).map(e => (
                      <option key={e.key} value={`catalog:${e.key}`}>
                        {e.name}{e.key === recommendedCatalogKey ? ' (recommended)' : ''}
                      </option>
                    ))}
                  </optgroup>
                )}
              </select>
            )}
            {recommendedEntry && selectedKey !== recommendedCatalogKey && !loading && (
              <p className="text-xs text-white/50 mt-1" data-testid="run-modal-recommended-hint">
                Recommended for this issue: {recommendedEntry.name}
              </p>
            )}
            {selected?.description && (
              <p className="text-xs text-white/70 mt-2" data-testid="run-modal-script-description">{selected.description}</p>
            )}
            {selected && (
              <p className="text-xs text-white/50 mt-1">
                Runs as {selected.run_as === 'user' ? 'the signed-in user' : 'SYSTEM'} at each device&apos;s next Intune check-in.
              </p>
            )}
            {restarts && (
              <p className="text-xs text-amber-300 mt-1" data-testid="run-modal-restart-warning">
                This restarts the device. Users get a 5-minute on-screen warning.
              </p>
            )}
          </div>

          {/* Target summary */}
          <div className="rounded-lg border border-white/10 bg-slate-950/60 p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-white/80">Targets</span>
              <span className="text-2xl font-semibold text-white" data-testid="run-modal-target-count">{eligible.length}</span>
            </div>
            {skipped > 0 && (
              <p className="text-xs text-amber-300 mt-1" data-testid="run-modal-skipped">
                {skipped} device{skipped === 1 ? '' : 's'} skipped (no Azure AD device id).
              </p>
            )}
            {(devices || []).length === 0 ? (
              // The Scripts tab has no device picker; the Devices tab is it.
              <div className="mt-2 text-xs text-white/70" data-testid="run-modal-no-devices">
                No devices selected. Choose devices on Discover &gt; Devices and use Remediate there.
                <Button size="sm" variant="ghost" className="ml-1 h-6 px-2 text-xs text-cyan-300 hover:bg-cyan-500/10"
                        onClick={() => { onClose(); navigate('/os-patch/discover?tab=devices'); }}>
                  Go to Devices
                </Button>
              </div>
            ) : (
              <div className="mt-3 max-h-32 overflow-y-auto space-y-1">
                {eligible.slice(0, 12).map((d, i) => (
                  <div key={i} className="text-xs text-white/70 font-mono truncate">
                    {d.device_name || d.azure_ad_device_id}
                  </div>
                ))}
                {eligible.length > 12 && (
                  <div className="text-xs text-white/50">…and {eligible.length - 12} more</div>
                )}
              </div>
            )}
          </div>

          <p className="text-xs text-white/50">
            Devices go into this script&apos;s own Entra group (ALICE-Remediation-…), which is reused for every run
            and emptied of devices once they have run it.
          </p>

          {recentDupes.length > 0 && (
            <div className="rounded-lg border border-amber-400/40 bg-amber-500/10 p-3" data-testid="run-modal-recent-warning">
              <p className="text-sm text-amber-100">
                {recentDupes.length} of these device{recentDupes.length === 1 ? ' was' : 's were'} sent this script in the
                last 24 hours. It runs at the next check-in, so the earlier run may simply not have reached
                {recentDupes.length === 1 ? ' it' : ' them'} yet.
              </p>
              <p className="text-xs text-amber-100/70 mt-1 truncate">
                {recentDupes.slice(0, 6).map(d => d.device_name).join(', ')}{recentDupes.length > 6 ? ` and ${recentDupes.length - 6} more` : ''}
              </p>
              <label className="mt-2 inline-flex items-center gap-2 text-xs text-white cursor-pointer">
                <input type="checkbox" checked={overrideRecent} onChange={e => setOverrideRecent(e.target.checked)}
                       className="accent-amber-400" data-testid="run-modal-recent-override" />
                Run again anyway
              </label>
            </div>
          )}

          {eligible.length > LARGE_REMEDIATION_RUN && (
            <LargeRunConfirm count={eligible.length} value={typedCount} onChange={setTypedCount} testId="run-modal-large-confirm" />
          )}

          <div>
            <label className="text-xs uppercase tracking-wider text-white/70">Notes (optional)</label>
            <Input value={notes} onChange={e => setNotes(e.target.value)}
                   data-testid="run-modal-notes"
                   placeholder="Triggered after Defender update kept failing"
                   className="bg-slate-950 border-[rgba(229,239,250,0.1)] text-white mt-1" />
          </div>
        </div>

        <div className="p-4 border-t border-[rgba(229,239,250,0.1)] flex items-center justify-end gap-2">
          {stage && <span className="text-xs text-white/60 mr-auto">{stage}</span>}
          <Button variant="ghost" onClick={onClose} disabled={running} className="text-white/80 hover:text-white">Cancel</Button>
          <Button onClick={handleRun} disabled={!canRun}
                  data-testid="run-modal-confirm-btn"
                  className="bg-[#E5EFFA] text-[#3C02CC] hover:bg-[#BBFF71] hover:text-[#3C02CC] font-semibold">
            <Play className="w-4 h-4 mr-2" />
            {running ? 'Assigning…' : selected
              ? `Run "${selected.name}" on ${eligible.length} device${eligible.length === 1 ? '' : 's'}`
              : `Remediate ${eligible.length} device${eligible.length === 1 ? '' : 's'}`}
          </Button>
        </div>
      </div>
    </div>
  );
};

// -----------------------------------------------------------------------------
// Run detail drawer (Runs tab)
// -----------------------------------------------------------------------------
const RunDetailModal = ({ runId, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async (refresh = false) => {
    if (refresh) setRefreshing(true); else setLoading(true);
    try {
      const res = await osPatchApi.getRemediationRun(runId, refresh);
      setData(res.data);
    } catch {
      toast.error('Failed to load run');
    } finally {
      setLoading(false); setRefreshing(false);
    }
  };

  useEffect(() => { load(false); /* eslint-disable-next-line */ }, [runId]);

  const run = data?.run;
  const states = data?.device_run_states || [];

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
         data-testid="remediation-run-detail-modal" onClick={onClose}>
      <div className="alice-popup bg-slate-900 border border-[rgba(229,239,250,0.15)] rounded-xl shadow-2xl max-w-3xl w-full max-h-[85vh] flex flex-col"
           onClick={e => e.stopPropagation()}>
        <div className="p-6 border-b border-[rgba(229,239,250,0.1)] flex items-start justify-between">
          <div>
            <h2 className="text-xl font-semibold text-white">{run?.script_name || 'Run details'}</h2>
            <p className="text-xs text-white/60 mt-1 font-mono">{run?.group_name}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm"
                    onClick={() => load(true)} disabled={refreshing}
                    data-testid="run-detail-refresh-btn"
                    className="border-white/10 text-white hover:bg-white/5">
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh from Intune
            </Button>
            <Button variant="ghost" size="icon" onClick={onClose} className="text-white/70 hover:text-white">
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {loading ? <LoadingSpinner message="Loading run…" /> : !run ? (
            <p className="text-white/70">Run not found.</p>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div className="bg-slate-950 border border-white/10 rounded-lg p-3">
                  <div className="text-white/60 text-xs uppercase">Targets</div>
                  <div className="text-white font-semibold text-xl">{run.target_count}</div>
                </div>
                <div className="bg-slate-950 border border-white/10 rounded-lg p-3">
                  <div className="text-white/60 text-xs uppercase">Added</div>
                  <div className="text-emerald-300 font-semibold text-xl">{run.added_count}</div>
                </div>
                <div className="bg-slate-950 border border-white/10 rounded-lg p-3">
                  <div className="text-white/60 text-xs uppercase">Add failed</div>
                  <div className="text-red-300 font-semibold text-xl">{run.failed_count}</div>
                </div>
                <div className="bg-slate-950 border border-white/10 rounded-lg p-3">
                  <div className="text-white/60 text-xs uppercase">Assignment</div>
                  <div className={`font-semibold ${run.assignment_succeeded ? 'text-emerald-300' : 'text-red-300'}`}>
                    {run.assignment_succeeded ? 'OK' : 'Failed'}
                  </div>
                </div>
              </div>

              {run.notes && (
                <div className="text-sm text-white/80 italic">{run.notes}</div>
              )}

              <div>
                <h3 className="text-sm font-semibold text-white/80 uppercase tracking-wider mb-2">Targeted devices</h3>
                <div className="rounded-lg border border-white/10 max-h-72 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-white/5">
                      <tr className="text-white/70 text-xs uppercase">
                        <th className="text-left p-2">Device</th>
                        <th className="text-left p-2">AAD ID</th>
                        <th className="text-left p-2">Intune state</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(run.added_devices || []).map((d, i) => {
                        const st = states.find(s =>
                          (s.managedDeviceId && s.managedDeviceId === d.device_id) ||
                          ((s.managedDevice || {}).id === d.device_id)
                        );
                        // October 2026: each script reuses one group, so Intune
                        // can hold a state for this device from an EARLIER run.
                        // A state older than this run is shown as such rather
                        // than passed off as this run's result.
                        const stale = st && run.created_at && String(st.lastStateUpdateDateTime || '') < String(run.created_at);
                        return (
                          <tr key={i} className="border-t border-white/5">
                            <td className="p-2 text-white">{d.device_name || '—'}</td>
                            <td className="p-2 text-white/60 font-mono text-xs">{d.azure_ad_device_id}</td>
                            <td className="p-2 text-white/70 text-xs">
                              {!st ? <span className="text-white/40">no state yet</span>
                                : stale ? (
                                  <span className="text-white/40" title={`Last result (${st.detectionState || st.remediationState || 'unknown'}) is from an earlier run`}>
                                    waiting for check-in
                                  </span>
                                ) : (
                                  <>
                                    {st.detectionState || 'unknown'}
                                    {st.remediationState && st.remediationState !== 'unknown' && (
                                      <span className="text-white/50"> · remediation {st.remediationState}</span>
                                    )}
                                  </>
                                )}
                            </td>
                          </tr>
                        );
                      })}
                      {(run.failed_devices || []).map((d, i) => (
                        <tr key={`f-${i}`} className="border-t border-white/5 bg-red-500/5">
                          <td className="p-2 text-red-200">{d.device_name || '—'}</td>
                          <td className="p-2 text-red-200/70 font-mono text-xs">{d.azure_ad_device_id}</td>
                          <td className="p-2 text-red-300 text-xs">{d.reason || 'failed to add to group'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {run.assignment_error && (
                <div className="bg-red-500/10 border border-red-400/30 rounded-lg p-3 text-sm text-red-200">
                  <strong>Assignment error:</strong> {run.assignment_error}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// -----------------------------------------------------------------------------
// Group cleanup (October 2026)
// -----------------------------------------------------------------------------
// Each script now reuses one Entra group. This shows, before doing anything,
// which devices have finished and will come out of those groups, and which
// one-group-per-run groups from before the change will be deleted. Nothing is
// touched until "Clean up" is pressed; the preview is the backend's dry run.
const RemediationCleanupModal = ({ onClose, onApplied }) => {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState(null);
  const [removeMembers, setRemoveMembers] = useState(true);
  const [deleteLegacy, setDeleteLegacy] = useState(true);

  useEffect(() => {
    let cancelled = false;
    osPatchApi.cleanupRemediationGroups({ dry_run: true })
      .then((res) => { if (!cancelled) setPreview(res.data); })
      .catch((e) => { if (!cancelled) setError(_errText(e, 'Could not preview the cleanup')); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const scripts = (preview?.scripts || []).filter(s => (s.finished || []).length > 0 || s.error);
  const groups = preview?.groups || [];
  const nothingToDo = !loading && !error && (preview?.finished_members || 0) === 0 && groups.length === 0;

  const handleApply = async () => {
    setApplying(true);
    try {
      const res = await osPatchApi.cleanupRemediationGroups({
        dry_run: false,
        remove_finished_members: removeMembers,
        delete_legacy_groups: deleteLegacy,
      });
      const r = res.data || {};
      const deleted = (r.groups || []).filter(g => g.action === 'deleted' || g.action === 'already_gone').length;
      const failed = (r.groups || []).filter(g => g.action === 'delete_failed').length;
      // `check_failed`: Graph could not confirm the group either way, so it was
      // left alone and stays on offer for the next cleanup.
      const unchecked = (r.groups || []).filter(g => g.action === 'check_failed').length;
      toast.success(`Cleaned up: ${r.finished_members || 0} device(s) removed from groups, ${deleted} old group(s) deleted`);
      if (failed) toast.warning(`${failed} group(s) could not be deleted — check Group.ReadWrite.All`);
      if (unchecked) toast.warning(`${unchecked} group(s) could not be checked with Microsoft Graph and were left; try again later`);
      onApplied && onApplied();
      onClose();
    } catch (e) {
      toast.error(_errText(e, 'Cleanup failed'));
      setApplying(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
         data-testid="remediation-cleanup-modal" onClick={() => !applying && onClose()}>
      <div className="alice-popup bg-slate-900 border border-[rgba(229,239,250,0.15)] rounded-xl shadow-2xl max-w-2xl w-full max-h-[85vh] flex flex-col"
           onClick={e => e.stopPropagation()}>
        <div className="p-6 border-b border-[rgba(229,239,250,0.1)] flex items-start justify-between">
          <div>
            <h2 className="text-xl font-semibold text-white">Clean up remediation groups</h2>
            <p className="text-xs text-white/60 mt-1">Preview — nothing has been changed yet.</p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} disabled={applying} className="text-white/70 hover:text-white">
            <X className="w-5 h-5" />
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {loading ? <LoadingSpinner message="Checking groups against Intune run states…" /> : error ? (
            <p className="text-sm text-red-300">{error}</p>
          ) : nothingToDo ? (
            <p className="text-sm text-white/70" data-testid="cleanup-nothing">
              Nothing to clean up. No device has finished a script it is still grouped for, and there are no
              groups left over from older runs.
            </p>
          ) : (
            <>
              <div>
                <label className="flex items-center gap-2 text-sm text-white font-medium cursor-pointer">
                  <input type="checkbox" checked={removeMembers} onChange={e => setRemoveMembers(e.target.checked)}
                         className="accent-cyan-400" data-testid="cleanup-remove-members" />
                  Remove {preview?.finished_members || 0} device(s) that have finished
                </label>
                <p className="text-xs text-white/50 ml-6 mt-0.5">
                  Each run does this automatically for its own script; this does it for every script now, and
                  also removes devices that have reported nothing for 30 days.
                </p>
                {scripts.length > 0 && (
                  <ul className="ml-6 mt-2 space-y-1 text-xs text-white/75">
                    {scripts.map(s => (
                      <li key={s.script_id}>
                        <span className="text-white">{s.script_name}</span>
                        {s.error ? <span className="text-red-300"> — {s.error}</span> : (
                          <span className="text-white/50">
                            {' '}— {s.finished.filter(f => !f.stale).length} finished
                            {s.finished.some(f => f.stale) && `, ${s.finished.filter(f => f.stale).length} with no report in 30 days`}
                            {' '}({s.finished.slice(0, 4).map(f => f.device_name).join(', ')}
                            {s.finished.length > 4 ? '…' : ''}), {s.still_waiting} still waiting
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div>
                <label className="flex items-center gap-2 text-sm text-white font-medium cursor-pointer">
                  <input type="checkbox" checked={deleteLegacy} onChange={e => setDeleteLegacy(e.target.checked)}
                         className="accent-cyan-400" data-testid="cleanup-delete-legacy" />
                  Delete {groups.length} old group(s) from Entra
                </label>
                <p className="text-xs text-white/50 ml-6 mt-0.5">
                  Groups ALICE created one per run before each script had its own, or the group of a script since
                  deleted. Only groups ALICE recorded creating are listed.
                </p>
                {groups.length > 0 && (
                  <ul className="ml-6 mt-2 space-y-1 text-xs text-white/75 max-h-40 overflow-y-auto">
                    {groups.map(g => (
                      <li key={g.group_id}>
                        <span className="font-mono">{g.group_name}</span>
                        <span className="text-white/40"> · {g.runs} run(s), last {_fmtTime(g.last_run_at)}</span>
                        {/* Still the script's live assignment (not yet moved to a
                            standing group): devices in it that haven't run won't. */}
                        {g.current_assignment && (
                          <span className="block text-amber-300" data-testid={`cleanup-live-${g.group_id}`}>
                            Still this script&apos;s current assignment — devices in it that haven&apos;t run yet won&apos;t once it is deleted.
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </>
          )}
        </div>

        <div className="p-4 border-t border-[rgba(229,239,250,0.1)] flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={applying} className="text-white/80 hover:text-white">
            {nothingToDo ? 'Close' : 'Cancel'}
          </Button>
          {!nothingToDo && (
            <Button onClick={handleApply}
                    disabled={loading || applying || Boolean(error) || (!removeMembers && !deleteLegacy)}
                    data-testid="cleanup-apply-btn"
                    className="bg-[#E5EFFA] text-[#3C02CC] hover:bg-[#BBFF71] hover:text-[#3C02CC] font-semibold">
              {applying ? 'Cleaning up…' : 'Clean up'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

// -----------------------------------------------------------------------------
// Main page
// -----------------------------------------------------------------------------
export const RemediationPage = () => {
  const { canWriteOSPatch, canViewOSPatchPolicies, canManageOSPatches } = usePermissions();
  // October 2026: creating, editing, deleting and running scripts needs the
  // manage_os_patches permission as well as the write plan — Viewers see the
  // page read-only. The backend refuses them independently.
  const canRemediate = canWriteOSPatch && canManageOSPatches;
  const [tab, setTab] = useState('scripts'); // scripts | catalog | runs
  const [scripts, setScripts] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);

  const [modalCatalogKey, setModalCatalogKey] = useState(null);
  const [modalCustom, setModalCustom] = useState(false);
  const [editScript, setEditScript] = useState(null);
  const [runScript, setRunScript] = useState(null);
  const [runDetailId, setRunDetailId] = useState(null);
  const [createMenuOpen, setCreateMenuOpen] = useState(false);
  const [showCleanup, setShowCleanup] = useState(false);
  // Scripts sitting in Intune with no local record — see the banner below.
  const [orphans, setOrphans] = useState([]);
  // Per-section load failures, so a broken part names itself instead of
  // blanking the page.
  const [loadErrors, setLoadErrors] = useState([]);

  // 03/09: this was `Promise.all` with a single catch, so one failing call
  // emptied the entire page — the built-in catalog is served from memory and
  // cannot fail on its own, yet it vanished because the runs query threw. Each
  // section now loads independently and says what actually broke.
  const loadAll = async () => {
    setLoading(true);
    const [s, c, r] = await Promise.allSettled([
      osPatchApi.getRemediationScriptsV2(),
      osPatchApi.getRemediationCatalog(),
      osPatchApi.getRemediationRuns(),
    ]);

    const failures = [];
    const failureOf = (res, label) => {
      const detail = res.reason?.response?.data?.detail;
      failures.push(`${label}: ${typeof detail === 'string' ? detail : (res.reason?.message || 'request failed')}`);
    };

    if (s.status === 'fulfilled') {
      setScripts(s.value.data.scripts || []);
      setOrphans(s.value.data.orphaned_in_intune || []);
    } else failureOf(s, 'Installed scripts');

    if (c.status === 'fulfilled') setCatalog(c.value.data.catalog || []);
    else failureOf(c, 'Catalog');

    if (r.status === 'fulfilled') setRuns(r.value.data.runs || []);
    else failureOf(r, 'Run history');

    setLoadErrors(failures);
    if (failures.length) toast.error(failures[0]);
    setLoading(false);
  };

  useEffect(() => {
    if (!canViewOSPatchPolicies) return;
    loadAll();
  }, [canViewOSPatchPolicies]);

  const handleDelete = async (script) => {
    if (!window.confirm(`Delete script "${script.name}"? This removes it from Intune as well.`)) return;
    try {
      await osPatchApi.deleteRemediation(script.id);
      toast.success('Script deleted.');
      loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to delete script');
    }
  };

  // --- Scripts tab ---
  const ScriptsTab = () => (
    <div className="space-y-3" data-testid="remediation-scripts-tab">
      {/* 03/09: this list is a local mirror of Intune, and the two can drift —
          a script created in Intune whose local record failed to save leaves
          nothing on this page to see. The backend now reconciles the two, and
          anything ALICE put in Intune but cannot account for is named here
          rather than left to be found by hand. */}
      {orphans.length > 0 && (
        <Card className="bg-amber-500/10 border border-amber-500/30" data-testid="remediation-orphans-banner">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-4 h-4 text-amber-300 mt-0.5 shrink-0" />
              <div className="text-sm">
                <p className="text-amber-200 font-medium">
                  {orphans.length} script{orphans.length === 1 ? '' : 's'} in Intune with no record in ALICE
                </p>
                <p className="text-white/70 text-xs mt-1">
                  Created by ALICE but never saved locally, so {orphans.length === 1 ? 'it is' : 'they are'} not
                  listed below and cannot be run or deleted from here. Installing the same catalog entry again
                  will adopt {orphans.length === 1 ? 'it' : 'them'} rather than create a duplicate.
                </p>
                <ul className="mt-2 space-y-1">
                  {orphans.map(o => (
                    <li key={o.intune_script_id} className="text-xs text-white/80">
                      <span className="font-medium text-white">{o.name || 'Unnamed script'}</span>
                      <span className="text-white/45"> · {o.intune_script_id}</span>
                      {o.created && <span className="text-white/45"> · created {_fmtTime(o.created)}</span>}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      {scripts.length === 0 ? (
        <Card className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] border-[rgba(229,239,250,0.1)]">
          <CardContent className="p-12 text-center">
            <Wrench className="w-12 h-12 text-[#E5EFFA]/30 mx-auto mb-4" />
            <h3 className="text-xl font-medium text-white mb-2">No scripts installed yet</h3>
            <p className="text-white/90 mb-4">{canRemediate ? 'Pick a built-in from the Catalog or create a custom script.' : 'Browse the Catalog to see the built-in scripts.'}</p>
            <Button onClick={() => setTab('catalog')} data-testid="scripts-empty-browse-catalog-btn"
                    className="bg-[#E5EFFA] text-[#3C02CC] hover:bg-[#BBFF71] hover:text-[#3C02CC] font-semibold">
              Browse catalog
            </Button>
          </CardContent>
        </Card>
      ) : scripts.map(s => (
        <Card key={s.id} className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] border-[rgba(229,239,250,0.1)]"
              data-testid={`script-row-${s.id}`}>
          <CardContent className="p-5">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-[200px]">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="text-lg font-medium text-white">{s.name}</h3>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${SOURCE_STYLE[s.source] || 'bg-slate-700/60 text-white/80 border border-white/10'}`}>
                    {s.source}
                  </span>
                  {s.severity && (
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${SEVERITY_STYLE[s.severity] || SEVERITY_STYLE.low}`}>
                      {s.severity}
                    </span>
                  )}
                  {/* Drift the other way: ALICE has a record, Intune does not.
                      Running it would fail, so say so on the card. */}
                  {s.in_intune === false && (
                    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-500/15 text-red-300 border border-red-500/30"
                          title="Deleted in Intune, or never created there. Running this will fail."
                          data-testid={`script-missing-intune-${s.id}`}>
                      Missing in Intune
                    </span>
                  )}
                </div>
                <p className="text-white/80 text-sm mt-1 line-clamp-2">{s.description || 'No description'}</p>
                <p className="text-white/50 text-xs mt-2">
                  Created {_fmtTime(s.created_at)}{s.last_run_at ? ` • Last run ${_fmtTime(s.last_run_at)}` : ''}
                </p>
              </div>
              {canRemediate && (
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="sm" onClick={() => setRunScript(s)}
                        data-testid={`script-run-btn-${s.id}`}
                        className="text-emerald-300 hover:bg-emerald-500/10">
                  <Play className="w-4 h-4 mr-1" /> Run
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setEditScript(s)}
                        data-testid={`script-edit-btn-${s.id}`}
                        className="text-white/80 hover:bg-white/10">
                  <Edit className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => handleDelete(s)}
                        data-testid={`script-delete-btn-${s.id}`}
                        className="text-red-300 hover:bg-red-500/10">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
              )}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );

  // --- Catalog tab ---
  const CatalogTab = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="remediation-catalog-tab">
      {catalog.map(entry => (
        <Card key={entry.key}
              className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] border-[rgba(229,239,250,0.1)] hover:border-cyan-400/30 transition-colors"
              data-testid={`catalog-card-${entry.key}`}>
          <CardContent className="p-5">
            <div className="flex items-start justify-between gap-2">
              <div>
                <h3 className="text-base font-semibold text-white">{entry.name}</h3>
                <div className="flex flex-wrap items-center gap-2 mt-1">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${SEVERITY_STYLE[entry.severity] || SEVERITY_STYLE.low}`}>
                    {entry.severity}
                  </span>
                  {entry.installed && (
                    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-500/20 text-emerald-200 border border-emerald-400/30">
                      Installed
                    </span>
                  )}
                </div>
              </div>
            </div>
            <p className="text-white/80 text-sm mt-3 line-clamp-3">{entry.description}</p>
            <div className="flex flex-wrap gap-1 mt-3">
              {(entry.tags || []).slice(0, 4).map(t => (
                <span key={t} className="px-1.5 py-0.5 rounded text-[10px] bg-slate-700/60 text-white/70 border border-white/10">{t}</span>
              ))}
            </div>
            <div className="flex items-center gap-2 mt-4">
              <Button size="sm" variant="outline" onClick={() => setModalCatalogKey(entry.key)}
                      data-testid={`catalog-view-btn-${entry.key}`}
                      className="border-white/10 text-white hover:bg-white/5">
                <Eye className="w-4 h-4 mr-1" /> View
              </Button>
              {canRemediate && !entry.installed && (
                <Button size="sm" onClick={() => setModalCatalogKey(entry.key)}
                        data-testid={`catalog-install-btn-${entry.key}`}
                        className="bg-[#E5EFFA] text-[#3C02CC] hover:bg-[#BBFF71] hover:text-[#3C02CC] font-semibold">
                  <Plus className="w-4 h-4 mr-1" /> Install
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );

  // --- Runs tab ---
  const RunsTab = () => (
    <div className="space-y-2" data-testid="remediation-runs-tab">
      {runs.length === 0 ? (
        <Card className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] border-[rgba(229,239,250,0.1)]">
          <CardContent className="p-12 text-center">
            <Clock className="w-12 h-12 text-[#E5EFFA]/30 mx-auto mb-4" />
            <p className="text-white">No remediation runs yet.</p>
            <p className="text-white/60 text-sm mt-1">Run a script from the Scripts tab, or from the Stuck Devices section in OS Updates.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-[rgba(229,239,250,0.1)]">
          <table className="w-full text-sm">
            <thead className="bg-[rgba(229,239,250,0.08)] text-white/80">
              <tr>
                <th className="text-left p-3 font-medium">When</th>
                <th className="text-left p-3 font-medium">Script</th>
                <th className="text-left p-3 font-medium">By</th>
                <th className="text-left p-3 font-medium">AAD group</th>
                <th className="text-right p-3 font-medium">Targets</th>
                <th className="text-right p-3 font-medium">Added</th>
                <th className="text-left p-3 font-medium">Status</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {runs.map(r => (
                <tr key={r.id} className="border-t border-white/5 hover:bg-white/5" data-testid={`run-row-${r.id}`}>
                  <td className="p-3 text-white/80 text-xs">{_fmtTime(r.created_at)}</td>
                  <td className="p-3 text-white">{r.script_name}</td>
                  {/* Runs before October 2026 recorded no one. */}
                  <td className="p-3 text-white/70 text-xs">{r.requested_by || <span className="text-white/40">—</span>}</td>
                  <td className="p-3 text-white/60 font-mono text-xs">{r.group_name}</td>
                  <td className="p-3 text-right text-white">{r.target_count}</td>
                  <td className="p-3 text-right text-emerald-300">{r.added_count}</td>
                  <td className="p-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${r.assignment_succeeded ? 'bg-emerald-500/15 text-emerald-200 border border-emerald-400/30' : 'bg-red-500/15 text-red-200 border border-red-400/30'}`}>
                      {r.status}
                    </span>
                  </td>
                  <td className="p-3 text-right">
                    <Button variant="ghost" size="sm" onClick={() => setRunDetailId(r.id)}
                            data-testid={`run-view-btn-${r.id}`}
                            className="text-cyan-300 hover:bg-cyan-500/10">
                      <Eye className="w-4 h-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  // After every hook, so hook order is identical whichever plan the tenant is on.
  if (!canViewOSPatchPolicies) {
    return (
      <OSPatchPolicyUnavailable
        title="Remediation"
        testId="os-patch-remediation-unavailable"
        message="Remediation scripts aren't included in your organisation's ALICE plan."
      />
    );
  }

  return (
    <div className="p-8" data-testid="os-patch-remediation">
      <div className="flex items-start justify-between mb-6 gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold text-white mb-1">Remediation</h1>
          <p className="text-white/80">Scripts that unstick devices stuck on Windows Update.</p>
        </div>
        {canRemediate && (
        <div className="relative flex items-center gap-2">
          <Button variant="outline" onClick={() => setShowCleanup(true)}
                  data-testid="remediation-cleanup-btn"
                  title="Remove devices that have finished from each script's group, and delete the one-group-per-run groups left by older runs"
                  className="border-white/20 text-white hover:bg-white/10">
            <Trash2 className="w-4 h-4 mr-2" /> Clean up groups
          </Button>
          <Button onClick={() => setCreateMenuOpen(o => !o)}
                  data-testid="remediation-create-btn"
                  className="bg-[#E5EFFA] text-[#3C02CC] hover:bg-[#BBFF71] hover:text-[#3C02CC] font-semibold">
            <Plus className="w-4 h-4 mr-2" /> Create script <ChevronDown className="w-4 h-4 ml-1" />
          </Button>
          {createMenuOpen && (
            <div className="absolute right-0 mt-2 w-56 rounded-lg border border-white/10 bg-slate-900 shadow-xl z-10"
                 data-testid="remediation-create-menu">
              <button onClick={() => { setCreateMenuOpen(false); setTab('catalog'); }}
                      data-testid="remediation-create-from-catalog"
                      className="w-full text-left px-3 py-2 text-sm text-white hover:bg-white/5">
                From catalog (recommended)
              </button>
              <button onClick={() => { setCreateMenuOpen(false); setModalCustom(true); }}
                      data-testid="remediation-create-custom"
                      className="w-full text-left px-3 py-2 text-sm text-white hover:bg-white/5">
                Custom PowerShell…
              </button>
            </div>
          )}
        </div>
        )}
      </div>

      <OSPatchReadOnlyNotice className="mb-4" />

      {/* Whatever failed to load, named. The rest of the page still works. */}
      {loadErrors.length > 0 && (
        <Card className="bg-red-500/10 border border-red-500/30 mb-4" data-testid="remediation-load-errors">
          <CardContent className="p-4 flex items-start gap-3">
            <AlertTriangle className="w-4 h-4 text-red-300 mt-0.5 shrink-0" />
            <div className="text-sm">
              <p className="text-red-200 font-medium">
                {loadErrors.length === 1 ? 'One section' : `${loadErrors.length} sections`} could not be loaded
              </p>
              <ul className="mt-1 space-y-0.5 text-xs text-white/75">
                {loadErrors.map((e, i) => <li key={i}>{e}</li>)}
              </ul>
              <Button size="sm" variant="outline" onClick={loadAll}
                      className="mt-3 border-white/20 text-white h-7 text-xs"
                      data-testid="remediation-retry-load">
                Retry
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <div className="flex items-center gap-2 mb-5" data-testid="remediation-tabs">
        {[
          { k: 'scripts', label: `Scripts (${scripts.length})` },
          { k: 'catalog', label: `Catalog (${catalog.length})` },
          { k: 'runs',    label: `Runs (${runs.length})` },
        ].map(t => (
          <button key={t.k} onClick={() => setTab(t.k)}
                  data-testid={`remediation-tab-${t.k}`}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${tab === t.k
                    ? 'bg-[#BBFF71] text-[#3C02CC] border-[#BBFF71]'
                    : 'bg-white/5 text-white border-white/10 hover:bg-white/10'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {loading ? <LoadingSpinner message="Loading remediation scripts…" /> :
        tab === 'scripts' ? <ScriptsTab /> :
        tab === 'catalog' ? <CatalogTab /> :
        <RunsTab />}

      {/* Modals */}
      {modalCatalogKey && (
        <CatalogDetailModal entryKey={modalCatalogKey} onClose={() => setModalCatalogKey(null)}
                            onInstalled={loadAll} />
      )}
      {(modalCustom || editScript) && (
        <CustomScriptModal script={editScript} onClose={() => { setModalCustom(false); setEditScript(null); }}
                           onSaved={loadAll} />
      )}
      {runScript && (
        <RunScriptModal preselectedScript={runScript} devices={[]} onClose={() => setRunScript(null)}
                        source="remediation_page" onViewRun={setRunDetailId}
                        onRunCreated={() => { setTab('runs'); loadAll(); }} />
      )}
      {showCleanup && (
        <RemediationCleanupModal onClose={() => setShowCleanup(false)} onApplied={loadAll} />
      )}
      {runDetailId && (
        <RunDetailModal runId={runDetailId} onClose={() => setRunDetailId(null)} />
      )}
    </div>
  );
};

// Exported so other sections (e.g. Stuck Devices) can launch the run flow.
export { RunScriptModal as RemediationRunModal };

// Feature Update Create Modal
const FeatureUpdateCreateModal = ({ catalog, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    target_version: '',
    installation_type: 'required',
    rollout_start: '',
    rollout_end: '',
    target_group_id: '',
  });
  const [saving, setSaving] = useState(false);
  const [groups, setGroups] = useState([]);

  useEffect(() => {
    osPatchApi.getDeploymentGroups().then(res => {
      setGroups(Array.isArray(res.data) ? res.data : []);
    }).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(formData);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <Card className="alice-popup bg-slate-900 border-[rgba(229,239,250,0.1)] w-full max-w-lg mx-4">
        <CardHeader className="border-b border-[rgba(229,239,250,0.1)]">
          <div className="flex items-center justify-between">
            <CardTitle className="text-white flex items-center gap-2">
              <Layers className="w-5 h-5 text-purple-400" />
              Create Custom Feature Update Profile
            </CardTitle>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="w-4 h-4 text-white/90" />
            </Button>
          </div>
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent className="p-6 space-y-4">
            <div>
              <label className="block text-sm text-white/90 mb-1">Profile Name *</label>
              <Input 
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] border-[rgba(229,239,250,0.1)] text-white"
                placeholder="e.g., Windows 11 24H2 Rollout"
                required
              />
            </div>
            <div>
              <label className="block text-sm text-white/90 mb-1">Description</label>
              <Input 
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className="bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] border-[rgba(229,239,250,0.1)] text-white"
                placeholder="Optional description"
              />
            </div>
            <div>
              <label className="block text-sm text-white/90 mb-1">Target Version *</label>
              <select
                value={formData.target_version}
                onChange={(e) => setFormData({...formData, target_version: e.target.value})}
                className="w-full bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] border border-[rgba(229,239,250,0.1)] text-white rounded-md px-3 py-2"
                required
              >
                <option value="" className="bg-slate-900">Select a version...</option>
                {catalog.filter(c => c.isInService).map(item => (
                  <option key={item.id} value={item.displayName} className="bg-slate-900">
                    {item.displayName}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm text-white/90 mb-1">Target Deployment Group</label>
              <select
                value={formData.target_group_id}
                onChange={(e) => setFormData({...formData, target_group_id: e.target.value})}
                className="w-full bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] border border-[rgba(229,239,250,0.1)] text-white rounded-md px-3 py-2"
                data-testid="feature-profile-group-select"
              >
                <option value="" className="bg-slate-900">No group (assign manually later)</option>
                {groups.map(g => (
                  <option key={g.id} value={g.id} className="bg-slate-900">
                    {g.name} ({g.device_count || 0} devices)
                  </option>
                ))}
              </select>
              <p className="text-xs text-white/50 mt-1">Select a custom group from the Deployments page</p>
            </div>
            <div>
              <label className="block text-sm text-white/90 mb-1">Installation Type *</label>
              <div className="flex gap-3">
                <label className={`flex-1 flex items-center gap-2 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                  formData.installation_type === 'required' 
                    ? 'border-purple-500 bg-purple-500/10' 
                    : 'border-[rgba(229,239,250,0.1)] hover:border-white/30 bg-[rgba(229,239,250,0.05)]'
                }`}>
                  <input type="radio" name="modal_install_type" value="required"
                    checked={formData.installation_type === 'required'}
                    onChange={() => setFormData({...formData, installation_type: 'required'})}
                    className="sr-only" />
                  <Shield className="w-4 h-4 text-purple-400" />
                  <div>
                    <div className="text-white text-sm font-medium">Required</div>
                    <div className="text-white/50 text-xs">Auto-installs on devices</div>
                  </div>
                </label>
                <label className={`flex-1 flex items-center gap-2 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                  formData.installation_type === 'optional'
                    ? 'border-cyan-500 bg-cyan-500/10'
                    : 'border-[rgba(229,239,250,0.1)] hover:border-white/30 bg-[rgba(229,239,250,0.05)]'
                }`}>
                  <input type="radio" name="modal_install_type" value="optional"
                    checked={formData.installation_type === 'optional'}
                    onChange={() => setFormData({...formData, installation_type: 'optional'})}
                    className="sr-only" />
                  <Info className="w-4 h-4 text-cyan-400" />
                  <div>
                    <div className="text-white text-sm font-medium">Optional</div>
                    <div className="text-white/50 text-xs">User chooses to install</div>
                  </div>
                </label>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-white/90 mb-1">Rollout Start</label>
                <Input 
                  type="datetime-local"
                  value={formData.rollout_start}
                  onChange={(e) => setFormData({...formData, rollout_start: e.target.value})}
                  className="bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] border-[rgba(229,239,250,0.1)] text-white"
                />
              </div>
              <div>
                <label className="block text-sm text-white/90 mb-1">Rollout End</label>
                <Input 
                  type="datetime-local"
                  value={formData.rollout_end}
                  onChange={(e) => setFormData({...formData, rollout_end: e.target.value})}
                  className="bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] border-[rgba(229,239,250,0.1)] text-white"
                />
              </div>
            </div>
          </CardContent>
          <div className="border-t border-[rgba(229,239,250,0.1)] p-4 flex justify-end gap-3">
            <Button type="button" variant="outline" className="border-[rgba(229,239,250,0.1)] text-white" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" className="bg-purple-500 hover:bg-purple-600" disabled={saving}>
              {saving ? 'Creating...' : 'Create Profile'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

// Feature Update Edit Modal
const FeatureUpdateEditModal = ({ profile, catalog, onSave, onClose }) => {
  const formatDateForInput = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toISOString().slice(0, 16);
    } catch {
      return '';
    }
  };

  const [formData, setFormData] = useState({
    name: profile.name || '',
    description: profile.description || '',
    target_version: profile.target_version || '',
    rollout_start: formatDateForInput(profile.rollout_start),
    rollout_end: formatDateForInput(profile.rollout_end),
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(formData);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <Card className="alice-popup bg-slate-900 border-[rgba(229,239,250,0.1)] w-full max-w-lg mx-4">
        <CardHeader className="border-b border-[rgba(229,239,250,0.1)]">
          <div className="flex items-center justify-between">
            <CardTitle className="text-white flex items-center gap-2">
              <Edit className="w-5 h-5 text-purple-400" />
              Edit Feature Update Profile
            </CardTitle>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="w-4 h-4 text-white/90" />
            </Button>
          </div>
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent className="p-6 space-y-4">
            <div>
              <label className="block text-sm text-white/90 mb-1">Profile Name *</label>
              <Input 
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] border-[rgba(229,239,250,0.1)] text-white"
                placeholder="e.g., Windows 11 24H2 Rollout"
                required
              />
            </div>
            <div>
              <label className="block text-sm text-white/90 mb-1">Description</label>
              <Input 
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className="bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] border-[rgba(229,239,250,0.1)] text-white"
                placeholder="Optional description"
              />
            </div>
            <div>
              <label className="block text-sm text-white/90 mb-1">Target Version *</label>
              <select
                value={formData.target_version}
                onChange={(e) => setFormData({...formData, target_version: e.target.value})}
                className="w-full bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] border border-[rgba(229,239,250,0.1)] text-white rounded-md px-3 py-2"
                required
              >
                <option value="" className="bg-slate-900">Select a version...</option>
                {catalog.filter(c => c.isInService).map(item => (
                  <option key={item.id} value={item.displayName} className="bg-slate-900">
                    {item.displayName}
                  </option>
                ))}
                {/* Include current version if not in catalog */}
                {formData.target_version && !catalog.find(c => c.displayName === formData.target_version) && (
                  <option value={formData.target_version} className="bg-slate-900">
                    {formData.target_version}
                  </option>
                )}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-white/90 mb-1">Rollout Start</label>
                <Input 
                  type="datetime-local"
                  value={formData.rollout_start}
                  onChange={(e) => setFormData({...formData, rollout_start: e.target.value})}
                  className="bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] border-[rgba(229,239,250,0.1)] text-white"
                />
              </div>
              <div>
                <label className="block text-sm text-white/90 mb-1">Rollout End</label>
                <Input 
                  type="datetime-local"
                  value={formData.rollout_end}
                  onChange={(e) => setFormData({...formData, rollout_end: e.target.value})}
                  className="bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] border-[rgba(229,239,250,0.1)] text-white"
                />
              </div>
            </div>
          </CardContent>
          <div className="border-t border-[rgba(229,239,250,0.1)] p-4 flex justify-end gap-3">
            <Button type="button" variant="outline" className="border-[rgba(229,239,250,0.1)] text-white" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" className="bg-purple-500 hover:bg-purple-600" disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

// Expedited Update Create Modal
const ExpeditedUpdateCreateModal = ({ availableReleases, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    quality_update_release: '',
    days_until_forced_reboot: 2,
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(formData);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <Card className="alice-popup bg-slate-900 border-[rgba(229,239,250,0.1)] w-full max-w-lg mx-4">
        <CardHeader className="border-b border-[rgba(229,239,250,0.1)]">
          <div className="flex items-center justify-between">
            <CardTitle className="text-white flex items-center gap-2">
              <Zap className="w-5 h-5 text-[#FC5442]" />
              Deploy Expedited Update
            </CardTitle>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="w-4 h-4 text-white/90" />
            </Button>
          </div>
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent className="p-6 space-y-4">
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-sm text-amber-300">
              <AlertTriangle className="w-4 h-4 inline mr-2" />
              Expedited updates bypass deferral settings and install immediately. Use only for critical patches.
            </div>
            <div>
              <label className="block text-sm text-white/90 mb-1">Policy Name *</label>
              <Input 
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] border-[rgba(229,239,250,0.1)] text-white"
                placeholder="e.g., Critical Security Patch - Feb 2026"
                required
              />
            </div>
            <div>
              <label className="block text-sm text-white/90 mb-1">Description</label>
              <Input 
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className="bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] border-[rgba(229,239,250,0.1)] text-white"
                placeholder="Optional description"
              />
            </div>
            <div>
              <label className="block text-sm text-white/90 mb-1">Quality Update Release *</label>
              <select
                value={formData.quality_update_release}
                onChange={(e) => setFormData({...formData, quality_update_release: e.target.value})}
                className="w-full bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] border border-[rgba(229,239,250,0.1)] text-white rounded-md px-3 py-2"
                required
              >
                <option value="" className="bg-slate-900">Select an update...</option>
                {availableReleases.map(release => (
                  <option key={release.id} value={release.displayName} className="bg-slate-900">
                    {release.displayName} ({release.kbNumber})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm text-white/90 mb-1">Days Until Forced Reboot</label>
              <Input 
                type="number"
                min="0"
                max="7"
                value={formData.days_until_forced_reboot}
                onChange={(e) => setFormData({...formData, days_until_forced_reboot: parseInt(e.target.value) || 2})}
                className="bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] border-[rgba(229,239,250,0.1)] text-white w-24"
              />
              <p className="text-xs text-white/70 mt-1">Days users have before automatic reboot (0-7)</p>
            </div>
          </CardContent>
          <div className="border-t border-[rgba(229,239,250,0.1)] p-4 flex justify-end gap-3">
            <Button type="button" variant="outline" className="border-[rgba(229,239,250,0.1)] text-white" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" className="bg-[#FC5442] hover:bg-[#e84a3a]" disabled={saving}>
              {saving ? 'Deploying...' : 'Deploy Update'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

// ==================== FEATURE UPDATE DEPLOYMENT WIZARD ====================

// ==================== PHASE DEVICE TABLE (Preview Report) ====================

const PhaseDeviceTable = ({ phaseKey, phaseName, phaseColor, phaseIcon, devices, percentage, searchQuery, expanded, onToggle, onExclude, loading, showAppRisk = false }) => {
  const filteredDevices = useMemo(() => {
    if (!searchQuery) return devices;
    const q = searchQuery.toLowerCase();
    return devices.filter(d =>
      (d.device_name || '').toLowerCase().includes(q) ||
      (d.user_name || '').toLowerCase().includes(q)
    );
  }, [devices, searchQuery]);

  const borderColor = phaseColor === 'cyan' ? 'border-cyan-500/30' : 'border-purple-500/30';
  const bgColor = phaseColor === 'cyan' ? 'bg-cyan-500/5' : 'bg-purple-500/5';
  const textColor = phaseColor === 'cyan' ? 'text-cyan-400' : 'text-purple-400';
  const badgeBg = phaseColor === 'cyan' ? 'bg-cyan-500/20' : 'bg-purple-500/20';

  const riskBadge = (risk) => {
    const colors = {
      green: 'bg-green-500/20 text-green-400', low: 'bg-green-500/20 text-green-400',
      amber: 'bg-amber-500/20 text-amber-400', medium: 'bg-amber-500/20 text-amber-400',
      red: 'bg-red-500/20 text-red-400', high: 'bg-red-500/20 text-red-400',
    };
    return colors[risk] || 'bg-white/10 text-white/60';
  };

  // `scott/feature-updates-3` — Reason column shows ONLY the readiness
  // reason + a "< FU_MIN_DISK_GB disk" flag. App-risk context is intentionally
  // dropped at the UI layer (it remains in the upstream payload).
  const reasonOf = (d) => {
    const parts = [];
    if (d.readiness_reason) parts.push(String(d.readiness_reason));
    const disk = d.free_disk_space_gb;
    if (disk != null && Number(disk) < FU_MIN_DISK_GB) {
      parts.push(`Less than ${FU_MIN_DISK_GB} GB free disk (${Number(disk).toFixed(1)} GB)`);
    }
    return parts;
  };

  return (
    <div className={`border ${borderColor} rounded-lg overflow-hidden`} data-testid={`phase-${phaseKey}`}>
      {/* Phase Header - clickable */}
      <button
        onClick={onToggle}
        className={`w-full flex items-center justify-between px-4 py-3 ${bgColor} hover:brightness-110 transition-all`}
        data-testid={`phase-toggle-${phaseKey}`}
      >
        <div className="flex items-center gap-3">
          <span className={textColor}>{phaseIcon}</span>
          <span className={`font-medium ${textColor}`}>{phaseName}</span>
          <span className={`px-2 py-0.5 ${badgeBg} ${textColor} text-xs rounded-full`}>
            {devices.length} device{devices.length !== 1 ? 's' : ''}
          </span>
          {percentage !== undefined && (
            <span className="text-white/40 text-xs">{percentage}%</span>
          )}
          {searchQuery && filteredDevices.length !== devices.length && (
            <span className="text-white/40 text-xs">({filteredDevices.length} matching)</span>
          )}
        </div>
        {expanded ? <ChevronDown className="w-4 h-4 text-white/50" /> : <ChevronRight className="w-4 h-4 text-white/50" />}
      </button>

      {/* Device Table - collapsible */}
      {expanded && (
        <div className="border-t border-white/5">
          {filteredDevices.length === 0 ? (
            <div className="px-4 py-6 text-center text-white/40 text-sm">
              {searchQuery ? 'No devices match your search' : 'No devices in this sprint'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/5">
                    <th className="text-left px-4 py-2 text-white/50 font-normal text-xs">Device Name</th>
                    <th className="text-left px-4 py-2 text-white/50 font-normal text-xs">User</th>
                    <th className="text-left px-4 py-2 text-white/50 font-normal text-xs">OS Version</th>
                    {/* 09/09 — app criticality gates an App Risk deployment
                        only. A Staged plan ignores it end to end, so showing it
                        here read as if it shaped the sprints. */}
                    {showAppRisk && <th className="text-left px-4 py-2 text-white/50 font-normal text-xs">App Risk</th>}
                    <th className="text-left px-4 py-2 text-white/50 font-normal text-xs">Readiness Risk</th>
                    <th className="text-right px-4 py-2 text-white/50 font-normal text-xs">Free disk</th>
                    <th className="text-left px-4 py-2 text-white/50 font-normal text-xs">Reason</th>
                    <th className="text-right px-4 py-2 text-white/50 font-normal text-xs">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDevices.map((device) => {
                    const reasons = reasonOf(device);
                    const disk = device.free_disk_space_gb;
                    return (
                      <tr key={device.device_id} className="border-b border-white/[0.03] hover:bg-white/[0.02] transition-colors" data-testid={`preview-device-row-${device.device_id}`}>
                        <td className="px-4 py-2.5">
                          <div className="flex items-center gap-2">
                            <Monitor className="w-3.5 h-3.5 text-white/30" />
                            <span className="text-white">{device.device_name}</span>
                            {/* 09/09 — a VM that made it into a sprint should
                                be visible here, where the plan is signed off,
                                not only in the planner. */}
                            {device.virtualisation && device.virtualisation !== 'physical' && (
                              <span
                                className="px-1.5 py-0.5 rounded text-[10px] border bg-white/10 text-white/60 border-white/20"
                                title={device.model || ''}
                              >
                                {device.virtualisation_label || 'Virtual machine'}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-2.5 text-white/60">{device.user_name || '-'}</td>
                        <td className="px-4 py-2.5 text-white/60 text-xs">{device.current_os_version || '-'}</td>
                        {showAppRisk && (
                          <td className="px-4 py-2.5">
                            <span className={`px-1.5 py-0.5 rounded text-[10px] ${riskBadge(device.app_risk_category)}`}>
                              {(device.app_risk_category || 'low').toUpperCase()}
                            </span>
                          </td>
                        )}
                        {/* 09/09 — "Unknown" here means Intune has not
                            assessed the device, not that ALICE lost the value. */}
                        <td className="px-4 py-2.5 text-white/60 text-xs">
                          {!device.readiness_status || device.readiness_status === 'Unknown'
                            ? <span className="text-white/40">Not assessed</span>
                            : device.readiness_status}
                        </td>
                        <td className="px-4 py-2.5 text-right text-xs">
                          {disk == null ? (
                            <span className="text-white/40">—</span>
                          ) : (
                            <span className={disk < FU_MIN_DISK_GB ? 'text-red-300 font-semibold' : 'text-white/70'}>
                              {Number(disk).toFixed(1)} GB
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-xs">
                          {reasons.length === 0 ? (
                            <span className="text-white/30">—</span>
                          ) : (
                            <div className="space-y-0.5" data-testid={`preview-device-reason-${device.device_id}`}>
                              {reasons.map((r, i) => (
                                <div
                                  key={i}
                                  className={r.startsWith(`Less than ${FU_MIN_DISK_GB} GB`) ? 'text-red-300' : 'text-amber-200'}
                                >
                                  {r}
                                </div>
                              ))}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onExclude(device.device_id, device.device_name)}
                            disabled={loading}
                            className="text-red-400/70 hover:text-red-400 hover:bg-red-500/10 h-6 px-2 text-xs"
                            data-testid={`exclude-device-${device.device_id}`}
                          >
                            <X className="w-3 h-3 mr-1" />
                            Exclude
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};


// ==================== CREATE GROUP MODAL ====================
const CreateGroupModal = ({ onClose, onCreated }) => {
  const [groupName, setGroupName] = useState('');
  const [description, setDescription] = useState('');
  const [mode, setMode] = useState('manual'); // manual, device_phase, app_risk
  const [saving, setSaving] = useState(false);
  const [devices, setDevices] = useState([]);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [selectedDeviceIds, setSelectedDeviceIds] = useState([]);
  const [deviceSearch, setDeviceSearch] = useState('');
  const [selectedPhase, setSelectedPhase] = useState('blue');
  const [selectedRiskLevel, setSelectedRiskLevel] = useState('green');

  useEffect(() => {
    loadDevices();
  }, []);

  const loadDevices = async () => {
    setLoadingDevices(true);
    try {
      const res = await osPatchApi.getDevices();
      setDevices(Array.isArray(res.data) ? res.data : []);
    } catch { /* empty */ } finally {
      setLoadingDevices(false);
    }
  };

  const windowsDevices = devices.filter(d => {
    const os = (d.os_name || d.operating_system || d.os || d.os_version || '').toLowerCase();
    return os.includes('windows') || (d.os_version || '').match(/^10\.0\./);
  });

  const filteredDevices = windowsDevices.filter(d => {
    const matchSearch = !deviceSearch || 
      d.device_name?.toLowerCase().includes(deviceSearch.toLowerCase()) ||
      d.user_name?.toLowerCase().includes(deviceSearch.toLowerCase());
    
    if (mode === 'device_phase') {
      return matchSearch && (d.feature_update_phase || 'green') === selectedPhase;
    }
    if (mode === 'app_risk') {
      const risk = (d.app_risk_category || 'low').toLowerCase();
      const riskMap = { green: ['green', 'low'], amber: ['amber', 'medium'], red: ['red', 'high'] };
      return matchSearch && (riskMap[selectedRiskLevel] || []).includes(risk);
    }
    return matchSearch;
  });

  const handleSelectAll = () => {
    const ids = filteredDevices.map(d => d.id);
    setSelectedDeviceIds(prev => {
      const all = new Set(prev);
      ids.forEach(id => all.add(id));
      return [...all];
    });
  };

  const handleDeselectAll = () => {
    const ids = new Set(filteredDevices.map(d => d.id));
    setSelectedDeviceIds(prev => prev.filter(id => !ids.has(id)));
  };

  const toggleDevice = (id) => {
    setSelectedDeviceIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleCreate = async () => {
    if (!groupName.trim()) { toast.error('Group name is required'); return; }
    
    let deviceIds = selectedDeviceIds;
    if (mode === 'device_phase') {
      deviceIds = filteredDevices.map(d => d.id);
    } else if (mode === 'app_risk') {
      deviceIds = filteredDevices.map(d => d.id);
    }

    setSaving(true);
    try {
      const res = await osPatchApi.createDeploymentGroup({ name: groupName, description });
      const groupId = res.data?.id || res.data?._id;
      
      if (groupId && deviceIds.length > 0) {
        try {
          await axios.post(`${API}/deployment-groups/${groupId}/devices`, { device_ids: deviceIds });
          toast.success(`Group created with ${deviceIds.length} device(s)`);
        } catch {
          toast.success('Group created (device assignment requires Azure AD credentials)');
        }
      } else {
        toast.success('Group created');
      }
      onCreated();
    } catch (err) {
      toast.error('Failed to create group');
    } finally {
      setSaving(false);
    }
  };

  const modes = [
    { id: 'manual', label: 'Manual Selection', desc: 'Search and select specific devices', icon: Search },
    { id: 'device_phase', label: 'Device Phase', desc: 'Use Deployment Sprint categorisation', icon: Target },
    { id: 'app_risk', label: 'App Risk Category', desc: 'Use application risk levels', icon: Shield },
  ];

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 overflow-y-auto py-4">
      <Card className="alice-popup bg-slate-900 border-[rgba(229,239,250,0.1)] w-full max-w-2xl mx-4 max-h-[85vh] flex flex-col">
        <CardHeader className="border-b border-[rgba(229,239,250,0.1)]">
          <div className="flex items-center justify-between">
            <CardTitle className="text-white flex items-center gap-2">
              <Users className="w-5 h-5 text-cyan-400" />
              Create Deployment Group
            </CardTitle>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="w-4 h-4 text-white/90" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-6 space-y-5 overflow-y-auto flex-1">
          {/* Name & Description */}
          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="block text-sm text-white/90 mb-1">Group Name *</label>
              <Input value={groupName} onChange={e => setGroupName(e.target.value)}
                className="bg-[rgba(229,239,250,0.1)] border-[rgba(229,239,250,0.1)] text-white"
                placeholder="e.g., Pilot Devices - Blue Phase" data-testid="group-name-input" />
            </div>
            <div>
              <label className="block text-sm text-white/90 mb-1">Description</label>
              <Input value={description} onChange={e => setDescription(e.target.value)}
                className="bg-[rgba(229,239,250,0.1)] border-[rgba(229,239,250,0.1)] text-white"
                placeholder="Optional description" />
            </div>
          </div>

          {/* Mode Selection */}
          <div>
            <label className="block text-sm text-white/90 mb-2">Device Selection Method</label>
            <div className="grid grid-cols-3 gap-3">
              {modes.map(m => (
                <button key={m.id} onClick={() => { setMode(m.id); setSelectedDeviceIds([]); }}
                  className={`p-3 rounded-lg border-2 text-left transition-all ${
                    mode === m.id ? 'border-cyan-500 bg-cyan-500/10' : 'border-[rgba(229,239,250,0.1)] hover:border-white/30 bg-[rgba(229,239,250,0.05)]'
                  }`} data-testid={`mode-${m.id}`}>
                  <m.icon className={`w-4 h-4 mb-1 ${mode === m.id ? 'text-cyan-400' : 'text-white/50'}`} />
                  <div className="text-white text-sm font-medium">{m.label}</div>
                  <div className="text-white/50 text-xs">{m.desc}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Phase / Risk Filter */}
          {mode === 'device_phase' && (
            <div>
              <label className="block text-sm text-white/90 mb-2">Select Deployment Sprint</label>
              <div className="flex gap-3">
                {Object.entries(PHASE_CONFIG).filter(([k]) => k !== 'red').map(([phase, cfg]) => (
                  <button key={phase} onClick={() => setSelectedPhase(phase)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all ${
                      selectedPhase === phase ? `border-current ${cfg.textColor} bg-current/10` : 'border-[rgba(229,239,250,0.1)] text-white/50'
                    }`} data-testid={`phase-filter-${phase}`}>
                    <div className={`w-3 h-3 rounded-full ${cfg.color}`} />
                    <span className="text-sm">{cfg.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          {mode === 'app_risk' && (
            <div>
              <label className="block text-sm text-white/90 mb-2">Select Risk Level</label>
              <div className="flex gap-3">
                {[
                  { id: 'green', label: 'Low', color: 'bg-green-500', text: 'text-green-400' },
                  { id: 'amber', label: 'Medium', color: 'bg-amber-500', text: 'text-amber-400' },
                  { id: 'red', label: 'High', color: 'bg-red-500', text: 'text-red-400' },
                ].map(r => (
                  <button key={r.id} onClick={() => setSelectedRiskLevel(r.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all ${
                      selectedRiskLevel === r.id ? `border-current ${r.text} bg-current/10` : 'border-[rgba(229,239,250,0.1)] text-white/50'
                    }`} data-testid={`risk-filter-${r.id}`}>
                    <div className={`w-3 h-3 rounded-full ${r.color}`} />
                    <span className="text-sm">{r.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Device List */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm text-white/90">
                {mode === 'manual' ? 'Select Devices' : 'Matching Devices'}
                <span className="ml-2 text-white/50">({mode === 'manual' ? selectedDeviceIds.length : filteredDevices.length} selected)</span>
              </label>
              {mode === 'manual' && (
                <div className="flex gap-2">
                  <button onClick={handleSelectAll} className="text-xs text-cyan-400 hover:underline">Select all</button>
                  <button onClick={handleDeselectAll} className="text-xs text-white/50 hover:underline">Deselect all</button>
                </div>
              )}
            </div>
            {mode === 'manual' && (
              <div className="relative mb-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/50" />
                <Input value={deviceSearch} onChange={e => setDeviceSearch(e.target.value)}
                  className="pl-10 bg-[rgba(229,239,250,0.1)] border-[rgba(229,239,250,0.1)] text-white text-sm"
                  placeholder="Search devices..." data-testid="group-device-search" />
              </div>
            )}
            <div className="border border-[rgba(229,239,250,0.1)] rounded-lg max-h-48 overflow-y-auto">
              {loadingDevices ? (
                <div className="p-4 text-center text-white/50 text-sm">Loading devices...</div>
              ) : filteredDevices.length === 0 ? (
                <div className="p-4 text-center text-white/50 text-sm">
                  {windowsDevices.length === 0 ? 'No devices synced. Sync from Intune first.' : 'No matching devices'}
                </div>
              ) : (
                filteredDevices.map(d => (
                  <label key={d.id} className="flex items-center gap-3 px-3 py-2 hover:bg-white/5 cursor-pointer border-b border-white/5 last:border-0">
                    {mode === 'manual' && (
                      <input type="checkbox" checked={selectedDeviceIds.includes(d.id)} onChange={() => toggleDevice(d.id)}
                        className="w-3.5 h-3.5 rounded border-white/30 bg-transparent text-cyan-500" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-white text-sm truncate">{d.device_name}</div>
                      <div className="text-white/50 text-xs">{d.user_name || 'N/A'} · {d.os_version || ''}</div>
                    </div>
                    <div className={`w-3 h-3 rounded-full ${PHASE_CONFIG[d.feature_update_phase || 'green']?.color || 'bg-green-500'}`} 
                      title={PHASE_CONFIG[d.feature_update_phase || 'green']?.label} />
                  </label>
                ))
              )}
            </div>
          </div>
        </CardContent>
        <div className="border-t border-[rgba(229,239,250,0.1)] p-4 flex justify-end gap-3">
          <Button variant="outline" className="border-[rgba(229,239,250,0.1)] text-white" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleCreate} disabled={saving || !groupName.trim()} 
            className="bg-[#E5EFFA] text-[#3C02CC] hover:bg-[#BBFF71] font-semibold" data-testid="create-group-submit">
            {saving ? 'Creating...' : 'Create Group'}
          </Button>
        </div>
      </Card>
      {saving && (
        <CreationProgressOverlay 
          message="Creating Deployment Group" 
          detail="Setting up Azure AD group and assigning devices..." 
        />
      )}
    </div>
  );
};

// ==================== CREATE RING MODAL ====================
const CreateRingModal = ({ onClose, onCreated }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    quality_deferral: 0,
    feature_deferral: 0,
    quality_deadline: 0,
    feature_deadline: 0,
    automatic_update_mode: 'autoInstallAtMaintenanceTime',
    allow_pause: false,
    check_for_updates: true,
    target_group_id: null,
  });
  const [saving, setSaving] = useState(false);
  const [groups, setGroups] = useState([]);

  useEffect(() => {
    osPatchApi.getDeploymentGroups().then(res => {
      setGroups(Array.isArray(res.data) ? res.data : []);
    }).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await osPatchApi.createUpdateRing(formData);
      toast.success('Update ring created');
      onCreated();
    } catch {
      toast.error('Failed to create update ring');
    } finally {
      setSaving(false);
    }
  };

  const updateModes = [
    { value: 'autoInstallAtMaintenanceTime', label: 'Auto Install (Maintenance Time)' },
    { value: 'autoInstallAndRebootAtMaintenanceTime', label: 'Auto Install & Reboot (Maintenance)' },
    { value: 'autoInstallAndRebootAtScheduledTime', label: 'Auto Install & Reboot (Scheduled)' },
    { value: 'notifyDownload', label: 'Notify Download' },
    { value: 'windowsDefault', label: 'Windows Default' },
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <Card className="alice-popup bg-slate-900 border-[rgba(229,239,250,0.1)] w-full max-w-lg mx-4">
        <CardHeader className="border-b border-[rgba(229,239,250,0.1)]">
          <div className="flex items-center justify-between">
            <CardTitle className="text-white flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-cyan-400" />
              Create Custom Update Ring
            </CardTitle>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="w-4 h-4 text-white/90" />
            </Button>
          </div>
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent className="p-6 space-y-4 max-h-[65vh] overflow-y-auto">
            <div>
              <label className="block text-sm text-white/90 mb-1">Ring Name *</label>
              <Input value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })}
                className="bg-[rgba(229,239,250,0.1)] border-[rgba(229,239,250,0.1)] text-white"
                placeholder="e.g., Pilot Ring - Fast Updates" required data-testid="ring-name-input" />
            </div>
            <div>
              <label className="block text-sm text-white/90 mb-1">Description</label>
              <Input value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })}
                className="bg-[rgba(229,239,250,0.1)] border-[rgba(229,239,250,0.1)] text-white"
                placeholder="Optional description" />
            </div>
            <div>
              <label className="block text-sm text-white/90 mb-1">Target Deployment Group</label>
              <select value={formData.target_group_id || ''}
                onChange={e => setFormData({ ...formData, target_group_id: e.target.value || null })}
                className="w-full bg-[rgba(229,239,250,0.1)] border border-[rgba(229,239,250,0.1)] text-white rounded-md px-3 py-2"
                data-testid="ring-group-select">
                <option value="" className="bg-slate-900">No group (assign manually later)</option>
                {groups.map(g => (
                  <option key={g.id} value={g.id} className="bg-slate-900">{g.name} ({g.device_count || 0} devices)</option>
                ))}
              </select>
              <p className="text-xs text-white/50 mt-1">Select a deployment group from the Deployments page</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-white/90 mb-1">Quality Deferral (days)</label>
                <Input type="number" min="0" max="30" value={formData.quality_deferral}
                  onChange={e => setFormData({ ...formData, quality_deferral: parseInt(e.target.value) || 0 })}
                  className="bg-[rgba(229,239,250,0.1)] border-[rgba(229,239,250,0.1)] text-white" />
              </div>
              <div>
                <label className="block text-sm text-white/90 mb-1">Feature Deferral (days)</label>
                <Input type="number" min="0" max="365" value={formData.feature_deferral}
                  onChange={e => setFormData({ ...formData, feature_deferral: parseInt(e.target.value) || 0 })}
                  className="bg-[rgba(229,239,250,0.1)] border-[rgba(229,239,250,0.1)] text-white" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-white/90 mb-1">Quality Deadline (days)</label>
                <Input type="number" min="0" max="30" value={formData.quality_deadline}
                  onChange={e => setFormData({ ...formData, quality_deadline: parseInt(e.target.value) || 0 })}
                  className="bg-[rgba(229,239,250,0.1)] border-[rgba(229,239,250,0.1)] text-white" />
                <p className="text-xs text-white/40 mt-0.5">Days before forced install after deferral</p>
              </div>
              <div>
                <label className="block text-sm text-white/90 mb-1">Feature Deadline (days)</label>
                <Input type="number" min="0" max="30" value={formData.feature_deadline}
                  onChange={e => setFormData({ ...formData, feature_deadline: parseInt(e.target.value) || 0 })}
                  className="bg-[rgba(229,239,250,0.1)] border-[rgba(229,239,250,0.1)] text-white" />
                <p className="text-xs text-white/40 mt-0.5">Days before forced install after deferral</p>
              </div>
            </div>
            <div>
              <label className="block text-sm text-white/90 mb-1">Update Mode</label>
              <select value={formData.automatic_update_mode}
                onChange={e => setFormData({ ...formData, automatic_update_mode: e.target.value })}
                className="w-full bg-[rgba(229,239,250,0.1)] border border-[rgba(229,239,250,0.1)] text-white rounded-md px-3 py-2">
                {updateModes.map(m => (
                  <option key={m.value} value={m.value} className="bg-slate-900">{m.label}</option>
                ))}
              </select>
            </div>
            <div className="space-y-3 pt-2 border-t border-[rgba(229,239,250,0.1)]">
              <label className="flex items-center justify-between cursor-pointer">
                <span className="text-white text-sm">Allow Pause Windows Updates</span>
                <button type="button" onClick={() => setFormData({ ...formData, allow_pause: !formData.allow_pause })}
                  className={`w-10 h-5 rounded-full transition-colors ${formData.allow_pause ? 'bg-cyan-500' : 'bg-white/20'}`}>
                  <div className={`w-4 h-4 rounded-full bg-white transition-transform mx-0.5 ${formData.allow_pause ? 'translate-x-5' : 'translate-x-0'}`} />
                </button>
              </label>
              <label className="flex items-center justify-between cursor-pointer">
                <span className="text-white text-sm">Check for Windows Updates</span>
                <button type="button" onClick={() => setFormData({ ...formData, check_for_updates: !formData.check_for_updates })}
                  className={`w-10 h-5 rounded-full transition-colors ${formData.check_for_updates ? 'bg-cyan-500' : 'bg-white/20'}`}>
                  <div className={`w-4 h-4 rounded-full bg-white transition-transform mx-0.5 ${formData.check_for_updates ? 'translate-x-5' : 'translate-x-0'}`} />
                </button>
              </label>
            </div>
          </CardContent>
          <div className="border-t border-[rgba(229,239,250,0.1)] p-4 flex justify-end gap-3">
            <Button type="button" variant="outline" className="border-[rgba(229,239,250,0.1)] text-white" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving || !formData.name} 
              className="bg-[#E5EFFA] text-[#3C02CC] hover:bg-[#BBFF71] font-semibold" data-testid="create-ring-submit">
              {saving ? 'Creating...' : 'Create Ring'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

// 09/09 — live preview of the sprint activation announcement.
//
// Rendered by the backend, not here, and deliberately so: the send wraps the
// body in the tenant's branded email shell (banner, colours, company name,
// support links from Settings → Branding), and a preview built in the frontend
// would be a second implementation of that shell, free to drift from the one
// that actually goes out. The endpoint runs the real renderer over sample data.
//
// Shown in a sandboxed iframe with srcDoc — the payload is a full HTML document
// with its own styles, and dropping that into the page would leak its CSS into
// the wizard.
const AnnouncementPreview = ({
  subject, bodyHtml, targetVersion, deploymentName, installationType, autoRequiredAfterDays,
  phaseLabel, kind = 'announcement',
}) => {
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(true);

  useEffect(() => {
    let cancelled = false;
    // Debounced: the body is a textarea, and re-rendering on every keystroke
    // would be a request per character.
    const t = setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await osPatchApi.previewFeatureAnnouncement({
          kind,
          subject: subject || '',
          body_html: bodyHtml || '',
          target_version: targetVersion || '',
          deployment_name: deploymentName || '',
          phase_label: phaseLabel || '',
          installation_type: installationType || 'required',
          auto_required_after_days: autoRequiredAfterDays || 0,
        });
        if (!cancelled) setPreview(res.data);
      } catch (e) {
        if (!cancelled) {
          setPreview(null);
          setError(e?.response?.data?.detail || 'Could not render the preview');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 500);
    return () => { cancelled = true; clearTimeout(t); };
  }, [kind, subject, bodyHtml, targetVersion, deploymentName, phaseLabel,
      installationType, autoRequiredAfterDays]);

  return (
    <div className="rounded-lg border border-white/10 bg-black/20" data-testid="wizard-announce-preview">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-3 py-2 text-xs text-white/70 hover:text-white"
      >
        <span className="flex items-center gap-2">
          <Mail className="w-3.5 h-3.5" />
          Preview — what the user receives
          {preview?.branded === false && (
            <span className="text-amber-300">(unbranded — branding could not be loaded)</span>
          )}
        </span>
        {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
      </button>
      {open && (
        <div className="px-3 pb-3">
          {error ? (
            <div className="rounded border border-amber-400/40 bg-amber-500/10 p-2 text-xs text-amber-100"
              data-testid="wizard-announce-preview-error">
              {error}
            </div>
          ) : (
            <>
              <div className="text-[11px] text-white/50 mb-1">
                Subject: <span className="text-white/80">{preview?.subject || '…'}</span>
                {loading && <span className="ml-2 text-white/40">rendering…</span>}
              </div>
              <iframe
                title="Announcement preview"
                sandbox=""
                srcDoc={preview?.html || '<p style="font-family:sans-serif;color:#666">Loading…</p>'}
                className="w-full h-72 rounded bg-white border border-white/10"
                data-testid="wizard-announce-preview-frame"
              />
              <p className="text-[11px] text-white/40 mt-1.5">
                Sample device and user. The banner, colours and footer come from your tenant branding
                (Settings → Branding).
                {kind === 'announcement' && (preview?.deadline_shown
                  ? ' The deadline shown is calculated from the optional window above.'
                  : ' No deadline is shown because this sprint installs automatically.')}
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
};

// =============================================================================
// Feature Update deployment wizard — step model (10/09)
// -----------------------------------------------------------------------------
// Until today the wizard was four steps and the second one, "Configure", held
// every decision below it: 20 controls in roughly 1,000 lines of JSX inside a
// 90vh modal. Four unrelated questions — who is in scope, how big are the
// sprints, when does the next one start, what do users see and get told —
// stacked into one scroll.
//
// The bigger problem was ordering. The eligibility figures printed at the top
// of Configure were computed ONCE, on step 1, and never recomputed. Ticking
// "Include High Risk", dropping Cloud PCs or turning on the firmware gate
// re-planned the rollout without moving a single number on screen; the admin
// only found out when the preview came back with a different split. So the
// steps are ordered by cause: scope settles how many devices there are, and
// only then does sizing have something true to divide.
//
// Review is the existing preview report, not a new page. It already lists every
// device, its sprint, the resources to be created and the warnings — a summary
// page in front of it would be two review screens disagreeing about which one
// is authoritative.
// 10/09 round 2 — the default sprint ramp, mirroring
// `_fu_calculate_wave_distribution` on the backend: sprint 1 is a pilot at
// clamp(round(N x 5%), 2, 50), and the remainder is split as evenly as integers
// allow across sprints 2..S.
//
// It used to be inlined in the wizard's own preview only. Now that BOTH sizing
// modes are prefilled from it, it has to be one function: the whole point of
// the prefill is that switching modes shows you the same plan in different
// units, and two copies of a ramp is how those two units start disagreeing.
const fuDefaultSprintCounts = (total, numSprints) => {
  const N = Math.max(0, Math.floor(Number(total) || 0));
  const S = Math.max(0, Math.floor(Number(numSprints) || 0));
  if (S <= 0) return [];
  if (N <= 0) return Array.from({ length: S }, () => 0);
  // More sprints than devices: one each until they run out.
  if (S >= N) return Array.from({ length: S }, (_, i) => (i < N ? 1 : 0));
  if (S === 1) return [N];
  const pilotSize = Math.min(50, Math.max(2, Math.round(N * 0.05)));
  // Never let the pilot starve the sprints behind it.
  const pilot = Math.min(pilotSize, Math.max(1, N - (S - 1)));
  const rest = S - 1;
  const remaining = N - pilot;
  const q = Math.floor(remaining / rest);
  const r = remaining % rest;
  return [pilot, ...Array.from({ length: rest }, (_, i) => q + (i < r ? 1 : 0))];
};

// Counts -> percentages, to three decimal places.
//
// Three decimals is not fussiness. The backend turns a percentage back into a
// count with `int(N * pct / 100)`, so precision decides how many machines move:
// on 2,000 devices 48.75% is exactly 975 where 48.8% is 976, and on 100,000 the
// difference between 9.995% and 10% is 5 devices per sprint. Three decimals
// keeps the prefill honest up to a six-figure fleet, so editing ONE sprint does
// not quietly re-size the others.
//
// It does not make the round trip lossless — see sendCustomPercentages — which
// is why a prefilled ramp is never sent.
const fuCountsToPercentages = (counts, total) => {
  const N = Math.max(0, Math.floor(Number(total) || 0));
  return (counts || []).map(c => (N ? Math.round((Number(c) || 0) / N * 100000) / 1000 : 0));
};

// Percentages -> counts, mirroring the backend's percentage branch in
// `_calculate_sprint_waves` line for line.
//
// Every sprint but the last gets `max(1, int(N * pct / 100))` clamped to what
// is left; the last takes the remainder. The `max(1, ...)` keeps a sprint the
// admin sized to nothing from vanishing while devices remain, and the clamp
// stops it inventing devices once they do not — 2 devices across 5 sprints
// returned 1/1/1/1/0 before the clamp was added on both sides.
const fuCountsFromPercentages = (percentages, total, numSprints) => {
  const N = Math.max(0, Math.floor(Number(total) || 0));
  const S = Math.max(0, Math.floor(Number(numSprints) || 0));
  const out = [];
  let assigned = 0;
  for (let i = 0; i < S; i++) {
    const isLast = i === S - 1;
    const pct = Number((percentages || [])[i]) || 0;
    const remaining = Math.max(0, N - assigned);
    const n = isLast
      ? remaining
      : Math.min(Math.max(1, Math.trunc(N * pct / 100)), remaining);
    out.push(n);
    assigned += n;
  }
  return out;
};

const FU_WIZARD_STEPS = [
  { id: 1, label: 'Version' },
  { id: 2, label: 'Scope' },
  { id: 3, label: 'Sprint design' },
  { id: 4, label: 'Progression' },
  { id: 5, label: 'Install & comms' },
  { id: 6, label: 'Review & create' },
];
const FU_WIZARD_LAST_STEP = 6;
const FU_WIZARD_DONE_STEP = 7; // terminal "Deployment created" screen

// The config fields that change WHICH devices are eligible. Any edit to one of
// them invalidates the eligible count, the sprint split and the preview, so the
// wizard re-runs check-eligibility rather than keep showing a number it no
// longer believes. Everything else (sprint count, install type, comms) divides
// or schedules that population without changing it.
const fuScopeSignature = (c) => JSON.stringify({
  target_version: c.target_version || '',
  include_medium_risk: Boolean(c.include_medium_risk),
  include_high_risk: Boolean(c.include_high_risk),
  min_disk_space_gb: c.min_disk_space_gb,
  min_firmware_release_date: c.firmware_gate_enabled ? (c.min_firmware_release_date || '') : '',
  excluded_virtualisation_types: [...(c.excluded_virtualisation_types || [])].sort(),
});

// The 80% deployment, offered on step 1 so an admin who wants the standard
// rollout never sees steps 2-5 at all. Everything here is the value the wizard
// would have defaulted to anyway EXCEPT the device types, which are the one
// place the default (include everything) is not what most rollouts want.
//
// Azure VMs and AVD session hosts are out — they are rebuilt from an image
// rather than upgraded in place. Cloud PCs stay in: they are user desktops and
// need the update like any other endpoint. That is the advice the scope step
// has always given in prose; the recommended plan just follows it.
const FU_RECOMMENDED_CONFIG = {
  num_sprints: 3,
  sizing_mode: 'percentage',
  wave_device_counts: [],
  wave_percentages: [],
  test_group_size: 0,
  include_medium_risk: true,
  include_high_risk: false,
  // 10/09 round 2 — Optional with a deadline, matching the new wizard default.
  // This is the rollout the code has always described as standard: the sprint
  // goes live Optional, the users are emailed, and ALICE switches that sprint to
  // Required when the window closes. Optional with NO window would be worse than
  // the Required it replaced — nothing ever escalates, a sprint can sit at 40%
  // forever, and threshold progression then never fires at all.
  installation_type: 'optional',
  auto_required_after_days: 10,
  auto_required_anchor: 'comms',
  progression_mode: 'threshold',
  completion_threshold: 60,
  offer_lead_days: 1,
  offer_start_at: '',
  spread_models_across_sprints: true,
  firmware_gate_enabled: false,
  excluded_virtualisation_types: [...FU_DEFAULT_EXCLUDED_DEVICE_TYPES],
  // 10/09 round 2 — ON. This is the only setting in the recommended plan that
  // acts outside the tenant, so it is spelled out on the card before the admin
  // commits, and the preview report is still shown before anything is created.
  //
  // It is not decoration. The 10-day window above is anchored to the comms
  // date, and ALICE deliberately will not force-install before users have been
  // told — so Optional + a comms-anchored deadline + no comms is a deployment
  // that never escalates and never completes. That is a worse default than the
  // Required it replaced. Either the announcement is on or the deadline is a
  // fiction; a recommended plan should not ship the second one.
  announce_on_activation: true,
};

const FeatureUpdateDeploymentWizard = ({
  catalog, onClose, onDeploymentCreated, appRiskMode = false,
  // 03/09 — set when the wizard is opened from the Eligibility Planner, which
  // has already chosen a target version. See the auto-run effect below.
  initialTargetVersion = '',
  // 09/09 — and the device-type scoping it was planned with. 10/09 round 2 —
  // null (the default) means the wizard was NOT opened from the planner, so it
  // applies FU_DEFAULT_EXCLUDED_DEVICE_TYPES. An array, empty included, is a
  // decision the planner already made and is honoured verbatim.
  initialExcludedDeviceTypes = null,
}) => {
  // 10/09 — see FU_WIZARD_STEPS. 1 Version, 2 Scope, 3 Sprint design,
  // 4 Progression, 5 Install & comms, 6 Review & create, 7 Complete.
  const [step, setStep] = useState(1);
  // The furthest step reached, so the stepper can offer a jump back to a page
  // already filled in without letting anyone skip ahead into an unconfigured one.
  const [maxStepReached, setMaxStepReached] = useState(1);
  // 'recommended' takes the standard plan straight to the preview; 'custom'
  // walks steps 2-5. Chosen on step 1.
  const [startMode, setStartMode] = useState('custom');
  // True while a scope change is being re-measured against the backend.
  const [scopeChecking, setScopeChecking] = useState(false);
  const [loading, setLoading] = useState(false);
  const [eligibilityData, setEligibilityData] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [deploymentResult, setDeploymentResult] = useState(null);
  const [excludedDeviceIds, setExcludedDeviceIds] = useState([]);
  const [previewSearch, setPreviewSearch] = useState('');
  const [expandedPhases, setExpandedPhases] = useState({});
  
  const [config, setConfig] = useState({
    name: '',
    target_version: initialTargetVersion || '',
    num_sprints: 3,
    completion_threshold: 60,
    include_medium_risk: true,
    // 02/09 — device readiness High risk. Off by default: including devices
    // Intune expects to fail must be a decision, never an accident.
    include_high_risk: false,
    // Bug fix 2 (01/09) — ONE mode field. The backend derives every other
    // flag from it (`_resolve_fu_deployment_mode`), so Staged and App Risk
    // deployments can no longer share allocation rules by accident.
    //
    //   staged   → Deployment Sprint + Readiness Risk + disk space (+ optional
    //              firmware date). App criticality ignored end to end.
    //   app_risk → App criticality first, Readiness Risk as the fallback.
    //              Disk-space gate off.
    //
    // The legacy booleans are still sent so an older backend keeps working.
    deployment_mode: appRiskMode ? 'app_risk' : 'staged',
    use_app_risk_categories: appRiskMode,
    use_device_phases: !appRiskMode,
    use_app_risk_mode: appRiskMode,
    include_app_risk: appRiskMode,
    apply_disk_gate: !appRiskMode,
    min_disk_space_gb: FU_MIN_DISK_GB,
    test_group_size: 0,
    // 10/09 round 2 — Optional by default. A feature update is a disruptive,
    // hour-plus in-place upgrade; starting it by force on people who have not
    // been told is the decision that generates the support calls. The optional
    // window below is how it stops being optional, on a deadline the users
    // were given.
    installation_type: 'optional',
    // Sprint schedule (01/09 round 2). Before this, sprint progression was
    // threshold-only and there was no date anywhere in the module, so the
    // standard "optional for 10 days, then required" rollout couldn't be
    // expressed at all.
    //   progression_mode: 'threshold' (default, unchanged behaviour)
    //                   | 'deadline'  (advance after a fixed sprint duration)
    //                   | 'both'      (whichever lands first)
    progression_mode: 'threshold',
    // 10/09 round 2 — 7 days, was 14. A fortnight per sprint puts a three-sprint
    // rollout six weeks out and a five-sprint one past the next feature update;
    // a week is the cadence customers actually run.
    sprint_duration_days: 7,
    // Days a sprint stays Optional before ALICE escalates it to Required.
    // Only applies when installation_type is 'optional'. 0/blank = never
    // (the admin flips each sprint by hand on the deployment card).
    auto_required_after_days: 0,
    // 01/09 round 3 — the optional window counts from the day the USERS were
    // told, not the day the sprint went live. Anchoring to activation would
    // force-install before the deadline the comms gave them.
    auto_required_anchor: 'comms',
    // Offer scheduling. The profile and its assignment land immediately, but
    // Intune only presents the update from `offer_start_at` / activation +
    // `offer_lead_days`. This is the gap that lets Intune's backend register
    // the offer before users are told it exists.
    offer_lead_days: 1,
    offer_start_at: '',
    // Sprint sizing. Empty = the default percentage ramp. A list of counts
    // (e.g. 25, 50, 100, 350, 500) overrides it.
    // 02/09 — hold the rollout at the test group until someone signs it off.
    require_test_approval: true,
    sizing_mode: 'percentage',
    wave_device_counts: [],
    // 10/09 round 2 — the percentage ramp is editable now. The backend has
    // always accepted wave_percentages (_calculate_sprint_waves takes it as an
    // override); the wizard simply never sent one, so the split was fixed at
    // pilot-then-even. Prefilled from fuDefaultSprintCounts, so leaving it
    // alone reproduces exactly the ramp that used to be hard-coded.
    wave_percentages: [],
    // Phase 2 — Firmware Analysis & Updates §9.4. Off by default.
    // When `firmware_gate_enabled` flips on, `min_firmware_release_date`
    // pre-populates from the target-OS map (FEATURE_UPDATE_FIRMWARE_DATE_DEFAULTS)
    // but can be overridden by the admin.
    firmware_gate_enabled: false,
    min_firmware_release_date: '',
    // 09/09 — device-type scoping. Which virtual machine categories to keep
    // out of this deployment: 'cloud_pc' | 'azure_vm' | 'other_vm'. Seeded
    // from the Eligibility Planner when the wizard is opened from there, so a
    // decision made while planning is not silently dropped on the way in.
    // 10/09 round 2 — otherwise it starts at the house default: Cloud PCs in,
    // Azure VMs and AVD session hosts out. See FU_DEFAULT_EXCLUDED_DEVICE_TYPES.
    excluded_virtualisation_types: Array.isArray(initialExcludedDeviceTypes)
      ? initialExcludedDeviceTypes
      : [...FU_DEFAULT_EXCLUDED_DEVICE_TYPES],
    // 09/09 — spread device models across sprints. Default ON. Sprints are cut
    // as contiguous slices off an ordered list, and a batch of identical
    // machines bought and enrolled together sits together in that list, so a
    // sprint could be almost entirely one model — the worst shape for a staged
    // rollout, whose whole point is to meet a model-specific driver problem on
    // 20 machines rather than 300.
    spread_models_across_sprints: true,
    // 09/09 — email each sprint's users when it activates. Default OFF: this
    // sends mail to end users and is not a decision to make on their behalf.
    // Seeded onto every sprint at creation and switchable per sprint from the
    // deployment card afterwards.
    announce_on_activation: false,
    announcement_subject: '',
    announcement_body_html: '',
  });

  // 10/09 — a ref mirror of `config`, so a handler can read the latest values
  // without every handler being rebuilt on each keystroke. Only the preview
  // call needs it today; it runs both from a button (state committed) and from
  // the recommended plan (state set microseconds earlier, not yet committed).
  const configRef = useRef(config);
  configRef.current = config;

  // Default firmware-gate date per target OS (loaded from
  // GET /firmware/feature-update-defaults on wizard open).
  const [firmwareDateDefaults, setFirmwareDateDefaults] = useState({});

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/firmware/feature-update-defaults`)
      .then(res => { if (!cancelled) setFirmwareDateDefaults(res.data?.defaults || {}); })
      .catch(() => { /* non-fatal; user can still type a date */ });
    return () => { cancelled = true; };
  }, []);

  // When the target version changes (or the gate is toggled on), refresh
  // the prepopulated date — but only when the admin hasn't typed their own.
  useEffect(() => {
    if (!config.firmware_gate_enabled) return;
    const mapped = firmwareDateDefaults[config.target_version];
    if (mapped && !config.min_firmware_release_date) {
      setConfig(prev => ({ ...prev, min_firmware_release_date: mapped }));
    }
    // eslint-disable-next-line
  }, [config.firmware_gate_enabled, config.target_version, firmwareDateDefaults]);

  // 10/09 — every step change goes through here so `maxStepReached` stays
  // honest and the stepper knows which chips are safe to offer as a jump back.
  const goStep = (n) => {
    setStep(n);
    setMaxStepReached(prev => Math.max(prev, n));
  };

  const defaultDeploymentName = (targetVersion) => {
    const versionShort = (targetVersion || '')
      .replace('Windows 11, version ', 'Win11-')
      .replace('Windows 10, version ', 'Win10-');
    return `Feature Update ${versionShort} - ${new Date().toLocaleDateString()}`;
  };

  // The eligibility call, split out from the step-1 button so the scope step
  // can re-run it whenever the scope changes.
  //
  // `cfgOverride` matters: the recommended plan sets several scope fields at
  // once, and React state has not committed by the time the check has to run.
  // Passing the config explicitly is the difference between measuring the plan
  // being built and measuring the one before it.
  const eligibleSigRef = useRef('');
  const runEligibilityCheck = async (cfgOverride = null, { silent = false } = {}) => {
    const cfg = cfgOverride || config;
    if (!cfg.target_version) {
      if (!silent) toast.error('Please select a target version');
      return null;
    }
    if (!silent) setLoading(true);
    try {
      const res = await osPatchApi.checkDeviceEligibility({
        target_version: cfg.target_version,
        include_medium_risk: cfg.include_medium_risk,
        include_high_risk: cfg.include_high_risk,
        min_disk_space_gb: cfg.min_disk_space_gb,
        // Bug fix 2 (01/09) — App Risk deployments are not disk-gated.
        apply_disk_gate: !appRiskMode,
        min_firmware_release_date: cfg.firmware_gate_enabled
          ? (cfg.min_firmware_release_date || null)
          : null,
        excluded_virtualisation_types: cfg.excluded_virtualisation_types || [],
      });
      setEligibilityData(res.data);
      // Record the scope these numbers were measured with, so the re-check
      // effect can tell "already current" from "needs re-running".
      eligibleSigRef.current = fuScopeSignature(cfg);
      if (!cfg.name) {
        setConfig(prev => (prev.name ? prev : { ...prev, name: defaultDeploymentName(cfg.target_version) }));
      }
      return res.data;
    } catch (error) {
      console.error('Error checking eligibility:', error);
      toast.error('Failed to check device eligibility');
      return null;
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // Step 1: Check eligibility when version is selected, then on to Scope.
  const handleCheckEligibility = async () => {
    const data = await runEligibilityCheck();
    if (data) goStep(2);
  };

  // 10/09 — Scope changes re-measure. Debounced, because clicking through
  // three device types or typing a firmware date should be one request rather
  // than four, and flagged with `scopeChecking` so the tiles are visibly
  // provisional while it is in flight instead of quietly wrong.
  //
  // Any preview already generated was built from the old scope, so it is
  // dropped: the review step must never render a split that the current scope
  // would not produce.
  const scopeSig = fuScopeSignature(config);
  useEffect(() => {
    if (!eligibilityData) return;              // nothing measured yet
    if (step < 2 || step > FU_WIZARD_LAST_STEP) return;
    if (eligibleSigRef.current === scopeSig) return;
    let cancelled = false;
    setScopeChecking(true);
    const timer = setTimeout(async () => {
      await runEligibilityCheck(null, { silent: true });
      if (!cancelled) {
        setScopeChecking(false);
        setPreviewData(null);
      }
    }, 450);
    return () => { cancelled = true; clearTimeout(timer); };
    // eslint-disable-next-line
  }, [scopeSig, step]);

  // 10/09 — the fast path. Most rollouts are the same rollout, and walking an
  // admin through five pages to arrive back at the defaults is how a wizard
  // gets abandoned half-way. Fill in the standard answers, measure them, and
  // land on the preview report — which is where the decision actually gets
  // made. Back from there opens the full wizard with these values in place.
  const handleUseRecommended = async () => {
    if (!config.target_version) {
      toast.error('Please select a target version');
      return;
    }
    const next = {
      ...config,
      ...FU_RECOMMENDED_CONFIG,
      name: config.name || defaultDeploymentName(config.target_version),
    };
    setConfig(next);
    setLoading(true);
    try {
      const data = await runEligibilityCheck(next, { silent: true });
      if (!data) return;
      if (!data.eligible_count) {
        toast.error('No devices are eligible for the recommended plan — open the scope step to widen it.');
        goStep(2);
        return;
      }
      setMaxStepReached(FU_WIZARD_LAST_STEP);
      await handlePreviewDeployment([], next);
    } finally {
      setLoading(false);
    }
  };

  // 03/09 — opened from the Eligibility Planner, which has already picked the
  // target version. Step 1 asks only that question, so there is nothing left to
  // ask: run the check and land the user on Scope (10/09 — was Configure).
  //
  // It deliberately re-runs the wizard's OWN eligibility call rather than
  // carrying the planner's result across. The planner shows the whole picture;
  // a deployment applies its own gates (disk, firmware date, which risk bands
  // are in), so these are the numbers the deployment will actually be built
  // from. Reusing the planner's would show one set of figures and deploy
  // another.
  const autoCheckedRef = useRef(false);
  useEffect(() => {
    if (!initialTargetVersion || autoCheckedRef.current) return;
    autoCheckedRef.current = true;
    handleCheckEligibility();
    // eslint-disable-next-line
  }, [initialTargetVersion]);

  // Step 6: Preview deployment (with exclusions).
  // `cfgOverride` is used by the recommended plan, which previews a config
  // that React has not committed to state yet.
  const handlePreviewDeployment = async (excludedIds = null, cfgOverride = null) => {
    const config = cfgOverride || configRef.current;
    setLoading(true);
    try {
      const previewConfig = {
        ...config,
        // Scrub the firmware date when the gate is toggled off — keeps the
        // typed date in the wizard UI so re-enabling restores it, but the
        // backend should never see a non-null date when the gate is off.
        min_firmware_release_date: config.firmware_gate_enabled
          ? (config.min_firmware_release_date || null)
          : null,
        // 01/09 round 3 — only send explicit sprint counts when the admin
        // actually chose that sizing mode; otherwise the backend keeps the
        // percentage ramp.
        wave_device_counts: config.sizing_mode === "explicit"
          ? (config.wave_device_counts || []).map(n => parseInt(n, 10) || 0)
          : [],
        // 10/09 round 2 — only send a ramp the admin actually edited. A
        // prefilled one is shown for editing, never sent: the round trip
        // through percentages is lossy, so an untouched deployment must keep
        // running the backend pilot model it always did. See
        // sendCustomPercentages.
        wave_percentages: sendCustomPercentages
          ? (config.wave_percentages || []).map(n => Number(n) || 0)
          : null,
        offer_start_at: config.offer_start_at || null,
        excluded_device_ids: excludedIds !== null ? excludedIds : excludedDeviceIds,
      };
      const res = await osPatchApi.previewFeatureDeployment({ config: previewConfig });
      setPreviewData(res.data);
      // Auto-expand all phases on first preview
      if (!Object.keys(expandedPhases).length) {
        const phases = {};
        if (res.data.test_group?.enabled) phases['test'] = true;
        (res.data.sprint_waves || []).forEach((_, i) => { phases[`sprint_${i+1}`] = true; });
        setExpandedPhases(phases);
      }
      goStep(FU_WIZARD_LAST_STEP);
    } catch (error) {
      console.error('Error previewing deployment:', error);
      toast.error('Failed to preview deployment');
    } finally {
      setLoading(false);
    }
  };

  // Handle excluding a device - re-preview with updated exclusion list
  const handleExcludeDevice = async (deviceId, deviceName) => {
    const newExcluded = [...excludedDeviceIds, deviceId];
    setExcludedDeviceIds(newExcluded);
    toast(`Excluded ${deviceName} - recalculating...`);
    await handlePreviewDeployment(newExcluded);
  };

  // Handle re-including an excluded device
  const handleReincludeDevice = async (deviceId, deviceName) => {
    const newExcluded = excludedDeviceIds.filter(id => id !== deviceId);
    setExcludedDeviceIds(newExcluded);
    toast(`Re-included ${deviceName} - recalculating...`);
    await handlePreviewDeployment(newExcluded);
  };

  // Step 3: Create deployment (includes exclusions)
  const handleCreateDeployment = async () => {
    setLoading(true);
    try {
      const deployConfig = {
        ...config,
        min_firmware_release_date: config.firmware_gate_enabled
          ? (config.min_firmware_release_date || null)
          : null,
        wave_device_counts: config.sizing_mode === "explicit"
          ? (config.wave_device_counts || []).map(n => parseInt(n, 10) || 0)
          : [],
        // 10/09 round 2 — only send a ramp the admin actually edited. A
        // prefilled one is shown for editing, never sent: the round trip
        // through percentages is lossy, so an untouched deployment must keep
        // running the backend pilot model it always did. See
        // sendCustomPercentages.
        wave_percentages: sendCustomPercentages
          ? (config.wave_percentages || []).map(n => Number(n) || 0)
          : null,
        offer_start_at: config.offer_start_at || null,
        excluded_device_ids: excludedDeviceIds,
      };
      const res = await osPatchApi.createFeatureDeployment({ config: deployConfig });
      setDeploymentResult(res.data);
      goStep(FU_WIZARD_DONE_STEP);
      toast.success('Deployment created successfully!');
      if (onDeploymentCreated) onDeploymentCreated();
    } catch (error) {
      console.error('Error creating deployment:', error);
      toast.error('Failed to create deployment');
    } finally {
      setLoading(false);
    }
  };

  // Toggle phase expansion in preview
  const togglePhase = (phaseKey) => {
    setExpandedPhases(prev => ({ ...prev, [phaseKey]: !prev[phaseKey] }));
  };

  // 01/09 round 3 — the sprint plan the customer signs off before anything is
  // created in Intune. One row per device, with the sprint it lands in and the
  // data the approval conversation actually turns on (readiness, disk, model).
  // Excluded devices are included and marked rather than dropped, so the
  // customer can see what was taken out and put it back.
  const handleExportSprintPlan = () => {
    if (!previewData) return;
    const rows = [];
    const push = (sprint, note, d) => rows.push({
      sprint,
      offer_note: note,
      enforcement: enforceNote,
      progression: progressNote,
      device_name: d.device_name || '',
      user: d.user_name || '',
      current_os: d.current_os_version || '',
      device_type: d.virtualisation_label || 'Physical device',
      model: d.model || '',
      readiness: (!d.readiness_status || d.readiness_status === 'Unknown') ? 'Not assessed' : d.readiness_status,
      readiness_reason: d.readiness_reason || (d.eligibility_reasons_readiness || []).join('; '),
      app_risk: d.app_risk_category || '',
      fu_phase: d.feature_update_phase || '',
      free_disk_gb: d.free_disk_space_gb == null ? '' : Number(d.free_disk_space_gb).toFixed(1),
      disk_ok: d.disk_check_passed === false ? 'NO' : 'yes',
      last_checkin_days: d.days_since_checkin == null ? '' : d.days_since_checkin,
      status: excludedDeviceIds.includes(d.device_id) ? 'EXCLUDED' : 'Included',
    });

    // 02/09 — read the deployment's own settings, falling back to the wizard
    // config. The preview response did not echo the offer fields, so this
    // always printed "offers as soon as possible" no matter what was set.
    const cfg = { ...config, ...(previewData.config || {}) };
    const fmt = (v) => {
      const t = Date.parse(v);
      return Number.isNaN(t) ? String(v) : new Date(t).toLocaleString();
    };
    const offerNote = cfg.offer_start_at
      ? `Sprint 1 offers from ${fmt(cfg.offer_start_at)}; later sprints ${
          cfg.offer_lead_days ? `${cfg.offer_lead_days} day(s) after activation` : 'as soon as possible'}`
      : cfg.offer_lead_days
        ? `offers ${cfg.offer_lead_days} day(s) after the sprint activates`
        : 'offers as soon as possible';
    // 07/09 — a Required sprint has nothing to escalate, so do not print an
    // enforcement deadline for one just because the deployment now carries the
    // window as a policy for later per-sprint flips.
    const enforceNote = (cfg.installation_type || 'required') !== 'optional'
      ? 'Required from the start'
      : cfg.auto_required_after_days
        ? `becomes Required ${cfg.auto_required_after_days} day(s) after ${
            (cfg.auto_required_anchor || 'comms') === 'comms' ? 'comms are sent' : 'the sprint activates'}`
        : 'stays Optional until switched by hand';
    const progressNote = cfg.progression_mode === 'deadline'
      ? `next sprint starts ${cfg.sprint_duration_days} day(s) after this one activates (fixed duration)`
      : cfg.progression_mode === 'manual'
        ? 'next sprint starts only when activated by hand (manual)'
        : cfg.progression_mode === 'both'
          ? `next sprint starts at ${cfg.completion_threshold}% complete or after ${cfg.sprint_duration_days} day(s)`
          : `next sprint starts at ${cfg.completion_threshold}% complete (fully automated)`;

    if (previewData.test_group?.devices?.length) {
      previewData.test_group.devices.forEach(d => push('Test Group', offerNote, d));
    }
    (previewData.sprint_waves || []).forEach(w => {
      (w.devices || []).forEach(d => push(`Sprint ${w.wave_number}`, offerNote, d));
    });
    (previewData.planner_excluded || []).forEach(d => push('Not allocated', '', d));

    if (!rows.length) {
      toast.info('Nothing to export yet — run the preview first');
      return;
    }

    const headers = [
      ['sprint', 'Sprint'],
      ['offer_note', 'Offer schedule'],
      ['enforcement', 'Enforcement'],
      ['progression', 'Progression'],
      ['device_name', 'Device'],
      ['user', 'User'],
      ['current_os', 'Current OS'],
      ['device_type', 'Device Type'],
      ['model', 'Model'],
      ['readiness', 'Readiness Risk'],
      ['readiness_reason', 'Readiness Reason'],
      ['app_risk', 'App Criticality'],
      ['fu_phase', 'Deployment Sprint'],
      ['free_disk_gb', 'Free Disk (GB)'],
      ['disk_ok', 'Disk OK'],
      ['last_checkin_days', 'Days Since Check-in'],
      ['status', 'Status'],
    ];
    const esc = (v) => {
      if (v == null) return '';
      const s = String(v).replace(/"/g, '""');
      return /[",\n]/.test(s) ? `"${s}"` : s;
    };
    const csv = '﻿' + [
      headers.map(h => h[1]).join(','),
      ...rows.map(r => headers.map(h => esc(r[h[0]])).join(',')),
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sprint-plan-${(config.name || 'deployment').replace(/[^a-zA-Z0-9]+/g, '-')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Sprint plan exported (${rows.length} devices)`);
  };

  // Calculate phase distribution based on eligible devices
  const calculateWaveDevices = () => {
    if (!eligibilityData) return [];
    const totalDevices = eligibilityData.eligible_count - (config.test_group_size || 0) - excludedDeviceIds.length;
    const waves = [];

    if (totalDevices <= 0 || config.num_sprints <= 0) return waves;

    const S = config.num_sprints;

    // 02/09 — honour explicit per-sprint counts here too. This preview was
    // still running the percentage/pilot model regardless of the sizing mode,
    // so the numbers on screen disagreed with the deployment that was actually
    // created. Mirrors `_fu_waves_from_explicit_counts` on the backend: counts
    // are clamped to what is left, and the last sprint takes the remainder.
    if (config.sizing_mode === 'explicit') {
      const counts = config.wave_device_counts || [];
      let remaining = totalDevices;
      for (let i = 0; i < S; i++) {
        const isLast = i === S - 1;
        const wanted = parseInt(counts[i], 10) || 0;
        const n = isLast ? remaining : Math.min(wanted, remaining);
        remaining -= n;
        waves.push({
          wave: i + 1,
          percentage: totalDevices ? Math.round((n / totalDevices) * 1000) / 10 : 0,
          devices: Math.max(0, n),
        });
      }
      return waves;
    }

    // 10/09 round 2 — the percentage ramp is a set of numbers now, and this
    // mirrors the backend's percentage branch when they are in play.
    //
    // "In play" means EDITED, not merely prefilled, and `sendCustomPercentages`
    // is the single condition both this preview and the payload builders use.
    // Rounding a ramp to percentages and back is not lossless — on 100,000
    // devices across 11 sprints the round trip moves 45 machines, and at any
    // precision the `max(1, ...)` rule diverges from the pilot model where
    // sprints approach devices. So an untouched ramp is not sent at all: the
    // backend runs the same pilot model it always has, and a deployment built
    // today allocates exactly as one built yesterday.
    if (sendCustomPercentages) {
      return fuCountsFromPercentages(config.wave_percentages, totalDevices, S).map((n, i) => ({
        wave: i + 1,
        percentage: totalDevices ? Math.round((n / totalDevices) * 1000) / 10 : 0,
        devices: n,
      }));
    }

    // No custom percentages — the default pilot ramp, shared with the prefill.
    return fuDefaultSprintCounts(totalDevices, S).map((n, i) => ({
      wave: i + 1,
      percentage: totalDevices ? Math.round((n / totalDevices) * 1000) / 10 : 0,
      devices: n,
    }));
  };

  // 10/09 round 2 — sprint sizing prefill.
  //
  // Explicit counts used to open as a row of empty boxes: the mode was unusable
  // until the admin had done the arithmetic themselves, and the placeholder "0"
  // read as a real value. The percentage ramp had no controls at all — it was a
  // radio button whose behaviour you could only discover by running a preview.
  //
  // Both now start prefilled from the SAME default ramp (sprint 1 at 5% of the
  // devices in scope, the rest split evenly) and both are editable from there,
  // so the two modes are the same plan in different units rather than one plan
  // and one mystery. Leaving either alone reproduces the ramp that was hard
  // -coded before this change.
  const [sizingDirty, setSizingDirty] = useState(false);
  // What the sprints actually divide: eligible devices, less the test group,
  // less anything excluded by hand on the review step.
  const sizingPool = Math.max(0,
    (eligibilityData?.eligible_count || 0)
    - (config.test_group_size || 0)
    - excludedDeviceIds.length);

  useEffect(() => {
    if (sizingDirty) return;
    const counts = fuDefaultSprintCounts(sizingPool, config.num_sprints);
    const pcts = fuCountsToPercentages(counts, sizingPool);
    setConfig(prev => {
      const same = (prev.wave_device_counts || []).length === counts.length
        && counts.every((c, i) => Number(prev.wave_device_counts[i]) === c)
        && (prev.wave_percentages || []).length === pcts.length
        && pcts.every((p, i) => Number(prev.wave_percentages[i]) === p);
      // Bail out unchanged rather than setting state to an equal value — this
      // effect depends on config, so returning a new object every run would
      // spin.
      return same ? prev : { ...prev, wave_device_counts: counts, wave_percentages: pcts };
    });
  }, [sizingPool, config.num_sprints, sizingDirty]);

  // Whether the admin's percentages override the backend's own ramp. Prefilled
  // values are shown, never sent — see the note in calculateWaveDevices.
  const sendCustomPercentages = config.sizing_mode === 'percentage'
    && sizingDirty
    && (config.wave_percentages || []).length >= config.num_sprints;

  const resetSizingToDefault = () => {
    const counts = fuDefaultSprintCounts(sizingPool, config.num_sprints);
    setConfig(prev => ({
      ...prev,
      wave_device_counts: counts,
      wave_percentages: fuCountsToPercentages(counts, sizingPool),
    }));
    setSizingDirty(false);
  };

  // 10/09 — per-step blockers. A step says what is wrong with it beside its own
  // Next button rather than failing on the far side of the wizard: "no devices
  // are eligible" is a scope problem and belongs on the scope page, not in a
  // toast four steps later.
  const scopeBlocker = !eligibilityData
    ? null
    : scopeChecking
      ? 'Re-measuring…'
      : (eligibilityData.eligible_count || 0) === 0
        ? 'No devices are eligible with this scope — widen it above.'
        : null;
  const designBlocker = !config.name?.trim()
    ? 'Give the deployment a name.'
    : (config.num_sprints || 0) < 1
      ? 'Choose at least one sprint.'
      : null;

  const progressionSummary = config.progression_mode === 'deadline'
    ? `next sprint every ${config.sprint_duration_days} day(s)`
    : config.progression_mode === 'manual'
      ? 'sprints advanced by hand'
      : `next sprint at ${config.completion_threshold}%`;

  // Shared footer for steps 2-5. Called as a function, not rendered as a
  // component, so it does not remount its children on every keystroke.
  const wizardNav = ({ back, next, nextLabel = 'Continue', blocker = null, testId }) => (
    <div className="flex items-center justify-between gap-3 pt-2">
      <Button
        variant="outline"
        className="border-[rgba(229,239,250,0.1)] text-white"
        onClick={back}
        disabled={loading}
      >
        Back
      </Button>
      <div className="flex items-center gap-3">
        {blocker && (
          <span className="text-xs text-amber-300 text-right max-w-xs" data-testid={`${testId}-blocker`}>
            {blocker}
          </span>
        )}
        <Button
          onClick={next}
          disabled={loading || Boolean(blocker)}
          className="bg-purple-500 hover:bg-purple-600"
          data-testid={testId}
        >
          {nextLabel}
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 overflow-y-auto py-4">
      <Card className="alice-popup bg-slate-900 border-[rgba(229,239,250,0.1)] w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
        <CardHeader className="border-b border-[rgba(229,239,250,0.1)] sticky top-0 bg-slate-900 z-10">
          <div className="flex items-center justify-between">
            <CardTitle className="text-white flex items-center gap-2">
              <Rocket className="w-5 h-5 text-purple-400" />
              Feature Update Staged Deployment
              {appRiskMode && (
                <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 text-xs rounded-full font-normal">App Risk Mode</span>
              )}
            </CardTitle>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="w-4 h-4 text-white/90" />
            </Button>
          </div>
          {/* Progress Steps — 10/09. Six chips now, and every step already
              visited is clickable: changing the sprint count after seeing the
              preview should not mean walking back through four pages. Review
              is only reachable when a preview exists, because a scope change
              discards the old one. */}
          <div className="flex items-center gap-1 mt-4 flex-wrap" data-testid="fu-wizard-stepper">
            {FU_WIZARD_STEPS.map((s, i) => {
              const done = step > s.id || step === FU_WIZARD_DONE_STEP;
              const current = step === s.id;
              const reachable = step !== FU_WIZARD_DONE_STEP
                && !current
                && s.id <= maxStepReached
                && (s.id !== FU_WIZARD_LAST_STEP || Boolean(previewData));
              return (
                <React.Fragment key={s.id}>
                  <button
                    type="button"
                    disabled={!reachable || loading}
                    onClick={() => goStep(s.id)}
                    className={`flex items-center gap-1.5 rounded-md px-1 py-1 transition-colors ${
                      current ? 'text-purple-400' : done ? 'text-purple-300/70' : 'text-white/50'
                    } ${reachable ? 'hover:bg-white/10 cursor-pointer' : 'cursor-default'}`}
                    data-testid={`fu-wizard-step-${s.id}`}
                  >
                    <span className={`w-7 h-7 shrink-0 rounded-full flex items-center justify-center text-xs font-medium border-2 ${
                      done ? 'bg-purple-500 border-purple-500 text-white' :
                      current ? 'border-purple-400 text-purple-400' :
                      'border-[rgba(229,239,250,0.15)] text-white/50'
                    }`}>
                      {done ? '✓' : s.id}
                    </span>
                    <span className={`text-xs whitespace-nowrap ${current ? 'inline' : 'hidden lg:inline'}`}>
                      {s.label}
                    </span>
                  </button>
                  {i < FU_WIZARD_STEPS.length - 1 && (
                    <ArrowRight className="w-3 h-3 text-white/20 shrink-0" />
                  )}
                </React.Fragment>
              );
            })}
          </div>

          {/* 10/09 — the plan so far, carried on every step. The decisions are
              spread over five pages now, and the numbers they add up to are the
              thing an admin loses track of first. */}
          {step >= 2 && step <= FU_WIZARD_LAST_STEP && eligibilityData && (
            <div
              className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs"
              data-testid="fu-wizard-summary-bar"
            >
              <span className="text-white/40">This plan:</span>
              {scopeChecking ? (
                <span className="text-cyan-300" data-testid="fu-wizard-summary-recalculating">
                  re-measuring devices in scope…
                </span>
              ) : (
                <span className="text-white/75">
                  <span className="text-white font-medium">{eligibilityData.eligible_count}</span>
                  {' '}device{eligibilityData.eligible_count === 1 ? '' : 's'} in scope
                </span>
              )}
              <span className="text-white/20">·</span>
              <span className="text-white/75">
                {config.num_sprints} sprint{config.num_sprints === 1 ? '' : 's'}
                {config.test_group_size > 0 ? ` + ${config.test_group_size} test` : ''}
              </span>
              <span className="text-white/20">·</span>
              <span className="text-white/75">
                {config.installation_type === 'optional' ? 'Optional' : 'Required'}
                {config.auto_required_after_days > 0 && config.installation_type === 'optional'
                  ? ` for ${config.auto_required_after_days}d`
                  : ''}
              </span>
              <span className="text-white/20">·</span>
              <span className="text-white/75">{progressionSummary}</span>
              {config.announce_on_activation && (
                <>
                  <span className="text-white/20">·</span>
                  <span className="text-cyan-300/80">emails users</span>
                </>
              )}
            </div>
          )}
        </CardHeader>

        <CardContent className="p-6">
          {/* Step 1: Select Target Version */}
          {step === 1 && (
            <div className="space-y-6">
              <div>
                {/* 7 Oct: the rule set for this mode, moved here from the banner
                    that sat on the Deployments page and described both. */}
                {appRiskMode ? (
                  <div className="mb-5 rounded-lg border border-amber-500/30 bg-amber-500/10 p-4 flex items-start gap-3"
                       data-testid="wizard-mode-explainer-app-risk">
                    <Target className="w-5 h-5 text-amber-400 mt-0.5 shrink-0" />
                    <p className="text-white/90 text-sm">
                      <span className="text-amber-300 font-medium">App Risk Deployment</span> orders sprints by app business
                      criticality first — Low, then Medium, then High — falling back to Readiness Risk for devices with no
                      app-risk signal. The test sprint selects <span className="text-blue-400 font-medium">Test/Sprint 1</span>{' '}
                      phase devices. Free disk space is not applied as a gate.
                    </p>
                  </div>
                ) : (
                  <div className="mb-5 rounded-lg border border-purple-500/30 bg-purple-500/10 p-4 flex items-start gap-3"
                       data-testid="wizard-mode-explainer-staged">
                    <Rocket className="w-5 h-5 text-purple-300 mt-0.5 shrink-0" />
                    <p className="text-white/90 text-sm">
                      <span className="text-purple-300 font-medium">Staged Deployment</span> allocates sprints from Deployment
                      Sprint + Readiness Risk, gated on free disk space (and optionally a minimum firmware release date). App
                      business criticality is not used.
                    </p>
                  </div>
                )}
                <h3 className="text-lg font-medium text-white mb-4">Select Target Feature Update</h3>
                <p className="text-white/90 text-sm mb-4">Choose the Windows version you want to deploy to eligible devices.</p>
                
                <div className="space-y-3">
                  {catalog.filter(c => c.isInService !== false).map((item, i) => (
                    <label 
                      key={item.id || i}
                      className={`flex items-center p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        config.target_version === item.id 
                          ? 'border-purple-500 bg-purple-500/10' 
                          : 'border-[rgba(229,239,250,0.1)] hover:border-white/30 bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px]'
                      }`}
                    >
                      <input
                        type="radio"
                        name="target_version"
                        value={item.id}
                        checked={config.target_version === item.id}
                        onChange={(e) => setConfig({ ...config, target_version: e.target.value })}
                        className="sr-only"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-white font-medium">{item.displayName || item.version}</span>
                          {item.isInService && (
                            <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full">In Service</span>
                          )}
                        </div>
                        <span className="text-white/80 text-sm">{item.id}</span>
                      </div>
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        config.target_version === item.id ? 'border-purple-500 bg-purple-500' : 'border-white/30'
                      }`}>
                        {config.target_version === item.id && <CheckCircle className="w-3 h-3 text-white" />}
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* 10/09 — the fast path. Roughly four rollouts in five are the
                  same rollout, and making an admin walk five configuration
                  pages to arrive back at the defaults is how a wizard gets
                  abandoned half-way — or worse, mis-configured on the way
                  through. The recommended plan fills the standard answers in
                  and goes straight to the preview report, which is where the
                  decision is actually made. Nothing is hidden: Back from the
                  preview opens the full wizard with these values in place. */}
              <div
                className="rounded-lg border border-[rgba(229,239,250,0.15)] bg-[rgba(229,239,250,0.05)] p-4 space-y-3"
                data-testid="wizard-start-mode-section"
              >
                <div className="flex items-center gap-2">
                  <Rocket className="w-4 h-4 text-purple-400" />
                  <span className="text-white font-medium">How do you want to set this up?</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {[
                    {
                      v: 'recommended',
                      label: 'Recommended plan',
                      tag: 'One more click',
                      desc: 'The standard staged rollout, filled in for you. Straight to the preview report — change anything from there.',
                    },
                    {
                      v: 'custom',
                      label: 'Configure it myself',
                      tag: 'Five steps',
                      desc: 'Scope, sprint design, progression, then installation and comms. Every setting, one decision per page.',
                    },
                  ].map(opt => (
                    <label
                      key={opt.v}
                      className={`flex flex-col gap-1 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                        startMode === opt.v
                          ? 'border-purple-500 bg-purple-500/10'
                          : 'border-[rgba(229,239,250,0.1)] hover:border-white/30 bg-[rgba(229,239,250,0.05)]'
                      }`}
                      data-testid={`wizard-start-mode-${opt.v}`}
                    >
                      <input
                        type="radio"
                        name="wizard_start_mode"
                        value={opt.v}
                        checked={startMode === opt.v}
                        onChange={() => setStartMode(opt.v)}
                        className="sr-only"
                      />
                      <span className="text-white text-sm font-medium">{opt.label}</span>
                      <span className="text-purple-200/70 text-[11px] uppercase tracking-wide">{opt.tag}</span>
                      <span className="text-white/50 text-xs leading-snug">{opt.desc}</span>
                    </label>
                  ))}
                </div>

                {startMode === 'recommended' && (
                  <div
                    className="rounded-md bg-black/20 border border-white/10 p-3"
                    data-testid="wizard-recommended-summary"
                  >
                    <p className="text-xs text-white/60 mb-2">This is what will be set:</p>
                    <ul className="text-xs text-white/75 space-y-1 leading-relaxed">
                      <li>· <span className="text-white/90">3 sprints.</span> Sprint 1 is a pilot sized at 5% of the devices in scope (never fewer than 2, never more than 50); the rest split evenly.</li>
                      <li>· <span className="text-white/90">Optional</span> at first, becoming <span className="text-white/90">Required 10 days</span> after that sprint&apos;s users are emailed.</li>
                      <li>· <span className="text-amber-200/90">Emails each sprint&apos;s users when it goes live</span> — the update is available, and the date it becomes automatic. This is what starts the 10-day clock, so the two go together. Users already upgraded, and devices with no user on record, are skipped.</li>
                      <li>· The next sprint starts <span className="text-white/90">automatically at 60% complete</span>.</li>
                      <li>· <span className="text-white/90">Medium</span> readiness risk included, <span className="text-white/90">High</span> risk left out.</li>
                      <li>· <span className="text-white/90">Azure VMs and AVD session hosts excluded</span>; Cloud PCs and physical devices kept — a Cloud PC is a user desktop and needs the update, an AVD host is rebuilt from an image.</li>
                      <li>· Each sprint starts offering <span className="text-white/90">1 day after it activates</span>, so Intune has registered the offer before anyone looks.</li>
                      <li>· Device models <span className="text-white/90">spread across sprints</span>, so a model-specific driver fault surfaces early and on few machines.</li>
                      <li>· No firmware date gate, and no test group held back for sign-off.</li>
                    </ul>
                    <p className="text-[11px] text-white/40 mt-2">
                      You will see the full device-by-device preview before anything is created in Intune, and
                      no email is sent until a sprint actually activates.
                    </p>
                  </div>
                )}
              </div>

              <div className="flex justify-end">
                <Button
                  onClick={startMode === 'recommended' ? handleUseRecommended : handleCheckEligibility}
                  disabled={!config.target_version || loading}
                  className="bg-purple-500 hover:bg-purple-600"
                  data-testid="wizard-step1-next"
                >
                  {loading
                    ? 'Checking...'
                    : startMode === 'recommended'
                      ? 'Build recommended plan'
                      : 'Continue to scope'}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 2: Scope &amp; readiness */}
          {step === 2 && eligibilityData && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-white">Scope &amp; readiness</h3>
                <p className="text-white/60 text-sm mt-1">Which devices qualify for this rollout? Every change here re-measures the eligible count, so the sprint sizes on the next step are built from the number you sign off.</p>
              </div>

              {/* 10/09 — these tiles used to be measured once, on step 1, and
                  never again. Anything changed below re-measures them, and they
                  are visibly provisional while that is in flight rather than
                  showing a number the wizard has already stopped believing. */}
              {scopeChecking && (
                <div
                  className="flex items-center gap-2 text-xs text-cyan-300"
                  data-testid="wizard-scope-recalculating"
                >
                  <RefreshCw className="w-3 h-3 animate-spin" />
                  Re-measuring eligible devices with this scope…
                </div>
              )}
              <div
                className={`transition-opacity ${scopeChecking ? 'opacity-40' : ''}`}
                data-testid="wizard-eligibility-tiles"
              >
              {/* Eligibility Summary */}
              <div className="alice-kpi-row grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 text-center">
                  <div className="text-3xl font-bold text-green-400">{eligibilityData.eligible_count}</div>
                  <div className="text-sm text-white/90">Eligible Devices</div>
                </div>
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-center">
                  <div className="text-3xl font-bold text-red-400">{eligibilityData.summary?.high_risk_excluded || 0}</div>
                  <div className="text-sm text-white/90">High Risk (Excluded)</div>
                </div>
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 text-center">
                  <div className="text-3xl font-bold text-amber-400">{eligibilityData.summary?.medium_risk_excluded || 0}</div>
                  <div className="text-sm text-white/90">Medium Risk</div>
                </div>
                <div className="bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] border border-[rgba(229,239,250,0.1)] rounded-lg p-4 text-center">
                  <div className="text-3xl font-bold text-white" data-testid="wizard-total-windows-devices">
                    {eligibilityData.summary?.windows_devices ?? eligibilityData.windows_devices ?? eligibilityData.total_devices}
                  </div>
                  <div className="text-sm text-white/90">Total Windows Devices</div>
                </div>
              </div>
              </div>

              {/* New Feature 1 (01/09) — multiple deployments. A device can
                  only be targeted by one feature update profile at a time, so
                  devices already allocated to a live deployment are held back
                  from this one. Saying so here is the difference between
                  "the wizard is broken" and "this is working as designed". */}
              {(eligibilityData.summary?.already_in_deployment_excluded || 0) > 0 && (
                <div
                  className="rounded-lg border border-blue-500/30 bg-blue-500/10 p-3 text-sm text-blue-100"
                  data-testid="wizard-already-in-deployment-note"
                >
                  <span className="font-medium text-blue-300">
                    {eligibilityData.summary.already_in_deployment_excluded} device
                    {eligibilityData.summary.already_in_deployment_excluded === 1 ? ' is' : 's are'} held by
                    {(eligibilityData.existing_deployment_names || []).length === 1 ? ' an existing deployment' : ' existing deployments'}
                  </span>
                  {(eligibilityData.existing_deployment_names || []).length > 0 && (
                    <span> ({eligibilityData.existing_deployment_names.join(', ')})</span>
                  )}
                  {' '}and are not offered to this one. They are released when that deployment completes or is deleted.
                </div>
              )}

              {/* Readiness risk inclusion — 02/09.
                  These are DEVICE readiness bands from Intune, not application
                  criticality. Medium is on by default; High is a deliberate
                  choice, so it is off and says what it means. */}
              <div className="space-y-3">
                <div>
                  <label className="block text-sm text-white/90 mb-2">Device readiness risk to include</label>
                  <div className="space-y-2">
                    <label className="flex items-start gap-3 cursor-pointer" data-testid="wizard-include-medium-risk">
                      <input
                        type="checkbox"
                        checked={config.include_medium_risk}
                        onChange={(e) => setConfig({ ...config, include_medium_risk: e.target.checked })}
                        className="w-4 h-4 mt-0.5 rounded border-[rgba(229,239,250,0.1)] bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] text-amber-500"
                      />
                      <span className="text-white text-sm">
                        Include Medium readiness risk devices
                        <span className="block text-xs text-white/50 mt-0.5">
                          {eligibilityData?.summary?.readiness_bands?.MediumRisk ?? 0} device(s) in this band.
                          Usually safe to include and remediate as they surface.
                        </span>
                      </span>
                    </label>
                    <label className="flex items-start gap-3 cursor-pointer" data-testid="wizard-include-high-risk">
                      <input
                        type="checkbox"
                        checked={config.include_high_risk}
                        onChange={(e) => setConfig({ ...config, include_high_risk: e.target.checked })}
                        className="w-4 h-4 mt-0.5 rounded border-[rgba(229,239,250,0.1)] bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] text-red-500"
                      />
                      <span className="text-white text-sm">
                        Include High readiness risk devices
                        <span className="block text-xs text-white/50 mt-0.5">
                          {eligibilityData?.summary?.readiness_bands?.HighRisk ?? 0} device(s) Intune expects to
                          fail or need work first. Off by default — include them only when the blockers are
                          understood, and put them in a late sprint.
                        </span>
                      </span>
                    </label>
                  </div>
                  {/* 09/09 — these two toggles act on Intune's assessment and
                      nothing else. Most of a real estate has no assessment at
                      all, and neither toggle touches those devices; say so here
                      rather than leaving an admin to work out why the numbers
                      are far smaller than the fleet. */}
                  <p className="text-xs text-white/45 mt-2" data-testid="wizard-readiness-unassessed-note">
                    {eligibilityData?.summary?.readiness_bands?.Unknown ?? 0} device(s) have no Intune readiness
                    assessment. Neither toggle applies to them — they are planned on the other criteria (upgrade
                    path, disk, FU sprint, firmware). Intune compliance is not a readiness band.
                  </p>
                  {config.include_high_risk && (
                    <p className="text-xs text-amber-300 mt-2" data-testid="wizard-high-risk-warning">
                      High risk devices are now in scope. Check the preview report before creating the
                      deployment — these are the devices most likely to generate support calls.
                    </p>
                  )}
                </div>

                {/* 09/09 — device-type scoping. Sits with the other "who is in
                    this rollout" decisions rather than in a separate step. The
                    counts are the whole Windows estate, deliberately: an admin
                    needs to see how many Cloud PCs they would be dropping
                    BEFORE they drop them, and a count taken after the exclusion
                    would always read zero. */}
                <div data-testid="wizard-device-type-section">
                  <label className="block text-sm text-white/90 mb-1">Device types to include</label>
                  <p className="text-xs text-white/50 mb-3">
                    Intune reports Cloud PCs, Azure VMs and AVD session hosts all as virtual machines. Most
                    rollouts keep Cloud PCs (they are user desktops) and leave Azure VMs and AVD session
                    hosts out, because those are rebuilt from an image rather than upgraded in place.
                  </p>
                  <DeviceTypeScopeSelector
                    catalogue={eligibilityData?.virtualisation_catalogue}
                    counts={eligibilityData?.summary?.virtualisation_counts}
                    value={config.excluded_virtualisation_types || []}
                    onChange={(next) => setConfig({ ...config, excluded_virtualisation_types: next })}
                    testIdPrefix="wizard-device-type"
                    note={
                      (config.excluded_virtualisation_types || []).length > 0 ? (
                        <p className="text-xs text-white/45 mt-2">
                          The eligible count above is re-measured with this choice applied — the number
                          on screen is the number the sprints are built from.
                        </p>
                      ) : null
                    }
                  />
                </div>

              {/* Phase 2 — Hardware / Firmware subsection (§9.4). Off by
                  default; when on, devices whose latest catalogued BIOS
                  release date is older than the picked date are excluded
                  before sprint allocation. */}
              <div
                className="rounded-lg border border-cyan-500/20 bg-cyan-500/5 p-4 space-y-3"
                data-testid="wizard-firmware-gate-section"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Cpu className="w-4 h-4 text-cyan-400" />
                    <span className="text-white font-medium">Hardware / Firmware</span>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={config.firmware_gate_enabled}
                      onChange={(e) => setConfig({
                        ...config,
                        firmware_gate_enabled: e.target.checked,
                        // Auto-fill the date the first time the gate is enabled,
                        // unless the admin already typed something.
                        min_firmware_release_date: e.target.checked
                          ? (config.min_firmware_release_date
                              || firmwareDateDefaults[config.target_version]
                              || '')
                          : config.min_firmware_release_date,
                      })}
                      className="w-4 h-4 rounded border-cyan-500/40 bg-[rgba(229,239,250,0.1)] text-cyan-500"
                      data-testid="wizard-firmware-gate-toggle"
                    />
                    <span className="text-white/90 text-sm">
                      Require minimum firmware release date
                    </span>
                  </label>
                </div>
                <p className="text-white/60 text-xs">
                  When enabled, devices whose latest available BIOS release date in the
                  central OEM catalogue is older than this date will be excluded from
                  this rollout. Default for {config.target_version || 'the selected OS version'}
                  {firmwareDateDefaults[config.target_version]
                    ? `: ${firmwareDateDefaults[config.target_version]}.`
                    : ': none.'}
                </p>
                {config.firmware_gate_enabled && (
                  <div className="flex items-center gap-3">
                    <label className="text-white/80 text-sm whitespace-nowrap">
                      Minimum BIOS release date
                    </label>
                    <Input
                      type="date"
                      value={config.min_firmware_release_date}
                      onChange={(e) => setConfig({
                        ...config,
                        min_firmware_release_date: e.target.value,
                      })}
                      className="bg-[rgba(229,239,250,0.1)] border-cyan-500/30 text-white max-w-[200px]"
                      data-testid="wizard-firmware-gate-date"
                    />
                    {firmwareDateDefaults[config.target_version]
                      && config.min_firmware_release_date !== firmwareDateDefaults[config.target_version] && (
                      <button
                        type="button"
                        onClick={() => setConfig({
                          ...config,
                          min_firmware_release_date: firmwareDateDefaults[config.target_version],
                        })}
                        className="text-cyan-400 hover:text-cyan-300 text-xs underline"
                        data-testid="wizard-firmware-gate-reset"
                      >
                        Reset to default
                      </button>
                    )}
                  </div>
                )}
              </div>

                {/* Bug fix 2 (01/09) — the "Order by App Risk Categories" and
                    "Include App-based Risk" toggles are gone. They let a
                    Staged deployment silently allocate on app criticality,
                    which is the App Risk deployment's job. The two modes are
                    now fixed rule sets, stated below rather than configurable. */}
                <div
                  className={`rounded-lg border p-3 text-xs leading-relaxed ${
                    appRiskMode
                      ? 'bg-amber-500/5 border-amber-500/20 text-amber-100/80'
                      : 'bg-purple-500/5 border-purple-500/20 text-purple-100/80'
                  }`}
                  data-testid="wizard-mode-rules"
                >
                  {appRiskMode ? (
                    <>
                      <span className="font-medium text-amber-300">App Risk rule set:</span>{' '}
                      sprints are ordered by <span className="font-medium">App Business Criticality</span> first
                      (Low → Medium → High), falling back to <span className="font-medium">Readiness Risk</span> for
                      devices with no app-risk signal. Readiness High Risk devices are excluded.{' '}
                      <span className="font-medium">Free disk space is not used as a gate</span> in this mode.
                    </>
                  ) : (
                    <>
                      <span className="font-medium text-purple-300">Staged rule set:</span>{' '}
                      sprints are allocated from <span className="font-medium">Deployment Sprint + Readiness Risk</span> and
                      devices must clear the <span className="font-medium">{config.min_disk_space_gb} GB free disk</span>{' '}
                      check{config.firmware_gate_enabled ? ' and the firmware release-date gate' : ' (firmware date gate optional, above)'}.{' '}
                      <span className="font-medium">App business criticality is ignored</span> — use an App Risk
                      Deployment if you want application blast radius to drive the order.
                    </>
                  )}
                </div>
              </div>

              {wizardNav({ back: () => goStep(1), next: () => goStep(3), nextLabel: 'Continue to sprint design', blocker: scopeBlocker, testId: 'wizard-scope-next' })}
            </div>
          )}

          {/* Step 3: Sprint design */}
          {step === 3 && eligibilityData && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-white">Sprint design</h3>
                <p className="text-white/60 text-sm mt-1">How should the rollout be split? Sizing runs against the devices left in scope by the previous step.</p>
              </div>

              {/* Configuration Form */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm text-white/90 mb-1">Deployment Name</label>
                  <Input 
                    value={config.name}
                    onChange={(e) => setConfig({ ...config, name: e.target.value })}
                    className="bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] border-[rgba(229,239,250,0.1)] text-white"
                    placeholder="e.g., Win11 25H2 Rollout"
                  />
                </div>
                
                <div>
                  <label className="block text-sm text-white/90 mb-1">Number of Sprints</label>
                  <select
                    value={config.num_sprints}
                    onChange={(e) => {
                      // A ramp typed for three sprints means nothing at five, so
                      // let the prefill re-spread it. "Reset to the default ramp"
                      // is there for anyone who wants it back without this.
                      setSizingDirty(false);
                      setConfig({ ...config, num_sprints: parseInt(e.target.value) });
                    }}
                    className="w-full bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] border border-[rgba(229,239,250,0.1)] text-white rounded-md px-3 py-2"
                  >
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => (
                      <option key={n} value={n} className="bg-slate-900">{n} Sprint{n > 1 ? 's' : ''}</option>
                    ))}
                  </select>
                </div>

                {/* 07/09 — the Completion Threshold input used to sit here,
                    beside Number of Sprints and a long way above the
                    progression choice that consumes it. Picking "Fully
                    automated" showed the percentage in the option text with no
                    field next to it to change, so it read as a fixed 60%.
                    Moved into the progression panel, where "Fixed duration"
                    already reveals its own days field. */}

                <div>
                  <label className="block text-sm text-white/90 mb-1">Test Group Size (Optional)</label>
                  <Input 
                    type="number"
                    min="0"
                    max={eligibilityData.eligible_count}
                    value={config.test_group_size}
                    onChange={(e) => setConfig({ ...config, test_group_size: parseInt(e.target.value) || 0 })}
                    className="bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] border-[rgba(229,239,250,0.1)] text-white w-24"
                  />
                  <p className="text-xs text-white/70 mt-1">Devices to deploy first as test (0 = no test group)</p>
                  {/* 02/09 — the POC gate. Without this the test group rolls
                      into Sprint 1 on a threshold or a timer while the customer
                      is still deciding whether it went well. */}
                  {config.test_group_size > 0 && (
                    <label className="flex items-start gap-2 cursor-pointer mt-2" data-testid="wizard-require-test-approval">
                      <input
                        type="checkbox"
                        checked={config.require_test_approval}
                        onChange={(e) => setConfig({ ...config, require_test_approval: e.target.checked })}
                        className="w-4 h-4 mt-0.5 rounded border-[rgba(229,239,250,0.1)] bg-[rgba(229,239,250,0.1)] text-green-500"
                      />
                      <span className="text-xs text-white/80">
                        Hold until the test group is signed off
                        <span className="block text-white/50 mt-0.5">
                          Sprint 1 will not start automatically until someone approves the test group on the
                          deployment card. Advancing by hand still works.
                        </span>
                      </span>
                    </label>
                  )}
                </div>
              </div>

              {/* Sprint sizing (01/09 round 3).
                  The default pilot model puts clamp(5%, 2, 50) in sprint 1 and
                  splits the rest EVENLY — on 15k devices across 12 sprints
                  that is 50 then eleven sprints of ~1,359, which is not a
                  controlled ramp. Explicit counts let a 25 → 50 → 100 → 350 →
                  500 ramp be typed directly. Percentage stays the default
                  because most customers prefer it. */}
              <div
                className="rounded-lg border border-[rgba(229,239,250,0.15)] bg-[rgba(229,239,250,0.05)] p-4 space-y-3"
                data-testid="wizard-sizing-section"
              >
                <div className="flex items-center gap-2">
                  <Layers className="w-4 h-4 text-purple-400" />
                  <span className="text-white font-medium">Sprint sizing</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {[
                    { v: 'percentage', label: 'Percentage ramp (default)', desc: 'Share of the fleet per sprint. Prefilled with a 5% pilot then an even split — edit any of them.' },
                    { v: 'explicit', label: 'Explicit device counts', desc: 'Exact machines per sprint. Prefilled with the same ramp, e.g. for a 25 / 50 / 100 / 350 climb.' },
                  ].map(opt => (
                    <label
                      key={opt.v}
                      className={`flex flex-col gap-1 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                        config.sizing_mode === opt.v
                          ? 'border-purple-500 bg-purple-500/10'
                          : 'border-[rgba(229,239,250,0.1)] hover:border-white/30 bg-[rgba(229,239,250,0.05)]'
                      }`}
                      data-testid={`wizard-sizing-${opt.v}`}
                    >
                      <input
                        type="radio"
                        name="wizard_sizing_mode"
                        value={opt.v}
                        checked={config.sizing_mode === opt.v}
                        onChange={() => setConfig({ ...config, sizing_mode: opt.v })}
                        className="sr-only"
                      />
                      <span className="text-white text-sm font-medium">{opt.label}</span>
                      <span className="text-white/50 text-xs leading-snug">{opt.desc}</span>
                    </label>
                  ))}
                </div>

                {/* 10/09 round 2 — the ramp is prefilled in both units and
                    editable in both. `sizingDirty` stops the prefill effect
                    overwriting a number the admin has typed; changing the sprint
                    count clears it, because a ramp typed for three sprints means
                    nothing at five. */}
                {sizingDirty && (
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={resetSizingToDefault}
                      className="text-purple-300 hover:text-purple-200 text-xs underline"
                      data-testid="wizard-sizing-reset"
                    >
                      Reset to the default ramp
                    </button>
                  </div>
                )}

                {config.sizing_mode === 'percentage' && (() => {
                  const pcts = config.wave_percentages || [];
                  const S = config.num_sprints;
                  // Take the counts from the same function the Sprint
                  // Distribution preview uses, so the hint under each box and
                  // the panel below it can never disagree — including while the
                  // ramp is still prefilled and the backend's own model applies.
                  const waves = calculateWaveDevices();
                  const counts = Array.from({ length: S }, (_, i) => waves[i]?.devices ?? 0);
                  const beforeLast = pcts
                    .slice(0, Math.max(0, S - 1))
                    .reduce((a, b) => a + (Number(b) || 0), 0);
                  const lastPct = Math.max(0, Math.round((100 - beforeLast) * 100) / 100);
                  return (
                    <div className="space-y-3" data-testid="wizard-percentage-editor">
                      <div className="flex flex-wrap gap-2">
                        {Array.from({ length: S }, (_, i) => {
                          const isLast = i === S - 1;
                          return (
                            <div key={i} className="flex flex-col gap-1">
                              <label className="text-[11px] text-white/60">
                                Sprint {i + 1}{isLast ? ' (rest)' : ''}
                              </label>
                              <div className="flex items-center gap-1">
                                <Input
                                  type="number"
                                  min="0"
                                  max="100"
                                  step="0.1"
                                  disabled={isLast}
                                  value={isLast ? lastPct : (pcts[i] ?? '')}
                                  onChange={(e) => {
                                    const next = Array.from({ length: S }, (_, j) => pcts[j] ?? 0);
                                    next[i] = e.target.value;
                                    setSizingDirty(true);
                                    setConfig({ ...config, wave_percentages: next });
                                  }}
                                  className="bg-[rgba(229,239,250,0.1)] border-[rgba(229,239,250,0.1)] text-white w-20 disabled:opacity-60"
                                  data-testid={`wizard-wave-percentage-${i + 1}`}
                                />
                                <span className="text-white/50 text-xs">%</span>
                              </div>
                              <span className="text-[11px] text-white/40">
                                {counts[i] ?? 0} device{(counts[i] ?? 0) === 1 ? '' : 's'}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                      <p className="text-xs text-white/50" data-testid="wizard-percentage-summary">
                        Percentages are of the {sizingPool} device{sizingPool === 1 ? '' : 's'} the sprints
                        divide — eligible devices, less the test group and anything excluded by hand. The last
                        sprint takes whatever is left, so the column always adds up.
                        {beforeLast > 100 && (
                          <span className="block text-amber-300 mt-1" data-testid="wizard-percentage-overflow">
                            The first {S - 1} sprint{S - 1 === 1 ? '' : 's'} already total {Math.round(beforeLast * 100) / 100}%
                            — the last sprint will come back empty.
                          </span>
                        )}
                      </p>
                    </div>
                  );
                })()}

                {config.sizing_mode === 'explicit' && (() => {
                  const counts = config.wave_device_counts || [];
                  const planned = Array.from({ length: config.num_sprints }, (_, i) =>
                    parseInt(counts[i], 10) || 0);
                  const allocatedBeforeLast = planned
                    .slice(0, Math.max(0, config.num_sprints - 1))
                    .reduce((a, b) => a + b, 0);
                  const lastSprint = Math.max(0, sizingPool - allocatedBeforeLast);
                  return (
                    <div className="space-y-3">
                      <div className="flex flex-wrap gap-2">
                        {Array.from({ length: config.num_sprints }, (_, i) => (
                          <div key={i} className="flex flex-col gap-1">
                            <label className="text-[11px] text-white/60">
                              Sprint {i + 1}{i === config.num_sprints - 1 ? ' (rest)' : ''}
                            </label>
                            <Input
                              type="number"
                              min="0"
                              disabled={i === config.num_sprints - 1}
                              value={i === config.num_sprints - 1 ? lastSprint : (counts[i] ?? '')}
                              placeholder="0"
                              onChange={(e) => {
                                const next = Array.from({ length: config.num_sprints }, (_, j) => counts[j] ?? '');
                                next[i] = e.target.value;
                                setSizingDirty(true);
                                setConfig({ ...config, wave_device_counts: next });
                              }}
                              className="bg-[rgba(229,239,250,0.1)] border-[rgba(229,239,250,0.1)] text-white w-24 disabled:opacity-60"
                              data-testid={`wizard-wave-count-${i + 1}`}
                            />
                          </div>
                        ))}
                      </div>
                      <p className="text-xs text-white/50" data-testid="wizard-sizing-summary">
                        Prefilled with the default ramp over the {sizingPool} device
                        {sizingPool === 1 ? '' : 's'} the sprints divide. The last sprint always takes whatever
                        is left ({lastSprint} device{lastSprint === 1 ? '' : 's'}), so no device is dropped by a
                        short list.
                        {allocatedBeforeLast > sizingPool && (
                          <span className="block text-amber-300 mt-1">
                            Your counts total more than the {sizingPool} devices available — later sprints will
                            come back empty.
                          </span>
                        )}
                      </p>
                    </div>
                  );
                })()}
              </div>

                {/* 09/09 — model spread. */}
                <div data-testid="wizard-model-spread-section">
                  <label className="flex items-start gap-3 cursor-pointer" data-testid="wizard-spread-models">
                    <input
                      type="checkbox"
                      checked={config.spread_models_across_sprints !== false}
                      onChange={(e) => setConfig({ ...config, spread_models_across_sprints: e.target.checked })}
                      className="w-4 h-4 mt-0.5 rounded border-[rgba(229,239,250,0.1)] bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] text-purple-500"
                    />
                    <span className="text-white text-sm">
                      Spread device models across sprints
                      <span className="block text-xs text-white/50 mt-0.5">
                        On by default. Machines bought and enrolled in one batch sit together in the
                        allocation, so without this a sprint can be almost entirely one model — which
                        defeats the point of going in waves. With it on, every sprint gets a proportional
                        mix, so a model-specific driver or firmware problem shows up early, on a small
                        number of machines, and is attributable to the model. Within a model the freshest
                        check-ins still go first.
                      </span>
                      {config.spread_models_across_sprints === false && (
                        <span className="block text-xs text-amber-300 mt-0.5">
                          Off — sprints will be filled in Deployment Sprint then check-in order, which tends to
                          group identical hardware together.
                        </span>
                      )}
                    </span>
                  </label>
                </div>

              {/* Sprint Phase Preview */}
              <div>
                <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-purple-400" />
                  Sprint Distribution
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {config.test_group_size > 0 && (
                    <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-cyan-400 font-medium">Test Group</span>
                        <span className="text-white">{config.test_group_size} devices</span>
                      </div>
                      <div className="h-2 bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] rounded-full overflow-hidden">
                        <div className="h-full bg-cyan-500 rounded-full" style={{ width: '100%' }} />
                      </div>
                    </div>
                  )}
                  {calculateWaveDevices().map((wave) => (
                    <div key={wave.wave} className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-purple-400 font-medium">Sprint {wave.wave}</span>
                        <span className="text-white">{wave.devices} devices ({wave.percentage}%)</span>
                      </div>
                      <div className="h-2 bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-purple-500 rounded-full" 
                          style={{ width: `${wave.percentage}%` }} 
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* 02/09 — the phase/app-risk distribution previews are removed.
                  Sprint Distribution above already shows how the devices split,
                  and against the real allocator rather than a client-side
                  re-implementation of it. Two views of the same thing invite
                  the question of which one is right. */}

              {/* Device Phase / App Risk Mode Info */}
              <div className={`rounded-lg border p-3 ${appRiskMode ? 'bg-amber-500/5 border-amber-500/20' : 'bg-blue-500/5 border-blue-500/20'}`}>
                <div className="flex items-start gap-2">
                  <Info className={`w-4 h-4 mt-0.5 ${appRiskMode ? 'text-amber-400' : 'text-blue-400'}`} />
                  <div className="text-sm">
                    {appRiskMode ? (
                      <>
                        <p className="text-amber-400 font-medium mb-1">App Risk Categorisation Mode</p>
                        <p className="text-white/70">
                          Test phase uses <span className="text-blue-400 font-medium">Blue</span> phase devices.
                          Sprints ordered by app criticality: Low → Medium → High, with{' '}
                          <span className="text-white/90 font-medium">Readiness Risk as the fallback</span> for devices
                          carrying no app-risk signal. Free disk space is <span className="text-white/90 font-medium">not</span> a
                          gate in this mode.
                        </p>
                      </>
                    ) : (
                      <>
                        <p className="text-blue-400 font-medium mb-1">Device Sprint Categorisation</p>
                        <p className="text-white/70">
                          Devices assigned to sprints by their Deployment Sprint set on the Devices page, gated on Readiness
                          Risk and free disk space. App business criticality is not used:
                        </p>
                        <div className="flex flex-wrap gap-3 mt-2">
                          {Object.entries(PHASE_CONFIG).map(([phase, cfg]) => (
                            <span key={phase} className="flex items-center gap-1.5">
                              <span className={`w-2.5 h-2.5 rounded-full ${cfg.color}`} />
                              <span className={`text-xs ${cfg.textColor}`}>{cfg.label}</span>
                            </span>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {wizardNav({ back: () => goStep(2), next: () => goStep(4), nextLabel: 'Continue to progression', blocker: designBlocker, testId: 'wizard-design-next' })}
            </div>
          )}

          {/* Step 4: Sprint progression */}
          {step === 4 && eligibilityData && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-white">Sprint progression</h3>
                <p className="text-white/60 text-sm mt-1">When should the next sprint begin? This clock moves devices that have not been touched yet — it is not the one that enforces an install.</p>
              </div>

              {/* Sprint progression (01/09 round 2; renamed 07/09).
                  Progression used to be threshold-only, and there was no date
                  anywhere in the module — so "deploy optional, comms, 10 days,
                  then required" could not be expressed.

                  07/09 — this panel is now ONLY about starting the next
                  sprint. The Optional→Required window moved to Installation type,
                  where the Optional choice is made (10/09: its own step, the next
                  one). Holding both in one box, with the progression radio sat
                  between them, is what made people read the escalation as
                  something that happens "at progression". It does not. */}
              <div
                className="rounded-lg border border-indigo-500/25 bg-indigo-500/[0.06] p-4 space-y-4"
                data-testid="wizard-sprint-schedule-section"
              >
                {/* 02/09 — "sprint duration" and "optional window" were read as
                    the same setting. They are two different clocks on two
                    different subjects, so say what each one moves before
                    asking for a number. */}
                <div className="rounded-md bg-black/15 border border-white/10 p-3 text-xs text-white/70 leading-relaxed">
                  <p className="mb-1">
                    <span className="text-indigo-300 font-medium">Progression</span> decides when
                    <span className="text-white/90"> the next sprint</span> starts. It affects devices that
                    have not been touched yet.
                  </p>
                  <p className="text-white/55">
                    It is a separate clock from the
                    <span className="text-cyan-300"> optional window</span> on the next step, which decides when
                    a sprint stops asking and installs itself for the devices already in it. A sprint can become
                    Required without the next one starting, and the next one can start while this one is still
                    optional.
                  </p>
                  <p className="mt-1.5 text-white/45">
                    They are usually different numbers — a ten-day optional window inside a fourteen-day
                    sprint gives four days for the enforced installs to land before the next group begins.
                  </p>
                </div>

                <div>
                  <label className="block text-sm text-white/90 mb-2">
                    <span className="text-indigo-300">How does the next sprint start?</span>
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                    {[
                      // 02/09 — "Either, whichever first" is replaced by
                      // "Manual". Two automatic rules plus a fully manual one
                      // is a clearer choice than three overlapping automatic
                      // ones: ALICE decides on a number, on a date, or the
                      // customer decides.
                      //
                      // 07/09 — named for what they DO, not for the field they
                      // read. "Completion threshold" and "Fixed duration" are
                      // both automatic, which the old labels did not say, so
                      // neither read as the fully-automated rollout.
                      {
                        v: 'threshold',
                        label: 'Fully automated',
                        tag: 'Completion threshold',
                        desc: `The next sprint starts on its own once this one reaches ${config.completion_threshold}% on the target version. No dates, no clicks.`,
                      },
                      {
                        v: 'deadline',
                        label: 'Fixed duration',
                        tag: 'Every N days',
                        desc: `The next sprint starts ${config.sprint_duration_days} days after this one went live, whatever the completion figure.`,
                      },
                      {
                        v: 'manual',
                        label: 'Manual',
                        tag: 'You activate each sprint',
                        desc: 'Nothing starts on its own. You press Advance Sprint on the deployment card, and set that sprint’s offer date as you do.',
                      },
                    ].map(opt => (
                      <label
                        key={opt.v}
                        className={`flex flex-col gap-1 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                          config.progression_mode === opt.v
                            ? 'border-indigo-400 bg-indigo-500/15'
                            : 'border-[rgba(229,239,250,0.1)] hover:border-white/30 bg-[rgba(229,239,250,0.05)]'
                        }`}
                        data-testid={`wizard-progression-${opt.v}`}
                      >
                        <input
                          type="radio"
                          name="wizard_progression_mode"
                          value={opt.v}
                          checked={config.progression_mode === opt.v}
                          onChange={() => setConfig({ ...config, progression_mode: opt.v })}
                          className="sr-only"
                        />
                        <span className="text-white text-sm font-medium">{opt.label}</span>
                        <span className="text-indigo-200/70 text-[11px] uppercase tracking-wide">{opt.tag}</span>
                        <span className="text-white/50 text-xs leading-snug">{opt.desc}</span>
                      </label>
                    ))}
                  </div>
                  {/* 07/09 — say plainly where "fully automated" is gated, so
                      nobody sets it and then waits for a hand-over that is
                      deliberately being held for POC sign-off. */}
                  {config.progression_mode !== 'manual' && (
                    <p className="text-xs text-white/45 mt-2">
                      {config.test_group_size > 0 && config.require_test_approval
                        ? 'Sprint 1 onwards hands over automatically, but the test group is held until you approve it — a pilot is there to be judged, not timed out. Untick "Hold until the test group is signed off" on the sprint design step to automate that hand-over too.'
                        : 'Every hand-over is automatic. Nobody needs to touch the deployment once it is created.'}
                    </p>
                  )}
                </div>

                {config.progression_mode === 'threshold' && (
                  <div className="flex items-center gap-3 flex-wrap">
                    <label className="text-sm text-white/90 whitespace-nowrap">Start the next sprint at (% complete)</label>
                    <Input
                      type="number"
                      min="10"
                      max="100"
                      value={config.completion_threshold}
                      onChange={(e) => setConfig({
                        ...config,
                        completion_threshold: parseInt(e.target.value) || 60,
                      })}
                      className="bg-[rgba(229,239,250,0.1)] border-[rgba(229,239,250,0.1)] text-white w-24"
                      data-testid="wizard-completion-threshold"
                    />
                    <span className="text-xs text-white/50">
                      Percentage of this sprint&apos;s devices on the target version. 60% is the usual choice.
                    </span>
                  </div>
                )}

                {config.progression_mode === 'deadline' && (
                  <div className="flex items-center gap-3 flex-wrap">
                    <label className="text-sm text-white/90 whitespace-nowrap">Start the next sprint after (days)</label>
                    <Input
                      type="number"
                      min="1"
                      max="365"
                      value={config.sprint_duration_days}
                      onChange={(e) => setConfig({
                        ...config,
                        sprint_duration_days: parseInt(e.target.value) || 1,
                      })}
                      className="bg-[rgba(229,239,250,0.1)] border-[rgba(229,239,250,0.1)] text-white w-24"
                      data-testid="wizard-sprint-duration-days"
                    />
                    <span className="text-xs text-white/50">
                      Counted from the moment each sprint activates, not from deployment creation.
                    </span>
                  </div>
                )}
              </div>

              {wizardNav({ back: () => goStep(3), next: () => goStep(5), nextLabel: 'Continue to installation', blocker: null, testId: 'wizard-progression-next' })}
            </div>
          )}

          {/* Step 5: Installation &amp; communications */}
          {step === 5 && eligibilityData && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-white">Installation &amp; communications</h3>
                <p className="text-white/60 text-sm mt-1">What does a sprint do while it is live, and what are its users told? The enforcement deadline is counted from the comms date, so both are set here together.</p>
              </div>

              {/* Installation Type + the Optional→Required window.
                  07/09 — the enforcement window used to live inside the
                  "Sprint schedule" panel, directly under the Progression
                  radio, which made it read as "flip to Required when the next
                  sprint starts". It does not: it is a per-sprint clock that has
                  nothing to do with progression. Moved here, under the Optional
                  choice it belongs to, so the two are never read as one. */}
              <div
                className="rounded-lg border border-cyan-500/25 bg-cyan-500/[0.05] p-4 space-y-4"
                data-testid="wizard-installation-type-section"
              >
                <label className="block text-sm text-white/90">
                  <span className="text-cyan-300 font-medium">Installation type</span> — how the update
                  reaches the devices in a sprint
                </label>
                <div className="flex gap-3">
                  <label className={`flex-1 flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    config.installation_type === 'required'
                      ? 'border-purple-500 bg-purple-500/10'
                      : 'border-[rgba(229,239,250,0.1)] hover:border-white/30 bg-[rgba(229,239,250,0.05)]'
                  }`} data-testid="install-type-required">
                    <input type="radio" name="wizard_install_type" value="required"
                      checked={config.installation_type === 'required'}
                      onChange={() => setConfig({ ...config, installation_type: 'required' })}
                      className="sr-only" />
                    <Shield className="w-5 h-5 text-purple-400" />
                    <div>
                      <div className="text-white font-medium">Required</div>
                      <div className="text-white/50 text-xs">
                        Downloads and installs on its own, then prompts the user to restart
                      </div>
                    </div>
                  </label>
                  <label className={`flex-1 flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    config.installation_type === 'optional'
                      ? 'border-cyan-500 bg-cyan-500/10'
                      : 'border-[rgba(229,239,250,0.1)] hover:border-white/30 bg-[rgba(229,239,250,0.05)]'
                  }`} data-testid="install-type-optional">
                    <input type="radio" name="wizard_install_type" value="optional"
                      checked={config.installation_type === 'optional'}
                      onChange={() => setConfig({ ...config, installation_type: 'optional' })}
                      className="sr-only" />
                    <Info className="w-5 h-5 text-cyan-400" />
                    <div>
                      <div className="text-white font-medium">Optional</div>
                      <div className="text-white/50 text-xs">
                        Waits in Windows Update for the user to start it; Windows notifies them it is there
                      </div>
                    </div>
                  </label>
                </div>
                <p className="text-xs text-white/50">
                  Applied to every sprint at creation. Each sprint can then be set independently on the
                  deployment card — that is how you enforce Sprint 1 while Sprint 3 is still optional.
                </p>

                {/* The enforcement window. Belongs to Installation type, not to
                    Progression: it decides when THIS sprint stops asking and
                    starts installing, for the devices already in it. */}
                <div className="pt-3 border-t border-white/10 space-y-2">
                  <label className="flex items-start gap-3 cursor-pointer" data-testid="wizard-auto-required-toggle">
                    <input
                      type="checkbox"
                      checked={config.auto_required_after_days > 0}
                      onChange={(e) => setConfig({
                        ...config,
                        auto_required_after_days: e.target.checked ? 10 : 0,
                      })}
                      className="w-4 h-4 mt-0.5 rounded border-[rgba(229,239,250,0.1)] bg-[rgba(229,239,250,0.1)] text-cyan-500"
                    />
                    <span className="text-white text-sm">
                      Automatically make a sprint Required once its optional window closes
                      <span className="block text-xs text-white/50 mt-0.5">
                        The standard rollout: the sprint goes live Optional, you send comms with a deadline, and
                        ALICE switches <span className="text-white/80">that sprint</span> to Required when the
                        deadline passes. Nothing else moves — later sprints keep their own clocks. Leave off to
                        flip each sprint by hand.
                      </span>
                    </span>
                  </label>

                  {/* 07/09 — this used to be hidden entirely on a Required
                      deployment ("nothing to escalate"), and the value was
                      discarded on save. But installation type is per sprint:
                      switch Sprint 3 back to Optional later and it had no
                      deadline and no way to be given one. The window is the
                      deployment's policy for whichever sprints are optional. */}
                  {config.installation_type !== 'optional' && config.auto_required_after_days > 0 && (
                    <p className="text-xs text-white/45 pl-7" data-testid="wizard-auto-required-required-note">
                      This deployment starts Required, so there is nothing to escalate today. The window is kept
                      as the deployment&apos;s policy — switch a sprint back to Optional from the card later and
                      this is the deadline it gets.
                    </p>
                  )}

                  {config.auto_required_after_days > 0 && (
                    <div className="flex items-center gap-3 flex-wrap pl-7">
                      <label className="text-sm text-white/90 whitespace-nowrap">Keep a sprint optional for (days)</label>
                      <Input
                        type="number"
                        min="1"
                        max="365"
                        value={config.auto_required_after_days}
                        onChange={(e) => setConfig({
                          ...config,
                          auto_required_after_days: parseInt(e.target.value) || 1,
                        })}
                        className="bg-[rgba(229,239,250,0.1)] border-[rgba(229,239,250,0.1)] text-white w-24"
                        data-testid="wizard-auto-required-days"
                      />
                      {config.progression_mode === 'deadline'
                        && config.auto_required_after_days >= config.sprint_duration_days && (
                        <span className="text-xs text-amber-300" data-testid="wizard-auto-required-warning">
                          This is not shorter than the {config.sprint_duration_days}-day sprint duration, so the
                          next sprint starts before (or as) this one becomes Required.
                        </span>
                      )}
                    </div>
                  )}

                  {/* 01/09 round 3 — WHERE the window counts from. Anchoring
                      to activation forces devices ~2 days before the deadline
                      the users were actually given, because the sprint goes
                      live before the comms go out. */}
                  {config.auto_required_after_days > 0 && (
                    <div className="pl-7 space-y-2">
                      <span className="text-xs text-white/70">Counted from</span>
                      <div className="flex flex-col gap-2">
                        {[
                          {
                            v: 'comms',
                            label: 'The day the users were told',
                            desc: 'Recorded when ALICE sends the comms, or entered by hand if the customer sends their own. A sprint with no comms date never auto-escalates.',
                          },
                          {
                            v: 'activation',
                            label: 'The day the sprint activated',
                            desc: 'Simpler, but the clock starts before anyone has been told — the deadline will land early.',
                          },
                        ].map(opt => (
                          <label
                            key={opt.v}
                            className={`flex flex-col gap-0.5 p-2.5 rounded-lg border cursor-pointer transition-all ${
                              config.auto_required_anchor === opt.v
                                ? 'border-cyan-400/60 bg-cyan-500/10'
                                : 'border-[rgba(229,239,250,0.1)] hover:border-white/25 bg-[rgba(229,239,250,0.04)]'
                            }`}
                            data-testid={`wizard-required-anchor-${opt.v}`}
                          >
                            <input
                              type="radio"
                              name="wizard_required_anchor"
                              value={opt.v}
                              checked={config.auto_required_anchor === opt.v}
                              onChange={() => setConfig({ ...config, auto_required_anchor: opt.v })}
                              className="sr-only"
                            />
                            <span className="text-white text-sm font-medium">{opt.label}</span>
                            <span className="text-white/50 text-xs leading-snug">{opt.desc}</span>
                          </label>
                        ))}
                      </div>
                      {/* 09/09 — this warning used to be the whole story: the
                          clock could only be started by hand. It now depends on
                          whether the announcement is on, so say which case the
                          admin is actually in. */}
                      {config.auto_required_anchor === 'comms' && (
                        config.announce_on_activation ? (
                          <p className="text-xs text-emerald-300/90" data-testid="wizard-required-anchor-comms-ok">
                            The activation announcement below starts this clock automatically, on each sprint,
                            on the day its users are emailed.
                          </p>
                        ) : (
                          <p className="text-xs text-amber-300/90" data-testid="wizard-required-anchor-comms-warning">
                            With the announcement below turned off, you must send the reminder from the sprint
                            (or use &quot;Set comms date&quot;) or the clock never starts — ALICE will not
                            force-install before the users have been told.
                          </p>
                        )
                      )}
                    </div>
                  )}

                  {/* 09/09 — the announcement itself. Lives here, with the
                      enforcement window, because it is the event that window
                      is measured from: "you have 10 days" is meaningless until
                      somebody has been told. */}
                  <div className="pt-3 mt-2 border-t border-white/10" data-testid="wizard-announcement-section">
                    <label className="flex items-start gap-3 cursor-pointer" data-testid="wizard-announce-toggle">
                      <input
                        type="checkbox"
                        checked={Boolean(config.announce_on_activation)}
                        onChange={(e) => setConfig({ ...config, announce_on_activation: e.target.checked })}
                        className="w-4 h-4 mt-0.5 rounded border-[rgba(229,239,250,0.1)] bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] text-cyan-500"
                      />
                      <span className="text-white text-sm">
                        Email each sprint&apos;s users when that sprint goes live
                        <span className="block text-xs text-white/50 mt-0.5">
                          Off by default. When a sprint activates, ALICE emails the primary user of every
                          device in it: the update is available, how to install it, and — for an Optional
                          sprint with a window — the date it becomes automatic. Users who have already
                          upgraded, and devices with no user on record, are skipped.
                          {' '}Set per sprint from the deployment card afterwards, so a pilot group briefed in
                          person can stay silent while the rest are mailed.
                        </span>
                      </span>
                    </label>

                    {config.announce_on_activation && (
                      <div className="pl-7 mt-3 space-y-3">
                        <div>
                          <label className="block text-xs uppercase tracking-wider text-white/60 mb-1">
                            Subject (optional)
                          </label>
                          <Input
                            value={config.announcement_subject}
                            onChange={(e) => setConfig({ ...config, announcement_subject: e.target.value })}
                            placeholder="Your PC has a Windows update available: {target_version}"
                            className="bg-[rgba(229,239,250,0.1)] border-[rgba(229,239,250,0.1)] text-white"
                            data-testid="wizard-announce-subject"
                          />
                        </div>
                        <div>
                          <label className="block text-xs uppercase tracking-wider text-white/60 mb-1">
                            Body HTML (optional)
                          </label>
                          <textarea
                            rows={4}
                            value={config.announcement_body_html}
                            onChange={(e) => setConfig({ ...config, announcement_body_html: e.target.value })}
                            placeholder="Leave blank to use the ALICE template."
                            className="w-full rounded-md bg-[rgba(229,239,250,0.1)] border border-[rgba(229,239,250,0.1)] text-white text-xs p-2 font-mono"
                            data-testid="wizard-announce-body"
                          />
                          <p className="text-[11px] text-white/40 mt-1">
                            Placeholders: <code>{'{display_name}'}</code> <code>{'{device_name}'}</code>{' '}
                            <code>{'{target_version}'}</code> <code>{'{deployment_name}'}</code>{' '}
                            <code>{'{phase_label}'}</code> <code>{'{action_line}'}</code>{' '}
                            <code>{'{deadline_block}'}</code>. Leave both blank for the standard template —
                            it already adapts to Optional vs Required and states the deadline.
                          </p>
                        </div>

                        {/* 09/09 — the actual email, rendered by the backend
                            through the same branded shell the send uses. A
                            textarea of raw HTML told an admin nothing about
                            what lands in a user's inbox — least of all whether
                            their own logo and colours are on it. */}
                        <AnnouncementPreview
                          subject={config.announcement_subject}
                          bodyHtml={config.announcement_body_html}
                          targetVersion={config.target_version}
                          deploymentName={config.name}
                          installationType={config.installation_type}
                          autoRequiredAfterDays={config.auto_required_after_days}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* 10/09 — offer scheduling moved out of the progression panel. It was
                  never about starting the NEXT sprint: it is the gap that lets Intune
                  register the offer before the users are emailed, so it belongs beside
                  the email settings it is timed against. */}
              <div
                className="rounded-lg border border-cyan-500/25 bg-cyan-500/[0.05] p-4 space-y-3"
                data-testid="wizard-offer-schedule-section"
              >
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-cyan-300" />
                  <span className="text-white font-medium">When each sprint starts offering</span>
                </div>
                {/* 01/09 round 3 — offer scheduling.
                    The profile and its group assignment land immediately; only
                    the OFFER is date-gated. That is the point: Intune's backend
                    registers the offer now, so a device that checks in before
                    the date already knows it is coming and the update is
                    waiting when the user reads the comms. Creating the profile
                    the same morning the comms go out is what produces "I have
                    no update showing" tickets. */}
                <div className="space-y-3">
                  <div className="flex items-center gap-3 flex-wrap">
                    <label className="text-sm text-white/90 whitespace-nowrap">
                      Start offering each sprint
                    </label>
                    <Input
                      type="number"
                      min="0"
                      max="30"
                      value={config.offer_lead_days}
                      onChange={(e) => setConfig({
                        ...config,
                        offer_lead_days: Math.max(0, parseInt(e.target.value) || 0),
                      })}
                      className="bg-[rgba(229,239,250,0.1)] border-[rgba(229,239,250,0.1)] text-white w-20"
                      data-testid="wizard-offer-lead-days"
                    />
                    <span className="text-sm text-white/90">day(s) after it activates</span>
                  </div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <label className="text-sm text-white/90 whitespace-nowrap">
                      Or offer the first sprint from
                    </label>
                    <Input
                      type="datetime-local"
                      value={config.offer_start_at}
                      onChange={(e) => setConfig({ ...config, offer_start_at: e.target.value })}
                      className="bg-[rgba(229,239,250,0.1)] border-[rgba(229,239,250,0.1)] text-white max-w-[230px]"
                      data-testid="wizard-offer-start-at"
                    />
                    {config.offer_start_at && (
                      <button
                        type="button"
                        onClick={() => setConfig({ ...config, offer_start_at: '' })}
                        className="text-cyan-400 hover:text-cyan-300 text-xs underline"
                        data-testid="wizard-offer-start-clear"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                  <p className="text-xs text-white/50">
                    Sets Intune&apos;s <span className="text-white/70">&quot;Make update available on a specific
                    date&quot;</span>. Create the sprint 24–48 hours before this date, and send the user comms
                    <span className="text-white/70"> 1–2 days after it</span> — that gap is what lets Intune&apos;s
                    backend register the offer, so the update is already waiting when people are told about it.
                    {config.offer_lead_days === 0 && !config.offer_start_at && (
                      <span className="block text-amber-300/90 mt-1">
                        Set to 0 with no date — sprints will offer as soon as possible, with no buffer before comms.
                      </span>
                    )}
                  </p>
                </div>
              </div>

              {wizardNav({ back: () => goStep(4), next: () => handlePreviewDeployment(), nextLabel: (loading ? 'Generating preview…' : 'Review & create'), blocker: null, testId: 'wizard-install-next' })}
            </div>
          )}

          {/* Step 6: Preview Report with Device Assignment & Exclusion */}
          {step === FU_WIZARD_LAST_STEP && previewData && (
            <div className="space-y-6">
              <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Info className="w-5 h-5 text-purple-400" />
                  <span className="text-purple-400 font-medium">Deployment Preview Report</span>
                </div>
                <p className="text-white/90 text-sm">Review which devices will be assigned to each deployment sprint. You can exclude individual devices (e.g. VIP machines) before proceeding.</p>
                {/* 01/09 round 3 — the sprint plan export. This is the artefact
                    that goes to the customer for sign-off before anything is
                    created in Intune, replacing the merged Power BI extract. */}
                <div className="mt-3 flex items-center gap-3 flex-wrap">
                  <Button
                    size="sm"
                    onClick={handleExportSprintPlan}
                    className="bg-cyan-500/20 border border-cyan-400/40 text-cyan-100 hover:bg-cyan-500/30"
                    data-testid="preview-export-sprint-plan-btn"
                  >
                    <Download className="w-3.5 h-3.5 mr-1.5" />
                    Export sprint plan (CSV)
                  </Button>
                  <span className="text-xs text-white/50">
                    Every device with its sprint, user, model, readiness, disk and reason — send this for approval
                    before creating the deployment.
                  </span>
                </div>
              </div>

              {/* Summary Cards */}
              <div className="alice-kpi-row grid grid-cols-2 md:grid-cols-5 gap-3">
                <div className="bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-white" data-testid="preview-eligible-count">{previewData.eligibility_summary?.eligible || 0}</div>
                  <div className="text-xs text-white/90">Eligible</div>
                </div>
                <div className="bg-cyan-500/10 border border-cyan-500/20 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-cyan-400" data-testid="preview-test-count">{previewData.test_group?.device_count || 0}</div>
                  <div className="text-xs text-white/90">Test Group</div>
                </div>
                <div className="bg-purple-500/10 border border-purple-500/20 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-purple-400" data-testid="preview-sprint-count">{previewData.sprint_waves?.length || 0}</div>
                  <div className="text-xs text-white/90">Sprints</div>
                </div>
                <div className="bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-white">{previewData.groups_to_create?.length || 0}</div>
                  <div className="text-xs text-white/90">Groups</div>
                </div>
                <div className={`rounded-lg p-3 text-center ${excludedDeviceIds.length > 0 ? 'bg-amber-500/10 border border-amber-500/20' : 'bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px]'}`}>
                  <div className={`text-2xl font-bold ${excludedDeviceIds.length > 0 ? 'text-amber-400' : 'text-white/50'}`} data-testid="preview-excluded-count">{excludedDeviceIds.length}</div>
                  <div className="text-xs text-white/90">Excluded</div>
                </div>
              </div>

              {/* Search bar */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/50" />
                <Input
                  value={previewSearch}
                  onChange={(e) => setPreviewSearch(e.target.value)}
                  placeholder="Search devices by name or user..."
                  className="pl-10 bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] border-[rgba(229,239,250,0.1)] text-white"
                  data-testid="preview-search-input"
                />
              </div>

              {/* Excluded Devices Panel */}
              {excludedDeviceIds.length > 0 && previewData.manually_excluded?.length > 0 && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-amber-400 font-medium flex items-center gap-2">
                      <Shield className="w-4 h-4" />
                      Excluded Devices ({previewData.manually_excluded.length})
                    </h4>
                  </div>
                  <div className="space-y-2">
                    {previewData.manually_excluded.map((device) => (
                      <div key={device.device_id} className="flex items-center justify-between bg-amber-500/5 rounded-lg px-3 py-2">
                        <div className="flex items-center gap-3">
                          <Monitor className="w-4 h-4 text-amber-400/60" />
                          <div>
                            <span className="text-white text-sm">{device.device_name}</span>
                            {device.user_name && <span className="text-white/50 text-xs ml-2">{device.user_name}</span>}
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleReincludeDevice(device.device_id, device.device_name)}
                          className="text-amber-400 hover:text-amber-300 hover:bg-amber-500/20 h-7 px-2 text-xs"
                          data-testid={`reinclude-device-${device.device_id}`}
                        >
                          <Plus className="w-3 h-3 mr-1" />
                          Re-include
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Phase-by-Phase Device Assignment Report */}
              <div className="space-y-3">
                <h4 className="text-white font-medium flex items-center gap-2">
                  <Layers className="w-4 h-4 text-purple-400" />
                  Device Assignment by Sprint
                </h4>

                {/* Test Group Phase */}
                {previewData.test_group?.enabled && (
                  <PhaseDeviceTable
                    phaseKey="test"
                    phaseName="Test Group"
                    phaseColor="cyan"
                    phaseIcon={<Target className="w-4 h-4" />}
                    devices={previewData.test_group.devices || []}
                    searchQuery={previewSearch}
                    expanded={expandedPhases['test']}
                    onToggle={() => togglePhase('test')}
                    onExclude={handleExcludeDevice}
                    loading={loading}
                    showAppRisk={appRiskMode}
                  />
                )}

                {/* Sprint Phases */}
                {previewData.sprint_waves?.map((wave, i) => (
                  <PhaseDeviceTable
                    key={wave.wave_number}
                    phaseKey={`sprint_${wave.wave_number}`}
                    phaseName={`Sprint ${wave.wave_number}`}
                    phaseColor="purple"
                    phaseIcon={<Rocket className="w-4 h-4" />}
                    devices={wave.devices || []}
                    percentage={wave.percentage}
                    searchQuery={previewSearch}
                    expanded={expandedPhases[`sprint_${wave.wave_number}`]}
                    onToggle={() => togglePhase(`sprint_${wave.wave_number}`)}
                    onExclude={handleExcludeDevice}
                    loading={loading}
                    showAppRisk={appRiskMode}
                  />
                ))}
              </div>

              {/* Azure AD Groups & Intune Profiles to Create */}
              <div className="bg-[rgba(229,239,250,0.05)] rounded-lg p-4">
                <h4 className="text-white/80 font-medium mb-3 text-sm">Resources to be created in Azure AD / Intune</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {previewData.groups_to_create?.map((group, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm text-white/70">
                      <Users className="w-3 h-3 text-purple-400/60" />
                      <span>{group.name}</span>
                      <span className="text-white/40">({group.device_count})</span>
                      {group.is_test_group && <span className="px-1.5 py-0.5 bg-cyan-500/20 text-cyan-400 text-[10px] rounded">TEST</span>}
                    </div>
                  ))}
                </div>
              </div>

              {/* High Risk / Medium Risk Excluded */}
              {(previewData.excluded_devices?.high_risk?.length > 0 || previewData.excluded_devices?.medium_risk?.length > 0) && (
                <div className="bg-red-500/5 border border-red-500/20 rounded-lg p-3">
                  <h4 className="text-red-400/80 font-medium mb-1 text-sm flex items-center gap-2">
                    <AlertTriangle className="w-3 h-3" />
                    Auto-Excluded (Risk)
                  </h4>
                  <div className="flex gap-4 text-xs text-white/60">
                    <span><span className="text-red-400">{previewData.excluded_devices?.high_risk?.length || 0}</span> High Risk</span>
                    <span><span className="text-amber-400">{previewData.excluded_devices?.medium_risk?.length || 0}</span> Medium Risk</span>
                  </div>
                </div>
              )}

              <div className="flex justify-between pt-2">
                <Button variant="outline" className="border-[rgba(229,239,250,0.1)] text-white" onClick={() => goStep(5)}>
                  Back
                </Button>
                <Button 
                  onClick={handleCreateDeployment} 
                  disabled={loading}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="create-deployment-btn"
                >
                  {loading ? 'Creating...' : 'Confirm & Create Deployment'}
                  <Rocket className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 7: Complete */}
          {step === FU_WIZARD_DONE_STEP && deploymentResult && (
            <div className="space-y-6 text-center py-8">
              <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto">
                <CheckCircle className="w-8 h-8 text-green-400" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-white mb-2">Deployment Created!</h3>
                <p className="text-white/90">{deploymentResult.deployment_name}</p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-2xl mx-auto">
                <div className="bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] rounded-lg p-3">
                  <div className="text-xl font-bold text-white">{deploymentResult.eligible_devices}</div>
                  <div className="text-xs text-white/90">Eligible Devices</div>
                </div>
                <div className="bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] rounded-lg p-3">
                  <div className="text-xl font-bold text-cyan-400">{deploymentResult.test_group_devices}</div>
                  <div className="text-xs text-white/90">Test Group</div>
                </div>
                <div className="bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] rounded-lg p-3">
                  <div className="text-xl font-bold text-purple-400">{deploymentResult.sprint_waves}</div>
                  <div className="text-xs text-white/90">Sprints</div>
                </div>
                <div className="bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] rounded-lg p-3">
                  <div className="text-xl font-bold text-green-400">{deploymentResult.created_profiles}</div>
                  <div className="text-xs text-white/90">Profiles Created</div>
                </div>
              </div>

              {deploymentResult.errors?.length > 0 && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 text-left max-w-2xl mx-auto">
                  <h4 className="text-amber-400 font-medium mb-2">Warnings</h4>
                  <ul className="text-sm text-white/90 space-y-1">
                    {deploymentResult.errors.map((err, i) => (
                      <li key={i}>• {err}</li>
                    ))}
                  </ul>
                </div>
              )}

              <Button onClick={onClose} className="bg-purple-500 hover:bg-purple-600">
                Close
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
      {loading && step === FU_WIZARD_LAST_STEP && (
        <CreationProgressOverlay 
          message="Creating Feature Update Deployment" 
          detail="Creating Azure AD groups, assigning devices, and configuring Intune profiles. This may take a minute..." 
        />
      )}
    </div>
  );
};

// =============================================================================
// MoveDevicesModal — New Feature 2 (01/09)
// -----------------------------------------------------------------------------
// Manual override for sprint allocation. Pick a source phase, tick devices,
// pick a destination (another sprint on this deployment, a sprint on another
// deployment targeting the same release, or "remove from deployment"), and the
// backend rewrites the allocation AND the Entra group membership so Intune
// follows.
// =============================================================================
const MoveDevicesModal = ({ deployment, deployments, onClose, onMoved }) => {
  const phaseOptions = useMemo(() => {
    const out = [];
    if (deployment.test_group?.enabled) out.push({ key: 'test', label: 'Test Group' });
    (deployment.sprint_waves || []).forEach(w => {
      out.push({ key: `sprint-${w.wave_number}`, label: `Sprint ${w.wave_number}` });
    });
    return out;
  }, [deployment]);

  const [fromPhase, setFromPhase] = useState(phaseOptions[0]?.key || '');
  const [toDeploymentId, setToDeploymentId] = useState(deployment.id);
  const [toPhase, setToPhase] = useState('');
  const [devices, setDevices] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [moving, setMoving] = useState(false);
  const [search, setSearch] = useState('');

  // Other deployments on the SAME target version — the backend refuses a
  // cross-version move because it would silently change which release the
  // device is offered.
  const otherDeployments = useMemo(
    () => (deployments || []).filter(
      d => d.id !== deployment.id && (d.target_version || '') === (deployment.target_version || ''),
    ),
    [deployments, deployment],
  );

  const targetDeployment = useMemo(
    () => (toDeploymentId === deployment.id
      ? deployment
      : otherDeployments.find(d => d.id === toDeploymentId)),
    [toDeploymentId, deployment, otherDeployments],
  );

  const targetPhaseOptions = useMemo(() => {
    const out = [];
    if (targetDeployment?.test_group?.enabled) out.push({ key: 'test', label: 'Test Group' });
    (targetDeployment?.sprint_waves || []).forEach(w => {
      out.push({ key: `sprint-${w.wave_number}`, label: `Sprint ${w.wave_number}` });
    });
    if (toDeploymentId === deployment.id) {
      out.push({ key: 'remove', label: 'Remove from this deployment' });
    }
    return out.filter(o => !(toDeploymentId === deployment.id && o.key === fromPhase));
  }, [targetDeployment, toDeploymentId, deployment.id, fromPhase]);

  useEffect(() => {
    // Keep the destination valid whenever the source or deployment changes.
    if (!targetPhaseOptions.some(o => o.key === toPhase)) {
      setToPhase(targetPhaseOptions[0]?.key || '');
    }
  }, [targetPhaseOptions, toPhase]);

  useEffect(() => {
    if (!fromPhase) return;
    let cancelled = false;
    setLoading(true);
    setSelectedIds([]);
    osPatchApi.getDeploymentPhaseDevices(deployment.id, fromPhase)
      .then(res => { if (!cancelled) setDevices(res.data?.devices || []); })
      .catch(() => { if (!cancelled) { setDevices([]); toast.error('Failed to load sprint devices'); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [deployment.id, fromPhase]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return devices;
    return devices.filter(d =>
      (d.device_name || '').toLowerCase().includes(q)
      || (d.user_email || '').toLowerCase().includes(q));
  }, [devices, search]);

  const handleMove = async () => {
    if (!selectedIds.length || !toPhase) return;
    const destLabel = targetPhaseOptions.find(o => o.key === toPhase)?.label || toPhase;
    const destSuffix = toDeploymentId === deployment.id ? '' : ` on "${targetDeployment?.name}"`;
    if (!window.confirm(
      `Move ${selectedIds.length} device${selectedIds.length === 1 ? '' : 's'} to ${destLabel}${destSuffix}? `
      + 'Entra group membership is updated immediately, so Intune will change what it offers these devices.'
    )) return;
    setMoving(true);
    try {
      const res = await osPatchApi.moveDeploymentDevices(deployment.id, {
        device_ids: selectedIds,
        from_phase: fromPhase,
        to_phase: toPhase,
        ...(toDeploymentId === deployment.id ? {} : { to_deployment_id: toDeploymentId }),
      });
      const moved = res.data?.moved || 0;
      const entra = res.data?.entra || {};
      if (!res.data?.graph_configured) {
        toast.success(`Moved ${moved} device(s) — Intune is not connected, so no group was changed`);
      } else if (entra.failed) {
        toast.warning(`Moved ${moved} device(s), but ${entra.failed} Entra group change(s) failed`);
      } else if (entra.skipped_no_azure_id) {
        toast.warning(`Moved ${moved} device(s); ${entra.skipped_no_azure_id} have no Entra device ID and were not re-grouped`);
      } else if (res.data?.membership_unresolved_phases?.length) {
        // 04/09 — the backend now re-reads the groups after the move, so this
        // is a real unsettled state rather than the replication lag that used
        // to surface as a drift banner on a move that had actually worked.
        toast.warning(
          `Moved ${moved} device(s) to ${destLabel}, but Entra has not confirmed `
          + `${res.data.membership_unresolved_phases.join(', ')} yet — re-check in a moment`,
        );
      } else {
        toast.success(`Moved ${moved} device(s) to ${destLabel} — Entra groups updated`);
      }
      if (onMoved) onMoved();
      onClose();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to move devices');
    } finally {
      setMoving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="fu-move-devices-modal"
    >
      <div className="alice-popup bg-slate-900 border border-white/10 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="flex items-start justify-between gap-4 p-5 border-b border-white/10">
          <div>
            <h2 className="text-lg font-semibold text-white">Move devices between sprints</h2>
            <p className="text-xs text-white/60 mt-1">{deployment.name} · {deployment.target_version}</p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} data-testid="fu-move-devices-close">
            <X className="w-4 h-4 text-white/80" />
          </Button>
        </div>

        <div className="p-5 space-y-4 overflow-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs text-white/70 mb-1">From sprint</label>
              <select
                value={fromPhase}
                onChange={e => setFromPhase(e.target.value)}
                className="w-full bg-[rgba(229,239,250,0.1)] border border-white/15 text-white rounded-md px-3 py-2 text-sm"
                data-testid="fu-move-devices-from"
              >
                {phaseOptions.map(o => (
                  <option key={o.key} value={o.key} className="bg-slate-900">{o.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-white/70 mb-1">To deployment</label>
              <select
                value={toDeploymentId}
                onChange={e => setToDeploymentId(e.target.value)}
                className="w-full bg-[rgba(229,239,250,0.1)] border border-white/15 text-white rounded-md px-3 py-2 text-sm"
                data-testid="fu-move-devices-to-deployment"
              >
                <option value={deployment.id} className="bg-slate-900">This deployment</option>
                {otherDeployments.map(d => (
                  <option key={d.id} value={d.id} className="bg-slate-900">{d.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-white/70 mb-1">To sprint</label>
              <select
                value={toPhase}
                onChange={e => setToPhase(e.target.value)}
                className="w-full bg-[rgba(229,239,250,0.1)] border border-white/15 text-white rounded-md px-3 py-2 text-sm"
                data-testid="fu-move-devices-to"
              >
                {targetPhaseOptions.map(o => (
                  <option key={o.key} value={o.key} className="bg-slate-900">{o.label}</option>
                ))}
              </select>
            </div>
          </div>

          {!otherDeployments.length && (deployments || []).length > 1 && (
            <p className="text-[11px] text-white/45" data-testid="fu-move-devices-cross-note">
              Only deployments with the same target version ({deployment.target_version}) can receive devices from
              this one.
            </p>
          )}

          <div className="flex items-center gap-2">
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search device or user…"
              className="bg-[rgba(229,239,250,0.1)] border-white/15 text-white text-sm"
              data-testid="fu-move-devices-search"
            />
            <Button
              variant="outline"
              size="sm"
              className="border-white/20 text-white whitespace-nowrap"
              onClick={() => setSelectedIds(
                selectedIds.length === filtered.length
                  ? []
                  : filtered.map(d => d.device_id).filter(Boolean),
              )}
              data-testid="fu-move-devices-select-all"
            >
              {selectedIds.length === filtered.length && filtered.length ? 'Clear' : 'Select all'}
            </Button>
          </div>

          <div className="rounded-lg border border-white/10 overflow-hidden">
            {loading ? (
              <div className="p-6 text-center text-white/60 text-sm">
                <RefreshCw className="w-5 h-5 mx-auto animate-spin mb-2" /> Loading devices…
              </div>
            ) : !filtered.length ? (
              <div className="p-6 text-center text-white/50 text-sm" data-testid="fu-move-devices-empty">
                No devices in this sprint.
              </div>
            ) : (
              <div className="max-h-[40vh] overflow-auto">
                <table className="w-full text-sm">
                  <thead className="bg-white/5 text-white/60 text-xs uppercase tracking-wider sticky top-0">
                    <tr>
                      <th className="w-9 px-2 py-2" />
                      <th className="text-left px-3 py-2 font-medium">Device</th>
                      <th className="text-left px-3 py-2 font-medium">User</th>
                      <th className="text-left px-3 py-2 font-medium">OS build</th>
                      <th className="text-right px-3 py-2 font-medium">Free disk</th>
                    </tr>
                  </thead>
                  <tbody className="text-white/90">
                    {filtered.map((d, idx) => (
                      <tr key={d.device_id || idx} className="border-t border-white/5 hover:bg-white/[0.03]">
                        <td className="px-2 py-2 text-center">
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(d.device_id)}
                            onChange={(e) => setSelectedIds(prev => e.target.checked
                              ? [...new Set([...prev, d.device_id])]
                              : prev.filter(x => x !== d.device_id))}
                            data-testid={`fu-move-devices-select-${idx}`}
                          />
                        </td>
                        <td className="px-3 py-2 font-medium text-white">{d.device_name}</td>
                        <td className="px-3 py-2 text-white/70">{d.user_email || '—'}</td>
                        <td className="px-3 py-2 text-white/60 font-mono text-xs">{d.os_version || '—'}</td>
                        <td className="px-3 py-2 text-right text-xs text-white/70">
                          {d.free_disk_gb == null ? '—' : `${Number(d.free_disk_gb).toFixed(1)} GB`}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 p-5 border-t border-white/10">
          <span className="text-xs text-white/60" data-testid="fu-move-devices-count">
            {selectedIds.length} selected
          </span>
          <div className="flex gap-2">
            <Button variant="outline" className="border-white/20 text-white" onClick={onClose}>Cancel</Button>
            <Button
              className="bg-cyan-500 hover:bg-cyan-600 text-white"
              disabled={!selectedIds.length || !toPhase || moving}
              onClick={handleMove}
              data-testid="fu-move-devices-confirm"
            >
              {moving ? 'Moving…' : `Move ${selectedIds.length || ''}`.trim()}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};


// ==================== FEATURE UPDATE DEPLOYMENT PROGRESS ====================

const FeatureUpdateDeploymentCard = ({ deployment, deployments, onAdvanceWave, onDelete, onRefresh }) => {
  const { canWriteOSPatch } = usePermissions();
  const [progress, setProgress] = useState(null);
  const [loadingProgress, setLoadingProgress] = useState(false);
  const [advancing, setAdvancing] = useState(false);
  const [fixingMembership, setFixingMembership] = useState(false);
  const [expanded, setExpanded] = useState(false);
  // Per-sprint device drill-down. Mirrors the Update Ring Deployments
  // card behaviour — clicking "View devices" on a sprint opens the
  // same FeatureUpdateDeviceDrilldown used by the Reports > Feature
  // Updates cards. Works for both active sprints (cached Intune export
  // data) and pending sprints (synthesised from targeted_device_ids).
  const [sprintDrillIn, setSprintDrillIn] = useState(null);
  // New Feature 2 (01/09) — manual deployment selections.
  const [installType, setInstallType] = useState(
    (deployment.installation_type || 'required').toLowerCase(),
  );
  const [switchingInstallType, setSwitchingInstallType] = useState(false);
  const [showMoveDevices, setShowMoveDevices] = useState(false);
  const [schedulingPhase, setSchedulingPhase] = useState(null);

  useEffect(() => {
    setInstallType((deployment.installation_type || 'required').toLowerCase());
  }, [deployment.installation_type]);

  // New Feature 2 (01/09) — promote an Optional (user-initiated) rollout to
  // Required (auto-install), or step a Required one back to Optional, without
  // rebuilding the deployment.
  //
  // 01/09 round 2 — `phase` makes this per-sprint. Omitted, it still sets
  // every sprint at once (and moves the deployment-level default); passed as
  // 'test' / 'sprint-N' it patches only that sprint's Intune profile, which is
  // what the real rollout needs: enforce Sprint 1 while Sprint 3 stays
  // optional.
  const handleChangeInstallType = async (next, phase = null, phaseLabel = null) => {
    if (switchingInstallType) return;
    if (!phase && next === installType) return;
    const scope = phase ? phaseLabel : 'this deployment and every sprint in it';
    const wording = next === 'required'
      ? `Make ${scope} REQUIRED? Targeted devices will install the feature update automatically once the sprint is active.`
      : `Make ${scope} OPTIONAL? Targeted users choose when to install from Windows Update.`;
    if (!window.confirm(wording)) return;
    setSwitchingInstallType(phase || true);
    try {
      const res = await osPatchApi.setDeploymentInstallationType(deployment.id, next, phase);
      if (!phase) setInstallType(next);
      const failed = res.data?.profiles_failed || [];
      const skipped = res.data?.profiles_skipped || [];
      if (!res.data?.graph_configured) {
        toast.success(`Set ${scope} to ${next} in ALICE — Intune is not connected, so no profile was patched`);
      } else if (failed.length) {
        toast.warning(`Set to ${next}, but Intune rejected: ${failed.join(', ')}`);
      } else if (skipped.length) {
        toast.warning(`Set to ${next}. No Intune profile yet for: ${skipped.join(', ')}`);
      } else {
        toast.success(`${phase ? phaseLabel : 'Deployment'} is now ${next}`);
      }
      loadProgress();
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to change installation type');
    } finally {
      setSwitchingInstallType(false);
    }
  };

  // 09/09 — the activation announcement, per sprint.
  //
  // Same scoping rule as the installation type above: with `phase` only that
  // sprint changes and the deployment default is left alone, because otherwise
  // the next sprint to activate would silently inherit a value the admin only
  // ever chose for one of them.
  const [announcing, setAnnouncing] = useState(false);

  const handleToggleAnnounce = async (next, phase = null, phaseLabel = null) => {
    if (announcing) return;
    setAnnouncing(phase || true);
    try {
      await osPatchApi.setFeatureAnnouncementSettings(deployment.id, {
        announce_on_activation: next,
        ...(phase ? { phase } : {}),
      });
      toast.success(
        next
          ? `${phase ? phaseLabel : 'Every sprint'} will email its users when it activates`
          : `${phase ? phaseLabel : 'No sprint'} will email users on activation`
      );
      loadProgress();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to change the announcement setting');
    } finally {
      setAnnouncing(false);
    }
  };

  // Send (or re-send) a sprint's announcement now — for a sprint that
  // activated before the option was turned on, or one whose send failed.
  const handleAnnounceNow = async (phase, phaseLabel, alreadySent) => {
    if (announcing) return;
    const wording = alreadySent
      ? `${phaseLabel} has already been announced. Email its users again now?`
      : `Email every user in ${phaseLabel} that the update is available?`;
    if (!window.confirm(wording)) return;
    setAnnouncing(phase);
    try {
      const res = await osPatchApi.announceFeaturePhase(deployment.id, phase);
      if (res.data?.status === 'noop') {
        toast.info(res.data?.message || 'Nothing to send');
      } else {
        const d = res.data || {};
        toast.success(
          `Announced ${phaseLabel}: ${d.sent_count || 0} sent`
          + (d.skipped_count ? `, ${d.skipped_count} skipped` : '')
          + (d.error_count ? `, ${d.error_count} failed` : '')
        );
      }
      loadProgress();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to send the announcement');
    } finally {
      setAnnouncing(false);
    }
  };

  // Local date formatter for the sprint schedule chips. The `formatDate`
  // helpers elsewhere in this file are scoped to other components, so this
  // card needs its own.
  const fmtScheduleDate = (iso) => {
    if (!iso) return '—';
    const t = Date.parse(iso);
    if (Number.isNaN(t)) return '—';
    const d = new Date(t);
    const days = Math.round((t - Date.now()) / 86400000);
    const when = d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
    if (days > 1) return `${when} (in ${days} days)`;
    if (days === 1) return `${when} (tomorrow)`;
    if (days === 0) return `${when} (today)`;
    if (days === -1) return `${when} (yesterday)`;
    return `${when} (${Math.abs(days)} days ago)`;
  };

  // 01/09 round 3 — record the day the users were told, which is what the
  // Optional window counts from. Separate from ALICE's own comms send because
  // customers frequently send their own.
  const handleMarkCommsSent = async (phaseKey, phaseLabel, existing) => {
    const suggested = (existing || new Date().toISOString()).slice(0, 10);
    const answer = window.prompt(
      `What date were the users in ${phaseLabel} told about this update?\n\n`
      + 'The Optional window counts from this date. Format: YYYY-MM-DD.\n'
      + 'Leave blank and press OK to clear it.',
      suggested,
    );
    if (answer === null) return;
    setSchedulingPhase(phaseKey);
    try {
      const res = await osPatchApi.setDeploymentPhaseSchedule(deployment.id, {
        phase: phaseKey,
        comms_sent_at: answer.trim() ? `${answer.trim()}T09:00:00Z` : null,
      });
      const req = res.data?.required_at;
      toast.success(
        answer.trim()
          ? `${phaseLabel}: comms recorded${req ? ` · becomes Required ${new Date(req).toLocaleDateString()}` : ''}`
          : `${phaseLabel}: comms date cleared`,
      );
      loadProgress();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to update the schedule');
    } finally {
      setSchedulingPhase(null);
    }
  };

  // 02/09 — the POC gate. A test group exists to be judged, so the deployment
  // does not roll into Sprint 1 on a threshold or a timer until someone says
  // it passed. Manual Advance Sprint still works — pressing it IS the decision.
  const [approvingTest, setApprovingTest] = useState(false);
  const handleApproveTestGroup = async (approved) => {
    const wording = approved
      ? 'Approve the test group? The deployment will then be free to advance to Sprint 1.'
      : 'Withdraw approval? The deployment will be held at the test group until it is approved again.';
    if (!window.confirm(wording)) return;
    setApprovingTest(true);
    try {
      const res = await osPatchApi.approveTestGroup(deployment.id, { approved });
      toast.success(res.data?.message || (approved ? 'Test group approved' : 'Approval withdrawn'));
      loadProgress();
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to update the approval');
    } finally {
      setApprovingTest(false);
    }
  };

  // Small helper for the per-sprint controls below.
  const renderInstallTypeToggle = (current, onPick, testIdBase, busy) => (!canWriteOSPatch ? (
    <span className="text-[11px] text-white/80 capitalize" data-testid={`${testIdBase}-readonly`}>{current}</span>
  ) : (
    <div className="inline-flex rounded-md overflow-hidden border border-white/15" data-testid={testIdBase}>
      {[
        { key: 'optional', label: 'Optional', on: 'bg-cyan-500 text-white' },
        { key: 'required', label: 'Required', on: 'bg-purple-500 text-white' },
      ].map(opt => (
        <button
          key={opt.key}
          type="button"
          disabled={!!busy}
          onClick={() => onPick(opt.key)}
          className={`px-2.5 py-0.5 text-[11px] font-medium transition-colors disabled:opacity-50 ${
            current === opt.key ? opt.on : 'text-white/70 hover:bg-white/10'
          }`}
          data-testid={`${testIdBase}-${opt.key}`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  ));

  // Short date for a timeline node. The full, relative-dated string goes in
  // the node's `title`, so the rail stays readable at a glance.
  const fmtNodeDate = (iso) => {
    const t = Date.parse(iso);
    if (Number.isNaN(t)) return '—';
    return new Date(t).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  };

  // 07/09 — one sprint's life as a timeline instead of a wrap of chips.
  //
  // The chips said the same things but in no particular order, so the reader
  // had to reassemble the sequence themselves — and the two clocks (this
  // sprint's enforcement vs. when the NEXT sprint starts) sat side by side
  // looking like one chain of events. The rail below puts them in order and
  // separates the last node, because that node belongs to the other clock.
  //
  //   Activated → Offering → Comms → Required   |   Next sprint
  //   \______ this sprint, its own devices ____/     \_ the rollout _/
  const renderPhaseSchedule = (phase, phaseKey, phaseLabel, testIdBase) => {
    const optional = (phase.installation_type || 'required') === 'optional';
    const escalated = !!phase.auto_required_applied_at;
    const anchorsToComms = (progress?.auto_required_anchor || 'comms') === 'comms';
    const windowDays = progress?.auto_required_after_days;
    const mode = progress?.progression_mode || 'threshold';
    const started = phase.status === 'active' || phase.status === 'completed';
    const now = Date.now();
    const isPast = (iso) => { const t = Date.parse(iso); return !Number.isNaN(t) && t <= now; };

    // Each node: { key, label, value, state, note?, title? }
    // state drives the dot and the colour, and nothing else.
    //   done    it has happened
    //   due     it is scheduled and still ahead
    //   waiting the rule is on but the date cannot be worked out yet
    //   none    not applicable to this sprint — rendered, but muted, because
    //           "there is no offer date" is information too
    const nodes = [];

    nodes.push(!started
      ? { key: 'activated', label: 'Activated', value: 'Not yet', state: 'none',
          title: 'This sprint has not been activated. Its clocks start when it does.' }
      : { key: 'activated', label: 'Activated', value: fmtNodeDate(phase.activated_at), state: 'done',
          title: fmtScheduleDate(phase.activated_at) });

    if (phase.available_from_cleared_at) {
      nodes.push({ key: 'offering', label: 'Offering', value: 'As soon as possible', state: 'done',
        title: "Switching this sprint to Required also moved Intune's rollout option back to "
          + `'as soon as possible', so the scheduled offer date no longer applies (cleared ${fmtScheduleDate(phase.available_from_cleared_at)}).` });
    } else if (phase.available_from) {
      nodes.push({ key: 'offering', label: 'Offering', value: fmtNodeDate(phase.available_from),
        state: isPast(phase.available_from) ? 'done' : 'due',
        title: `Intune starts offering the update ${fmtScheduleDate(phase.available_from)}.` });
    } else {
      nodes.push({ key: 'offering', label: 'Offering', value: started ? 'Immediately' : 'On activation',
        state: started ? 'done' : 'none',
        title: 'No offer date set — Intune offers this sprint as soon as possible.' });
    }

    // 09/09 — `announces` is the per-sprint activation announcement. It
    // changes what "Comms sent: not recorded" MEANS: with it on, the date is
    // pending rather than missing, and the admin has nothing to remember.
    const announces = Boolean(phase.announce_on_activation);
    if (phase.comms_sent_at) {
      nodes.push({ key: 'comms', label: 'Comms sent', value: fmtNodeDate(phase.comms_sent_at), state: 'done',
        note: phase.comms_source === 'manual' ? 'recorded by hand'
          : phase.comms_source === 'alice_announcement' ? 'activation email' : null,
        title: `Users were told ${fmtScheduleDate(phase.comms_sent_at)}.` });
    } else if (announces && !started) {
      nodes.push({ key: 'comms', label: 'Comms sent', value: 'On activation', state: 'due',
        title: 'ALICE emails this sprint’s users when it activates, and records that date here.' });
    } else if (optional && windowDays && anchorsToComms && !escalated) {
      nodes.push({ key: 'comms', label: 'Comms sent', value: 'Not recorded', state: 'waiting',
        title: `The ${windowDays}-day optional window counts from this date, so it has not started. `
          + (announces
            ? 'This sprint is set to announce on activation but nothing was sent — use Announce now.'
            : 'Send the reminder from this sprint, or set the date by hand.') });
    } else {
      nodes.push({ key: 'comms', label: 'Comms sent', value: '—', state: 'none',
        title: 'No comms recorded for this sprint.' });
    }

    if (escalated) {
      nodes.push({ key: 'required', label: 'Required', value: fmtNodeDate(phase.auto_required_applied_at),
        state: 'done', note: 'auto-escalated',
        title: `ALICE switched this sprint to Required ${fmtScheduleDate(phase.auto_required_applied_at)}.` });
    } else if (!optional) {
      nodes.push({ key: 'required', label: 'Required', value: 'From the start', state: 'done',
        title: 'This sprint was created Required — there is nothing to escalate.' });
    } else if (phase.required_at) {
      nodes.push({ key: 'required', label: 'Becomes Required', value: fmtNodeDate(phase.required_at),
        state: isPast(phase.required_at) ? 'waiting' : 'due',
        note: phase.required_window_restarted_at
          // Switching a sprint back to Optional restarts the window from that
          // moment, not from the original comms date. Say so, or the date
          // looks wrong against the comms node next to it.
          ? `window restarted ${fmtNodeDate(phase.required_window_restarted_at)}` : null,
        title: isPast(phase.required_at)
          ? `Due ${fmtScheduleDate(phase.required_at)} — the nightly sync will apply it on its next run.`
          : `This sprint switches itself to Required ${fmtScheduleDate(phase.required_at)}.` });
    } else if (windowDays) {
      nodes.push({ key: 'required', label: 'Becomes Required', value: `${windowDays} days after comms`,
        state: 'waiting',
        title: 'The window is set but has no start date yet — record the comms date to start it.' });
    } else {
      nodes.push({ key: 'required', label: 'Becomes Required', value: 'By hand only', state: 'none',
        title: 'No automatic escalation on this deployment. Use the Optional / Required control above.' });
    }

    // --- the other clock -------------------------------------------------
    let next;
    if (phase.status === 'completed') {
      next = { key: 'next', label: 'Next sprint', value: 'Handed over', state: 'done',
        title: 'This sprint has handed over. Its own enforcement clock keeps running for the devices in it that have not updated.' };
    } else if (!started) {
      next = { key: 'next', label: 'Next sprint', value: '—', state: 'none',
        title: 'This sprint has not started yet.' };
    } else if (mode === 'manual') {
      next = { key: 'next', label: 'Next sprint', value: 'When you activate it', state: 'none',
        title: 'Manual progression — press Advance Sprint when you are ready.' };
    } else if (mode === 'deadline' && phase.deadline_at) {
      next = { key: 'next', label: 'Next sprint', value: fmtNodeDate(phase.deadline_at),
        state: isPast(phase.deadline_at) ? 'waiting' : 'due',
        title: `Fixed duration — the next sprint starts ${fmtScheduleDate(phase.deadline_at)}.` };
    } else if (mode === 'both' && phase.deadline_at) {
      next = { key: 'next', label: 'Next sprint', value: `${progress?.completion_threshold}% or ${fmtNodeDate(phase.deadline_at)}`,
        state: 'due', title: `Whichever comes first: ${progress?.completion_threshold}% complete, or ${fmtScheduleDate(phase.deadline_at)}.` };
    } else {
      next = { key: 'next', label: 'Next sprint', value: `At ${progress?.completion_threshold ?? 60}%`,
        state: 'due',
        title: `Fully automated — the next sprint starts once this one reaches ${progress?.completion_threshold ?? 60}% on the target version.` };
    }

    const dot = {
      done:    'bg-cyan-400 border-cyan-400',
      due:     'bg-transparent border-cyan-400/70',
      waiting: 'bg-amber-400 border-amber-400',
      none:    'bg-transparent border-white/25',
    };
    const text = {
      done: 'text-white/90', due: 'text-cyan-100/85', waiting: 'text-amber-300', none: 'text-white/40',
    };

    const renderNode = (n, isLast) => (
      <div
        key={n.key}
        className="flex-1 min-w-[8.5rem] flex flex-col gap-1"
        title={n.title}
        data-testid={`${testIdBase}-tl-${n.key}`}
      >
        <div className="flex items-center gap-1.5">
          <span className={`w-2 h-2 rounded-full border shrink-0 ${dot[n.state]}`} />
          {!isLast && <span className="h-px flex-1 bg-white/15" />}
        </div>
        <span className="text-[10px] uppercase tracking-wide text-white/45 leading-tight">{n.label}</span>
        <span className={`text-[11px] leading-tight ${text[n.state]}`}>
          {n.value}
          {n.note && <span className="block text-[10px] text-white/40">{n.note}</span>}
        </span>
      </div>
    );

    return (
      <div className="mt-2" data-testid={`${testIdBase}-timeline`}>
        <div className="overflow-x-auto">
          <div className="flex items-start gap-2 min-w-[34rem]">
            {nodes.map(n => renderNode(n, false))}
            {/* The divider is the point: everything left of it is this
                sprint's own clock; the node to the right belongs to the
                rollout's. */}
            <div className="self-stretch w-px bg-white/20 mx-1 shrink-0" aria-hidden="true" />
            {renderNode(next, true)}
          </div>
        </div>
        {/* 09/09 — the activation announcement, per sprint. Sits under the
            timeline because it is what produces the "Comms sent" node. */}
        <div className="mt-1.5 flex items-center gap-3 flex-wrap">
          {canWriteOSPatch && (
          <label
            className="flex items-center gap-1.5 text-[11px] text-white/60 cursor-pointer"
            data-testid={`${testIdBase}-announce-toggle`}
          >
            <input
              type="checkbox"
              checked={Boolean(phase.announce_on_activation)}
              disabled={announcing === phaseKey}
              onChange={(e) => handleToggleAnnounce(e.target.checked, phaseKey, phaseLabel)}
              className="w-3.5 h-3.5 rounded border-white/20 bg-white/10 text-cyan-500"
            />
            Email users when this sprint activates
          </label>
          )}
          {phase.announcement_sent_at && (
            <span
              className="text-[11px] text-white/45"
              title={`Announced ${fmtScheduleDate(phase.announcement_sent_at)}`}
              data-testid={`${testIdBase}-announced`}
            >
              Announced {fmtNodeDate(phase.announcement_sent_at)}
              {phase.announcement_result && (
                <> · {phase.announcement_result.sent || 0} sent
                  {phase.announcement_result.skipped ? ` · ${phase.announcement_result.skipped} skipped` : ''}
                  {phase.announcement_result.errors
                    ? <span className="text-amber-300"> · {phase.announcement_result.errors} failed</span>
                    : ''}
                </>
              )}
            </span>
          )}
          {/* Only offered once the sprint is live. Announcing a pending sprint
              would tell users an update is available that Intune is not
              offering them yet, which generates the support call this feature
              exists to prevent. */}
          {canWriteOSPatch && started && (
            <button
              type="button"
              disabled={announcing === phaseKey}
              onClick={() => handleAnnounceNow(phaseKey, phaseLabel, Boolean(phase.announcement_sent_at))}
              className="text-[11px] text-cyan-300 hover:text-cyan-200 underline underline-offset-2 disabled:opacity-50"
              data-testid={`${testIdBase}-announce-now`}
            >
              {announcing === phaseKey
                ? 'Sending…'
                : phase.announcement_sent_at ? 'Announce again' : 'Announce now'}
            </button>
          )}
          {canWriteOSPatch && (
          <button
            type="button"
            disabled={schedulingPhase === phaseKey}
            onClick={() => handleMarkCommsSent(phaseKey, phaseLabel, phase.comms_sent_at)}
            className="text-[11px] text-white/55 hover:text-white underline underline-offset-2 disabled:opacity-50"
            data-testid={`${testIdBase}-set-comms`}
          >
            {schedulingPhase === phaseKey
              ? 'Saving…'
              : phase.comms_sent_at ? 'Change comms date' : 'Set comms date'}
          </button>
          )}
        </div>
      </div>
    );
  };

  const loadProgress = async () => {
    setLoadingProgress(true);
    try {
      const res = await osPatchApi.getDeploymentProgress(deployment.id);
      setProgress(res.data);
    } catch (error) {
      console.error('Error loading progress:', error);
    } finally {
      setLoadingProgress(false);
    }
  };

  useEffect(() => {
    if (expanded) {
      loadProgress();
    }
  }, [expanded, deployment.id]);

  // 02/09 — activating a sprint and scheduling its offer are the same decision,
  // so they happen together. Previously you activated the sprint and then went
  // to edit its offer date, leaving it briefly live and unscheduled.
  const handleAdvanceWave = async () => {
    const isManual = progress?.progression_mode === 'manual';
    if (!window.confirm(
      isManual
        ? 'Activate the next sprint? You can set the date Intune starts offering it on the next prompt.'
        : 'Advance to the next sprint? This should only be done once the current sprint has reached its completion threshold.'
    )) return;

    // Offer date is optional — leave it blank and the deployment's own lead
    // time applies, exactly as before.
    const answer = window.prompt(
      'When should Intune start offering this sprint?\n\n'
      + 'Format: YYYY-MM-DD (leave blank to use the deployment\'s offer lead time).\n'
      + 'Create the sprint a day or two ahead of this date, and send the user comms after it.',
      '',
    );
    if (answer === null) return;
    const availableFrom = answer.trim() ? `${answer.trim()}T08:00:00Z` : null;

    setAdvancing(true);
    try {
      const res = await osPatchApi.advanceDeploymentWave(
        deployment.id, availableFrom ? { available_from: availableFrom } : {},
      );
      const offer = res.data?.available_from;
      toast.success(
        offer
          ? `Sprint activated · offering from ${fmtScheduleDate(offer)}`
          : 'Advanced to next sprint',
      );
      loadProgress();
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to advance sprint');
    } finally {
      setAdvancing(false);
    }
  };

  // Repairs phases whose Entra group has drifted from the ALICE allocation
  // (e.g. a sprint marked active against an empty group).
  const handleFixMembership = async (unownedAction = 'report') => {
    const prompts = {
      report: 'Cross-check every phase against its Entra group and add back any missing devices?',
      adopt: 'Add every untracked group member into the sprint it is already a member of? ALICE will start tracking and reporting on them.',
      remove: 'Remove every untracked group member from the sprint groups? Intune will stop offering the update to them.',
    };
    if (!window.confirm(prompts[unownedAction])) return;
    setFixingMembership(true);
    try {
      const res = await osPatchApi.reconcileDeploymentMembership(deployment.id, false, unownedAction);
      const added = res.data?.total_added || 0;
      const missing = res.data?.total_missing || 0;
      const removed = res.data?.cross_phase_strays_removed || 0;
      const removedExcluded = res.data?.excluded_members_removed || 0;
      const adopted = res.data?.adopted_into_allocation || 0;
      const untracked = res.data?.untracked_members || 0;
      const untrackedRemoved = res.data?.untracked_members_removed || 0;
      const parts = [];
      if (added) parts.push(`added ${added} device(s) to the right sprint group`);
      if (removed) parts.push(`removed ${removed} duplicate membership(s)`);
      if (removedExcluded) parts.push(`removed ${removedExcluded} excluded device(s)`);
      if (adopted) parts.push(`adopted ${adopted} group member(s) into the sprint`);
      if (untrackedRemoved) parts.push(`removed ${untrackedRemoved} untracked member(s)`);
      if (untracked) parts.push(`${untracked} untracked member(s) left alone — choose Adopt or Remove`);
      if (missing) parts.push(`${missing} still unresolved`);
      toast.success(parts.length ? `Membership: ${parts.join(', ')}` : 'Group membership already matches');
      loadProgress();
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to reconcile group membership');
    } finally {
      setFixingMembership(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this deployment? This will remove all associated Azure AD groups and Intune profiles.')) return;
    try {
      await osPatchApi.deleteFeatureDeployment(deployment.id);
      toast.success('Deployment deleted');
      if (onDelete) onDelete();
    } catch (error) {
      toast.error('Failed to delete deployment');
    }
  };

  // 07/09 — sprints whose optional window has been configured but never
  // started, because nobody recorded a comms date.
  //
  // This is the module's quietest failure. Under the default 'comms' anchor a
  // sprint with no comms date never auto-escalates — deliberately, so ALICE
  // cannot force-install before users have been told — but the consequence is
  // a sprint that sits Optional indefinitely while the deployment looks
  // healthy. On a 10-15 sprint rollout where the customer sends its own comms,
  // that is easy to let slip past every sprint. Counted at deployment level so
  // it is visible without expanding the card.
  //
  // Computed from the deployment document, NOT from `progress`: progress is
  // only fetched once the card is expanded, and a warning you have to open the
  // card to see is no warning at all. The list endpoint returns whole
  // documents, so every field needed is already here — no extra request per
  // card. Falls back to `progress` for the phases once it has loaded, so the
  // count cannot drift from the timelines below it.
  const stalledWindows = (() => {
    const src = progress || deployment;
    const windowDays = src.auto_required_after_days ?? deployment.auto_required_after_days;
    const anchor = src.auto_required_anchor || deployment.auto_required_anchor || 'comms';
    if (anchor !== 'comms' || !windowDays) return [];
    const phases = [];
    if (src.test_group) phases.push(['Test Group', src.test_group]);
    (src.sprint_waves || []).forEach(w => phases.push([`Sprint ${w.wave_number}`, w]));
    return phases
      .filter(([, p]) => (p.status === 'active' || p.status === 'completed')
        && (p.installation_type || src.installation_type || 'required') === 'optional'
        && !p.auto_required_applied_at
        && !p.comms_sent_at)
      .map(([label]) => label);
  })();

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-500/20 text-green-400';
      case 'completed': return 'bg-blue-500/20 text-blue-400';
      case 'pending': return 'bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] text-white/90';
      case 'error': return 'bg-red-500/20 text-red-400';
      default: return 'bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] text-white/90';
    }
  };

  return (
    <Card className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border-[rgba(229,239,250,0.1)]">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <button onClick={() => setExpanded(!expanded)} className="text-white/90 hover:text-white">
                {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </button>
              <Rocket className="w-5 h-5 text-purple-400" />
              <h3 className="text-lg font-medium text-white">{deployment.name}</h3>
              <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(deployment.status)}`}>
                {deployment.status}
              </span>
              {/* Bug fix 2 (01/09) — which rule set built this deployment.
                  Older deployments carry only the legacy booleans, so fall
                  back to those. */}
              {(() => {
                const isAppRisk = (deployment.deployment_mode
                  || (deployment.use_app_risk_mode || deployment.use_app_risk_categories ? 'app_risk' : 'staged')) === 'app_risk';
                return (
                  <span
                    className={`px-2 py-1 text-xs rounded-full ${
                      isAppRisk ? 'bg-amber-500/20 text-amber-300' : 'bg-purple-500/20 text-purple-300'
                    }`}
                    title={isAppRisk
                      ? 'Sprints ordered by app business criticality, falling back to readiness risk. No disk-space gate.'
                      : 'Sprints allocated from Deployment Sprint + Readiness Risk, gated on free disk space. App criticality not used.'}
                    data-testid={`fu-deployment-${deployment.id}-mode-chip`}
                  >
                    {isAppRisk ? 'App Risk' : 'Staged'}
                  </span>
                );
              })()}
              {/* 07/09 — a sprint whose optional window never started is doing
                  nothing, silently. Surface it on the collapsed card, where the
                  admin is actually looking, and open the card on click. */}
              {stalledWindows.length > 0 && (
                <button
                  type="button"
                  onClick={() => setExpanded(true)}
                  className="px-2 py-1 text-xs rounded-full bg-amber-500/20 text-amber-300 border border-amber-400/30 hover:bg-amber-500/30"
                  title={`${stalledWindows.join(', ')} — the ${progress?.auto_required_after_days ?? deployment.auto_required_after_days}-day optional window counts from the day users were told, and no comms date has been recorded. These sprints stay Optional until one is. Click to open the deployment and set it.`}
                  data-testid={`fu-deployment-${deployment.id}-stalled-windows`}
                >
                  {stalledWindows.length} sprint{stalledWindows.length === 1 ? '' : 's'} awaiting a comms date
                </button>
              )}
            </div>
            <div className="ml-9 flex flex-wrap gap-4 text-sm text-white/90">
              <span>Target: <span className="text-purple-400">{deployment.target_version}</span></span>
              {/* Fall back to the wave count: `num_sprints` is only persisted
                  by the current create path, so older deployments rendered a
                  blank value here. */}
              <span>Sprints: <span className="text-white">
                {deployment.num_sprints ?? deployment.sprint_waves?.length ?? '—'}
              </span></span>
              <span>Devices: <span className="text-white">{deployment.assigned_device_ids?.length || 0}</span></span>
              <span>Created: <span className="text-white">{new Date(deployment.created_at).toLocaleDateString()}</span></span>
            </div>

            {/* New Feature 2 (01/09) — Optional ↔ Required without rebuilding
                the deployment. 01/09 round 2: this control is now the "set
                every sprint" shortcut; each sprint has its own control on its
                row below, which is what the comms-then-enforce rollout needs. */}
            <div className="ml-9 mt-3 flex items-center gap-2 flex-wrap" data-testid={`fu-deployment-${deployment.id}-install-type`}>
              <span className="text-xs text-white/60">All sprints:</span>
              {!canWriteOSPatch && (
                <span className="text-xs text-white/90 capitalize" data-testid={`fu-deployment-${deployment.id}-install-type-readonly`}>{installType}</span>
              )}
              {canWriteOSPatch && (
              <div className="inline-flex rounded-md overflow-hidden border border-white/15">
                {[
                  { key: 'optional', label: 'Optional', on: 'bg-cyan-500 text-white' },
                  { key: 'required', label: 'Required', on: 'bg-purple-500 text-white' },
                ].map(opt => (
                  <button
                    key={opt.key}
                    type="button"
                    disabled={!!switchingInstallType}
                    onClick={() => handleChangeInstallType(opt.key)}
                    className={`px-3 py-1 text-xs font-medium transition-colors disabled:opacity-50 ${
                      installType === opt.key ? opt.on : 'text-white/70 hover:bg-white/10'
                    }`}
                    data-testid={`fu-deployment-${deployment.id}-install-type-${opt.key}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              )}
              {switchingInstallType && (
                <span className="text-xs text-white/50">Updating Intune profiles…</span>
              )}
              {canWriteOSPatch && (
              <span className="text-[11px] text-white/40">
                Sets every sprint at once — expand to set them individually
              </span>
              )}
              {/* 09/09 — the same "set them all" shortcut for the activation
                  announcement. Per-sprint control is on each sprint row. */}
              {canWriteOSPatch && (
              <label
                className="flex items-center gap-1.5 text-[11px] text-white/60 cursor-pointer border-l border-white/15 pl-2 ml-1"
                data-testid={`fu-deployment-${deployment.id}-announce-all`}
              >
                <input
                  type="checkbox"
                  checked={Boolean(progress?.announce_on_activation)}
                  disabled={announcing === true}
                  onChange={(e) => handleToggleAnnounce(e.target.checked)}
                  className="w-3.5 h-3.5 rounded border-white/20 bg-white/10 text-cyan-500"
                />
                Email users on activation (all sprints)
              </label>
              )}
              {/* 01/09 round 2 — how this deployment progresses and whether it
                  escalates itself, stated where the admin can see it. */}
              {progress?.progression_mode && (
                <span
                  className="text-[11px] text-indigo-200/80 border-l border-white/15 pl-2 ml-1"
                  data-testid={`fu-deployment-${deployment.id}-schedule-summary`}
                >
                  {progress.progression_mode === 'threshold'
                    && `Fully automated — next sprint at ${progress.completion_threshold}% complete`}
                  {progress.progression_mode === 'deadline'
                    && `Fixed duration — next sprint ${progress.sprint_duration_days} days after each one activates`}
                  {progress.progression_mode === 'manual'
                    && 'Manual — sprints start only when you activate them'}
                  {progress.progression_mode === 'both'
                    && `Next sprint at ${progress.completion_threshold}% or after ${progress.sprint_duration_days} days`}
                  {/* 07/09 — the window is now stored on Required deployments
                      too, as the policy for a sprint later flipped to Optional.
                      Say that, rather than implying sprints are escalating when
                      none of them is optional. */}
                  {progress.auto_required_after_days
                    ? (progress.installation_type || 'required') === 'optional'
                      ? ` · Optional sprints become Required after ${progress.auto_required_after_days} days`
                      : ` · any sprint switched to Optional gets ${progress.auto_required_after_days} days`
                    : ' · no automatic Optional→Required'}
                </span>
              )}
              {/* 09/09 — device types this deployment was created to leave out.
                  A rollout with no AVD hosts in it should say so on the card,
                  not only in the Cosmos document. */}
              {(progress?.excluded_virtualisation_labels || []).length > 0 && (
                <span
                  className="text-[11px] text-violet-200/80 border-l border-white/15 pl-2 ml-1"
                  data-testid={`fu-deployment-${deployment.id}-device-type-scope`}
                >
                  Excludes {progress.excluded_virtualisation_labels.join(', ')}
                </span>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            {/* New Feature 2 (01/09) — manual sprint/deployment reassignment. */}
            {canWriteOSPatch && (
            <Button
              variant="outline"
              size="sm"
              className="border-cyan-500/50 text-cyan-300 hover:bg-cyan-500/20"
              onClick={() => setShowMoveDevices(true)}
              data-testid={`fu-deployment-${deployment.id}-move-devices-btn`}
            >
              <ArrowRight className="w-4 h-4 mr-1" />
              Move devices
            </Button>
            )}
            {canWriteOSPatch && progress && (
              <Button
                variant="outline"
                size="sm"
                className="border-amber-500/50 text-amber-300 hover:bg-amber-500/20"
                onClick={() => handleFixMembership('report')}
                disabled={fixingMembership}
                data-testid={`fu-deployment-${deployment.id}-fix-membership-btn`}
              >
                <Wrench className="w-4 h-4 mr-1" />
                {fixingMembership ? 'Fixing...' : 'Fix group membership'}
              </Button>
            )}
            {canWriteOSPatch && deployment.status === 'active' && (
              <Button
                variant="outline"
                size="sm"
                className="border-green-500/50 text-green-400 hover:bg-green-500/20"
                onClick={handleAdvanceWave}
                disabled={advancing}
                data-testid={`fu-deployment-${deployment.id}-advance-sprint-btn`}
              >
                <Play className="w-4 h-4 mr-1" />
                {advancing ? 'Advancing...' : 'Advance Sprint'}
              </Button>
            )}
            {canWriteOSPatch && (
            <Button
              variant="ghost"
              size="icon"
              className="text-white/90 hover:text-red-400"
              onClick={handleDelete}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
            )}
          </div>
        </div>

        {/* Expanded Progress View */}
        {expanded && (
          <div className="mt-4 ml-9 space-y-4">
            {loadingProgress ? (
              <div className="text-white/90 text-sm">Loading progress...</div>
            ) : progress ? (
              <>
                {/* 07/09 — tenant drift. A rollout runs for weeks and the
                    estate does not hold still: devices are retired, rebuilt
                    and enrolled underneath it. The nightly pass now repairs
                    all three, and this is where it reports what it did — a
                    sprint that quietly changed size is otherwise indis-
                    tinguishable from a bug. */}
                {(progress.drift_log?.length > 0 || progress.unplaced_arrivals > 0) && (
                  <div
                    className="rounded-lg border border-white/15 bg-[rgba(229,239,250,0.06)] p-3 text-sm"
                    data-testid={`fu-deployment-${deployment.id}-drift-panel`}
                  >
                    <div className="flex items-baseline justify-between gap-3 flex-wrap">
                      <span className="font-medium text-white/90">Tenant changes since this deployment was created</span>
                      {progress.drift_checked_at && (
                        <span className="text-[11px] text-white/45">
                          Last checked {fmtScheduleDate(progress.drift_checked_at)}
                        </span>
                      )}
                    </div>

                    {progress.unplaced_arrivals > 0 && (
                      <div className="mt-2 text-amber-200/90" data-testid={`fu-deployment-${deployment.id}-unplaced-arrivals`}>
                        <span className="text-amber-300 font-medium">
                          {progress.unplaced_arrivals} new eligible device
                          {progress.unplaced_arrivals === 1 ? '' : 's'} could not be placed.
                        </span>{' '}
                        Every sprint has already activated, and ALICE will not add a device to a sprint that is
                        already offering — it would receive the update without ever appearing in the plan you
                        signed off. Add a sprint, or create a follow-up deployment for them.
                      </div>
                    )}

                    {progress.drift_log?.length > 0 && (() => {
                      const counts = progress.drift_log.reduce((acc, e) => {
                        acc[e.action] = (acc[e.action] || 0) + 1;
                        return acc;
                      }, {});
                      const wording = {
                        departed: 'left Intune and were removed from their sprint',
                        relinked: 'were rebuilt and re-linked to the sprint they were already in',
                        arrived: 'were enrolled since and added to a pending sprint',
                        // 09/09 — an Entra group edited outside ALICE. The
                        // repair is right; doing it silently was not.
                        group_repaired: 'were removed from their Entra group outside ALICE and added back',
                      };
                      return (
                        <>
                          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-white/75 text-[13px]">
                            {['departed', 'relinked', 'arrived', 'group_repaired'].filter(k => counts[k]).map(k => (
                              <span key={k} data-testid={`fu-deployment-${deployment.id}-drift-${k}`}>
                                <span className="text-white font-medium">{counts[k]}</span> {wording[k]}
                              </span>
                            ))}
                          </div>
                          <details className="mt-2">
                            <summary className="text-[11px] text-white/50 hover:text-white/80 cursor-pointer">
                              Show the last {Math.min(progress.drift_log.length, 20)} changes
                            </summary>
                            <div className="mt-2 max-h-48 overflow-y-auto flex flex-col gap-1">
                              {progress.drift_log.slice(0, 20).map((e, i) => (
                                <div key={`${e.device_id}-${e.at}-${i}`} className="text-[11px] text-white/60 flex gap-2">
                                  <span className="text-white/35 shrink-0 w-20 tabular-nums">
                                    {e.at ? new Date(e.at).toLocaleDateString() : ''}
                                  </span>
                                  <span className={
                                    e.action === 'departed' ? 'text-amber-300/80 shrink-0 w-16'
                                      : e.action === 'relinked' ? 'text-cyan-300/80 shrink-0 w-16'
                                        : e.action === 'group_repaired' ? 'text-violet-300/80 shrink-0 w-20'
                                          : 'text-green-300/80 shrink-0 w-16'
                                  }>
                                    {e.action === 'group_repaired' ? 'group fix' : e.action}
                                  </span>
                                  <span className="text-white/80">{e.device_name || e.device_id}</span>
                                  <span className="text-white/40">· {e.phase}</span>
                                  {e.matched_on && <span className="text-white/35">· matched on {e.matched_on}</span>}
                                  {e.action === 'group_repaired' && (
                                    <span className="text-white/35" title={e.reason}>· why?</span>
                                  )}
                                </div>
                              ))}
                            </div>
                          </details>
                        </>
                      );
                    })()}

                    {Object.keys(progress.arrivals_rejected || {}).length > 0 && (
                      <details className="mt-2">
                        <summary className="text-[11px] text-white/50 hover:text-white/80 cursor-pointer">
                          New devices ALICE did not add, and why
                        </summary>
                        <div className="mt-1.5 flex flex-col gap-0.5">
                          {Object.entries(progress.arrivals_rejected).map(([reason, n]) => (
                            <span key={reason} className="text-[11px] text-white/60">
                              <span className="text-white/85">{n}</span> — {reason}
                            </span>
                          ))}
                        </div>
                      </details>
                    )}
                  </div>
                )}

                {/* Membership drift banner — a phase can be 'active' with the
                    right group attached yet have no members, which silently
                    stalls the whole rollout. */}
                {progress.membership_mismatch && (
                  <div
                    className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-200"
                    data-testid={`fu-deployment-${deployment.id}-membership-warning`}
                  >
                    <div className="font-medium text-amber-300">Entra group membership does not match the plan</div>
                    <div className="mt-1 text-amber-200/90">
                      Affected {progress.membership_mismatch_phases?.length === 1 ? 'sprint' : 'sprints'}:{' '}
                      {(progress.membership_mismatch_phases || []).join(', ')}. Devices allocated in ALICE are missing
                      from (or unexpected in) the phase group, so Intune will not offer the update to them. Use
                      &quot;Fix group membership&quot; to repair.
                    </div>
                  </div>
                )}

                {/* Untracked group members — ALICE cannot know the admin's
                    intent, so it never guesses: choose Adopt or Remove. */}
                {(() => {
                  const phasesArr = [...(progress.test_group ? [progress.test_group] : []), ...(progress.sprint_waves || [])];
                  const untracked = phasesArr.reduce((n, p) => n + (p.extra_in_group || 0), 0);
                  if (!untracked) return null;
                  return (
                    <div
                      className="rounded-lg border border-sky-500/40 bg-sky-500/10 p-3 text-sm text-sky-100"
                      data-testid={`fu-deployment-${deployment.id}-untracked-members`}
                    >
                      <div className="font-medium text-sky-300">
                        {untracked} group member{untracked === 1 ? '' : 's'} not allocated to the sprint they are in
                      </div>
                      <div className="mt-1 text-sky-200/90">
                        Intune is targeting them through the sprint group, but ALICE does not track them in that sprint.
                        Adopt them to report on them, or remove them from the group to stop the update being offered.
                      </div>
                      {canWriteOSPatch && (
                      <div className="mt-2 flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="border-sky-500/50 text-sky-200 hover:bg-sky-500/20"
                          disabled={fixingMembership}
                          onClick={() => handleFixMembership('adopt')}
                          data-testid={`fu-deployment-${deployment.id}-adopt-untracked-btn`}
                        >
                          Adopt into sprint
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="border-red-500/50 text-red-300 hover:bg-red-500/20"
                          disabled={fixingMembership}
                          onClick={() => handleFixMembership('remove')}
                          data-testid={`fu-deployment-${deployment.id}-remove-untracked-btn`}
                        >
                          Remove from group
                        </Button>
                      </div>
                      )}
                    </div>
                  );
                })()}

                {/* Overall Progress */}
                <div className="bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-white font-medium">Overall Progress</span>
                    <span className="text-white">{progress.overall_progress?.completion_percentage || 0}% updated</span>
                  </div>
                  <div className="h-3 bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-purple-500 to-green-500 rounded-full transition-all duration-500"
                      style={{ width: `${progress.overall_progress?.completion_percentage || 0}%` }}
                    />
                  </div>
                  <div className="mt-2 text-sm text-white/90">
                    {progress.overall_progress?.updated_devices || 0} / {progress.overall_progress?.total_devices || 0} devices on target version
                  </div>
                </div>

                {/* Test Group Progress */}
                {progress.test_group && (
                  <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-cyan-400 font-medium">Test Group</span>
                      <div className="flex items-center gap-2">
                        {progress.test_group.intune_profile_id && (
                          <button
                            type="button"
                            onClick={() => setSprintDrillIn({
                              kind: 'profile',
                              id: progress.test_group.intune_profile_id,
                              label: 'Test Group',
                              title: `${deployment.name} — Test Group`,
                            })}
                            className="text-[11px] text-cyan-300 hover:text-cyan-200 underline underline-offset-2"
                            data-testid={`fu-deployment-${deployment.id}-test-group-view-devices`}
                          >
                            View devices
                          </button>
                        )}
                        <span className={`px-2 py-0.5 text-xs rounded-full ${getStatusColor(progress.test_group.status)}`}>
                          {progress.test_group.status}
                        </span>
                      </div>
                    </div>
                    <div className="h-2 bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-cyan-500 rounded-full"
                        style={{ width: `${progress.test_group.completion_percentage || 0}%` }}
                      />
                    </div>
                    <div className="mt-1 text-xs text-white/80">
                      {progress.test_group.updated_devices || 0} / {progress.test_group.total_devices} updated ({progress.test_group.completion_percentage}%)
                      {summariseFeatureBuckets(progress.test_group.status_counts) && (
                        <span className="text-white/60"> · {summariseFeatureBuckets(progress.test_group.status_counts)}</span>
                      )}
                      {progress.test_group.membership_known && (
                        <span className={progress.test_group.membership_mismatch ? 'text-amber-300' : 'text-white/60'}>
                          {' '}· {progress.test_group.devices_in_group} in group
                          {progress.test_group.missing_from_group > 0 && ` · ${progress.test_group.missing_from_group} missing`}
                          {progress.test_group.extra_in_group > 0 && ` · ${progress.test_group.extra_in_group} not allocated here`}
                          {progress.test_group.stale_allocations > 0 && ` · ${progress.test_group.stale_allocations} stale allocations`}
                        </span>
                      )}
                    </div>

                    {/* 01/09 round 2 — per-phase Optional/Required + schedule. */}
                    <div className="mt-2 pt-2 border-t border-white/10 flex items-center gap-2 flex-wrap">
                      <span className="text-[11px] text-white/50">Installation:</span>
                      {renderInstallTypeToggle(
                        progress.test_group.installation_type || 'required',
                        (next) => handleChangeInstallType(next, 'test', 'Test Group'),
                        `fu-deployment-${deployment.id}-test-group-install-type`,
                        switchingInstallType,
                      )}
                    </div>
                    {renderPhaseSchedule(
                      progress.test_group, 'test', 'Test Group',
                      `fu-deployment-${deployment.id}-test-group`,
                    )}

                    {/* 02/09 — POC sign-off. Shown only while the gate is on,
                        because otherwise it reads as a control that does
                        nothing. */}
                    {progress.require_test_approval && (
                      <div className="mt-2 pt-2 border-t border-white/10 flex items-center gap-2 flex-wrap">
                        {progress.test_group.approved_at ? (
                          <>
                            <span
                              className="text-[11px] px-1.5 py-0.5 rounded-full border border-green-400/40 bg-green-500/10 text-green-300"
                              data-testid={`fu-deployment-${deployment.id}-test-approved`}
                            >
                              Approved
                            </span>
                            <span className="text-[11px] text-white/50">
                              {fmtScheduleDate(progress.test_group.approved_at)}
                              {progress.test_group.approved_by ? ` · ${progress.test_group.approved_by}` : ''}
                            </span>
                            {canWriteOSPatch && (
                            <button
                              type="button"
                              disabled={approvingTest}
                              onClick={() => handleApproveTestGroup(false)}
                              className="text-[11px] text-white/50 hover:text-white underline underline-offset-2 disabled:opacity-50"
                              data-testid={`fu-deployment-${deployment.id}-withdraw-approval`}
                            >
                              Withdraw
                            </button>
                            )}
                          </>
                        ) : (
                          <>
                            <span className="text-[11px] text-amber-300/90">
                              Awaiting sign-off — the rollout will not advance past the test group
                            </span>
                            {canWriteOSPatch && (
                            <Button
                              size="sm"
                              disabled={approvingTest}
                              onClick={() => handleApproveTestGroup(true)}
                              className="bg-green-600 hover:bg-green-700 text-white h-6 px-2 text-[11px]"
                              data-testid={`fu-deployment-${deployment.id}-approve-test`}
                            >
                              {approvingTest ? 'Saving…' : 'Approve test group'}
                            </Button>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Sprint Phases */}
                <div className="space-y-2">
                  {progress.sprint_waves?.map((wave) => (
                    <div 
                      key={wave.wave_number}
                      className={`rounded-lg p-3 ${
                        wave.status === 'active' ? 'bg-purple-500/10 border border-purple-500/30' :
                        wave.status === 'completed' ? 'bg-green-500/10 border border-green-500/30' :
                        'bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] border border-[rgba(229,239,250,0.1)]'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className={`font-medium ${
                          wave.status === 'active' ? 'text-purple-400' :
                          wave.status === 'completed' ? 'text-green-400' : 'text-white/90'
                        }`}>
                          Sprint {wave.wave_number}
                        </span>
                        <div className="flex items-center gap-2">
                          {wave.intune_profile_id && (
                            <button
                              type="button"
                              onClick={() => setSprintDrillIn({
                                kind: 'profile',
                                id: wave.intune_profile_id,
                                label: `Sprint ${wave.wave_number}`,
                                title: `${deployment.name} — Sprint ${wave.wave_number}`,
                              })}
                              className={`text-[11px] underline underline-offset-2 ${
                                wave.status === 'completed' ? 'text-green-300 hover:text-green-200' :
                                wave.status === 'active' ? 'text-purple-300 hover:text-purple-200' :
                                'text-white/70 hover:text-white'
                              }`}
                              data-testid={`fu-deployment-${deployment.id}-sprint-${wave.wave_number}-view-devices`}
                            >
                              View devices
                            </button>
                          )}
                          <span className={`px-2 py-0.5 text-xs rounded-full ${getStatusColor(wave.status)}`}>
                            {wave.status}
                          </span>
                        </div>
                      </div>
                      <div className="h-2 bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] rounded-full overflow-hidden">
                        <div 
                          className={`h-full rounded-full ${
                            wave.status === 'completed' ? 'bg-green-500' :
                            wave.status === 'active' ? 'bg-purple-500' : 'bg-white/20'
                          }`}
                          style={{ width: `${wave.completion_percentage || 0}%` }}
                        />
                      </div>
                      <div className="mt-1 text-xs text-white/80" data-testid={`fu-deployment-${deployment.id}-sprint-${wave.wave_number}-progress`}>
                        {wave.updated_devices || 0} / {wave.total_devices} updated ({wave.completion_percentage}%)
                        {summariseFeatureBuckets(wave.status_counts) && (
                          <span className="text-white/60"> · {summariseFeatureBuckets(wave.status_counts)}</span>
                        )}
                        {wave.membership_known && (
                          <span className={wave.membership_mismatch ? 'text-amber-300' : 'text-white/60'}>
                            {' '}· {wave.devices_in_group} in group
                            {wave.missing_from_group > 0 && ` · ${wave.missing_from_group} missing`}
                            {wave.extra_in_group > 0 && ` · ${wave.extra_in_group} not allocated here`}
                            {wave.stale_allocations > 0 && ` · ${wave.stale_allocations} stale allocation${wave.stale_allocations === 1 ? '' : 's'}`}
                          </span>
                        )}
                      </div>

                      {/* 01/09 round 2 — per-sprint Optional/Required plus the
                          dates driving this sprint. This is the control the
                          "optional, comms, 10 days, then required" rollout
                          actually needs: Sprint 1 can be enforced while
                          Sprint 3 is still optional. */}
                      <div className="mt-2 pt-2 border-t border-white/10 flex items-center gap-2 flex-wrap">
                        <span className="text-[11px] text-white/50">Installation:</span>
                        {renderInstallTypeToggle(
                          wave.installation_type || 'required',
                          (next) => handleChangeInstallType(
                            next, `sprint-${wave.wave_number}`, `Sprint ${wave.wave_number}`,
                          ),
                          `fu-deployment-${deployment.id}-sprint-${wave.wave_number}-install-type`,
                          switchingInstallType,
                        )}
                      </div>
                      {renderPhaseSchedule(
                        wave, `sprint-${wave.wave_number}`, `Sprint ${wave.wave_number}`,
                        `fu-deployment-${deployment.id}-sprint-${wave.wave_number}`,
                      )}
                    </div>
                  ))}
                </div>

                <Button size="sm" variant="outline" className="border-[rgba(229,239,250,0.1)] text-white" onClick={loadProgress}>
                  <RefreshCw className="w-3 h-3 mr-1" /> Refresh Progress
                </Button>
              </>
            ) : (
              <div className="text-white/70 text-sm">No progress data available</div>
            )}
          </div>
        )}

        {/* Per-sprint device drill-down modal — same component used by
            the Reports > Feature Updates cards. */}
        {sprintDrillIn && (
          <FeatureUpdateDeviceDrilldown
            drillIn={sprintDrillIn}
            onClose={() => setSprintDrillIn(null)}
          />
        )}

        {/* New Feature 2 (01/09) — manual sprint/deployment reassignment. */}
        {showMoveDevices && (
          <MoveDevicesModal
            deployment={deployment}
            deployments={deployments}
            onClose={() => setShowMoveDevices(false)}
            onMoved={() => {
              loadProgress();
              if (onRefresh) onRefresh();
            }}
          />
        )}
      </CardContent>
    </Card>
  );
};
